import configment from '@adc/configment';
import _, { isNil, keyBy, get, isString, snakeCase, isEqual, escapeRegExp, merge, omit } from 'lodash';
import coreLogger from '@adc/logger';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import moment from 'moment-timezone';
import * as graph from '@adc/sdk';
import { gql as gql$5, enums as enums$5, utils as utils$e } from '@adc/sdk';
import parse from 'url-parse';
import { match, P } from 'ts-pattern';
import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo, isValidElement, useReducer } from 'react';
import moment$1 from 'moment';
import ms from 'ms';
import * as adcHooks from '@adc/hooks';
import { usePrevious, useInterval, useIsMounted, useOnce } from '@adc/hooks';
import instrument$1 from '@adc/instrument';
import * as fetch from '@adc/fetch';
import fetch__default from '@adc/fetch';
import { contactNumbers } from '@adc/constants';
import { conformToMask } from 'react-text-mask';
import tinycolor from 'tinycolor2';
import pluralize from 'pluralize';
import { differenceInSeconds, differenceInCalendarDays, isFuture, isToday, isYesterday, isAfter, addSeconds, isBefore, subSeconds, parseISO, subDays } from 'date-fns';
import gql$6 from 'graphql-tag';
import MurmurHash3 from 'imurmurhash';
import { z } from 'zod';
import LRUCache from 'lru-cache';

// We used this to enable optimistics updates while hiding the detail from the consumer. Updates are optimistic when
// apps use the bound utils to trigger mutations
const SPECIAL_SELF_REFERENCE = Symbol.for('adc.blueprint.idempotent_element_self_ref');
const embeddableOperation = name => [_.camelCase(configment.get('APP_NAME', '')), name].filter(Boolean).join('_');

// We are duplicating this because "Little bit of duplication better than a little bit of dependency" - CATHERTON 2024
const formatMoney$2 = (val, overrides, hideDecimals = false) => {
  if (Number.isFinite(val)) {
    const stringVal = String(val);
    const number = hideDecimals ? Number.parseInt(stringVal) : Number.parseFloat(stringVal);
    const minimumFractionDigits = Number.isInteger(number) ? 0 : 2;
    return number.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits,
      ...overrides
    });
  }
  return String(val) || '';
};
// Marketing url supported prefixes
const MARKETING_SUPPORT_PREFIXES = ['utm', 'sfid', 'sfmc', 'marketing'];
const extractMarketingSupportPrefixes = urlString => {
  try {
    if (!urlString) return null;
    const {
      query
    } = parse(urlString, true);
    return _.pickBy(query, (_, key) => MARKETING_SUPPORT_PREFIXES.some(prefix => key.startsWith(prefix)));
  } catch {
    return null;
  }
};
const getTextFromReactNode = richTextNode => {
  if (_.isNil(richTextNode)) return '';
  return match(typeof richTextNode).with('string', () => richTextNode).with('number', () => richTextNode.toString()).with('boolean', () => '').with('object', () => {
    if (richTextNode instanceof Array) return richTextNode.map(getTextFromReactNode).join('');
    if (richTextNode.props) {
      const nodeWithProps = richTextNode;
      if (nodeWithProps.props?.children) {
        const children = nodeWithProps.props.children;
        return getTextFromReactNode(children);
      }
    }
    return ''; // fall through otherwise
  }).otherwise(() => {
    console.warn('Unresolved `richTextNode` of type:', typeof richTextNode, richTextNode);
    return '';
  });
};
const isNumberType = obj => typeof obj === 'number';
const isBooleanType = obj => typeof obj === 'boolean';
const isStringType = obj => typeof obj === 'string';
const isObjectType = obj => obj !== null && obj !== undefined && typeof obj === 'object';
const UNRESOLVED_TAGS_MATCH_PATTERN = /({{?[^}]+}})/g;
const removeTagDelimiters = tag => tag.slice(2, tag.length - 2);
const interpolate = (unresolvedInput, ctx, replacer) => {
  const output = [];
  const pattern = UNRESOLVED_TAGS_MATCH_PATTERN;
  let processedInput = unresolvedInput;
  let result = pattern.exec(processedInput);
  while (result !== null) {
    const matchIndex = result.index;
    const match = result[0];
    const contentBeforeMatch = processedInput.substring(0, matchIndex);
    const key = removeTagDelimiters(match);
    const value = _.get(ctx, key) || '';
    let replacedMatch = Array.isArray(value) ? value.join(',') : value;
    if (replacer) {
      replacedMatch = replacer(key, replacedMatch);
    }
    output.push(contentBeforeMatch);
    output.push(replacedMatch);
    processedInput = processedInput.substring(matchIndex + match.length, processedInput.length + 1);
    pattern.lastIndex = 0; // need to continue matching
    result = pattern.exec(processedInput);
  }
  if (processedInput) {
    output.push(processedInput);
  }
  return output;
};
const calculateTimeZone = () => moment.tz.guess() || Intl.DateTimeFormat().resolvedOptions().timeZone;
// TODO: How do we surface this to all clients without breaking anyone?
const partialGraphErrorFactory$3 = (message, errors) => {
  const e = new Error(message);
  e.errors = errors;
  return e;
};
/**
 * Type guard to check if an unknown error has GraphQLError structure
 */
const isGraphQLError = error => {
  return isObjectType(error) && Array.isArray(error.errors);
};
/**
 * Check if a GraphQL subscription error is an authentication error
 */
const isAuthenticationError = error => {
  if (!isGraphQLError(error)) {
    return false;
  }
  return Boolean(error.errors?.some(err => {
    const e = err;
    return e?.extensions?.code === 'UNAUTHENTICATED' || e?.message?.includes('Invalid user session');
  }));
};
const isShortTermExpiredError = error => {
  return error?.extensions?.code === 'BACKEND_UNAUTHORIZED_ERROR' && error?.extensions?.specificErrorCode === graph.enums.UserShortTermStatus.ShortTermExpired;
};
/**
 * Check if a GraphQL error is a backend forbidden error, which can be caused by either an expired session or insufficient permissions.
 */
const isBackendForbiddenError = error => {
  if (!isGraphQLError(error)) {
    return false;
  }
  return Boolean(error.errors?.some(err => {
    const e = err;
    return e?.extensions?.code === 'BACKEND_FORBIDDEN_ERROR' || e?.extensions?.specificErrorCode === 'FORBIDDEN' || e?.message?.includes('403: Access Denied');
  }));
};
const isAnyAuthenticationError = error => {
  return isAuthenticationError(error) || isBackendForbiddenError(error);
};
// Simple utility to determine if a value is a number (including numeric strings)
const isNumberInput = value => _.isFinite(parseInt(value, 10));

var utils$d = /*#__PURE__*/Object.freeze({
    __proto__: null,
    calculateTimeZone: calculateTimeZone,
    embeddableOperation: embeddableOperation,
    extractMarketingSupportPrefixes: extractMarketingSupportPrefixes,
    formatMoney: formatMoney$2,
    getTextFromReactNode: getTextFromReactNode,
    interpolate: interpolate,
    isAnyAuthenticationError: isAnyAuthenticationError,
    isAuthenticationError: isAuthenticationError,
    isBackendForbiddenError: isBackendForbiddenError,
    isBooleanType: isBooleanType,
    isGraphQLError: isGraphQLError,
    isNumberInput: isNumberInput,
    isNumberType: isNumberType,
    isObjectType: isObjectType,
    isShortTermExpiredError: isShortTermExpiredError,
    isStringType: isStringType,
    partialGraphErrorFactory: partialGraphErrorFactory$3
});

const logger$k = coreLogger.forModule('blueprint:GraphWebsocket');
const MAXIMUM_GRAPH_CONTEXT_UPDATE_RATE = 400;
class GraphWebsocket {
  constructor(options = {}) {
    this.client = new SubscriptionClient(configment.get('RESI_GRAPH_API_SUBSCRIPTIONS_URL') + '/graphql/subscriptions', {
      reconnect: true,
      connectionCallback: () => {
        this.markAndResolve();
      },
      reconnectionAttempts: 10,
      ...options
    }, WebSocket, 'adc-graphql-ws');
    // @ts-ignore ts doesn't like lodash
    this.markAndResolve = _.noop;
    this.waitForEstablishedConnection = new Promise(resolve => {
      // NOTE: The reason we track this is due to how the library handles internal connections. If we try to connect and
      // it fails it throws a disconnect and an error, and then it runs the backoff logic. If that backoff produces
      // a connection it comes back in as a `reconnect` not a `connected`. So to properly handle all cases we need to
      // listen for the first instance of either event.
      let markResolved = false;
      const markAndResolve = () => {
        if (markResolved) return;
        markResolved = true;
        resolve();
      };
      this.markAndResolve = markAndResolve;
    });
    this.eventListeners = {
      onConnected: [],
      onDisconnected: [],
      onReconnected: [],
      onError: []
    };
    this.pendingSubscriptionsCount = 0;
    this.pendingWaitAttemptsCount = 3;
    this.client.onConnected(() => {
      this.eventListeners.onConnected.forEach(listener => listener());
    });
    this.client.onDisconnected(() => {
      this.eventListeners.onDisconnected.forEach(listener => listener());
    });
    this.client.onReconnected(() => {
      this.eventListeners.onReconnected.forEach(listener => listener());
    });
    this.client.onError(error => {
      this.eventListeners.onError.forEach(listener => listener(error));
    });
  }
  onConnected(listener) {
    this.eventListeners.onConnected.push(listener);
  }
  onDisconnected(listener) {
    this.eventListeners.onDisconnected.push(listener);
  }
  onReconnected(listener) {
    this.eventListeners.onReconnected.push(listener);
  }
  onError(listener) {
    this.eventListeners.onError.push(listener);
  }
  subscribe({
    operationOptions,
    onData,
    onError
  }) {
    this.pendingSubscriptionsCount++;
    return this.waitForEstablishedConnection.then(() => {
      const unsubscriber = this.client.request(operationOptions).subscribe({
        error: error => {
          onError(error);
        },
        next: ({
          data,
          errors
        }) => {
          if (errors && errors.length) {
            // @ts-ignore unsafe call of any
            logger$k.warn(`graphWebsocket next errors: ${JSON.stringify(errors)}`);
            onError(partialGraphErrorFactory$3('graph websocket errors', Array.from(errors)));
          }
          if (!data) return;
          onData(data);
        }
      });
      // @ts-ignore accessing private member
      const operationId = this.client.nextOperationId;
      this.pendingSubscriptionsCount--;
      return {
        unsubscribe: unsubscriber.unsubscribe,
        operationId,
        resubscribe: _.noop,
        updateContext: _.debounce(context => {
          // @ts-ignore accessing private method
          this.client.sendMessage(String(operationId), 'GQL_CONTEXT_UPDATE', {
            context
          });
        }, MAXIMUM_GRAPH_CONTEXT_UPDATE_RATE),
        subscriptionFeedback: feedback => {
          // @ts-ignore accessing private method
          this.client.sendMessage(`${operationId}`, 'GQL_SUBSCRIPTION_FEEDBACK', {
            ...feedback
          });
        }
      };
    });
  }
  close() {
    this.client.close();
  }
  async restart() {
    while (this.pendingSubscriptionsCount > 0 && this.pendingWaitAttemptsCount > 0) {
      await waitFor(300);
      this.pendingWaitAttemptsCount--;
    }
    await this.client.close(false);
    this.pendingWaitAttemptsCount = 3;
  }
}
const waitFor = delay => new Promise(resolve => setTimeout(resolve, delay));
class NullGraphWebsocket {
  onConnected() {}
  onDisconnected() {}
  onReconnected() {}
  onError() {}
  subscribe() {
    return Promise.resolve({
      unsubscribe() {},
      resubscribe() {},
      updateContext() {},
      subscriptionFeedback() {},
      operationId: Infinity
    });
  }
  close() {}
  restart() {}
}

/* istanbul ignore file */
const logger$j = coreLogger.forModule('devicestorage');
class NullDeviceStorage {
  constructor(options = {
    namespace: ''
  }) {
    this.namespace = options.namespace;
  }
  get() {
    return Promise.resolve(null);
  }
  set() {
    return Promise.resolve();
  }
  // noop is used so that typescript doesn't complain
  // about empty functions
  publish() {
    _.noop();
  }
  subscribe() {
    return () => _.noop();
  }
}
class DeviceStorageAdapter {
  constructor(options = {
    storage: new NullDeviceStorage()
  }) {
    this._storage = options.storage;
  }
  async get(key) {
    try {
      const item = await this._storage.get(key);
      return item;
    } catch (err) {
      logger$j.error(err);
    }
    return null;
  }
  async set(key, value) {
    try {
      await this._storage.set(key, value);
      this.publish(key, value);
    } catch (err) {
      logger$j.error(err);
    }
  }
  publish(key, newValue) {
    if (typeof this._storage.publish === 'function') {
      this._storage.publish(key, newValue);
    }
  }
  subscribe(listener) {
    if (typeof this._storage.subscribe === 'function') {
      return this._storage.subscribe(listener);
    }
    return () => _.noop();
  }
}
// Web local storage wrapper
class WebLocalStorage {
  constructor(options = {
    namespace: ''
  }) {
    this.namespace = options.namespace;
  }
  get(key) {
    try {
      const item = window.localStorage.getItem(key);
      return Promise.resolve(item);
    } catch (err) {
      return Promise.reject(err);
    }
  }
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  }
  publish(key, newValue) {
    window.dispatchEvent(new StorageEvent('storage', {
      key,
      newValue
    }));
  }
  subscribe(listener) {
    window.addEventListener('storage', listener);
    return () => {
      window.removeEventListener('storage', listener);
    };
  }
}

const SUPPORTED_OFFSETS = {
  SLACK: ms('5m'),
  NORMAL: ms('2m'),
  URGENT: ms('30s'),
  CRITICAL: ms('10s')
};

// NOTE: We use seconds precision down below because it's useful enough for debugging at a glance. Our polling logic is
// millisecond precision but mentally computing the diff between milliseconds timestamps is more taxing than seconds at
// a glance. And usually it's enough: if we are polling for the same data any more frequently than a few seconds we
// know there is a bug somewhere.
//
// Also, `AR` stands for 'Anno Roman' and marks a relative diff from when the start of the arbitrary epoch when this
// realtime logic was first loaded.
const TIME_STYLES = `
  color: #fff;
  text-transform: uppercase;
  padding: 3px 6px;
  background-color: #5A4FFF;
  border-radius: 4px;
  font: monospace;
`;
const START_OF_TIME = Date.now();
const timestamp = () => `${moment$1().diff(START_OF_TIME, 'seconds')} AR`;
const timestampf = (...messages) => console.log(`%c${timestamp()}`, TIME_STYLES, ...messages);
class DynamicIntervalPoller {
  constructor(id, onData, onError, onEvent) {
    this.terminated = false;
    this.id = id;
    // We always trigger a poll on the leading edge when the page mounts which should return data that
    // would cause the frequency computation to either speed up the polling or slow it down.
    this.frequency = this.lastPollOffset = SUPPORTED_OFFSETS.NORMAL;
    this.onData = onData;
    this.onError = onError;
    this.onEvent = onEvent;
    this.subjectIds = [];
    // We debounce here because there are events that can happen concurrently that would requires a leading edge poll. The
    // best example would be transitioning from one set of subjects to another where the new subjects require a quick
    // polling offset (maybe listings that are closer to the end of the auction).
    // We needed to create a new function per instance given that the target of debounce is the prototype, so one instance of DynamicIntervalPoller could be preventing another one from calling schedulePoll function
    this.schedulePoll = _.debounce(() => {
      this.terminate();
      this.terminated = false; // reset — this is an intentional restart, not a permanent shutdown
      this.triggerPoll();
      // NOTE: had originally planned to use this but after testing it's clear that when we first mount the app
      // thrashes while it loads all the data it needs to accurately compute what the current polling would be.
      // Without forcefully terminating the current request we end up scheduling a few polls right away since this
      // idle callback is scheduled whenever the browser wants. Instead we rely on timeouts which we can cancel ahead
      // of time.
      // requestIdleCallback(this.triggerPoll.bind(this), {
      //   timeout: MAX_ALLOWED_POLL_DRIFT
      // })
    }, 500);
  }
  setPollingFrequency(newFrequency) {
    this.frequency = newFrequency;
    return this;
  }
  setOnData(newOnData) {
    this.onData = newOnData;
    return this;
  }
  setPollingExecutor(newPollingExecutor) {
    if (this.poller !== newPollingExecutor) {
      this.poller = newPollingExecutor;
    }
    return this;
  }
  setSubjectIds(newSubjectIds) {
    this.subjectIds = newSubjectIds;
    return this;
  }
  calculateNextOffset() {
    const nextPollOffset = _.isFunction(this.frequency) ? this.frequency() : this.frequency;
    if (this.lastPollOffset !== nextPollOffset) {
      this.onEvent?.('polling:frequency_change', {
        previous: this.lastPollOffset,
        current: nextPollOffset
      });
    }
    return nextPollOffset;
  }
  potentiallyScheduleNewPoll(newFrequency) {
    this.frequency = newFrequency;
    const nextPollOffset = this.calculateNextOffset();
    // If anything about the frequency value changes in the background we need to schedule a new poll. If we don't do
    // this we are assuming that the end of every poll cycle will be enough to compute and catch the next poll cycle.
    // Something might happen while we are waiting to schedule another poll that would cause us to switch to a more
    // frequent polling pattern
    if (nextPollOffset !== this.lastPollOffset) {
      timestampf('Polling frequency change detected. Scheduling new poll.', {
        previous: ms(this.lastPollOffset, {
          long: true
        }),
        next: ms(nextPollOffset, {
          long: true
        })
      });
      this.lastPollOffset = nextPollOffset;
      this.schedulePoll();
    }
    return this;
  }
  async triggerPoll() {
    if (!this.poller || !this.subjectIds?.length) {
      return;
    }
    timestampf('Triggering new poll: ' + this.id);
    this.onEvent?.('polling:ready', {
      terminate: this.terminate.bind(this)
    });
    try {
      this.currentAbortController = new AbortController();
      await this.poller({
        subjectIds: this.subjectIds,
        controller: this.currentAbortController
      }).then(data => {
        return this.onData({
          id: this.id,
          data: data?.length ? data : [],
          source: 'polling'
        });
      });
    } catch (error) {
      this.onError?.({
        id: this.id,
        source: 'polling',
        error
      });
    } finally {
      if (!this.terminated) {
        this.lastPollOffset = this.calculateNextOffset();
        this.timeoutRef = setTimeout(this.triggerPoll.bind(this), this.lastPollOffset);
      }
    }
  }
  terminate() {
    timestampf('Terminating poller: ' + this.id);
    this.terminated = true;
    this.currentAbortController?.abort();
    clearTimeout(this.timeoutRef);
  }
}
const deterministicSerialization = keys => keys.sort().join('-');
function useRealtimeWatcher({
  id,
  subjectIds,
  dependencies = [],
  polling,
  websocket,
  onData,
  onError,
  onEvent = _.noop
}) {
  // A quick note on this here: we could get away without doing this and relying on `subjectIds` to be a stable value.
  // The issue happens when apps pass different array objects containing the same keys, which frequently happens with
  // polling for parent data, and not adding a delta check. To stop that firing polling in here we normaliza those
  // subjects to some deterministic string which won't change, thereby avoiding the polling internally here.
  const [triggerKey, setTriggerKey] = useState(deterministicSerialization(subjectIds));
  const prevTriggerKey = usePrevious(triggerKey);
  const prevDependencies = usePrevious(dependencies);
  const unsubscribe = useRef(null);
  const isResubscribing = useRef(false);
  // once this is set after reconnect, we want to trigger a re-render to attempt subscription one more time
  const [shouldResubscribeOnReconnect, setShouldResubscribeOnReconnect] = useState(false);
  // track this as ref as we don't want it to trigger anything by itself, it's just a value we save until we reconnect and check if it was set true
  const hadSubscriptionAuthError = useRef(false);
  const pollingExecutor = useRef(polling ? new DynamicIntervalPoller(id, onData, onError, onEvent).setPollingFrequency(polling.frequency).setPollingExecutor(polling.executor) : null);
  const onDataRef = useRef(onData);
  useEffect(() => {
    pollingExecutor.current?.setOnData(onData);
    onDataRef.current = onData;
  }, [onData]);
  useEffect(() => {
    if (polling?.executor) {
      pollingExecutor.current?.setPollingExecutor(polling.executor);
    }
  }, [polling?.executor]);
  // so, rollup issue, see comment at the top of the file.
  // Otherwise we'd use hooks.useMountEffect here
  // @ts-ignore unknown
  useEffect(() => {
    if (!polling?.enabled) {
      onEvent('polling:disabled', {
        id
      });
    }
    if (!websocket?.enabled) {
      onEvent('websocket:disabled', {
        id
      });
    } else {
      websocket.graphWebsocket.onConnected(() => onEvent('websocket:connected', {
        id
      }));
      websocket.graphWebsocket.onDisconnected(() => onEvent('websocket:disconnected', {
        id
      }));
    }
    // Register reconnection listener regardless of enabled state.
    // The subscription may not be active at mount time (e.g. cognition starts
    // with no evaluation groups) but becomes active later. If we only register
    // when enabled=true at mount, we miss reconnection events that happen after
    // the subscription is established (e.g. login triggers restart()).
    if (websocket?.graphWebsocket) {
      websocket.graphWebsocket.onReconnected(() => {
        onEvent('websocket:reconnected', {
          id
        });
        // on reconnect, check if we had a subscriptionAuthError and if so, set the flag to resubscribe, also set the
        // hadSubscriptionAuthError to false to avoid infinite resubscription attempt
        // Resubscribe on reconnect to ensure subscriptions are on the
        // newly authenticated connection after restart().
        setShouldResubscribeOnReconnect(true);
        if (hadSubscriptionAuthError.current) {
          onEvent('websocket:will_attempt_resubscription_because_auth_error', {
            id
          });
          hadSubscriptionAuthError.current = false;
        }
      });
    }
    return () => {
      pollingExecutor.current?.terminate();
      unsubscribe.current?.();
    };
  }, []);
  useEffect(() => {
    setTriggerKey(deterministicSerialization(subjectIds));
    pollingExecutor.current?.setSubjectIds(subjectIds);
  }, [subjectIds]);
  // This is triggered whenever the polling frequency changes and polling frequency can be a function which could be
  // re-set yet still evaluate to the same value, that's why the function at the bottom of this is called potentially schedule new poll,
  // the logic in there determines whether it's actually a new value and we need to schedule a poll or not,
  // but it's expected that this useEffect will be called many times yet no new poll will be scheduled.
  useEffect(() => {
    if (!polling?.enabled) {
      return;
    }
    pollingExecutor.current?.potentiallyScheduleNewPoll(polling.frequency);
  }, [polling?.frequency, polling?.enabled]);
  useEffect(() => {
    if (!polling?.enabled || !triggerKey) {
      return;
    }
    timestampf('New trigger key detected.');
    pollingExecutor.current?.schedulePoll();
  }, [triggerKey, polling?.enabled]);
  const onWebsocketData = useCallback(data => {
    if (websocket) onDataRef.current({
      id,
      source: 'websocket',
      data: websocket.dataExtractor(data)
    });
  }, [onDataRef]);
  // NOTE: It's important that we track the websocket connected once down below. If the client renders the hooks with
  // the trigger key already present but before the
  useEffect(() => {
    if (!websocket?.enabled) {
      return;
    }
    // If there is nothing to trigger on return
    if (!triggerKey) {
      return;
    }
    // Only check dependencies if they were actually provided (non-empty array)
    // This prevents resubscription when dependencies defaults to [] but wasn't explicitly set
    const dependenciesPopulatedAndChanged = dependencies.length > 0 && !_.isEqual(prevDependencies, dependencies);
    // Future enhancement: Trigger polling when dependencies change
    // Unlike WebSocket resubscription, polling can safely use user.logged_in as a dependency
    // because each poll is a fresh HTTP request with current cookies. This could replace
    // manual useEffect polling patterns with declarative dependency tracking:
    // if (polling?.enabled && dependenciesChanged) {
    //   pollingExecutor.current?.schedulePoll()
    // }
    // Resubscribe when:
    // 1. The triggerKey changed (different listing IDs)
    // 2. Dependencies changed (e.g., feature flags, user preferences)
    //    NOTE: See WatcherDefinition.dependencies JSDoc for important limitations
    //    around auth state - can't use user.logged_in in WebSocket re-subscriptions as a restart is still required.
    // 3. WebSocket reconnected (after restart() on login or network drop)
    // 4. graphWebsocket instance replaced (mobile pattern: new instance with fresh auth)
    const triggerKeyChanged = prevTriggerKey !== triggerKey;
    const shouldResubscribe = triggerKeyChanged || dependenciesPopulatedAndChanged || shouldResubscribeOnReconnect;
    if (shouldResubscribe) {
      if (triggerKeyChanged) {
        onEvent('websocket:resubscribe:trigger_key_changed', {
          id
        });
      } else if (dependenciesPopulatedAndChanged) {
        onEvent('websocket:resubscribe:dependencies_changed', {
          id,
          prevDependencies,
          currentDependencies: dependencies
        });
      } else if (shouldResubscribeOnReconnect) {
        onEvent('websocket:resubscribe:graphWebsocket_instance_changed', {
          id
        });
      }
      // Guard against multiple resubscriptions from the same reconnect event.
      // The effect can fire multiple times if other deps change concurrently.
      if (shouldResubscribeOnReconnect && isResubscribing.current) {
        return;
      }
      if (shouldResubscribeOnReconnect) {
        isResubscribing.current = true;
      }
      // this comment down below is from previous implementation. Needs further investigation
      // TODO: Possible race condition here. Fix this.
      // update 11/28/2025, not sure exactly what's going on but sometimes I'll see a new websocket reconnect then stop, then reconnect
      // again possibly due to this line being triggered in situations where it shouldn't be, difficult to reproduce so leaving this comment
      // and if it comes up again, investigate further
      unsubscribe.current?.();
      websocket.graphWebsocket.subscribe({
        operationOptions: websocket.executor({
          subjectIds
        }),
        onData: onWebsocketData,
        onError: error => {
          onError?.({
            id,
            source: 'websocket',
            error
          });
          onEvent('websocket:subscription:error', {
            id,
            error
          });
          // If this is an auth error, mark the hadSubscriptionAuthError flag, this doesn't directly trigger a
          // resubscribe, but on reconnect we check this flag to set the shouldResubscribeOnReconnect flag
          // Cast to GraphQLError to check error structure
          const graphError = error;
          if (isAuthenticationError(graphError)) {
            hadSubscriptionAuthError.current = true;
            onEvent('websocket:subscription:auth_error_flagged', {
              id,
              error
            });
          }
        }
      }).then(graphSubscriber => {
        unsubscribe.current = () => {
          graphSubscriber.unsubscribe();
        };
        onEvent('websocket:subscribed', {
          graphSubscriber,
          id
        });
        // Reset the retry flag on successful subscription
        setShouldResubscribeOnReconnect(false);
        isResubscribing.current = false;
        return true;
      }).catch(err => {
        isResubscribing.current = false;
        return err;
      });
      onEvent('polling:preemptively_schedule', {
        id
      });
      pollingExecutor.current?.schedulePoll();
    }
  }, [triggerKey, id, onData, onError, onEvent, websocket, subjectIds, prevTriggerKey, onWebsocketData, dependencies, prevDependencies, shouldResubscribeOnReconnect]);
}

// https://stackoverflow.com/a/39473604
const isReactNative = () => typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
const getPlatform = () => isReactNative() ? 'mobile' : 'web';

/* istanbul ignore file */
const DEFAULT_BLUEPRINT_TAGS = [`platform:${getPlatform()}`];
const increment = (name, value = 1, tags = []) => {
  const newTags = [...tags, ...DEFAULT_BLUEPRINT_TAGS];
  instrument$1.increment(name, value, newTags);
};
const gauge = (name, value, tags = []) => {
  const newTags = [...tags, ...DEFAULT_BLUEPRINT_TAGS];
  instrument$1.gauge(name, value, newTags);
};
const blueprintInstrument = {
  increment,
  gauge
};

var instrument = /*#__PURE__*/Object.freeze({
    __proto__: null,
    default: blueprintInstrument,
    gauge: gauge,
    increment: increment
});

// @ts-ignore
const logger$i = coreLogger.forModule('blueprint:idempotency');
// this is used to do data shape check on attemptWrite, to resolve the possible race condition in current_user fields b/w polling or subscription
const CURRENT_USER_FIELDS_PREFIX = 'current_user_';
function createBlueprintBoundElement(element, utils, attemptWriteRef, attemptBatchWriteRef) {
  // We cast because this is the place where we transform to confirm to the type
  // Shallowly copy the element so we can augment it below. The original may be frozen.
  const boundElement = {
    ...element
  };
  boundElement.$ = boundElement.__methods__ = Object.fromEntries(Object.entries(utils).map(([k, f]) => [k,
  // Agdlkjlkasjl .. oh well, we move on
  // https://github.com/microsoft/TypeScript/issues/54707
  f.bind(null, boundElement)]));
  // @ts-ignore next
  boundElement[SPECIAL_SELF_REFERENCE] = {
    attemptWriteRef,
    attemptBatchWriteRef
  };
  boundElement.__history__ ||= {
    // This represents the total count of new elements we've constructed since the code mounted. You can also thing of
    // this like `accepted_change_count` or something like that and it's irrespective of the underlying reason _why_
    // soemthing changed.
    acceptedClientUpdateCounter: 0,
    lastAcceptedAt: Date.now(),
    previous: null
  };
  boundElement.__history__.acceptedClientUpdateCounter++;
  boundElement.__history__.lastAcceptedAt = Date.now();
  // So long as it's not the first write, we accept it. This is because we prefer to represent the first element as
  // having no previous history. It's important because all helper functions downstream will have to account for that
  // type.
  if (boundElement.__history__.acceptedClientUpdateCounter !== 1) ;
  return boundElement;
}
function useIdempotentBlueprintCollection(
// We needed to add the [] default value because we are using this hook in a disabled state for server-side rendering
initialCollection = [], {
  primaryKey = 'id',
  // The important thing about this key is that the resulting value be ordinal. Timestamps work by default because we
  // have a centralized server that places all writes in a global nicreasing order.
  //
  // NOTE: Do not use `updated_date` here. It does not behave consistently and will sometimes be undefined even when
  // writing the data. You have been warned.
  idempotencyKey = 'update_date',
  // These will be bound for partial application to each element in the collection. It's a pattern that allows for more
  // natural optimistic updates, keeping the state in sync so apps don't have to worry about it
  utils = {},
  // Overwiting the full element is a good default since it ensures not stale data is left behind from the previous
  // collection element. For collections where the update is small relative to the initial load and most of the data
  // should be static, merging is a good option.
  updateBehavior = 'overwrite',
  collectionName = 'blueprint:idempotentCollection',
  id = 'blueprint:idempotentCollection'
}) {
  const attemptWriteRef = useRef();
  const attemptBatchWriteRef = useRef();
  const [collectionIdempotencyTracking, setCollectionIdempotencyTracking] = useState(new Map());
  const [collection, setCollection] = useState({
    raw: initialCollection,
    mapped: initialCollection.reduce((acc, element) => acc.set(element[primaryKey], createBlueprintBoundElement(element, utils,
    // @ts-ignore next
    attemptWriteRef, attemptBatchWriteRef)), new Map())
  });
  /**
   * Here is the magic!
   *
   * What is special here is that the objects being different isn't solely what matter. When dealing with multiple
   * channels we need to account for the fact that at some point things will go wrong and the expected order of data
   * updates will become unexpected. We need to detect when we recieve stale date and drop it.
   *
   * Exactly-once is at-least-once coupled with some idempotency strategy. Our strategy is to use audit timestamps to
   * establish a global system order that we can rely on here. When we process a collection we need to detect and
   * silently drop collection items we already have. This can happen for a few different reasons, but the main one is
   * because we've processed a websocket message and polled after. In that case we should ignore all polling state if
   * nothing changed in between.
   *
   * This in intentionally called an 'attempt' because we might throw away the data if we detect it is stale.
   *
   * Exactly-one is at-least-once coupled with some idempotency strategy. Our strategy is to use audit timestamps to
   * establish a global system order that we can rely on.
   */
  const attemptWrite = useCallback(({
    data = [],
    source,
    force = false
  }) => {
    const shouldForceUpdate = source === 'local_optimistic_action' || force;
    // This is to track the reason for the update, as hasBeenUpdated is more a computed value now
    // it can have the following values null | force | key_advance | shape_delta
    let updatedReason = shouldForceUpdate ? 'force' : null;
    // Detect subset of data with differences
    // 1) Either we have more data that wasn't in the original collection, which happens on initial write, for example
    // 2) We get a partial list of updates from various sources -- polling, websocket, optimistic client update etc
    //
    // NOTE: Important to assert order here and not just the difference. If we see an event in the past the date
    // will be different but would be historical so we wouldn't want to write
    const changedElements = data.filter(Boolean).filter(element => {
      const previouslySeenAt = collectionIdempotencyTracking.get(element[primaryKey]);
      const hasUpdatedFromIdempotencyKey = !previouslySeenAt ||
      // We can do this cast because of the IdempotencyKey type
      new Date(element[idempotencyKey]) > new Date(previouslySeenAt);
      updatedReason = hasUpdatedFromIdempotencyKey ? 'key_advance' : updatedReason;
      let hasUpdatedDataShape = false;
      // Only do this current_user check if we aren't force updating or we have same updated date b/w polling or subscription
      // looking to resolve the race condition on current_user fields on subscription
      if (!hasUpdatedFromIdempotencyKey && !shouldForceUpdate) {
        const currentElementCurrentUserFields = _.pickBy(element, (_, key) => typeof key === 'string' && key?.startsWith(CURRENT_USER_FIELDS_PREFIX));
        const collectionElementCurrentUserFields = _.pickBy(collection.mapped.get(element[primaryKey]), (_, key) => typeof key === 'string' && key?.startsWith(CURRENT_USER_FIELDS_PREFIX));
        hasUpdatedDataShape = !_.isMatch(collectionElementCurrentUserFields, currentElementCurrentUserFields);
        updatedReason = hasUpdatedDataShape ? 'shape_delta' : updatedReason;
      }
      const hasBeenUpdated = hasUpdatedFromIdempotencyKey || hasUpdatedDataShape;
      logger$i.forModule(`${collectionName}:state:debug`).debug(element[primaryKey], ' => ', {
        hasBeenUpdated,
        shouldForceUpdate,
        updatedReason,
        existing: previouslySeenAt,
        new: element[idempotencyKey]
      });
      return hasBeenUpdated || shouldForceUpdate;
    });
    const upsertRequired = !_.isEmpty(changedElements);
    blueprintInstrument.increment('blueprint.idempotent_collection.write_attempt', 1, [`source:${source}`, `caused_change:${upsertRequired}`, `updated_reason:${updatedReason}`, `id:${id}`]);
    logger$i.forModule(collectionName).info(`Data Transport Effectiveness. source=${source} caused_change=${upsertRequired} updated_reason=${updatedReason}`);
    if (!upsertRequired) {
      return;
    }
    // NOTE: We tried using the ImmutableMap structure from immutable.js because it has the nice property
    // that one call call `.merge` and it will return a new reference with only the newly changed tuples modified.
    //
    // We had some others issues in map operations that we decided to stop that branch of work to focus on the core
    // reactive functionality.
    const updatedCollectionIdempotencyTracking = new Map(collectionIdempotencyTracking);
    changedElements.forEach(element => updatedCollectionIdempotencyTracking.set(element[primaryKey], element[idempotencyKey]));
    const updatedCollection = {
      raw: [],
      mapped: new Map(collection.mapped)
    };
    changedElements.forEach(element => {
      const currentElement = updatedCollection.mapped.get(element[primaryKey]);
      const newElement = updateBehavior === 'overwrite' ? element : _.mergeWith({}, currentElement, element, (_, srcValue) => {
        if (Array.isArray(srcValue)) {
          return srcValue;
        }
        return undefined;
      });
      updatedCollection.mapped.set(element[primaryKey], createBlueprintBoundElement(newElement, utils,
      // @ts-ignore next
      attemptWriteRef, attemptBatchWriteRef));
    });
    updatedCollection.raw = Array.from(updatedCollection.mapped.values()).map(({
      $,
      __methods__,
      __history__,
      ...element
    }) => element);
    setCollectionIdempotencyTracking(updatedCollectionIdempotencyTracking);
    setCollection(updatedCollection);
  }, [collection.mapped, collectionIdempotencyTracking, idempotencyKey, primaryKey, updateBehavior, utils]);
  const attemptBatchWrite = useCallback(batchedData => {
    let shouldUpdateState = false;
    // NOTE: We tried using the ImmutableMap structure from immutable.js because it has the nice property
    // that one call call `.merge` and it will return a new reference with only the newly changed tuples modified.
    //
    // We had some others issues in map operations that we decided to stop that branch of work to focus on the core
    // reactive functionality.
    const updatedCollectionIdempotencyTracking = new Map(collectionIdempotencyTracking);
    const updatedCollection = {
      raw: [],
      mapped: new Map(collection.mapped)
    };
    batchedData.forEach(data => {
      if (data.path && data.updateBehavior === 'overwrite') {
        data.data.forEach(element => {
          const collectionElement = updatedCollection.mapped.get(element[primaryKey]);
          const newElement = _.merge({}, collectionElement);
          if (data.path && element[data.path]) {
            newElement[data.path] = element[data.path];
            updatedCollection.mapped.set(element[primaryKey], createBlueprintBoundElement(newElement, utils,
            // @ts-ignore next
            attemptWriteRef, attemptBatchWriteRef));
            updatedCollectionIdempotencyTracking.set(element[primaryKey], element[idempotencyKey]);
            shouldUpdateState = true;
          }
        });
      } else if (!data.path && data.updateBehavior === 'overwrite') {
        data.data.forEach(element => {
          updatedCollection.mapped.set(element[primaryKey], createBlueprintBoundElement(element, utils,
          // @ts-ignore next
          attemptWriteRef, attemptBatchWriteRef));
          updatedCollectionIdempotencyTracking.set(element[primaryKey], element[idempotencyKey]);
          shouldUpdateState = true;
        });
      } else {
        const shouldForceUpdate = data.source === 'local_optimistic_action' || data.force;
        // This is to track the reason for the update, as hasBeenUpdated is more a computed value now
        // it can have the following values null | force | key_advance | shape_delta
        let updatedReason = shouldForceUpdate ? 'force' : null;
        // Detect subset of data with differences
        // 1) Either we have more data that wasn't in the original collection, which happens on initial write, for example
        // 2) We get a partial list of updates from various sources -- polling, websocket, optimistic client update etc
        //
        // NOTE: Important to assert order here and not just the difference. If we see an event in the past the date
        // will be different but would be historical so we wouldn't want to write
        const changedElements = data.data.filter(Boolean).filter(element => {
          const previouslySeenAt = collectionIdempotencyTracking.get(element[primaryKey]);
          const hasUpdatedFromIdempotencyKey = !previouslySeenAt ||
          // We can do this cast because of the IdempotencyKey type
          new Date(element[idempotencyKey]) > new Date(previouslySeenAt);
          updatedReason = hasUpdatedFromIdempotencyKey ? 'key_advance' : updatedReason;
          let hasUpdatedDataShape = false;
          // Only do this current_user check if we aren't force updating or we have same updated date b/w polling or subscription
          // looking to resolve the race condition on current_user fields on subscription
          if (!hasUpdatedFromIdempotencyKey && !shouldForceUpdate) {
            const currentElementCurrentUserFields = _.pickBy(element, (_, key) => typeof key === 'string' && key?.startsWith(CURRENT_USER_FIELDS_PREFIX));
            const collectionElementCurrentUserFields = _.pickBy(collection.mapped.get(element[primaryKey]), (_, key) => typeof key === 'string' && key?.startsWith(CURRENT_USER_FIELDS_PREFIX));
            hasUpdatedDataShape = !_.isMatch(collectionElementCurrentUserFields, currentElementCurrentUserFields);
            updatedReason = hasUpdatedDataShape ? 'shape_delta' : updatedReason;
          }
          const hasBeenUpdated = hasUpdatedFromIdempotencyKey || hasUpdatedDataShape;
          logger$i.forModule(`${collectionName}:state:debug`).debug(element[primaryKey], ' => ', {
            hasBeenUpdated,
            shouldForceUpdate,
            updatedReason,
            existing: previouslySeenAt,
            new: element[idempotencyKey]
          });
          return hasBeenUpdated || shouldForceUpdate;
        });
        const upsertRequired = !_.isEmpty(changedElements);
        shouldUpdateState = upsertRequired;
        blueprintInstrument.increment('blueprint.idempotent_collection.write_attempt', 1, [`source:${data.source}`, `caused_change:${upsertRequired}`, `updated_reason:${updatedReason}`, `id:${id}`]);
        logger$i.forModule(collectionName).info(`Data Transport Effectiveness. source=${data.source} caused_change=${upsertRequired} updated_reason=${updatedReason}`);
        if (!upsertRequired) {
          return;
        }
        changedElements.forEach(element => updatedCollectionIdempotencyTracking.set(element[primaryKey], element[idempotencyKey]));
        changedElements.forEach(element => {
          const currentElement = updatedCollection.mapped.get(element[primaryKey]);
          const newElement = _.merge({}, currentElement, element);
          updatedCollection.mapped.set(element[primaryKey], createBlueprintBoundElement(newElement, utils,
          // @ts-ignore next
          attemptWriteRef, attemptBatchWriteRef));
        });
      }
    });
    updatedCollection.raw = Array.from(updatedCollection.mapped.values()).map(({
      $,
      __methods__,
      __history__,
      ...element
    }) => element);
    if (shouldUpdateState) {
      setCollectionIdempotencyTracking(updatedCollectionIdempotencyTracking);
      setCollection(updatedCollection);
    }
  }, [collection.mapped, collectionIdempotencyTracking, idempotencyKey, primaryKey, updateBehavior, utils]);
  const reset = useCallback((newData = []) => {
    // Clear the idempotency tracking
    setCollectionIdempotencyTracking(new Map());
    // Reset the collection with new data
    const newCollection = {
      raw: newData,
      mapped: newData.reduce((acc, element) => acc.set(element[primaryKey], createBlueprintBoundElement(element, utils,
      // @ts-ignore next
      attemptWriteRef, attemptBatchWriteRef)), new Map())
    };
    setCollection(newCollection);
    // Update idempotency tracking for new data
    const newIdempotencyTracking = new Map();
    newData.forEach(element => newIdempotencyTracking.set(element[primaryKey], element[idempotencyKey]));
    setCollectionIdempotencyTracking(newIdempotencyTracking);
  }, [primaryKey, idempotencyKey, utils]);
  attemptWriteRef.current = attemptWrite;
  attemptBatchWriteRef.current = attemptBatchWrite;
  // const attemptWriteRef = useRef(attemptWrite)
  useEffect(() => {
    attemptWriteRef.current = attemptWrite;
  }, [attemptWrite]);
  useEffect(() => {
    attemptBatchWriteRef.current = attemptBatchWrite;
  }, [attemptBatchWrite]);
  useEffect(() => {
    attemptWriteRef.current?.({
      data: initialCollection,
      source: 'initial_mount'
    });
  }, [initialCollection]);
  return {
    collection,
    attemptWrite,
    attemptBatchWrite,
    reset
  };
}

/**
 * useIsomorphicLayoutEffect
 *
 * A hook that uses useLayoutEffect on the client and useEffect on the server.
 * This prevents SSR warnings about useLayoutEffect not being available during server rendering.
 *
 * @see https://react.dev/reference/react/useLayoutEffect#caveats
 */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const logger$h = coreLogger.forModule('blueprint:localstorage');
function useLocalStorage({
  key,
  initialValue,
  onValueSet,
  deviceStorage
}) {
  const getValueFromLocalStorage = useCallback(async () => {
    try {
      const item = await deviceStorage.get(key);
      if (!_.isEmpty(item)) {
        // TODO: add zod schema validation here ?
        // should it be here in the hook or in
        // the LocalStorageAdapter/DeviceStorage ?
        return JSON.parse(String(item));
      }
    } catch (error) {
      logger$h.error(error);
    }
    return initialValue;
  }, [initialValue, key, deviceStorage]);
  const [storedValue, setStoredValue] = useState(initialValue);
  // Mirrors storedValue so async callbacks (hydration, storage events) can
  // skip no-op updates: a fresh ref for a deep-equal value would retrigger
  // consumer effects keyed on the value.
  const storedValueRef = useRef(storedValue);
  useEffect(() => {
    storedValueRef.current = storedValue;
  }, [storedValue]);
  // Bumped on every local update (setValue, storage event). Hydration takes
  // a ticket before its async read and only applies the result if nothing
  // updated in between — otherwise the stale snapshot would clobber the
  // newer value (state reverts while storage keeps the write).
  const updatesRef = useRef(0);
  // it's important to memo initialValue in the parent hook
  // so that new ref isn't passed in causing getValueFromLocalStorage to be
  // recreated and cause this useEffect go into inifinite loop
  useEffect(() => {
    const handleEnabledChange = async () => {
      const ticket = updatesRef.current;
      const value = await getValueFromLocalStorage();
      if (ticket === updatesRef.current && !_.isEmpty(value) && !_.isEqual(value, storedValueRef.current)) {
        setStoredValue(value);
      }
    };
    handleEnabledChange().catch(e => logger$h.error(e));
  }, [getValueFromLocalStorage]);
  const setValue = useCallback(async value => {
    try {
      // Set before publish so the same-window storage event (own-publish
      // loopback) is recognized as a no-op by storageEventListener.
      storedValueRef.current = value;
      updatesRef.current += 1;
      // TODO: add zod schema validation here ?
      await deviceStorage.set(key, JSON.stringify(value));
      setStoredValue(value);
      if (typeof onValueSet === 'function') {
        onValueSet(value, key);
      }
    } catch (error) {
      // The write failed: restore the mirror so future events are not
      // mistakenly skipped as duplicates of a value that never persisted.
      storedValueRef.current = storedValue;
      logger$h.error(error);
    }
  }, [key, onValueSet, deviceStorage, setStoredValue, storedValue]);
  const storageEventListener = useCallback(event => {
    if (key === event.key && event.newValue !== event.oldValue) {
      const newValue = JSON.parse(String(event.newValue)) || initialValue;
      if (_.isEqual(newValue, storedValueRef.current)) {
        return;
      }
      updatesRef.current += 1;
      setStoredValue(newValue);
      if (typeof onValueSet === 'function') {
        onValueSet(newValue, key);
      }
    }
  }, [initialValue, key, onValueSet]);
  // Listen for changes to this local storage value made from other windows.
  useIsomorphicLayoutEffect(() => {
    const unsubscribe = deviceStorage.subscribe(storageEventListener);
    return () => unsubscribe();
  }, [deviceStorage, storageEventListener]);
  return [storedValue, setValue];
}

// Sentinel used internally to distinguish "no previous value" (initial mount) from
// an actual value of undefined/null. This prevents transition callbacks from firing
// spuriously on the first render.
const INITIAL = Symbol('@adc/blueprint.primitives.initial');
/**
 * Fires side-effect callbacks whenever a scalar field on an object transitions
 * between specific values.
 *
 * Intended for non-UI concerns triggered by state changes inside an auction
 * blueprint collection: analytics events, sound effects, toast notifications, etc.
 *
 * ## Mount timing
 * This hook intentionally does not fire on the first render — the initial value is
 * recorded as the baseline and only subsequent changes trigger callbacks. However,
 * this only holds if the element already has meaningful data when the component
 * mounts. If a field starts as `undefined` (e.g. segment data hasn't loaded yet)
 * and later gains a value, that `undefined → X` transition WILL fire because from
 * the hook's perspective it is a genuine change.
 *
 * To avoid spurious callbacks on initial data load, gate the mount of the component
 * using this hook on the data being present first. For example, if watching an
 * `OnlineAuctionSegment`, don't render the watcher component until `segment_status`
 * is non-null — that guarantees the first render sees a fully-populated baseline.
 */
function useBoundElementTransition(element, field, transitions) {
  // Tracks the previous value of the field across renders. Seeded with INITIAL
  // so that we can skip firing callbacks on the very first render.
  const prevValueRef = useRef(INITIAL);
  // Keep a ref to the latest transitions array so the effect closure is never
  // stale, without needing to include the array itself in the dep list (which
  // would change identity every render and cause unnecessary re-runs).
  const transitionsRef = useRef(transitions);
  transitionsRef.current = transitions;
  // Support dot fields so long as they are scalars
  const currentValue = _.get(element, field);
  useEffect(() => {
    const previous = prevValueRef.current;
    // Initial mount — record the starting value but don't fire any transitions.
    // There is no meaningful "from" state on first render.
    if (previous === INITIAL) {
      prevValueRef.current = currentValue;
      return;
    }
    prevValueRef.current = currentValue;
    // If the value didn't change, nothing to do. This is only for scalar leaf values.
    if (currentValue === previous) return;
    for (const [[from, to], callback] of transitionsRef.current) {
      const fromMatches = from === '*' || from === previous;
      const toMatches = to === '*' || to === currentValue;
      if (fromMatches && toMatches) {
        callback();
      }
    }
  }, [currentValue]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Polls the session-refresh endpoint to detect when the user's session has
 * gone stale (token expired, reverted to anonymous) or when the deployed
 * version has changed. In either case the supplied `onSessionEnd` callback is
 * invoked. The callback is injected rather than imported so this primitive
 * stays free of any app/browser-specific logout concerns.
 */
const performFreshnessCheck = async (loggedInRef, onSessionEnd) => {
  let logout = false;
  let versionMismatch = false;
  try {
    const response = await fetch__default.get(configment.get('URL_BASE_PREFIX', '') + '/fdn/session-refresh');
    const body = response.body;
    const sessionKey = body?.authentication?.session_key || 'anon:';
    const isLoggedIn = !sessionKey.startsWith('anon:');
    const pageDeploymentVersion = window.INITIAL_STATE?.deployment?.current_version;
    const serverDeploymentVersion = body?.deployment?.current_version;
    // If the deployment version is known and has changed, log out.
    versionMismatch = pageDeploymentVersion && serverDeploymentVersion && pageDeploymentVersion !== serverDeploymentVersion;
    logout =
    // If the token has expired, we will have reverted to an anonymous session, so log out.
    loggedInRef.current.wasLoggedIn && !isLoggedIn || versionMismatch;
    loggedInRef.current.wasLoggedIn = isLoggedIn;
  } catch (e) {
    // The service used to throw a 403 when the token expired, so we log out for backwards compatibility.
    logout = e.status === 403;
  }
  // If a timeout ID is already set, we needn't set another one, even if we've decided we need to log out.
  if (logout && !loggedInRef.current.loggingOut) {
    loggedInRef.current.loggingOut = true;
    // In case of a version mismatch, we want to delay the page refresh by a random amount of time of up to 10 minutes
    // to avoid refreshing all users' pages within two minutes (the refresh interval), because that hammers the FE
    // service that sent them this page.
    const delayMS = versionMismatch ? Math.round(Math.random() * ms(configment.get('FRESHNESS_CHECK_LOGOUT_DELAY', '10m'))) : 0;
    setTimeout(() => onSessionEnd(window.location.pathname), delayMS);
  }
};

/**
 * Periodically checks whether the current session is still fresh and triggers
 * the supplied `onSessionEnd` callback when it is not. The callback is injected
 * so this primitive stays free of app/browser-specific logout concerns.
 */
const useFreshnessDetection = (user, onSessionEnd) => {
  // We use this ref with the interval below to remember whether the user was logged in the last time we checked.
  const loggedInRef = useRef({
    wasLoggedIn: user.logged_in,
    loggingOut: false
  });
  useInterval(() => {
    performFreshnessCheck(loggedInRef, onSessionEnd);
  }, ms('2m'));
};

/**
 * Shared client-side upload/ingest request lifecycle.
 *
 * Modeled as a const-object "enum": use `UploadStatus.Uploading` for member
 * access, while the type stays the underlying string union so consumers can
 * still compare against the literal (`status === 'uploading'`) without importing
 * the enum. No runtime reverse-map / nominal-typing friction of a TS `enum`.
 */
const UploadStatus = {
  Idle: 'idle',
  Uploading: 'uploading',
  Success: 'success',
  Failed: 'failed'
};
var TargetAudience;
(function (TargetAudience) {
  TargetAudience["SELLER"] = "SELLER";
  TargetAudience["BUYER"] = "BUYER";
  TargetAudience["AUCTION_STAFF"] = "AUCTION_STAFF";
})(TargetAudience || (TargetAudience = {}));

var index$m = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DeviceStorageAdapter: DeviceStorageAdapter,
    GraphWebsocket: GraphWebsocket,
    NullDeviceStorage: NullDeviceStorage,
    NullGraphWebsocket: NullGraphWebsocket,
    get TargetAudience () { return TargetAudience; },
    UploadStatus: UploadStatus,
    WebLocalStorage: WebLocalStorage,
    useBoundElementTransition: useBoundElementTransition,
    useFreshnessDetection: useFreshnessDetection,
    useIdempotentBlueprintCollection: useIdempotentBlueprintCollection,
    useLocalStorage: useLocalStorage,
    useRealtimeWatcher: useRealtimeWatcher
});

var WhatChangedFields;
(function (WhatChangedFields) {
  WhatChangedFields["paymentType"] = "/terms/payment_type";
  WhatChangedFields["offerAmount"] = "/terms/offer_amount";
  WhatChangedFields["downPaymentAmount"] = "/terms/down_payment_amount";
  WhatChangedFields["earnestMoneyDepositAmount"] = "/terms/earnest_money_deposit_amount";
  WhatChangedFields["closeEscrowDuration"] = "/terms/close_escrow_duration";
  WhatChangedFields["leasebackDuration"] = "/terms/leaseback_duration";
  WhatChangedFields["leasebackAmount"] = "/terms/leaseback_amount";
  WhatChangedFields["sellerConcessionAmount"] = "/terms/seller_concession_amount";
  WhatChangedFields["commission"] = "/terms/commission/0";
  WhatChangedFields["agentCommissionAmount"] = "/terms/commission/0/agent_commission_amount";
  WhatChangedFields["paidBy"] = "/terms/commission/0/paid_by";
  WhatChangedFields["inspectionContingencyPeriodDuration"] = "/terms/inspection_contingency_period_duration";
  WhatChangedFields["optionPeriodDuration"] = "/terms/option_period_duration";
  WhatChangedFields["optionPeriodCostAmount"] = "/terms/option_period_cost_amount";
  WhatChangedFields["financingContingencyDuration"] = "/terms/financing_contingency_duration";
  WhatChangedFields["appraisalContingencyDuration"] = "/terms/appraisal_contingency_duration";
  WhatChangedFields["additionalNotes"] = "/terms/notes/additional_notes";
})(WhatChangedFields || (WhatChangedFields = {}));

// TODO: This is an issue. Nothing about this is specific to state deposit, this is related to the
// concept of an AuctionExpression which is a primitive, not specific to bidding
var AuctionExpressionRule;
(function (AuctionExpressionRule) {
  AuctionExpressionRule["Percentage"] = "PERCENTAGE";
  AuctionExpressionRule["Flat"] = "FLAT";
  AuctionExpressionRule["PercentageOrMinimum"] = "PERCENTAGE_OR_MINIMUM";
  AuctionExpressionRule["PercentageOrMaximum"] = "PERCENTAGE_OR_MAXIMUM";
  AuctionExpressionRule["PercentageOrMinimumAndMaximum"] = "PERCENTAGE_OR_MINIMUM_AND_MAXIMUM";
})(AuctionExpressionRule || (AuctionExpressionRule = {}));

var types$8 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get AuctionExpressionRule () { return AuctionExpressionRule; },
    get WhatChangedFields () { return WhatChangedFields; }
});

var _staticBlock$B;
const shortenFlatLabel = label => {
  if (typeof label !== 'string') return null;
  const auctionExpression = AuctionExpression.parse(label);
  if (auctionExpression) {
    const values = auctionExpression.getValues();
    return values?.shortenedLabel ?? label;
  }
  return label;
};
const PERCENTAGE_AMOUNT = '(?<percentage>[1-9]\\d{0,1}(\\.\\d+)?|100)%';
const AMOUNT = '\\d+([,]\\d+)*(\\.\\d+)?';
const FLAT_AMOUNT = `\\$(?<flat>${AMOUNT})`;
const MINIMUM_AMOUNT = `\\$(?<minimumAmount>${AMOUNT}) minimum`;
const MAXIMUM_AMOUNT = `\\$(?<maximumAmount>${AMOUNT}) maximum`;
const FLAT_REGEX = new RegExp(`^${FLAT_AMOUNT}$`, 'i');
const PERCENTAGE_REGEX = new RegExp(`^${PERCENTAGE_AMOUNT}$`, 'i');
const PERCENTAGE_OR_MINIMUM_REGEX = new RegExp(`^${PERCENTAGE_AMOUNT} or ${MINIMUM_AMOUNT}$`, 'i');
const PERCENTAGE_OR_MAXIMUM_REGEX = new RegExp(`^${PERCENTAGE_AMOUNT} or ${MAXIMUM_AMOUNT}$`, 'i');
const PERCENTAGE_OR_MINIMUM_AND_MAXIMUM_REGEX = new RegExp(`^${PERCENTAGE_AMOUNT} or ${MINIMUM_AMOUNT} and ${MAXIMUM_AMOUNT}$`, 'i');
const calculatePercentageAmount = (amount, percentage) => parseFloat((amount / 100 * parseFloat(percentage)).toFixed(2));
const shortenMoneyLabel = label => {
  const number = parseInt(label.replace(/,/g, ''), 10);
  // Check if it's a valid number
  if (!isNaN(number)) {
    // If it's an exact multiple of 1000, format as 'k', otherwise, keep the original number
    const formattedNumber = number % 1000 === 0 ? `$${number / 1000}k` : `$${number}`;
    return formattedNumber;
  }
  return label;
};
const flatParser = rule => {
  if (!FLAT_REGEX.test(rule)) return null;
  const {
    flat
  } = FLAT_REGEX.exec(rule)?.groups || {};
  if (!flat) return null;
  return parseFloat(flat);
};
const flatValuesParser = rule => {
  const flatParserResult = flatParser(rule);
  if (!flatParserResult) return null;
  const label = formatMoney$2(flatParserResult);
  return {
    label,
    type: AuctionExpressionRule.Flat,
    shortenedLabel: shortenMoneyLabel(`${flatParserResult}`)
  };
};
const percentageParser = (rule, amount) => {
  if (!PERCENTAGE_REGEX.test(rule)) return null;
  const {
    percentage
  } = PERCENTAGE_REGEX.exec(rule)?.groups || {};
  if (!percentage) return null;
  return calculatePercentageAmount(amount, percentage);
};
const percentageValuesParser = rule => {
  if (!PERCENTAGE_REGEX.test(rule)) return null;
  const {
    percentage
  } = PERCENTAGE_REGEX.exec(rule)?.groups || {};
  if (!percentage) return null;
  return {
    label: rule,
    shortenedLabel: `${parseInt(percentage)}%`,
    type: AuctionExpressionRule.Percentage
  };
};
const percentageOrMinimumParser = (rule, amount) => {
  if (!PERCENTAGE_OR_MINIMUM_REGEX.test(rule)) return null;
  const {
    percentage,
    minimumAmount
  } = PERCENTAGE_OR_MINIMUM_REGEX.exec(rule)?.groups || {};
  if (!percentage || !minimumAmount) return null;
  const calculatedAmount = calculatePercentageAmount(amount, percentage);
  const parsedMinimumAmount = parseFloat(minimumAmount);
  return calculatedAmount < parsedMinimumAmount ? parsedMinimumAmount : calculatedAmount;
};
const percentageOrMinimumValuesParser = rule => {
  if (!PERCENTAGE_OR_MINIMUM_REGEX.test(rule)) return null;
  const {
    percentage,
    minimumAmount
  } = PERCENTAGE_OR_MINIMUM_REGEX.exec(rule)?.groups || {};
  if (!percentage || !minimumAmount) return null;
  return {
    label: `${percentage}% or $${minimumAmount} (whichever is greater)`,
    shortenedLabel: `${parseInt(percentage)}% or ${shortenMoneyLabel(minimumAmount)}`,
    type: AuctionExpressionRule.PercentageOrMinimum
  };
};
// TODO: This hasn't been implemented yet
const percentageOrMaximumParser = (rule, amount) => {
  if (!PERCENTAGE_OR_MAXIMUM_REGEX.test(rule)) return null;
  const {
    percentage,
    maximumAmount
  } = PERCENTAGE_OR_MAXIMUM_REGEX.exec(rule)?.groups || {};
  if (!percentage || !maximumAmount) return null;
  const calculatedAmount = calculatePercentageAmount(amount, percentage);
  const parsedMaximumAmount = parseFloat(maximumAmount);
  return calculatedAmount > parsedMaximumAmount ? parsedMaximumAmount : calculatedAmount;
};
// TODO: This hasn't been implemented yet
const percentageOrMaximumValuesParser = rule => {
  if (!PERCENTAGE_OR_MAXIMUM_REGEX.test(rule)) return null;
  const {
    percentage,
    maximumAmount
  } = PERCENTAGE_OR_MAXIMUM_REGEX.exec(rule)?.groups || {};
  if (!percentage || !maximumAmount) return null;
  return {
    label: `${percentage}% or $${maximumAmount} (whichever is less)`,
    shortenedLabel: `${parseInt(percentage)}% or ${shortenMoneyLabel(maximumAmount)} maximum`,
    type: AuctionExpressionRule.PercentageOrMaximum
  };
};
// TODO: This hasn't been implemented yet
const percentageOrMinimumAndMaximumParser = (rule, amount) => {
  if (!PERCENTAGE_OR_MINIMUM_AND_MAXIMUM_REGEX.test(rule)) return null;
  const {
    percentage,
    minimumAmount,
    maximumAmount
  } = PERCENTAGE_OR_MINIMUM_AND_MAXIMUM_REGEX.exec(rule)?.groups || {};
  if (!percentage || !minimumAmount || !maximumAmount) return null;
  const calculatedAmount = calculatePercentageAmount(amount, percentage);
  const parsedMinimumAmount = parseFloat(minimumAmount);
  const parsedMaximumAmount = parseFloat(maximumAmount);
  if (calculatedAmount < parsedMinimumAmount) return parsedMinimumAmount;
  if (calculatedAmount > parsedMaximumAmount) return parsedMaximumAmount;
  return calculatedAmount;
};
// TODO: This hasn't been implemented yet
const percentageOrMinimumAndMaximumValuesParser = rule => {
  if (!PERCENTAGE_OR_MINIMUM_AND_MAXIMUM_REGEX.test(rule)) return null;
  const {
    percentage,
    minimumAmount,
    maximumAmount
  } = PERCENTAGE_OR_MINIMUM_AND_MAXIMUM_REGEX.exec(rule)?.groups || {};
  if (!percentage || !minimumAmount || !maximumAmount) return null;
  return {
    label: `${percentage}% or between $${minimumAmount} and $${maximumAmount}`,
    shortenedLabel: `${parseInt(percentage)}% or between ${shortenMoneyLabel(minimumAmount)} and ${shortenMoneyLabel(maximumAmount)}`,
    type: AuctionExpressionRule.PercentageOrMinimumAndMaximum
  };
};
const AuctionExpressionsMap = new Map();
class AuctionExpression {
  static parse(expression) {
    if (AuctionExpressionsMap.has(expression)) {
      return AuctionExpressionsMap.get(expression);
    }
    const newAuctionExpression = new AuctionExpression(expression);
    AuctionExpressionsMap.set(expression, newAuctionExpression);
    return newAuctionExpression;
  }
  constructor(expression) {
    this.expression = expression?.trim();
  }
  evaluate(amount) {
    // TODO: This should be a bug
    if (!this.expression) return null;
    return AuctionExpression.PARSER_PRECEDENCE.map(parser => parser(this.expression, amount)).find(Boolean);
  }
  getValues() {
    if (!this.expression) return null;
    return AuctionExpression.VALUES_PARSER_PRECEDENCE.map(parser => parser(this.expression)).find(Boolean);
  }
  static #_ = _staticBlock$B = () => (this.PARSER_PRECEDENCE = [flatParser, percentageParser, percentageOrMinimumParser, percentageOrMaximumParser, percentageOrMinimumAndMaximumParser], this.VALUES_PARSER_PRECEDENCE = [flatValuesParser, percentageValuesParser, percentageOrMinimumValuesParser, percentageOrMaximumValuesParser, percentageOrMinimumAndMaximumValuesParser]);
}
_staticBlock$B();

const CERTIFIED_FUNDS_STATES = ['WA', 'OR', 'CA', 'AZ', 'NV', 'ID', 'TX', 'AR', 'NE', 'TN', 'MS', 'AL', 'NC', 'VA', 'WV', 'NY', 'NJ', 'GA', 'MO'];
const globalPhysicalStructures = [{
  value: '4PLEX_BUILDING',
  text: '4 Plex Building',
  adcSearchMapping: 'Single-family'
}, {
  value: '4PLEX_UNIT',
  text: '4 Plex Unit',
  adcSearchMapping: 'Single-family'
}, {
  value: '3PLEX_UNIT',
  text: '3Plex Unit',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'FOUR_PLEX_BUILDING',
  text: '4 Plex Building',
  adcSearchMapping: 'Single-family'
}, {
  value: 'FOUR_PLEX_UNIT',
  text: '4 Plex Unit',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'COMMERCIAL',
  text: 'Commercial',
  adcSearchMapping: 'Single-family'
}, {
  value: 'THREE_PLEX_BUILDING',
  text: '3 Plex building',
  adcSearchMapping: 'Single-family'
}, {
  value: 'APARTMENT_BUILDING',
  text: 'Apartment Building',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'APARTMENT_COMPLEX',
  text: 'Apartment Complex',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'APARTMENT_UNIT',
  text: 'Apartment Unit',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'ATTACHED_GARAGE',
  text: 'Attached Garage',
  adcSearchMapping: 'Single-family'
}, {
  value: 'CONDOMINIUM_BUILDING',
  text: 'Condominium Building',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'CONDOMINIUM_UNIT',
  text: 'Condominium Unit',
  adcSearchMapping: 'Single-family'
}, {
  value: 'COOP_BUILDING',
  text: 'Co-op Building',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'COOP_UNIT',
  text: 'Co-op Unit',
  adcSearchMapping: 'Single-family'
}, {
  value: 'DETACHED_GARAGE',
  text: 'Detached Garage',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'DUPLEX_BUILDING',
  text: 'Duplex Building',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'DUPLEX_UNIT',
  text: 'Duplex Unit',
  adcSearchMapping: 'Single-family'
}, {
  value: 'FLOOR',
  text: 'Floor',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'HI_RISE_CONDO_UNIT',
  text: 'High-Rise Condo Unit',
  adcSearchMapping: 'Single-family'
}, {
  value: 'LAND',
  text: 'Land',
  adcSearchMapping: 'Land'
}, {
  value: 'LOFT',
  text: 'Loft',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'MANUFACTURED_HOME',
  text: 'Manufactured Home',
  adcSearchMapping: 'Single-family'
}, {
  value: 'MOBILE_HOME',
  text: 'Mobile Home',
  adcSearchMapping: 'Single-family'
}, {
  value: 'MODULAR_HOME',
  text: 'Modular Home',
  adcSearchMapping: 'Single-family'
}, {
  value: 'MULTI_UNIT_BUILDING',
  text: 'Multi Unit Building',
  adcSearchMapping: 'Single-family'
}, {
  value: 'OTHER',
  text: 'Other',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'PUD_HOME',
  text: 'PUD Home',
  adcSearchMapping: 'Single-family'
}, {
  value: 'SINGLE_FAMILY_ATTACHED',
  text: 'Single Family Attached',
  adcSearchMapping: 'Single-family'
}, {
  value: 'SINGLE_FAMILY_DETACHED',
  text: 'Single Family Detached',
  adcSearchMapping: 'Single-family'
}, {
  value: 'SINGLE_FAMILY_HOME',
  text: 'Single Family Home',
  adcSearchMapping: 'Single-family'
}, {
  value: 'STUDIO',
  text: 'Studio',
  adcSearchMapping: 'Single-family'
}, {
  value: 'TOWNHOUSE',
  text: 'Townhouse',
  adcSearchMapping: 'Single-family'
}, {
  value: 'THREE_PLEX_UNIT',
  text: 'Triplex',
  adcSearchMapping: 'Condos & Townhouses'
}, {
  value: 'TWIN_HOME',
  text: 'Twin Home',
  adcSearchMapping: 'Single-family'
}, {
  value: 'ROW_HOUSE',
  text: 'Row House',
  adcSearchMapping: 'Single-family'
}];

var constants$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    CERTIFIED_FUNDS_STATES: CERTIFIED_FUNDS_STATES,
    globalPhysicalStructures: globalPhysicalStructures
});

/* istanbul ignore file */
// This is useful if you want to render presentation states for an enum and want decent default behavior for if backend
// adds new values to the schema without the app updating at the same time.
const formatEnum$1 = enumVal => (enumVal || '').split('_').map(_.capitalize).join(' ');
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
const formatDate$2 = (date, format) => moment$1(date).format(format || DEFAULT_DATE_FORMAT);
/**
 * This is an add-on for text mask inputs
 * allows users to define custom masks for number inputs
 * full reference on how it works https://github.com/text-mask/text-mask/tree/master/addons/#readme
 */
const phoneNumberMask = ['(', /[2-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
const formatPhoneNumber = value => {
  const digits = (value + '').replace(/[^\d]/g, '');
  // NOTE: This can cause weird results if QA randomized these numbers because it's possible to get numbers with
  // a leading 0, in which case `parseInt` will strip it from the result and cause this condition to fail and the
  // formatting not to be added
  return digits.length === 10 && digits === '' + parseInt(digits, 10) ? conformToMask(digits, phoneNumberMask).conformedValue : value;
};
const formatMoney$1 = (val, opts) => {
  const {
    defaultValue = '',
    hideDecimals = false,
    overrides = null
  } = opts || {};
  const numVal = typeof val === 'string' ? parseFloat(val) : val;
  if (Number.isFinite(numVal)) {
    const number = hideDecimals ? Math.floor(numVal) : numVal;
    const minimumFractionDigits = Number.isInteger(number) ? 0 : 2;
    return number.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits,
      ...(overrides || {})
    });
  }
  if (val === null || val === undefined || val === '' || Number.isNaN(numVal) || !Number.isFinite(numVal)) {
    return defaultValue;
  }
  // Return non-numeric values as-is (for backward compatibility with core tests)
  return val;
};
const formatNumber$1 = (val, overrides, defaultValue = '') => {
  if (val === null || val === undefined) {
    return defaultValue;
  }
  const numVal = typeof val === 'string' ? parseFloat(val) : val;
  if (Number.isFinite(numVal)) {
    return numVal.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...(overrides || {})
    });
  }
  return defaultValue;
};

var formatters = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DEFAULT_DATE_FORMAT: DEFAULT_DATE_FORMAT,
    formatDate: formatDate$2,
    formatEnum: formatEnum$1,
    formatMoney: formatMoney$1,
    formatNumber: formatNumber$1,
    formatPhoneNumber: formatPhoneNumber
});

// TODO: current_user_highest_bid should be available for Buyer users,
// so we need to include it only for Buyer users
// TODO @mpillegi or @ecuevas please remove duplicated fields in current_user_highest_bid from here, we should not be doing that as those are intended to be used in the focusedListing query
const AUTHENTICATED_USER_ONLINE_AUCTION_SEGMENT_FIELDS = gql$5`
  fragment AuthenticatedUserOnlineAuctionSegmentFragment on OnlineAuctionSegment {
    __typename
    current_reserve_amount @include(if: $isSellerAudience)
    match_bid_count
    bid_count
    buyer_bid_count
    current_user_highest_bid {
      record_status
      listing_id
      bid_amount
      bid_id
      bidder_party_id
      type
      version
      updated_date
      rank
      ...OnlineAuctionSegmentBidTermsFragment
      negotiation_sequence {
        latest_counter_bid {
          listing_id
          bid_amount
          bid_id
          bidder_party_id
          type
          version
          updated_date
          ...OnlineAuctionSegmentBidTermsFragment
        }
      }
    }
    next_bid_amount
    current_user_max_bid {
      current_user_next_max_bid_amount
      listing_id
      max_bid_amount
      record_status
      updated_date
    }
    current_user_bidding_status
    is_current_user_registered_for_online_auction
  }
`;
// TODO we'll probably end up using closing_method, when it is completely developed adapt this by removing winning_bidder_user and including this field
const SELLER_EXCLUSIVE_ONLINE_AUCTION_SEGMENT_FIELDS = gql$5`
  fragment SellerExclusiveOnlineAuctionSegmentFragment on OnlineAuctionSegment {
    __typename
    latest_relevant_action {
      online_segment_id
      latest_action
      action_date
    }

    current_highest_buyer_bid {
      bid_amount
    }
    current_reserve_amount @include(if: $hasAuthenticatedUser)
    seller_counter_bid_count
    configuration @include(if: $hasAuthenticatedUser) {
      review_date
      review_date_updatable_by_date
      is_highest_and_best_round_allowed
    }
    result {
      winning_bid @include(if: $hasAuthenticatedUser) {
        bid_id
      }
      winning_bid_amount @include(if: $hasAuthenticatedUser)
      winning_bidder_user @include(if: $hasAuthenticatedUser) {
        first_name
        last_name
        email
        party {
          phones {
            phone_id
            number
          }
        }
      }
    }
  }
`;
const ONLINE_AUCTION_SEGMENT_BID_TERMS = gql$5`
  fragment OnlineAuctionSegmentBidTermsFragment on OnlineAuctionSegmentBid {
    terms {
      seller_net_offer_amount
      buyer_net_offer_amount
      market_place_fee_amount
      market_place_fee_percentage
      status
      offer_amount
      offeror_type
      buyers_premium_amount
      seller_concession_amount
      earnest_money_deposit_amount
      down_payment_amount
      payment_type
      close_escrow_duration
      leaseback_amount
      leaseback_duration
      option_period_duration
      option_period_cost_amount
      inspection_contingency_period_duration
      is_agent_included
      financing_contingency_duration
      appraisal_contingency_duration
      seller_concession_amount
      notes {
        additional_notes
        latest_update_reason
      }
      expiration_date
      commission {
        paid_by
        agent_commission_amount
      }
      documents {
        document_id
        document_purpose_type
        urn
        document {
          file_descriptor {
            content_type
          }
        }
      }
    }
  }
`;
const ONLINE_AUCTION_SEGMENT_BIDS_RANKING = gql$5`
  fragment OnlineAuctionSegmentBidsRankingFragment on OnlineAuctionSegment {
    bids_ranking @include(if: $hasAuthenticatedUser) {
      __typename
      listing_id
      online_segment_id
      ranking {
        belongs_to_current_user @include(if: $hasAuthenticatedUser)
        is_viewable_by_current_user @include(if: $hasAuthenticatedUser)
        overtake_delta @include(if: $hasAuthenticatedUser)
        net_offer_equal_delta @include(if: $hasAuthenticatedUser)
        bid_id
        party_id
        rank
        seller_assigned_score
        system_calculated_score
        max_tied_rank
        bid {
          listing_id
          bid_amount
          bid_id
          bidder_party_id
          type
          version
          updated_date
          created_date
          audit_history {
            updated_date
            delta {
              added
              modified
              removed
            }
          }
          ...OnlineAuctionSegmentBidTermsFragment
          negotiation_sequence {
            counters {
              listing_id
              bid_amount
              bid_id
              bidder_party_id
              type
              version
              updated_date
              created_date
              ...OnlineAuctionSegmentBidTermsFragment
            }
            latest_counter_bid {
              listing_id
              bid_amount
              bid_id
              bidder_party_id
              type
              version
              updated_date
              ...OnlineAuctionSegmentBidTermsFragment
            }
          }
        }
        party {
          person {
            first_name
            last_name
          }
        }
      }
    }
  }
`;
const getCurrentServerTime = () => fetch.forAuctionGraph({
  query: `
        query ${embeddableOperation('blueprint_getCurrentTime')} {
          current_online_auction_time
        }
      `
}).then(res => res.body).then(({
  data
}) => {
  if (data === null) {
    coreLogger.forModule('blueprint:time').warn('Failed to get Current Server Time');
  }
  return data?.current_online_auction_time;
});
const getLatestOnlineSegments = (controller, hasAuthenticatedUser = false, listingIds, isSellerAudience = false) => {
  return fetch.forAuctionGraph({
    variables: {
      listingIds,
      hasAuthenticatedUser,
      isSellerAudience
    },
    query: gql$5`
                query ${embeddableOperation('blueprint_getTransactionalOnlineSegments')}($listingIds: [ID!]!, $hasAuthenticatedUser: Boolean!, $isSellerAudience: Boolean!) {
                  online_segments_batched(filters: { listing_ids: $listingIds }) {
                    __typename
                    update_date
                    listing_id
                    online_segment_id
                    start_date
                    initial_end_date
                    current_increment_amount
                    segment_type
                    segment_status
                    reserve_status
                    current_time
                    starting_bid_amount
                    match_bidding_period_end_date
                    is_match_bidding_ended
                    current_highest_bid {
                      bid_amount
                      belongs_to_current_user @include(if: $hasAuthenticatedUser)
                      type
                      bid_id
                      updated_date
                    }
                    current_bid_extension {
                      extension_type
                      reason
                    }
                    subject_to_status
                    configuration {
                      bids_away_status
                      is_one_away_enabled
                      is_one_away_displayed
                      is_initial_end_date_extended
                      is_match_bidding_enabled
                      is_max_bidding_enabled
                      should_bid_again
                      review_date
                    }
                    result {
                      winning_bid_amount
                    }
                    ...AuthenticatedUserOnlineAuctionSegmentFragment @include(if: $hasAuthenticatedUser)
                    ...SellerExclusiveOnlineAuctionSegmentFragment @include(if: $isSellerAudience)
                  }
                }
                ${ONLINE_AUCTION_SEGMENT_BID_TERMS}
                ${AUTHENTICATED_USER_ONLINE_AUCTION_SEGMENT_FIELDS}
                ${SELLER_EXCLUSIVE_ONLINE_AUCTION_SEGMENT_FIELDS}
            `
  }, {
    controller
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    // TODO: we need to specify this as retryable if it's the initial polling
    if (data === null || data.online_segments_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest online segments', errors);
    }
    return data.online_segments_batched;
  });
};
const getLatestOnlineFocusedListingsSegments = (controller, hasAuthenticatedUser = false, listingIds, isSellerAudience = false, includeBidsRanking = true) => {
  const shouldIncludeBidsRanking = hasAuthenticatedUser && includeBidsRanking;
  return fetch.forAuctionGraph({
    variables: {
      listingIds,
      isSellerAudience,
      hasAuthenticatedUser,
      shouldIncludeBidsRanking
    },
    query: gql$5`
                query ${embeddableOperation('blueprint_getTransactionalOnlineSegmentsFocused')}($listingIds: [ID!]!, $isSellerAudience :Boolean!, $hasAuthenticatedUser: Boolean!, $shouldIncludeBidsRanking: Boolean!) {
                  online_segments_batched(filters: { listing_ids: $listingIds }) {
                    __typename
                    segment_type
                    segment_status
                    listing_id
                    update_date
                    current_highest_bid {
                      bid_amount
                      belongs_to_current_user @include(if: $hasAuthenticatedUser)
                      type
                      bid_id
                      updated_date
                    }
                    configuration {
                      review_date
                      is_max_bidding_enabled
                    }
                    ...OnlineAuctionSegmentBidsRankingFragment @include(if: $shouldIncludeBidsRanking)
                    ...AuthenticatedUserOnlineAuctionSegmentFragment @include(if: $hasAuthenticatedUser)
                    ...SellerExclusiveOnlineAuctionSegmentFragment @include(if: $isSellerAudience)
                  }
                }
                ${ONLINE_AUCTION_SEGMENT_BID_TERMS}
                ${ONLINE_AUCTION_SEGMENT_BIDS_RANKING}
                ${AUTHENTICATED_USER_ONLINE_AUCTION_SEGMENT_FIELDS}
                ${SELLER_EXCLUSIVE_ONLINE_AUCTION_SEGMENT_FIELDS}

            `
  }, {
    controller
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.online_segments_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest focused online segments', errors);
    }
    return data.online_segments_batched;
  });
};
const getLatestOnlineListings = (listingIds, controller) => {
  return fetch.forAuctionGraph({
    query: gql$5`
                    query ${embeddableOperation('blueprint_getOnlineListings')}($listingIds: [ID!]!) {
                        listings_batched(filters: { listing_ids: $listingIds }) {
                            __typename
                            updated_date
                            update_date
                            listing_id
                            listing_status
                            listing_status_label
                            auction {
                                starting_bid
                            }
                        }
                    }
                `,
    variables: {
      listingIds
    }
  }, {
    controller
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.listings_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest online listings', errors);
    }
    return data.listings_batched;
  });
};
const submitOnlineMatchBid = (segment, input) => {
  return fetch.forAuctionGraph({
    query: `
        mutation blueprint_placeOnlineAuctionSegmentMatchBid(
          $listingId: ID!,
          $offerTemplateId: ID!
        ) {
          placeOnlineAuctionSegmentMatchBid(
            listingId: $listingId,
            offerTemplateId: $offerTemplateId
          ) {
            bid_id
          }
        }
      `,
    variables: {
      listingId: segment.listing_id,
      offerTemplateId: input.offerTemplateId
    }
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.placeOnlineAuctionSegmentMatchBid === null) {
      throw partialGraphErrorFactory$3('Could not submit online match bid', errors);
    }
    return data.placeOnlineAuctionSegmentMatchBid;
  });
};
const submitOnlineRegularBid = (segment, input) => {
  return fetch.forAuctionGraph({
    query: `
        mutation ${embeddableOperation('blueprint_updateOnlineAuctionSegmentHighestBid')}($input: OnlineAuctionSegmentBidInput!) {
          updateOnlineAuctionSegmentHighestBid(input: $input) {
            bid_id
            online_segment_id
            listing_id
            bid_amount
            type
            belongs_to_current_user
            record_status
            max_bid_update_date
            created_date
            updated_date
            was_instantly_outbid
          }
        }
      `,
    variables: {
      input: {
        listing_id: segment.listing_id,
        ...input
      }
    }
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.updateOnlineAuctionSegmentHighestBid === null) {
      throw partialGraphErrorFactory$3('Could not update online bid', errors);
    }
    return data.updateOnlineAuctionSegmentHighestBid;
  });
};
const upsertListingOnlineMaxBid = (segment, input) => {
  return fetch.forAuctionGraph({
    query: `
        mutation ${embeddableOperation('blueprint_upsertListingOnlineMaxBid')}($input: ListingOnlineMaxBidInput!) {
          upsertListingOnlineMaxBid(input: $input) {
            max_bid_id
            listing_id
            property_id
            max_bid_amount
            record_status
            created_date
            updated_date
            was_instantly_outbid
          }
        }
      `,
    variables: {
      input: {
        listing_id: segment.listing_id,
        ...input
      }
    }
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.upsertListingOnlineMaxBid === null) {
      throw partialGraphErrorFactory$3('Could not create/update online max bid', errors);
    }
    return data.upsertListingOnlineMaxBid;
  });
};
const deleteOnlineAuctionSegmentBid = (segment, partyId) => {
  return fetch.forAuctionGraph({
    query: `
        mutation ${embeddableOperation('blueprint_deleteOnlineAuctionSegmentBid')}($listingId: ID!, $partyId: ID!) {
          deleteOnlineAuctionSegmentBid(listingId: $listingId, partyId: $partyId) 
        }
      `,
    variables: {
      listingId: segment.listing_id,
      partyId
    }
  }).then(res => {
    const errors = res.body.errors?.[0];
    if (errors) throw errors;
    // @ts-ignore next
    return res.body.data.deleteOnlineAuctionSegmentBid; // eslint-disable-line
  }).catch(error => {
    console.error('Error deleting online auction segment bid:', error);
    throw error;
  });
};
const handleBidErrorMessage = errors => match({
  code: errors?.[0]?.extensions?.specificErrorCode || ''
}).with({
  code: 'INVALID_FINANCE_AND_OR_APPRAISAL_CONTINGENCY_FOR_PAYMENT_TYPE'
}, () => {
  const message = 'Please verify the number of days requested for your contingencies is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'INVALID_DOWN_PAYMENT_AMOUNT'
}, () => {
  const message = 'Please review your down payment amount and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'PROOF_OF_FUNDS_DOCUMENT_MISSING'
}, () => {
  const message = 'Please upload a proof of funds document and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'INVALID_AGENT_COMMISSION'
}, () => {
  const message = 'Please verify the amount entered for the buyer’s agent is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'INVALID_DOWN_PAYMENT_AMOUNT'
}, () => {
  const message = 'Please verify the amount entered for your down payment is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'INVALID_EARNEST_MONEY_DEPOSIT_AMOUNT'
}, () => {
  const message = 'Please verify the amount entered for your earnest money deposit is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'INVALID_SELLER_CONCESSION_AMOUNT'
}, () => {
  const message = 'Please verify the amount of requested seller paid closing costs is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'MISSING_INSPECTION_CONTINGENCY_PERIOD_DURATION'
}, () => {
  const message = 'Please verify the number of days requested for your contingencies is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'MISSING_AGENT_COMMISSION'
}, () => {
  const message = 'Please verify the amount entered for the buyer’s agent is accurate and try again.';
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'SEALED_BID_BELOW_STARTING_BID_CODE'
}, () => {
  const message = `Please contact customer care at ${formatPhoneNumber(contactNumbers.SMART_SALE_CONCIERGE)} for assistance.`;
  return {
    message,
    unresolved_message: message
  };
}).with({
  code: 'ONLINE_SEGMENT_ALREADY_ENDED'
}, () => {
  const message = 'The seller is no longer accepting offers.';
  return {
    title: 'Listing has been removed.',
    message,
    unresolved_message: 'The seller is no longer accepting offers.{{more_properties}}'
  };
}).with({
  code: 'NO_ACTIVE_ONLINE_SEGMENT_FOUND_FOR_LISTING'
}, () => {
  const message = 'The seller is no longer accepting offers.';
  return {
    title: 'Listing has been removed.',
    message,
    unresolved_message: 'The seller is no longer accepting offers.{{more_properties}}'
  };
}).with({
  code: 'PARTY_NOT_REGISTERED'
}, () => {
  const message = 'Please complete the registration for this property and try again.';
  return {
    title: 'Listing has been removed.',
    message,
    unresolved_message: message
  };
}).with({
  code: 'PARTY_PARTICIPATION_DISALLOWED'
}, () => {
  const message = `Please contact customer care at ${formatPhoneNumber(contactNumbers.ONLINE_BUYER_EXPERIENCE)} for assistance.`;
  return {
    title: 'Listing has been removed.',
    message,
    unresolved_message: message
  };
}).with({
  code: 'INVALID_SEGMENT_STATUS_FOR_BUYER_UPDATE'
}, () => {
  const message = 'The seller is no longer accepting new offers.';
  return {
    message,
    unresolved_message: 'The seller is no longer accepting offers.{{more_properties}}'
  };
}).otherwise(() => {
  const message = `Please contact customer care at ${formatPhoneNumber(contactNumbers.ONLINE_BUYER_EXPERIENCE)} for assistance.`;
  return {
    message,
    unresolved_message: message
  };
});
const upsertSealedBid = (_segment, variables) => fetch.forAuctionGraph({
  query: `
        mutation ${embeddableOperation('blueprint_upsertSealedBid')}($sealedBidInput: SealedBidInput!) {
          upsertSealedBid(input: $sealedBidInput) {
            bid_id
            listing_id
            bid_amount
          }
        }
      `,
  variables
}).then(({
  body: {
    data,
    errors
  }
}) => {
  if (!!errors?.[0]) {
    throw handleBidErrorMessage(errors);
  }
  return data.upsertSealedBid;
}).catch(() => {
  throw handleBidErrorMessage();
});
const acceptSellerCounterBid = (_segment, variables) => fetch.forAuctionGraph({
  query: `
        mutation ${embeddableOperation('blueprint_acceptSellerCounterBid')}(
          $input: AcceptOrRejectBidInput!
        ) {
          acceptSellerCounterBid(input: $input) {
            bid_id
          }
        }
      `,
  variables
}).then(({
  body: {
    data,
    errors
  }
}) => {
  if (!!errors?.[0]) {
    throw handleBidErrorMessage(errors);
  }
  return data.acceptSellerCounterBid;
}).catch(() => {
  throw handleBidErrorMessage();
});
const rejectSellerCounterBid = (_segment, variables) => fetch.forAuctionGraph({
  query: `
        mutation ${embeddableOperation('blueprint_rejectSellerCounterBid')}(
          $input: AcceptOrRejectBidInput!
        ) {
          rejectSellerCounterBid(input: $input)
        }
      `,
  variables
}).then(({
  body: {
    data,
    errors
  }
}) => {
  if (!!errors?.[0]) {
    throw handleBidErrorMessage(errors);
  }
  return data.rejectSellerCounterBid;
}).catch(() => {
  throw handleBidErrorMessage();
});
const withdrawSealedBid = (_segment, variables) => fetch.forAuctionGraph({
  query: `
        mutation ${embeddableOperation('blueprint_withdrawSealedBid')}($listing_id: ID!, $party_id: ID!) {
          withdrawSealedBid(listingId: $listing_id, partyId: $party_id)
        }
      `,
  variables
}).then(({
  body: {
    data,
    errors
  }
}) => {
  if (!!errors?.[0]) {
    throw handleBidErrorMessage(errors);
  }
  return data.withdrawSealedBid;
}).catch(() => {
  throw handleBidErrorMessage();
});
const acceptBuyerBid = (_segment, variables) => {
  return fetch.forAuctionGraph({
    query: `
            mutation ${embeddableOperation('blueprint_acceptBuyerBid')}($input: AcceptOrRejectBidInput!) {
              acceptBuyerBid(input: $input) {
                bid_id
              }
            }
          `,
    variables
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.acceptBuyerBid;
  });
};
const placeSellerCounterBid = (_segment, variables) => {
  return fetch.forAuctionGraph({
    query: `
            mutation ${embeddableOperation('blueprint_placeSellerCounterBid')}(
              $input: SellerCounterBidInput!
            ) {
              placeSellerCounterBid(input: $input) {
                bid_id
              }
            }
          `,
    variables
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.placeSellerCounterBid;
  });
};
const updateListingReserve = async (_segment, {
  listing_id,
  current_reserve_amount
}) => {
  return fetch.forAuctionGraph({
    query: `
            mutation ${embeddableOperation('blueprint_patchOnlineAuctionSegment')}(
              $input: OnlineAuctionSegmentInput!
            ) {
              patchOnlineAuctionSegment(input: $input) {
                listing_id
              }
            }
          `,
    variables: {
      input: {
        listing_id,
        current_reserve_amount,
        update_source: 'LISTING_PORTAL'
      }
    }
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.patchOnlineAuctionSegment;
  });
};
const updateOnlineSegmentFragments = async (_segment, {
  listing_id,
  review_date
}) => {
  return fetch.forAuctionGraph({
    query: `
            mutation ${embeddableOperation('blueprint_updateOnlineSegmentFragments')}(
              $input: OnlineSegmentFragmentsInput!
            ) {
              updateOnlineSegmentFragments(input: $input) {
                listing_id
              }
            }
          `,
    variables: {
      input: {
        listing_id,
        fragments: [{
          type: 'OFFER_REVIEW_PERIOD',
          start_date: review_date,
          status: 'PENDING'
        }]
      }
    }
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.updateOnlineSegmentFragments;
  });
};
const updateListingReviewDate = async (_segment, {
  listing_id,
  review_date
}) => {
  return fetch.forAuctionGraph({
    query: `
            mutation ${embeddableOperation('blueprint_patchOnlineAuctionSegment')}(
              $input: OnlineAuctionSegmentInput!
            ) {
              patchOnlineAuctionSegment(input: $input) {
                listing_id
              }
            }
          `,
    variables: {
      input: {
        listing_id,
        configuration: {
          review_date
        },
        update_source: 'LISTING_PORTAL'
      }
    }
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.patchOnlineAuctionSegment;
  });
};
const rejectBuyerBid = (_segment, variables) => {
  return fetch.forAuctionGraph({
    query: `
        mutation ${embeddableOperation('blueprint_rejectBuyerBid')}($input: AcceptOrRejectBidInput!) {
          rejectBuyerBid(input: $input)
        }
      `,
    variables
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.rejectBuyerBid;
  });
};
const withdrawSellerCounterBid = (_segment, variables) => {
  return fetch.forAuctionGraph({
    query: `
        mutation ${embeddableOperation('blueprint_withdrawSellerCounterBid')}(
          $input: AcceptOrRejectBidInput!
        ) {
          withdrawSellerCounterBid(input: $input)
        }
      `,
    variables
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.withdrawSellerCounterBid;
  });
};
// TODO: Unclear why the listing id is passed in as a variable when it's always on the segment always
const startHighestAndBestRound = (_segment, variables) => {
  return fetch.forAuctionGraph({
    query: `
        mutation ${embeddableOperation('blueprint_startHighestAndBestRound')}($listingId: ID!) {
          startHighestAndBestRound(listingId: $listingId)
        }
      `,
    variables
  }).then(({
    body: {
      data,
      errors
    }
  }) => {
    if (!!errors?.[0]) {
      throw handleBidErrorMessage(errors);
    }
    return data.startHighestAndBestRound;
  });
};
const getSummarizedBidHistory$1 = segment => fetch.forAuctionGraph({
  // eslint-disable-next-line @adc/named-gql-operations
  query: gql$5`
          query ${embeddableOperation('blueprint_getSummarizedBidHistory')}($listingIds: [ID!]!) {
            online_segments_batched(filters: { listing_ids: $listingIds }) {
              bids(filters: { take: 3 }) {
                content {
                  bid_id
                  type
                  bid_amount
                  belongs_to_current_user
                }
              }
            }
          }
        `,
  variables: {
    listingIds: [segment.listing_id]
  }
}).then(res => res.body?.data?.online_segments_batched[0]?.bids);

const fragments$7 = {
  badges: {
    reference: '...ListingBadgesFields',
    definition: gql$5`
      fragment ListingBadgesFields on Listing {
        listing_status
        listing_configuration {
          product_type
          broker_commission_message
          buyer_premium_available
          financing_available
          is_first_look_enabled
          first_look_end_date
          interior_access_allowed
          occupancy_status
          redemption_expiration_date
          trustee_sales_channel
          is_buyers_premium_available
          buyers_premium_paid_by
        }

        listing_summary {
          is_remote_bid_enabled
          is_remote_before_and_during_auction_enabled
        }

        program_configuration {
          buyers_premium_minimum_amount
          buyers_premium_primary_amount
          buyers_premium_commission_type
        }

        primary_property {
          is_currently_saved
          is_newly_listed
        }

        open_houses {
          local_date
          start_time
          end_time
        }

        rental {
          current_rent
          lease_start_date
          lease_expiration_date
          is_security_deposit_charged
          security_deposit_amount
        }

        short_sale {
          agreement_expiration_date
        }

        event {
          event_code
          trustee_sale
        }

        parties {
          party_role
        }

        venue {
          venue_type
        }

        marketing_tags {
          tag
        }
      }
    `
  },
  changeRequest: {
    reference: '...BlueprintChangeRequest',
    definition: gql$5`
      fragment BlueprintChangeRequest on ObjectChange {
        change_request_id
        change_request
        review_status
        change_type
        change_path_variable
      }
    `
  },
  invitation: {
    reference: '...BlueprintInvitation',
    definition: gql$5`
      fragment BlueprintInvitation on Invitation {
        __typename
        invitation_id
        invitation_token
        updated_date
        intent
        status
        context {
          __typename
          ... on ConsumerListingPartyAssociationContext {
            intent
            license_number
            party_organization_title
            phone_number
            address {
              street_name
              municipality
              country_primary_subdivision
              postal_code
            }
          }
        }
        relatives {
          object_id
          object_type
          recipient_role_code
        }
        recipient {
          __typename
          ... on InvitationDanglingUser {
            first_name
            last_name
            email
            phone
          }

          ... on MinimalUser {
            first_name
            last_name
            email
            party {
              phones {
                phone_id
                number
              }
              addresses {
                street_number
                street_name
                municipality
                country_primary_subdivision
                postal_code
              }
            }
          }
        }
      }
    `
  },
  merchandising: {
    reference: '...BlueprintMerchandisedListing',
    definition: gql$5`
      fragment BlueprintMerchandisedListing on Listing {
        __typename
        updated_date
        listing_id
        urn
        listing_status
        reserve_price
        closing_configuration {
          responsibility_type
          is_closing_document_required
        }
        listing_page_path
        formatted_address(format: DOUBLE_LINE)
        primary_photo
        photos {
          file_descriptor {
            urls {
              cdn
            }
          }
        }
        is_published
        is_searchable
        listing_configuration {
          is_local_disclosure_applicable
          agent_commission_message
          agent_commission_paid_by
          product_classification
          are_private_tours_available
          days_to_close
          financing_available
          occupancy_status
          product_type
          asset_type
          is_third_party_online
          monthly_hoa_name
          monthly_hoa_phone
          monthly_hoa_amount
        }
        days_on_market

        valuation {
          seller_current_value_amount
        }
        auction {
          start_date
          end_date
          reserve_price
          is_online
        }

        program_configuration {
          earnest_money_deposit_minimum_amount
          earnest_money_deposit_primary_amount
        }

        seller_terms {
          inspection_terms {
            is_option_contingency
            is_contingency
          }
          leaseback_terms {
            leaseback_period_in_days
            leaseback_period_rent
          }
          finance_terms {
            finance_preference
            is_contingency
          }
          intent
        }

        strategy {
          selling_method_attributes {
            type
            online_segment_type
          }
        }

        document_tracking {
          document_status
          document_type
          initial_sent_date
          completion_date
          last_sent_date
        }

        documents {
          metadata {
            document_purpose_type_code
          }
          document_tracking_urn
        }

        listing_remarks {
          mls_comment
          seller_contract_terms
        }

        parties {
          party_id
          party_name
          party_role
          party_phone
          party_email
          party_license_number
          party_organization_title
          assigned_local_date
          assigned_date
          party_address {
            street_description
            municipality
            country_primary_subdivision
            postal_code
          }
        }

        open_houses {
          local_date
          start_time
          end_time
          rank
          is_appointment_only
        }

        primary_property {
          property_id
          property_documents {
            property_document_id
            property_id
            document_file_name
            document_internal_title
            document_external_title
            status
            file_descriptor {
              urls {
                preview
                cdn
                download
              }
            }
            metadata {
              document_purpose_type_code
            }
          }
          property_photos {
            property_photo_id
            created_date
            updated_date
            photo_internal_title
            photo_external_title
            metadata {
              photo_default_rank
            }
            photo_tags
            status
            file_descriptor {
              urls {
                cdn
              }
            }
          }
          summary {
            structure_type_code
            total_bedrooms
            total_bathrooms
            square_footage
            lot_size
            apn
            year_built
            street_address
            address {
              municipality
              country_secondary_subdivision
              country_primary_subdivision
              postal_code
              coordinates {
                lat
                lon
              }
            }
          }
          is_newly_listed
          current_user_tracking_state {
            is_seen
            is_updated
          }
        }
        review_status
        marketing {
          agreement_sent_date
          agreement_received_date
        }
      }
    `
  },
  listingCard: {
    reference: '...ListingCardFields',
    definition: gql$5`
      fragment ListingCardFields on Listing {
        __typename
        listing_id
        urn
        listing_status_group
        listing_status
        listing_status_label(intent: SEARCH)
        primary_photo
        primary_property_id
        # TEMPORARY dual-track dedupe metadata (search-only).
        dual_track_sibling_lobs {
          listing_id
          product_type
          asset_type
        }
        listing_photos_count
        listing_page_path
        reserve_price @include(if: $hasAuthenticatedUser)
        is_hot
        formatted_address(format: DOUBLE_LINE)

        listing_configuration {
          product_type
          is_reserve_displayed
          is_reserve_price_available
          broker_commission
          financing_available
          buyer_premium_available
          interior_access_allowed
          occupancy_status
          asset_type
          is_first_look_enabled
          is_direct_offer_enabled
          is_third_party_online
        }

        attribution_source {
          origin_code
        }

        external_identifiers {
          data_source
          external_identifier
        }

        venue {
          venue_type
        }

        event {
          event_code
          trustee_sale
        }

        valuation {
          seller_current_value_amount
        }

        strategy {
          selling_method_attributes {
            online_segment_type
          }
        }

        seller_property {
          street_description
          municipality
          country_primary_subdivision
          country_secondary_subdivision
          postal_code
        }

        program_configuration {
          program_enrollment_code
        }

        seller_terms {
          inspection_terms {
            is_option_contingency
            is_contingency
          }
          leaseback_terms {
            leaseback_period_in_days
            leaseback_period_rent
          }
          finance_terms {
            finance_preference
            is_contingency
          }
          intent
        }

        primary_property {
          property_id
          summary {
            total_bedrooms
            total_bathrooms
            square_footage
            lot_size
            year_built
            valuation
            structure_type_code
            structure_type_group
            address {
              coordinates {
                lon
                lat
              }
            }
          }
          is_currently_saved @include(if: $hasAuthenticatedUser)
          is_newly_listed
          current_user_tracking_state {
            is_seen
            is_updated
          }
        }

        auction {
          start_date
          end_date
          starting_bid
          is_online
          visible_auction_start_date_time
          bid_instruction {
            nos_amount
          }
        }

        marketing_tags {
          tag
        }

        open_houses {
          local_date
          start_time
          end_time
        }

        listing_summary {
          is_remote_bid_enabled
          is_remote_before_and_during_auction_enabled
          show_opening_bid
        }

        external_information(resolvePolicy: CACHE_ONLY) {
          collateral {
            summary {
              estimated
              low
              high
              type
            }
          }
        }

        selling_method(resolvePolicy: CACHE_ONLY) {
          __typename
          ... on OnlineAuctionSegment {
            _alias_OnlineAuctionSegment__starting_bid_amount: starting_bid_amount

            _alias_OnlineAuctionSegment__configuration: configuration {
              is_match_bidding_enabled
              is_registration_deposit_required_enabled
              bid_again_count
              should_bid_again
            }
            listing_id
            __typename
            start_date
            segment_type
            initial_end_date
            current_time
            reserve_status
            starting_bid_amount
            subject_to_status
            current_highest_bid {
              bid_id
              updated_date
              bid_amount
              type
              terms {
                status
              }
            }
            segment_status
            current_increment_amount
            bid_count
            result {
              winning_bid_amount
            }
          }
          ... on LiveAuctionSegment {
            _alias_LiveAuctionSegment__starting_bid_amount: starting_bid_amount

            _alias_LiveAuctionSegment__configuration: configuration {
              state_deposit_rule
            }
            current_highest_bid {
              bid_amount
            }
          }
        }
      }
    `
  },
  requiringActiveSession: {
    reference: '...FieldsRequiringActiveSessionFields',
    definition: gql$5`
      fragment FieldsRequiringActiveSessionFields on Listing {
        reserve_price
        listing_id
        is_current_user_first_look_qualified
        is_current_user_registered_for_online_auction

        current_user_online_registration {
          registration_status
        }

        current_user_remote_bid @include(if: $isRemoteBiddingEnabled) {
          remote_bid_amount
        }

        current_user_bid_deposit_requirements {
          is_registration_deposit_applicable
          is_registration_deposit_required_enabled
          absolute_amount_required
          payment_type
        }

        is_current_user_allowed(
          actions: [
            ONLINE_BIDDING_REGISTRATION
            ONLINE_REGULAR_BIDDING
            ONLINE_PROXY_BIDDING
            ONLINE_DIRECT_OFFER
          ]
        ) {
          __typename
          action
          allowed
          reason
          reason_code
        }
        online_auction_segment(resolvePolicy: ORIGIN_ONLY) {
          segment_status
          next_bid_amount
          current_user_max_bid {
            current_user_next_max_bid_amount
            max_bid_amount
            record_status
            updated_date
          }
          bids_ranking {
            ranking {
              belongs_to_current_user
            }
          }
          current_highest_bid {
            bid_id
            belongs_to_current_user
            bid_amount
            type
            updated_date
          }
          configuration {
            is_registration_deposit_required_enabled
            is_max_bidding_enabled
          }
          current_user_bidding_status
          is_current_user_registered_for_online_auction
        }
      }
    `
  },
  address: {
    reference: '...Address',
    definition: gql$5`
      fragment Address on Address {
        street_number
        street_name
        street_unit
        country_primary_subdivision
        country_secondary_subdivision
        country_tertiary_subdivision
        municipality
        postal_code
        coordinates {
          lat
          lon
        }
      }
    `
  },
  activeListing: {
    reference: '...ActiveListingFields',
    definition: gql$5`
      fragment ActiveListingFields on Listing {
        __typename
        applicable_global_documents(
          filters: { media_usage_type_codes: [WEB] }
        ) {
          __typename
          media_purpose_type_code
          media_content_type
          media_download_uri
          media_file_name
          media_internal_title
          media_external_title
          media_desc
          priority_level_type_code
          updated_date
        }
        attribution_source {
          origin_code
        }
        auction {
          auction_date
          start_date_time
          end_date
          end_date_time
          starting_bid
          provisional_sale_date
          is_outbid_active
          is_online
          visible_auction_start_date_time
          bid_instruction {
            nos_amount
          }
        }
        current_listing {
          listing_page_path
        }
        documents(filters: { document_usage_type_codes: [WEB] }) {
          document_external_title
          updated_date
          metadata {
            document_purpose_type_code
          }
          file_descriptor {
            urls {
              cdn
              download
            }
          }
        }
        event {
          title
          trustee_sale
          event_type
          # Auction Number
          event_code
          event_id
          event_schedule {
            campaign_start_date
          }
          description
        }
        external_identifiers {
          external_identifier
          data_source
        }
        external_information {
          collateral {
            is_valuation_available
          }
        }
        formatted_address(format: DOUBLE_LINE)
        fcl_marketing {
          appraiser_uri
          clerk_of_court_uri
        }
        intake_source {
          origin_code
        }
        is_coming_soon
        is_hot
        is_published
        is_searchable
        live_auction_segment(resolvePolicy: ORIGIN_ONLY) {
          listing_id
          __typename
          starting_bid_amount
          live_auction {
            location {
              lat
              lon
            }
            status
          }
        }
        listing_configuration {
          sale_instruction
          total_estimated_debt
          final_judgement_amount
          occupancy_status
          principal_original_amount
          trustee_sales_agent
          postponement_new_auction_date
          postponement_new_auction_start_date
          postponement_new_auction_end_date
          postponement_new_auction_time
          minimum_credit_bid_amount
          trustee_sales_channel
          is_third_party_online
          is_reserve_displayed
          is_reserve_price_available
          is_proxy_bidding_enabled
          asset_type
          financing_available
          title_insurance
          monthly_hoa_amount
          is_buyers_premium_available
          is_earnest_money_deposit_available
          is_first_look_enabled
          first_look_end_date
          product_type
          broker_commission_message
          buyer_premium_available
          interior_access_allowed
          redemption_expiration_date
          is_direct_offer_enabled
          is_minimum_bid_enabled
          cried_by
        }
        closing_configuration {
          responsibility_type
          is_closing_document_required
          earnest_money_deposit_timeline_label
        }
        urn
        listing_id
        listing_media {
          media_external_title
          media_download_uri
          media_cdn_uri
          updated_date
          media_type
        }
        listing_page_path
        listing_remarks {
          # Property Description
          mls_comment
          # Disclaimer Field
          legal_description
          # Special Notes
          special_notes
          additional_seller_terms
          special_proceedings_number
        }
        listing_summary {
          is_remote_bid_enabled
          show_opening_bid
          is_remote_before_and_during_auction_enabled
        }
        listing_status
        listing_status_group
        listing_status_label
        marketing_attributes(
          resolveTags: true
          attributeClasses: [FEATURE_TOGGLE, CONTENT]
        ) {
          ... on FeatureToggleMarketingAttribute {
            attribute_type
            is_enabled
          }

          ... on ContentMarketingAttribute {
            default_display_value
            attribute_type
            show
            label
            message
          }
        }
        marketing_tags {
          tag
        }
        metrics {
          pdp_view_count
        }
        offer_suggestion {
          suggested_offer_low
          suggested_offer_high
        }
        offers_summary {
          has_accepted_direct_offer
        }
        online_auction_segment(resolvePolicy: ORIGIN_ONLY) {
          listing_id
          __typename
          start_date
          segment_type
          bids_ranking {
            ranking {
              belongs_to_current_user
            }
          }
          initial_end_date
          current_time
          reserve_status
          configuration {
            is_match_bidding_enabled
            is_registration_deposit_required_enabled
            bid_again_count
            should_bid_again
            is_registration_enabled
            precision_unit
          }
          starting_bid_amount
          subject_to_status
          current_highest_bid {
            bid_id
            belongs_to_current_user @include(if: $hasAuthenticatedUser)
            bid_amount
            type
          }
          segment_status
          current_reserve_amount
          current_increment_amount
          bid_count
          result {
            winning_bid_amount
          }
        }
        open_houses {
          local_date
          start_time
          end_time
        }
        seller_configuration {
          parties {
            role
            name
          }
          seller_code
        }
        seller_plan {
          seller_plan_code
        }
        seller_terms {
          leaseback_terms {
            leaseback_period_in_days
          }
          inspection_terms {
            is_option_contingency
            is_contingency
          }
          finance_terms {
            is_contingency
            finance_preference
          }
        }
        parties {
          party_role
          party_name
          party_email
          party_phone
          party_license_number
          party_address {
            postal_code
            street_description
            municipality
            country_primary_subdivision
          }
        }

        photos {
          file_descriptor {
            urls {
              cdn
            }
          }
        }
        primary_photo
        primary_property {
          first_activation_date
          is_currently_saved
          is_newly_listed
          metrics {
            pdp_view_count
          }
          property_id
          summary {
            total_bedrooms
            total_bathrooms
            living_size
            year_built
            apn
            hoa_dues_amount
            lot_size
            structure_type_code
            structure_type_group
            valuation
            street_address
            formatted_address
            address {
              ...Address
            }
          }
          land_parcels {
            # APN Number?
            parcel_number
          }
          vendor_report_url
          has_vendor_information_available
          property_documents(filters: { document_usage_type_codes: [WEB] }) {
            property_document_id
            property_id
            document_file_name
            document_internal_title
            document_external_title
            status
            file_descriptor {
              urls {
                cdn
                download
              }
            }
            metadata {
              document_purpose_type_code
            }
          }
        }
        primary_property_id
        program_configuration {
          deed_types
          buyers_premium_minimum_amount
          buyers_premium_primary_amount
          buyers_premium_commission_type

          earnest_money_deposit_minimum_amount
          earnest_money_deposit_primary_amount
          earnest_money_deposit_commission_type

          third_party_title_provider
          program_enrollment_code
        }
        recommended_listings {
          ...ListingCardFields
        }
        rental {
          current_rent
          lease_start_date
          lease_expiration_date
          is_security_deposit_charged
          security_deposit_amount
        }

        seller_plan {
          seller_plan_code
        }
        seller_property {
          street_description
          country_primary_subdivision
          country_secondary_subdivision
          municipality
          postal_code
        }
        short_sale {
          agreement_expiration_date
          is_income_producing
        }

        strategy {
          selling_method_attributes {
            online_segment_type
          }
        }
        trustee {
          sale_time
          online_state_deposit_amount
          partnership_details
        }
        trustee_reference_id
        trustee_sale_number
        valuation {
          seller_current_value_amount
        }
        venue {
          venue_id
          venue_code
          venue_title
          venue_name
          venue_url
          venue_location_directions
          venue_type
          venue_address {
            ...Address
          }
          formatted_address(format: DOUBLE_LINE)
          venue_configuration {
            venue_setting
          }
          venue_schedule {
            initial_bidding_end_time
            bid_start_local_date
            bid_start_local_time
            initial_bidding_end_local_date
            initial_bidding_end_local_time
          }
        }
        ...FieldsRequiringActiveSessionFields
          @include(if: $hasAuthenticatedUser)
        ...ListingBadgesFields
      }
    `
  },
  sealedBidOfferManagement: {
    reference: '...SealedBidOfferManagementFields',
    definition: gql$5`
      ${ONLINE_AUCTION_SEGMENT_BID_TERMS}
      fragment SealedBidOfferManagementFields on Listing {
        # NOTE: There is some overlap between the fields here and in activeListing,
        # but activeListing contains way more than we want for offer management
        # (e.g., recommended_listings which creates deep nesting and circular references).
        # By defining these fields explicitly, we avoid exceeding GraphQL max depth limits.

        listing_id
        urn
        listing_status
        listing_status_label
        listing_page_path
        formatted_address(format: DOUBLE_LINE)
        primary_photo
        reserve_price
        potential_return_score

        auction {
          auction_date
          start_date_time #TODO: maybe only one of these is needed? check usage in pdp oms
          start_date
          starting_bid
        }

        event {
          event_id
          event_code
        }

        venue {
          venue_id
          venue_type
        }

        listing_configuration {
          asset_type
          product_type
          occupancy_status
          is_buyers_premium_available
          buyer_premium_available
        }

        program_configuration {
          buyers_premium_commission_type
          buyers_premium_primary_amount
          buyers_premium_minimum_amount
          earnest_money_deposit_commission_type
          earnest_money_deposit_minimum_amount
          earnest_money_deposit_primary_amount
          program_enrollment_code
        }

        primary_property {
          property_id
          summary {
            total_bedrooms
            total_bathrooms
            square_footage
            structure_type_code
            formatted_address
            address {
              ...Address
            }
          }
          is_newly_listed
          current_user_tracking_state {
            is_seen
            is_updated
          }
        }

        seller_property {
          country_secondary_subdivision
        }

        parties {
          party_role
          party_name
          party_phone
          party_email
        }

        valuation {
          seller_current_value_amount
        }

        seller_terms {
          inspection_terms {
            is_option_contingency
            is_contingency
          }
          leaseback_terms {
            leaseback_period_in_days
            leaseback_period_rent
          }
          finance_terms {
            finance_preference
            is_contingency
          }
        }

        # OnlineAuctionSegment-specific fields for sealed bid management
        selling_method(resolvePolicy: ORIGIN_ONLY) {
          __typename
          ... on OnlineAuctionSegment {
            current_user_bidding_status
            configuration {
              review_date
              is_registration_enabled
            }
            update_date
            online_segment_id
            listing_id
            segment_type
            segment_status

            current_user_highest_bid {
              listing_id
              bid_amount
              bid_id
              bidder_party_id
              type
              version
              updated_date
              created_date
              ...OnlineAuctionSegmentBidTermsFragment
              negotiation_sequence {
                counters {
                  listing_id
                  bid_amount
                  bid_id
                  bidder_party_id
                  type
                  version
                  updated_date
                  created_date
                  ...OnlineAuctionSegmentBidTermsFragment
                }
                latest_counter_bid {
                  listing_id
                  bid_amount
                  bid_id
                  bidder_party_id
                  type
                  version
                  updated_date
                  ...OnlineAuctionSegmentBidTermsFragment
                }
              }
            }

            current_user_max_bid {
              max_bid_amount
              record_status
            }

            # Use inline bids_ranking fields instead of fragment to reduce depth
            bids_ranking {
              listing_id
              online_segment_id
              ranking {
                bid_id
                party_id
                rank
                seller_assigned_score
                system_calculated_score
                belongs_to_current_user
                is_viewable_by_current_user
                bid {
                  bid_id
                  version
                  bid_amount
                  type
                  rank
                  record_status
                  terms {
                    status
                    offeror_type
                    buyers_premium_amount
                    payment_type
                    down_payment_amount
                    earnest_money_deposit_amount
                  }
                  negotiation_sequence {
                    latest_counter_bid {
                      bid_amount
                      bid_id
                      version
                      terms {
                        expiration_date
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
  }
};

const isForeclosure = listing => [enums$5.ProductType.Trustee, enums$5.ProductType.FclMarketing].includes(listing.listing_configuration?.product_type);
const getProductType = listing => _.get(listing, 'listing_configuration.product_type', '');
const getAssetType$1 = listing => _.get(listing, 'listing_configuration.asset_type', '');
const getOriginCode = listing => _.get(listing, 'attribution_source.origin_code', '');
const getSegmentType = listing => _.get(listing, 'strategy.selling_method_attributes.online_segment_type', '');
const getTrusteeSale = listing => _.get(listing, 'event.trustee_sale', false);
const getVenueType = listing => _.get(listing, 'venue.venue_type', '');
const getPartyMemberByRole = (listing, role) => listing.parties?.find(party => party?.party_role === role);
const hasADCAuctioneer = listing => _.get(listing, 'listing_configuration.trustee_sales_channel') === 'AUCTIONEERING';
const isLive = listing =>
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
getVenueType(listing) === graph.enums.VenueType.Live;
const isTPS = listing => getTrusteeSale(listing);
const isREO = listing => getAssetType$1(listing) === graph.enums.AssetType.BankOwned;
const isTPSLive$1 = listing => isTPS(listing) && isLive(listing);
// TODO: this is exported from listing utils as it is used in multiple apps, may be worth it to move more of these there to be used by other apps as a few apps have a listingUtils file that has a lot of overlap with this
const isTPSOnline$1 = listing => Boolean(isTPS(listing) && listing.auction?.is_online);
const isShortSale = listing =>
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
getProductType(listing) === graph.enums.ProductType.ShortSale;
const isPrivateSeller = listing => getAssetType$1(listing) === graph.enums.AssetType.PrivateSeller;
const isSmartSale = listing => getProductType(listing) === graph.enums.ProductType.PrivateSeller;
const isSparkOffer$1 = listing => isPrivateSeller(listing) && getOriginCode(listing) === graph.enums.AttributionSourceType.SparkOffer;
const shouldDisplayListPrice$1 = listing => isSparkOffer$1(listing) || isPrivateSeller(listing) && getSegmentType(listing) === graph.enums.OnlineAuctionSegmentType.SealedBid;
const isFCLMarketing$2 = listing =>
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
getProductType(listing) === graph.enums.ProductType.FclMarketing;
const getBrokerAgent = listing => getPartyMemberByRole(listing,
// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
graph.enums.ListingPartyRole.ListingAgent);
const getForeclosureContact = listing => getPartyMemberByRole(listing,
// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
graph.enums.ListingPartyRole.ForeclosureAttorneyContact);
const hasMarketingTag = (listing, name) => {
  return listing.marketing_tags?.find(listingTag => listingTag?.tag === name);
};
const formatMoney = (value, overrides, hideDecimals = false, defaultValue = '') => {
  const val = hideDecimals ? value : Number.parseFloat(value);
  if (Number.isFinite(val)) {
    const minimumFractionDigits = Number.isInteger(val) ? 0 : 2;
    return val.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits,
      ...overrides
    });
  }
  return val || defaultValue;
};
const isOnline = listing => listing?.selling_method?.__typename === 'OnlineAuctionSegment';
const isLiveSegment = listing => listing?.selling_method?.__typename === 'LiveAuctionSegment';
const isRemoteBidEnabled$2 = listing => {
  return isLiveSegment(listing) && Boolean(listing.listing_summary?.is_remote_bid_enabled || listing.listing_summary?.is_remote_before_and_during_auction_enabled);
};
const isOnlineOffer = listing => isOnline(listing) &&
// @ts-ignore ignoring as we already know segment type exists at this point
listing?.selling_method?.segment_type === enums$5.OnlineAuctionSegmentType.SealedBid;
const parseDate = date => {
  if (!date) return null;
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
};
const formatDays = days => `${days} day${days !== 1 ? 's' : ''}`;

const CREDIT_BID_REVEAL_FLAG = 'fe.resi.enable-credit-bid-reveal';
/** Resolved credit bid amount. Falsy means undisclosed. */
const getCreditBidAmount = listing => listing.listing_configuration?.minimum_credit_bid_amount || listing.reserve_price;
/** listing_configuration.is_reserve_price_available (not yet in the SDK type). */
const isReservePriceAvailable = listing => Boolean(listing.listing_configuration?.is_reserve_price_available);
/**
 * Derives the credit bid PDP treatment. First match wins:
 * legacy → hidden → disclosed → available → subscribed → coming-soon
 * The box is hidden once the auction is over (matches the nudge rule).
 */
const getCreditBidState = ({
  featureFlags,
  listing,
  user,
  isPropertySaved,
  isAuctionActive
}) => {
  // TODO: remove this flag gate once fe.resi.enable-credit-bid-reveal is fully rolled out.
  const isCreditBidRevealEnabled = Boolean(featureFlags?.[CREDIT_BID_REVEAL_FLAG]);
  const isTrusteeSale = Boolean(listing.event?.trustee_sale);
  if (!isCreditBidRevealEnabled || !isTrusteeSale) return 'legacy';
  if (!listing.listing_configuration?.is_reserve_displayed) return 'hidden';
  // Auction is over: hide the box entirely, same as the login nudge.
  if (!isAuctionActive) return 'hidden';
  const undisclosedReserve = !getCreditBidAmount(listing);
  if (!undisclosedReserve && user.logged_in) return 'disclosed';
  if (undisclosedReserve && isReservePriceAvailable(listing)) return 'available';
  if (undisclosedReserve && isPropertySaved) return 'subscribed';
  return 'coming-soon';
};
// The following is a list of priorities in descending order. The first element has the highest priority.
const priorities = [enums$5.GlobalMediaPriorityLevel.Highest, enums$5.GlobalMediaPriorityLevel.High, enums$5.GlobalMediaPriorityLevel.Medium, enums$5.GlobalMediaPriorityLevel.Low, enums$5.GlobalMediaPriorityLevel.Lowest];
const getClosingCompanyData$1 = listing => {
  const listingParties = listing?.parties || [];
  const wireCompanyContact = listingParties.find(elem => elem?.party_role === enums$5.ListingPartyRole.TitleCompanyContact); // find const
  const wireCompany = listingParties.find(elem => elem?.party_role === enums$5.ListingPartyRole.TitleCompany); // find const
  const wireAddress = wireCompany?.party_address || wireCompanyContact?.party_address || {};
  const closingCompany = {
    //  It's a business decision to prioritize TITLE_COMPANY party_name over TITLE_COMPANY_CONTACT party_name
    party_name: wireCompany?.party_name || wireCompanyContact?.party_name || '',
    party_phone: wireCompanyContact?.party_phone || wireCompany?.party_phone || '',
    party_email: wireCompanyContact?.party_email || wireCompany?.party_email || '',
    street_description: wireAddress.street_description || '',
    municipality: wireAddress.municipality || '',
    country_primary_subdivision: wireAddress.country_primary_subdivision || '',
    postal_code: wireAddress.postal_code || ''
  };
  const {
    party_name,
    street_description,
    municipality,
    country_primary_subdivision,
    postal_code,
    party_phone,
    party_email
  } = closingCompany;
  return {
    party_name,
    address: (street_description ? street_description + ', ' : '') + (municipality ? municipality + ', ' : '') + country_primary_subdivision + ' ' + postal_code,
    party_phone,
    party_email
  };
};
const getMarketingAttributeMessage = (listing, attributeType) => {
  const marketingAttribute = (listing.marketing_attributes || []).find(elem => elem?.attribute_type === attributeType);
  if (marketingAttribute && marketingAttribute.__typename === 'ContentMarketingAttribute') {
    return marketingAttribute.message;
  }
  return null;
};
const getPartnershipDetails$1 = listing => {
  return listing.trustee?.partnership_details || getMarketingAttributeMessage(listing, enums$5.MarketingAttributeType.SalesAgentContact);
};
const getPaymentInformation$1 = listing => getMarketingAttributeMessage(listing, enums$5.MarketingAttributeType.DepositCheckVerbiage);
const getProvisionalSaleDateTime$1 = listing => {
  const auctionStartDateTime = listing.auction?.start_date;
  // the fallback is because of the timezone guessing being spotty outside the US in recent versions
  const guessedTimezone = calculateTimeZone();
  return moment(listing.auction?.provisional_sale_date).format('MMM DD') + ', ' + moment(auctionStartDateTime).tz(guessedTimezone).format('hh:mm a z');
};
const getSellerDeedTypeLabel$1 = listing => {
  return (listing.program_configuration?.deed_types || []).map(deedType => match(deedType).when(deedType => deedType === enums$5.ListingDeedType.WarrantyDeed, () => 'Warranty Deed').when(deedType => deedType === enums$5.ListingDeedType.SpecialWarrantyDeed, () => 'Special Warranty Deed').when(deedType => deedType === enums$5.ListingDeedType.QuitclaimDeed, () => 'Quitclaim Deed').when(deedType => deedType === enums$5.ListingDeedType.QdAssignmentInterest, () => "Quitclaim Deed and Assignment of Interest in Sheriff's Deed").when(deedType => deedType === enums$5.ListingDeedType.QdAssignmentCert, () => "Quitclaim Deed and Assignment of Sheriff's Certificate of Title").when(deedType => deedType === enums$5.ListingDeedType.TrusteeDeed, () => "Trustee's Deed").when(deedType => deedType === enums$5.ListingDeedType.ReleaseDeed, () => 'Release Deed').otherwise(() => null)).filter(Boolean).join(' or ');
};
const getTitleInsurancePaidBy$1 = listing => {
  return match(listing.listing_configuration?.title_insurance).when(titleInsurance => titleInsurance === enums$5.ListingTitleInsurancePolicy.BuyerToPay, () => 'Buyer').when(titleInsurance => titleInsurance === enums$5.ListingTitleInsurancePolicy.SellerToPay, () => 'Seller').when(titleInsurance => titleInsurance === enums$5.ListingTitleInsurancePolicy.NotOffered, () => 'Not Available').when(titleInsurance => titleInsurance === enums$5.ListingTitleInsurancePolicy.ReferToPsa, () => 'Refer to Purchase Agreement').otherwise(() => 'Unknown');
};
const isAuctionOnline = listing => listing.auction?.is_online;
const isFclOnlineAuction = listing => isForeclosure(listing) && isAuctionOnline(listing);
const isTPSOhio$1 = listing => Boolean(isFclOnlineAuction(listing) && listing.seller_property?.country_primary_subdivision === 'OH');
const isTPSIllinois = listing => Boolean(isFclOnlineAuction(listing) && listing.seller_property?.country_primary_subdivision === 'IL');
const isFCLMarketing$1 = listing => listing.listing_configuration?.product_type === enums$5.ProductType.FclMarketing;
/**
 * This util was originally part of @adc/ui-core
 * Given an unordered list of objects, it splits the object by a key, which should be unique, to provide
 * the UI fields with a string key reference to object fields, which are easy to reference and update.
 *
 * @param {Array} - original collection of objects
 * @param {String} - key pointing to the value in each collection item that can be used as a unique reference
 * @return {Object<string, Object>}
 */
const splitEnumArrayByField = original => {
  const res = {};
  // Some APIs will represent an empty list with no list at all. Handle that case.
  const auxDocumentList = [...original];
  // sort the list by display_label
  auxDocumentList.sort((a, b) => getDocumentTitle$1(a) < getDocumentTitle$1(b) ? -1 : 1);
  auxDocumentList.forEach(originalField => {
    const docType = getDocumentType(originalField);
    if (docType) {
      const resultField = res[docType];
      const resultFieldPriority = resultField && getDocumentPriority(resultField);
      const originalFieldPriority = getDocumentPriority(originalField);
      const isNewPriorityLevelHigher = originalFieldPriority && resultFieldPriority && priorities.indexOf(originalFieldPriority) <= priorities.indexOf(resultFieldPriority);
      if (!resultField || isNewPriorityLevelHigher) {
        res[docType] = originalField;
      }
    }
  });
  return res;
};
const getDocumentTitle$1 = document => {
  if (document) {
    if (isGlobalMediaType(document)) {
      return document.media_external_title || '';
    }
    return document.document_external_title || '';
  }
  return '';
};
const getDocumentUrl = document => {
  if (document) {
    if (isGlobalMediaType(document)) {
      return document.media_download_uri || null;
    }
    return document.file_descriptor?.urls?.cdn || document.file_descriptor?.urls?.download || null;
  }
  return null;
};
const getSpecialDocuments$1 = (documentList, isTPSOhio = false) => {
  const documentMapping = splitEnumArrayByField(documentList);
  // prettier-ignore
  return {
    /**
     * Documents that are propagated from property documents to the listing level
     */
    titleReport: documentMapping[enums$5.PropertyDocumentPurposeType.PreliminaryTitleReport],
    addendum: documentMapping[enums$5.PropertyDocumentPurposeType.PropertyConditionAddendumAndRelease] || documentMapping[enums$5.GlobalMediaPurpose.PurchaseAgreementAddendum],
    noticeOfSale: documentMapping[enums$5.PropertyDocumentPurposeType.NoticeOfSale],
    propertyInspectionReport: documentMapping[enums$5.PropertyDocumentPurposeType.InspectionReport],
    additionalPropertyInfo: documentMapping[enums$5.PropertyDocumentPurposeType.RedactedBrokerPriceOpinion],
    /**
     * Documents that come from marketing packages
     */
    sampleCertOfSale: documentMapping[enums$5.GlobalMediaPurpose.SampleTrusteeCertOfSale],
    sampleDeed: documentMapping[enums$5.GlobalMediaPurpose.SampleDeed],
    purchaseAgreement: documentMapping[enums$5.GlobalMediaPurpose.PurchaseAgreement],
    termsAndConditions: isTPSOhio ? documentMapping[enums$5.GlobalMediaPurpose.TermsAndConditions] : undefined,
    legalDescription: isTPSOhio ? documentMapping[enums$5.PropertyDocumentPurposeType.LegalDescription] : undefined
  };
};
const formatDocumentsForDisplay$1 = documents => documents.map(doc => ({
  label: getDocumentTitle$1(doc),
  url: getDocumentUrl(doc),
  updatedDate: new Date(doc?.updated_date || ''),
  type: getDocumentType(doc)
}));
const getAllDocuments$1 = listing => {
  const documents = listing.documents || [];
  const globalDocuments = listing.applicable_global_documents || [];
  return [...documents, ...globalDocuments];
};
const isGlobalMediaType = document => {
  return document?.__typename === 'GlobalMedia';
};
const getDocumentType = document => {
  if (document) {
    if (isGlobalMediaType(document)) {
      return document.media_purpose_type_code || '';
    }
    return document.metadata?.document_purpose_type_code || '';
  }
  return '';
};
const getDocumentPriority = document => isGlobalMediaType(document) ? document.priority_level_type_code : null;
const getPDPTargetingContext = listing => ({
  // This defines the shared custom context
  kind: 'blueprint-listing-usePage',
  // And whatever we want to target from now can be defined and added and will show
  // up in the tooling etc
  key: listing.listing_id,
  listing_status: listing.listing_status,
  asset_type: listing.listing_configuration?.asset_type
});

var listingUtils$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    formatDocumentsForDisplay: formatDocumentsForDisplay$1,
    getAllDocuments: getAllDocuments$1,
    getClosingCompanyData: getClosingCompanyData$1,
    getCreditBidAmount: getCreditBidAmount,
    getCreditBidState: getCreditBidState,
    getDocumentTitle: getDocumentTitle$1,
    getDocumentType: getDocumentType,
    getDocumentUrl: getDocumentUrl,
    getMarketingAttributeMessage: getMarketingAttributeMessage,
    getPDPTargetingContext: getPDPTargetingContext,
    getPartnershipDetails: getPartnershipDetails$1,
    getPaymentInformation: getPaymentInformation$1,
    getProvisionalSaleDateTime: getProvisionalSaleDateTime$1,
    getSellerDeedTypeLabel: getSellerDeedTypeLabel$1,
    getSpecialDocuments: getSpecialDocuments$1,
    getTitleInsurancePaidBy: getTitleInsurancePaidBy$1,
    isAuctionOnline: isAuctionOnline,
    isFCLMarketing: isFCLMarketing$1,
    isFclOnlineAuction: isFclOnlineAuction,
    isGlobalMediaType: isGlobalMediaType,
    isReservePriceAvailable: isReservePriceAvailable,
    isTPSIllinois: isTPSIllinois,
    isTPSOhio: isTPSOhio$1,
    splitEnumArrayByField: splitEnumArrayByField
});

const formatEnum = enumVal => (enumVal || '').split('_').map(_.capitalize).join(' ');
const BASE_COLORS = {
  BLUE: '#036EB5',
  // $utk-color-info
  GREEN: '#509B13',
  // $utk-dark-green lightened
  PURPLE: '#a639bc',
  // $utk-color-adc-light-purple
  GRAY: '#868b8e' // $utk-color-gray-dark-60
};
const FADED_LABEL_BASE = '#5c676e';
// [lighten, desaturation]
const labelSettings = [-5, 0];
const fadedSettings = [30, 60];
const fadedLabelSettings = [6, 0]; // lightest gray that's still AA compliant
const lightenAndDesaturateHexColor = (hexColor, lightenAmount, desaturationAmount) => {
  const color = tinycolor(hexColor);
  const lightenedColor = color.lighten(lightenAmount);
  const desaturatedLightenedColor = lightenedColor.desaturate(desaturationAmount);
  return desaturatedLightenedColor.toHexString();
};
const getColorVariations = baseColor => {
  const colorDark = lightenAndDesaturateHexColor(baseColor, ...labelSettings);
  return {
    color: baseColor,
    colorDark,
    colorFaded: lightenAndDesaturateHexColor(baseColor, ...fadedSettings),
    colorDarkFaded: lightenAndDesaturateHexColor(FADED_LABEL_BASE,
    // same gray for all variants
    ...fadedLabelSettings)
  };
};
const PRODUCT_TYPE_LABELS = _.mapValues(_.mapKeys(enums$5.ProductType, value => value), value => {
  if (value.includes('REO')) return 'Bank Owned';
  if (value === 'TRUSTEE' || value === 'FCL_MARKETING') return 'Foreclosure Sale';
  if (value === 'SHORT_SALE') return 'Short Sale';
  if (value.includes('PRIVATE_SELLER')) return 'Private Seller';
  return formatEnum(value);
});
/**
 * Note the "color" values below primarily correspond to the pin color (the dot).
 */
const getProductTypeColors = assetType => {
  return match(assetType).when(assetType => assetType === enums$5.AssetType.BankOwned, () => getColorVariations(BASE_COLORS.BLUE)).when(assetType => assetType === enums$5.AssetType.Foreclosure, () => getColorVariations(BASE_COLORS.GREEN)).when(assetType => assetType === enums$5.AssetType.ShortSale, () => getColorVariations(BASE_COLORS.PURPLE)).when(assetType => assetType === enums$5.AssetType.PrivateSeller, () => getColorVariations(BASE_COLORS.PURPLE)).otherwise(() => getColorVariations(BASE_COLORS.GRAY));
};
const getDisplayIndicator = listing => {
  const assetType = listing.listing_configuration?.asset_type || null;
  const productType = listing.listing_configuration?.product_type;
  const label = productType ? PRODUCT_TYPE_LABELS[productType] : undefined;
  const colorVariations = getProductTypeColors(assetType);
  return {
    label,
    description: getProductTypeDescription(listing),
    ...colorVariations
  };
};
// TODO: rename surrounding types/files once we move to cycle-based paths (not just dual-track)
// One synthetic path-listing per LOB, deduped and ordered PS → Trustee → FCL,
// so a card renders one indicator per path. Presence of dual_track_sibling_lobs
// (set only by the graph dedupe pass) is the gate — no FE flag.
const SIBLING_LISTING_ORDER = [enums$5.ProductType.PrivateSeller, enums$5.ProductType.Trustee, enums$5.ProductType.FclMarketing];
const siblingOrderIndex = productType => {
  const i = SIBLING_LISTING_ORDER.indexOf(productType || '');
  return i === -1 ? SIBLING_LISTING_ORDER.length : i;
};
const buildSiblingListingPaths = listing => {
  const siblings = listing.dual_track_sibling_lobs || [];
  if (!siblings.length) return [];
  const configs = [listing.listing_configuration, ...siblings.map(sibling => ({
    product_type: sibling.product_type,
    asset_type: sibling.asset_type
  }))];
  const seen = new Set();
  const unique = configs.filter(config => {
    const key = String(config?.product_type ?? config?.asset_type ?? '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (unique.length < 2) return [];
  return unique.sort((a, b) => siblingOrderIndex(a?.product_type) - siblingOrderIndex(b?.product_type)).map(config => ({
    ...listing,
    listing_configuration: config
  }));
};
// These Custom product types used only for determining descriptions
const CUSTOM_PRODUCT_TYPES = {
  BANK: 'bank',
  TPS_ONLINE: 'tps-online',
  TPS_LIVE: 'tps-live'
};
const getProductTypeDescription = listing => {
  const isOnlineEvent = isFclOnlineAuction(listing);
  const productType = listing.listing_configuration?.product_type;
  const isThirdPartyOnline = listing.listing_configuration?.is_third_party_online;
  let descriptionPropertyType;
  if (listing.event?.trustee_sale) {
    descriptionPropertyType = isOnlineEvent || isThirdPartyOnline ? CUSTOM_PRODUCT_TYPES.TPS_ONLINE : CUSTOM_PRODUCT_TYPES.TPS_LIVE;
  } else {
    descriptionPropertyType = productType && [enums$5.ProductType.Rentals, enums$5.ProductType.ShortSale, enums$5.ProductType.PrivateSellerInstitutional, enums$5.ProductType.PrivateSellerIndividual, enums$5.ProductType.PrivateSeller].includes(productType) ? productType : CUSTOM_PRODUCT_TYPES.BANK;
  }
  return match(descriptionPropertyType).when(descriptionPropertyType => descriptionPropertyType === enums$5.ProductType.ShortSale, () => ['A Lender-assisted sale when a borrower may be behind on their mortgage payments or owe more than the property is worth. Lender approval may be required.']).when(descriptionPropertyType => descriptionPropertyType.includes(enums$5.ProductType.PrivateSeller), () => ['This property is owned by a private seller.']).when(descriptionPropertyType => descriptionPropertyType === enums$5.ProductType.Rentals, () => ['Rentals are tenant-occupied properties with a lease in place.', 'This is a unique investment opportunity that could provide cash flow from day 1 after closing.']).when(descriptionPropertyType => descriptionPropertyType === CUSTOM_PRODUCT_TYPES.TPS_ONLINE, () => ['Foreclosures occur when a lender attempts to sell a property to recover the balance of the loan.', 'Foreclosure properties are generally sold at in-person public auctions, but this foreclosure sale is allowed to be conducted online.']).when(descriptionPropertyType => descriptionPropertyType === CUSTOM_PRODUCT_TYPES.BANK, () => ['Bank-owned properties are typically sold through online auctions. These properties are owned by the lending institution.']).when(descriptionPropertyType => descriptionPropertyType === CUSTOM_PRODUCT_TYPES.TPS_LIVE, () => ['These properties are generally sold at in-person public auctions.', 'Foreclosures occur when a lender attempts to sell a property to recover the balance of the loan.']).otherwise(() => []);
};

var BadgePriority;
(function (BadgePriority) {
  BadgePriority[BadgePriority["FIRST"] = 1] = "FIRST";
  BadgePriority[BadgePriority["SECOND"] = 2] = "SECOND";
  BadgePriority[BadgePriority["THIRD"] = 3] = "THIRD";
  BadgePriority[BadgePriority["FOURTH"] = 4] = "FOURTH";
  BadgePriority[BadgePriority["FIFTH"] = 5] = "FIFTH";
  BadgePriority[BadgePriority["SIXTH"] = 6] = "SIXTH";
  BadgePriority[BadgePriority["SEVENTH"] = 7] = "SEVENTH";
  BadgePriority[BadgePriority["EIGHTH"] = 8] = "EIGHTH";
  BadgePriority[BadgePriority["NINTH"] = 9] = "NINTH";
  // // these remain for backward compatibility, but also useful for approximate priorities
  // HIGH = 1,
  // MEDIUM = 4,
  // LOW = 7,
})(BadgePriority || (BadgePriority = {}));
var BadgePosition;
(function (BadgePosition) {
  BadgePosition["TOP"] = "top";
  BadgePosition["BOTTOM"] = "bottom";
})(BadgePosition || (BadgePosition = {}));
var BadgeMode;
(function (BadgeMode) {
  BadgeMode["EXPANDED"] = "expanded";
  BadgeMode["CONDENSED"] = "condensed";
})(BadgeMode || (BadgeMode = {})); // move to types file
var MarketingTags;
(function (MarketingTags) {
  MarketingTags["GOTO"] = "GOTO";
  MarketingTags["PRCD"] = "PRCD";
  MarketingTags["NEWV"] = "NEWV";
})(MarketingTags || (MarketingTags = {}));
var BadgesGroup;
(function (BadgesGroup) {
  BadgesGroup["Available"] = "Available";
  BadgesGroup["ImportantInfo"] = "Important Info";
  BadgesGroup["SellerTerms"] = "Seller Terms";
  BadgesGroup["PrivateSeller"] = "Private Seller";
  BadgesGroup["SellerDashboard"] = "Seller Dashboard";
  BadgesGroup["Other"] = "Other";
})(BadgesGroup || (BadgesGroup = {}));

var types$7 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get BadgeMode () { return BadgeMode; },
    get BadgePosition () { return BadgePosition; },
    get BadgePriority () { return BadgePriority; },
    get BadgesGroup () { return BadgesGroup; },
    get MarketingTags () { return MarketingTags; }
});

var _staticBlock$A;
class ListingBadge {
  constructor(listing, segment) {
    this.listing = listing;
    this.segment = segment;
  }

  /**
   * Decides whether this badge considers the current listing to have
   * everything it needs either to calculate the badge content. This takes
   * the place of what a lot of the guard conditions in pal already do.
   *
   * For example, a lot of v2 data right doesn't have bidding information, so if any badge has any bidding information
   * embedded, we can't render that right now.
   *
   * @param {*} listing
   * @param {*} badgeMode -- specifies which mode you are asking about
   * @returns Boolean
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  get content() {
    return '';
  }
  get title() {
    return null;
  }
  /**
   * Only to be used if different than standard listing icon.  Don't want to have to do this, but we're just not 100%
   * consistent yet between the different usages.
   */

  /**
   * Only to be used if different than standard listing icon.  Don't want to have to do this, but we're just not 100%
   * consistent yet between the different usages.
   */

  getShouldBeIncluded(props) {
    const constructedBadge = this.constructor;
    return constructedBadge.shouldBeIncluded?.(props) || false;
  }
  getOverrideProps(listing) {
    const constructedBadge = this.constructor;
    return constructedBadge.overrideProps?.(listing);
  }
  get(name) {
    // @ts-ignore next line
    return this.constructor[name];
  }
  static #_ = _staticBlock$A = () => (this.renderPriority = BadgePriority.FOURTH, this.titleIcon = 'check', this.title = '', this.textIcon = 'check', this.summary = 'Yes', this.position = BadgePosition.TOP, this.contentPositive = true, this.shouldBeHoisted = false, this.content = '', this.tooltipContent = '', this.color = undefined, this.foregroundColor = undefined, this.overrideProps = _ => {
    return {};
  }, this.shouldBeIncluded = _props => {
    // do not include for any modes - by default
    return false;
  }, this.condensedTitleIcon = '', this.condensedTitle = '');
}
_staticBlock$A();

var _staticBlock$z, _staticBlock2$b;
class ShortSaleBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && isShortSale(listing);
  }
  static #_ = _staticBlock$z = () => (this.id = 'ShortSaleBadge', this.title = 'Short Sale', this.titleIcon = 'sign', this.content = "This property is part of Auction.com's Short Sale program. Learn more");
}
_staticBlock$z();
class ShortSaleOpenHousesAvailableBadge extends ShortSaleBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    if (opts?.badgeMode === BadgeMode.CONDENSED) return false;
    const brokerAgent = getBrokerAgent(listing);
    return _.get(listing, 'listing_configuration.interior_access_allowed', true) && _.isPlainObject(brokerAgent) && isShortSale(listing);
  }
  static #_ = _staticBlock2$b = () => (this.id = 'ShortSaleOpenHousesAvailableBadge', this.summary = 'Open Houses Scheduled', this.titleIcon = 'unlock-alt', this.shouldBeHoisted = true, this.content = 'For dates and times or to schedule a private showing contact the listing agent');
}
_staticBlock2$b();

var _staticBlock$y;
class RentalBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    getProductType(listing) === graph.enums.ProductType.Rentals && isREO(listing);
  }
  static #_ = _staticBlock$y = () => (this.id = 'RentalBadge', this.title = 'Income Producing', this.summary = 'Yes', this.titleIcon = 'hands-usd', this.importantInfoRenderPriority = BadgePriority.NINTH);
}
_staticBlock$y();

var _staticBlock$x;
class FirstLookBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    if (opts?.badgeMode === BadgeMode.CONDENSED) return false;
    const isFirstLookEnabled = _.get(listing, 'listing_configuration.is_first_look_enabled', '');
    return Boolean(isFirstLookEnabled);
  }
  get content() {
    const formattedFirstLookDate = moment$1(this.listing.listing_configuration?.first_look_end_date).format('MM/DD/YYYY');
    return `Sold only to buyers who intend to live in the property or Approved Entities, until ${formattedFirstLookDate}`;
  }
  static #_ = _staticBlock$x = () => (this.title = 'First Look', this.id = 'FirstLookBadge', this.titleIcon = 'eye', this.color = 'info', this.textIcon = 'check', this.renderPriority = BadgePriority.FOURTH);
}
_staticBlock$x();

var _staticBlock$w, _staticBlock2$a;
class BrokerCoopAvailableBadge extends ListingBadge {
  // TODO: see if we can reconcile these two

  static shouldBeIncluded({
    listing
  }) {
    // same for all modes
    return Boolean(listing.listing_configuration?.broker_commission_message && isREO(listing)) && !isPrivateSeller(listing);
  }
  dynamicContent() {
    return `${this.listing.listing_configuration?.broker_commission_message} commission is available.`;
  }
  static #_ = _staticBlock$w = () => (this.title = 'Broker Co-op', this.id = 'BrokerCoopAvailableBadge', this.titleIcon = 'handshake', this.condensedTitle = 'Broker Commission Available', this.renderPriority = BadgePriority.SIXTH, this.contentPositive = true, this.summary = 'Available');
}
_staticBlock$w();
class BrokerCoopUnavailableBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && !BrokerCoopAvailableBadge.shouldBeIncluded({
      listing
    }) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock2$a = () => (this.title = 'Broker Co-op', this.id = 'BrokerCoopUnavailableBadge', this.titleIcon = 'handshake', this.textIcon = 'times', this.renderPriority = BadgePriority.SIXTH, this.contentPositive = false, this.summary = 'Not Offered', this.content = 'No Broker/Agent commission on this property. Learn more about Broker Co-op.');
}
_staticBlock2$a();

var _staticBlock$v, _staticBlock2$9, _staticBlock3$5;
const occupancyStatus = listing => _.get(listing, 'listing_configuration.occupancy_status', '');
class PropertyOccupiedBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    occupancyStatus(listing) === graph.enums.OccupancyStatus.Occupied;
  }
  static #_ = _staticBlock$v = () => (this.id = 'PropertyOccupiedBadge', this.title = 'Occupancy', this.titleIcon = 'users', this.textIcon = 'times', this.summary = 'Occupied', this.renderPriority = BadgePriority.SECOND, this.importantInfoRenderPriority = BadgePriority.FIRST, this.contentPositive = false, this.shouldBeHoisted = true);
}
_staticBlock$v();
class PropertyUnknownOccupancyBadge extends PropertyOccupiedBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    const isUnknown =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    occupancyStatus(listing) === graph.enums.OccupancyStatus.Unknown || _.isNil(occupancyStatus(listing));
    return opts?.badgeMode === BadgeMode.EXPANDED && isUnknown;
  }
  static #_ = _staticBlock2$9 = () => (this.id = 'PropertyUnknownOccupancyBadge', this.summary = 'Occupancy Unknown');
}
_staticBlock2$9();
class PropertyVacantBadge extends PropertyOccupiedBadge {
  static shouldBeIncluded({
    listing
  }) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return occupancyStatus(listing) === graph.enums.OccupancyStatus.Vacant;
  }
  get content() {
    return isREO(this.listing) ? 'Although the property is vacant, do not enter unless interior access is available and you receive permission from the owner or agent.' : 'Although the property is believed to be vacant, do not enter premises. Trespassing is a crime.';
  }
  static #_ = _staticBlock3$5 = () => (this.title = 'Occupancy', this.id = 'PropertyVacantBadge', this.titleIcon = 'users', this.textIcon = 'check', this.summary = 'Vacant', this.condensedTitle = 'Vacant', this.condensedTitleIcon = 'sign', this.renderPriority = BadgePriority.SECOND, this.importantInfoRenderPriority = BadgePriority.FIRST, this.contentPositive = true, this.shouldBeHoisted = true);
}
_staticBlock3$5();

var _staticBlock$u, _staticBlock2$8;
// TODO: update all apps, references in design system, and these here to use new graph field 'is_buyers_premium_available'
// currently @buyersPremiumAvailableValueReceived and @isBuyersPremiumAvailable are looking at two fields to maintain backwards compatibility
const buyersPremiumAvailableValueReceived = listing => Object.prototype.hasOwnProperty.call(listing.listing_configuration || {}, 'is_buyers_premium_available') || Object.prototype.hasOwnProperty.call(listing.listing_configuration || {}, 'buyer_premium_available');
const isBuyersPremiumAvailable$1 = listing => listing.listing_configuration?.buyer_premium_available || listing.listing_configuration?.is_buyers_premium_available;
const premiumMinimumAmount$1 = listing => listing.program_configuration?.buyers_premium_minimum_amount || 0;
const premiumPrimaryAmount$1 = listing => listing.program_configuration?.buyers_premium_primary_amount || 0;
const hasPremiumStrategySet = listing => premiumMinimumAmount$1(listing) > 0 || premiumPrimaryAmount$1(listing) > 0;
const hasZeroBuyersPremiumState = listing => premiumMinimumAmount$1(listing) === 0 && premiumPrimaryAmount$1(listing) === 0;
/**
 * Takes a const string template e.g. 'hello %s' and replaces it with passed in values
 * @param  {string} [template='%s']   String template to fill
 * @param  {Array}  [input=[]]        Array of values to use to fill in
 * @return {string}                   Resultant string
 */
const interpolateString = (template, input) => {
  const newTemplate = template || '%s'; // assume input fills if template unspecified/null
  const newInput = input.slice();
  /**
   * NOTE: Don't try replacing this function. You could use `.bind(newInput)` which would create a new function anyway
   * same as below
   */
  return newTemplate.replace(/%s/g, () => String(newInput.shift()));
};
const BUYERS_PREMIUM_EXPLANATIONS$1 = {
  MESSAGE: 'Winner will be charged a fee for this property. %s',
  FIXED_AMOUNT: '%s regardless of Winning Bid Amount.',
  PERCENTAGE_WITH_GUARANTEED_MINIMUM: '%s% of Winning Bid Amount (A minimum of %s will apply)'
};
class BuyerPremiumAvailableBadge extends ListingBadge {
  /**
   * This is here as a fallback for when we program configuration has buyers premium enabled, but the values for the
   * amounts don't seem correct (as is possible for new listings that are still being entered into the systemm so are
   * published, but not in their final form)
   */
  static shouldBeIncluded({
    listing,
    opts
  }) {
    return Boolean(opts?.badgeMode === BadgeMode.EXPANDED && isBuyersPremiumAvailable$1(listing) && hasPremiumStrategySet(listing) && (isTPSOnline$1(listing) || isREO(listing) || isPrivateSeller(listing) &&
    // TODO check if this condition is relevant for other than PS, the team decided to not risk it and just check it for PS but I think we should analyze other cases
    listing.listing_configuration?.buyers_premium_paid_by === graph.enums.ListingPaidBy.Buyer));
  }
  // NOTE: This was hella complex in v1 through plat-property and I expect between that service and resi-pal, there
  // will be some minor bugs in the relationships to these things in the enxt few months.
  //
  // I am not sure what seller-level informatino this means for primary buyer. Likely need an override for `content`
  // above also. Originaly logic for the text that resi-pal sends through currently is
  // https://git.infra.aucn.io/projects/PA/repos/platform-property/browse/src/main/java/com/auction/acp/property/das/service/impl/PropertyServiceImpl.java#1663,1665,1668,1681,1685-1686,1692-1693,1698,1703,1708,1714,1716

  get content() {
    const {
      buyers_premium_commission_type,
      buyers_premium_minimum_amount,
      buyers_premium_primary_amount
    } = this.listing.program_configuration || {};
    let suffix = '';
    if (buyers_premium_commission_type === graph.enums.CommissionStrategyType.Dollar) {
      suffix = interpolateString(BUYERS_PREMIUM_EXPLANATIONS$1.FIXED_AMOUNT, [formatMoney(String(buyers_premium_primary_amount))]);
    } else if (buyers_premium_commission_type === graph.enums.CommissionStrategyType.Percentage) {
      suffix = interpolateString(BUYERS_PREMIUM_EXPLANATIONS$1.PERCENTAGE_WITH_GUARANTEED_MINIMUM, [String(buyers_premium_primary_amount), formatMoney(String(buyers_premium_minimum_amount))]);
    }
    return interpolateString(BUYERS_PREMIUM_EXPLANATIONS$1.MESSAGE, [suffix]);
  }
  static #_ = _staticBlock$u = () => (this.id = 'BuyerPremiumAvailableBadge', this.title = "Buyer's Premium", this.titleIcon = 'dollar-sign', this.textIcon = 'times', this.contentPositive = false, this.overrideProps = listing => {
    const buyersPremiumMinAmount = premiumMinimumAmount$1(listing);
    const buyersPremiumAmount = premiumPrimaryAmount$1(listing);
    const type = _.get(listing.program_configuration, 'buyers_premium_commission_type');
    const calculatedSummary = [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    type === graph.enums.CommissionStrategyType.Percentage && buyersPremiumAmount ? `${buyersPremiumAmount}%` : '', buyersPremiumMinAmount ? formatMoney(String(buyersPremiumMinAmount)) : ''].filter(Boolean).join(' or ');
    return {
      summary: calculatedSummary || 'Applicable'
    };
  });
}
/**
 * This one is a little tricky as the condensed version is showing here when as a "positive case" by the fact
 * there is an absence of this field.  Hence the title and icon and negated to indicate no premium
 * (which is a good thing)
 *
 * NOTE: Just a reminder again. We have to handle complex cases here. There is no buyers premium applicable if we indicate
 * it's not available but that the value is 0, which is good. That is treated as a different case in the product than it
 * not being available and having no values at all (nobody seems to remember or find a reason _why_ this extra logic is
 * there or if it's worth the contortion we have to code to make it work).
 */
_staticBlock$u();
class BuyerPremiumUnavailableBadge extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return (isTPSOnline$1(listing) || isREO(listing)) && (buyersPremiumAvailableValueReceived(listing) && !isBuyersPremiumAvailable$1(listing) || isBuyersPremiumAvailable$1(listing) && hasZeroBuyersPremiumState(listing));
  }
  // istanbul ignore next
  static #_ = _staticBlock2$8 = () => (this.title = "Buyer's Premium", this.id = 'BuyerPremiumUnavailableBadge', this.titleIcon = 'dollar-sign', this.condensedTitle = "No Buyer's Premium", this.renderPriority = BadgePriority.FOURTH, this.condensedTitleIcon = 'ban', this.contentPositive = false, this.summary = 'Not Applicable', this.content = "Winner will not be charged a Buyer's Premium for this property.", this.overrideProps = listing => {
    const contentPositive = Boolean(isBuyersPremiumAvailable$1(listing) && hasZeroBuyersPremiumState(listing));
    let calculatedSummary = '';
    if (!isBuyersPremiumAvailable$1(listing)) {
      calculatedSummary = '';
    } else {
      const amount = premiumMinimumAmount$1(listing);
      calculatedSummary = _.isFinite(amount) ? String(formatMoney(String(amount))) : '';
    }
    return {
      contentPositive,
      calculatedSummary
    };
  });
}
_staticBlock2$8();

var _staticBlock$t, _staticBlock2$7;
class CashOnlyBadge extends ListingBadge {
  static get title() {
    return 'Cash Only';
  }
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && !_.get(listing, 'listing_configuration.financing_available', false) && !isPrivateSeller(listing);
  }
  get title() {
    const state = this.listing?.seller_property?.country_primary_subdivision;
    if (isForeclosure(this.listing) && state && CERTIFIED_FUNDS_STATES.includes(state)) {
      return 'Certified Funds Preferred';
    }
    return 'Cash Only';
  }
  get content() {
    const trustee = this.listing.event?.trustee_sale || false;
    const state = this.listing?.seller_property?.country_primary_subdivision;
    if (isForeclosure(this.listing) && state && CERTIFIED_FUNDS_STATES.includes(state)) {
      return `To bid on site at this auction, please have funds in the form of cashiers check or other certified funds present. ${trustee ? 'What should I bring?' : 'Learn more'}`;
    }
    return `Conventional financing is NOT accepted for this property. ${trustee ? 'What should I bring?' : 'Learn more'}`;
  }
  static #_ = _staticBlock$t = () => (this.id = 'CashOnlyBadge', this.titleIcon = 'money-bill', this.contentPositive = false);
}
_staticBlock$t();
class FinancingAvailableBadge extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    // applies to all modes
    return _.get(listing, 'listing_configuration.financing_available', false) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock2$7 = () => (this.summary = 'Eligible for Financing', this.titleIcon = 'money-bill', this.condensedTitle = 'Financing Available', this.renderPriority = BadgePriority.FIFTH, this.shouldBeHoisted = true, this.content = 'Buyers are responsible for determining financing eligibility prior to bidding. Learn More');
}
_staticBlock2$7();

var _staticBlock$s, _staticBlock2$6;
class OpenHouseScheduledBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    if (opts?.badgeMode === BadgeMode.CONDENSED) return false;
    const openHouseSchedules = listing.open_houses || [];
    // We don't check if there is a _valid_ date, only that a date exists. We can maybe look at changing this later on.
    return Boolean(Boolean(openHouseSchedules.length) && openHouseSchedules[0]?.date && isREO(listing));
  }
  static #_ = _staticBlock$s = () => (this.id = 'OpenHouseScheduledBadge', this.title = 'Open House', this.titleIcon = 'sign-in-alt', this.color = 'info');
}
_staticBlock$s();
const getMostRecentOpenHouse = listing => {
  const openHouseList = listing.open_houses || [];
  const now = new Date();
  const activeOpenHousePeriods = openHouseList.filter(openHouse => {
    if (!openHouse || typeof openHouse.local_date !== 'string' || typeof openHouse.start_time !== 'string') {
      return false;
    }
    const dateStr = `${openHouse.local_date}T${openHouse.start_time}`;
    const dateObj = new Date(dateStr);
    return !isNaN(dateObj.getTime()) && dateObj >= now;
  }).sort((a, b) => {
    // @ts-ignore we already filtered out undefined local_date or start_tiem in filter function
    const aDate = new Date(`${a.local_date}T${a.start_time}`); // eslint-disable-line
    // @ts-ignore we already filtered out undefined local_date or start_tiem in filter function
    const bDate = new Date(`${b.local_date}T${b.start_time}`); // eslint-disable-line
    // @ts-ignore we already filtered out undefined local_date or start_tiem in filter function
    return aDate - bDate; // eslint-disable-line
  });
  return activeOpenHousePeriods.length ? activeOpenHousePeriods[0] : null;
};
class NextOpenHouseBadge extends ListingBadge {
  get title() {
    const mostRecentOpenHouse = getMostRecentOpenHouse(this.listing);
    if (!mostRecentOpenHouse?.local_date) return '';
    const openHouseDate = new Date(`${mostRecentOpenHouse.local_date} ${mostRecentOpenHouse.end_time}`);
    return `Open ${openHouseDate.getMonth() + 1}/${openHouseDate.getDate()}`;
  }
  static shouldBeIncluded({
    listing
  }) {
    return Boolean(getMostRecentOpenHouse(listing)?.local_date);
  }
  static #_ = _staticBlock2$6 = () => (this.titleIcon = 'door-open', this.color = 'info', this.renderPriority = BadgePriority.FIRST);
}
_staticBlock2$6();

var _staticBlock$r, _staticBlock2$5, _staticBlock3$4;
class InteriorAccessAvailableBadge extends ListingBadge {
  // TODO: see if we can use the same icon for both (why not?)

  static shouldBeIncluded({
    listing
  }) {
    // check for all modes
    const brokerAgent = getBrokerAgent(listing);
    return _.get(listing, 'listing_configuration.interior_access_allowed', false) && _.isPlainObject(brokerAgent) && (isREO(listing) || isShortSale(listing)) && !Boolean(getMostRecentOpenHouse(listing)?.local_date);
  }
  get content() {
    const brokerAgent = getBrokerAgent(this.listing) || {};
    return `Access is available through the listing agent ${brokerAgent.party_name} Contact Agent`;
  }
  static #_ = _staticBlock$r = () => (this.id = 'InteriorAccessAvailableBadge', this.title = 'Interior Access', this.titleIcon = 'door-open', this.color = 'adcLightGreen', this.condensedTitleIcon = 'door-open', this.renderPriority = BadgePriority.THIRD);
}
_staticBlock$r();
class InteriorAccessAvailableNoAgentBadge extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    // check for all modes
    const brokerAgent = getBrokerAgent(listing);
    return _.get(listing, 'listing_configuration.interior_access_allowed', false) && !brokerAgent && (isREO(listing) || isShortSale(listing)) && !Boolean(getMostRecentOpenHouse(listing)?.local_date);
  }
  static #_ = _staticBlock2$5 = () => (this.id = 'InteriorAccessAvailableNoAgentBadge', this.title = 'Interior Access', this.titleIcon = 'door-open', this.condensedTitleIcon = 'door-open', this.renderPriority = BadgePriority.THIRD, this.color = 'adcLightGreen', this.content = 'Licensed agents only, please contact {{interiorAccessNumber}} for details.');
}
_staticBlock2$5();
class InteriorAccessUnavailableBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && !_.get(listing, 'listing_configuration.interior_access_allowed', false) && (isREO(listing) || isShortSale(listing));
  }
  static #_ = _staticBlock3$4 = () => (this.title = 'Interior Access', this.id = 'InteriorAccessUnavailableBadge', this.titleIcon = 'unlock-alt', this.summary = 'No', this.contentPositive = false, this.content = 'Interior access is not available for this property.');
}
_staticBlock3$4();

var _staticBlock$q, _staticBlock2$4, _staticBlock3$3, _staticBlock4$2;
const listingStatus = listing => _.get(listing, 'listing_status', '');
class ClearedForSaleYesBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && listingStatus(listing) === graph.enums.ListingStatus.SalePendingCleared && isTPSLive$1(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock$q = () => (this.title = 'Cleared For Auction', this.id = 'ClearedForSaleYesBadge', this.titleIcon = 'clipboard-check', this.contentPositive = true, this.summary = 'Yes', this.content = 'We confirmed that this property will sell on auction day. It should not be postponed or cancelled.', this.renderPriority = BadgePriority.FIRST);
}
_staticBlock$q();
class ClearedForSaleComingSoonBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && (listingStatus(listing) === graph.enums.ListingStatus.PostponedPendingScheduling || listingStatus(listing) === graph.enums.ListingStatus.SaleFailed) && isTPSLive$1(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock2$4 = () => (this.title = 'Cleared For Auction', this.id = 'ClearedForSaleComingSoonBadge', this.titleIcon = 'clipboard-check', this.contentPositive = false, this.summary = 'No', this.content = 'This property is postponed. Awaiting new auction date and time.');
}
_staticBlock2$4();
class ClearedForSalePendingBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && listingStatus(listing) === graph.enums.ListingStatus.SalePending && isTPSLive$1(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock3$3 = () => (this.title = 'Cleared For Auction', this.id = 'ClearedForSalePendingBadge', this.titleIcon = 'clipboard-check', this.contentPositive = false, this.summary = 'Not Yet', this.content = 'The auction is scheduled. It may be cancelled or postponed.');
}
_staticBlock3$3();
class ClearedForSalePostponedBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && listingStatus(listing) === graph.enums.ListingStatus.Postponed && isTPSLive$1(listing) && !isPrivateSeller(listing);
  }
  get content() {
    const postponementDate = this.listing.listing_configuration?.postponement_new_auction_date || '';
    const postponedDate = moment$1(postponementDate, DEFAULT_DATE_FORMAT);
    const outputDate = postponedDate.isValid() ? postponedDate.format('MMM DD') : 'TBD';
    return `No, this auction has been postponed to ${outputDate}`;
  }
  static #_ = _staticBlock4$2 = () => (this.title = 'Cleared For Auction', this.id = 'ClearedForSalePostponedBadge', this.titleIcon = 'clipboard-check', this.contentPositive = false, this.summary = 'No');
}
_staticBlock4$2();

var _staticBlock$p, _staticBlock2$3, _staticBlock3$2, _staticBlock4$1;
class ConductedByADCOnlineBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && isTPSOnline$1(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock$p = () => (this.title = 'Conducted by Auction.com', this.id = 'ConductedByADCOnlineBadge', this.titleIcon = 'gavel', this.content = 'This is an online auction conducted by Auction.com. Learn More.');
}
_staticBlock$p();
class ConductedByADCLiveBadge extends ConductedByADCOnlineBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && isTPSLive$1(listing) && hasADCAuctioneer(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock2$3 = () => (this.id = 'ConductedByADCLiveBadge', this.content = 'Auction.com will conduct the sale in person. Download the app for real time updates and time-saving benefits.');
}
_staticBlock2$3();
class NotConductedByADCLive extends ConductedByADCOnlineBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && isTPSLive$1(listing) && !hasADCAuctioneer(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock3$2 = () => (this.id = 'NotConductedByADCLive', this.summary = 'No', this.contentPositive = false, this.content = 'This in-person auction will be conducted by a third party at a county location. Learn More.');
}
_staticBlock3$2();
class NotConductedByADCFCLBadge extends ConductedByADCOnlineBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && isFCLMarketing$2(listing) && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock4$1 = () => (this.id = 'NotConductedByADCFCLBadge', this.summary = 'No', this.contentPositive = false, this.content = 'This is an online auction conducted by a third party at the county website. Learn More');
}
_staticBlock4$1();

var _staticBlock$o;
class AgentInformationBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && _.isPlainObject(getBrokerAgent(listing)) && isREO(listing);
  }
  static #_ = _staticBlock$o = () => (this.title = 'Contact Agent Info', this.id = 'AgentInformationBadge', this.titleIcon = 'user-tie', this.content = 'Get in touch with the listing agent for this property');
}
_staticBlock$o();

var _staticBlock$n;
class SubjectToRedemptionBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && getProductType(listing) === 'REDEMPTION' && isREO(listing);
  }
  get content() {
    const redemptionDate = String(this.listing.listing_configuration?.redemption_expiration_date || '');
    const date = moment$1.tz(redemptionDate, DEFAULT_DATE_FORMAT, 'America/Los_Angeles');
    const contextSuffix = date.isValid() ? ` until ${date.format('MMM DD, YYYY')}` : '.';
    return `Per state laws, there is a period when certain parties have an opportunity to "redeem" a foreclosed property${contextSuffix} Learn More`;
  }
  static #_ = _staticBlock$n = () => (this.id = 'SubjectToRedemptionBadge', this.title = 'Subject To Redemption', this.titleIcon = 'exclamation-triangle', this.textIcon = 'times', this.contentPositive = false, this.importantInfoRenderPriority = BadgePriority.FOURTH);
}
_staticBlock$n();

var _staticBlock$m;
class ForeclosureContactInformationBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && isTPS(listing) && _.isPlainObject(getForeclosureContact(listing));
  }
  get content() {
    const contact = getForeclosureContact(this.listing) || {};
    const secondLine = contact.party_phone ? `Borrowers/homeowners should call {{${contact.party_phone}}}` : '';
    return `${contact.party_name || ''} \n
        ${secondLine}`;
  }
  static #_ = _staticBlock$m = () => (this.id = 'ForeclosureContactInformationBadge', this.title = 'Foreclosure Contact Info', this.titleIcon = 'phone', this.importantInfoRenderPriority = BadgePriority.FIFTH);
}
_staticBlock$m();

var _staticBlock$l;
class TitleAndLiensBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    // only applies to expanded, hide for condensed
    return opts?.badgeMode === BadgeMode.EXPANDED && !isPrivateSeller(listing);
  }
  static #_ = _staticBlock$l = () => (this.id = 'TitleAndLiensBadge', this.title = 'Title and Liens', this.summary = 'Title and Liens', this.titleIcon = 'file-certificate', this.contentPositive = false, this.shouldBeHoisted = true, this.importantInfoRenderPriority = BadgePriority.THIRD);
}
_staticBlock$l();

var _staticBlock$k;
class RemoteBidBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    opts
  }) {
    const {
      is_remote_bid_enabled,
      is_remote_before_and_during_auction_enabled
    } = listing.listing_summary || {};
    const remoteBidEnabled = is_remote_bid_enabled || is_remote_before_and_during_auction_enabled;
    return Boolean(opts?.badgeMode === BadgeMode.CONDENSED && remoteBidEnabled);
  }
  static #_ = _staticBlock$k = () => (this.title = 'Remote Bid', this.id = 'RemoteBidBadge', this.summary = '', this.renderPriority = BadgePriority.FIRST);
}
_staticBlock$k();

var _staticBlock$j;
/**
 * Only used in condensed mode at this time.
 */
class NewlyListedBadge extends ListingBadge {
  // not intended for expanded mode - at this time

  static shouldBeIncluded({
    listing,
    opts
  }) {
    // check for condensed mode, hide for expanded
    return Boolean(opts?.badgeMode === BadgeMode.CONDENSED && listing.primary_property?.is_newly_listed);
  }
  static #_ = _staticBlock$j = () => (this.title = 'Newly Listed', this.id = 'NewlyListedBadge', this.summary = '', this.titleIcon = 'certificate', this.content = '', this.renderPriority = BadgePriority.FIRST);
}
_staticBlock$j();

var _staticBlock$i;
class FinanceAllowedBadge extends ListingBadge {
  static shouldBeIncluded() {
    return true;
  }
  get content() {
    return this.listing.seller_terms?.finance_terms?.finance_preference === enums$5.ListingFinanceType.Conventional ? 'Yes' : 'No';
  }
  static #_ = _staticBlock$i = () => (this.title = 'Financing Allowed', this.id = 'FinanceAllowedBadge', this.titleIcon = 'money-bill', this.contentPositive = false, this.tooltipContent = 'Indicates whether the property can be purchased with conventional financing options. If "No," the purchase must be with cash or cash equivalent, such as hard money loans.');
}
_staticBlock$i();

var _staticBlock$h;
class AcceptedContingenciesBadge extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return isPrivateSeller(listing);
  }
  get content() {
    return [this.listing.seller_terms?.finance_terms?.is_contingency && 'Financing', this.listing.seller_terms?.inspection_terms?.is_contingency && 'Inspection', this.listing.seller_terms?.inspection_terms?.is_option_contingency && 'Option Period'].filter(Boolean).join(', ') || 'None';
  }
  static #_ = _staticBlock$h = () => (this.title = 'Accepted Contingencies', this.titleIcon = 'file-signature', this.contentPositive = false, this.sellerTermsRenderPriority = BadgePriority.SECOND, this.tooltipContent = 'List of contingencies that the seller is willing to consider such as inspection, appraisal, or financing. "None" means the sale is as-is, with no contingencies.');
}
_staticBlock$h();

var _staticBlock$g, _staticBlock2$2, _staticBlock3$1, _staticBlock4, _staticBlock5;
class RentBackBadge extends ListingBadge {
  static shouldBeIncluded() {
    return true;
  }
  get content() {
    const rentBack = this.listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    return rentBack ? `${rentBack} ${pluralize('Day', rentBack)}` : 'None';
  }
  static #_ = _staticBlock$g = () => (this.id = 'RentBackBadge', this.title = 'Rentback', this.titleIcon = 'house-person-return', this.contentPositive = false, this.tooltipContent = 'The rentback period is the number of days the seller is requesting to stay in the property after the sale closes.');
}
_staticBlock$g();
class RentbackOffer extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    const rentbackPeriod = listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    return Boolean(!isTPS(listing) && rentbackPeriod);
  }
  static #_ = _staticBlock2$2 = () => (this.id = 'RentbackOffer', this.title = 'Rentback', this.titleIcon = 'user-tie', this.renderPriority = BadgePriority.THIRD, this.importantInfoRenderPriority = BadgePriority.SECOND);
}
_staticBlock2$2();
class RentbackAuction extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    const rentbackPeriod = listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    return Boolean(isTPS(listing) && rentbackPeriod);
  }
  static #_ = _staticBlock3$1 = () => (this.id = 'RentbackAuction', this.title = 'Rentback', this.titleIcon = 'user-tie', this.importantInfoRenderPriority = BadgePriority.SECOND);
}
_staticBlock3$1();
class RentbackRequestedPeriod extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    const rentbackPeriod = listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    const isAuctionDisposition = listing.strategy?.selling_method_attributes?.online_segment_type === enums$5.OnlineAuctionSegmentType.English;
    return Boolean(!isAuctionDisposition && rentbackPeriod);
  }
  get content() {
    const rentbackPeriod = this.listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    return `Rentback for ${rentbackPeriod} days`;
  }
  static #_ = _staticBlock4 = () => (this.title = 'Rentback Requested', this.titleIcon = 'house-user', this.sellerTermsRenderPriority = BadgePriority.SIXTH);
}
_staticBlock4();
class RentbackRequiredPeriod extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    const rentbackPeriod = listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    const isAuctionDisposition = listing.strategy?.selling_method_attributes?.online_segment_type === enums$5.OnlineAuctionSegmentType.English;
    return Boolean(isAuctionDisposition && rentbackPeriod);
  }
  get content() {
    const rentbackPeriod = this.listing.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    return `Rentback for ${rentbackPeriod} days`;
  }
  static #_ = _staticBlock5 = () => (this.title = 'Rentback Required', this.titleIcon = 'house-user', this.sellerTermsRenderPriority = BadgePriority.SIXTH);
}
_staticBlock5();

var _staticBlock$f;
class PrivateToursBadge extends ListingBadge {
  static shouldBeIncluded() {
    return true;
  }
  get content() {
    return this.listing.listing_configuration?.are_private_tours_available ? 'Yes' : 'No';
  }
  static #_ = _staticBlock$f = () => (this.title = 'Private Tours Allowed', this.titleIcon = 'house-lock', this.contentPositive = false, this.tooltipContent = 'This property allows private tours upon request to the listing agent.');
}
_staticBlock$f();

var _staticBlock$e;
class EarnestMoneyDepositBadge extends ListingBadge {
  static shouldBeIncluded(_listingArgs) {
    return true;
  }
  get content() {
    const {
      program_configuration
    } = this.listing;
    const primaryAmount = program_configuration?.earnest_money_deposit_primary_amount ? `${String(program_configuration?.earnest_money_deposit_primary_amount)}%` : '';
    const minimunAmount = program_configuration?.earnest_money_deposit_minimum_amount ? `${formatMoney$2(program_configuration?.earnest_money_deposit_minimum_amount)}` : '';
    if (!primaryAmount) {
      return minimunAmount;
    }
    if (!minimunAmount) {
      return primaryAmount;
    }
    return `${primaryAmount} (${minimunAmount} min)`;
  }
  static #_ = _staticBlock$e = () => (this.title = 'Earnest Money Deposit', this.titleIcon = 'money-check-dollar', this.moneyValue = false, this.contentPositive = false, this.sellerTermsRenderPriority = BadgePriority.FOURTH, this.tooltipContent = 'A deposit placed by the buyer to let the seller know that the buyer is earnest or serious about buying the property.');
}
_staticBlock$e();
class EarnestMoneyDepositBadgeTerm extends EarnestMoneyDepositBadge {
  static shouldBeIncluded({
    listing
  }) {
    const {
      program_configuration
    } = listing;
    return Boolean((program_configuration?.earnest_money_deposit_primary_amount || program_configuration?.earnest_money_deposit_minimum_amount) && isPrivateSeller(listing));
  }
  get content() {
    const {
      program_configuration
    } = this.listing;
    const formattedMinimumAmount = program_configuration?.earnest_money_deposit_minimum_amount && formatMoney$2(program_configuration?.earnest_money_deposit_minimum_amount);
    if (!program_configuration?.earnest_money_deposit_primary_amount) {
      return String(formattedMinimumAmount);
    }
    if (!program_configuration?.earnest_money_deposit_minimum_amount) {
      return `${program_configuration?.earnest_money_deposit_primary_amount}%`;
    }
    return `${program_configuration?.earnest_money_deposit_primary_amount}% (${formattedMinimumAmount} min)`;
  }
}

var _staticBlock$d, _staticBlock2$1;
class FinanceContingency extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return Boolean(listing.seller_terms?.finance_terms?.is_contingency);
  }
  static #_ = _staticBlock$d = () => (this.id = 'FinanceContingency', this.title = 'Finance Contingency', this.titleIcon = 'user-tie', this.importantInfoRenderPriority = BadgePriority.SIXTH);
}
_staticBlock$d();
class InspectionContigency extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return Boolean(listing.seller_terms?.inspection_terms?.is_contingency);
  }
  static #_ = _staticBlock2$1 = () => (this.id = 'InspectionContigency', this.title = 'Inspection Contingency', this.titleIcon = 'user-tie', this.importantInfoRenderPriority = BadgePriority.SEVENTH);
}
_staticBlock2$1();

var _staticBlock$c;
class OptionFee extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return Boolean(listing.seller_terms?.inspection_terms?.is_option_contingency && listing.seller_property?.country_primary_subdivision && ['GA', 'TX'].includes(listing.seller_property.country_primary_subdivision));
  }
  static #_ = _staticBlock$c = () => (this.id = 'OptionFee', this.title = 'Option Fee', this.titleIcon = 'user-tie', this.importantInfoRenderPriority = BadgePriority.EIGHTH);
}
_staticBlock$c();

var _staticBlock$b;
class AcceptedPaymentTypesBadge extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return isPrivateSeller(listing);
  }
  get content() {
    // Adding logic to check if the listing has a finance preference or if financing is available.
    // This does not change whether the badge should be included, as it should always be displayed for PS.
    const hasFinancePreference = !!this.listing.seller_terms?.finance_terms?.finance_preference && this.listing.seller_terms.finance_terms.finance_preference !== enums$5.ListingFinanceType.Cash;
    const hasFinancingAvailable = this.listing.listing_configuration?.financing_available;
    return hasFinancePreference || hasFinancingAvailable ? 'Cash, Financing' : 'Cash';
  }
  static #_ = _staticBlock$b = () => (this.title = 'Accepted Payment Type', this.titleIcon = 'file-lines', this.sellerTermsRenderPriority = BadgePriority.FIRST);
}
_staticBlock$b();

var _staticBlock$a;
class OptionPeriod extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    return Boolean(isPrivateSeller(listing) && listing.seller_property?.country_primary_subdivision && ['GA', 'TX'].includes(listing.seller_property.country_primary_subdivision));
  }
  get content() {
    return this.listing.seller_terms?.inspection_terms?.is_option_contingency ? 'Yes' : 'No';
  }
  static #_ = _staticBlock$a = () => (this.title = 'Option Period', this.titleIcon = 'magnifying-glass', this.sellerTermsRenderPriority = BadgePriority.THIRD);
}
_staticBlock$a();

var _staticBlock$9;
class AdditionalTermsBadge extends ListingBadge {
  static shouldBeIncluded({
    listing
  }) {
    const auctioneerInfo = listing.marketing_attributes?.find(item => item?.attribute_type === enums$5.MarketingAttributeType.AuctioneerLicensing);
    const transactionDetails = listing.marketing_attributes?.find(item => item?.attribute_type === enums$5.MarketingAttributeType.TransactionDetails);
    return Boolean(auctioneerInfo || transactionDetails?.message);
  }
  static #_ = _staticBlock$9 = () => (this.id = 'AdditionalTermsBadge', this.title = 'Additional Documents', this.titleIcon = 'file-lines', this.sellerTermsRenderPriority = BadgePriority.SEVENTH, this.content = 'Read the additional documents here');
}
_staticBlock$9();

// NOTE: `bidding_status` and `current_user_bidding_status` mean the same thing. The former is deprecated is remains
// here for backwards compat. Applications should only use `current_user_*` fields for anything that requires a
// contextual current user to resolve the value.
const AUTHENTICATED_USER_LIVE_AUCTION_SEGMENT_FIELDS = gql$5`
  fragment AuthenticatedUserLiveAuctionSegmentFragment on LiveAuctionSegment {
    current_highest_bid {
      belongs_to_current_user
    }
    current_user_highest_bid {
      bid_amount
    }
    current_user_remote_bid {
      remote_bid_amount
      purchase_profile_id
    }
    current_user_bidding_status
    bidding_status
    bidder_count
    bid_count
  }
`;
const getLatestLiveAuctionSegments = (controller, hasAuthenticatedUser, listingIds) => {
  if (!listingIds) {
    return Promise.resolve();
  }
  return fetch.forAuctionGraph({
    variables: {
      listingIds,
      hasAuthenticatedUser
    },
    query: gql$5`
          query ${embeddableOperation('blueprint_getTransactionalLiveSegments')}($listingIds: [ID!]!, $hasAuthenticatedUser: Boolean!) {
            live_auction_segments_batched(
              filters: { listing_ids: $listingIds }
            ) {
              listing_id
              update_date
              auction_segment_id
              current_highest_bid {
                bid_amount
                bid_id
                bid_type
                listing_id
                updated_date
              }
              end_status
              configuration {
                bid_increment_amount
                state_deposit_rule
              }
              pending_update_status
              segment_status
              starting_bid_amount
              bid_extend {
                bid_extend_date_time
              }
              live_auction {
                location {
                  lat
                  lon
                }
              }
              ...AuthenticatedUserLiveAuctionSegmentFragment @include(if: $hasAuthenticatedUser)
            }
          }
          ${AUTHENTICATED_USER_LIVE_AUCTION_SEGMENT_FIELDS}
        `
  }, {
    controller
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    // TODO: we need to specify this as retryable if it's the initial polling
    if (data === null || data.live_auction_segments_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest live segments', errors);
    }
    return data.live_auction_segments_batched;
  });
};
const getLatestLiveFocusedListingsSegments = (controller, listingIds, bidderLabelIntent) => {
  if (!listingIds) {
    return Promise.resolve();
  }
  return fetch.forAuctionGraph({
    variables: {
      listingIds,
      bidderLabelIntent
    },
    query: gql$5`
          query ${embeddableOperation('blueprint_getTransactionalFocusedListings')}($listingIds: [ID!]!, $bidderLabelIntent: BidderLabelIntent) {
            live_auction_segments_batched(
              filters: { listing_ids: $listingIds }
            ) {
              listing_id
              update_date
              auction_segment_id
              bids {
                bid_id
                bid_amount
                bid_type
                bidder_label(intent:$bidderLabelIntent)
                bidder_number
                belongs_to_current_user
                listing_id
                updated_date
              }
            }
          }
        `
  }, {
    controller
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    // TODO: we need to specify this as retryable if it's the initial polling
    if (data === null || data.live_auction_segments_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest live segments for focused listings', errors);
    }
    return data.live_auction_segments_batched;
  });
};
const getLatestLiveListings = (listingIds, hasAuthenticatedUser, controller) => {
  return fetch.forAuctionGraph({
    query: gql$5`
            query ${embeddableOperation('blueprint_getLiveListings')}($listingIds: [ID!]!, $hasAuthenticatedUser: Boolean!) {
              listings_batched(filters: { listing_ids: $listingIds }) {
                update_date
                listing_id
                listing_status
                listing_status_label
                auction {
                  starting_bid
                }
                reserve_price @include(if: $hasAuthenticatedUser)
              }
            }
          `,
    variables: {
      listingIds,
      hasAuthenticatedUser
    }
  }, {
    controller
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.listings_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest live listings', errors);
    }
    return data.listings_batched;
  });
};
const submitLiveRemoteBid = (segment, input) => {
  return fetch.forAuctionGraph({
    query: gql$5`
        mutation ${embeddableOperation('blueprint_submitRemoteBid')}($input: SubmitRemoteBidInput!) {
          submitRemoteBid(input: $input) {
            party_id
          }
        }
      `,
    variables: {
      input: {
        listing_id: segment.listing_id,
        purchase_profile_id: input.purchase_profile_id,
        bid_amount: input.bid_amount
      }
    }
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.submitRemoteBid === null) {
      throw partialGraphErrorFactory$3('Unable to submit remote bid', errors);
    }
    return data.submitRemoteBid;
  });
};
const updateLiveRemoteBid = (segment, input) => {
  return fetch.forAuctionGraph({
    query: gql$5`
        mutation ${embeddableOperation('blueprint_updateRemoteBid')}($input: UpdateRemoteBidInput!) {
          updateRemoteBid(input: $input) {
            party_id
          }
        }
      `,
    variables: {
      input: {
        listing_id: segment.listing_id,
        bid_amount: input.bid_amount
      }
    }
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.updateRemoteBid === null) {
      throw partialGraphErrorFactory$3('Unable to update remote bid', errors);
    }
    return data.updateRemoteBid;
  });
};

const LIVE_BIDDING_MINIMUM = 1;
const LIVE_BID_INCREMENT_MINIMUM = 100;
const VALID_LISTING_STATUSES_TO_BID = new Set([enums$5.ListingStatus.AuctionInProgress, enums$5.ListingStatus.SalePending, enums$5.ListingStatus.SalePendingCleared]);
const DISALLOWED_SEGMENT_END_STATUSES_TO_BID = new Set([enums$5.LiveAuctionSegmentBidEndStatus.FinalizingBid, enums$5.LiveAuctionSegmentBidEndStatus.Sold, enums$5.LiveAuctionSegmentBidEndStatus.NoSale]);
const calculateAskingBid = (segment,
// @deprecated listing.auction.starting_bid, use segment.starting_bid_amount instead
listing) => {
  // It is common for some listings to not have a known opening bid until late into the auction. For these listings we
  // allow $0.01 bids to increase participation, even if we know it's not likely anywhere near the winning bid.
  // (technically, `0.01` might be correct as the next least valid bid, but 1 is a nicer round number is all)
  const startingBid = segment?.starting_bid_amount || listing?.auction?.starting_bid || LIVE_BIDDING_MINIMUM;
  const bidIncrementAmount = segment?.configuration?.bid_increment_amount;
  const currentHighestBidAmount = segment?.current_highest_bid?.bid_amount;
  if (currentHighestBidAmount) return currentHighestBidAmount + (bidIncrementAmount || LIVE_BID_INCREMENT_MINIMUM);
  // starting bid amount is already default to 1, bid increment amount does not need to do so
  return bidIncrementAmount ? startingBid + bidIncrementAmount : startingBid;
};
const calculateAuctionValues$1 = (segment,
// @deprecated listing.auction.starting_bid, use segment.starting_bid_amount instead
listing) => {
  return {
    askingBidAmount: calculateAskingBid(segment, listing),
    currentUserHighestBidAmount: getCurrentUserHighestBidAmount(segment),
    currentHighestBidAmount: segment?.current_highest_bid?.bid_amount,
    currentUserNextBidAmount: calculateNextBid(segment, listing, getCurrentUserHighestBidAmount(segment))
  };
};
const calculateNextBid = (segment,
// @deprecated listing.auction.starting_bid, use segment.starting_bid_amount instead
listing, defaultInitialValue) => {
  const bidIncrementAmount = segment?.configuration?.bid_increment_amount;
  const startingBid = segment?.starting_bid_amount || listing?.auction?.starting_bid || LIVE_BIDDING_MINIMUM;
  const currentHighestBidAmount = segment?.current_highest_bid?.bid_amount;
  let nextBidAmount = bidIncrementAmount ? startingBid + bidIncrementAmount : startingBid;
  if (defaultInitialValue) nextBidAmount = Math.max(defaultInitialValue + (bidIncrementAmount || LIVE_BID_INCREMENT_MINIMUM), nextBidAmount);
  if (currentHighestBidAmount) nextBidAmount = Math.max(currentHighestBidAmount + (bidIncrementAmount || LIVE_BID_INCREMENT_MINIMUM), nextBidAmount);
  return nextBidAmount;
};
const calculateProposedBidBreakdown = (segment, proposedBidAmount) => {
  const stateDepositRule = segment.configuration?.state_deposit_rule;
  // I talked to Paulo and asked him about this being null,
  // and he told me we can assume that when this field is not present is 100% we can come back into this later because I think this should always be populated.
  const calculatedStateDepositRule = String(stateDepositRule || '100%');
  const auctionExpression = AuctionExpression.parse(calculatedStateDepositRule);
  return {
    stateDepositRule: calculatedStateDepositRule,
    requiredStateDepositAmount: auctionExpression.evaluate(proposedBidAmount)
  };
};
const getBiddingStatusText$1 = segment => {
  switch (segment.current_user_bidding_status || segment.bidding_status) {
    case enums$5.LiveAuctionSegmentBiddingStatus.Won:
      return {
        id: 'bidding:live:bid_status',
        title: "You've Won",
        intent: 'POSITIVE'
      };
    default:
      return null;
  }
};
const getCurrentUserHighestBidAmount = segment => segment?.current_user_remote_bid?.remote_bid_amount || segment?.current_user_highest_bid?.bid_amount || null;
const getStateDepositValues = (segment, listing) => {
  const stateDepositRule = segment.configuration?.state_deposit_rule;
  const calculatedStateDepositRule = String(stateDepositRule || '100%');
  const auctionExpression = AuctionExpression.parse(calculatedStateDepositRule);
  const values = auctionExpression.getValues();
  const stateDepositPaymentDate = listing?.trustee_contract?.expected_payments?.find(p => p?.payment_reason === enums$5.TrusteeContractExpectedPaymentReason.StateDeposit)?.payment_date;
  const finalPaymentDeadline = listing?.trustee_contract?.expected_payments?.find(p => !p?.payment_reason)?.payment_date;
  return {
    stateDepositRule: calculatedStateDepositRule,
    stateDepositLabel: values?.label,
    stateDepositShortenedLabel: values?.shortenedLabel,
    stateDepositType: values?.type,
    stateDepositPaymentDate: stateDepositPaymentDate ? new Date(stateDepositPaymentDate) : undefined,
    finalPaymentDeadline: finalPaymentDeadline ? new Date(finalPaymentDeadline) : undefined,
    shouldShowStateDeposit: Boolean(stateDepositRule) && stateDepositRule !== '100%'
  };
};
const getStateDepositText = (_segment, requiredStateDepositAmount) => {
  if (!requiredStateDepositAmount) return null;
  const message = {
    id: 'bidding:live:state_deposit_text',
    title: `A winning bid deposit of ${formatMoney$2(requiredStateDepositAmount)} will be debited from your available Remote Bid funds at time of sale.`,
    intent: 'NEUTRAL'
  };
  return message;
};
const isValidListingStatusesToBid = listing => VALID_LISTING_STATUSES_TO_BID.has(listing.listing_status);
const isAcceptingRemoteBids = (segment, listing) => isValidListingStatusesToBid(listing) && (!segment.end_status || !DISALLOWED_SEGMENT_END_STATUSES_TO_BID.has(segment.end_status));
const submitOrUpdateRemoteBid = async (segment, bidAmount, purchaseProfileId) => {
  const userAlreadyPlacedBid = Boolean(segment?.current_user_remote_bid);
  if (userAlreadyPlacedBid) {
    await updateLiveRemoteBid(segment, {
      bid_amount: bidAmount
    });
  } else {
    await submitLiveRemoteBid(segment, {
      purchase_profile_id: purchaseProfileId,
      bid_amount: bidAmount
    });
  }
  // If there's a current_highest_bid that belongs to the user, do not update the bidding_status to PlacingBid
  // biddingStatus is deprecated should use currentUserBiddingStatus, but we need to keep it for now
  const biddingStatus = segment?.current_highest_bid && segment?.current_highest_bid.belongs_to_current_user ? segment?.bidding_status : enums$5.LiveAuctionSegmentBiddingStatus.PlacingBid;
  const currentUserBiddingStatus = segment?.current_highest_bid && segment?.current_highest_bid.belongs_to_current_user ? segment?.current_user_bidding_status : enums$5.LiveAuctionSegmentBiddingStatus.PlacingBid;
  // @ts-ignore resolving symbol
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
  segment[SPECIAL_SELF_REFERENCE].attemptWriteRef?.current({
    data: [{
      ...segment,
      bidding_status: biddingStatus,
      current_user_bidding_status: currentUserBiddingStatus,
      current_user_remote_bid: {
        remote_bid_amount: bidAmount,
        purchase_profile_id: purchaseProfileId
      }
    }],
    source: 'local_optimistic_action'
  });
};
const validateNextBidAmount = (segment, listing, nextBid) => {
  const {
    askingBidAmount,
    currentUserHighestBidAmount
  } = calculateAuctionValues$1(segment, listing);
  const nextBidIsGreaterThanCurrentUserHighestBid = currentUserHighestBidAmount ? nextBid > currentUserHighestBidAmount : true;
  const safeAskingBidAmount = askingBidAmount || 0;
  const nextBidIsHigherOrEqualThanAskingBid = nextBid >= safeAskingBidAmount;
  if (!nextBidIsHigherOrEqualThanAskingBid || !nextBidIsGreaterThanCurrentUserHighestBid) {
    const currentUserBidIsGreaterThanAskingBid = currentUserHighestBidAmount && currentUserHighestBidAmount >= safeAskingBidAmount;
    const minimumNextBid = formatMoney$2(currentUserBidIsGreaterThanAskingBid ? currentUserHighestBidAmount : askingBidAmount || LIVE_BIDDING_MINIMUM);
    return {
      valid: false,
      message: currentUserBidIsGreaterThanAskingBid ? `Bid amount must be greater than ${minimumNextBid}.` : `Bid amount must be at least ${minimumNextBid}.`
    };
  }
  // No more bid sniping check: It’s been discussed that it gives a benefit to remote bidders who effectively get some sniping.
  if (!isAcceptingRemoteBids(segment, listing)) return {
    valid: false,
    message: `You can't bid right now.`
  };
  return {
    valid: true,
    message: ''
  };
};
const getUpdatedSegments = (oldSegment, newSegment) => {
  const bids = oldSegment?.bids;
  const latestBid = bids?.[0];
  const currentHighestBid = newSegment.current_highest_bid;
  let updatedBidHistory = [];
  // no bids in the segment
  if (!latestBid && !currentHighestBid) return null;else if (currentHighestBid && currentHighestBid.updated_date) {
    if (latestBid && latestBid.updated_date && bids.length) {
      // to handle for add / delete bids
      if (latestBid.bid_id !== currentHighestBid.bid_id) {
        updatedBidHistory = currentHighestBid.updated_date > latestBid.updated_date ? [currentHighestBid, ...bids] : bids.filter(b => b?.updated_date && currentHighestBid.updated_date && b?.updated_date <= currentHighestBid.updated_date);
        return {
          ...newSegment,
          bids: updatedBidHistory
        };
      } else {
        // to handle update on the current highest bid
        if (latestBid.updated_date !== currentHighestBid.updated_date) {
          updatedBidHistory = [currentHighestBid, ...bids.slice(1)];
          return {
            ...newSegment,
            bids: updatedBidHistory
          };
        }
      }
    } else {
      updatedBidHistory.push({
        ...currentHighestBid
      });
      return {
        ...newSegment,
        bids: updatedBidHistory
      };
    }
  }
  return null;
};
const getBatchedSegmentData = (source, focusedSegments, idempotentSegments) => {
  // When Auction is restarted or when we get a new segment, we need to overwrite the entire segment instead of partial updates.
  const segmentsForFullOverWrite = focusedSegments.filter(l => {
    const segment = idempotentSegments.mapped.get(l.listing_id);
    return segment.auction_segment_id !== l.auction_segment_id;
  });
  // When a new bid is accteped or the existing high bid is updated, we need to update the bids array in the segment.
  const segmentsForPathOverwrite = focusedSegments.reduce((acc, current) => {
    const segment = idempotentSegments.mapped.get(current.listing_id);
    const updatedSegment = getUpdatedSegments(segment, current);
    updatedSegment && acc.push(updatedSegment);
    return acc;
  }, []);
  const batchedData = [];
  segmentsForPathOverwrite?.length && batchedData.push({
    source,
    data: segmentsForPathOverwrite,
    path: 'bids',
    updateBehavior: 'overwrite'
  });
  segmentsForFullOverWrite?.length && batchedData.push({
    source,
    data: segmentsForFullOverWrite,
    updateBehavior: 'overwrite'
  });
  return batchedData;
};
const isEligibleForSaleFailed = (segment, listing) => {
  const doesListingHasAB2424Constraint = Boolean(listing.legal_constraints?.find(constraint => constraint.is_applicable && constraint.law_codes?.includes(enums$5.LawCode.Ab2424)));
  if (doesListingHasAB2424Constraint) return Boolean(segment.bids?.filter(b => b?.bid_type !== enums$5.LiveAuctionSegmentBidType.OpeningBid).length === 0);
  return false;
};
const liveAuctionUtils = {
  calculateAuctionValues: calculateAuctionValues$1,
  calculateProposedBidBreakdown,
  getBiddingStatusText: getBiddingStatusText$1,
  getStateDepositValues,
  getStateDepositText,
  isAcceptingRemoteBids,
  submitOrUpdateRemoteBid,
  validateNextBidAmount,
  isEligibleForSaleFailed
};

var _staticBlock$8;
class WinningBidDepositBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    segment
  }) {
    if (isForeclosure(listing)) {
      if (listing.trustee?.online_state_deposit_amount) {
        return true;
      }
      if (segment && segment.__typename === 'LiveAuctionSegment') {
        const {
          shouldShowStateDeposit
        } = getStateDepositValues(segment, listing);
        return shouldShowStateDeposit;
      }
    }
    return false;
  }
  get content() {
    if (this.listing.trustee?.online_state_deposit_amount) {
      return String(formatMoney(String(this.listing.trustee?.online_state_deposit_amount)));
    }
    if (this.segment && this.segment.__typename === 'LiveAuctionSegment') {
      const {
        stateDepositLabel
      } = getStateDepositValues(this.segment, this.listing);
      return stateDepositLabel || '';
    }
    return '';
  }
  static #_ = _staticBlock$8 = () => (this.title = 'Winning Bid Deposit', this.id = 'WinningBidDepositBadge', this.titleIcon = 'circle-dollar-to-slot', this.sellerTermsRenderPriority = BadgePriority.EIGHTH, this.tooltipContent = 'A Winning Bid Deposit is required for each property you successfully bid on and must be paid upon being declared the winning bidder. Further action will be required to submit the remaining balance.');
}
_staticBlock$8();

// - Badge generation is split into two stages. The first is data formatting, whereby all data shall be referenced from
//   the central v2 listing schema.
// - Badges have a natural priority, which defines their sort order. In the past badges were appended to lists and that
//   determined the natural priority. The list below was derived from the ordering here:
//   https://git.infra.aucn.io/projects/RA/repos/resi-pal/browse/src/server/endpoints/badges.js#414
// - That sort order can be overridden by passing an `only` prop to the list component
// - Badges have to be registered with a central component for processing
// - Badges should support a hidden content underneath the main badge element
const AVAILABLE_BADGES = [AgentInformationBadge, BrokerCoopAvailableBadge, BrokerCoopUnavailableBadge, BuyerPremiumAvailableBadge, BuyerPremiumUnavailableBadge, CashOnlyBadge, ClearedForSaleComingSoonBadge, ClearedForSalePendingBadge, ClearedForSalePostponedBadge, ClearedForSaleYesBadge, ConductedByADCLiveBadge, ConductedByADCOnlineBadge, FinancingAvailableBadge, FirstLookBadge, ForeclosureContactInformationBadge, InteriorAccessAvailableBadge, InteriorAccessAvailableNoAgentBadge, InteriorAccessUnavailableBadge, NewlyListedBadge, NotConductedByADCFCLBadge, NotConductedByADCLive, OpenHouseScheduledBadge, PropertyOccupiedBadge, PropertyUnknownOccupancyBadge, PropertyVacantBadge, RemoteBidBadge, RentalBadge, ShortSaleBadge, ShortSaleOpenHousesAvailableBadge, SubjectToRedemptionBadge, TitleAndLiensBadge];
const getCalculatedBadgeList = ({
  listing,
  priorityField,
  opts,
  badgeGroup,
  segment,
  viewingFrom
}) => {
  return _.chain(badgeGroup).map(Badge => new Badge(listing, segment)).filter(badge => badge.getShouldBeIncluded({
    listing,
    segment,
    opts,
    viewingFrom
  })).map(badge => {
    const overriddenProps = badge.getOverrideProps(listing);
    const fullBadge = {
      condensedTitle: badge.get('condensedTitle'),
      condensedTitleIcon: badge.get('condensedTitleIcon'),
      content: badge.content || badge.get('content'),
      contentPositive: badge.get('contentPositive'),
      color: badge.get('color'),
      renderPriority: badge.get(priorityField),
      shouldBeHoisted: badge.get('shouldBeHoisted'),
      summary: badge.get('summary'),
      textIcon: badge.get('textIcon'),
      title: badge.title || badge.get('title'),
      titleIcon: badge.get('titleIcon'),
      tooltipContent: badge.get('tooltipContent'),
      moneyValue: badge.get('moneyValue'),
      id: badge.get('id'),
      position: badge.get('position')
    };
    return {
      ...fullBadge,
      ...overriddenProps
    };
  }).sortBy('title').sortBy('renderPriority').value();
};
const calculateBadges = (listing, opts, badgeGroup) => {
  const listingBadges = {
    hoistedBadges: [],
    nonHoistedBadges: []
  };
  const calculatedBadges = getCalculatedBadgeList({
    listing,
    priorityField: 'renderPriority',
    opts,
    badgeGroup: badgeGroup || AVAILABLE_BADGES
  });
  calculatedBadges.forEach(badge => {
    badge.shouldBeHoisted ? listingBadges.hoistedBadges.push(badge) : listingBadges.nonHoistedBadges.push(badge);
  });
  return listingBadges;
};
const PRIVATE_SELLER_BADGES = [FinanceAllowedBadge, AcceptedContingenciesBadge, PrivateToursBadge];
const SELLER_DASHBOARD_BADGES = [...PRIVATE_SELLER_BADGES, RentBackBadge, EarnestMoneyDepositBadge];
const calculatePrivateSellerBadges = (listing, viewingFrom = graph.enums.CurrentTargetAudience.Seller) => {
  const badges = viewingFrom === graph.enums.CurrentTargetAudience.Seller ? SELLER_DASHBOARD_BADGES : _.compact([...PRIVATE_SELLER_BADGES, listing?.seller_terms?.leaseback_terms?.leaseback_period_in_days ? RentBackBadge : undefined]).map((badge, index) => {
    _.set(badge, 'renderPriority', index);
    return badge;
  });
  const calculatedBadges = calculateBadges(listing, undefined, badges);
  return calculatedBadges.hoistedBadges.flat().concat(calculatedBadges.nonHoistedBadges.flat());
};
const IMPORTANT_INFO_BADGE_GROUP = [PropertyOccupiedBadge, PropertyVacantBadge, RentbackOffer, RentbackAuction, TitleAndLiensBadge, SubjectToRedemptionBadge, ForeclosureContactInformationBadge, FinanceContingency, InspectionContigency, OptionFee, RentalBadge];
const calculatePDPRedesignBadges = listing => getCalculatedBadgeList({
  listing,
  priorityField: 'importantInfoRenderPriority',
  opts: {
    badgeMode: BadgeMode.EXPANDED
  },
  badgeGroup: IMPORTANT_INFO_BADGE_GROUP
});
const SELLER_TERMS_BADGE_GROUP = [AcceptedPaymentTypesBadge, AcceptedContingenciesBadge, BuyerPremiumAvailableBadge, OptionPeriod, EarnestMoneyDepositBadgeTerm, RentbackRequestedPeriod, RentbackRequiredPeriod, AdditionalTermsBadge, ClearedForSalePostponedBadge, ClearedForSalePendingBadge, ClearedForSaleComingSoonBadge, ClearedForSaleYesBadge, NotConductedByADCFCLBadge, ConductedByADCOnlineBadge, ConductedByADCLiveBadge, NotConductedByADCLive, CashOnlyBadge, FinancingAvailableBadge, BrokerCoopAvailableBadge, BrokerCoopUnavailableBadge, WinningBidDepositBadge];
const calculatePDPSellerTermsBadges = (listing, segment) => {
  const badges = getCalculatedBadgeList({
    listing,
    priorityField: 'sellerTermsRenderPriority',
    opts: {
      badgeMode: BadgeMode.EXPANDED
    },
    badgeGroup: SELLER_TERMS_BADGE_GROUP,
    segment
  });
  return badges;
};
// @deprecate: Use getBadges(BadgesGroup.badges) to retrieve these badges
// TODO: Rename this collection of badges to something that can provide more context
// i.e. AVAILABLE_BADGES, IMPORTANT_INFO_BADGE_GROUP or SELLER_TERMS_BADGE_GROUP
const badges = {
  FinanceContingency,
  InspectionContigency,
  OptionFee,
  RentbackOffer,
  RentbackAuction,
  AdditionalTermsBadge,
  WinningBidDepositBadge
};
const getBadges = badgeGroup => {
  switch (badgeGroup) {
    case BadgesGroup.Available:
      return AVAILABLE_BADGES;
    case BadgesGroup.ImportantInfo:
      return IMPORTANT_INFO_BADGE_GROUP;
    case BadgesGroup.SellerTerms:
      return SELLER_TERMS_BADGE_GROUP;
    case BadgesGroup.PrivateSeller:
      return PRIVATE_SELLER_BADGES;
    case BadgesGroup.SellerDashboard:
      return SELLER_DASHBOARD_BADGES;
    case BadgesGroup.Other:
      return badges;
  }
};

const ACTION_TERMS = {
  OH: {
    acceptedPaymentTypes: 'Cash via wire only.',
    buyerInformation: 'Complete a buyer information sheet provided to you within 2 hours of the auction.',
    paymentInstructions: 'Submit your deposit in the amount designated by the terms of the sale within 2 hours of auction end. The remaining balance will be due within 30 days.'
  },
  IL: {
    acceptedPaymentTypes: 'Cash via wire or certified check.',
    buyerInformation: 'We will contact you via phone within 2 hours of the close of the auction to collect the information for your receipt of sale.',
    paymentInstructions: 'You will be required to remit the full funds for your purchase by the next business day.'
  },
  IL_ONLINE: {
    buyerInformation: 'We will contact you via phone within 2 hours of the close of the auction to collect the information for your receipt of sale.',
    paymentInstructionsTitle: 'Full Payment Due Within 24 Hours',
    paymentInstructions: ['If you are the winning bidder, the full purchase price is due within 24 hours of the auction end. Payment must be made via wire transfer or certified funds. If funds are not received timely, you may not be able to complete the purchase of the property and may be prevented from participating in future auctions.', 'By clicking continue, you agree to and understand the above requirements.'],
    registrationNotice: 'You are registering to participate in a judicial foreclosure sale conducted pursuant to a court order.'
  }
};

const EMD_TIMELINE_FALLBACK = 'Due within 2 business days of winning unless otherwise stated.';
/**
 * Returns the EMD timeline label from the listing's closing configuration,
 * falling back to a default message if the flag is off or field is empty.
 * @param listing - listing object with closing_configuration
 * @param featureFlags - feature flags from application context
 * @returns EMD timeline label string
 */
const getEmdTimelineLabel = (listing, featureFlags) => {
  if (!featureFlags?.['fe.resi.pdp.enable_pdp_emd_instructions']) {
    return EMD_TIMELINE_FALLBACK;
  }
  return listing.closing_configuration?.earnest_money_deposit_timeline_label || EMD_TIMELINE_FALLBACK;
};
/**
 * Returns the online registration action terms based on the listing's state.
 * @param listing - listing containing seller property, product type and auction information.
 * @param options - optional modifiers; isFclOnlineDeposit selects the online-deposit copy variant.
 * @returns OnlineRegistrationActionTerms object.
 */
const getOnlineRegistrationActionTerms = (listing, options) => {
  const input = {
    isTPSOhio: isTPSOhio$1(listing),
    isTPSIllinois: isTPSIllinois(listing),
    isFclOnlineDeposit: Boolean(options?.isFclOnlineDeposit)
  };
  return match(input).with({
    isTPSOhio: true,
    isTPSIllinois: false
  }, () => ACTION_TERMS.OH).with({
    isTPSOhio: false,
    isTPSIllinois: true,
    isFclOnlineDeposit: true
  }, () => ACTION_TERMS.IL_ONLINE).with({
    isTPSOhio: false,
    isTPSIllinois: true
  }, () => ACTION_TERMS.IL).otherwise(() => ({}));
};

var registrationUtils = /*#__PURE__*/Object.freeze({
    __proto__: null,
    EMD_TIMELINE_FALLBACK: EMD_TIMELINE_FALLBACK,
    getEmdTimelineLabel: getEmdTimelineLabel,
    getOnlineRegistrationActionTerms: getOnlineRegistrationActionTerms
});

var _staticBlock$7;
class HotBadge extends ListingBadge {
  static #_ = _staticBlock$7 = () => (this.title = 'Hot', this.titleIcon = 'fire-flame-curved', this.color = 'orangeToRed', this.renderPriority = BadgePriority.THIRD, this.shouldBeIncluded = ({
    listing
  }) => Boolean(listing.is_hot));
}
_staticBlock$7();

var _staticBlock$6;
class LikelyToGoToSaleBadge extends ListingBadge {
  static #_ = _staticBlock$6 = () => (this.title = 'FCL Predict', this.titleIcon = 'rocket', this.color = 'purpleToBlue', this.renderPriority = BadgePriority.THIRD, this.shouldBeIncluded = ({
    listing
  }) => Boolean(hasMarketingTag(listing, MarketingTags.GOTO) && isForeclosure(listing)));
}
_staticBlock$6();

var _staticBlock$5;
class NewlyVacantBadge extends ListingBadge {
  static #_ = _staticBlock$5 = () => (this.title = 'Vacant', this.titleIcon = 'house-blank', this.color = 'info', this.overrideProps = listing => {
    return {
      renderPriority: isForeclosure(listing) ? BadgePriority.FOURTH : BadgePriority.FIRST
    };
  }, this.shouldBeIncluded = ({
    listing
  }) => Boolean(hasMarketingTag(listing, MarketingTags.NEWV)));
}
_staticBlock$5();

var _staticBlock$4, _staticBlock2, _staticBlock3;
const findLifecycleEvent = (listing, eventType) => listing?.lifecycle_events?.find(event => event?.event_type === eventType);
const hasLifecycleEvent = (listing, eventType) => Boolean(findLifecycleEvent(listing, eventType));
const labelOverride = eventType => listing => {
  const label = findLifecycleEvent(listing, eventType)?.label;
  return label ? {
    title: label
  } : {};
};
class NewListingEventBadge extends ListingBadge {
  static #_ = _staticBlock$4 = () => (this.id = 'NewListingEventBadge', this.title = 'New Listing', this.color = 'info', this.renderPriority = BadgePriority.FIRST, this.overrideProps = labelOverride(enums$5.LifeCycleEventTypeName.ListingNewlyListed), this.shouldBeIncluded = ({
    listing
  }) => hasLifecycleEvent(listing, enums$5.LifeCycleEventTypeName.ListingNewlyListed));
}
_staticBlock$4();
class PriceDropEventBadge extends ListingBadge {
  static #_ = _staticBlock2 = () => (this.id = 'PriceDropEventBadge', this.title = 'Price Drop', this.color = 'success', this.renderPriority = BadgePriority.FIRST, this.overrideProps = labelOverride(enums$5.LifeCycleEventTypeName.ListingPriceDropped), this.shouldBeIncluded = ({
    listing
  }) => hasLifecycleEvent(listing, enums$5.LifeCycleEventTypeName.ListingPriceDropped));
}
_staticBlock2();
class CreditBidRevealedEventBadge extends ListingBadge {
  static #_ = _staticBlock3 = () => (this.id = 'CreditBidRevealedEventBadge', this.title = 'Credit Bid Disclosed', this.color = 'success', this.renderPriority = BadgePriority.FIRST, this.overrideProps = labelOverride(enums$5.LifeCycleEventTypeName.ListingCreditBidRevealed), this.shouldBeIncluded = ({
    listing
  }) => hasLifecycleEvent(listing, enums$5.LifeCycleEventTypeName.ListingCreditBidRevealed));
}
/**
 * Lifecycle badge ids — lets core split lifecycle pills (image overlay on
 * mobile) from standard badges (MERCH-279).
 */
_staticBlock3();
const LIFECYCLE_EVENT_BADGE_IDS = [NewListingEventBadge.id, PriceDropEventBadge.id, CreditBidRevealedEventBadge.id];
const isLifecycleEventBadge = id => Boolean(id && LIFECYCLE_EVENT_BADGE_IDS.includes(id));
/**
 * Previous price for the strikethrough treatment on PropertyCard, taken from
 * the newest price-drop event. Null when there is no price-drop event or the
 * event carries no previous amount.
 */
const getLifecyclePreviousPrice = listing => {
  if (!listing) return null;
  const event = findLifecycleEvent(listing, enums$5.LifeCycleEventTypeName.ListingPriceDropped);
  return event?.previous_seller_valuation_amount ?? null;
};
/**
 * Amount the price dropped by, taken from the newest price-drop event
 * (previous - current). Used for the "Reserve Reduced by $X" treatment on
 * PropertyCard when the reserve is NOT disclosed — the absolute previous
 * price must not be shown in that case, only the delta. Null when there is
 * no price-drop event, either amount is missing, or the delta is not
 * positive.
 */
const getLifecyclePriceDropAmount = listing => {
  if (!listing) return null;
  const event = findLifecycleEvent(listing, enums$5.LifeCycleEventTypeName.ListingPriceDropped);
  const previous = event?.previous_seller_valuation_amount;
  const current = event?.current_seller_valuation_amount;
  if (previous == null || current == null) return null;
  const delta = previous - current;
  return delta > 0 ? delta : null;
};

var _staticBlock$3;
class PriceDroppedBadge extends ListingBadge {
  static #_ = _staticBlock$3 = () => (this.title = 'Price Reduced', this.titleIcon = 'chart-line-down', this.color = 'caution', this.renderPriority = BadgePriority.SECOND, this.shouldBeIncluded = ({
    listing
  }) => Boolean(hasMarketingTag(listing, MarketingTags.PRCD) && !isForeclosure(listing) &&
  // The lifecycle Price Drop event badge (feed surfaces) supersedes the
  // marketing-tag badge — never show both for the same fact.
  !hasLifecycleEvent(listing, enums$5.LifeCycleEventTypeName.ListingPriceDropped)));
}
_staticBlock$3();

var _staticBlock$2;
class MakeAnOfferBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    viewingFrom
  }) {
    return listing.strategy?.selling_method_attributes?.online_segment_type === enums$5.OnlineAuctionSegmentType.SealedBid && viewingFrom === enums$5.CurrentTargetAudience.Buyer;
  }
  static #_ = _staticBlock$2 = () => (this.title = 'Make an Offer', this.titleIcon = 'hand-holding-dollar', this.renderPriority = BadgePriority.SECOND, this.color = 'purple');
}
_staticBlock$2();

var _staticBlock$1;
function calculateOverrideProps(listing, currentDate = new Date()) {
  const startDate = listing.selling_method?.__typename === 'OnlineAuctionSegment' && parseDate(listing.selling_method?.start_date);
  const endDate = listing.selling_method?.__typename === 'OnlineAuctionSegment' && parseDate(listing.selling_method?.initial_end_date);
  const auctionDate = parseDate(listing.auction?.visible_auction_start_date_time);
  return match({
    isOffer: isOnlineOffer(listing),
    isOnline: isOnline(listing),
    isRemoteBid: isRemoteBidEnabled$2(listing),
    isLive: isLiveSegment(listing)
  }).with({
    isOffer: true
  }, () =>
  // @ts-ignore we can ignore this one as we are already inside isOffer
  listing.selling_method?.current_highest_bid?.terms?.status === graph.enums.SealedBidStatus.SellerAccepted ? {
    color: 'grayExtraLight',
    foregroundColor: 'gray',
    titleIcon: 'messages-dollar',
    title: 'Offer Accepted'
  } : {
    shouldBeIncluded: false
  }).with({
    isOnline: true
  }, () => {
    const secondsToEnd = endDate ? differenceInSeconds(endDate, currentDate) : -1;
    const daysToStart = startDate ? differenceInCalendarDays(startDate, currentDate) : -1;
    const daysToEnd = endDate ? differenceInCalendarDays(endDate, currentDate) : -1;
    const isActive =
    // @ts-ignore we already know it's online
    listing.selling_method.segment_status !== graph.enums.OnlineAuctionSegmentStatus.Ended;
    const dateState = {
      startsFuture: startDate && isFuture(startDate) && Boolean(daysToStart),
      endsFuture: endDate && isFuture(endDate) && Boolean(daysToEnd),
      endsWithinHour: secondsToEnd > 0 && secondsToEnd <= 3600,
      endsToday: endDate && isToday(endDate) && secondsToEnd > 0,
      endedToday: endDate && isToday(endDate) && !isActive,
      endedYesterday: endDate && isYesterday(endDate),
      daysToStart,
      daysToEnd,
      isActive
    };
    return match(dateState).with({
      endsWithinHour: true
    }, () => ({
      color: 'errorExtraLight',
      foregroundColor: 'error',
      titleIcon: 'desktop',
      title: 'Ends within the hour'
    })).with({
      endsToday: true
    }, () => ({
      color: 'errorExtraLight',
      foregroundColor: 'error',
      titleIcon: 'desktop',
      title: 'Ends today'
    })).with({
      startsFuture: true
    }, ({
      daysToStart
    }) => ({
      color: 'successExtraLight',
      foregroundColor: 'success',
      titleIcon: 'desktop',
      title: `Starts in ${formatDays(daysToStart)}`
    })).with({
      endsFuture: true,
      isActive: true
    }, ({
      daysToEnd
    }) => ({
      color: 'smartBlueExtraLight',
      foregroundColor: 'smartBlue',
      titleIcon: 'desktop',
      title: `Ends in ${formatDays(daysToEnd)}`
    })).with({
      endedToday: true
    }, () => ({
      color: 'grayExtraLight',
      foregroundColor: 'gray',
      titleIcon: 'desktop',
      title: 'Ended today'
    })).with({
      endedYesterday: true
    }, () => ({
      color: 'grayExtraLight',
      foregroundColor: 'gray',
      titleIcon: 'desktop',
      title: 'Ended yesterday'
    })).with({
      endsFuture: false,
      isActive: false
    }, ({
      daysToEnd
    }) => ({
      color: 'grayExtraLight',
      foregroundColor: 'gray',
      titleIcon: 'desktop',
      title: `Ended ${formatDays(Math.abs(daysToEnd))} ago`
    })).otherwise(() => ({
      shouldBeIncluded: false
    }));
  }).with(P.union({
    isRemoteBid: true
  }, {
    isLive: true
  }), ({
    isRemoteBid
  }) => {
    const icon = isRemoteBid ? 'mobile-signal-out' : 'gavel';
    const daysToAuction = auctionDate ? differenceInCalendarDays(auctionDate, currentDate) : -1;
    const isActive = listing.listing_status_group === graph.enums.ListingStatusGroup.Active;
    const dateState = {
      startsFuture: auctionDate && isFuture(auctionDate) && Boolean(daysToAuction) && isActive,
      isToday: auctionDate && isToday(auctionDate) && isActive,
      isYesterday: auctionDate && isYesterday(auctionDate),
      daysToAuction
    };
    return match(dateState).with({
      isToday: true
    }, () => ({
      color: 'errorExtraLight',
      foregroundColor: 'error',
      titleIcon: icon,
      title: 'Auction Today'
    })).with({
      startsFuture: true
    }, ({
      daysToAuction
    }) => ({
      color: 'successExtraLight',
      foregroundColor: 'success',
      titleIcon: icon,
      title: `Starts in ${formatDays(daysToAuction)}`
    })).with({
      isYesterday: true
    }, () => ({
      color: 'grayExtraLight',
      foregroundColor: 'gray',
      titleIcon: icon,
      title: 'Ended yesterday'
    })).otherwise(() => ({
      shouldBeIncluded: false
    }));
  }).otherwise(() => ({
    shouldBeIncluded: false
  }));
}
class RemainingTimeBadge extends ListingBadge {
  static shouldBeIncluded({
    listing,
    viewingFrom
  }) {
    return viewingFrom === graph.enums.CurrentTargetAudience.Buyer && calculateOverrideProps(listing).shouldBeIncluded !== false;
  }
  static #_ = _staticBlock$1 = () => (this.renderPriority = BadgePriority.FIRST, this.position = BadgePosition.BOTTOM, this.overrideProps = listing => {
    return calculateOverrideProps(listing);
  });
}
_staticBlock$1();

const availableBadges = [NewListingEventBadge, PriceDropEventBadge, CreditBidRevealedEventBadge, RemainingTimeBadge, NextOpenHouseBadge, MakeAnOfferBadge, NewlyVacantBadge, PriceDroppedBadge, FirstLookBadge, InteriorAccessAvailableBadge, InteriorAccessAvailableNoAgentBadge, HotBadge, LikelyToGoToSaleBadge];
const calculatePropertyCardBadges = (listing, viewingFrom) => {
  const badges = getCalculatedBadgeList({
    listing,
    priorityField: 'renderPriority',
    badgeGroup: availableBadges,
    viewingFrom
  });
  return badges;
};

const {
  enums: enums$4
} = graph;
const GOING_ONCE_UPPER_BOUND = 12;
const GOING_TWICE_UPPER_BOUND = 6;
const ONLINE_BIDDING_MINIMUM = 1;
const CLOSING_WAITING_PERIOD_DAYS = 1;
// taken from packages/adc/src/util/date.js
const nextBusinessDay = date => {
  if (date.isoWeekday() > 5) {
    date.add(8 % date.isoWeekday(), 'days');
  }
  return date;
};
const calculateAuctionValues = segment => {
  return {
    // @ts-ignore - Need to update business logic to calculate next bid amount
    nextBidAmount: getNextBidAmount(segment),
    // @ts-ignore - See TODO within getNextMaxBidAmount
    nextProxyBidAmount: getNextMaxBidAmount(segment),
    hasBids: hasBids(segment)
  };
};
/**
 * @deprecated use calculateAuctionValues() instead
 */
const calculateOnlineAuctionValues = segment => {
  return {
    // @ts-ignore - Deprecated, use calculateAuctionValues() instead
    nextBidAmount: getNextBidAmount(segment),
    // @ts-ignore - Deprecated, use calculateAuctionValues() instead
    nextProxyBidAmount: getNextMaxBidAmount(segment),
    hasBids: hasBids(segment)
  };
};
const didHighestBidMeetsReserve = segment => {
  return !!(segment?.current_reserve_amount && segment.result?.winning_bid_amount && segment.result.winning_bid_amount >= segment.current_reserve_amount);
};
const formatCountdownTime = (_segment, end) => {
  const countDownTimeObject = {
    primary: '',
    secondary: ''
  };
  const endDateTime = moment$1(isNumberType(end) ? new Date().getTime() + end * 1000 : end);
  const duration = moment$1.duration(endDateTime.diff(Date()));
  if (duration.asDays() >= 1) {
    countDownTimeObject.primary = `${duration.days()}d ${duration.hours()}h`;
  } else if (duration.asHours() >= 1) {
    countDownTimeObject.primary = `${duration.hours()}h ${duration.minutes()}m`;
  } else if (duration.asMinutes() >= 1) {
    countDownTimeObject.primary = `${duration.minutes()}m ${duration.seconds()}s`;
  } else {
    const seconds = duration.seconds();
    countDownTimeObject.primary = seconds > 0 ? `${seconds}s` : '';
    if (GOING_ONCE_UPPER_BOUND >= seconds) {
      if (seconds > GOING_TWICE_UPPER_BOUND) {
        countDownTimeObject.secondary = 'GOING ONCE!';
      } else if (seconds > 0) {
        countDownTimeObject.secondary = 'GOING TWICE!';
      } else {
        countDownTimeObject.secondary = `FINALIZING...`;
      }
    }
  }
  return countDownTimeObject;
};
/**
 * @deprecated removing this as this is more of a label function which should be covered by listing.listing_status_group instead of a function like this to duplicate the logic.
 *
 * NOTE: Let it be known to the world that I detest this function. Because people downstream coded to this as if it were
 * a status as part of the broader FSM and not as some plaintext label to bring sanity to inconsistent product
 * messaging, which is what it is good at.
 */
const getAuctionStatus = (segment, listing) => {
  const closedListingStatuses = [enums$4.ListingStatus.Closed, enums$4.ListingStatus.SaleGatekeeping, enums$4.ListingStatus.SalePaymentPending];
  switch (segment?.segment_status) {
    case enums$4.OnlineAuctionSegmentStatus.Pending:
      return 'Not Started';
    case enums$4.OnlineAuctionSegmentStatus.Started:
    case enums$4.OnlineAuctionSegmentStatus.SubjectToReserveMet:
    case enums$4.OnlineAuctionSegmentStatus.ReserveMet:
    case enums$4.OnlineAuctionSegmentStatus.SellerReviewPeriod:
      return 'Live';
    case enums$4.OnlineAuctionSegmentStatus.Ended:
      if (closedListingStatuses.includes(listing?.listing_status)) {
        return segment.subject_to_status === enums$4.SubjectToStatus.Pending ? 'Sold subject to' : 'Sold';
      }
      break;
  }
  return 'Gone';
};
const isAuctionInProgress = segment => {
  const inProgressEnglishArray = [enums$4.OnlineAuctionSegmentStatus.Started, enums$4.OnlineAuctionSegmentStatus.SubjectToReserveMet, enums$4.OnlineAuctionSegmentStatus.ReserveMet];
  return segment.segment_type === enums$4.OnlineAuctionSegmentType.SealedBid ? [...inProgressEnglishArray, enums$4.OnlineAuctionSegmentStatus.HighestAndBestDecision, enums$4.OnlineAuctionSegmentStatus.HighestAndBestOpen, enums$4.OnlineAuctionSegmentStatus.SellerReviewPeriod].includes(segment?.segment_status) : inProgressEnglishArray.includes(segment?.segment_status);
};
const isOfferPathViewableForCurrentUser = segment => {
  if (segment.__typename === 'OnlineAuctionSegment' && segment.segment_type === enums$4.OnlineAuctionSegmentType.SealedBid) {
    const doesCurrentUserHasAnOffer = Boolean(segment.current_user_bidding_status && ![enums$4.OnlineAuctionSegmentBiddingStatus.NoBid, enums$4.OnlineAuctionSegmentBiddingStatus.WithdrawnBid].includes(segment.current_user_bidding_status));
    const doesCurrentUserHasRejectedOfferAndSegmentIsEnded = enums$4.OnlineAuctionSegmentBiddingStatus.RejectedBid === segment.current_user_bidding_status && enums$4.OnlineAuctionSegmentStatus.Ended === segment.segment_status;
    if (!isAuctionInProgress(segment)) {
      if (doesCurrentUserHasRejectedOfferAndSegmentIsEnded) {
        return false;
      }
      return doesCurrentUserHasAnOffer;
    }
    return match(segment).with({
      segment_status: enums$4.OnlineAuctionSegmentStatus.HighestAndBestOpen,
      current_user_bidding_status: enums$4.OnlineAuctionSegmentBiddingStatus.NoBid
    }, () => false).with({
      segment_status: enums$4.OnlineAuctionSegmentStatus.HighestAndBestDecision
    }, () => doesCurrentUserHasAnOffer).otherwise(() => true);
  }
  return false;
};
const getBiddingStatusText = segment => {
  const messageId = 'bidding:online:bid_status';
  switch (segment.current_user_bidding_status) {
    case enums$4.OnlineAuctionSegmentBiddingStatus.HighestBid:
      // eslint-disable-next-line no-case-declarations
      const shouldBidAgain = segment.configuration?.should_bid_again;
      return {
        id: messageId,
        title: shouldBidAgain ? 'Bid again. Below reserve.' : 'You are Ranked #1',
        intent: shouldBidAgain ? 'NEUTRAL' : 'POSITIVE'
      };
    case enums$4.OnlineAuctionSegmentBiddingStatus.Lost:
      return {
        id: messageId,
        title: 'You Did Not Win',
        intent: 'NEGATIVE'
      };
    case enums$4.OnlineAuctionSegmentBiddingStatus.Outbid:
      return {
        id: messageId,
        title: 'Your Rank has Changed',
        intent: 'NEGATIVE'
      };
    case enums$4.OnlineAuctionSegmentBiddingStatus.Won:
      return {
        id: messageId,
        title: "You've Won!",
        intent: 'POSITIVE'
      };
    default:
      return null;
  }
};
const getNextBidAmount = segment => segment.next_bid_amount;
//TODO: Remove after releasing new max bidding functionality
/**
 * @deprecated use getNextMaxBidAmount instead
 */
const getNextProxyBidAmount$1 = (segment, listing) => {
  if (isProxyBiddingActive$1(segment, listing)) {
    const startingBidAmount = segment.starting_bid_amount;
    const maxBid = segment.current_user_max_bid;
    const currentUserMaxBidAmount = maxBid?.record_status !== enums$4.RecordStatus.Inactive ? maxBid?.max_bid_amount : null;
    return currentUserMaxBidAmount ? Math.max(startingBidAmount ?? ONLINE_BIDDING_MINIMUM, currentUserMaxBidAmount + ONLINE_BIDDING_MINIMUM) : startingBidAmount;
  }
  return null; // TODO: remove this case and have the assumption for this function be that proxy bidding is active so that this function's return type can always be number
};
const getNextMaxBidAmount = segment => {
  if (!isMaxBiddingActive(segment)) return null;
  const maxBid = segment.current_user_max_bid;
  return !!maxBid ? maxBid.current_user_next_max_bid_amount : segment.next_bid_amount;
};
const isAuctionNotStarted = segment => segment?.segment_status === enums$4.OnlineAuctionSegmentStatus.Pending;
const hasBids = segment => (segment.bid_count || 0) > 0;
const highestBidMeetsReserve = segment => {
  return didHighestBidMeetsReserve(segment) || segment.reserve_status === enums$4.ReserveStatus.ReserveMet;
};
const isOnlineAuctionActive = segment => [enums$4.OnlineAuctionSegmentStatus.Started, enums$4.OnlineAuctionSegmentStatus.SubjectToReserveMet, enums$4.OnlineAuctionSegmentStatus.ReserveMet, enums$4.OnlineAuctionSegmentStatus.HighestAndBestOpen, enums$4.OnlineAuctionSegmentStatus.HighestAndBestDecision, enums$4.OnlineAuctionSegmentStatus.SellerReviewPeriod].includes(segment.segment_status);
//TODO: Remove after releasing new max bidding functionality
/**
 * @deprecated use segment.configuration.is_max_bidding_enabled instead
 */
const isProxyBiddingActive$1 = (segment, listing) => isAuctionNotStarted(segment) && segment.configuration.is_max_bidding_enabled && Boolean(listing.is_current_user_registered_for_online_auction);
const isMaxBiddingActive = segment => segment.configuration.is_max_bidding_enabled && Boolean(segment.is_current_user_registered_for_online_auction);
const hasValidMaxBid = segment => {
  return Boolean(segment.current_user_max_bid && segment.current_user_max_bid?.max_bid_amount > 0 && segment.current_user_max_bid.record_status === enums$4.RecordStatus.Active);
};
const getSellerDisclosureValues = (segment, listing, currentUserPartyId) => {
  const marketingAttribute = (listing.marketing_attributes || []).find(elem => elem?.attribute_type === enums$4.MarketingAttributeType.SellerDisclosure);
  const showSellerDisclosure = segment?.current_highest_bid?.type === enums$4.OnlineAuctionSegmentBidType.Seller && marketingAttribute?.show === true;
  const bidAgainCount = segment.configuration?.bid_again_count || 0;
  const isBidAgain = currentUserPartyId === segment.current_highest_bid?.bidder_party_id && bidAgainCount > 0;
  const sellerDisclosureLabel = sellerDisclosureLabelMatch({
    isBidAgain,
    show: showSellerDisclosure,
    label: marketingAttribute?.label
  });
  const sellerDisclosureTooltipText = sellerDisclosureTooltipMatch({
    isBidAgain,
    show: showSellerDisclosure,
    tooltip: marketingAttribute?.message
  });
  return {
    showSellerDisclosure,
    sellerDisclosureLabel,
    sellerDisclosureTooltipText
  };
};
const sellerDisclosureLabelMatch = status => match(status).with({
  isBidAgain: true
}, () => 'Counter Bid').when(() => status.show && status.label, () => status.label).otherwise(() => '');
const sellerDisclosureTooltipMatch = status => match(status).with({
  isBidAgain: true
}, () => 'You did not meet the reserve. Bid Again.').when(() => status.show && status.tooltip, () => status.tooltip).otherwise(() => '');
const shouldShowOneBidAway = segment => enums$4.BidsAwayStatus.OneAwayDisplayed === segment.configuration.bids_away_status || enums$4.BidsAwayStatus.OneAway === segment.configuration.bids_away_status;
const shouldShowTwoBidAway = segment => enums$4.BidsAwayStatus.TwoAwayDisplayed === segment.configuration.bids_away_status || enums$4.BidsAwayStatus.TwoAway === segment.configuration.bids_away_status;
const calculateOccupancyDisclaimerRequirements = (segment, listing) => {
  const currentPropertyAssetType = listing.listing_configuration?.asset_type;
  const currentPropertyOccupancyStatus = listing.listing_configuration?.occupancy_status;
  const required = currentPropertyAssetType === enums$4.AssetType.BankOwned && currentPropertyOccupancyStatus === enums$4.OccupancyStatus.Occupied;
  const currentUserBiddingStatus = segment?.current_user_bidding_status;
  const acknowledged = Boolean(currentUserBiddingStatus) && currentUserBiddingStatus !== enums$4.OnlineAuctionSegmentBiddingStatus.NoBid || Boolean(segment?.current_user_max_bid?.max_bid_amount);
  return {
    required,
    acknowledged
  };
};
// logic from biddingStatus model
const isContractingPastDue = segment => {
  if (!segment.initial_end_date) {
    return false;
  }
  const provideByTime = nextBusinessDay(moment$1(segment.initial_end_date).add(CLOSING_WAITING_PERIOD_DAYS, 'days'));
  return moment$1().isAfter(provideByTime);
};
const shouldShowSealedBidTreatment = (segment, featureFlags = {}) => Boolean(featureFlags['fe.resi.enable-private-seller'] && segment?.__typename === 'OnlineAuctionSegment' && segment.segment_type === enums$4.OnlineAuctionSegmentType.SealedBid);
const getOptimisticValueForNegotiationSequence = (nextBid, existingBid) => {
  if (nextBid?.negotiation_sequence) {
    return nextBid.negotiation_sequence;
  }
  const negotiationSequence = existingBid?.negotiation_sequence || {
    start_date: new Date()
  };
  const existingNegotiationCounters =
  // @ts-ignore optimistic code
  negotiationSequence?.counters || [];
  return {
    ...negotiationSequence,
    counters: [...existingNegotiationCounters, nextBid]
  };
};
// This update is intended for the case in which a bid action is performed in a specific bid but does not change the order of the ranking.
// In the case in which an action is performed that changes the order OR the length of the ranking the onlineAuctionBidRankingUpdated watcher will handle this re-ordering by being consistent with what BE published.
const updateIfExistingBidInRanking = (attemptSegmentWrite, source, bid, bidRanking) => {
  const existingBidRank = bidRanking?.ranking?.find(r => r.bid_id === bid?.bid_id);
  if (existingBidRank && (existingBidRank.rank === bid?.rank || !bid?.rank)) {
    const isBidWithdrawn = bid?.terms?.status === enums$4.SealedBidStatus.Withdrawn;
    // @ts-ignore bidRanking should be defined at this point
    const nextRanking = [...bidRanking?.ranking];
    isBidWithdrawn ? nextRanking.splice(existingBidRank.rank - 1, 1) :
    // @ts-ignore optimistic code
    nextRanking.splice(existingBidRank.rank - 1, 1,
    // @ts-ignore merging existing and new
    {
      ...existingBidRank,
      bid: {
        ...existingBidRank.bid,
        ...bid,
        // @ts-ignore optimistic code
        negotiation_sequence: getOptimisticValueForNegotiationSequence(bid, existingBidRank.bid)
      }
    });
    let updatedSegment = {
      listing_id: bidRanking?.listing_id,
      bids_ranking: {
        ...bidRanking,
        ranking: nextRanking
      }
    };
    if (bid?.terms?.status === enums$4.SealedBidStatus.SellerCountered) {
      // @ts-ignore key is correct
      updatedSegment = {
        ...updatedSegment,
        seller_counter_bid_count: 1
      };
    }
    const data = [updatedSegment];
    // @ts-ignore TODO review types here
    attemptSegmentWrite({
      data,
      source,
      force: true
    });
  }
};
const isHighestAndBestRound = segment => {
  return segment?.segment_type === enums$4.OnlineAuctionSegmentType.SealedBid && (segment?.segment_status === enums$4.OnlineAuctionSegmentStatus.HighestAndBestOpen || segment?.segment_status === enums$4.OnlineAuctionSegmentStatus.HighestAndBestDecision);
};
/**
 * Function to know if the segment is rejected.
 * @param segment
 * @returns if the segment is rejected
 */
const isCurrentUserSealedBidRejected = segment => {
  return segment?.segment_type === enums$4.OnlineAuctionSegmentType.SealedBid && segment?.current_user_bidding_status === graph.enums.OnlineAuctionSegmentBiddingStatus.RejectedBid;
};
/**
 * Function to extract the current user's bid from bids_ranking inside segment.
 * If the segment is rejected we use the current_user_highest_bid if not
 * it comes from the ranking
 *
 * @param {OnlineAuctionSegment} segment - The segment object that may include current_user_highest_bid or bids_ranking.
 * @returns {OnlineAuctionSegmentBid} the associated bid for the current user
 */
const getCurrentUserSealedBid = segment => {
  return isCurrentUserSealedBidRejected(segment) ? segment?.current_user_highest_bid : getCurrentUserSealedBidRank(segment)?.bid;
};
/**
 * Function to extract the current user's bid ranking from bids_ranking inside segment.
 *
 * @param {OnlineAuctionSegment} segment - The segment object that may include bids_ranking.
 * @returns {OnlineAuctionSegmentBidRank} the associated bid ranking for the current user
 */
const getCurrentUserSealedBidRank = segment => {
  return segment.bids_ranking?.ranking.find(bid => bid.belongs_to_current_user);
};
/**
 * Determines if the user has a valid counteroffer bid based on its expiration date.
 *
 * This function checks if the current user's bid or the provided bid contain a latest counter bid with defined negotiation
 * terms and an expiration date. It then compares the expiration date to the given endDate.
 * The counteroffer is considered valid if its expiration
 * date is in the future relative to the comparison date.
 *
 * @param {OnlineAuctionSegment} segment - The segment object that may include bid and negotiation details.
 * @param {Maybe<OnlineAuctionSegmentBid>} bid - The bid object that may include negotiation details.
 * @returns {OnlineAuctionSegmentBid} OnlineAuctionSegmentBid if the counteroffer's expiration date is in the future relative to endDate, otherwise null.
 */
const getCounterofferBid = (segment, bid) => {
  const segmentBid = bid ?? getCurrentUserSealedBidRank(segment);
  const counteroffer = segmentBid?.negotiation_sequence?.latest_counter_bid ?? null;
  if (counteroffer?.terms?.expiration_date) {
    const expirationDate = new Date(counteroffer.terms.expiration_date);
    return isAfter(expirationDate, new Date()) ? counteroffer : null;
  }
  return counteroffer;
};
const notAllowedStatusesToSubmitNewOfferAfterRejected = new Set([graph.enums.OnlineAuctionSegmentStatus.HighestAndBestDecision, graph.enums.OnlineAuctionSegmentStatus.Ended]);
const allowedStatusesToSubmitNewOffer = new Set([graph.enums.OnlineAuctionSegmentStatus.Started, graph.enums.OnlineAuctionSegmentStatus.SellerReviewPeriod, graph.enums.OnlineAuctionSegmentStatus.HighestAndBestOpen]);
/**
 * Function to know if the segment allows to submit new offers.
 * @param segment
 * @returns if the segment allows to submit new offers
 */
const allowToSubmitNewOffers = segment => {
  if (isCurrentUserSealedBidRejected(segment)) {
    return !notAllowedStatusesToSubmitNewOfferAfterRejected.has(segment.segment_status);
  }
  return allowedStatusesToSubmitNewOffer.has(segment.segment_status);
};
const previousValueOrUndefined = (currentValue, previousValue, isMoneyValue = false) => {
  if (!!previousValue && currentValue !== previousValue) {
    return previousValue;
  }
  if (!!currentValue && !previousValue && isMoneyValue) {
    return 0;
  }
  return undefined;
};
const cleanFieldName = fieldKey => fieldKey.replace('/terms/', '').replace('notes/', '');
const updatePreviousValues = (currentOfferTerms, fieldKey, fieldValue, previousValues) => {
  if (!fieldValue) return previousValues;
  const currentFieldValue = currentOfferTerms[fieldKey];
  const finalPreviousValue = previousValueOrUndefined(currentFieldValue, fieldValue, typeof fieldValue === 'number');
  if (finalPreviousValue !== undefined) {
    previousValues = {
      ...previousValues,
      [fieldKey]: fieldValue
    };
  }
  return previousValues;
};
const getWhatFieldChanged = bid => {
  const currentOfferTerms = bid?.terms;
  const auditHistory = bid?.audit_history;
  if (!auditHistory?.length || auditHistory.length <= 1) return undefined;
  let previousValues = {};
  auditHistory.sort(function (a, b) {
    return moment$1(a?.updated_date).isBefore(b?.updated_date) ? -1 : moment$1(a?.updated_date).isAfter(b?.updated_date) ? 1 : 0;
  });
  const currentOfferDelta = auditHistory[auditHistory.length - 1]?.delta;
  if (currentOfferDelta) {
    // Check if there is any modified, added, or removed
    if (currentOfferDelta?.modified && Object.keys(currentOfferDelta?.modified).length > 0 || currentOfferDelta?.added && Object.keys(currentOfferDelta?.added).length > 0 || Array.isArray(currentOfferDelta?.removed) && currentOfferDelta?.removed.length > 0) {
      const fieldKeys = Object.values(WhatChangedFields);
      fieldKeys.forEach(fieldKey => {
        const currentModifiedValue = (currentOfferDelta?.modified)[fieldKey];
        const currentAddedValue = (currentOfferDelta?.added)[fieldKey];
        const currentRemovedValue = currentOfferDelta?.removed?.includes(fieldKey) ? fieldKey : undefined;
        // Check if the field was modified or removed
        if (currentModifiedValue !== undefined || currentRemovedValue !== undefined) {
          // Get the previous value from Audit History
          for (let i = auditHistory.length - 2; i >= 0; i--) {
            const entryDelta = auditHistory[i]?.delta;
            const modiFiedValue = (entryDelta?.modified)[fieldKey];
            const added = entryDelta?.added;
            const addedTerms = added['/terms'];
            const addedFieldValue = added[fieldKey];
            const removedFieldValue = entryDelta?.removed?.includes(fieldKey);
            if (modiFiedValue !== undefined) {
              previousValues = updatePreviousValues(currentOfferTerms, fieldKey, modiFiedValue, previousValues);
              break;
            } else if (addedTerms !== undefined) {
              // Especial addedTerms structure.
              // First Offer version (this entryDelta has a different structure).
              // All fields are newly added. There is no modified or removed.
              const addedFieldValue = addedTerms[cleanFieldName(fieldKey)];
              if (addedFieldValue !== undefined) {
                previousValues = updatePreviousValues(currentOfferTerms, fieldKey, addedFieldValue, previousValues);
                break;
              }
            } else if (addedFieldValue !== undefined) {
              // Normal added structure
              if (fieldKey === WhatChangedFields.commission) {
                // Special case for commission, because is an Array
                // we need to get Object OnlineAuctionSegmentBidProposedAgentCommission
                const commission = addedFieldValue;
                if (commission?.agent_commission_amount !== undefined && commission?.paid_by !== undefined) {
                  previousValues = updatePreviousValues(currentOfferTerms, WhatChangedFields.agentCommissionAmount, commission.agent_commission_amount, previousValues);
                  previousValues = updatePreviousValues(currentOfferTerms, WhatChangedFields.paidBy, commission.paid_by, previousValues);
                }
              } else {
                previousValues = updatePreviousValues(currentOfferTerms, fieldKey, addedFieldValue, previousValues);
              }
              break;
            } else if (removedFieldValue) {
              previousValues = {
                ...previousValues,
                [fieldKey]: ''
              };
              break;
            }
          }
        } else if (currentAddedValue !== undefined) {
          // if the field was newly added there is no previus value
          if (fieldKey === WhatChangedFields.commission) {
            // Special case for commission, because is an Array
            // we need to get Object OnlineAuctionSegmentBidProposedAgentCommission
            const commission = currentAddedValue;
            if (commission?.agent_commission_amount !== undefined && commission?.paid_by !== undefined) {
              previousValues = {
                ...previousValues,
                [WhatChangedFields.agentCommissionAmount]: 0
              };
              previousValues = {
                ...previousValues,
                [WhatChangedFields.paidBy]: 'N/A'
              };
            }
          } else {
            previousValues = {
              ...previousValues,
              [fieldKey]: typeof currentAddedValue === 'number' ? 0 : ''
            };
          }
        }
      });
    }
  }
  return Object.keys(previousValues).length > 0 ? previousValues : undefined;
};
const getSummarizedBidHistory = async segment => {
  const bids = await getSummarizedBidHistory$1(segment);
  const bidsLength = bids?.content?.length ?? 0;
  const summary = (bids?.content ?? []).reduce((acc, bid, i) => {
    const number = bidsLength - i;
    const belongsToCurrentUser = bid?.belongs_to_current_user;
    const bidHistoryDescription = `${formatMoney$2(bid?.bid_amount || 0)}`;
    let bidHistoryWithAnnotations = '';
    if (!acc.counterOfferAnnotated && bid?.type === enums$4.OnlineAuctionSegmentBidType.Seller) {
      bidHistoryWithAnnotations += '{{counteroffer}}';
      acc.counterOfferAnnotated = true;
    } else if (belongsToCurrentUser) {
      bidHistoryWithAnnotations += '{{your_bid}}';
    }
    acc.result.push({
      bid,
      bidHistoryDescription,
      bidHistoryUnresolved: `${bidHistoryDescription}${bidHistoryWithAnnotations}`,
      number
    });
    return acc;
  }, {
    result: [],
    counterOfferAnnotated: false
  }).result;
  return summary;
};
/**
 * Determines whether the current user can adjust (lower) or remove their max bid.
 * When this returns false and the user has a max bid, they can only increase it.
 *
 * @param segment - The online auction segment
 * @param config - Feature flag values for max bid update validation
 * @returns true if the user can lower or remove their max bid
 */
const canAdjustOrRemoveMaxBid = (segment, config) => {
  // Pre-bidding: always allowed
  if (isAuctionNotStarted(segment)) {
    return true;
  }
  // If enhanced validation is disabled, lowering is never allowed post-bidding
  if (!config.isMaxBidUpdateValidationEnabled) {
    return false;
  }
  // Must have an active max bid to adjust/remove
  if (!hasValidMaxBid(segment)) {
    return false;
  }
  const currentHighestBid = segment.current_highest_bid;
  const maxBid = segment.current_user_max_bid;
  // User must be the highest bidder via a max bid
  const isMaxBidRelatedToHighestBid = currentHighestBid != null && currentHighestBid.belongs_to_current_user === true && currentHighestBid.type === enums$4.OnlineAuctionSegmentBidType.MaxBid;
  if (!isMaxBidRelatedToHighestBid) {
    return false;
  }
  // Highest bid must be below reserve
  if (segment.reserve_status === enums$4.ReserveStatus.ReserveMet) {
    return false;
  }
  const now = segment.current_time ? new Date(segment.current_time) : new Date();
  const updatedDate = maxBid?.updated_date ? new Date(maxBid.updated_date) : null;
  const auctionEndDate = segment.initial_end_date ? new Date(segment.initial_end_date) : null;
  if (!updatedDate || !auctionEndDate) {
    return false;
  }
  // Must be within time limit after placing the max bid
  const maxBidUpdateDeadline = addSeconds(updatedDate, config.maxBidUpdateTimeLimitSeconds);
  if (!isBefore(now, maxBidUpdateDeadline)) {
    return false;
  }
  // Must be more than minTimeLimitBeforeAuctionEnd before auction ends
  const auctionEndCutoff = subSeconds(auctionEndDate, config.minTimeLimitBeforeAuctionEndSeconds);
  if (!isBefore(now, auctionEndCutoff)) {
    return false;
  }
  return true;
};
const onlineAuctionUtils = {
  calculateAuctionValues,
  calculateOnlineAuctionValues,
  didHighestBidMeetsReserve,
  formatCountdownTime,
  getAuctionStatus,
  isAuctionInProgress,
  isOfferPathViewableForCurrentUser,
  getBiddingStatusText,
  getNextBidAmount,
  getNextProxyBidAmount: getNextProxyBidAmount$1,
  getNextMaxBidAmount,
  getSellerDisclosureValues,
  hasBids,
  highestBidMeetsReserve,
  isAuctionNotStarted,
  isOnlineAuctionActive,
  isProxyBiddingActive: isProxyBiddingActive$1,
  isMaxBiddingActive,
  hasValidMaxBid,
  shouldShowOneBidAway,
  shouldShowTwoBidAway,
  shouldShowSealedBidTreatment,
  calculateOccupancyDisclaimerRequirements,
  submitOnlineMatchBid,
  submitOnlineRegularBid,
  upsertListingOnlineMaxBid,
  deleteOnlineAuctionSegmentBid,
  isContractingPastDue,
  isHighestAndBestRound,
  upsertSealedBid,
  acceptSellerCounterBid,
  rejectSellerCounterBid,
  withdrawSealedBid,
  acceptBuyerBid,
  placeSellerCounterBid,
  updateListingReserve,
  updateListingReviewDate,
  rejectBuyerBid,
  startHighestAndBestRound,
  withdrawSellerCounterBid,
  getCurrentUserSealedBid,
  getCounterofferBid,
  getCurrentUserSealedBidRank,
  isCurrentUserSealedBidRejected,
  allowToSubmitNewOffers,
  updateOnlineSegmentFragments,
  getSummarizedBidHistory,
  canAdjustOrRemoveMaxBid
};

/**
 * Gets the starting bid amount from a listing.
 * Checks both the selling_method and auction objects.
 */
const getStartingBidAmount = listing => {
  const sellingMethod = listing?.selling_method;
  let startingBid = null;
  if (sellingMethod) {
    const normalizedSellingMethod = utils$e.normalizeAliases(sellingMethod);
    startingBid = normalizedSellingMethod.starting_bid_amount;
  }
  return startingBid || listing?.auction?.starting_bid;
};
/**
 * Auction end date. Online segments carry initial_end_date; live/trustee sales
 * use the scheduled auction_date. Pass a reactive segment to override
 * listing.selling_method (e.g. live-bidding data).
 */
const getAuctionEndDate = (listing, segment = listing?.selling_method) => segment?.__typename === 'OnlineAuctionSegment' ? segment.initial_end_date : listing?.auction?.auction_date;
/** True when the auction end is in the future and the listing is not sold. */
const isAuctionActive = (listing, segment = listing?.selling_method) => {
  if (listing?.listing_status_group === enums$5.ListingStatusGroup.Sold) {
    return false;
  }
  const auctionEndDate = getAuctionEndDate(listing, segment);
  if (!auctionEndDate) return false;
  const endDate = typeof auctionEndDate === 'string' ? parseISO(auctionEndDate) : new Date(auctionEndDate);
  // Live/trustee auction_date is date-only ("2026-08-11") — keep the box up
  // for the whole auction day. Online segments have a full datetime, so the
  // exact instant applies.
  const isDateOnly = typeof auctionEndDate === 'string' && !auctionEndDate.includes('T');
  return isDateOnly ? isToday(endDate) || isFuture(endDate) : isFuture(endDate);
};

const MISSING_VALUE_LABEL = 'TBD';
const RESERVE_LABELS = {
  EST_CREDIT_BID: 'Est. Credit Bid',
  RESERVE_AMOUNT: 'Reserve Amount'
};
const getReserveLabel = listing => isForeclosure(listing) ? RESERVE_LABELS.EST_CREDIT_BID : RESERVE_LABELS.RESERVE_AMOUNT;

const getERV = listing => listing.primary_property?.summary?.valuation;
// TODO: verify with platform on this
const valuationEstimate = listing => {
  const valuation = getERV(listing);
  return valuation ? formatMoney$2(valuation, undefined, true) : '';
};
const getOpeningBidValue = listing => {
  const showOpeningBid = _.get(listing, 'listing_summary.show_opening_bid');
  if (!showOpeningBid) return MISSING_VALUE_LABEL;
  const startingBid = getStartingBidAmount(listing);
  if (startingBid == null) return '';
  return formatMoney$2(startingBid, undefined, true);
};
const getAddressLine = (address = {}, displayCounty = false) => {
  const streetNumber = address.street_number || '';
  const streetName = address.street_name || '';
  return `${streetNumber} ${streetName} ${address.municipality || ''}, ${address.country_primary_subdivision || ''} ${address.postal_code || ''}${displayCounty && address.country_secondary_subdivision ? `, ${address.country_secondary_subdivision}` : ''}`;
};
const getDisplayedValue = (listing, options = {}) => match(listing).when(l => options.featureFlags?.['fe.resi.enable-credit-bid-reveal'] && (l.listing_status === enums$5.ListingStatus.Canceled || l.listing_status === enums$5.ListingStatus.Removed), () => ({
  label: '',
  amount: ''
})).when(shouldDisplayListPrice$1, l => {
  const value = l.valuation?.seller_current_value_amount;
  return {
    label: 'List Price',
    amount: value != null ? formatMoney$2(value, undefined, true) : ''
  };
}).when(shouldDisplayCurrentBid, l => {
  const bid = getCurrentBid(l);
  return {
    label: 'Current Bid',
    amount: bid != null ? formatMoney$2(bid, undefined, true) : ''
  };
}).when(isForeclosure, l => match({
  reservePrice: l.reserve_price,
  startingBid: getStartingBidAmount(l),
  erv: getERV(l)
}).with({
  reservePrice: P.when(Boolean)
}, ({
  reservePrice
}) => ({
  label: RESERVE_LABELS.EST_CREDIT_BID,
  amount: reservePrice != null ? formatMoney$2(reservePrice, undefined, true) : '',
  isCreditBid: true
})).with({
  startingBid: P.when(Boolean)
}, () => ({
  label: 'Opening Bid',
  amount: getOpeningBidValue(l)
})).with({
  erv: P.when(Boolean)
}, ({
  erv
}) => ({
  label: 'Est. Market Value',
  amount: erv != null ? formatMoney$2(erv, undefined, true) : ''
})).otherwise(() => ({
  label: 'Opening Bid',
  amount: MISSING_VALUE_LABEL
}))).when(l => getStartingBidAmount(l), l => ({
  label: 'Opening Bid',
  amount: getOpeningBidValue(l)
})).when(l => getERV(l), l => {
  const erv = getERV(l);
  return {
    label: 'Est. Market Value',
    amount: erv != null ? formatMoney$2(erv, undefined, true) : ''
  };
}).otherwise(() => ({
  label: 'Opening Bid',
  amount: MISSING_VALUE_LABEL
}));
const getIsReserveMet = listing => {
  const {
    selling_method
  } = listing;
  return selling_method?.__typename === 'OnlineAuctionSegment' && selling_method?.reserve_status === enums$5.ReserveStatus.ReserveMet;
};
const isListingSoldOrPending = listing => [enums$5.ListingStatusGroup.Pending, enums$5.ListingStatusGroup.Sold].includes(listing.listing_status_group);
const shouldDisplayCurrentBid = listing => !isListingSoldOrPending(listing) && !!getCurrentBid(listing);
const getCurrentBid = listing => {
  const {
    selling_method
  } = listing;
  if (selling_method?.__typename === 'LiveAuctionSegment' || selling_method?.__typename === 'OnlineAuctionSegment') {
    return selling_method?.current_highest_bid?.bid_amount;
  }
  return undefined;
};
const shouldDisplayReserveMet = listing => {
  return !isListingSoldOrPending(listing) && getIsReserveMet(listing) && !isPrivateSeller(listing);
};

var card = /*#__PURE__*/Object.freeze({
    __proto__: null,
    MISSING_VALUE_LABEL: MISSING_VALUE_LABEL,
    RESERVE_LABELS: RESERVE_LABELS,
    getAddressLine: getAddressLine,
    getCurrentBid: getCurrentBid,
    getDisplayedValue: getDisplayedValue,
    getERV: getERV,
    getIsReserveMet: getIsReserveMet,
    getOpeningBidValue: getOpeningBidValue,
    getReserveLabel: getReserveLabel,
    shouldDisplayCurrentBid: shouldDisplayCurrentBid,
    shouldDisplayReserveMet: shouldDisplayReserveMet,
    valuationEstimate: valuationEstimate
});

const isEstCreditBidPending$1 = listing => !listing.reserve_price;
const isTrustee$1 = listing => Boolean(listing.event?.trustee_sale);
const isCancelled$1 = listing => listing.listing_status === enums$5.ListingStatus.Canceled;
const isRemoved = listing => listing.listing_status === enums$5.ListingStatus.Removed;
const isWithdrawn = listing => listing.listing_status === enums$5.ListingStatus.Withdrawn;
// Whether a listing still renders a public PDP a buyer can act on. Canceled /
// removed (foreclosure) and withdrawn (private-seller) listings dead-end on the
// "property no longer available" page and must not be offered as a purchase
// path, auto-selected, or shown as a dual-track sibling. Publish flags are
// Maybe<Boolean> — only an explicit false marks a dead end; absence is not
// evidence of one. Single source of truth for dual-track displayability
// (resi-fe-pdp getPurchasePaths + server cycle selection consume this).
const isPubliclyDisplayable = listing => Boolean(listing) && listing.is_published !== false && listing.is_searchable !== false && !isCancelled$1(listing) && !isRemoved(listing) && !isWithdrawn(listing);
const isPostponed$1 = listing => [enums$5.ListingStatus.Postponed, enums$5.ListingStatus.PostponedPendingScheduling].includes(listing.listing_status);
const isSaleFailed$1 = listing => listing.listing_status === enums$5.ListingStatus.SaleFailed;
const isCleared$1 = listing => [enums$5.ListingStatus.SaleGatekeeping, enums$5.ListingStatus.SalePaymentPending, enums$5.ListingStatus.Closed, enums$5.ListingStatus.NoSale].includes(listing.listing_status);
const showOpeningBid$1 = listing => {
  if (listing.listing_summary && !isNil(listing.listing_summary.show_opening_bid)) return Boolean(listing.listing_summary.show_opening_bid);
  const openingBidMarkettingAttribute = listing.marketing_attributes?.find(ele => ele?.attribute_type === enums$5.MarketingAttributeType.OpeningBid);
  return Boolean(openingBidMarkettingAttribute?.show);
};
const isClosed = listing => [enums$5.ListingStatus.Closed, enums$5.ListingStatus.SaleGatekeeping, enums$5.ListingStatus.SalePaymentPending].includes(listing.listing_status);
const isRemoteBidEnabled$1 = listing => {
  const {
    listing_summary
  } = listing;
  if (!listing_summary?.is_remote_bid_enabled && !listing_summary?.is_remote_before_and_during_auction_enabled) {
    const attributes = keyBy(listing.marketing_attributes, 'attribute_type');
    return Boolean(attributes?.[enums$5.MarketingAttributeType.RemoteBid]?.is_enabled || attributes?.[enums$5.MarketingAttributeType.RemoteBidBeforeAndDuringAuction]?.is_enabled);
  }
  return Boolean(listing_summary?.is_remote_bid_enabled || listing_summary?.is_remote_before_and_during_auction_enabled);
};
const isUsingSellerLevelFees$1 = listing => Boolean(listing.listing_configuration?.is_buyers_premium_available || listing.listing_configuration?.is_earnest_money_deposit_available);
const isPositiveNumber = value => typeof value === 'number' && value >= 0;
const calculateMax = (a, b) => [a, b].every(isPositiveNumber) ? Math.max(a, b) : null;
const calculateFlatFee = fee => isPositiveNumber(fee) ? fee : null;
const calculatePercentageFee = (base, percentage) => [base, percentage].every(isPositiveNumber) ?
// @ts-ignore if it's a positive number is not undefined
parseFloat((base * (percentage / 100)).toFixed(2)) : null;
const premiumMinimumAmount = listing => listing.program_configuration?.buyers_premium_minimum_amount;
const premiumPrimaryAmount = listing => listing.program_configuration?.buyers_premium_primary_amount;
const buyersPremiumCommissionType = listing => listing.program_configuration?.buyers_premium_commission_type;
/**
 * Whether a Buyer's Premium is actually charged to the buyer for this listing.
 *
 * This gates on the listing-level flag (new `is_buyers_premium_available`, legacy
 * `buyer_premium_available`) which the backend derives from the program configuration
 * AND `buyers_premium_paid_by`. When the premium is paid by the seller, the backend
 * sets this flag to false even though program_configuration still carries the amounts
 * (they describe the seller's cost, not the buyer's). Returns:
 *  - `false` when the buyer is explicitly NOT charged
 *  - `true`/`undefined` otherwise (undefined = flag not present, preserve prior behavior)
 */
const isBuyersPremiumAvailable = listing => listing.listing_configuration?.is_buyers_premium_available ?? listing.listing_configuration?.buyer_premium_available;
const calculateBuyersPremium = (listing, bidAmount) => {
  // If the backend explicitly marks the premium unavailable to the buyer (e.g. it is
  // paid by the seller), do not charge or display it — regardless of configured amounts.
  if (isBuyersPremiumAvailable(listing) === false) {
    return null;
  }
  let fee;
  switch (buyersPremiumCommissionType(listing)) {
    case enums$5.CommissionStrategyType.Percentage:
      {
        fee = calculatePercentageFee(bidAmount, premiumPrimaryAmount(listing));
        break;
      }
    case enums$5.CommissionStrategyType.Dollar:
      {
        fee = calculateFlatFee(premiumPrimaryAmount(listing));
        break;
      }
    default:
      fee = null;
  }
  return calculateMax(fee, premiumMinimumAmount(listing));
};
const earnestMoneyDepositCommissionType = listing => listing.program_configuration?.earnest_money_deposit_commission_type;
const earnestMinimumAmount = listing => listing.program_configuration?.earnest_money_deposit_minimum_amount;
const earnestPrimaryAmount = listing => listing.program_configuration?.earnest_money_deposit_primary_amount;
/**
 * Calculates the earnest money deposit required for a given listing and total purchase price.
 * The calculation depends on the commission type defined in the listing's program configuration:
 * - If the commission type is "Percentage", the fee is calculated as a percentage of the total purchase price.
 * - If the commission type is "Dollar", the fee is a flat amount.
 * - If the listing is an FCL online auction, the function returns null.
 * The function ensures that the calculated fee is not less than the minimum amount specified in the listing's configuration.
 *
 * @param listing - The listing object containing program configuration and auction details.
 * @param totalPurchasePrice - The total purchase price to use as the base for percentage calculations.
 * @returns The calculated earnest money deposit as a number, or null if not applicable.
 */
const calculateEarnestMoneyDeposit = (listing, totalPurchasePrice) => {
  if (isFclOnlineAuction(listing)) {
    return null;
  }
  let fee;
  switch (earnestMoneyDepositCommissionType(listing)) {
    case enums$5.CommissionStrategyType.Percentage:
      {
        fee = calculatePercentageFee(totalPurchasePrice, earnestPrimaryAmount(listing));
        break;
      }
    case enums$5.CommissionStrategyType.Dollar:
      {
        fee = calculateFlatFee(earnestPrimaryAmount(listing));
        break;
      }
    default:
      fee = null;
  }
  return calculateMax(fee, earnestMinimumAmount(listing));
};
const getCostCalculations$1 = (listing, bidAmount) => {
  const buyersPremium = calculateBuyersPremium(listing, bidAmount);
  const totalPurchasePrice = bidAmount + (buyersPremium || 0);
  const earnestMoneyDeposit = calculateEarnestMoneyDeposit(listing, totalPurchasePrice);
  return {
    buyersPremium,
    totalPurchasePrice,
    earnestMoneyDeposit
  };
};
const BUYERS_PREMIUM_EXPLANATIONS = {
  ABSENT: "A Buyer's Premium does not apply to this property.",
  TYPE_FLAT: '{{amount}} regardless of Winning Bid Amount.',
  TYPE_PERCENT: '{{percentage}}% of winning bid or {{amount}}, whichever is higher. Only the winner pays this fee.'
};
const getBuyersPremiumExplanation$1 = (listing, bidAmount) => {
  const buyersPremium = calculateBuyersPremium(listing, bidAmount);
  const {
    buyers_premium_commission_type,
    buyers_premium_minimum_amount,
    buyers_premium_primary_amount
  } = listing.program_configuration || {};
  if (!buyersPremium) {
    return BUYERS_PREMIUM_EXPLANATIONS.ABSENT;
  } else if (buyers_premium_commission_type === enums$5.CommissionStrategyType.Dollar) {
    const amount = formatMoney$2(buyersPremium);
    return interpolate(BUYERS_PREMIUM_EXPLANATIONS.TYPE_FLAT, {}, () => amount).join('');
  } else if (buyers_premium_commission_type === enums$5.CommissionStrategyType.Percentage) {
    const amount = formatMoney$2(buyers_premium_minimum_amount ?? 0);
    return interpolate(BUYERS_PREMIUM_EXPLANATIONS.TYPE_PERCENT, {}, key => key === 'amount' ? amount : buyers_premium_primary_amount).join('');
  } else {
    return null;
  }
};
const filterOpenHouses = listings => {
  if (!listings?.length) {
    return listings;
  }
  return listings.map(listing => {
    if (!listing.open_houses?.length) {
      return listing;
    }
    let modifiedOpenHouseInfo = listing.open_houses?.sort((a, b) => {
      if (!a?.local_date || !b?.local_date) return 0;
      return new Date(`${a.local_date} ${a.end_time || ''}`).getTime() - new Date(`${b.local_date} ${b.end_time || ''}`).getTime();
    });
    modifiedOpenHouseInfo = modifiedOpenHouseInfo?.filter(oh => {
      const ohDate = oh?.local_date && new Date(`${oh.local_date} ${oh.end_time || ''}`);
      return ohDate && ohDate >= new Date();
    });
    return {
      ...listing,
      open_houses: modifiedOpenHouseInfo
    };
  });
};
const getPropertyTypeInfo = ({
  listing,
  featureFlags
}) => {
  if (isSparkOffer$1(listing)) return null;
  const {
    listing_summary,
    online_auction_segment
  } = listing;
  let offerType = null;
  const combinedTextArray = [];
  if (listing_summary?.is_remote_bid_enabled || listing_summary?.is_remote_before_and_during_auction_enabled) {
    offerType = 'Remote Bid';
  } else {
    if (online_auction_segment && onlineAuctionUtils.shouldShowSealedBidTreatment(online_auction_segment, featureFlags)) {
      offerType = 'Offer';
    }
  }
  // below this is the handling of the bid deposit rule
  const bidDepositRule = get(listing, 'live_auction_segment.configuration.state_deposit_rule', '');
  if (bidDepositRule && !bidDepositRule.includes('100%')) {
    combinedTextArray.push(`Winning Bid Deposit ${shortenFlatLabel(bidDepositRule) || ''}`);
  }
  const physicalStructureCode = listing.primary_property?.summary?.structure_type_code;
  const physicalStructure = globalPhysicalStructures.find(entry => entry.value === physicalStructureCode);
  const brokerName = listing.parties?.find(p => p?.party_role === enums$5.ListingPartyRole.Broker)?.party_name;
  const structureInfoCombined = (physicalStructure ? physicalStructure.text : '') + (brokerName ? ` Listed by ${brokerName}` : '');
  structureInfoCombined && combinedTextArray.push(structureInfoCombined);
  return {
    combinedTextArray,
    offerType,
    structureInfoCombined
  };
};
const utils$c = {
  calculateBadges,
  calculatePDPRedesignBadges,
  calculatePDPSellerTermsBadges,
  calculatePrivateSellerBadges,
  calculatePropertyCardBadges,
  getLifecyclePreviousPrice,
  getLifecyclePriceDropAmount,
  hasLifecycleEvent,
  isLifecycleEventBadge,
  filterOpenHouses,
  getBadges,
  getBuyersPremiumExplanation: getBuyersPremiumExplanation$1,
  getCostCalculations: getCostCalculations$1,
  getDisplayIndicator,
  buildSiblingListingPaths,
  getPropertyTypeInfo,
  getSegmentType,
  getStartingBidAmount,
  getAuctionEndDate,
  isAuctionActive,
  isCancelled: isCancelled$1,
  isClosed,
  isPubliclyDisplayable,
  isRemoved,
  isWithdrawn,
  isEstCreditBidPending: isEstCreditBidPending$1,
  isForeclosure,
  isPostponed: isPostponed$1,
  isSaleFailed: isSaleFailed$1,
  isCleared: isCleared$1,
  isPrivateSeller,
  isTPSLive: isTPSLive$1,
  isTPSOnline: isTPSOnline$1,
  isRemoteBidEnabled: isRemoteBidEnabled$1,
  isSmartSale,
  isSparkOffer: isSparkOffer$1,
  isTrustee: isTrustee$1,
  isUsingSellerLevelFees: isUsingSellerLevelFees$1,
  shouldDisplayListPrice: shouldDisplayListPrice$1,
  showOpeningBid: showOpeningBid$1,
  ...listingUtils$1,
  ...registrationUtils,
  card
};

/* istanbul ignore file */
const pdpFragments = [fragments$7.address.definition, fragments$7.listingCard.definition, fragments$7.badges.definition, fragments$7.requiringActiveSession.definition, fragments$7.activeListing.definition];
const offerManagementFragments = [fragments$7.address.definition, fragments$7.requiringActiveSession.definition, fragments$7.sealedBidOfferManagement.definition];
const getActiveListings = ({
  listingId,
  isRemoteBiddingEnabled,
  isLoggedIn
}, {
  req
}) => fetch.forAuctionGraph({
  query: gql$5`
            ${pdpFragments.join()}
            query ${embeddableOperation('blueprint_getActiveListingSummary')}($listingId: ID!, $isRemoteBiddingEnabled: Boolean!, $hasAuthenticatedUser: Boolean!) {
              active_listing_summary(listingId: $listingId) {
                active_listings {
                  ${fragments$7.activeListing.reference}
                  ${fragments$7.requiringActiveSession.reference}
                     @include(if: $hasAuthenticatedUser)
                }
              }
            }
        `,
  variables: {
    listingId,
    isRemoteBiddingEnabled,
    hasAuthenticatedUser: isLoggedIn
  }
}, {
  req
})
// eslint-disable-next-line @typescript-eslint/no-unsafe-return
.then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null || data.active_listing_summary === null) {
    throw partialGraphErrorFactory$3('Could not fetch active listing summary', errors);
  }
  return data.active_listing_summary;
}).then(response => {
  if (!response.active_listings?.length) {
    return response;
  }
  response.active_listings = filterOpenHouses(response.active_listings);
  return response;
});
const getCurrentDispositionSalesCycle = (args, fetcherArgs) => {
  return fetch.forAuctionGraph({
    query: gql$5`
            ${pdpFragments.join()}
            query ${embeddableOperation(`blueprint_getCurrentDispositionSalesCycle`)}($urn: URN!, $isRemoteBiddingEnabled: Boolean!, $hasAuthenticatedUser: Boolean!) {
              current_disposition_sales_cycle(urn: $urn) {
                cycle_number
                property_id
                current_listing_id
                active_listings {
                  ${fragments$7.activeListing.reference}
                  ${fragments$7.requiringActiveSession.reference} @include(if: $hasAuthenticatedUser)
                }
              }
            }
        `,
    // These are called args because not all things that are passed through will be straight variables in some cases
    variables: args
  }, fetcherArgs)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.current_disposition_sales_cycle === null) {
      throw partialGraphErrorFactory$3('Could not fetch current disposition sales cycle', errors);
    }
    return data.current_disposition_sales_cycle;
  }).then(cycle => {
    // This is possible, in which case we defer to the caller to determine how the product should react
    if (!cycle.active_listings?.length) {
      return cycle;
    }
    // This is client manipulation we prefer to do closer to the client when we have local clocks to use the end
    // timezone to show local times in schedules
    cycle.active_listings = filterOpenHouses(cycle.active_listings);
    // Now we lookup for the app to do something in cases where it expects to have a single representative listing
    cycle.current_listing = cycle.active_listings.find(listing => listing.listing_id === cycle.current_listing_id);
    return cycle;
  });
};
// Specialized version for offer management that includes sealed bid fragments and doesn't include all of the fluff in activeListing
const getCurrentSalesCycleForOfferManagement = (args, fetcherArgs) => {
  return fetch.forAuctionGraph({
    query: gql$5`
            ${offerManagementFragments.join()}
            query ${embeddableOperation(`blueprint_getCurrentDispositionSalesCycleForOfferManagement`)}($urn: URN!, $isRemoteBiddingEnabled: Boolean!, $hasAuthenticatedUser: Boolean!) {
              current_disposition_sales_cycle(urn: $urn) {
                cycle_number
                property_id
                current_listing_id
                active_listings {
                  ${fragments$7.sealedBidOfferManagement.reference}
                  ${fragments$7.requiringActiveSession.reference} @include(if: $hasAuthenticatedUser)
                }
              }
            }
        `,
    // These are called args because not all things that are passed through will be straight variables in some cases
    variables: args
  }, fetcherArgs)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.current_disposition_sales_cycle === null) {
      throw partialGraphErrorFactory$3('Could not fetch current disposition sales cycle for offer management', errors);
    }
    return data.current_disposition_sales_cycle;
  }).then(cycle => {
    // This is possible, in which case we defer to the caller to determine how the product should react
    if (!cycle.active_listings?.length) {
      return cycle;
    }
    // This is client manipulation we prefer to do closer to the client when we have local clocks to use the end
    // timezone to show local times in schedules
    cycle.active_listings = filterOpenHouses(cycle.active_listings);
    // Now we lookup for the app to do something in cases where it expects to have a single representative listing
    cycle.current_listing = cycle.active_listings.find(listing => listing.listing_id === cycle.current_listing_id);
    return cycle;
  });
};

const clearListingForLiveAuction = (listing, foreclosureListingInput) => fetch.forAuctionGraph({
  query: `
        mutation ${embeddableOperation('blueprint_clearLiveListing')}(
          $input: UpdateForeclosureListingInput!
        ) {
          clearListing(
            input: $input
          ) {
            listing_id
          }
        }
        `,
  variables: {
    input: {
      listing_id: listing.listing_id,
      ...foreclosureListingInput
    }
  }
}).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null || data.clearListing === null) {
    throw partialGraphErrorFactory$3('Unable to clear listing', errors);
  }
  return data.clearListing;
});
const cancelListingForLiveAuction = (listing, foreclosureListingInput) => fetch.forAuctionGraph({
  query: `mutation ${embeddableOperation('blueprint_cancelLiveListing')}(
          $input: CancelForeclosureListingInput!
        ) {
          cancelListing(
            input: $input
          )
        }
          `,
  variables: {
    input: {
      listing_id: listing.listing_id,
      ...foreclosureListingInput
    }
  }
}).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null || !data.cancelListing) {
    throw partialGraphErrorFactory$3('Unable to cancel listing', errors);
  }
  return data.cancelListing;
});
const postponeListingForLiveAuction = (listing, foreclosureListingInput) => fetch.forAuctionGraph({
  query: `mutation ${embeddableOperation('blueprint_postponeLiveListing')}(
          $input: PostponeForeclosureListingInput!
        ) {
          postponeListing(
            input: $input
          )
        }
          `,
  variables: {
    input: {
      listing_id: listing.listing_id,
      ...foreclosureListingInput
    }
  }
}).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null || !data.postponeListing) {
    throw partialGraphErrorFactory$3('Unable to postpone listing', errors);
  }
  return data.postponeListing;
});
const getMerchandisedListings = (listingIds, controller) => {
  return fetch.forAuctionGraph({
    query: gql$5`
            query ${embeddableOperation('blueprint_getMerchandisedListings')}($listingIds: [ID!]!) {
                listings_batched(filters: { listing_ids: $listingIds }) {
                  listing_id
                }
            }
            ${fragments$7.merchandising.definition}
            `,
    variables: {
      listingIds
    }
  }, {
    controller
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.listings_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest online listings', errors);
    }
    return data.listings_batched;
  });
};
const getChangeRequests = (listingIds, controller) => {
  return fetch.forAuctionGraph({
    query: gql$5`
            query ${embeddableOperation('blueprint_getChangeRequests')}($listingIds: [ID!]!) {
                listings_batched(filters: { listing_ids: $listingIds }) {
                  listing_id
                  updated_date
                  change_requests(filters: { review_statuses: [PENDING] }) {
                    ${fragments$7.changeRequest.reference}
                  }
                }
            }
            ${fragments$7.changeRequest.definition}
            `,
    variables: {
      listingIds
    }
  }, {
    controller
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.listings_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest change requests for online listings', errors);
    }
    return data.listings_batched;
  });
};

var providers$c = /*#__PURE__*/Object.freeze({
    __proto__: null,
    cancelListingForLiveAuction: cancelListingForLiveAuction,
    clearListingForLiveAuction: clearListingForLiveAuction,
    getActiveListings: getActiveListings,
    getChangeRequests: getChangeRequests,
    getCurrentDispositionSalesCycle: getCurrentDispositionSalesCycle,
    getCurrentSalesCycleForOfferManagement: getCurrentSalesCycleForOfferManagement,
    getMerchandisedListings: getMerchandisedListings,
    postponeListingForLiveAuction: postponeListingForLiveAuction
});

const calculateBidTypeForLiveAuctionClearance = (listing, creditBid, estimatedTotalDebt) => {
  if (listing.seller_property?.country_primary_subdivision && ['FL', 'NJ'].includes(listing.seller_property?.country_primary_subdivision)) {
    return enums$5.BidType.HighLow;
  } else if (creditBid < estimatedTotalDebt) return enums$5.BidType.Specified;else if (creditBid === estimatedTotalDebt) return enums$5.BidType.TotalDebt;
  return null;
};
const validateBidTypeForLiveAuctionClearance = (listing, bidType, creditBid, totalEstimatedDebt, loanType) => {
  if (listing.seller_property?.country_primary_subdivision && ['FL', 'NJ'].includes(listing.seller_property.country_primary_subdivision) && bidType !== enums$5.BidType.HighLow && loanType !== enums$5.LoanType.Fha) {
    return {
      valid: false,
      message: 'Cannot select bid type other than High/Low'
    };
  } else if (bidType === enums$5.BidType.Specified && creditBid >= totalEstimatedDebt && loanType !== enums$5.LoanType.Fha) return {
    valid: false,
    message: 'Cannot select Specified bid type when credit bid is equal to or greater then total debt'
  };else if (bidType === enums$5.BidType.TotalDebt && creditBid !== totalEstimatedDebt && loanType !== enums$5.LoanType.Fha) return {
    valid: false,
    message: `Cannot select Total Debt bid type when credit bid and attorney's total debt amount do not match`
  };
  return {
    valid: true,
    message: ''
  };
};
const utils$b = {
  calculateBidTypeForLiveAuctionClearance,
  validateBidTypeForLiveAuctionClearance,
  clearListingForLiveAuction,
  cancelListingForLiveAuction,
  postponeListingForLiveAuction
};

/* istanbul ignore file */
const {
  formatDocumentsForDisplay,
  getAllDocuments,
  getClosingCompanyData,
  getDocumentTitle,
  getPartnershipDetails,
  getPaymentInformation,
  getProvisionalSaleDateTime,
  getSellerDeedTypeLabel,
  getSpecialDocuments,
  getTitleInsurancePaidBy,
  isFCLMarketing,
  isTPSOhio
} = listingUtils$1;
const getDueDiligenceData = listing => ({
  titleInsurancePaidBy: getTitleInsurancePaidBy(listing),
  sellerDeedType: getSellerDeedTypeLabel(listing),
  closingCompanyData: getClosingCompanyData(listing),
  provisionalSaleDateTime: getProvisionalSaleDateTime(listing),
  partnershipDetails: getPartnershipDetails(listing),
  isFclMarketing: isFCLMarketing(listing),
  paymentInformation: getPaymentInformation(listing),
  isTPSOhio: isTPSOhio(listing),
  specialDocuments: getSpecialDocuments(getAllDocuments(listing), isTPSOhio(listing))
});
const isListingIdOnly = listing => {
  return typeof listing === 'string';
};
// The idea with this hook is to have a single source of truth for the PDP
// listing info displayed on the screen.
// As we rework the different sections in future tickets as part of our
// redesign initiative it would be a good idea to have all the logic that
// transforms the Listing, User and other data into whatever PDP needs to
// show for each section.
const usePage = ({
  initialData,
  featureFlags,
  isLoggedIn
}) => {
  const [initialListings] = useState(initialData);
  const initialListing = initialListings[0];
  const [currentListingId, setCurrentListingId] = useState(isListingIdOnly(initialListing) ? initialListing : initialListing.listing_id);
  const isRemoteBiddingEnabled = featureFlags['fe.resi.enable-web-remote-bidding'];
  const getComputedListingValues = listing => {
    const documentList = getAllDocuments(listing);
    // Turns specialDocs object into an easily-hashable object keyed by `media_external_title`
    const specialDocs = _.invertBy(getSpecialDocuments(documentList, isTPSOhio(listing)), value => getDocumentTitle(value));
    const sortedDocs = documentList.filter(doc => getDocumentTitle(doc) && !specialDocs[getDocumentTitle(doc)]);
    return {
      applicableDocuments: formatDocumentsForDisplay(sortedDocs),
      dueDiligence: getDueDiligenceData(listing)
    };
  };
  const initializeActiveListings = useCallback(async () => {
    if (initialListing) {
      const response = await getActiveListings({
        listingId: isListingIdOnly(initialListing) ? initialListing : initialListing.listing_id,
        isRemoteBiddingEnabled,
        isLoggedIn
      }, {
        req: null
      });
      if (response?.active_listings?.length) {
        const extendedResponse = response?.active_listings.map(listing => ({
          ...listing,
          __computed__: getComputedListingValues(listing)
        }));
        attemptActiveListingsWrite({
          data: extendedResponse,
          source: 'local_optimistic_action'
        });
      }
    }
  }, [initialListing, getComputedListingValues]);
  useEffect(() => {
    if (featureFlags['app.enable-pdp-design-2025'] || featureFlags['fe.resi.enable-unified-pdp']) {
      initializeActiveListings();
    }
  }, []);
  // Seed the collection from ALL initial listings (not just the first) so
  // that SSR-provided siblings are available on first render — avoids the
  // client-side layout jump when the useEffect fetch adds them later.
  const initialPDPListings = useMemo(() => initialListings.map(entry => !isListingIdOnly(entry) ? {
    ...entry,
    __computed__: getComputedListingValues(entry)
  } : {
    listing_id: entry,
    __computed__: {
      applicableDocuments: [],
      dueDiligence: {}
    }
  }), [initialListings]);
  // On re-auth, refetch the FULL active-listings set (all sibling cycles), not
  // just the current listing — otherwise a dual-track PDP collapses to a single
  // listing after login. Forces the authenticated session so session-gated
  // fields hydrate on every sibling.
  const refresh = async () => {
    try {
      const response = await getActiveListings({
        listingId: isListingIdOnly(initialListing) ? initialListing : initialListing.listing_id,
        isRemoteBiddingEnabled,
        isLoggedIn: true
      }, {
        req: null
      });
      if (response?.active_listings?.length) {
        const extendedResponse = response.active_listings.map(listing => ({
          ...listing,
          __computed__: getComputedListingValues(listing)
        }));
        attemptActiveListingsWrite({
          data: extendedResponse,
          source: 'local_optimistic_action'
        });
      }
    } catch (err) {
      console.error(err);
    }
  };
  // TODO: Moving property save logic here is a BIG change
  // for now limiting this to persist the new save value on the listing
  const updateSaveProperty = value => {
    if (currentListing) {
      attemptActiveListingsWrite({
        data: [{
          ...currentListing,
          primary_property: {
            ...currentListing.primary_property,
            is_currently_saved: value
          }
        }],
        source: 'local_optimistic_action'
      });
    }
  };
  const {
    collection: activeListings,
    attemptWrite: attemptActiveListingsWrite
  } = useIdempotentBlueprintCollection(initialPDPListings, {
    primaryKey: 'listing_id',
    updateBehavior: 'merge',
    utils: listingUtils$1
  });
  // Resolve the current listing from the collection. Compare as strings so a
  // number/string id mismatch (SSR seed vs authed refetch) can't cause a miss,
  // and fall back to the first listing so a transient lookup miss never yields
  // `undefined` for a non-empty collection — the PDP dereferences
  // currentListing directly, so an undefined here is a white-screen crash.
  const currentListing = useMemo(() => activeListings.raw.find(listing => String(listing.listing_id) === String(currentListingId)) ?? activeListings.raw[0], [activeListings.raw, currentListingId]);
  // When unified PDP is enabled and multiple listings share a property,
  // hoist property-level fields (photos for now) from sibling
  // listings so that toggling doesn't lose shared property data.
  const hoistedListing = useMemo(() => {
    if (!featureFlags['fe.resi.enable-unified-pdp'] || !currentListing || activeListings.raw.length <= 1) {
      return currentListing;
    }
    const siblings = activeListings.raw.filter(l => String(l.listing_id) !== String(currentListingId));
    // Photos are a property-level asset. Prefer the private-seller listing's
    // photos (PS-precedence), then the current listing, then any sibling.
    const privateSellerListing = [currentListing, ...siblings].find(utils$c.isPrivateSeller);
    const photos = [privateSellerListing, currentListing, ...siblings].find(l => (l?.photos?.length ?? 0) > 0)?.photos || currentListing.photos;
    return {
      ...currentListing,
      photos
    };
  }, [currentListing, activeListings.raw, currentListingId, featureFlags]);
  return {
    data: {
      activeListings,
      currentListing: hoistedListing
    },
    actions: {
      refresh,
      updateSaveProperty,
      setCurrentListingId
    }
  };
};

const listingLogger = coreLogger.forModule('blueprint:merchandised_listing');
const useMerchandisedListings = ({
  graphWebsocket,
  initialData
}) => {
  const subjectIds = useMemo(() => {
    return initialData.map(l => l.listing_id);
  }, [initialData]);
  const {
    collection,
    attemptWrite
  } = useIdempotentBlueprintCollection(initialData, {
    primaryKey: 'listing_id',
    // @ts-ignore idempotencyKey<Listing> seems to be an incorrect type fefinition
    idempotencyKey: 'updated_date',
    updateBehavior: 'merge',
    collectionName: 'blueprint:idempotent_collection:merchandised_listing'
  });
  useRealtimeWatcher({
    id: 'useMerchandisedListing',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: gql$5`
          subscription ${embeddableOperation('blueprint_merchandisedListingsUpdated')}($filters: ListingsUpdatedFilters) {
            listingsUpdated(filters: $filters) {
              ${fragments$7.merchandising.reference}
            }
          }
          ${fragments$7.merchandising.definition}
        `,
        variables: {
          filters: {
            listingIds: subjectIds
          }
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.listingsUpdated].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      listingLogger.info(`New merchandised listing data loaded. source=${source}`, data);
      attemptWrite({
        source,
        // @ts-ignore We need to make this type Partial<T> if we allow overwriting
        data
      });
    },
    onError: error => {
      listingLogger.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      listingLogger.info('Have event', event, ctx);
    }
  });
  const data = useMemo(() => ({
    listings: collection
  }), [collection]);
  const output = useMemo(() => ({
    data
  }), [data]);
  return output;
};
const changeReqLogger = coreLogger.forModule('blueprint:change_requests');
const makeCollection = collectionData => ({
  raw: collectionData,
  mapped: collectionData.reduce((acc, element) => acc.set(element.listing_id, element), new Map())
});
const useChangeRequests = ({
  initialData
}) => {
  const subjectIds = useMemo(() => {
    return initialData.map(l => l.listing_id);
  }, [initialData]);
  // not using idempotant collection because don't know what to use for
  // idempotentcy key. updated_date on listing seem to not get updated when change
  // requests change
  const [collection, setCollection] = useState(makeCollection(initialData));
  // TODO change to use idempotent collection to prevent this kind of already-solved bugs
  useEffect(() => {
    setCollection(makeCollection(initialData));
  }, [initialData]);
  const attemptWrite = useCallback(({
    source,
    data
  }) => {
    const upsertRequired = !_.isEqual(collection.raw, data);
    if (upsertRequired || source === 'local_optimistic_action') {
      changeReqLogger.info(`Data Transport Effectiveness.`, {
        source,
        causedChange: upsertRequired
      });
      setCollection(makeCollection(data));
    }
  }, [collection.raw]);
  const refresh = useCallback(async () => {
    try {
      const changeRequests = await getChangeRequests(subjectIds);
      attemptWrite({
        source: 'local_optimistic_action',
        data: changeRequests
      });
    } catch (err) {
      changeReqLogger.error(err);
    }
  }, [subjectIds, attemptWrite]);
  useRealtimeWatcher({
    id: 'useChangeRequests',
    subjectIds,
    polling: {
      enabled: true,
      frequency: SUPPORTED_OFFSETS.URGENT,
      executor: ({
        subjectIds,
        controller
      }) => {
        return subjectIds?.length ? getChangeRequests(subjectIds, controller) : Promise.resolve();
      }
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      changeReqLogger.info(`New change requests data loaded. source=${source}`, data);
      attemptWrite({
        source,
        data
      });
    },
    onError: error => {
      changeReqLogger.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      changeReqLogger.info('Have event', event, ctx);
    }
  });
  const data = useMemo(() => ({
    listings: collection
  }), [collection]);
  const actions = useMemo(() => ({
    refresh
  }), [refresh]);
  const output = useMemo(() => ({
    data,
    actions
  }), [data, actions]);
  return output;
};
const invitationLogger = coreLogger.forModule('blueprint:invitation');
const useInvitations = ({
  graphWebsocket,
  initialData
}) => {
  const subjectIds = useMemo(() => {
    return initialData.map(l => l.listing_id);
  }, [initialData]);
  const invitationsInitialData = useMemo(() => initialData.map(l => ({
    listing_id: l.listing_id,
    associated_invitations: l.associated_invitations
  })), [initialData]);
  // not using idempotant collection because don't know what to use for
  // idempotentcy key. updated_date on invitation is null
  const [collection, setCollection] = useState(makeCollection(invitationsInitialData));
  useEffect(() => {
    setCollection(makeCollection(invitationsInitialData));
  }, [invitationsInitialData]);
  const attemptWrite = ({
    source,
    data
  }) => {
    const action = data.action;
    const invitation = data.invitation;
    if (!invitation) {
      invitationLogger.info(`Data Transport Effectiveness.`, {
        source,
        causedChange: false
      });
      return;
    }
    if (invitation) {
      const invitationRelative = invitation.relatives?.find(r => typeof r?.object_id === 'string' && Boolean(collection.mapped.get(r?.object_id)));
      const listingId = typeof invitationRelative?.object_id === 'string' ? invitationRelative?.object_id : '';
      match(action).with('CREATED', () => {
        const newRawCollection = collection.raw.map(l => {
          if (l.listing_id === listingId) {
            return {
              ...l,
              associated_invitations: [...(Array.isArray(l.associated_invitations) ? l.associated_invitations : []), invitation]
            };
          }
          return l;
        });
        setCollection(makeCollection(newRawCollection));
        invitationLogger.info(`Data Transport Effectiveness.`, {
          source,
          causedChange: true
        });
      }).with('REVOKED', 'ACCEPTED', () => {
        const newRawCollection = collection.raw.map(l => {
          if (l.listing_id === listingId) {
            return {
              ...l,
              associated_invitations: Array.isArray(l.associated_invitations) ? l.associated_invitations.map(inv => {
                if (inv.invitation_id === invitation.invitation_id) {
                  return invitation;
                }
                return inv;
              }) : []
            };
          }
          return l;
        });
        setCollection(makeCollection(newRawCollection));
        invitationLogger.info(`Data Transport Effectiveness.`, {
          source,
          causedChange: true
        });
      }).otherwise(() => {
        invitationLogger.info(`Data Transport Effectiveness.`, {
          source,
          causedChange: false
        });
      });
    }
  };
  useRealtimeWatcher({
    id: 'useMerchandisedListing',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: gql$5`
          subscription ${embeddableOperation('blueprint_invitationUpdated')}($filters: InvitationUpdatedFilters) {
            invitationUpdated(filters: $filters) {
              action
              invitation {
                ${fragments$7.invitation.reference}
              }
            }
          }
          ${fragments$7.invitation.definition}
        `,
        variables: {
          filters: {
            urns: subjectIds.map(listingId => `adc:listing:${listingId}`)
          }
        }
      }),
      // @ts-ignore data is not typed
      dataExtractor: data => [data.invitationUpdated]
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      invitationLogger.info(`New invitation data loaded. source=${source}`, data);
      if (source === 'websocket') {
        // @ts-ignore TODO: Roman please fix
        const invitationData = data[0];
        attemptWrite({
          source,
          data: invitationData
        });
      }
    },
    onError: error => {
      invitationLogger.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      invitationLogger.info('Have event', event, ctx);
    }
  });
  const data = useMemo(() => ({
    listings: collection
  }), [collection]);
  const output = useMemo(() => ({
    data
  }), [data]);
  return output;
};

const useLiveAuctionPreparation = ({
  initialData
}) => {
  const {
    collection: idempotentListings
  } = useIdempotentBlueprintCollection(initialData?.listings, {
    primaryKey: 'listing_id',
    utils: utils$b,
    updateBehavior: 'merge'
  });
  const data = useMemo(() => ({
    listings: idempotentListings
  }), [idempotentListings]);
  const output = useMemo(() => ({
    data
  }), [data]);
  return output;
};

var hooks$e = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useChangeRequests: useChangeRequests,
    useInvitations: useInvitations,
    useLiveAuctionPreparation: useLiveAuctionPreparation,
    useMerchandisedListings: useMerchandisedListings,
    usePage: usePage
});

/* istanbul ignore file */
[...Object.values(enums$5.PropertyDocumentPurposeType), ...Object.values(enums$5.GlobalMediaPurpose), ...Object.values(enums$5.GlobalMediaPriorityLevel)];

/* eslint-disable blueprint/namespace-exports -- Legacy: non-standard exports (badgeTypes, badges, card, ListingBadgeVariant) and wildcard re-exports need migration */

var index$l = /*#__PURE__*/Object.freeze({
    __proto__: null,
    MISSING_VALUE_LABEL: MISSING_VALUE_LABEL,
    RESERVE_LABELS: RESERVE_LABELS,
    badgeTypes: types$7,
    badges: badges,
    card: card,
    constants: constants$5,
    fragments: fragments$7,
    getReserveLabel: getReserveLabel,
    hooks: hooks$e,
    providers: providers$c,
    utils: utils$c
});

/**
 * @deprecated use onlineAuctionUtils.getNextProxyBidAmount() instead
 */
/* istanbul ignore next */
const getNextProxyBidAmount = (listing, segment) => {
  if (isProxyBiddingActive(listing, segment)) {
    return Math.max(
    // @ts-ignore deprecated, use onlineAuctionUtils.getNextProxyBidAmount() instead
    segment.starting_bid_amount, (segment.current_user_max_bid?.max_bid_amount || listing.current_user_max_bid?.max_bid_amount || 0) + 1 // allows getting from listing or segment in case there's no segment when we're checking this, but we prefer value from segment as that's more up to date when user is actively proxy bidding due to our max bid realtime watcher
    );
  }
  return null;
};
//TODO: Remove after releasing new max bidding functionality
/**
 * @deprecated use onlineAuctionUtils.isProxyBiddingActive() instead
 */
const isProxyBiddingActive = (listing, segment) => (!segment || onlineAuctionUtils.isAuctionNotStarted(segment)) && listing.is_current_user_registered_for_online_auction && listing.listing_configuration?.is_proxy_bidding_enabled;
const {
  getBuyersPremiumExplanation,
  getCostCalculations,
  isCancelled,
  isEstCreditBidPending,
  isPostponed,
  isSaleFailed,
  isCleared,
  isRemoteBidEnabled,
  isTPSLive,
  isTPSOnline,
  isTrustee,
  isUsingSellerLevelFees,
  showOpeningBid,
  shouldDisplayListPrice,
  isSparkOffer
} = utils$c;
const listingUtils = {
  getBuyersPremiumExplanation,
  getCostCalculations,
  getNextProxyBidAmount,
  isCancelled,
  isEstCreditBidPending,
  isPostponed,
  isSaleFailed,
  isCleared,
  isProxyBiddingActive,
  isRemoteBidEnabled,
  isTrustee,
  isTPSLive,
  isTPSOnline,
  isUsingSellerLevelFees,
  showOpeningBid,
  shouldDisplayListPrice,
  isSparkOffer
};

var utils$a = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getWhatFieldChanged: getWhatFieldChanged,
    listingUtils: listingUtils,
    liveAuctionUtils: liveAuctionUtils,
    onlineAuctionUtils: onlineAuctionUtils,
    shortenFlatLabel: shortenFlatLabel
});

const CURRENT_SERVER_TIME_POLLING_INTERVAL = ms('1m');
const getLocalTimeIfNecessary = m => m?.isValid() ? m : moment$1();
// NOTE: We have chosen now specifically to compute this from segment data only, since that
// is the only data we can reliably rely on to be accurate since the listing data comes from
// index and isn't updated after initially being fetched.
/* eslint-disable camelcase */
const computeNextLivePollingOffset = segments => {
  if (segments.every(({
    end_status
  }) => end_status === enums$5.LiveAuctionSegmentBidEndStatus.NoSale || end_status === enums$5.LiveAuctionSegmentBidEndStatus.Sold)) {
    return SUPPORTED_OFFSETS.SLACK;
  }
  return segments.some(({
    segment_status
  }) => segment_status === enums$5.AuctionSegmentStatus.Started) ? SUPPORTED_OFFSETS.CRITICAL : SUPPORTED_OFFSETS.NORMAL;
};
// NOTE: We have chosen now specifically to compute this from segment data only, since that
// is the only data we can reliably rely on the be accurate since the listing data comes from
// index and isn't updated after initially being fetched.
//
// TODO: Why is this signature order the reverse of above?!
//
/* eslint-disable camelcase */
const computeNextOnlinePollingOffset = (currentDateTime, segments) => {
  if (segments.every(({
    segment_status
  }) => segment_status === enums$5.OnlineAuctionSegmentStatus.Pending || segment_status === enums$5.OnlineAuctionSegmentStatus.Ended)) {
    return SUPPORTED_OFFSETS.SLACK;
  }
  // Now we need to pick our polling strategy based off the most urgent priority in the whole list. Another strategy
  // would be offsetting polling and polling on different intervals with different slices of the same data on the
  // screen. We don't do that. Because we have a single poll the next duration must be quick enough to update the
  // highest priority item, which for us is ones that are about to end.
  const currentInstant = getLocalTimeIfNecessary(moment$1(currentDateTime));
  const estimatedFinishingTimeDeltas = segments.filter(({
    segment_status
  }) => segment_status !== enums$5.OnlineAuctionSegmentStatus.Ended).map(({
    initial_end_date
  }) => {
    return moment$1(initial_end_date).diff(currentInstant, 'milliseconds');
  });
  const priorityDelta = Math.min(...estimatedFinishingTimeDeltas);
  if (priorityDelta < ms('30s')) {
    return SUPPORTED_OFFSETS.CRITICAL;
  } else if (priorityDelta < ms('2m')) {
    return SUPPORTED_OFFSETS.URGENT;
  } else {
    return SUPPORTED_OFFSETS.NORMAL;
  }
};

const DEFAULT_MAX_LIMIT = 3;
const useBoundedArray = ({
  size = DEFAULT_MAX_LIMIT,
  onCapacityReachedText = 'bounded array'
}) => {
  const [keys, setKeys] = useState(new Set());
  const addElement = useCallback(key => {
    if (key && !keys.has(key)) {
      const latestElement = new Set(keys);
      latestElement.add(key);
      if (latestElement.size > size) {
        const elementToBeRemoved = Array.from(latestElement.values())[0];
        latestElement.delete(elementToBeRemoved);
        coreLogger.info(`Max limit reached on ${onCapacityReachedText}, Removed element `, elementToBeRemoved);
      }
      setKeys(latestElement);
    }
  }, [onCapacityReachedText, keys, size]);
  const removeElement = useCallback(key => {
    if (key && keys.has(key)) {
      setKeys(prev => {
        const keys = new Set(prev);
        keys.delete(key);
        return keys;
      });
    }
  }, [keys]);
  return useMemo(() => ({
    keys,
    addElement,
    removeElement
  }), [keys, addElement, removeElement]);
};

const logger$g = coreLogger.forModule('blueprint:bidding');
const MAX_LIMIT_FOCUSED_LISTINGS = 3;
function useLiveAuctions({
  enabled,
  initialData,
  graphWebsocket,
  user,
  bidderLabelIntent
}) {
  const {
    collection: idempotentListings,
    attemptWrite: attemptListingWrite
  } = useIdempotentBlueprintCollection(initialData?.listings, {
    primaryKey: 'listing_id',
    utils: listingUtils,
    updateBehavior: 'merge',
    id: 'useLiveAuctions_Listing'
  });
  const {
    collection: idempotentSegments,
    attemptWrite: attemptSegmentWrite,
    attemptBatchWrite: attemptBatchSegmentWrite
  } = useIdempotentBlueprintCollection(initialData?.segments, {
    primaryKey: 'listing_id',
    utils: liveAuctionUtils,
    updateBehavior: 'merge',
    id: 'useLiveAuctions_LiveAuctionSegment'
  });
  const {
    keys: focusedListings,
    addElement: focusListing,
    removeElement: unFocusListing
  } = useBoundedArray({
    size: MAX_LIMIT_FOCUSED_LISTINGS,
    onCapacityReachedText: 'focused listings'
  });
  const data = useMemo(() => ({
    focusedListings: Array.from(focusedListings),
    isFocusedListingsAtCapacity: focusedListings.size === MAX_LIMIT_FOCUSED_LISTINGS,
    listings: idempotentListings,
    segments: idempotentSegments
  }), [focusedListings, idempotentListings, idempotentSegments]);
  const frequencyFunction = useCallback(() => computeNextLivePollingOffset(idempotentSegments.raw), [idempotentSegments]);
  const subjectIds = useMemo(() => initialData.listings.map(l => typeof l === 'string' ? l : l.listing_id), [initialData.listings]);
  const actions = useMemo(() => ({
    focusListing,
    unFocusListing
  }), [focusListing, unFocusListing]);
  const output = useMemo(() => ({
    data,
    actions
  }), [data, actions]);
  // See comment above equivalent code in the useOnlineAuctions hook
  const prevLoggedInRef = useRef(user.logged_in);
  useEffect(() => {
    const controller = new AbortController();
    if (user.logged_in !== prevLoggedInRef.current) {
      prevLoggedInRef.current = user.logged_in;
      getLatestLiveAuctionSegments(controller, user.logged_in, subjectIds).then(data => {
        logger$g.info('User Login poll live segment loaded', data);
        if (data) {
          attemptSegmentWrite({
            data,
            source: 'local_optimistic_action'
          });
        }
        return null;
      }).catch(error => {
        logger$g.error('User Login online live segment poll error', error);
      });
    }
    return () => controller.abort();
  }, [user.logged_in, subjectIds, attemptSegmentWrite]);
  // NOTE: For cases where we are not enabled we want to return the original data to keep the interface the same for
  // cases where we are doing server-side rendering. Ideally the component would not mount blueprint at all if it
  // determined it can't be used. This is where going all-in on an SPA is beneficial for things that are so reactive.
  // NOTE: we want this line to be after useIdempotentCollection creates collections with raw and mapped fields because
  // mapped is often used in apps and can cause a crash if it's undefined
  useRealtimeWatcher({
    id: 'useLiveAuctions_Listing',
    subjectIds,
    polling: {
      enabled: enabled,
      frequency: SUPPORTED_OFFSETS.NORMAL,
      executor: ({
        subjectIds,
        controller
      }) => {
        return subjectIds?.length ? getLatestLiveListings(subjectIds, Boolean(user.logged_in), controller) : Promise.resolve();
      }
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_liveListingsUpdated(
            $listingIds: [ID!]
            $hasAuthenticatedUser: Boolean!
          ) {
            listingsUpdated(filters: { listingIds: $listingIds }) {
              update_date
              listing_id
              listing_status
              listing_status_label
              auction {
                starting_bid
              }
              reserve_price @include(if: $hasAuthenticatedUser)
            }
          }
        `,
        variables: {
          listingIds: subjectIds,
          hasAuthenticatedUser: Boolean(user.logged_in)
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.listingsUpdated].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$g.info(`New live listing data loaded. source=${source}`, data);
      attemptListingWrite(payload);
    },
    onError: error => {
      logger$g.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$g.info('Have event', event, ctx);
    }
  });
  useRealtimeWatcher({
    id: 'useLiveAuctions_ListingRemoteBidCandidateSubmission',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false) && Boolean(user.logged_in),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_liveRemoteBidCandidateSubmitted(
            $listingIds: [ID!]
          ) {
            remoteBidCandidateSubmitted(
              filters: { listingIds: $listingIds, submittedByCurrentUser: true }
            ) {
              remote_bid {
                listing_id
                purchase_profile_id
                remote_bid_amount
              }
            }
          }
        `,
        variables: {
          listingIds: subjectIds
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.remoteBidCandidateSubmitted.remote_bid].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      const changedData = data.map(d => ({
        // @ts-ignore getting listing id from remote_bid
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        listing_id: d.listing_id,
        current_user_remote_bid: d
      }));
      logger$g.info(`Remote bid candidate submitted. source=${source}`, changedData);
      attemptSegmentWrite({
        source,
        // @ts-ignore We need to make this type Partial<T> if we allow overwriting
        data: changedData,
        // Since remote bid candidates aren't relate strictly to a segment we force write all changes from those events and rely on polling to correct with newest timestamps
        force: true
      });
    },
    onError: error => {
      logger$g.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$g.info('Have event', event, ctx);
    }
  });
  const liveAuctionSegmentExecutor = useCallback(({
    subjectIds,
    controller
  }) => {
    return getLatestLiveAuctionSegments(controller, user.logged_in, subjectIds);
  }, [user]);
  useRealtimeWatcher({
    id: 'useLiveAuctions_LiveAuctionSegment',
    subjectIds,
    polling: {
      enabled: enabled,
      frequency: frequencyFunction,
      executor: liveAuctionSegmentExecutor
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      // TODO: I think there is an issue here because this query is requesting fields that ought to be requested only
      // when a user is authenticated. Need more time to look to determine how this is being handled.
      executor: ({
        subjectIds
      }) => ({
        // TODO: Use gql util from design-system to embed app name in here
        query: gql$6`
          subscription blueprint_liveAuctionSegmentUpdated(
            $listingIds: [ID!]
            $hasAuthenticatedUser: Boolean!
          ) {
            liveAuctionSegmentUpdated(filters: { listingIds: $listingIds }) {
              action
              live_auction_segment {
                starting_bid_amount
                update_date
                listing_id
                auction_segment_id
                bid_count
                bidder_count
                pending_update_status
                segment_status
                current_user_bidding_status @include(if: $hasAuthenticatedUser)
                bidding_status @include(if: $hasAuthenticatedUser)
                end_status
                current_highest_bid {
                  bid_amount
                  bid_id
                  bid_type
                  belongs_to_current_user
                  listing_id
                  updated_date
                }
                configuration {
                  bid_increment_amount
                }
                bid_extend {
                  bid_extend_date_time
                }
                live_auction {
                  location {
                    lat
                    lon
                  }
                }
              }
            }
          }
        `,
        variables: {
          listingIds: subjectIds,
          hasAuthenticatedUser: Boolean(user.logged_in)
        }
      }),
      dataExtractor: data => {
        logger$g.info(
        // @ts-ignore Need to infer types from schema documents for this.
        `New live segment data loaded. action=${data.liveAuctionSegmentUpdated?.action}`);
        // @ts-ignore Need to infer types from schema documents for this.
        return [data.liveAuctionSegmentUpdated?.live_auction_segment].filter(Boolean);
      }
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$g.info(`New live segment data loaded. source=${source}`, data);
      const focusedSegments = data.filter(e => focusedListings.has(e.listing_id));
      const batchedData = getBatchedSegmentData(source, focusedSegments, idempotentSegments);
      attemptBatchSegmentWrite([{
        source,
        data
      }, ...batchedData]);
    },
    onError: error => {
      logger$g.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$g.info('Have event', event, ctx);
    }
  });
  const focusedListingsExecutor = useCallback(({
    subjectIds,
    controller
  }) => {
    return getLatestLiveFocusedListingsSegments(controller, subjectIds, bidderLabelIntent);
  }, [user, bidderLabelIntent]);
  useRealtimeWatcher({
    id: 'useLiveAuctions_FocusedListings',
    subjectIds: Array.from(focusedListings.values()),
    polling: {
      enabled: Boolean(focusedListings.size) && !_.isEmpty(idempotentSegments),
      frequency: frequencyFunction,
      executor: focusedListingsExecutor
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$g.info(`New data for live focused listings loaded. source=${source}`, JSON.stringify(data));
      attemptBatchSegmentWrite([{
        source,
        data,
        path: 'bids',
        updateBehavior: 'overwrite'
      }]);
    },
    onError: error => {
      logger$g.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$g.info('Have event', event, ctx);
    }
  });
  return output;
}
function useRemoteBiddingFunds({
  enabled,
  graphWebsocket,
  initialData
}) {
  const closeCurrentOperation = useRef(null);
  const [fund, setFund] = useState(initialData.fund);
  useEffect(() => {
    if (!enabled) return;
    graphWebsocket.subscribe({
      operationOptions: {
        query: gql$6`
            subscription blueprint_currentUserFundUpdated {
              remoteBidFundUpdated {
                remote_bid_fund {
                  fund_amount
                  fund_status
                }
              }
            }
          `
      },
      // @ts-ignore Make `.subscribe` accept a generic arg that shows up here
      onData: data => {
        setFund(data?.remoteBidFundUpdated?.remote_bid_fund);
      },
      onError: error => {
        logger$g.warn(error);
      }
    }).then(subscriber => {
      closeCurrentOperation.current = subscriber.unsubscribe;
    }).catch(err => logger$g.warn(err));
    return () => closeCurrentOperation.current?.();
  }, [enabled, graphWebsocket]);
  return fund;
}
/**
 * Lightweight hook that subscribes only to `liveAuctionUpdated` and exposes
 * hydrated live auction data (location + status) for a set of venue IDs.
 * Manages `liveAuctions` as idempotent elements via `useIdempotentBlueprintCollection`,
 * keyed by `venue_id` with `updated_date` for conflict resolution.
 *
 * Venue IDs are derived from `initialData.liveAuctions[].venue_id`.
 * Each entry must have a `venue_id`.
 */
function useLiveAuctionLocation({
  enabled,
  graphWebsocket,
  initialData
}) {
  // Capture initial value once — useIdempotentBlueprintCollection uses this
  // as a useEffect dep, so it must be referentially stable across renders.
  const initialLiveAuctions = useRef(initialData.liveAuctions ?? []).current;
  const {
    collection,
    attemptWrite
  } = useIdempotentBlueprintCollection(initialLiveAuctions, {
    primaryKey: 'venue_id',
    updateBehavior: 'merge',
    utils: {},
    id: 'useLiveAuctionLocation'
  });
  const venueIds = initialLiveAuctions.map(la => la.venue_id).filter(Boolean);
  useRealtimeWatcher({
    id: 'useLiveAuctionLocation',
    subjectIds: venueIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_liveAuctionLocationUpdated($venueIds: [ID!]) {
            liveAuctionUpdated(filters: { venueIds: $venueIds }) {
              live_auction {
                venue_id
                location {
                  lat
                  lon
                }
                status
                updated_date
              }
            }
          }
        `,
        variables: {
          venueIds: subjectIds
        }
      }),
      dataExtractor: event =>
      // @ts-ignore Need to infer types from schema documents for this.
      [event?.liveAuctionUpdated?.live_auction].filter(Boolean)
    },
    onData: ({
      data,
      source
    }) => {
      logger$g.info(`useLiveAuctionLocation data received. source=${source}`, data);
      attemptWrite({
        data,
        source
      });
    },
    onError: ({
      error
    }) => {
      logger$g.warn('useLiveAuctionLocation subscription error', error);
    }
  });
  return {
    data: collection,
    actions: null
  };
}

// TODO: How do we surface this to all clients without breaking anyone?
const partialGraphErrorFactory$2 = (message, errors) => {
  const e = new Error(message);
  e.errors = errors;
  return e;
};

var providers$b = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getCurrentServerTime: getCurrentServerTime,
    getLatestLiveAuctionSegments: getLatestLiveAuctionSegments,
    getLatestLiveFocusedListingsSegments: getLatestLiveFocusedListingsSegments,
    getLatestLiveListings: getLatestLiveListings,
    getLatestOnlineListings: getLatestOnlineListings,
    getLatestOnlineSegments: getLatestOnlineSegments,
    partialGraphErrorFactory: partialGraphErrorFactory$2,
    submitLiveRemoteBid: submitLiveRemoteBid,
    submitOnlineMatchBid: submitOnlineMatchBid,
    submitOnlineRegularBid: submitOnlineRegularBid,
    updateLiveRemoteBid: updateLiveRemoteBid,
    upsertListingOnlineMaxBid: upsertListingOnlineMaxBid
});

/* istanbul ignore file */
const logger$f = coreLogger.forModule('blueprint:time');
class CurrentTimePoller {
  constructor() {
    this.listeners = [];
    this.intervalId = null;
    this.polling = false;
    this.nextListenerId = 0;
    this.currentTime = null;
  }
  subscribe(listener) {
    const id = this.nextListenerId++;
    this.listeners.push({
      id,
      fn: listener
    });
    if (!this.polling) {
      this.polling = true;
      this.schedulePoll();
    }
    return id;
  }
  getCurrentTime() {
    return this.currentTime;
  }
  unsubscribe(listenerId) {
    this.listeners = this.listeners.filter(({
      id
    }) => id !== listenerId);
    if (this.listeners.length === 0) {
      this.terminatePoll();
    }
  }
  async updateCurrentTime() {
    try {
      const newCurrentTime = await getCurrentServerTime();
      this.currentTime = newCurrentTime || new Date();
      this.listeners.forEach(({
        fn
      }) => fn(this.currentTime));
    } catch (err) {
      logger$f.error(err);
    }
  }
  async schedulePoll() {
    await this.updateCurrentTime();
    this.intervalId = setInterval(() => {
      // @ts-ignore awaiting promises
      this.updateCurrentTime();
    }, CURRENT_SERVER_TIME_POLLING_INTERVAL);
  }
  terminatePoll() {
    // this makes ts happy
    if (typeof this.intervalId === 'number') {
      clearInterval(this.intervalId);
    }
    this.intervalId = null;
    this.polling = false;
    this.currentTime = null;
  }
}
const makeUseCurrentTimeHook = timePoller => () => {
  const unsubscribeId = useRef();
  const [currentTime, setCurrentTime] = useState(timePoller.getCurrentTime());
  useEffect(() => {
    unsubscribeId.current = timePoller.subscribe(setCurrentTime);
    return () => {
      if (typeof unsubscribeId.current === 'number') {
        timePoller.unsubscribe(unsubscribeId.current);
      }
    };
  }, []);
  return currentTime;
};
const experimental_useCurrentTime = makeUseCurrentTimeHook(new CurrentTimePoller());
// TODO: integrate `useInterval` here to make sure that if multiple abstractions are loaded on the page they are reading
// the same time instead of having n independent watchers
function useCurrentTime() {
  const time = useRef();
  time.current = new Date();
  // TODO: Put the interval logic in here....
  return time.current;
}
const useMomentCurrentTime = () => {
  const currentTime = experimental_useCurrentTime();
  const currentServerTime = useMemo(() => moment$1(currentTime), [currentTime]);
  return currentServerTime;
};

const logger$e = coreLogger.forModule('blueprint:bidding');
function useOnlineAuctions({
  enabled,
  initialData,
  graphWebsocket,
  user,
  targetAudience = TargetAudience.BUYER
}) {
  const {
    collection: idempotentListings,
    attemptWrite: attemptListingWrite
  } = useIdempotentBlueprintCollection(initialData?.listings, {
    primaryKey: 'listing_id',
    utils: listingUtils,
    updateBehavior: 'merge',
    // @ts-ignore idempotencyKey<Listing> seems to be an incorrect type definition
    idempotencyKey: 'updated_date',
    id: 'useOnlineAuctions_Listing'
  });
  const {
    collection: idempotentSegments,
    attemptWrite: attemptSegmentWrite
  } = useIdempotentBlueprintCollection(initialData?.segments, {
    primaryKey: 'listing_id',
    utils: onlineAuctionUtils,
    updateBehavior: 'merge',
    id: 'useOnlineAuctions_OnlineAuctionSegment'
  });
  const {
    keys: focusedListings,
    addElement: focusListing,
    removeElement: unFocusListing
  } = useBoundedArray({
    onCapacityReachedText: 'focused listings'
  });
  const data = useMemo(() => ({
    focusedListings: Array.from(focusedListings),
    listings: idempotentListings,
    segments: idempotentSegments
  }), [focusedListings, idempotentListings, idempotentSegments]);
  const isSellerAudience = targetAudience === TargetAudience.SELLER;
  const currentTime = useCurrentTime();
  const subjectIds = useMemo(() => initialData.listings.map(l => typeof l === 'string' ? l : l.listing_id), [initialData.listings]);
  /**
   * This method is exposed to allow one-off polls based on infrequent changes to the state triggered by the client where we want to get updated data in the UI
   * immediately without waiting for the next poll AND without needing to rely on a specific message be triggered to get our update. An example would be if the user in the lead of a
   * sealed bid auction accepts the seller counter offer, it does not change bid rank, so no bid rank message. It does change the segment status, but we don't want to fetch bid rank
   * fields with every segment updated message emission, so this function is built to fill that gap where when we know the user did something like accepting a seller counter offer
   * which will end the segment, we can call this to get the latest segment data and render it in the UI without leaving our fate up to the next poll.
   *
   * Note: maybe it would be too powerful a tool in this other way, but a possible extension to this would be for us to expose a refresh function that could accept a graph query
   * and variables to allow the client to fetch any data they want, food for thought.
   *
   * Note 2: Let's not over use this please.
   * @returns {Promise<void>}
   * @memberof useOnlineAuctions
   */
  const refreshFocusedListingsSegments = useCallback(async (listingIds = subjectIds, source = 'local_optimistic_action', includeBidsRanking = true) => {
    try {
      const data = await getLatestOnlineFocusedListingsSegments(new AbortController(), user.logged_in, listingIds, isSellerAudience, includeBidsRanking);
      logger$e.info(`New segment bid rank data loaded through manual refresh: `, data);
      attemptSegmentWrite({
        data,
        source,
        force: true
      });
    } catch (err) {
      console.error(err);
    }
  }, [user.logged_in, subjectIds, isSellerAudience, attemptSegmentWrite]);
  const actions = useMemo(() => ({
    focusListing,
    unFocusListing,
    refreshFocusedListingsSegments
  }), [focusListing, unFocusListing, refreshFocusedListingsSegments]);
  const output = useMemo(() => ({
    data,
    actions
  }), [data, actions]);
  /**
   * This is a hack to get the user logged in status to trigger a poll for the latest online segments. This is because when a user goes from logged out to logged in, it doesn't necessarily change the segment
   * data which means that blueprint doesn't know to check for the latest data, and even if it does, the idempotency key (update_date) hasn't changed so it may not emit the data (with all the user auth required fields) even if it does get it.
   * This forces one poll AND attemptsSegmentWrite as local_optimistic_action which bypasses the update_date check.
   * The alternative to having this in is remounting the client side components based off the changed login state so although this solution isn't perfect, it's better than re-rendering most of PDP to completely re-initialize the blueprint hook.
   *
   * If we need to do a more multi-dimensional approach for this situation (i.e. changes to the user OR the featureFlags of the client can result in different data being returned), then this approach won't work and we'll need to revisit.
   */
  const prevLoggedInRef = useRef(user.logged_in);
  const updateAttemptedRef = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    if (user.logged_in !== prevLoggedInRef.current && !updateAttemptedRef.current) {
      const getLatestSegmentsPromise = getLatestOnlineSegments(controller, user.logged_in, subjectIds, isSellerAudience);
      const getLatestListingsPromise = getLatestOnlineListings(subjectIds, controller);
      updateAttemptedRef.current = true;
      Promise.all([getLatestSegmentsPromise, getLatestListingsPromise]).then(([segments, listings]) => {
        attemptSegmentWrite({
          data: segments,
          source: 'local_optimistic_action'
        });
        logger$e.info('User Login poll online segment loaded', segments);
        attemptListingWrite({
          data: listings,
          source: 'local_optimistic_action'
        });
        logger$e.info('User Login poll online listing loaded', listings);
        prevLoggedInRef.current = user.logged_in;
        updateAttemptedRef.current = false;
        return null;
      }).catch(error => {
        logger$e.error('User Login online segment/listing poll error', error);
      }).finally(() => {
        controller.abort();
      });
    }
    // return () => controller.abort()
  }, [user.logged_in, subjectIds, attemptSegmentWrite, attemptListingWrite, isSellerAudience]);
  // NOTE: For cases where we are not enabled we want to return the original data to keep the interface the same for
  // cases where we are doing server-side rendering. Ideally the component would not mount blueprint at all if it
  // determined it can't be used. This is where going all-in on an SPA is beneficial for things that are so reactive.
  // NOTE: we want this line to be after useIdempotentCollectin creates collections with raw and mapped fields because mapped
  // is often used in apps and can cause a crash if it's undefined
  useRealtimeWatcher({
    id: 'useOnlineAuctions_Listing',
    subjectIds,
    polling: {
      enabled: enabled,
      frequency: SUPPORTED_OFFSETS.NORMAL,
      executor: ({
        subjectIds,
        controller
      }) => {
        return subjectIds?.length ? getLatestOnlineListings(subjectIds, controller) : Promise.resolve();
      }
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_onlineListingsUpdated($listingIds: [ID!]) {
            listingsUpdated(filters: { listingIds: $listingIds }) {
              __typename
              updated_date
              update_date
              listing_id
              listing_status
              listing_status_label
              reserve_price
            }
          }
        `,
        variables: {
          listingIds: subjectIds
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.listingsUpdated].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$e.info(`New online listing data loaded. source=${source}`, data);
      // We previously forces this, which was wasteful. If we care about reserves changes during the auction then we
      // ought to attach it to the segment since it is fresher
      attemptListingWrite({
        data,
        source
      });
    },
    onError: error => {
      logger$e.error('Realtime watcher error', error);
    }
  });
  const focusedListingsExecutor = useCallback(({
    subjectIds,
    controller
  }) => {
    return getLatestOnlineFocusedListingsSegments(controller, user.logged_in, subjectIds, isSellerAudience);
  }, [isSellerAudience, user.logged_in]);
  useRealtimeWatcher({
    id: 'useOnlineAuctions_FocusedListingsBidSubmitted',
    subjectIds: Array.from(focusedListings.values()),
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_onlineAuctionSegmentBidSubmitted(
            $listingIds: [ID!] #            $hasAuthenticatedUser: Boolean!
            $submittedByCurrentUser: Boolean!
          ) {
            onlineAuctionSegmentBidSubmitted(
              filters: {
                listingIds: $listingIds
                submittedByCurrentUser: $submittedByCurrentUser
              }
            ) {
              bid {
                updated_date
                listing_id
                bid_id
                rank
                type
                bid_amount
                bidder_party_id
                version
                created_date
                negotiation_sequence {
                  counters {
                    listing_id
                    bid_amount
                    bid_id
                    bidder_party_id
                    type
                    version

                    updated_date
                    created_date
                    ...OnlineAuctionSegmentBidTermsFragment
                  }
                  latest_counter_bid {
                    listing_id
                    bid_amount
                    bid_id
                    bidder_party_id
                    type
                    version
                    updated_date
                    ...OnlineAuctionSegmentBidTermsFragment
                  }
                }
                ...OnlineAuctionSegmentBidTermsFragment
              }
            }
          }
          ${ONLINE_AUCTION_SEGMENT_BID_TERMS}
        `,
        variables: {
          listingIds: subjectIds,
          // hasAuthenticatedUser: user.logged_in,
          submittedByCurrentUser: targetAudience === TargetAudience.BUYER
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.onlineAuctionSegmentBidSubmitted.bid].filter(Boolean).map(data => (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      {
        listing_id: data.bid?.listing_id,
        ...data
      }))
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$e.info(`New data for online focused listings bids submission loaded. source=${source}`, JSON.stringify(data));
      // @ts-ignore next TODO Sriram please address
      const bid = data[0];
      if (bid) {
        updateIfExistingBidInRanking(attemptSegmentWrite, source, bid, idempotentSegments.mapped.get(bid.listing_id)?.bids_ranking);
      }
    }
  });
  useRealtimeWatcher({
    id: 'useOnlineAuctions_FocusedListings',
    subjectIds: Array.from(focusedListings.values()),
    polling: {
      enabled: Boolean(focusedListings.size) && !_.isEmpty(idempotentSegments),
      frequency: SUPPORTED_OFFSETS.CRITICAL,
      executor: focusedListingsExecutor
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_onlineSegmentsRankingUpdated(
            $listingIds: [ID!]
            $hasAuthenticatedUser: Boolean!
          ) {
            onlineAuctionBidRankingUpdated(
              filters: { listingIds: $listingIds }
            ) {
              bids_ranking {
                updated_date
                listing_id
                ranking {
                  belongs_to_current_user @include(if: $hasAuthenticatedUser)
                  is_viewable_by_current_user
                    @include(if: $hasAuthenticatedUser)
                  overtake_delta @include(if: $hasAuthenticatedUser)
                  net_offer_equal_delta @include(if: $hasAuthenticatedUser)
                  bid_id
                  party_id
                  rank
                  seller_assigned_score
                  system_calculated_score
                  max_tied_rank
                  bid {
                    listing_id
                    bid_amount
                    bid_id
                    bidder_party_id
                    type
                    version
                    updated_date
                    created_date
                    negotiation_sequence {
                      counters {
                        listing_id
                        bid_amount
                        bid_id
                        bidder_party_id
                        type
                        version

                        updated_date
                        created_date
                        ...OnlineAuctionSegmentBidTermsFragment
                      }
                      latest_counter_bid {
                        listing_id
                        bid_amount
                        bid_id
                        bidder_party_id
                        type
                        version
                        updated_date
                        ...OnlineAuctionSegmentBidTermsFragment
                      }
                    }
                    ...OnlineAuctionSegmentBidTermsFragment
                  }
                  party {
                    person {
                      first_name
                      last_name
                    }
                  }
                }
              }
            }
          }
          ${ONLINE_AUCTION_SEGMENT_BID_TERMS}
        `,
        variables: {
          listingIds: subjectIds,
          hasAuthenticatedUser: Boolean(user.logged_in)
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.onlineAuctionBidRankingUpdated].filter(Boolean).map(data => (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      {
        listing_id: data.bids_ranking?.listing_id,
        ...data
      }))
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$e.info(`New data for online focused listings loaded. source=${source}`, JSON.stringify(data));
      attemptSegmentWrite({
        data,
        source,
        force: true
      });
    }
  });
  useRealtimeWatcher({
    id: 'useOnlineAuctions_ListingOnlineMaxBidSubmission',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false) && Boolean(user.logged_in),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_listingOnlineMaxBidSubmission(
            $listingIds: [ID!]
            $submittedByCurrentUser: Boolean
            $hasAuthenticatedUser: Boolean!
          ) {
            listingOnlineMaxbidSubmitted(
              filters: {
                listingIds: $listingIds
                submittedByCurrentUser: $submittedByCurrentUser
              }
            ) {
              max_bid @include(if: $hasAuthenticatedUser) {
                listing_id
                max_bid_amount
                record_status
              }
            }
          }
        `,
        variables: {
          listingIds: subjectIds,
          submittedByCurrentUser: true,
          hasAuthenticatedUser: Boolean(user.logged_in)
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.listingOnlineMaxbidSubmitted?.max_bid].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      const listingIds = data.map(d => d.listing_id);
      logger$e.info(`Max bid submitted. Refreshing listingIds=${listingIds} source=${source}`);
      // GraphQL now calculates next_bid_amount and current_user_next_max_bid_amount from the segment.
      // Refreshing the segment ensures these fields are up-to-date.
      refreshFocusedListingsSegments(listingIds, source, false);
    }
  });
  const onlineAuctionSegmentExecutor = useCallback(({
    subjectIds,
    controller
  }) => {
    return getLatestOnlineSegments(controller, user.logged_in, subjectIds, isSellerAudience);
  }, [user, isSellerAudience]);
  useRealtimeWatcher({
    id: 'useOnlineAuctions_OnlineAuctionSegment',
    subjectIds,
    polling: {
      enabled: enabled,
      frequency: useCallback(() => {
        return computeNextOnlinePollingOffset(currentTime, idempotentSegments.raw.filter(Boolean));
      }, [currentTime, idempotentSegments]),
      executor: onlineAuctionSegmentExecutor
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false) && user.logged_in,
      executor: ({
        subjectIds
      }) => ({
        // TODO: Use gql util from design-system to embed app name in here
        query: gql$6`
          subscription blueprint_onlineAuctionSegmentUpdated(
            $listingIds: [ID!]
            $isSellerAudience: Boolean!
            $hasAuthenticatedUser: Boolean!
          ) {
            onlineAuctionSegmentUpdated(filters: { listingIds: $listingIds }) {
              action
              changes_summary {
                is_there_a_higher_max_bid_configured
              }
              online_segment {
                update_date
                listing_id
                online_segment_id
                start_date
                initial_end_date
                current_increment_amount
                segment_status
                reserve_status
                current_time
                starting_bid_amount
                match_bidding_period_end_date
                is_match_bidding_ended
                match_bid_count
                bid_count
                buyer_bid_count
                current_highest_bid {
                  bid_id
                  bid_amount
                  belongs_to_current_user
                  type
                  updated_date
                }
                current_bid_extension {
                  extension_type
                  reason
                }
                current_user_highest_bid {
                  record_status
                  listing_id
                  bid_amount
                  type
                }
                next_bid_amount
                current_user_max_bid {
                  current_user_next_max_bid_amount
                  max_bid_amount
                  record_status
                  updated_date
                }
                current_user_bidding_status
                is_current_user_registered_for_online_auction
                subject_to_status
                configuration {
                  bids_away_status
                  is_one_away_enabled
                  is_one_away_displayed
                  is_initial_end_date_extended
                  is_match_bidding_enabled
                  is_max_bidding_enabled
                  should_bid_again
                  bid_again_count
                  review_date
                }
                result {
                  winning_bid_amount
                }
                ...SellerExclusiveOnlineAuctionSegmentFragment
                  @include(if: $isSellerAudience)
              }
            }
          }
          ${SELLER_EXCLUSIVE_ONLINE_AUCTION_SEGMENT_FIELDS}
        `,
        variables: {
          listingIds: subjectIds,
          isSellerAudience,
          hasAuthenticatedUser: Boolean(user.logged_in)
        }
      }),
      dataExtractor: data => {
        // @ts-ignore Need to infer types from schema documents for this.
        const submission = data.onlineAuctionSegmentUpdated;
        if (!submission?.online_segment) return [];
        // Note: changes_summary is a sibling of online_segment in the GraphQL response,
        // not nested inside it. I merge it into the segment object here.
        return [{
          ...submission.online_segment,
          changes_summary: {
            is_there_a_higher_max_bid_configured: submission.changes_summary?.is_there_a_higher_max_bid_configured ?? false
          }
        }];
      }
    },
    onData: ({
      data,
      source
    }) => {
      logger$e.info(`New segment data loaded. source=${source}`, data);
      // TODO: We want to add the timestamp checking here to have more granular control on when updates should trigger
      // in the UI. If this is stale we don't want to surface to the caller.
      attemptSegmentWrite({
        source,
        data
      });
    }
  });
  useRealtimeWatcher({
    id: 'useOnlineAuctions_onlineAuctionListing',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false) && user.logged_in,
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription blueprint_onlineAuctionBidderRegistrationUpdated(
            $listingIds: [ID!]
          ) {
            onlineAuctionBidderRegistrationUpdated(
              filters: { listingIds: $listingIds }
            ) {
              is_current_user_registered_for_online_auction
              online_bidder_registration {
                registration_status
              }
            }
          }
        `,
        variables: {
          listingIds: subjectIds
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data.listingsUpdated].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$e.info(`New online listing data loaded. source=${source}`, data);
      // This should use the attempt batch write at the specific key location, once we load idempotency keys
      attemptListingWrite({
        data,
        source,
        force: true
      });
    }
  });
  return output;
}

var hooks$d = /*#__PURE__*/Object.freeze({
    __proto__: null,
    experimental_useCurrentTime: experimental_useCurrentTime,
    useLiveAuctionLocation: useLiveAuctionLocation,
    useLiveAuctions: useLiveAuctions,
    useMomentCurrentTime: useMomentCurrentTime,
    useOnlineAuctions: useOnlineAuctions,
    useRemoteBiddingFunds: useRemoteBiddingFunds
});

/**
 * Selling Methods — `bids` shape inconsistency
 *
 * LiveAuctionSegment.bids and OnlineAuctionSegment.bids have different
 * return shapes in the GraphQL schema:
 *
 *   Live   → flat array: bids: [{ bid_id, bid_amount, bid_type, ... }]
 *   Online → paginated wrapper: bids { content: [{ bid_id, bid_amount, type, ... }] }
 *
 * Also note the bid type field name differs: bid_type (live) vs type (online).
 *
 * Any consumer that renders bids from both segment types must normalize:
 *
 *   const bidsList = isLive ? segment.bids : segment.bids?.content
 */
const enums$3 = types$8;

var index$k = /*#__PURE__*/Object.freeze({
    __proto__: null,
    enums: enums$3,
    hooks: hooks$d,
    providers: providers$b,
    types: types$8,
    utils: utils$a
});

const nonExistentFilterOption = {
  value: '$$UNSPECIFIED$$',
  label: '(Unspecified)'
};

var constants$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    nonExistentFilterOption: nonExistentFilterOption
});

// ============================================================================
// Constants
// ============================================================================
const DEFAULT_PAGE_SIZE$1 = 25;
const PAGE_PARAMS_VERSION = 2;
const VIEW_STATE_PARAM = 'view_state';
// ============================================================================
// Serialization Helpers
// ============================================================================
/**
 * Recursively removes empty values from an object.
 * Removes: null, undefined, empty strings, empty arrays, and objects with no remaining keys.
 */
function removeEmptyValues(obj) {
  if (obj === null || obj === undefined || obj === '') {
    return undefined;
  }
  // Preserve numbers and booleans
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }
  if (Array.isArray(obj)) {
    if (obj.length === 0) return undefined;
    const filtered = obj.map(removeEmptyValues).filter(v => v !== undefined);
    return filtered.length > 0 ? filtered : undefined;
  }
  if (obj instanceof Date) {
    return obj.toISOString();
  }
  if (typeof obj === 'object') {
    const filtered = {};
    let hasKeys = false;
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = removeEmptyValues(value);
      if (cleanedValue !== undefined) {
        filtered[key] = cleanedValue;
        hasKeys = true;
      }
    });
    return hasKeys ? filtered : undefined;
  }
  return _.isEmpty(obj) ? undefined : obj;
}
// ============================================================================
// Serialization Functions
// ============================================================================
/**
 * Serializes filter, sort, and pagination state into a base64-encoded string.
 * Embeds version number for future migration support.
 * Returns empty string if all params are empty.
 *
 * @example
 * ```ts
 * const encoded = serializePageParams({
 *   filters: { make: 'honda', year: { min: 2020, max: null } },
 *   sort: { field: 'price', order: 'desc' },
 *   pagination: { page: 1, pageSize: 25 }
 * })
 * // Returns base64 string like "eyJ2IjoyLCJmIjp7Im1ha2UiOiJob25kYSJ9fQ=="
 * ```
 */
function serializePageParams(params) {
  const pageParams = {
    v: PAGE_PARAMS_VERSION
  };
  // Only include non-empty values
  const cleanedFilters = removeEmptyValues(params.filters);
  if (cleanedFilters !== undefined) {
    pageParams.f = cleanedFilters;
  }
  const cleanedSort = removeEmptyValues(params.sort);
  if (cleanedSort !== undefined) {
    pageParams.s = cleanedSort;
  }
  const cleanedPagination = removeEmptyValues(params.pagination);
  if (cleanedPagination !== undefined) {
    pageParams.p = cleanedPagination;
  }
  if (params.searchText) {
    pageParams.t = params.searchText;
  }
  // If only version remains (no actual data), return empty string
  if (!pageParams.f && !pageParams.s && !pageParams.p && !pageParams.t) {
    return '';
  }
  const jsonString = JSON.stringify(pageParams);
  return btoa(jsonString);
}
/**
 * Deserializes a base64-encoded page params string back into filter, sort, and pagination state.
 * Handles version migration for older serialized formats.
 * Returns empty object for empty string input.
 * Throws on invalid input.
 *
 */
function deserializePageParams(encoded) {
  // Handle empty string
  if (!encoded || encoded === '') {
    return {};
  }
  let parsed;
  try {
    const jsonString = atob(encoded);
    parsed = JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Failed to deserialize page params: ${error instanceof Error ? error.message : 'Invalid encoding'}`);
  }
  // Version validation
  if (typeof parsed.v !== 'number') {
    throw new Error('Invalid page params: missing or invalid version');
  }
  // Version migration handling
  // Currently v2 is the only version, but this structure allows for future migrations
  if (parsed.v === 2) {
    return {
      filters: parsed.f,
      sort: parsed.s,
      pagination: parsed.p,
      searchText: parsed.t
    };
  }
  // Handle unknown/future versions with best-effort parsing
  // This allows forward compatibility - newer versions can still be partially read
  if (parsed.v > PAGE_PARAMS_VERSION) {
    console.warn(`Page params version ${parsed.v} is newer than supported version ${PAGE_PARAMS_VERSION}. ` + 'Some data may not be parsed correctly.');
  }
  // For older versions (v1 and below) or unknown versions, attempt best-effort parse
  return {
    filters: parsed.f,
    sort: parsed.s,
    pagination: parsed.p,
    searchText: parsed.t
  };
}
// ============================================================================
// URL Utilities
// ============================================================================
/**
 * Parse URL params on initial load (SSR-safe)
 *
 * @param paramKey URL param to read the encoded state from. Pass a different
 *   key to use something other than the default `view_state`.
 */
const getInitialPageParams = (defaults = {}, paramKey = VIEW_STATE_PARAM) => {
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const viewState = searchParams.get(paramKey);
  const urlParams = viewState ? deserializePageParams(viewState) : null;
  const pageSize = defaults.pageSize ?? DEFAULT_PAGE_SIZE$1;
  return {
    filters: urlParams?.filters ?? {},
    sort: urlParams?.sort ?? defaults.sort ?? {
      field: 'created_date',
      order: graph.enums.SortConditionOrder.Desc
    },
    pagination: urlParams?.pagination ?? {
      currentPage: 0,
      pageSize
    },
    searchText: urlParams?.searchText
  };
};
/**
 * Get a specific URL param (SSR-safe)
 */
const getUrlParam = param => {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(param);
};
/**
 * Update a specific URL param.
 * Pass `replace: true` to use replaceState instead of pushState,
 * avoiding a new browser history entry (useful for filter/selection changes).
 */
const setUrlParam = (param, value, {
  replace = false
} = {}) => {
  if (typeof window === 'undefined') return;
  const searchParams = new URLSearchParams(window.location.search);
  if (value) {
    searchParams.set(param, value);
  } else {
    searchParams.delete(param);
  }
  const newUrl = searchParams.toString() ? `?${searchParams.toString()}` : window.location.pathname;
  window.history[replace ? 'replaceState' : 'pushState'](null, '', newUrl);
};

const UI_DATE_FORMAT = 'MM/DD/YYYY';
const MAX_CHARACTERS_FOR_USING_GROUP_LABEL = 21;
const getFilterGroupDefinitionByName = (definitions, name) => {
  return definitions.find(def => def.name === name) || null;
};
const getFilterDefinition = filter => {
  // this check probably isn't really necessary (TS will catch issues), but just in case, let's be sure.
  if (['singleSelect', 'multiSelect', 'inlineSelect', 'range', 'boolean'
  // @ts-ignore next
  ].includes(filter.controlType)) {
    return filter;
  } else {
    throw new Error('Invalid control type');
  }
};
const getFilterDefinitionByName = (filterGroup, filterName) => {
  for (const item of filterGroup.items) {
    if (item.itemType === 'section') {
      for (const filter of item.filters || []) {
        if (filter.name === filterName) {
          return getFilterDefinition(filter);
        }
      }
    } else if (item.itemType === 'filter' && item.name === filterName) {
      // This is a filter and its name matches the filterName
      return getFilterDefinition(item);
    }
  }
  // If no filter is found, return null
  return null;
};
const getSingleFilterResetValue = filter => match(filter.controlType).with('singleSelect', () => {
  return match(filter.controlValueType).with('string', () => '').otherwise(() => null);
}).with('multiSelect', () => []).with('inlineSelect', () => {
  const inlineFilter = filter;
  if (inlineFilter.mode === 'single') {
    return '';
  } else if (inlineFilter.mode === 'multi' || inlineFilter.mode === 'range') {
    return [];
  }
  // Fallback for unexpected mode values
  return '';
}).with('range', () => {
  return {
    min: null,
    max: null
  };
}).with('boolean', () => false).otherwise(() => null);
/**
 * Initializes a filter group view model from a group definition
 * @param definition group definition
 * @returns
 */
const initializeFilterGroupViewModel = definition => {
  const groupVM = {};
  definition.items.forEach(item => {
    if (item.itemType === 'filter') {
      const filter = item;
      groupVM[filter.name] = getSingleFilterResetValue(filter);
    } else if (item.itemType === 'section') {
      const section = item;
      section.filters.forEach(filter => {
        groupVM[filter.name] = getSingleFilterResetValue(filter);
      });
    }
  });
  return groupVM;
};
const resetIndividualFilter = (definition, state, filter) => {
  const matchingModel = Object.entries(state).find(([name, data]) => name !== definition.name && filter.name in data)?.[0];
  if (!matchingModel) return state;
  return {
    ...state,
    [matchingModel]: {
      ...state[matchingModel],
      [filter.name]: getSingleFilterResetValue(filter)
    }
  };
};
const resetMatchingFiltersViewModel = (definition, prevState) => {
  let newState = {
    ...prevState
  };
  definition.items.forEach(item => {
    if (item.itemType === 'filter') {
      const filter = item;
      newState = resetIndividualFilter(definition, newState, filter);
    } else if (item.itemType === 'section') {
      const section = item;
      section.filters.forEach(filter => {
        newState = resetIndividualFilter(definition, newState, filter);
      });
    }
  });
  return newState;
};
const isClean = v => {
  if (_.isDate(v)) {
    return false;
  } else if (typeof v === 'object' && !Array.isArray(v) && v !== null) {
    return Object.keys(v).every(key => isClean(v[key]));
  } else if (_.isString(v) || _.isArray(v)) {
    return _.isEmpty(v);
  }
  return _.isNil(v) || v === false;
};
/**
 * Returns true if all filters are reset
 * @param data Filter group view model
 * @returns boolean indicating if all filters are reset
 */
const isGroupFilterReset = data => {
  const keys = Object.keys(data);
  return keys.every(key => isClean(data[key]));
};
const areAllFiltersReset = dataCollection => Object.values(dataCollection).every(isGroupFilterReset);
const convertToViewModelValue = (value, definition) => {
  if (definition.converterOverrides && definition.converterOverrides.toViewModelValue) {
    return definition.converterOverrides.toViewModelValue(value, definition);
  }
  // Convert date strings to Date objects for Date range filters
  if (definition.controlType === 'range' && definition.controlValueType === 'Date' && value && typeof value === 'object' && !Array.isArray(value)) {
    const rangeValue = value;
    return {
      min: rangeValue.min && !(rangeValue.min instanceof Date) ? new Date(rangeValue.min) : rangeValue.min || null,
      max: rangeValue.max && !(rangeValue.max instanceof Date) ? new Date(rangeValue.max) : rangeValue.max || null
    };
  }
  return value || null;
};
const convertToAppModelValue = (value, definition) => {
  return definition.converterOverrides && definition.converterOverrides.toAppModelValue ? definition.converterOverrides.toAppModelValue(value, definition) : value;
};
/**
 * Converts filter state to view models.
 * Assumption: filterState is a flat object with keys that match filter names.
 */
const defaultFilterStateToViewModels = (filterState, filterDefinitions) => {
  const vm = {};
  filterDefinitions.forEach(groupDefinition => {
    const groupName = groupDefinition.name;
    vm[groupName] = groupDefinition.items.reduce((acc, item) => {
      if (item.itemType === 'filter') {
        return {
          ...acc,
          [item.name]: convertToViewModelValue(filterState[item.name], item)
        };
      } else if (item.itemType === 'section') {
        const section = item;
        return {
          ...acc,
          ...section.filters.reduce((acc, filter) => ({
            ...acc,
            [filter.name]: convertToViewModelValue(filterState[filter.name], filter)
          }), {})
        };
      }
      return acc;
    }, {});
  });
  return vm;
};
/**
 * Converts view models to filter state.
 * Assumption: filterViewModels is a nested object with keys that match filter names.
 * @param filterViewModels
 * @param prevFilterState
 * @returns
 */
const defaultViewModelsToFilterState = (filterViewModels, prevFilterState, filterDefinitions) => {
  const newState = {
    ...prevFilterState
  };
  Object.keys(filterViewModels).forEach(groupName => {
    const groupDefinition = getFilterGroupDefinitionByName(filterDefinitions, groupName);
    const groupViewModel = filterViewModels[groupName];
    if (groupDefinition !== null) {
      Object.keys(groupViewModel).forEach(filterName => {
        newState[filterName] = convertToAppModelValue(groupViewModel[filterName],
        // @ts-ignore - TS doesn't know that groupDefinition is not null
        getFilterDefinitionByName(groupDefinition, filterName));
      });
    }
  });
  return newState;
};
const isSubset = (a, b) => {
  return _.difference(b, a).length === 0;
};
/**
 * Returns true if the two arrays are equal if items are same regardless of order
 */
const arraysAreEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  const setA = new Set(a);
  const setB = new Set(b);
  for (const item of Array.from(setA)) {
    if (!setB.has(item)) {
      return false;
    }
  }
  return true;
};
/* istanbul ignore next */
const formatDate$1 = date => {
  if (!date) return '';
  return moment$1(date).format(UI_DATE_FORMAT);
};
const formatNumber = (str = null, maximumFractionDigits = 0) => str && isNumberInput(str) ? Number(str).toLocaleString('en-US', {
  maximumFractionDigits
}) : '0';
/**
 * Handles case where user is typing a negative number but hasn't completed it yet (i.e. just a "-")
 */
const formatNumberWithNegatives = (str = null, maximumFractionDigits = 0, useGrouping = false) => {
  if (str === '-') return '-'; // special case for negative sign
  if (!isNumberInput(str)) return '';
  return Number(str).toLocaleString('en-US', {
    maximumFractionDigits,
    useGrouping
  });
};
const formatRangeValue = (value, type) => match(type).with('number', () => formatNumber(value)).with('Date', () => formatDate$1(value)).otherwise(() => value);
const filterOptions = value => option => value !== null && Array.isArray(value) && (
// @ts-ignore next
Array.isArray(option.value) && isSubset(value, option.value) ||
// @ts-ignore next
value.includes(option.value));
/**
 * Return array of 0 to many "parts" where a part is a string label representing a in-use filter value.
 * Some filters can have more than one parts (e.g. multiSelect) so we always return an array.
 */
const getPartsFromFilterAndValue = (filterDefinition, value) => {
  const filterLabel = filterDefinition.shortLabel || filterDefinition.label; // TODO: deal with fact this can be a react node
  // @ts-ignore - TS doesn't know that controlType is one of the known values
  return match(filterDefinition.controlType)
  // for single select - find the option and show it's text
  .with('singleSelect', () => {
    const definition = filterDefinition;
    const option = definition.options.find(option => option.value === value);
    if (value === nonExistentFilterOption.value) {
      return [definition.nonExistentSearchLabel || nonExistentFilterOption.label];
    }
    return option ? [option.label] : [];
  })
  // for multi select, find 1 to many options selected
  .with('multiSelect', () => {
    const options = filterDefinition.options.filter(filterOptions(value));
    return options.map(option => getTextFromRichText(option.label));
  })
  // for inline select, find 1 to many options selected
  .with('inlineSelect', () => {
    if (filterDefinition.mode === 'single') {
      // for single mode, just return the value as text
      return [`${filterLabel}: ${getTextFromRichText(value) || ''}`];
    } else {
      const options = filterDefinition.options.filter(filterOptions(value));
      const textOptions = options.map(option => getTextFromRichText(option.label));
      return [`${filterLabel}: ${textOptions.join(', ')}`];
    }
  })
  // for range, express the range
  .with('range', () => {
    if (!value) return '';
    const v = value;
    const min = v.min ? formatRangeValue(v.min, filterDefinition.controlValueType) : '';
    const max = v.max ? formatRangeValue(v.max, filterDefinition.controlValueType) : '';
    if (min && max) return [`${filterLabel} [${min.toString()}⏵${max.toString()}]`];else if (min) return [`${filterLabel} [${min.toString()}⏵]`];else if (max) return [`${filterLabel} [⏵${max.toString()}]`];
    return [];
  })
  // for boolean, label of check box
  .with('boolean', () => {
    return value ? [filterLabel] : [];
  }).otherwise(() => {
    return [value];
  });
};
const getLabelFromParts = (parts, groupLabel, displayParts = true) => {
  if (!displayParts) return parts.length ? `${groupLabel} · ${parts.length}` : groupLabel;
  const result = match(parts.length).with(0, () => groupLabel).with(1, () => {
    const temp = `${groupLabel}: ${parts[0]}`;
    return temp.length > MAX_CHARACTERS_FOR_USING_GROUP_LABEL ? parts[0] : temp;
  }).with(2, () => `${parts[0]}, ${parts[1]}`).otherwise(count => `${groupLabel} · ${count}`);
  return result;
};
const getPartsFromGroup = (groupModel, groupDefinition) => {
  const keys = Object.keys(groupModel);
  const names = keys.filter(key => !isClean(groupModel[key]));
  const count = names.length; // used filter count
  let parts = [];
  for (let i = 0; i < count; i++) {
    const filterDefinition = getFilterDefinitionByName(groupDefinition, names[i]);
    if (filterDefinition) parts = [...parts, ...getPartsFromFilterAndValue(filterDefinition, groupModel[names[i]])];
  }
  return parts;
};
const getLabelFromGroup = (groupModel, groupDefinition) => {
  return getLabelFromParts(getPartsFromGroup(groupModel, groupDefinition), groupDefinition.label, groupDefinition.mode !== 'completeFilter');
};
/* istanbul ignore next */
const getLabelFromAll = (viewModels, definitions, defaultLabel) => {
  const parts = definitions.map(definition => {
    const groupModel = viewModels[definition.name];
    return getPartsFromGroup(groupModel, definition);
  }).flat();
  return getLabelFromParts(parts, defaultLabel);
};
const getTextFromRichText = richTextNode => {
  if (_.isNil(richTextNode)) return '';
  const node = richTextNode;
  return match(typeof node).with('string', () => node).with('number', () => node.toString()).with('boolean', () => '').with('object', () => {
    if (node instanceof Array) return node.map(getTextFromRichText).join('');
    if (node.props) {
      const nodeWithProps = node;
      if (nodeWithProps.props?.children) {
        const children = nodeWithProps.props.children;
        return getTextFromRichText(children);
      }
    }
    return ''; // fall through otherwise
  }).otherwise(() => {
    console.warn('Unresolved `richTextNode` of type:', typeof node, node);
    return '';
  });
};
const jsonReplacer = (_key, value) => {
  // For properties that are React nodes, extract text content
  if (/*#__PURE__*/isValidElement(value)) {
    return getTextFromRichText(value);
  }
  return value;
};
/* istanbul ignore next */
const deriveOptionsList = (enums, override = {}) => Object.values(enums).map(entry => ({
  value: entry,
  label: override[entry] || formatEnum$1(entry)
}));
/**
 * Updates the label cache for async filters that have values but no cached labels.
 * This ensures that getPartsFromFilterAndValue can display labels for async filters.
 */
async function updateAsyncFilterLabelCache(filterDefinitions, viewModels) {
  const promises = [];
  for (const groupDef of filterDefinitions) {
    const groupModel = viewModels[groupDef.name];
    if (!groupModel) continue;
    groupDef.items.forEach(item => {
      if (item.itemType === 'filter') {
        promises.push(updateSingleFilterCache(item, groupModel));
      } else if (item.itemType === 'section') {
        const section = item;
        section.filters.forEach(filter => {
          promises.push(updateSingleFilterCache(filter, groupModel));
        });
      }
    });
  }
  await Promise.all(promises);
}
async function updateSingleFilterCache(filter, groupModel) {
  const value = groupModel[filter.name];
  // Skip if no value or not an async single select
  if (!value || filter.controlType !== 'singleSelect' || filter.lookupMode !== 'async' && filter.lookupMode !== 'asyncOnce' || !filter.getOptionByValue) {
    return;
  }
  const singleSelectFilter = filter;
  // Check if option already exists in cache
  const existingOption = singleSelectFilter.options.find(opt => opt.value === value);
  if (existingOption) {
    return; // Already cached, skip fetch
  }
  try {
    const option = await singleSelectFilter.getOptionByValue(value);
    if (option) {
      // Add to options array (mutating for efficiency)
      singleSelectFilter.options.push(option);
    }
  } catch (error) {
    console.error(`Error fetching label for value ${value}:`, error); // eslint-disable-line
  }
}

var utils$9 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    areAllFiltersReset: areAllFiltersReset,
    arraysAreEqual: arraysAreEqual,
    convertToAppModelValue: convertToAppModelValue,
    convertToViewModelValue: convertToViewModelValue,
    defaultFilterStateToViewModels: defaultFilterStateToViewModels,
    defaultViewModelsToFilterState: defaultViewModelsToFilterState,
    deriveOptionsList: deriveOptionsList,
    deserializePageParams: deserializePageParams,
    formatDate: formatDate$1,
    formatNumber: formatNumber,
    formatNumberWithNegatives: formatNumberWithNegatives,
    formatRangeValue: formatRangeValue,
    getFilterDefinitionByName: getFilterDefinitionByName,
    getFilterGroupDefinitionByName: getFilterGroupDefinitionByName,
    getInitialPageParams: getInitialPageParams,
    getLabelFromAll: getLabelFromAll,
    getLabelFromGroup: getLabelFromGroup,
    getLabelFromParts: getLabelFromParts,
    getPartsFromFilterAndValue: getPartsFromFilterAndValue,
    getPartsFromGroup: getPartsFromGroup,
    getTextFromRichText: getTextFromRichText,
    getUrlParam: getUrlParam,
    initializeFilterGroupViewModel: initializeFilterGroupViewModel,
    isGroupFilterReset: isGroupFilterReset,
    jsonReplacer: jsonReplacer,
    resetMatchingFiltersViewModel: resetMatchingFiltersViewModel,
    serializePageParams: serializePageParams,
    setUrlParam: setUrlParam,
    updateAsyncFilterLabelCache: updateAsyncFilterLabelCache
});

const getRangeSelectOptionList = ({
  currentValue,
  definition,
  minOrMax
}) => {
  const optionList = definition.optionList;
  if (!optionList?.length) return null;
  // Opt-in: emit the configured intent value instead of null for the
  // no-constraint option. Number(intent string) is NaN (falsy), so the
  // min/max window fallbacks below behave exactly like a null selection.
  const intentValueOptions = definition.intentValueOptions;
  if (minOrMax === 'min') {
    const selectedMax = Number(currentValue?.max);
    const max = selectedMax || Number(optionList.at(-1)?.value) + 1;
    return [{
      text: 'No Min',
      value: intentValueOptions?.min ?? null
    }, ...(optionList.filter(option => Number(option.value) < (max || 0)).map(option => option && {
      text: option.label,
      value: option.value
    }) || [])];
  } else {
    const selectedMin = Number(currentValue?.min);
    const min = selectedMin || Number(optionList.at(0)?.value) - 1;
    return [{
      text: 'No Max',
      value: intentValueOptions?.max ?? null
    }, ...(optionList.filter(option => Number(option.value) > (min || 0)).map(option => option && {
      text: option.label,
      value: option.value
    }) || [])];
  }
};
const BLUR_TIMEOUT = 250;
const useRangeFilter = ({
  definition,
  value,
  onChange
}) => {
  // DatePickerField requires string value e.g. '12/30/2024'
  const isDate = definition.controlValueType === 'Date';
  // keep track locally which values are selected
  const [currentValue, setCurrentValue] = useState(value || {
    min: null,
    max: null
  });
  // min/max formatted values
  const [minValueFormatted, setMinValueFormatted] = useState('');
  const [maxValueFormatted, setMaxValueFormatted] = useState('');
  // Track focus state to prevent parent sync from overwriting local changes during editing
  const isFocusedRef = useRef(false);
  const currentValueRef = useRef(currentValue);
  useEffect(() => {
    currentValueRef.current = currentValue;
    const minValueFormatted = isDate && currentValue?.min instanceof Date ? formatDate$1(currentValue.min.toISOString()) : formatNumberWithNegatives(currentValue?.min);
    setMinValueFormatted(minValueFormatted);
    const maxValueFormatted = isDate && currentValue?.max instanceof Date ? formatDate$1(currentValue.max.toISOString()) : formatNumberWithNegatives(currentValue?.max);
    setMaxValueFormatted(maxValueFormatted);
  }, [currentValue, isDate]);
  // Only sync from parent when not focused to prevent overwriting local changes during editing
  useEffect(() => {
    if (!isFocusedRef.current) {
      setCurrentValue(value);
    }
  }, [value]);
  const getValueFromEvent = e => {
    if (definition.controlValueType === 'number') {
      const value = parseFloat(e.target.value);
      return !isNumberInput(value) ? null : value;
    } else if (definition.controlValueType === 'Date') {
      const newDate = new Date(e);
      return isNaN(newDate) ? null : newDate;
    } else return e.target.value;
  };
  const validate = (rangeValue, minOrMax) => {
    let newMin = null;
    let newMax = null;
    const intentMin = definition.intentValueOptions?.min;
    const intentMax = definition.intentValueOptions?.max;
    if (isDate) {
      newMin = rangeValue?.min || null;
      newMax = rangeValue?.max || null;
    } else {
      // Preserve an explicitly selected intent value (opt-in only); anything
      // else keeps the numeric-or-null behavior.
      newMin = intentMin != null && rangeValue?.min === intentMin ? intentMin : !isNumberInput(rangeValue?.min) ? null : rangeValue?.min;
      newMax = intentMax != null && rangeValue?.max === intentMax ? intentMax : !isNumberInput(rangeValue?.max) ? null : rangeValue?.max;
    }
    // @ts-ignore next
    if (newMin !== null && newMax !== null && newMin !== intentMin && newMax !== intentMax &&
    // @ts-ignore next
    newMin > newMax) {
      if (minOrMax === 'min') newMin = null;else newMax = null;
    }
    return {
      min: newMin,
      max: newMax
    };
  };
  const formatCurrentValue = (value, minOrMax) => minOrMax === 'min' ? {
    min: value,
    max: currentValue?.max
  } : {
    min: currentValue?.min,
    max: value
  };
  const handleSelectChange = (value, minOrMax) => {
    const newValue = formatCurrentValue(value, minOrMax);
    setCurrentValue(newValue);
    onChange(definition.name, newValue);
  };
  const onChangeHandler = (e, minOrMax) => {
    // special case to allow user to type negative sign
    if (e?.target?.value === '-') {
      setCurrentValue(formatCurrentValue('-', minOrMax));
      return;
    }
    const newValue = formatCurrentValue(getValueFromEvent(e), minOrMax);
    setCurrentValue(newValue);
    // For Date types, propagate immediately since picker selection is discrete
    // (unlike typing where we wait for blur)
    if (isDate) {
      const validatedValue = validate(newValue, minOrMax);
      onChange(definition.name, validatedValue);
    }
  };
  const onFocusHandler = () => {
    isFocusedRef.current = true;
  };
  const onBlurHandler = minOrMax => {
    setTimeout(() => {
      isFocusedRef.current = false;
      const newValue = validate(currentValueRef.current, minOrMax);
      setCurrentValue(newValue);
      onChange(definition.name, newValue);
    }, BLUR_TIMEOUT);
  };
  const isSelect = definition.mode === 'select';
  const optionLists = useMemo(() => ({
    min: getRangeSelectOptionList({
      currentValue,
      definition,
      minOrMax: 'min'
    }),
    max: getRangeSelectOptionList({
      currentValue,
      definition,
      minOrMax: 'max'
    })
  }), [currentValue, definition]);
  const actions = {
    handleSelectChange,
    onBlurHandler,
    onChangeHandler,
    onFocusHandler
  };
  const data = {
    currentValue,
    isDate,
    isSelect,
    maxValueFormatted,
    minValueFormatted,
    optionLists
  };
  return {
    actions,
    data
  };
};

const useInlineSelectFilter = ({
  mode,
  onChange,
  options,
  value
}) => {
  const getModeDescription = useMemo(() => {
    switch (mode) {
      case 'single':
        return 'Single selection mode. Choose one option.';
      case 'multi':
        return 'Multiple selection mode. Click options to toggle selection.';
      case 'range':
        return 'Range selection mode. Click two options to select a range.';
      default:
        return '';
    }
  }, [mode]);
  // Normalize value to array for easier processing
  const normalizedValue = useMemo(() => {
    if (value === undefined || value === null || value === '') return [];
    return Array.isArray(value) ? value.filter(v => v !== '' && v !== null && v !== undefined) : [value];
  }, [value]);
  // Check if "Any" is selected (no other options selected)
  const isAnySelected = normalizedValue.length === 0;
  // Get all option values for range selection
  const allOptionValues = useMemo(() => options.map(option => option.value), [options]);
  const handleOptionClick = useCallback(optionValue => {
    let newValue;
    if (mode === 'single') {
      // Single select: replace current selection
      newValue = optionValue;
    } else if (mode === 'multi') {
      // Multi select: toggle option
      const currentValues = [...normalizedValue];
      const valueIndex = currentValues.indexOf(optionValue);
      if (valueIndex > -1) {
        currentValues.splice(valueIndex, 1);
      } else {
        currentValues.push(optionValue);
      }
      newValue = currentValues;
    } else if (mode === 'range') {
      // Range select: select range from first clicked to last clicked
      const optionIndex = allOptionValues.indexOf(optionValue);
      if (normalizedValue.length === 0) {
        // First selection
        newValue = [optionValue];
      } else if (normalizedValue.length === 1) {
        // Second selection - create range
        const firstIndex = allOptionValues.indexOf(normalizedValue[0]);
        const startIndex = Math.min(firstIndex, optionIndex);
        const endIndex = Math.max(firstIndex, optionIndex);
        newValue = allOptionValues.slice(startIndex, endIndex + 1);
      } else {
        // Reset and start new range
        newValue = [optionValue];
      }
    } else {
      newValue = optionValue;
    }
    onChange?.(newValue);
  }, [onChange, mode, normalizedValue, allOptionValues]);
  const handleAnyClick = useCallback(() => {
    if (!onChange) return;
    // "Any" means no selection
    onChange(mode === 'single' ? '' : []);
  }, [onChange, mode]);
  const isOptionSelected = useCallback(optionValue => normalizedValue.includes(optionValue), [normalizedValue]);
  // Generate accessible descriptions
  const getSelectionSummary = useMemo(() => {
    if (isAnySelected) return 'No filters applied';
    if (mode === 'single') return `${normalizedValue[0]} selected`;
    if (mode === 'multi') return `${normalizedValue.length} options selected: ${normalizedValue.join(', ')}`;
    if (mode === 'range') return `Range selected: ${normalizedValue.join(' to ')}`;
    return '';
  }, [isAnySelected, mode, normalizedValue]);
  return {
    getModeDescription,
    getSelectionSummary,
    handleAnyClick,
    handleOptionClick,
    isAnySelected,
    isOptionSelected,
    normalizedValue
  };
};

/**
 * URL Page State Synchronization Hooks
 *
 * This module is intended for pages that maintain filters, sorting, and pagination state,
 * and need to keep this state synchronized with URL parameters in real-time.
 *
 * useUrlPageStateSyncToUrl: Syncs React state TO the URL (on state changes)
 * useUrlPageStateSyncFromUrl: Syncs FROM the URL back to React state (on popstate/back button)
 *
 */
// Short debounce to batch rapid changes (e.g., typing in a text field)
// but short enough that distinct user actions create separate history entries
const URL_SERIALIZE_DEBOUNCE_MS = ms('50ms');
// Flag to prevent pushing to URL while handling popstate
let isHandlingPopstate = false;
const setIsHandlingPopstate = value => {
  isHandlingPopstate = value;
};
/**
 * Syncs page state (filters, sort, pagination) to URL params.
 * Use this hook to push state changes to the URL.
 */
const useUrlPageStateSyncToUrl = ({
  filterState,
  sort,
  pagination,
  getCompactFilterState,
  searchText,
  disabled = false,
  useHistoryReplace = false,
  paramKey = VIEW_STATE_PARAM
}) => {
  const isInitialMountRef = useRef(true);
  const serializeToUrl = useMemo(() => _.debounce((compactFilterState, sortState, paginationState, searchTextState, shouldUseReplace) => {
    // Skip if we're currently handling a popstate event
    if (isHandlingPopstate) {
      return;
    }
    const serialized = serializePageParams({
      filters: compactFilterState,
      sort: sortState,
      pagination: paginationState,
      searchText: searchTextState
    });
    const searchParams = new URLSearchParams(window.location.search);
    const currentViewState = searchParams.get(paramKey);
    // Skip if the view_state hasn't changed
    if (serialized === currentViewState || !serialized && !currentViewState) {
      return;
    }
    if (serialized) {
      searchParams.set(paramKey, serialized);
    } else {
      searchParams.delete(paramKey);
    }
    const newUrl = searchParams.toString() ? `?${searchParams.toString()}` : window.location.pathname;
    // Use replaceState or pushState based on configuration
    if (shouldUseReplace) {
      window.history.replaceState(null, '', newUrl);
    } else {
      window.history.pushState(null, '', newUrl);
    }
  }, URL_SERIALIZE_DEBOUNCE_MS), [paramKey]);
  useEffect(() => {
    if (disabled) return;
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    const compactState = getCompactFilterState();
    serializeToUrl(compactState, sort, pagination, searchText, useHistoryReplace);
  }, [filterState, sort, pagination, getCompactFilterState, searchText, serializeToUrl, disabled, useHistoryReplace]);
};
/**
 * Syncs page state FROM URL params back to React state.
 * Use this hook to respond to browser back/forward navigation (popstate events).
 *
 * @example
 * ```tsx
 * useUrlPageStateSyncFromUrl({
 *   onUrlChange: ({ filters, sort, pagination, searchText }) => {
 *     setFilterState(prev => ({ ...prev, ...filters }))
 *     setSortState(sort)
 *     setPagination(pagination)
 *     if (searchText !== undefined) {
 *       setSearchText(searchText)
 *     }
 *   }
 * })
 * ```
 */
const useUrlPageStateSyncFromUrl = ({
  onUrlChange,
  disabled = false,
  paramKey = VIEW_STATE_PARAM
}) => {
  const onUrlChangeRef = useRef(onUrlChange);
  // Keep callback ref up to date without triggering effect re-runs
  useEffect(() => {
    onUrlChangeRef.current = onUrlChange;
  }, [onUrlChange]);
  const handlePopState = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const viewState = searchParams.get(paramKey);
    try {
      const params = viewState ? deserializePageParams(viewState) : {};
      // Set flag to prevent useUrlPageStateSyncToUrl from pushing during state update
      // Clear after debounce window (50ms) + buffer to ensure the push is skipped
      setIsHandlingPopstate(true);
      onUrlChangeRef.current(params);
      setTimeout(() => setIsHandlingPopstate(false), ms('100ms'));
    } catch (error) {
      // If deserialization fails, call with empty params
      console.error('Failed to deserialize URL state on popstate:', error);
      setIsHandlingPopstate(true);
      onUrlChangeRef.current({});
      setTimeout(() => setIsHandlingPopstate(false), ms('100ms'));
    }
  }, [paramKey]);
  useEffect(() => {
    if (disabled || typeof window === 'undefined') return;
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [disabled, handlePopState]);
};
/**
 * Triggers a one-time action based on a URL parameter.
 * Waits until `isReady` is true, then calls `onTrigger` with the param value and clears the param.
 *
 * @example
 * ```tsx
 * useUrlActionParam('auto_open', {
 *   isReady: !isLoading && rows.length > 0,
 *   onTrigger: (value) => {
 *     const index = parseInt(value, 10)
 *     if (!isNaN(index) && index >= 0 && index < rows.length) {
 *       onViewClick(rows[index])
 *     }
 *   }
 * })
 * ```
 */
const useUrlActionParam = (paramName, {
  isReady,
  onTrigger
}) => {
  const hasTriggeredRef = useRef(false);
  const onTriggerRef = useRef(onTrigger);
  // Keep callback ref up to date without triggering effect re-runs
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);
  useEffect(() => {
    // Only trigger once
    if (hasTriggeredRef.current) {
      return;
    }
    // Wait until ready
    if (!isReady) {
      return;
    }
    // Mark as handled (whether param exists or not)
    hasTriggeredRef.current = true;
    // Check for param
    const paramValue = getUrlParam(paramName);
    if (paramValue === null) {
      return;
    }
    // Clear param from URL
    setUrlParam(paramName, null);
    // Trigger the action
    onTriggerRef.current(paramValue);
  }, [isReady, paramName]);
};

const useFilters = ({
  filterState: externalFilterState,
  definitionGenerator,
  definitionConfig,
  filterStateToViewModels = defaultFilterStateToViewModels,
  viewModelToFilterState = defaultViewModelsToFilterState
}) => {
  const [filterDefinitions, setFilterDefinitions] = useState(definitionGenerator(definitionConfig));
  useEffect(() => {
    setFilterDefinitions(definitionGenerator(definitionConfig));
  }, [definitionConfig, definitionGenerator]);
  /**
   * Filter Data (View Models)
   */
  const [filterViewModels, setFilterViewModels] = useState(filterStateToViewModels(externalFilterState, filterDefinitions));
  // update view models when external filter state changes
  useEffect(() => {
    const newViewModels = filterStateToViewModels(externalFilterState, filterDefinitions);
    setFilterViewModels(newViewModels);
    // Update async filter label cache
    updateAsyncFilterLabelCache(filterDefinitions, newViewModels).catch(error => coreLogger.error('Error updating async filter cache:', error));
  }, [externalFilterState, filterDefinitions, filterStateToViewModels]);
  /**
   * update: Updates a specific filter group view model's specific key with a new value.
   * E.g. update('property', 'beds', 3) will update the 'beds' key in the 'property' view model
   *
   * @param modelName // name of view model e.g. 'property', 'assetType', etc.
   * @param key  //  key of view model e.g. 'beds', 'baths', etc.
   * @param newValue // new value to update
   * @returns void
   */
  const update = (modelName, key, newValue) => {
    let stateAfterUpdate;
    // logger.log('update', modelName, key, newValue) // keeping this here on purpose for debugging
    setFilterViewModels(prevState => {
      // New design display filters as individual groups and repeated inside More Filters
      // In order to keep them in sync, we need to apply the changes done in any of the two cases into the other.
      const matchingModel = Object.entries(prevState).find(([name, data]) => name !== modelName && key in data)?.[0];
      let nextState;
      if (matchingModel) {
        nextState = {
          ...prevState,
          [modelName]: {
            ...prevState[modelName],
            [key]: newValue
          },
          [matchingModel]: {
            ...prevState[matchingModel],
            [key]: newValue
          }
        };
      } else {
        nextState = {
          ...prevState,
          [modelName]: {
            ...prevState[modelName],
            [key]: newValue
          }
        };
      }
      // Update async filter label cache for the changed value
      updateAsyncFilterLabelCache(filterDefinitions, nextState).catch(error => coreLogger.error('Error updating async filter cache:', error));
      stateAfterUpdate = viewModelToFilterState(nextState, externalFilterState, filterDefinitions);
      return nextState;
    });
    return stateAfterUpdate;
  };
  /**
   * Resets a specific group model to its initial state based on the group definition.
   * @param modelName // name of view model e.g. 'property', 'assetType', etc.
   */
  const reset = modelName => {
    const groupDefinition = filterDefinitions.find(def => def.name === modelName);
    if (!groupDefinition) throw new Error(`Group definition not found for ${modelName}`);
    let stateAfterReset = {};
    setFilterViewModels(prevState => {
      const newState = {
        ...prevState,
        [modelName]: initializeFilterGroupViewModel(groupDefinition)
      };
      const updatedFilters = resetMatchingFiltersViewModel(groupDefinition, newState);
      const nextState = {
        ...prevState,
        ...updatedFilters
      };
      stateAfterReset = viewModelToFilterState(nextState, externalFilterState, filterDefinitions);
      return nextState;
    });
    return stateAfterReset;
  };
  /* istanbul ignore next */
  const resetAll = () => {
    const viewModels = {};
    for (const filterDefinition of filterDefinitions) {
      viewModels[filterDefinition.name] = initializeFilterGroupViewModel(filterDefinition);
    }
    setFilterViewModels(viewModels);
  };
  /**
   * Gets the current filter state with only non-empty values.
   * Excludes null, undefined, empty strings, empty arrays, and empty range objects.
   * @returns Compact filter state
   */
  const getCompactState = () => {
    const currentFilterState = viewModelToFilterState(filterViewModels, externalFilterState, filterDefinitions);
    // Only include non-null values in the compact state
    const compactState = {};
    Object.entries(currentFilterState).forEach(([key, value]) => {
      // Check if value is "set" (not null, not empty string, not empty array, not empty object)
      if (value !== null && value !== undefined && value !== '') {
        // For arrays, only include if not empty
        if (Array.isArray(value) && value.length === 0) {
          return;
        }
        // For range values, check if min or max is set
        if (typeof value === 'object' && !Array.isArray(value)) {
          const rangeValue = value;
          // Exclude if both min and max are unset (null, undefined, or empty string)
          const isMinUnset = !rangeValue.min || rangeValue.min === '';
          const isMaxUnset = !rangeValue.max || rangeValue.max === '';
          if (isMinUnset && isMaxUnset) {
            return;
          }
        }
        compactState[key] = value;
      }
    });
    return compactState;
  };
  const data = useMemo(() => ({
    filterState: viewModelToFilterState(filterViewModels, externalFilterState, filterDefinitions),
    filterViewModels,
    filterDefinitions
  }), [viewModelToFilterState, filterViewModels, externalFilterState, filterDefinitions]);
  const actions = {
    update,
    reset,
    resetAll,
    getCompactState
  };
  return {
    data,
    actions
  };
};

var hooks$c = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useFilters: useFilters,
    useInlineSelectFilter: useInlineSelectFilter,
    useRangeFilter: useRangeFilter,
    useUrlActionParam: useUrlActionParam,
    useUrlPageStateSyncFromUrl: useUrlPageStateSyncFromUrl,
    useUrlPageStateSyncToUrl: useUrlPageStateSyncToUrl
});

var types$6 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

class FilterBuilder {
  constructor(name, controlType, controlValueType) {
    match(controlType).with('singleSelect', () => {
      if (name && ['string', 'number'].includes(controlValueType)) {
        this.filter = {
          name,
          itemType: 'filter',
          label: null,
          description: null,
          controlType,
          controlValueType: controlValueType,
          options: []
        };
      } else {
        throw new Error('Invalid value name or type for singleSelect');
      }
    }).with('multiSelect', () => {
      if (name && ['string', 'number', 'string[]'].includes(controlValueType)) {
        this.filter = {
          name,
          itemType: 'filter',
          label: null,
          description: null,
          controlType,
          controlValueType,
          options: []
        };
      } else {
        throw new Error('Invalid value name or type for multiSelect');
      }
    }).with('inlineSelect', () => {
      if (name && ['string', 'number'].includes(controlValueType)) {
        this.filter = {
          name,
          itemType: 'filter',
          label: null,
          description: null,
          controlType,
          controlValueType: controlValueType,
          options: [],
          mode: 'multi' // default to multi for inlineSelect
        };
      } else {
        throw new Error('Invalid value name or type for inlineSelect');
      }
    }).with('range', () => {
      if (name && ['string', 'number', 'Date'].includes(controlValueType)) {
        this.filter = {
          name,
          itemType: 'filter',
          label: null,
          description: null,
          controlType,
          controlValueType,
          min: null,
          max: null,
          placeholderMin: 'No Min',
          placeholderMax: 'No Max'
        };
      } else {
        throw new Error('Invalid name or value type for range');
      }
    }).with('boolean', () => {
      if (name && ['string', 'string[]', 'number', 'boolean'].includes(controlValueType)) {
        this.filter = {
          name,
          itemType: 'filter',
          label: null,
          description: null,
          controlType,
          controlValueType
        };
      } else {
        throw new Error('Invalid name or value type for boolean');
      }
    }).otherwise(() => {
      throw new Error('Invalid control type');
    });
  }
  label(label) {
    this.filter.label = label;
    return this;
  }
  labelIconProps(iconProps) {
    this.filter.labelIconProps = iconProps;
    return this;
  }
  shortLabel(shortLabel) {
    this.filter.shortLabel = shortLabel;
    return this;
  }
  description(description) {
    this.filter.description = description;
    return this;
  }
  options(options) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.options = options;
    } else if (this.filter.controlType === 'multiSelect') {
      this.filter.options = options;
    } else if (this.filter.controlType === 'inlineSelect') {
      this.filter.options = options;
    } else {
      throw new Error('Invalid control type for setting options');
    }
    return this;
  }
  range(range) {
    if (this.filter.controlType === 'range') {
      this.filter.min = range.min;
      this.filter.max = range.max;
      this.filter.optionList = range.optionList;
      this.filter.intentValueOptions = range.intentValueOptions;
    } else {
      throw new Error('Invalid control type for setting range');
    }
    return this;
  }
  mode(mode) {
    if (this.filter.controlType === 'inlineSelect') {
      this.filter.mode = mode;
    } else if (this.filter.controlType === 'multiSelect') {
      this.filter.mode = mode;
    } else if (this.filter.controlType === 'range') {
      this.filter.mode = mode;
    } else {
      throw new Error('Invalid control type for setting mode');
    }
    return this;
  }
  placeholder(...args) {
    if (this.filter.controlType === 'singleSelect' && args.length === 1) {
      this.filter.placeholder = args[0];
    } else if (this.filter.controlType === 'range' && args.length === 2) {
      this.filter.placeholderMin = args[0];
      this.filter.placeholderMax = args[1];
    } else {
      throw new Error('Invalid argument(s) for setting placeholder');
    }
    return this;
  }
  styleIntent(intent) {
    this.filter.styleIntent = intent;
    return this;
  }
  converterOverrides(overrides) {
    this.filter.converterOverrides = overrides;
    return this;
  }
  /**
   * Set a custom DSL clause builder for this filter.
   * When present, this function will be used instead of the default DSL generation logic.
   *
   * @param builderFunc - Function that generates a DSL clause from filter state
   * @returns this builder for chaining
   *
   * @example
   * new FilterBuilder('net_rating', 'multiSelect', 'string')
   *   .options([
   *     { label: 'Positive', value: 'POSITIVE' },
   *     { label: 'Negative', value: 'NEGATIVE' }
   *   ])
   *   .buildDSLClause((state, def) => {
   *     const values = state as string[]
   *     if (values.includes('POSITIVE')) {
   *       return { range: { field: def.name, gte: 0.01 } }
   *     }
   *     return null
   *   })
   *   .build()
   */
  buildDSLClause(builderFunc) {
    // eslint-disable-next-line
    this.filter.buildDSLClause = builderFunc;
    return this;
  }
  lookupMode(mode) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.lookupMode = mode;
    } else {
      throw new Error('lookupMode is only valid for singleSelect filters');
    }
    return this;
  }
  /**
   * Opt-in type-to-filter for static singleSelect dropdowns. See
   * SingleSelectTypeaheadOptions. Only valid for singleSelect filters.
   */
  typeahead(options) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.typeahead = options;
    } else {
      throw new Error('typeahead is only valid for singleSelect filters');
    }
    return this;
  }
  cacheKey(key) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.cacheKey = key;
    } else {
      throw new Error('cacheKey is only valid for singleSelect filters');
    }
    return this;
  }
  onSearchChange(searchFunc) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.onSearchChange = searchFunc;
    } else {
      throw new Error('onSearchChange is only valid for singleSelect filters');
    }
    return this;
  }
  searchDebounceMs(ms) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.searchDebounceMs = ms;
    } else {
      throw new Error('searchDebounceMs is only valid for singleSelect filters');
    }
    return this;
  }
  allowNonExistentSearch(allow, nonExistentSearchLabel) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.allowNonExistentSearch = allow;
      this.filter.nonExistentSearchLabel = nonExistentSearchLabel || nonExistentFilterOption.label;
    } else {
      throw new Error('allowNonExistentSearch is only valid for singleSelect filters');
    }
    return this;
  }
  minSearchLength(length) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.minSearchLength = length;
    } else {
      throw new Error('minSearchLength is only valid for singleSelect filters');
    }
    return this;
  }
  getOptionByValue(getOptionFunc) {
    if (this.filter.controlType === 'singleSelect') {
      this.filter.getOptionByValue = getOptionFunc;
    } else {
      throw new Error('getOptionByValue is only valid for singleSelect filters');
    }
    return this;
  }
  localFilterable(options) {
    if (['multiSelect', 'singleSelect', 'inlineSelect'].includes(this.filter.controlType)) {
      const filter = this.filter;
      filter.localFilterable = true;
      if (options?.placeholder) filter.localFilterPlaceholder = options.placeholder;
      if (options?.minLength) filter.localFilterMinLength = options.minLength;
      if (options?.matchMode) filter.localFilterMatchMode = options.matchMode;
    } else {
      throw new Error('localFilterable is only valid for select-type filters');
    }
    return this;
  }
  build() {
    return this.filter;
  }
}
class FilterSectionBuilder {
  constructor(name) {
    this.section = {
      name,
      itemType: 'section',
      label: null,
      description: null,
      filters: [],
      styleIntent: {
        compact: false,
        showDivider: true
      }
    };
  }
  label(label) {
    this.section.label = label;
    return this;
  }
  labelIconProps(iconProps) {
    this.section.labelIconProps = iconProps;
    return this;
  }
  description(description) {
    this.section.description = description;
    return this;
  }
  filters(filters) {
    this.section.filters = filters;
    return this;
  }
  styleIntent(intent) {
    this.section.styleIntent = intent;
    return this;
  }
  build() {
    return this.section;
  }
}
class FilterGroupBuilder {
  constructor(name, label) {
    this.group = {
      name,
      label,
      itemType: 'group',
      description: null,
      styleIntent: {
        compact: false
      },
      items: [],
      mode: 'quickFilter'
    };
  }
  label(label) {
    this.group.label = label;
    return this;
  }
  description(description) {
    this.group.description = description;
    return this;
  }
  labelIconProps(iconProps) {
    this.group.labelIconProps = iconProps;
    return this;
  }
  items(items) {
    this.group.items = items;
    return this;
  }
  styleIntent(intent) {
    this.group.styleIntent = intent;
    return this;
  }
  mode(mode) {
    this.group.mode = mode;
    return this;
  }
  build() {
    return this.group;
  }
}

var builders = /*#__PURE__*/Object.freeze({
    __proto__: null,
    FilterBuilder: FilterBuilder,
    FilterGroupBuilder: FilterGroupBuilder,
    FilterSectionBuilder: FilterSectionBuilder
});

var index$j = /*#__PURE__*/Object.freeze({
    __proto__: null,
    builders: builders,
    constants: constants$4,
    hooks: hooks$c,
    types: types$6,
    utils: utils$9
});

const SORT_TYPES_VALUES = {
  RECOMMENDED: 'resi_sort_v2',
  RECOMMENDED_V2: 'recommended_v2',
  NEWLY_ADDED: 'asset_activation_dt_sort_desc',
  DISTANCE: 'distance',
  AUCTION_DATE: 'auction_date_order',
  SQFT_LOW_HIGH: 'sqft',
  SQFT_HIGH_LOW: 'sqft desc',
  PRICE_LOW_HIGH: 'valuation_estimate',
  PRICE_HIGH_LOW: 'valuation_estimate desc'
};
const WEB_SORT_TYPES = {
  recommended: SORT_TYPES_VALUES.RECOMMENDED,
  recommendedV2: SORT_TYPES_VALUES.RECOMMENDED_V2,
  newlyAdded: SORT_TYPES_VALUES.NEWLY_ADDED,
  distance: SORT_TYPES_VALUES.DISTANCE,
  auctionStartDate: SORT_TYPES_VALUES.AUCTION_DATE,
  sqftLowHigh: SORT_TYPES_VALUES.SQFT_LOW_HIGH,
  sqftHighLow: SORT_TYPES_VALUES.SQFT_HIGH_LOW,
  priceLowHigh: SORT_TYPES_VALUES.PRICE_LOW_HIGH,
  priceHighLow: SORT_TYPES_VALUES.PRICE_HIGH_LOW
};
const SORT_OPTIONS = [{
  key: 'resi_sort_v2',
  name: 'Recommended',
  apiType: 'resi_sort_v2',
  apiOrder: 'asc',
  accessibilityLabel: 'Sort_Recommended_Button',
  testID: 'search_sort_recommended'
}, {
  key: 'asset_activation_dt_sort_desc',
  name: 'Newly Added',
  apiType: 'asset_activation_dt_sort',
  apiOrder: 'desc',
  accessibilityLabel: 'Sort_Newly_Added_Button',
  testID: 'search_sort_newly_added'
}, {
  key: 'distance',
  name: 'Distance',
  apiType: 'distance',
  apiOrder: 'asc',
  accessibilityLabel: 'Sort_Distance_Button',
  testID: 'search_sort_distance'
}, {
  key: 'sqft',
  name: 'Sq. Ft: Low - High',
  apiType: 'sqft',
  apiOrder: 'asc',
  accessibilityLabel: 'Sort_Sqft_Low_High_Button',
  testID: 'search_sort_sqft_low_high'
}, {
  key: 'sqft desc',
  name: 'Sq. Ft: High - Low',
  apiType: 'sqft',
  apiOrder: 'desc',
  accessibilityLabel: 'Sort_Sqft_High_Low_Button',
  testID: 'search_sort_sqft_high_low'
}, {
  key: 'auction_date_order',
  name: 'Auction Date',
  apiType: 'auction_date_order',
  apiOrder: 'asc',
  accessibilityLabel: 'Sort_Auction_Date_Button',
  testID: 'search_sort_auction_date'
}, {
  key: 'valuation_estimate',
  name: 'Price: Low - High',
  apiType: 'valuation_estimate',
  apiOrder: 'asc',
  accessibilityLabel: 'Sort_Price_Low_High_Button',
  testID: 'search_sort_price_low_high'
}, {
  key: 'valuation_estimate desc',
  name: 'Price: High - Low',
  apiType: 'valuation_estimate',
  apiOrder: 'desc',
  accessibilityLabel: 'Sort_Price_High_Low_Button',
  testID: 'search_sort_price_high_low'
}];
const DEFAULT_SORT_KEY = 'auction_date_order';
const WEB_SORT_OPTIONS = [{
  label: 'Recommended',
  value: SORT_TYPES_VALUES.RECOMMENDED
}, {
  label: 'Newly Added',
  value: SORT_TYPES_VALUES.NEWLY_ADDED
}, {
  label: 'Distance',
  value: SORT_TYPES_VALUES.DISTANCE
}, {
  label: 'Sq. Ft: Low - High',
  value: SORT_TYPES_VALUES.SQFT_LOW_HIGH
}, {
  label: 'Sq. Ft: High - Low',
  value: SORT_TYPES_VALUES.SQFT_HIGH_LOW
}, {
  label: 'Auction Date',
  value: SORT_TYPES_VALUES.AUCTION_DATE
}, {
  label: 'Price: Low - High',
  value: SORT_TYPES_VALUES.PRICE_LOW_HIGH
}, {
  label: 'Price: High - Low',
  value: SORT_TYPES_VALUES.PRICE_HIGH_LOW
}];
const ARVSortOptionsOrder = [{
  text: 'Recommended',
  value: WEB_SORT_TYPES.recommended
}, {
  text: 'Newly Added',
  value: WEB_SORT_TYPES.newlyAdded
}, {
  text: 'Distance',
  value: WEB_SORT_TYPES.distance
}, {
  text: 'Sq. Ft: Low - High',
  value: WEB_SORT_TYPES.sqftLowHigh
}, {
  text: 'Sq. Ft: High - Low',
  value: WEB_SORT_TYPES.sqftHighLow
}, {
  text: 'Auction Date',
  value: WEB_SORT_TYPES.auctionStartDate
}, {
  text: 'Price: Low - High',
  value: WEB_SORT_TYPES.priceLowHigh
}, {
  text: 'Price: High - Low',
  value: WEB_SORT_TYPES.priceHighLow
}];

var sorting$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ARVSortOptionsOrder: ARVSortOptionsOrder,
    DEFAULT_SORT_KEY: DEFAULT_SORT_KEY,
    SORT_OPTIONS: SORT_OPTIONS,
    SORT_TYPES_VALUES: SORT_TYPES_VALUES,
    WEB_SORT_OPTIONS: WEB_SORT_OPTIONS,
    WEB_SORT_TYPES: WEB_SORT_TYPES
});

/*
 * Listings Search constants
 */
/*
 * Filter names for popup in the fiters header
 */
const allListingTypesSelected = ['pending', 'closed', 'canceled'];
const interiorAccessSelected = 'y';
const financingAvailableSelected = 'y';
const maoFilterSelected = 'y';
const moreAllListingFilters = 'all_filters';
const brokerCoopAvailableSelected = 'y';
const hideEndingSoonSelected = 'y';
const noBuyerPremiumFilterSelected = 'n';
const showReserveSelected = 'y';
const vacantOccupancyStatus = ['vacant'];
const NEARBY_SEARCH_RADIUS_OPTIONS = [{
  key: '5',
  text: '5 Miles',
  value: 5
}, {
  key: '10',
  text: '10 Miles',
  value: 10
}, {
  key: '25',
  text: '25 Miles',
  value: 25
}, {
  key: '50',
  text: '50 Miles',
  value: 50
}, {
  key: '100',
  text: '100 Miles',
  value: 100
}];
NEARBY_SEARCH_RADIUS_OPTIONS.map(o => o.value);
const AGGREGATE_FIELDS = {
  property_group_type: 'primary_property_summary.structure_type_group.keyword',
  property_type: 'primary_property_summary.structure_type_code.keyword',
  remote_bid_assets: 'listing_summary.is_remote_bid_enabled'
};
// api names of address keyword fields used for filtering (except text search which is not .keyword)
const API_ADDRESS_FIELDS = {
  state: 'seller_property.country_primary_subdivision.keyword',
  county: 'seller_property.country_secondary_subdivision.keyword',
  zip: 'seller_property.postal_code.keyword',
  city: 'seller_property.municipality.keyword',
  coordinates: 'primary_property_summary.address.coordinates'
};
// the order if these is important
const LOCATION_PARAMS = ['property_city', 'property_zip', 'property_county'];
const PARAM_TO_API_FIELD_MAP = {
  property_city: API_ADDRESS_FIELDS.city,
  property_zip: API_ADDRESS_FIELDS.zip,
  property_county: API_ADDRESS_FIELDS.county
};
const DEFAULT_NEARBY_SEARCH_RADIUS = 50;
/**
 * Since most of the elements created in the useListingsSearch and useFilters hooks are dynamic,
 * we need to create default functions to ensure type safety.
 */
const getDefaultFilterState = () => ({
  search: '',
  state: [],
  city: [],
  county: [],
  zip: [],
  locations: '',
  mapBounds: [],
  userCoords: [],
  geoLocation: [],
  listingType: ['active'],
  auctionFormat: [],
  propertyType: [],
  occupancyStatus: [],
  nearbySearchRadius: 25,
  beds: null,
  baths: null,
  yearBuilt: null,
  sqft: {
    min: null,
    max: null
  },
  price: {
    min: null,
    max: null
  },
  sort: {
    type: 'auction_date_order',
    order: 'asc'
  },
  financingAvailable: false,
  accessAvailable: false,
  inspectionAllowed: false,
  showReserve: false,
  brokerCoopAvailable: false,
  reserveReduced: false,
  noBuyerPremiumFilter: false,
  hideEndingSoon: false,
  leasebackRequired: null,
  openedAssetId: null,
  presale: false,
  globalSearch: false,
  limit: 20,
  offset: 0,
  sellerCode: [],
  useNearbySearch: 'y',
  assetType: [],
  foreclosureBidType: [],
  mapView: false,
  isMapShown: true,
  marketingTag: '',
  daysSinceFirstPublished: null
});
const getDefaultListingsState = () => ({
  total_count: 0,
  content: [],
  offset: 0,
  nearbyTotal: {},
  propertyTypeTotals: {},
  remoteBideTotals: {}
});
const getDefaultUiFilterGroups = () => ({});
const getDefaultFiltersData = () => ({
  filterState: getDefaultFilterState(),
  filterViewModels: getDefaultUiFilterGroups(),
  filterDefinitions: []
});
const getDefaultFiltersActions = () => ({
  update: () => undefined,
  reset: () => undefined,
  resetAll: () => undefined,
  getCompactState: () => ({})
});
const getDefaultSearchData = () => ({
  listings: getDefaultListingsState(),
  currentUiFilterGroups: getDefaultUiFilterGroups(),
  defaultUiFilterGroups: getDefaultUiFilterGroups(),
  currentFilters: getDefaultFilterState(),
  listingCompatabilityFilters: {},
  currentUrl: '',
  currentSearchId: 0,
  isLoading: false,
  filtersData: getDefaultFiltersData()
});
const getDefaultSearchActions = () => ({
  onFilterChange: () => undefined,
  onLocationSuggestionSelect: () => undefined,
  clearFilters: () => undefined,
  filtersActions: getDefaultFiltersActions()
});

const ALL_SUGGESTION_TYPES = ['state', 'city', 'zip', 'county', 'address', 'text', 'savedSearch', 'recentSearch', 'currentLocation', 'globalSearch'];
const SEARCH_SUGGESTION_GROUPS = {
  places: 'Places',
  listings: 'Listings',
  recentSearch: 'Recent Searches',
  savedSearches: 'Saved Searches',
  other: 'Other',
  textSearch: 'Text Search'
};
const SUGGESTION_CATEGORY_ICONS = ['heart', 'input-text', 'house-chimney', 'location-dot'];
const ALL_FORMATTING_TYPES = ['highlight'];
const MAX_SAVED_SEARCHES = 3;
const SAVED_SEARCH_SOURCE = 'ADC';
const SORT_ORDERS = {
  desc: 'desc',
  asc: 'asc'
};
const SORT_TYPES = {
  default: 'resi_sort_v2',
  defaultDesc: 'resi_sort_v2_desc',
  newlyAdded: 'asset_activation_dt_sort',
  propertyAddress: 'resi_property_address',
  auctionStartDate: 'auction_date_order',
  beds: 'beds',
  baths: 'baths',
  sqft: 'sqft',
  valuationEstimate: 'valuation_estimate',
  distance: 'distance'
};
const SORT_TYP_VALUES = ['resi_sort_v2', 'resi_sort_v2_desc', 'asset_activation_dt_sort', 'resi_property_address', 'auction_date_order', 'beds', 'baths', 'sqft', 'valuation_estimate', 'distance'];
const SORT_ORDER_VALUES = ['desc', 'asc'];
const SORT_TYPES_DESC_OVERRIDE = {
  starting_bid_sort: 'starting_bid_sort_desc',
  asset_activation_dt_sort: 'asset_activation_dt_sort_desc'
};
const NOT_IMPLEMENTED = Symbol.for('adc.filters.not_implemented');
const MAXS_BEDS = 5;
const FILTER_GROUP_KEYS = {
  propertyTypeFilterGroup: 'propertyTypeFilterGroup',
  propertyInfoFilterGroup: 'propertyInfoFilterGroup',
  conditionFilterGroup: 'conditionFilterGroup',
  auctionTypeFilterGroup: 'auctionTypeFilterGroup',
  assetTypeFilterGroup: 'assetTypeFilterGroup',
  moreFilterGroup: 'moreFilterGroup',
  featuredFilterGroup: 'featuredFilterGroup',
  nearbySearchFilterGroup: 'nearbySearchFilterGroup',
  locationFilterGroup: 'locationFilterGroup',
  mapFilterGroup: 'mapFilterGroup',
  otherFilterGroup: 'otherFilterGroup'
};
var GenericLocations;
(function (GenericLocations) {
  GenericLocations["ALL_LOCATIONS"] = "All Locations";
  GenericLocations["CURRENT_LOCATION"] = "Current Location";
})(GenericLocations || (GenericLocations = {}));

var constants$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ALL_FORMATTING_TYPES: ALL_FORMATTING_TYPES,
    ALL_SUGGESTION_TYPES: ALL_SUGGESTION_TYPES,
    FILTER_GROUP_KEYS: FILTER_GROUP_KEYS,
    get GenericLocations () { return GenericLocations; },
    MAXS_BEDS: MAXS_BEDS,
    MAX_SAVED_SEARCHES: MAX_SAVED_SEARCHES,
    NOT_IMPLEMENTED: NOT_IMPLEMENTED,
    SAVED_SEARCH_SOURCE: SAVED_SEARCH_SOURCE,
    SEARCH_SUGGESTION_GROUPS: SEARCH_SUGGESTION_GROUPS,
    SORT_ORDERS: SORT_ORDERS,
    SORT_ORDER_VALUES: SORT_ORDER_VALUES,
    SORT_TYPES: SORT_TYPES,
    SORT_TYPES_DESC_OVERRIDE: SORT_TYPES_DESC_OVERRIDE,
    SORT_TYP_VALUES: SORT_TYP_VALUES,
    SUGGESTION_CATEGORY_ICONS: SUGGESTION_CATEGORY_ICONS,
    getDefaultFilterState: getDefaultFilterState,
    getDefaultFiltersActions: getDefaultFiltersActions,
    getDefaultFiltersData: getDefaultFiltersData,
    getDefaultListingsState: getDefaultListingsState,
    getDefaultSearchActions: getDefaultSearchActions,
    getDefaultSearchData: getDefaultSearchData,
    getDefaultUiFilterGroups: getDefaultUiFilterGroups,
    sortingConstants: sorting$2
});

const logger$d = coreLogger.forModule('blueprint:search');
const fetchLocationSuggestions = search => fetch.forAuctionGraph({
  query: gql$5`
      query ${embeddableOperation('blueprint_locationSuggestions')} ($search: String!) {
        location_suggestions(search: $search) {
          results {
            value
            county
            city
            state
            zip
            type
            url
            relates_to_urn
            coordinates {
              lat
              lon
            }
          }
        }
      }
    `,
  variables: {
    search
  }
}).then(res => res.body).then(({
  data
}) => {
  const rawLocationSuggestions = data?.location_suggestions?.results || [];
  return {
    rawLocationSuggestions
  };
}).catch(e => {
  logger$d.warn(e);
  return {
    rawLocationSuggestions: []
  };
});
const lookupLocation = search => fetch.forAuctionGraph({
  query: gql$5`
          query ${embeddableOperation('blueprint_determineCoordinates')} ($search: String!) {
            determine_coordinates(search: $search) {
              coordinates {
                lat
                lon
              }
            }
          }
        `,
  variables: {
    search
  }
}).then(res => res.body).then(({
  data
}) => {
  return data.determine_coordinates;
}).catch(e => {
  logger$d.warn(e);
  return null;
});
const fetchSavedSearches = controller => fetch.forAuctionGraph({
  query: gql$5`
      query ${embeddableOperation('blueprint_savedSearches')} ($maxSavedSearches: PositiveInt!, $savedSearchSource: SavedSearchSource!) {
      current_user {
          saved_searches(filters: {
            take: $maxSavedSearches,
            source: $savedSearchSource,
          }) {
            saved_search_id
            saved_search_name
            parameters
            namespace
          }
        }
      }
    `,
  variables: {
    maxSavedSearches: MAX_SAVED_SEARCHES,
    savedSearchSource: SAVED_SEARCH_SOURCE
  }
}, {
  controller
}).then(res => res.body).then(({
  data
}) => {
  const rawSavedSearches = data?.current_user?.saved_searches || [];
  return {
    rawSavedSearches
  };
}).catch(e => {
  logger$d.warn(e);
  return {
    rawSavedSearches: []
  };
});
const fetchMarketingTagConfig = async () => fetch.forAuctionGraph({
  query: gql$5`
          query ${embeddableOperation('blueprint_activeListingSearchMarketingTags')} {
            active_listing_search_marketing_tags {
              tag
              configuration {
                srp {
                  order
                  label
                  tooltip
                  is_tooltip_active
                }
              }
            }
          }
        `
}).then(res => res.body).then(({
  data
}) => {
  return data?.active_listing_search_marketing_tags;
}).catch(e => {
  logger$d.warn(e);
  return [];
});
// TODO: srp and resi-event use LISTING_CARD_FIELDS but that's in @adc/ui-adc
// and @adc/ui-adc has blueprint as a dependency.
// ???
// in mobile they alway wrap ListingCompatabilityFilters filters in Partial
// but all the fields on this type are partial except version: Scalars['PositiveInt'];
// not sure if it needs to be Partial because of required version field
const seekListingsFromFilters = async ({
  filters,
  aggregationFields,
  hasAuthenticatedUser,
  searchSource = 'adc:blueprint:search'
}) => {
  const startTime = +Date.now();
  instrument$1.domainEvent({
    // @ts-ignore
    type: enums$5.DomainEventTypeName.SearchResultsPageFilterChanged,
    source: searchSource,
    data: {
      filters
    }
  });
  return fetch.forAuctionGraph({
    query: gql$5`
        ${fragments$7.listingCard.definition} 
        query ${embeddableOperation('blueprint_seekListingsFromFilters')}(
          $filters: ListingCompatabilityFilters!,
          $aggregationFields: [String!]!,
          $hasAuthenticatedUser: Boolean!,
          $requiresAggregation: Boolean!
        ) {
          seek_listings_from_filters(filters: $filters) {
            total_count
            total_pages
            size
            current_page
            aggregation(fields: $aggregationFields) @include(if: $requiresAggregation)
            derived_signals {
              recognized_locations {
                urn
                label
                location_type
              }
            }
            content {
              ${fragments$7.listingCard.reference}
            }
          }
        }
      `,
    variables: {
      filters,
      // NOTE: We should not need this, but because SRP is fully typed it would allow another value here that would
      // result in a graphql error if left untreated. We can remove this restriction when we trust the app is sound.
      hasAuthenticatedUser: Boolean(hasAuthenticatedUser),
      aggregationFields,
      requiresAggregation: aggregationFields.length > 0
    }
  }).then(res => {
    const result = res.body.data.seek_listings_from_filters;
    instrument$1.domainEvent({
      // @ts-ignore
      type: enums$5.DomainEventTypeName.SearchResultsPageResponseReceived,
      source: searchSource,
      data: {
        elapsed_ms: +Date.now() - startTime,
        records_returned: result.content?.length || 0
      }
    });
    return result;
  });
};
const fetchTotalActiveInventory = async () => {
  const res = await fetch.forAuctionGraph({
    query: gql$5`
      query ${embeddableOperation('blueprint_getTotalActiveInventory')} {
        seek_listings_from_filters(filters: { version: 1 }) {
          total_count
        }
      }
    `
  });
  return res.body.data.seek_listings_from_filters?.total_count ?? 0;
};

/**
 * We've found that some states cannot be recognized by google service
 * so created this mapping to make it much accurate
 */
const OUT_OF_US_STATES = {
  AS: 'American Samoa',
  FM: 'Federated States of Micronesia',
  GU: 'Guam',
  MH: 'Marshall Islands',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  PW: 'Palau',
  VI: 'U.S. Virgin Islands',
  'Puerto Rico': 'Puerto Rico' // Since we only have properties in Puerto rico, so adding this mapping to make it a `OUT_OF_US` tag.
};
const ALL_STATES_MAPPING = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OK: 'Oklahoma',
  OH: 'Ohio',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia'
};
const US_STATES_AND_TERRITORIES_MAPPING = {
  ...ALL_STATES_MAPPING,
  ...OUT_OF_US_STATES
};
const stateNameToCode = () => {
  const statesNames = {};
  Object.keys(ALL_STATES_MAPPING).forEach(
  // @ts-ignore ROMAN YOU SHALL FIX THIS
  key => {
    const stateName = ALL_STATES_MAPPING[key].toUpperCase();
    statesNames[stateName] = key;
  });
  return statesNames;
};
const getStateCode = state => {
  if (!isString(state)) return null;
  const _state = state.toUpperCase();
  const states = stateNameToCode();
  const stateCode = states[_state] ||
  // @ts-ignore ROMAN YOU SHALL FIX THIS
  US_STATES_AND_TERRITORIES_MAPPING[_state] && _state || null;
  return stateCode;
};

// TODO TypeScript this
/* istanbul ignore file */

const productTypes = {
  resi_bo_v2: 'bank-owned',
  resi_ft: 'foreclosures',
  resi_new_foreclosed: 'newly-foreclosed',
  resi_ip: 'rentals',
  // TODO: product type or asset type ?
  resi_ss: 'short-sales',
  // TODO: product type or asset type ?
  resi_ps: 'private-seller'
};

/**
 * Usage: New asset type (product type) filter route
 * - new route has "_at" added after resi_ft & resi_new_foreclosed
 * - refer to toolkit-adc/src/components/header/constants/route
 */
const assetTypeFilterRoute = {
  resi_bo_v2: 'bank-owned',
  resi_ft: 'foreclosures_at',
  resi_new_foreclosed: 'newly-foreclosed_at',
  resi_ip: 'rentals',
  resi_ss: 'short-sales_at'
  // TODO: Private seller ?
};
const propertyTypes = {
  resi_sfr: 'single-family',
  resi_mfr: 'multi-family',
  resi_condo: 'condos-townhouses',
  resi_land: 'land'
};
const productDisplayTypes = {
  resi_bo_v2: 'Bank Owned',
  resi_ft: 'Foreclosures',
  resi_ip: 'Rentals',
  resi_ss: 'Short Sales'
};
const propertyDisplayTypes = {
  resi_sfr: 'Single Family',
  resi_mfr: 'Multi-family',
  resi_condo: 'Condo/Townhome',
  resi_land: 'Land'
};
const listingTypes = {
  active: 'Active',
  pending: 'Pending',
  closed: 'Sold',
  canceled: 'Canceled'
};
const assetEventTypes = {
  online: 'Online',
  in_person: 'In Person'
};
const mainDisplay = new Set(['State', 'City', 'County', 'Zip', 'Beds', 'Baths', 'Sqft Range']);
const propertyAndAssetTypesSet = new Set(['Property Type', 'Asset Type']);
const listAndAuctionTypesSet = new Set(['Listing Type', 'Auction Type']);
const showOnlySet = new Set(['Vacant', 'Financing Available', 'Interior Access Available', 'Reserve Disclosed', 'Hide Ending Soon', 'Broker Co-op', "No Buyer's Premium"]);
/*
 * Location Parameters
 */
const showOnlyFields = ['y_ica', 'y_fa', 'y_ia', 'y_sr', 'y_bc', 'y_nbp', 'y_hes',
// this was here before but it's not in srp showOnlyFields code
'y_cs',
// this was not here before but it is in srp showOnlyFields
'y_rr', 'y_ps', 'y_global', 'map_vt'];
const arrayToApiProductType = value => {
  const apiProductTypes = [];
  // This is used to deal with the old search url pass 'bank-owned' only
  // We need the `newly-foreclosed` send to API

  if (value[0] === 'bank-owned' && value.length === 1) {
    const apiProductTypes = Object.entries(productTypes).filter(([_, uiProductType]) => ['bank-owned', 'newly-foreclosed'].includes(uiProductType)).map(productType => productType[0]);
    return apiProductTypes.join(',');
  }
  value.forEach(type => {
    const productTypeEntry = Object.entries(productTypes).find(([_, uiProductType]) => uiProductType === type);
    if (productTypeEntry) {
      apiProductTypes.push(productTypeEntry[0]);
    }
  });
  return apiProductTypes.join(',');
};

/**
 *
 * @param {*} sortType
 * @param {*} sortOrder
 * This function will allow to add to sort override desc to handle special cases like starting_bid/starting_bid_desc
 * In this kind of cases doesn't work to use an empty space to separate, you have a special value to be used from
 * api side. From now on you may only need to add it to SORT_TYPES_DESC_OVERRIDE and will be handled
 */
const getSortOrder = (sortType, sortOrder) => {
  return SORT_TYPES_DESC_OVERRIDE[sortType] ? SORT_TYPES_DESC_OVERRIDE[sortType] : `${sortType} ${sortOrder}`;
};
const stringToSortObject = value => {
  const sort = value.split(',')[0].split(' ');
  let sortType = SORT_TYPES.default;
  let sortOrder = SORT_ORDERS.asc;
  if (sort[0] === SORT_TYPES.defaultDesc) {
    return {
      type: SORT_TYPES.default,
      order: SORT_ORDERS.desc
    };
  }

  /**
   * Added this escape hatch to handle special sort cases like starting_bid/starting_bid_desc
   * where the default way using an empty space doesn't work from api side
   *
   * else continue with regular flow
   */
  const overrideSortKey = _.findKey(SORT_TYPES_DESC_OVERRIDE, value => value === sort[0]);
  if (overrideSortKey) {
    sortOrder = SORT_ORDERS.desc;
    sortType = overrideSortKey;
  } else {
    for (const item in SORT_TYPES) {
      if (SORT_TYPES[item] === sort[0]) {
        sortType = SORT_TYPES[item];
      }
    }
    if (sort[1]) {
      for (const item in SORT_ORDERS) {
        if (SORT_ORDERS[item] === sort[1]) {
          sortOrder = SORT_ORDERS[item];
        }
      }
    }
  }
  return {
    type: sortType,
    order: sortOrder
  };
};
const sortObjectToString = sort => {
  const addSortOrder = sort.order === SORT_ORDERS.desc;
  if (sort.type === SORT_TYPES.default && addSortOrder) return SORT_TYPES.defaultDesc;
  if (sort.type === SORT_TYPES.default) return sort.type;
  // Tiebreaker (listing_id) is now handled server-side in graph-api.
  // Previously appended resi_sort_v2 here which injected first_activation_date
  // sort that dominated non-unique field sorts like valuation_estimate.
  return addSortOrder ? getSortOrder(sort.type, sort.order) : sort.type;
};
const stringToMinMaxObject = value => {
  const minMaxObject = {
    min: null,
    max: null
  };
  const minMaxArray = value.split('_').map(d => d === 'any' ? null : parseInt(d));
  if (minMaxArray[0]) minMaxObject.min = minMaxArray[0];
  if (minMaxArray[1]) minMaxObject.max = minMaxArray[1];
  return minMaxObject;
};
const arrayToApiPropertyType = value => {
  const apiPropertyTypes = [];
  value.forEach(type => {
    const propertyType = Object.entries(propertyTypes).find(([_, uiPropertyType]) => uiPropertyType === type);
    if (propertyType) apiPropertyTypes.push(propertyType[0]);
  });
  return apiPropertyTypes.join(',');
};
const stringToArrayFloat = value => value.split(',').map(o => parseFloat(o));
const stringToArrayAddingSpaces = value => value.replace(/-/g, ' ').replace(/\+/g, '-').split(',').map(o => o
// Split names composed of multiple words so we can capitalize each word.
.split(' ').map(namePart => namePart
// Split a name containing an apostrophe so we can capitalize the two parts, unless one is exactly a possessive
// suffix, in which case we coerce it to lowercase.
.split("'").map(part => part.match(/^[sS]$/) ? 's' : _.capitalize(part)).join("'")).join(' '));
const arrayToStringRemovingSpaces = value => value.join().replace(/-/g, '+').replace(/ /g, '-');
const stringToArray = value => value.split(',');
const addUrlKey = (value, filter) => {
  if (showOnlyFields.includes(filter.url)) {
    return filter.url;
  } else if (filter.api === 'property_state') {
    return value;
  } else if (filter.api === 'property_county') {
    return `${value}-${filter.url}`; // dash needed for county
  } else {
    return `${value}_${filter.url}`;
  }
};
const stateFeToApi = (state = []) => {
  if (state[0]) {
    return getStateCode(state[0]);
  }
  return '';
};
const stringSpaceToDash = value => value.replace(/-/g, '+').split(' ').join('-');
const auctionFormatConverterUpperCase = value => {
  const auctionArr = value.split(',');
  const auctionLabels = [];
  auctionArr.forEach(label => {
    const auctionLabel = assetEventTypes[label];
    if (auctionLabel) auctionLabels.push(auctionLabel);
  });
  return _.join(auctionLabels, ', ');
};
const booleanStringToBoolean = value => _.toLower(value) === 'y';
const noBuyerPremiumConverter = value => _.toLower(value) === 'n';
const propertyActivationDateToDateRange = value => {
  if (!value) return null;
  const today = new Date();
  const result = {};
  switch (value) {
    case '1':
      result.property_activation_date_min = subDays(today, 7).toISOString();
      break;
    case '2':
      result.property_activation_date_min = subDays(today, 30).toISOString();
      result.property_activation_date_max = subDays(today, 7).toISOString();
      break;
    case '3':
      result.property_activation_date_min = subDays(today, 90).toISOString();
      result.property_activation_date_max = subDays(today, 30).toISOString();
      break;
    case '4':
      result.property_activation_date_max = subDays(today, 90).toISOString();
      break;
    default:
      return null;
  }
  return result;
};
const propertyTypeConverter = value => {
  const valueArr = value.split(',');
  const apiPropertyTypes = [];
  valueArr.forEach(type => {
    const propertyType = propertyTypes[type];
    if (propertyType) apiPropertyTypes.push(propertyType);
  });
  return apiPropertyTypes.join(',');
};
const propertyTypeConverterWithSpace = value => {
  const valueArr = value.split(',');
  const apiPropertyTypes = [];
  valueArr.forEach(type => {
    const propertyType = propertyDisplayTypes[type];
    if (propertyType) apiPropertyTypes.push(propertyType);
  });
  return apiPropertyTypes.join(', ');
};
const assetTypeConverter = value => {
  const valueArr = value.split(',');
  const apiProductTypes = [];
  valueArr.forEach(type => {
    const productType = productTypes[type];
    if (productType) apiProductTypes.push(productType);
  });
  return apiProductTypes.join(',');
};
const assetTypeConverterWithSpace = value => {
  const valueArr = value.split(',');
  const apiProductTypes = [];
  valueArr.forEach(type => {
    const productType = productDisplayTypes[type];
    if (productType) apiProductTypes.push(productType);
  });
  return apiProductTypes.join(', ');
};
const listingTypeToLabels = value => {
  const valueArr = value.split(',');
  const listingTypelabels = [];
  valueArr.forEach((type, i) => {
    const listingType = valueArr.length > 2 && i === valueArr.length - 1 ? `or ${listingTypes[type]}` : listingTypes[type];
    if (listingType) listingTypelabels.push(listingType);
  });
  return listingTypelabels.join(', ');
};
const dateStringToUrl = value => {
  const isDisplay = !!value && moment$1(value).isSame(moment$1().add(5, 'days'), 'day');
  return isDisplay && `${value}_any`;
};
const minMaxObjectToString = value => {
  if (!(value.min || value.max)) return;
  return `${value.min ? value.min : 'any'}_${value.max ? value.max : 'any'}`;
};
const minMaxObjectToLabel = value => {
  if (!(value.min || value.max)) return;
  return `${value.min ? value.min : 'Any'} to ${value.max ? value.max : 'Any'}`;
};
const checkOccupancy = value => _.toLower(value) === 'vacant';
const numberWithPlus = value => value && `${value}+`;
const categorizeLabels = (labels, inputLabel, paramValue) => {
  if (mainDisplay.has(inputLabel)) {
    labels.mainDisplay.push([inputLabel, paramValue]);
  } else if (propertyAndAssetTypesSet.has(inputLabel)) {
    labels.propertyAndAssetTypes.push([inputLabel, paramValue]);
  } else if (listAndAuctionTypesSet.has(inputLabel)) {
    labels.listAndAuctionTypes.push([inputLabel, paramValue]);
  } else if (showOnlySet.has(inputLabel)) {
    labels.showOnlyFields.push([inputLabel, paramValue]);
  }
};

/**
 * Convert asset type search param to filter route part
 */
const assetTypeRouteConverter = value => {
  const valueArr = value.split(',');
  const apiProductTypes = [];
  valueArr.forEach(type => {
    const productType = assetTypeFilterRoute[type];
    if (productType) apiProductTypes.push(productType);
  });
  return apiProductTypes.join(',');
};

var _staticBlock;
const rangeToObject = key => value => {
  const returnValue = {};
  if (!Array.isArray(value)) {
    // Backward compatibility: non-array value is used as min only
    returnValue[`${key}_min`] = _.toString(value);
  } else {
    if (value.length === 0) {
      return returnValue;
    }
    const firstVal = value[0].toString();
    const lastVal = value[value.length - 1].toString();
    returnValue[`${key}_min`] = firstVal;
    if (value.length === 1) {
      if (firstVal !== MAXS_BEDS.toString()) {
        returnValue[`${key}_max`] = firstVal;
      }
    } else {
      if (lastVal !== MAXS_BEDS.toString()) {
        returnValue[`${key}_max`] = lastVal;
      }
    }
  }
  return returnValue;
};
const areBoundsSetRange = (min, max) => min > 0 && max > 0;
const isReversedRange = (min, max) => min > max;
const minMaxObjectToApi = key => value => {
  const minMaxApiObject = {};
  const minKey = `${key}_min`;
  const maxKey = `${key}_max`;
  // sepecial case when user sets both inputs but reverses the range
  // min and max values will be reversed only for the API; no changes on url
  if (areBoundsSetRange(value.min, value.max) && isReversedRange(value.min, value.max)) {
    return {
      [minKey]: `${value.max}`,
      [maxKey]: `${value.min}`
    };
  }
  if (value.min) minMaxApiObject[minKey] = `${value.min}`;
  if (value.max) minMaxApiObject[maxKey] = `${value.max}`;
  return minMaxApiObject;
};
/**
 * This schema helps to:
 *   - Map the different param naming convention between the platforms
 *   - Establish an easy to follow and manage conversion policy between
 *     the representation for the params on different platforms
 */
const schema = [
/**
 * Location Parameters
 */
{
  fe: 'search',
  api: 'search',
  url: 'qs',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: _.identity
  }
}, {
  label: 'State',
  fe: 'state',
  api: 'property_state',
  url: 0,
  converter: {
    fe2api: stateFeToApi,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: _.identity
  }
}, {
  label: 'City',
  fe: 'city',
  api: 'property_city',
  url: 'ct',
  converter: {
    fe2api: _.join,
    fe2url: arrayToStringRemovingSpaces,
    url2fe: stringToArrayAddingSpaces,
    api2url: stringSpaceToDash,
    url2label: _.identity
  }
}, {
  label: 'County',
  fe: 'county',
  api: 'property_county',
  url: 'county',
  converter: {
    fe2api: _.join,
    fe2url: arrayToStringRemovingSpaces,
    url2fe: stringToArrayAddingSpaces,
    api2url: stringSpaceToDash,
    url2label: _.identity
  }
}, {
  label: 'Zip',
  fe: 'zip',
  api: 'property_zip',
  url: 'zp',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: _.identity
  }
}, {
  fe: 'mapBounds',
  api: 'geo_location_box',
  url: 'bounds',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArrayFloat,
    api2url: _.identity
  }
}, {
  fe: 'geoLocation',
  api: 'geo_location',
  url: 'geoLocation',
  converter: {
    fe2api: _.join,
    // @ts-ignore empty function
    fe2url: () => {},
    url2fe: stringToArrayFloat
  }
}, {
  fe: 'userCoords',
  api: 'coords',
  url: NOT_IMPLEMENTED,
  converter: {
    fe2api: _.join,
    // @ts-ignore empty function
    fe2url: () => {},
    url2fe: () => {}
  }
},
/**
 * Listing
 */
{
  label: 'Listing Type',
  fe: 'listingType',
  api: 'listing_type',
  url: 'lt',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: listingTypeToLabels
  }
}, {
  label: 'Auction Type',
  fe: 'auctionFormat',
  api: 'asset_event_type',
  url: 'af',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: auctionFormatConverterUpperCase
  }
},
/**
 * Asset Info
 */
{
  label: 'Property Type',
  fe: 'propertyType',
  api: 'usecode_property_type',
  url: 'pt',
  converter: {
    fe2api: arrayToApiPropertyType,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: propertyTypeConverter,
    url2label: propertyTypeConverterWithSpace
  }
}, {
  label: 'Vacant',
  fe: 'occupancyStatus',
  api: 'occ_status',
  url: 'os',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: checkOccupancy
  }
}, {
  label: 'Beds',
  fe: 'beds',
  api: 'beds_min',
  url: 'bd',
  converter: {
    fe2api: rangeToObject('beds'),
    fe2url: _.toString,
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: numberWithPlus
  }
}, {
  label: 'Baths',
  fe: 'baths',
  api: 'baths_min',
  url: 'bt',
  converter: {
    fe2api: value => {
      const bathsValue = Array.isArray(value) ? value[0] : value;
      return bathsValue || null;
    },
    fe2url: value => {
      const bathsValue = Array.isArray(value) ? value[0] : value;
      return bathsValue ? _.toString(bathsValue) : '';
    },
    url2fe: stringToArray,
    api2url: _.identity,
    url2label: numberWithPlus
  }
}, {
  // Serialized geo location param (e.g. `geoid:206037`). Lets apiToUrlValue
  // emit a location into the SRP path and urlToFe read it back. Shared with
  // the graph LCF `locations` field; values are opaque (geoid refs).
  fe: 'locations',
  api: 'locations',
  url: 'g',
  converter: {
    fe2api: _.identity,
    // Both URL builders must emit the clean path form. feToUrlValue (fe2url)
    // and apiToUrlValue (api2url) both strip the `geoid:` prefix so the
    // segment is `206059_g`, not `geoid:206059_g` (the url-key `g` already
    // denotes geoid). url2fe re-adds the prefix when reading back.
    fe2url: value => String(value).replace(/(^|,)geoid:/g, '$1'),
    url2fe: value => String(value).split(',').map(id => `geoid:${id}`).join(','),
    api2url: value => String(value).replace(/(^|,)geoid:/g, '$1')
  }
}, {
  // Year Built is not a Search (SRP) filter, but the Buying Criteria form
  // (useBuyingCriteria) drives its fields through this same schema so it can
  // produce/consume identical ListingCompatabilityFilters. Single value only
  // (minimum), mapped to the LCF `yr_bt_min` key. Inert for Search since the
  // SRP FilterState never sets a `yearBuilt` key.
  label: 'Year Built',
  fe: 'yearBuilt',
  api: 'yr_bt_min',
  url: 'yb',
  converter: {
    fe2api: value => value != null ? _.toString(value) : '',
    fe2url: value => value != null ? _.toString(value) : '',
    url2fe: value => value ? Number(value) : null,
    api2url: _.identity
  }
}, {
  label: 'Sqft Range',
  fe: 'sqft',
  api: 'sqft_range',
  url: 'sqft',
  converter: {
    fe2api: minMaxObjectToApi('sqft'),
    fe2url: minMaxObjectToString,
    url2fe: stringToMinMaxObject,
    api2url: minMaxObjectToString,
    url2label: minMaxObjectToLabel
  }
}, {
  label: 'Price Range',
  fe: 'price',
  api: 'price',
  url: 'price',
  converter: {
    fe2api: minMaxObjectToApi('price'),
    fe2url: minMaxObjectToString,
    url2fe: stringToMinMaxObject,
    api2url: minMaxObjectToString,
    url2label: minMaxObjectToLabel
  }
},
/**
 * Sort
 */
{
  fe: 'sort',
  api: 'sort',
  url: 'st',
  converter: {
    fe2api: sortObjectToString,
    fe2url: sortObjectToString,
    url2fe: stringToSortObject,
    api2url: _.identity
  }
},
/**
 * [SHOW ONLY]
 */
{
  label: 'Financing Available',
  fe: 'financingAvailable',
  api: 'fin_avl',
  url: 'y_fa',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean,
    url2label: booleanStringToBoolean
  }
}, {
  label: 'Interior Access Available',
  fe: 'accessAvailable',
  api: 'resi_ia',
  url: 'y_ia',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean,
    url2label: booleanStringToBoolean
  }
}, {
  fe: 'inspectionAllowed',
  api: 'inspection_allowed',
  url: 'y_ica',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity
  }
}, {
  label: 'Reserve Disclosed',
  fe: 'showReserve',
  api: 'show_reserve',
  url: 'y_sr',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean,
    url2label: booleanStringToBoolean
  }
}, {
  label: 'Broker Co-op',
  fe: 'brokerCoopAvailable',
  api: 'broker_coop_avail',
  url: 'y_bc',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean,
    url2label: booleanStringToBoolean
  }
}, {
  label: "No Buyer's Premium",
  fe: 'noBuyerPremiumFilter',
  api: 'buyer_premium_avail',
  url: 'y_nbp',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: noBuyerPremiumConverter,
    url2label: noBuyerPremiumConverter
  }
}, {
  label: 'Hide Ending Soon',
  fe: 'hideEndingSoon',
  api: 'hide_ending_soon',
  url: 'y_hes',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean,
    url2label: booleanStringToBoolean
  }
}, {
  label: 'Leaseback Required',
  fe: 'leasebackRequired',
  api: 'leaseback_required',
  url: 'lbr',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean,
    url2label: booleanStringToBoolean
  }
},
/**
 * [SHOW ONLY] - DEPRECATED
 */
{
  fe: 'presale',
  api: 'presale',
  url: 'y_ps',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: booleanStringToBoolean
  }
},
/**
 * [SHOW ONLY] - HIDDEN FEATURE
 */
{
  fe: 'globalSearch',
  // TODO: this is not part of Listing Compatability Filters
  // and it affects fe to api conversion. However it is used
  // in construct link from filters.
  api: 'global_search',
  // TODO: this was just global before without y_ ,  which seems wrong.
  // Make sure it doesn't break location suggestions
  url: 'y_global',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: _.identity
  }
},
/**
 * [SHOW ONLY][Mobile] Active Section
 */
{
  fe: 'mapView',
  api: 'map_vt',
  url: 'map_vt',
  converter: {
    fe2api: _.noop,
    fe2url: _.identity,
    url2fe: _.identity
  }
},
/**
 * [FE ONLY] - Selected Asset | Pin popup
 */
{
  fe: 'openedAssetId',
  api: 'oa',
  url: 'oa',
  converter: {
    fe2api: _.noop,
    fe2url: _.toString,
    url2fe: parseFloat,
    api2url: _.noop
  }
},
/**
 * Feed Properties
 */
{
  api: 'distance',
  url: 'rd',
  converter: {
    api2url: _.identity,
    url2fe: _.identity
  }
}, {
  api: 'location',
  url: 'lq',
  converter: {
    api2url: _.identity,
    url2fe: _.identity
  }
},
/**
 * Feed Properties
 */
{
  fe: 'limit',
  api: 'limit',
  url: 'rpp',
  converter: {
    fe2api: _.toString,
    fe2url: _.toString,
    url2fe: parseInt
  }
},
/**
 * Marketing Tag (Previusly 'Feature9')
 */
{
  fe: 'marketingTag',
  api: 'marketing_tags',
  url: 'mt',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: _.identity
  }
},
/**
 * Days Since First Published ("Time on Auction")
 */
{
  label: 'Time on Auction',
  fe: 'daysSinceFirstPublished',
  api: 'property_activation_date',
  url: 'dsfp',
  converter: {
    fe2api: propertyActivationDateToDateRange,
    fe2url: value => {
      // Return empty string for null/undefined to avoid breaking URL
      return value ? _.toString(value) : '';
    },
    url2fe: value => value || null,
    api2url: _.identity
  }
},
/**
 * Other
 */
{
  fe: 'sellerCode',
  api: 'seller_cd',
  url: 'sc',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArray
  }
}, {
  fe: 'useNearbySearch',
  api: 'nearby_search',
  url: 'nbs',
  converter: {
    fe2api: _.identity,
    fe2url: _.identity,
    url2fe: _.identity,
    api2url: _.identity
  }
}, {
  fe: 'nearbySearchRadius',
  api: 'nearby_search_radius',
  url: 'nsr',
  converter: {
    fe2api: parseInt,
    fe2url: _.toString,
    url2fe: parseInt,
    api2url: _.identity
  }
}, {
  api: 'ending_soon_dt_all_min',
  url: 'dt',
  converter: {
    fe2api: _.toString,
    fe2url: _.toString,
    url2fe: parseInt,
    api2url: dateStringToUrl
  }
},
/**
 *  Implicit: not serializable in URL
 */
{
  fe: 'implicitLocationCoordinates',
  api: 'implicitLocationCoordinates',
  url: NOT_IMPLEMENTED,
  converter: {
    fe2api: _.identity,
    // @ts-ignore empty function
    fe2url: () => {},
    // @ts-ignore empty function
    url2fe: () => {}
  }
},
/**
 * DEPRECATED
 */
{
  label: 'Asset Type',
  fe: 'assetType',
  api: 'usecode_product_type',
  url: 'at',
  converter: {
    fe2api: arrayToApiProductType,
    fe2url: _.join,
    url2fe: stringToArray,
    api2url: assetTypeConverter,
    url2label: assetTypeConverterWithSpace
  }
}, {
  fe: 'foreclosureBidType',
  api: 'bid_type_label',
  url: 'bl',
  converter: {
    fe2api: _.join,
    fe2url: _.join,
    url2fe: stringToArray
  }
}];
/**
 * Filters Model for transformations
 *   - Transform the object to the needed API or URL representation.
 */
class FiltersModel {
  /**
   * Transform the initial URL parameters to a suitable FE filter structure
   */

  constructor(props) {
    this._filters = Object.assign({}, props);
  }
  /**
   * Get params
   */
  get rawValue() {
    return this._filters;
  }
  get labels() {
    const labels = {
      mainDisplay: [],
      propertyAndAssetTypes: [],
      listAndAuctionTypes: [],
      showOnlyFields: []
    };
    this._filters.sqft_range = {
      min: _.get(this._filters, 'sqft_min'),
      max: _.get(this._filters, 'sqft_max')
    };
    schema.forEach(param => {
      const apiParamValue = param.api === 'is_asset_online_event' ? _.toLower(this._filters[param.api]) : this._filters[param.api];
      if (apiParamValue) {
        const labelParamValue = param.converter.url2label && param.converter.url2label(apiParamValue);
        if (labelParamValue) categorizeLabels(labels, param.label, labelParamValue);
      }
    });
    return labels;
  }
  get feToApiValue() {
    let apiFilters = {};
    schema.forEach(filter => {
      const feFilterValue = this._filters[filter.fe];
      if (feFilterValue) {
        const urlFilterValue = filter.converter.fe2api(feFilterValue);
        if (urlFilterValue) {
          const objectFiltersToSpread = ['auctionDate', 'sqft', 'beds', 'price', 'daysSinceFirstPublished'];
          if (objectFiltersToSpread.includes(filter.fe)) {
            apiFilters = {
              ...apiFilters,
              ...urlFilterValue
            };
          } else {
            apiFilters[filter.api] = urlFilterValue;
          }
        }
      }
    });
    return apiFilters;
  }
  get feToUrlValue() {
    const urlFilters = [];
    schema.forEach(filter => {
      const feFilterValue = this._filters[filter.fe];
      if (feFilterValue) {
        const urlFilterValue = filter.converter.fe2url(feFilterValue);
        if (urlFilterValue) urlFilters.push(addUrlKey(urlFilterValue, filter));
      }
    });
    return urlFilters.join('/');
  }
  get apiToUrlValue() {
    const urlParams = [];
    this._filters.sqft_range = {
      min: _.get(this._filters, 'sqft_min'),
      max: _.get(this._filters, 'sqft_max')
    };
    schema.forEach(param => {
      const apiParamValue = param.api === 'is_asset_online_event' ? _.toLower(this._filters[param.api]) : this._filters[param.api];
      if (apiParamValue) {
        const urlParamValue = param.converter.api2url?.(apiParamValue);
        if (urlParamValue) urlParams.push(addUrlKey(urlParamValue, param));
      }
    });
    return urlParams.join('/');
  }
  get lastFoundCoordinates() {
    return this._filters.lastFoundLocationCoordinates;
  }
  static #_ = _staticBlock = () => this.urlToFe = queryParams => {
    // TODO:  ZOD validation will come in here somehwere
    const feFilters = {};
    const urlFilters = {};
    let unmapped = 0;
    const urlData = decodeURIComponent(queryParams).split('/');
    urlData.shift();
    urlData.forEach(urlVal => {
      if (showOnlyFields.includes(urlVal)) {
        // Show only values
        if (urlVal === 'y_nbp') {
          urlFilters[urlVal] = 'n';
        } else {
          urlFilters[urlVal] = 'y';
        }
      } else {
        urlVal = urlVal.split('_');
        let key = urlVal.pop(); // value_key
        urlVal = urlVal.join('_');
        // [Legacy] Counties are the only url value that use '-' instead of '_'
        if (!urlVal && key && _.endsWith(key.toLowerCase(), '-county')) {
          urlVal = key.toLowerCase().replace('-county', '');
          key = 'county';
        }
        // mapped to key
        if (key && urlVal) {
          urlFilters[key] = urlVal;
        } else {
          // no key found
          urlFilters[unmapped++] = key;
        }
      }
    });
    schema.forEach(filter => {
      const urlFilterValue = urlFilters[filter.url];
      if (urlFilterValue && filter.converter.url2fe) {
        feFilters[filter.fe] = filter.converter.url2fe(urlFilterValue);
      }
    });
    return feFilters;
  };
}
_staticBlock();

/* istanbul ignore file */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
const getPropertyTypeFiltersInitialValues = filters => ({
  single_family: _.find(filters.propertyType, type => type === 'single-family') ? 'single-family' : '',
  multi_family: _.find(filters.propertyType, type => type === 'multi-family') ? 'multi-family' : '',
  condos_townhouses: _.find(filters.propertyType, type => type === 'condos-townhouses') ? 'condos-townhouses' : '',
  land: _.find(filters.propertyType, type => type === 'land') ? 'land' : ''
});
const getPropertyInfoFiltersInitialValues = filters => ({
  beds: getMinSelectInputOptions(filters.beds),
  baths: getMinSelectInputOptions(filters.baths),
  sqft_min: filters.sqft.min ? `${filters.sqft.min}` : '',
  sqft_max: filters.sqft.max ? `${filters.sqft.max}` : '',
  price_min: filters.price.min ? `${filters.price.min}` : '',
  price_max: filters.price.max ? `${filters.price.max}` : ''
});
const getAssetTypeFiltersInitialValues = filters => ({
  bankOwned: _.find(filters.assetType, type => type === 'bank-owned') ? 'bankOwned' : '',
  foreclosure: _.find(filters.assetType, type => type === 'foreclosures') ? 'foreclosure' : '',
  shortSales: _.find(filters.assetType, type => type === 'short-sales') ? 'shortSales' : '',
  privateSeller: _.find(filters.assetType, type => type === 'private-seller') ? 'privateSeller' : ''
});
const getAuctionTypeFiltersInitialValues = filters => ({
  online: _.find(filters.auctionFormat, type => type === 'online') ? 'online' : '',
  live: _.find(filters.auctionFormat, type => type === 'in_person') ? 'in_person' : '',
  remoteBid: _.find(filters.auctionFormat, type => type === 'remote_bid') ? 'remote_bid' : '',
  directOffer: _.find(filters.auctionFormat, type => type === 'direct_offer') ? 'direct_offer' : '',
  offer: _.find(filters.auctionFormat, type => type === 'offer') ? 'offer' : ''
});
const getConditionFiltersInitialvalues = filters => ({
  access_available: filters.accessAvailable ? interiorAccessSelected : '',
  financing_available: filters.financingAvailable ? financingAvailableSelected : '',
  occupancy_status: _.find(filters.occupancyStatus, type => type === 'vacant') ? vacantOccupancyStatus[0] : '',
  inspectionAllowed: filters.inspectionAllowed ? 'y' : ''
});
const getFeaturedFiltersInitialValues = (filters, defaultOverride = {}) => {
  const activatedFeaturedFilters = {};
  Object.values(defaultOverride).forEach(value => {
    if ((filters.marketingTag || '').split(',').includes(value.toLowerCase())) {
      activatedFeaturedFilters[value] = value;
    }
  });
  return activatedFeaturedFilters;
};
const getMoreFiltersInitialValues = filters => ({
  broker_coop_available: filters.brokerCoopAvailable ? brokerCoopAvailableSelected : '',
  listing_type:
  // TODO: double check this
  _.difference(allListingTypesSelected, filters.listingType).length === 0 ? moreAllListingFilters : '',
  no_buyer_premium_filter: filters.noBuyerPremiumFilter ? noBuyerPremiumFilterSelected : '',
  presale: filters.presale ? maoFilterSelected : '',
  show_reserve: filters.showReserve ? showReserveSelected : '',
  hide_ending_soon: filters.hideEndingSoon ? hideEndingSoonSelected : '',
  leaseback_required: filters.leasebackRequired === 'n' ? 'n' : ''
});
/*
 * Min Select Input Options
 */
const getMinSelectInputOptions = value => {
  if (value) {
    // If it's an array of strings, convert the first element to number
    const numValue = Array.isArray(value) ? Number(value[0]) : value;
    // Check if we have a valid number after extraction/conversion
    if (!isNaN(numValue) && numValue !== undefined && numValue !== null) {
      if (numValue >= 4) {
        return '4';
      } else if (numValue >= 3) {
        return '3';
      } else if (numValue >= 2) {
        return '2';
      } else if (numValue >= 1) {
        return '1';
      } else {
        return 'any';
      }
    }
  }
  return 'any';
};
const getDefaultFeaturedFilters = marketingTagsConfig => {
  const featuredFilterDefaultOverride = {};
  (marketingTagsConfig || []).forEach(marketingTagObj => {
    featuredFilterDefaultOverride[marketingTagObj.tag] = marketingTagObj.tag;
  });
  return featuredFilterDefaultOverride;
};
const convertFiltersToUiFilterGroups = (filters, marketingTagsConfig = []) => {
  const {
    useNearbySearch,
    nearbySearchRadius,
    sort,
    search,
    state,
    city,
    county,
    zip,
    locations,
    globalSearch,
    mapView,
    isMapShown,
    mapBounds,
    userCoords,
    geoLocation,
    listingType,
    reserveReduced,
    limit,
    offset,
    sellerCode,
    foreclosureBidType,
    openedAssetId
  } = filters;
  return {
    propertyTypeFilterGroup: getPropertyTypeFiltersInitialValues(filters),
    propertyInfoFilterGroup: getPropertyInfoFiltersInitialValues(filters),
    conditionFilterGroup: getConditionFiltersInitialvalues(filters),
    auctionTypeFilterGroup: getAuctionTypeFiltersInitialValues(filters),
    assetTypeFilterGroup: getAssetTypeFiltersInitialValues(filters),
    moreFilterGroup: getMoreFiltersInitialValues(filters),
    featuredFilterGroup: getFeaturedFiltersInitialValues(filters, getDefaultFeaturedFilters(marketingTagsConfig)),
    nearbySearchFilterGroup: {
      useNearbySearch,
      nearbySearchRadius,
      sort
    },
    locationFilterGroup: {
      search,
      state,
      city,
      county,
      zip,
      locations,
      globalSearch
    },
    mapFilterGroup: {
      openedAssetId,
      mapView,
      isMapShown,
      mapBounds,
      userCoords,
      geoLocation
    },
    otherFilterGroup: {
      listingType,
      reserveReduced,
      limit,
      offset,
      sellerCode,
      foreclosureBidType
    }
  };
};
const API_LOCATION_FILTERS = ['geo_location_box', 'geo_location', 'locations', 'property_city', 'property_county', 'property_state', 'property_zip', 'search'];
const stripLocationFiltersIfGlobal = filters => {
  if (filters.global_search === 'y') {
    for (let i = 0; i < API_LOCATION_FILTERS.length; i++) {
      delete filters[API_LOCATION_FILTERS[i]];
    }
    delete filters.nearby_search;
  }
  // do not pass this to api
  delete filters.global_search;
};
const converFiltersToListingCompatabilityFilters = (filters, isValidForNearbySearch) => {
  const newFilters = new FiltersModel(filters);
  const listingCompatibilityFilters = newFilters.feToApiValue;
  // TODO
  listingCompatibilityFilters.version = 1;
  listingCompatibilityFilters.offset = filters.offset;
  listingCompatibilityFilters.limit = filters.limit;
  // TODO:
  // this is to keep backwards compatibility with the way url
  // currently behaves in srp. If All Locations selected  (which means global search) the y_nbs remains
  // in the url although it's not sent to the api.
  if (!isValidForNearbySearch) {
    delete listingCompatibilityFilters.nearby_search;
    delete listingCompatibilityFilters.nearby_search_radius;
  }
  // TODO:
  // another case that I think should be handled in processFilters function before
  // filters get to construct url step, mainly because filters that
  // are inactive should not be in the url. We're keeping this here for now to ensure
  // backwards compat with srp , since some of the qa automation tests test urls such as
  // /residential/OH/y_global/ (shoudln't have state in here if global is on and vice versa)
  stripLocationFiltersIfGlobal(listingCompatibilityFilters);
  return listingCompatibilityFilters;
};
/**
 * Pass in an object and an ordered list of keys and it returns the first key that's doesn't have an empty value
 */
const findFirstNonEmptyKey = (obj, keys) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined && value !== null && value !== '') {
        return key;
      }
    }
  }
  return null;
};
/**
 * Gets first location param key that's not empty, e.g. 'property_city'
 */
const getLocationParamKey = listingFilters => findFirstNonEmptyKey(listingFilters, LOCATION_PARAMS);
/**
 * Gets the first location param value that's not empty, e.g. 'chicago'
 */
const getLocationParamValue = listingFilters => {
  const key = getLocationParamKey(listingFilters);
  return key ? listingFilters[key] : null;
};
/**
 * Based on the first param that's not empty, returns corresponding API key e.g.
 * 'seller_property.municipality.keyword'
 */
const getLocationAPIKey = listingFilters => {
  const key = getLocationParamKey(listingFilters);
  return key ? PARAM_TO_API_FIELD_MAP[key] : null;
};
const formatAggregation = ({
  listings,
  listingFilters,
  collectionResult,
  locationAggregateField,
  requiresLocationAggregate
}) => {
  // This is where we grab the result of the aggregation.  Only care about the primary location we're searching for.
  if (requiresLocationAggregate) {
    // NOTE: This is nullable so we protect against that
    const filteredAggregation = collectionResult.aggregation || {
      [`${locationAggregateField}_count`]: {}
    };
    // TODO: Try to fetch by normalized property city key to handle dashes, spaces, casing issues etc. Try chase down backend logic for this.
    const normalizedEntry = getLocationParamValue(listingFilters).toLowerCase();
    listings.nearbyTotal = filteredAggregation[`${locationAggregateField}_count`][normalizedEntry] || 0;
  }
  const propertyAggregation = collectionResult?.aggregation || {
    [`${AGGREGATE_FIELDS.property_type}_count`]: {}
  };
  const remoteBidAggregation = collectionResult?.aggregation || {
    [`${AGGREGATE_FIELDS.remote_bid_assets}_count`]: {}
  };
  listings.propertyTypeTotals = propertyAggregation[`${AGGREGATE_FIELDS.property_type}_count`];
  listings.remoteBideTotals = remoteBidAggregation[`${AGGREGATE_FIELDS.remote_bid_assets}_count`];
  return listings;
};
const formatLocationSearch = (city, state, county, zip) => {
  if (city && state) return [city, state].join(', ');
  if (county && state) return [county + ' county', state].join(', ');
  if (state && !zip) return state;
  return zip;
};
/**
 * Only city and zip searches are valid for geo distance nearby searches. Even though we may have a coordinates point
 * for a state, for example, we do not want to use it.
 */
const getIsValidForNearbySearch = filters => filters.useNearbySearch === 'y' && filters.globalSearch !== 'y' && Boolean(!_.isEmpty(filters.city) || !_.isEmpty(filters.zip) || !_.isEmpty(filters.county));
const getIsSortByDistance = filters => filters.sort.type.includes(SORT_TYPES.distance);

const FILTER_BUCKETS = {
  FILTERS: 'FILTERS',
  SORTS: 'SORTS',
  SEARCHES: 'SEARCHES'
};
const FILTER_TRACKING_METRIC_NAME = 'search_filter_changed';
const defaultProcessedFilterObject = {
  name: '',
  value: null,
  bucket: ''
};
const LOCATION_FILTER_PRECEDENCE = ['city', 'county', 'state'];
const TRACKED_FILTER_NAME_BLOCK_LIST = new Set([...LOCATION_FILTER_PRECEDENCE, 'implicit_location_coordinates', 'offset']);
// TODO: filter out saved searches
// send sort to it's own function if we detect that it's sort bucket, remove specialHandling totally
const FILTER_FIELD_TO_BUCKET_MAPPING = {
  map_bounds: {
    bucket: FILTER_BUCKETS.SEARCHES,
    clearValue: true
  },
  city: {
    bucket: FILTER_BUCKETS.SEARCHES,
    clearValue: true
  },
  state: {
    bucket: FILTER_BUCKETS.SEARCHES
  },
  county: {
    bucket: FILTER_BUCKETS.SEARCHES
  },
  zip: {
    bucket: FILTER_BUCKETS.SEARCHES,
    clearValue: true
  },
  text: {
    bucket: FILTER_BUCKETS.SEARCHES
  },
  // does this ever get sent?
  search: {
    bucket: FILTER_BUCKETS.SEARCHES,
    clearValue: true
  },
  property_type: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  asset_type: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  auction_format: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  occupancy_status: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  financing_available: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  listing_type: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  hide_ending_soon: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  access_available: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  usenearby_search: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  presale: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  no_buyer_premium_filter: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  broker_coop_available: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  show_reserve: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  beds: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  baths: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  sqft: {
    bucket: FILTER_BUCKETS.FILTERS,
    clearValue: true
  },
  nearby_search_radius: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  use_nearby_search: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  auction_date: {
    bucket: FILTER_BUCKETS.SORTS
  },
  newly_added: {
    bucket: FILTER_BUCKETS.SORTS
  },
  address: {
    bucket: FILTER_BUCKETS.SORTS
  },
  price: {
    bucket: FILTER_BUCKETS.SORTS
  },
  distance: {
    bucket: FILTER_BUCKETS.FILTERS
  },
  marketing_tag: {
    bucket: FILTER_BUCKETS.FILTERS
  }
};
const getFilterBucketFromFilterName = filterName => FILTER_FIELD_TO_BUCKET_MAPPING[filterName]?.bucket;
const getLocationTrackingObject = newFilters => {
  const keyToExtract = _.find(LOCATION_FILTER_PRECEDENCE, key => _.get(newFilters, key) && _.get(newFilters, key).length > 0);
  if (keyToExtract && newFilters[keyToExtract]) {
    return {
      name: keyToExtract,
      value: '',
      bucket: FILTER_BUCKETS.SEARCHES,
      event: FILTER_TRACKING_METRIC_NAME
    };
  }
  return null;
};
// we have different requirements on what counts as a positive difference based on the actual field
// this particular case is to see if every value in the sqft object is null which, it can come in like {min: null, max: null} or {min: 948, max: null}
// we only want the latter case to count as a keyChangedPositively
const allObjectValuesEmpty = obj => !Object.values(obj).some(x => Boolean(x));
const processCommaSeparatedStringDiff = (newValue, oldValue) => {
  // to handle marketing tags which are stored as a comma separated string
  return newValue.split(',').filter(element => !oldValue.split(',').includes(element)) || [];
};
/**
 *
 * Function which compares filter differences in order to track the filter changes in a standardized manner in datadog.
 *
 * @param {*} keysChanged array of keys changed, we filter for ones that correspond to truthy values and then use the first only
 * @param {*} newFilters new filters object, used to do a diff of filters
 * @param {*} oldFilters old filter object from redux store, compared against new filters to find changed values
 */
const processFilterChanged = (keysChanged, previousFilters, currentFilters) => {
  const oldFilters = _.clone(previousFilters);
  const newFilters = _.clone(currentFilters);
  const singularFilterTurnedOn = {
    ...defaultProcessedFilterObject
  };
  // handle location tracking first as we remove those values after tracking
  const locationTrackingObject = getLocationTrackingObject(newFilters);
  if (locationTrackingObject) return locationTrackingObject;
  // omit the location fields from new and old filters
  const newFiltersTrimmed = _.omit(newFilters, ...Array.from(TRACKED_FILTER_NAME_BLOCK_LIST));
  const oldFiltersTrimmed = _.omit(oldFilters, ...Array.from(TRACKED_FILTER_NAME_BLOCK_LIST));
  // TODO: how is detailedDiff from deep-object-diff useful here?
  const keysChangedPositively = keysChanged.filter(key => {
    if (Array.isArray(newFiltersTrimmed[key])) {
      const newArrayFilterValue = newFiltersTrimmed[key];
      const oldArrayFilterValue = oldFiltersTrimmed[key];
      if (!Array.isArray(oldArrayFilterValue)) {
        return newArrayFilterValue.length > 0;
      }
      const oldArray = oldArrayFilterValue;
      return !_.isEmpty(newArrayFilterValue?.filter(x => !oldArray.includes(x)));
    } else if (_.isObject(newFiltersTrimmed[key])) {
      const newObjectFilterValue = newFiltersTrimmed[key];
      return !allObjectValuesEmpty(newObjectFilterValue);
    } else if (typeof newFiltersTrimmed[key] === 'string') {
      const newStringFilterValue = newFiltersTrimmed[key];
      const oldStringFilterValue = String(oldFiltersTrimmed[key]);
      if (newStringFilterValue.includes(',') || (oldStringFilterValue || '').includes(',')) {
        return !_.isEmpty(processCommaSeparatedStringDiff(newStringFilterValue, oldStringFilterValue));
      } else {
        return !_.isEmpty(newStringFilterValue) || newStringFilterValue;
      }
    } else {
      return !_.isEmpty(newFiltersTrimmed[key]) || newFiltersTrimmed[key];
    }
  });
  const key = keysChangedPositively[0];
  const snakeCaseKey = _.snakeCase(key);
  const config = FILTER_FIELD_TO_BUCKET_MAPPING[snakeCaseKey];
  if (Array.isArray(newFiltersTrimmed[key])) {
    let arrValue = newFiltersTrimmed[key];
    if (arrValue.length) {
      arrValue = arrValue[0];
    }
  }
  if (key === 'sort' && newFiltersTrimmed[key]) {
    const sortFilter = newFiltersTrimmed[key];
    singularFilterTurnedOn.name = sortFilter.type;
    singularFilterTurnedOn.value = sortFilter.order;
    singularFilterTurnedOn.bucket = FILTER_BUCKETS.SORTS;
    singularFilterTurnedOn.event = FILTER_TRACKING_METRIC_NAME;
  } else {
    if (key) {
      if (config) {
        // only move forward with tracking if we have a config for it
        singularFilterTurnedOn.name = snakeCaseKey;
        singularFilterTurnedOn.value = config.clearValue ? '' : newFiltersTrimmed[key];
        singularFilterTurnedOn.bucket = getFilterBucketFromFilterName(snakeCaseKey);
        singularFilterTurnedOn.event = FILTER_TRACKING_METRIC_NAME;
      } else {
        singularFilterTurnedOn.name = snakeCaseKey;
        singularFilterTurnedOn.event = `untracked_${FILTER_TRACKING_METRIC_NAME}`;
      }
    }
  }
  return singularFilterTurnedOn;
};
const searchResultBuckets = {
  empty: 'empty',
  small: 'small',
  medium: 'medium',
  large: 'large'
};
const getResultGroup = total => {
  if (total === 0) return searchResultBuckets.empty;
  if (total <= 20) return searchResultBuckets.small;
  if (total <= 50) return searchResultBuckets.medium;
  return searchResultBuckets.large;
};

/* istanbul ignore file */
const trackSearchResultBuckets = listings => {
  const assetTotals = {
    total: listings.total_count || 0,
    nearbyTotal: listings.nearbyTotal || 0
  };
  blueprintInstrument.increment('listing_search_result_count_buckets', 1, [`result_group:${getResultGroup(assetTotals.total || 0)}`, `variant:stable`]);
};
const trackFilterChanged = (previousFilters, currentFilters, filterDefinitions = []) => {
  const keysChanged = getObjectDiff(previousFilters, currentFilters);
  const filterData = processFilterChanged(keysChanged, previousFilters, currentFilters);
  const metricName = filterData?.event;
  if (filterData && metricName && filterData?.name) {
    const filterName = filterData?.name || '';
    const filterValue = filterData?.value || '';
    const filterBucket = filterData?.bucket || '';
    const filterLabels = [`filter_name:${filterName}`, `filter_value:${filterValue}`, `filter_bucket:${filterBucket}`, `variant:stable`];
    blueprintInstrument.increment(metricName, 1, filterLabels);
  }
  // Return tracked changes with human-readable values, reusing the same
  // definition->label mapper the applied-filter chips use.
  if (filterData?.name && filterData.bucket) {
    const changedKey = keysChanged.find(k => snakeCase(String(k)) === filterData.name);
    const definition = changedKey ? filterDefinitions.map(group => getFilterDefinitionByName(group, String(changedKey))).find(Boolean) : null;
    if (!changedKey || !definition) return null;
    // TODO(MERCH-95): This only emits on POSITIVE filter changes (filter
    // turned ON), mirroring the Datadog metric which is driven by
    // processFilterChanged's `keysChangedPositively` / `singularFilterTurnedOn`
    // logic. Deselecting a filter (e.g. show_reserve 'y' -> false) produces no
    // `change`, so no Amplitude event fires on removal — even though
    // previousValue/currentValue could express it. Tracking negative changes
    // requires decoupling this return path from the positive-only filterData
    // (derive the changed key from the delta instead) and handling the
    // reset-all branch separately, without disturbing the shared Datadog
    // metric. Larger refactor — deferred.
    return {
      name: filterData.name,
      previousValue: getPartsFromFilterAndValue(definition, previousFilters[changedKey]),
      currentValue: getPartsFromFilterAndValue(definition, currentFilters[changedKey])
    };
  }
  return null;
};
/*
 * https://stackoverflow.com/questions/31683075/how-to-do-a-deep-comparison-between-2-objects-with-lodash
 * Compare two objects by reducing an array of keys in obj1, having the
 * keys in obj2 as the intial value of the result. Key points:
 *
 * - All keys of obj2 are initially in the result.
 *
 * - If the loop finds a key (from obj1, remember) not in obj2, it adds
 *   it to the result.
 *
 * - If the loop finds a key that are both in obj1 and obj2, it compares
 *   the value. If it's the same value, the key is removed from the result.
 */
const getObjectDiff = (obj1, obj2) => {
  const diff = Object.keys(obj1).reduce((result, key) => {
    const typedKey = key;
    const resultKeyIndex = result.indexOf(typedKey);
    if (!isEqual(obj1[typedKey], obj2[typedKey]) && resultKeyIndex < 0) {
      result.push(typedKey);
    }
    return result;
  }, []);
  return diff;
};

/* istanbul ignore file */
// NOTE: order of second tuple matters - we may want to change that but it works for now
// const zodListingType = z.union([
//   z.tuple([z.literal('active')]),
//   z.tuple([z.literal('pending'), z.literal('closed'), z.literal('canceled')]),
// ])
const zodListingType = z.union([z.tuple([z.literal('active')]), z.union([z.tuple([z.literal('pending'), z.literal('closed'), z.literal('canceled')]), z.tuple([z.literal('pending'), z.literal('canceled'), z.literal('closed')]), z.tuple([z.literal('closed'), z.literal('pending'), z.literal('canceled')]), z.tuple([z.literal('closed'), z.literal('canceled'), z.literal('pending')]), z.tuple([z.literal('canceled'), z.literal('pending'), z.literal('closed')]), z.tuple([z.literal('canceled'), z.literal('closed'), z.literal('pending')])])]);
// ROMAN: I don't need these as I'm just using zodAllFilters (see buyerListingSearchFilters.tsx)
// const zodListingType = z.array(
//   z.union([
//     z.literal('active'),
//     z.literal('sold'),
//     z.literal('pending'),
//     z.literal('canceled'),
//   ])
// )
// const zodListingType = z.custom<'ZodListingType'>((val: string[]) => {
//   // ['active'] | ['closed', 'pending', 'canceled']
//   if (val.length === 0) {
//     return false
//   }
//   if (val.length === 1 && val[0] === 'active') {
//     return true
//   }
//   if (val.length === 3) {
//     const targetSet = new Set(['closed', 'pending', 'canceled'])
//     return val.every(el => targetSet.has(el))
//   }
//   return false
// })
const zodYesOrFalse = z.union([z.literal('y'), z.literal(false)]);
const zodFilterSchema = z.object({
  search: z.string(),
  state: z.string().array(),
  city: z.string().array(),
  county: z.string().array(),
  zip: z.string().array(),
  // Serialized geoid locations (e.g. `geoid:4005226,geoid:4002893`). Source of
  // truth for the geoid search paradigm; coexists with named fields during the
  // transition. Defaulted (not required) so URLs without a geoid still parse,
  // and so safeParse never strips it when present.
  locations: z.string().default(''),
  mapBounds: z.string().array(),
  userCoords: z.string().array(),
  geoLocation: z.string().array(),
  listingType: zodListingType,
  auctionFormat: z.array(z.union([z.literal('online'), z.literal('in_person'), z.literal('remote_bid'), z.literal('direct_offer'), z.literal('offer')])),
  propertyType: z.array(z.union([z.literal('single-family'), z.literal('multi-family'), z.literal('condos-townhouses'), z.literal('land')])),
  occupancyStatus: z.array(z.literal('vacant')),
  nearbySearchRadius: z.union([z.literal(5), z.literal(10), z.literal(25), z.literal(50), z.literal(100)]).nullable(),
  beds: z.string().array().nullable(),
  baths: z.string().array().nullable(),
  // Year Built (minimum). Not an SRP panel filter, but the Buying Criteria form
  // drives it into the URL (`yb`) -> yr_bt_min. Must be in the schema or
  // safeParse strips it and the year-built filter silently drops.
  yearBuilt: z.number().nullable().default(null),
  sqft: z.object({
    min: z.number().positive().nullable(),
    max: z.number().positive().nullable()
  }),
  price: z.object({
    min: z.number().positive().nullable(),
    max: z.number().positive().nullable()
  }),
  sort: z.object({
    type: z.union([z.literal('resi_sort_v2'), z.literal('resi_sort_v2_desc'), z.literal('asset_activation_dt_sort'), z.literal('asset_activation_dt_sort_desc'), z.literal('resi_property_address'), z.literal('auction_date_order'), z.literal('beds'), z.literal('baths'), z.literal('sqft'), z.literal('valuation_estimate'), z.literal('distance')]),
    order: z.union([z.literal('asc'), z.literal('desc')])
  }),
  financingAvailable: zodYesOrFalse,
  accessAvailable: zodYesOrFalse,
  inspectionAllowed: zodYesOrFalse,
  showReserve: zodYesOrFalse,
  brokerCoopAvailable: zodYesOrFalse,
  reserveReduced: z.boolean(),
  noBuyerPremiumFilter: z.union([z.literal('n'), z.literal(false)]),
  hideEndingSoon: zodYesOrFalse,
  leasebackRequired: z.literal('n').nullable(),
  presale: zodYesOrFalse,
  globalSearch: zodYesOrFalse,
  limit: z.number().nullable(),
  offset: z.number().nonnegative(),
  sellerCode: z.string().array(),
  useNearbySearch: z.union([z.literal('y'), z.literal('n'), z.literal(false)]),
  assetType: z.array(z.union([z.literal('bank-owned'), z.literal('newly-foreclosed'), z.literal('foreclosures'), z.literal('private-seller'), z.literal('short-sales')])),
  foreclosureBidType: z.string().array(),
  marketingTag: z.string(),
  openedAssetId: z.union([z.string(), z.number()]).nullable(),
  // mapView for mobile view
  mapView: z.union([z.boolean(), z.literal('y')]),
  // for desktop view  TODO: maybe this filter should be in app state
  isMapShown: z.boolean(),
  // Days since first published filter (Time on Auction)
  daysSinceFirstPublished: z.string().nullable()
});
// ROMAN: I don't need these as I'm just using zodAllFilters (see buyerListingSearchFilters.tsx)
// export const zodPropertyTypeFilters = zodFilterSchema
//   .pick({ propertyType: true })
//   .strict()
// export const zodPropertyInfoFilters = zodFilterSchema
//   .pick({ beds: true, baths: true, sqft: true })
//   .strict()
// export const zodAuctionTypeFilters = zodFilterSchema
//   .pick({ auctionFormat: true })
//   .strict()
// export const zodAssetTypeFilters = zodFilterSchema
//   .pick({ assetType: true })
//   .strict()
// export const zodConditionFilters = zodFilterSchema
//   .pick({
//     financingAvailable: true,
//     accessAvailable: true,
//     occupancyStatus: true,
//   })
//   .strict()
// export const zodMoreFilters = zodFilterSchema
//   .pick({
//     hideEndingSoon: true,
//     listingType: true,
//     brokerCoopAvailable: true,
//     showReserve: true,
//     noBuyerPremiumFilter: true,
//     presale: true,
//   })
//   .strict()
// export const zodFeaturedFilters = zodFilterSchema
//   .pick({ marketingTag: true })
//   .strict()
// ROMAN: We probably don't need these as well if you just use zodAllFilters (see buyerListingSearchFilters.tsx)
zodFilterSchema.pick({
  useNearbySearch: true,
  nearbySearchRadius: true,
  sort: true
}).strict();
zodFilterSchema.pick({
  search: true,
  state: true,
  city: true,
  county: true,
  zip: true,
  locations: true,
  globalSearch: true
}).strict();
zodFilterSchema.pick({
  mapView: true,
  isMapShown: true,
  mapBounds: true,
  geoLocation: true,
  userCoords: true
}).strict();
zodFilterSchema.pick({
  listingType: true,
  reserveReduced: true,
  limit: true,
  offset: true,
  sellerCode: true,
  foreclosureBidType: true,
  openedAssetId: true
}).strict();
zodFilterSchema.strict();

/**
 * Filter Group Definitions
 */
const transformMarketingConfigToOptions$1 = marketingConfig => marketingConfig.sort((a, b) => a.configuration.srp.order - b.configuration.srp.order).map(item => ({
  value: item.tag.toLowerCase(),
  label: item.configuration.srp.label,
  tooltip: item.configuration.srp.is_tooltip_active && item.configuration.srp.tooltip?.length ? item.configuration.srp.tooltip : null
}));
const propertyFilterDefinition = new FilterGroupBuilder('property', 'Property').description('Filter by property details').items([new FilterBuilder('beds', 'singleSelect', 'number').label('Beds').styleIntent({
  width: 'half'
}).options([{
  value: null,
  label: 'Any'
}, {
  value: 1,
  label: '1+ Beds'
}, {
  value: 2,
  label: '2+ Beds'
}, {
  value: 3,
  label: '3+ Beds'
}, {
  value: 4,
  label: '4+ Beds'
}, {
  value: 5,
  label: '5+ Beds'
}]).build(), new FilterBuilder('baths', 'singleSelect', 'number').label('Baths').styleIntent({
  width: 'half'
}).options([{
  value: null,
  label: 'Any'
}, {
  value: 1,
  label: '1+ Baths'
}, {
  value: 2,
  label: '2+ Baths'
}, {
  value: 3,
  label: '3+ Baths'
}, {
  value: 4,
  label: '4+ Baths'
}, {
  value: 5,
  label: '5+ Baths'
}]).build(), new FilterBuilder('sqft', 'range', 'number').label('Square Feet').shortLabel('Sqft').range({
  min: 0,
  max: 1000000
}).build(), new FilterBuilder('propertyType', 'multiSelect', 'string[]').label('Type').description('Filter by property type').options([{
  value: ['single-family'],
  label: 'Single Family',
  icon: 'house'
}, {
  value: ['multi-family'],
  label: 'Multi Family',
  icon: 'apartment'
}, {
  value: ['condos-townhouses'],
  label: 'Condo, single unit',
  icon: 'arrow-right-to-city'
}, {
  value: ['land'],
  label: 'Land',
  icon: 'trees'
}]).build()]).build();
const assetOptions$1 = [{
  value: ['bank-owned', 'newly-foreclosed'],
  label: 'REO Bank Owned'
}, {
  value: ['foreclosures'],
  label: 'Foreclosure Homes'
}, {
  value: ['private-seller'],
  label: 'Private Seller'
}];
/**
 * Gets the asset type filter definition...
 * This is a function as it allows for optional optionsDecorator to be passed in to modify the options.
 * @param optionsDecorator
 * @returns
 */
const getAssetTypeFilterDefinition = optionsDecorator => {
  const decoratedOptions = optionsDecorator ? optionsDecorator(assetOptions$1, 'assetType') : assetOptions$1;
  return new FilterGroupBuilder('assetType', 'Asset Type').items([new FilterBuilder('assetType', 'multiSelect', 'string[]').label(null).options(decoratedOptions).build()]).build();
};
const auctionFormatFilterDefinition = new FilterGroupBuilder('auctionFormat', 'Buying Type').items([new FilterBuilder('auctionFormat', 'multiSelect', 'string[]').label(null).options([{
  value: ['online'],
  label: 'Online Auction'
}, {
  value: ['in_person'],
  label: 'In Person Auction'
}, {
  value: ['remote_bid'],
  label: 'Remote Bid'
}, {
  value: ['direct_offer'],
  label: 'Direct Offer'
}]).build()]).build();
const conditionFilterDefinition = new FilterGroupBuilder('condition', 'Condition').description('Most properties on Auction.com are sold "Cash only" and "As-is," and many are occupied. Use these filters to potentially reduce your risk.').styleIntent({
  compact: true
}).items([new FilterBuilder('occupancyStatus', 'multiSelect', 'string[]').options([{
  value: ['vacant'],
  label: 'Vacant',
  description: 'Not all foreclosure properties are empty, but for these no eviction is needed.  '
}]).build(), new FilterBuilder('accessAvailable', 'boolean', 'string').label('Interior Access Available').description('Schedule a visit to view the property').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : null
}).build(), new FilterBuilder('financingAvailable', 'boolean', 'string').label('Eligible for Financing').description('These could be financed with a residential mortgage.').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : null
}).build()]).build();
/* istanbul ignore next */
const getMarketingTagFilterDefinition$1 = marketingTagConfig => new FilterGroupBuilder('marketingTag', 'Featured').items([new FilterBuilder('marketingTag', 'multiSelect', 'string').options(transformMarketingConfigToOptions$1(marketingTagConfig)).converterOverrides({
  toViewModelValue: v => {
    if (typeof v === 'string') {
      return v.split(',');
    }
    return v ? [v.toString()] : [];
  },
  toAppModelValue: v => {
    if (Array.isArray(v)) {
      return v.join(',');
    } else {
      return v;
    }
  }
}).build()]).build();
const moreFiltersFilterDefinition = new FilterGroupBuilder('moreFilters', 'More Filters').items([new FilterSectionBuilder('hideProperties').label('Hide properties that:').filters([new FilterBuilder('hideEndingSoon', 'boolean', 'boolean').label('Auction Ending Soon').description('Ending in the next 5 days').styleIntent({
  width: 'full'
}).converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : null
}).build()]).build(), new FilterSectionBuilder('showProperties').label('Show only properties that have/are:').styleIntent({
  compact: true
}).filters([new FilterBuilder('listingType', 'boolean', 'string[]').label('Sold, Pending, or Removed').converterOverrides({
  toViewModelValue: v => arraysAreEqual(v, ['pending', 'closed', 'canceled']),
  toAppModelValue: v => v ? ['pending', 'closed', 'canceled'] : ['active']
}).build(), new FilterBuilder('brokerCoopAvailable', 'boolean', 'string').label('Broker Coop').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : null
}).build(), new FilterBuilder('showReserve', 'boolean', 'string').label('Reserve Disclosed').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : null
}).build(), new FilterBuilder('noBuyerPremiumFilter', 'boolean', 'string').label("No Buyer's Premium").converterOverrides({
  toViewModelValue: v => v === 'n',
  toAppModelValue: v => v ? 'n' : null
}).build(), new FilterBuilder('presale', 'boolean', 'string').label('First Look').description('Learn more (TBD LINK HERE)').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : null
}).build()]).build()]).build();
const definitionGenerator$2 = config => {
  return [propertyFilterDefinition, getAssetTypeFilterDefinition(config?.optionsDecorator), auctionFormatFilterDefinition, conditionFilterDefinition, ...(config?.marketingTagConfig ? [getMarketingTagFilterDefinition$1(config.marketingTagConfig)] : []), moreFiltersFilterDefinition];
};
/**
 * This ugliness is due to how stateFilters handles the value of the assetType filter.
 * @param value
 * @returns
 */
const getAssetType = value => {
  return match(value).with(P.when(arr => arraysAreEqual(arr, ['bank-owned', 'newly-foreclosed'])), () => graph.enums.AssetType.BankOwned).with(P.when(arr => arraysAreEqual(arr, ['foreclosures'])), () => graph.enums.AssetType.Foreclosure).with(P.when(arr => arraysAreEqual(arr, ['private-seller'])), () => graph.enums.AssetType.PrivateSeller).with(P.when(arr => arraysAreEqual(arr, ['short-sale'])), () => graph.enums.AssetType.ShortSale).with(P.when(arr => arraysAreEqual(arr, ['rentals'])), () => graph.enums.AssetType.Rentals).otherwise(() => graph.enums.AssetType.BankOwned);
};

/* istanbul ignore file */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
const getFiltersDefaultState = (maxResultsAllowed, defaultNearbySearchRadius, sortExperimentFlag) => ({
  search: '',
  state: [],
  city: [],
  county: [],
  zip: [],
  mapBounds: [],
  userCoords: [],
  geoLocation: [],
  listingType: ['active'],
  auctionFormat: [],
  propertyType: [],
  occupancyStatus: [],
  nearbySearchRadius: defaultNearbySearchRadius,
  beds: null,
  baths: null,
  sqft: {
    min: null,
    max: null
  },
  price: {
    min: null,
    max: null
  },
  sort: {
    type: sortExperimentFlag === 'treatment' ? SORT_TYPES.default : SORT_TYPES.auctionStartDate,
    order: SORT_ORDERS.asc
  },
  financingAvailable: false,
  accessAvailable: false,
  inspectionAllowed: false,
  showReserve: false,
  brokerCoopAvailable: false,
  reserveReduced: false,
  noBuyerPremiumFilter: false,
  hideEndingSoon: false,
  leasebackRequired: null,
  openedAssetId: null,
  presale: false,
  globalSearch: false,
  limit: maxResultsAllowed,
  offset: 0,
  sellerCode: [],
  useNearbySearch: 'y',
  assetType: [],
  foreclosureBidType: [],
  mapView: false,
  isMapShown: true,
  marketingTag: '',
  daysSinceFirstPublished: null
});
const logger$c = coreLogger.forModule('blueprint:listingsSearch');
const attemptToLocateGeoCenter = async filters => {
  /**
   * NOTE: If we mount and have a specific location in the filters, with nearby search enabled, but no coordinates from
   * which to search on, fetch that here. We need this to implmenent nearby search, since we need some reference geoLocation
   * to be able to define what is near.
   *
   * NOTE: Importantly nearby only works exclusively on zip, city _or_ county, which is why this logic works for our current
   * implementation
   */
  let suggestionType = '';
  if (filters.zip.length) {
    suggestionType = 'zip';
  } else if (filters.city.length) {
    suggestionType = 'city';
  } else if (filters.county.length) {
    suggestionType = 'county';
  } else {
    return null;
  }
  const cityCounty = suggestionType === 'county' ? filters.county && `${filters.county[0]} County` : filters.city && filters.city[0];
  const coordinatesLookup = suggestionType === 'zip' ? [filters.zip[0]] : [cityCounty, filters.state && filters.state[0]];
  if (coordinatesLookup.every(Boolean)) {
    const searchText = suggestionType === 'zip' ? coordinatesLookup[0] : formatLocationSearch(...coordinatesLookup);
    const result = await lookupLocation(searchText);
    if (!result) {
      return null;
    }
    const {
      coordinates
    } = result;
    if (!coordinates || _.isEmpty(coordinates)) {
      return null;
    }
    return coordinates;
  }
  return null;
};
const validateFilters = (filters, filtersDefaultState) => {
  if (!filters) {
    return filtersDefaultState;
  }
  const parsed = zodFilterSchema.safeParse(filters);
  if (!parsed.success) {
    logger$c.warn('error parsing filters: ', parsed.error.issues);
    return null;
  }
  return parsed.data;
};
const parseFiltersFromUrl = (url, filtersDefaultState) => {
  if (!url) {
    return filtersDefaultState;
  }
  const {
    urlWithoutSearchParams
  } = splitUrl(url);
  const path = urlWithoutSearchParams.replace(/^\/residential\/?/, '/');
  const urlFilters = FiltersModel.urlToFe(path);
  const validatedFilters = validateFilters({
    ...filtersDefaultState,
    ...urlFilters
  });
  return validatedFilters ?? filtersDefaultState;
};
const splitUrl = url => {
  const searchParamsStart = url.indexOf('?');
  if (searchParamsStart !== -1) {
    return {
      urlWithoutSearchParams: url.slice(0, searchParamsStart),
      searchParams: new URLSearchParams(url.slice(searchParamsStart))
    };
  }
  return {
    urlWithoutSearchParams: url,
    searchParams: new URLSearchParams('')
  };
};
const useListingsSearch = ({
  url,
  currentLocation,
  definitionConfig,
  definitionGenerator = definitionGenerator$2,
  // initialLocationCoordinates,
  user,
  featureFlags,
  maxResultsAllowed,
  marketingTagsConfig = [],
  defaultNearbySearchRadius = DEFAULT_NEARBY_SEARCH_RADIUS,
  onFilterChanged
}) => {
  const sortExperimentFlag = featureFlags?.['fe.resi.srp-sort-experiment-v1'];
  const filtersDefaultState = useMemo(() => getFiltersDefaultState(maxResultsAllowed, defaultNearbySearchRadius, sortExperimentFlag), [maxResultsAllowed, defaultNearbySearchRadius, sortExperimentFlag]);
  const filtersInitialState = useMemo(() => {
    return parseFiltersFromUrl(url, filtersDefaultState);
  }, [url, filtersDefaultState]);
  const defaultUiFilterGroups = useMemo(() => convertFiltersToUiFilterGroups(filtersDefaultState, marketingTagsConfig), [filtersDefaultState, marketingTagsConfig]);
  const initialUiFilterGroups = useMemo(() => convertFiltersToUiFilterGroups(filtersInitialState, marketingTagsConfig), [marketingTagsConfig, filtersInitialState]);
  const {
    searchParams
  } = splitUrl(url);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [urlSearchParams, setUrlSearchParams] = useState(searchParams);
  const [currentFilters, setCurrentFilters] = useState(filtersInitialState);
  const [currentUiFilterGroups, setCurrentUiFilterGroups] = useState(initialUiFilterGroups);
  const [listingCompatabilityFilters, setListingCompatabilityFilters] = useState(converFiltersToListingCompatabilityFilters(currentFilters));
  const config = useMemo(() => ({
    filterState: currentFilters,
    definitionGenerator,
    definitionConfig: {
      ...(definitionConfig || {}),
      marketingTagConfig: marketingTagsConfig,
      featureFlags
    }
  }), [currentFilters, definitionGenerator, featureFlags, marketingTagsConfig]);
  const {
    data: filtersData,
    actions: filtersActions
  } = useFilters(config);
  const [isLoading, setIsLoading] = useState(true);
  const [listings, setListings] = useState({
    total_count: 0,
    content: []
  });
  const [currentSearchId, setCurrentSearchId] = useState(null);
  /** TODO: remove after removing all code from srp and tell QA to adjust
   * their tests to include nearby_search_radius in the url.
   * Why does this exist ? if you go to let's say /residential/CA/Irvine_ct
   * The url will stay /residential/CA/Irvine_ct, however implicitely it also applies
   * the following filters: active_lt/auction_date_order,resi_sort_v2_st/y_nbs.
   * Then if you select any other filter including from location suggestions in the dropdown
   * it will add on active_lt/auction_date_order,resi_sort_v2_st/y_nbs to the url along with your
   * newly selected filters. This is kind of a flaw in the current srp implementation. In other words,
   * if your initial url has any location configured such as state, county, city, zip or globalSearch it will
   * sneakily add on default filters active_lt/auction_date_order,resi_sort_v2_st/y_nbs but will not show them
   * in the url, until you start touching any other filters (then those defaults will appear in the url)!
   * For the sake of migrating to blueprint
   * and passing automation tests we keep the url behaviour the same as current SRP implementation
   * by employing this beautiful code of __RATIONALIZE_YOUR_AWFUL_HACKJOB__ .
   * Is this awful? Yes. Is this hacky? Yes. You cannot say this is not a descriptive variable name!
   */
  const __RATIONALIZE_YOUR_AWFUL_HACKJOB__ = useRef({
    nearbySearchRadius: false,
    filtersToOmitFromUrl: new Set(['listingType', 'sort', 'useNearbySearch'])
  });
  const processFilters = useCallback(async (newCurrentFilters, newQueryParams) => {
    const newUiFilterGroups = convertFiltersToUiFilterGroups(newCurrentFilters, marketingTagsConfig);
    // TODO: what are mutually exclusive groups of filters ?
    // for example if globalSearch is on we need to remove all location filters
    // if nearby is on we need to check if data and filters required for nearby search
    // is present. What about mapbounds ? Does map bounds removes all location fitlers ?
    const isValidForNearbySearch = getIsValidForNearbySearch(newCurrentFilters);
    const isSortByDistance = getIsSortByDistance(newCurrentFilters);
    if (!isValidForNearbySearch) {
      // TODO: after integrating into srp and removing old code
      // remove nearby related filters here so they don't show up in the url
      // Right now,  for the sake of backward compat with srp, we need to show y_nbs in the url
      // but not include this in the filters (it will be removed later when converting to
      // api filters).
      newCurrentFilters.geoLocation = [];
    }
    if (isSortByDistance) {
      if (currentLocation?.coordinates) {
        newCurrentFilters.userCoords = [currentLocation.coordinates.latitude, currentLocation.coordinates.longitude];
      } else if (!newCurrentFilters.userCoords || newCurrentFilters.userCoords.length === 0) {
        newCurrentFilters.sort = {
          ...filtersDefaultState.sort
        };
      }
    } else {
      newCurrentFilters.userCoords = [];
    }
    const requiresGeoLocationCoordinates = isValidForNearbySearch && _.isEmpty(newCurrentFilters.geoLocation);
    if (requiresGeoLocationCoordinates) {
      // TODO: perhaps in the future we could cash location geoLocation ?
      const coordinates = await attemptToLocateGeoCenter(newCurrentFilters);
      newCurrentFilters.geoLocation = coordinates ? [coordinates.lat, coordinates.lon] : [];
    }
    // some of the filters aren't supposed to be shown in the url.
    // TODO: nearbySearchRadius should be shown in the url, but because
    // of how qa tests are currently written we need to avoid adding this to the url
    // until a user changes nearby search radius filter which will flip __RATIONALIZE_YOUR_AWFUL_HACKJOB__
    // flag on and we will start showing radius value in the url. Same goes for location filters.
    const filtersToOmitFromUrl = ['limit'];
    __RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.filtersToOmitFromUrl.forEach(filter => filtersToOmitFromUrl.push(filter));
    if (!__RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.nearbySearchRadius) {
      filtersToOmitFromUrl.push('nearbySearchRadius');
    }
    const path = new FiltersModel(_.omit(newCurrentFilters, filtersToOmitFromUrl)).feToUrlValue;
    const searchParams = newQueryParams ? newQueryParams.toString() : urlSearchParams.toString();
    const newCurrentUrl = `/residential/${path}${searchParams ? '?' + searchParams : ''}`;
    const displayOnlyFilters = ['mapView', 'openedAssetId'];
    const newCurrentUrlWithoutDisplayOnlyFilters = `/residential/${new FiltersModel(_.omit(newCurrentFilters, ['limit', ...displayOnlyFilters])).feToUrlValue}`;
    // @ts-ignore No types for MurmurHash3
    const newCurrentSearchId = MurmurHash3(newCurrentUrlWithoutDisplayOnlyFilters).result();
    const newListingCompatabilityFilters = converFiltersToListingCompatabilityFilters(newCurrentFilters, isValidForNearbySearch);
    newQueryParams && setUrlSearchParams(newQueryParams);
    setCurrentFilters(newCurrentFilters);
    setListingCompatabilityFilters(newListingCompatabilityFilters);
    setCurrentUiFilterGroups(newUiFilterGroups);
    setCurrentUrl(currentUrl => {
      // if you load the page at /residential/CA/Irvine_ct/
      // if the initial url has trailing slash we don't want to
      // remove it and cause an extra rerender and extra url change in the app
      // also this could brake automation tests since they match against expected url
      // which is hardcoded with trailing slash
      if (currentUrl.endsWith('/') && currentUrl.slice(0, -1) === newCurrentUrl) {
        return currentUrl;
      }
      return newCurrentUrl;
    });
    setCurrentSearchId(newCurrentSearchId);
  }, [marketingTagsConfig, currentLocation?.coordinates, urlSearchParams, filtersDefaultState.sort]);
  // @ts-ignore adc/hooks any
  const prevUrl = adcHooks.usePrevious(url);
  useEffect(() => {
    // this handles the case when a user navigates back in history by using
    // browser arrows. Because the url changes, the filters need to be updated
    if (prevUrl !== url && currentUrl !== url) {
      const newCurrentFilters = parseFiltersFromUrl(url, filtersDefaultState);
      const {
        searchParams
      } = splitUrl(url);
      // @ts-ignore promises must be awaited
      const processFiltersFunc = async () => processFilters(newCurrentFilters, searchParams);
      processFiltersFunc();
    }
  }, [url, currentUrl, prevUrl, filtersDefaultState, processFilters]);
  // @ts-ignore @adc/hooks any
  const prevSearchId = adcHooks.usePrevious(currentSearchId);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const locationAggregateField = getLocationAPIKey(listingCompatabilityFilters);
      const requiresLocationAggregate = listingCompatabilityFilters.geo_location && locationAggregateField;
      const requiresSeoAggregate = listingCompatabilityFilters.property_state || listingCompatabilityFilters.property_city || listingCompatabilityFilters.property_county;
      const aggregationFields = [...(requiresSeoAggregate && !featureFlags['fe.resi.search-enable-aggregation-seo-optimization'] ? [AGGREGATE_FIELDS.property_type, AGGREGATE_FIELDS.remote_bid_assets] : []), ...(requiresLocationAggregate ? [locationAggregateField] : [])];
      // MERCH-1922/MERCH-2060: SRP Sort Experiment — override sort for experiment groups
      // Control users on "Recommended" sort (resi_sort_v2) get `recommended` sent to graph.
      // MERCH-1922/MERCH-2060: SRP Sort Experiment — override sort for treatment group
      // Treatment users on "Recommended" sort (resi_sort_v2) get `recommended_v2` sent to graph.
      const experimentFlag = featureFlags?.['fe.resi.srp-sort-experiment-v1'];
      let filtersForQuery = listingCompatabilityFilters;
      if (experimentFlag === 'treatment' && listingCompatabilityFilters.sort === 'resi_sort_v2') {
        filtersForQuery = {
          ...listingCompatabilityFilters,
          sort: 'recommended_v2'
        };
      }
      try {
        const collectionResult = await seekListingsFromFilters({
          filters: filtersForQuery,
          aggregationFields,
          hasAuthenticatedUser: user.logged_in
        });
        let listingResults = collectionResult.content;
        listingResults = filterOpenHouses(listingResults);
        /**
         * NOTE: We rely on optional chaining here to be resilient in the case of the graph or cs-seek or ms-search throwing
         * an error at us and us still having to render _something_.
         */
        const listings = formatAggregation({
          listings: {
            offset: listingCompatabilityFilters.offset || 0,
            content: listingResults,
            total_count: collectionResult.total_count
          },
          listingFilters: listingCompatabilityFilters,
          collectionResult,
          requiresLocationAggregate,
          locationAggregateField
        });
        trackSearchResultBuckets(listings);
        // Echo the applied locations resolved to display form so SRP can render
        // location chips from a geoid URL without a second round-trip.
        listings.recognizedLocations = collectionResult.derived_signals?.recognized_locations || [];
        setListings(listings);
      } catch (err) {
        logger$c.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    if (prevSearchId !== currentSearchId) {
      // @ts-ignore promises must be awaited
      fetchData();
    }
  }, [user.logged_in, currentSearchId, prevSearchId, filtersDefaultState, listingCompatabilityFilters, currentFilters, featureFlags]);
  // @ts-ignore adc/hooks any type
  adcHooks.useMountEffect(() => {
    // this handles getting filters from url on initial page load
    let newCurrentFilters = parseFiltersFromUrl(currentUrl, filtersDefaultState);
    // if no location filters present(if the url is just /residential for example)
    // get them from currentLocation
    const areLocationsFiltersSet = newCurrentFilters.search || !_.isEmpty(newCurrentFilters.state) || !_.isEmpty(newCurrentFilters.city) || !_.isEmpty(newCurrentFilters.county) || !_.isEmpty(newCurrentFilters.zip) || !_.isEmpty(newCurrentFilters.locations) || !_.isEmpty(newCurrentFilters.mapBounds);
    const isGlobalSearchSet = newCurrentFilters.globalSearch === 'y';
    /**
     * Either we have a location configured within the url, the common case, or we are coming for the first time and
     * assume the default behavior which is to use the most accurate location we have for the user based on the request
     * information (currentLocation arg). If location is configured within the url, we still need to get coordinates for
     * that location in order to do nearby search.
     * Since SRP gets them in server middleware we enable the hook to accept those via initialLocationCoordinates. We don't really
     * have to get them this way,  since processFilters does location lookup in case of geoLocation required but missing. However being able to accept
     * initial geoLocation as an arg in the hook saves us a request on initial page load.
     */
    // TODO: I don't trust SRP middlware for this. We expect coords of geo center of the place
    // we're searching but srp may send users coords here instead. We already have users coords
    // from currentLocation.
    // if (areLocationsFiltersSet && !isGlobalSearchSet) {
    //   newCurrentFilters.geoLocation = initialLocationCoordinates
    //     ? [initialLocationCoordinates.lat, initialLocationCoordinates.lon]
    //     : []
    // }
    if (currentLocation?.coordinates) {
      newCurrentFilters.userCoords = [currentLocation.coordinates.latitude, currentLocation.coordinates.longitude];
    }
    if (areLocationsFiltersSet || isGlobalSearchSet) {
      // TODO: temporary hack
      const urlFilters = FiltersModel.urlToFe(currentUrl);
      Array.from(__RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.filtersToOmitFromUrl).forEach(filter => {
        if (urlFilters[filter]) {
          __RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.filtersToOmitFromUrl.delete(filter);
        }
      });
      newCurrentFilters = {
        ...filtersDefaultState,
        ...newCurrentFilters
      };
    }
    if (!(areLocationsFiltersSet || isGlobalSearchSet)) {
      if (currentLocation?.city) {
        newCurrentFilters.city = [currentLocation.city];
      }
      if (currentLocation?.state) {
        newCurrentFilters.state = [currentLocation.state];
      }
      if (currentLocation?.coordinates) {
        newCurrentFilters.geoLocation = [currentLocation.coordinates.latitude, currentLocation.coordinates.longitude];
      }
      __RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.filtersToOmitFromUrl = new Set([]);
    }
    // TODO:
    // if  location filters are already set and it's not a currentLocation, before initial fetch
    // We need to lookup geoLocation for city, state, county , zip that's in the url
    // @ts-ignore promises must be awaited
    const processFiltersFunc = async () => processFilters(newCurrentFilters);
    processFiltersFunc();
  });
  const pendingFilters = useRef({});
  // Ref so onFilterChange's identity/deps stay unchanged regardless of the
  // consumer callback.
  const onFilterChangedRef = useRef(onFilterChanged);
  onFilterChangedRef.current = onFilterChanged;
  // Filter definitions, ref-held so onFilterChange's deps stay unchanged; the
  // tracker resolves values to labels via the shared filters utils.
  const filterDefinitionsRef = useRef(filtersData.filterDefinitions);
  filterDefinitionsRef.current = filtersData.filterDefinitions;
  const onFilterChange = useCallback(newFilters => {
    logger$c.info('onFilterChange ', newFilters);
    if (isLoading && newFilters) {
      pendingFilters.current = {
        ...pendingFilters.current,
        ...newFilters
      };
      return;
    }
    // any filter change results in default filters such as sort nearbySearch and listingType
    // being shown in the url. Weird but that's how srp works. Will get rid of this in the future
    __RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.filtersToOmitFromUrl = new Set([]);
    if (newFilters?.nearbySearchRadius) {
      // TODO: remove this when it's time to remove
      // this will cause nearbySearchRadius value to appear in the url
      // read other TODOs for explanation
      __RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.nearbySearchRadius = true;
    }
    const locationFilterKeys = ['search', 'state', 'city', 'county', 'zip', 'locations', 'globalSearch', 'geoLocation', 'mapBounds', 'useNearbySearch', 'nearbySearchRadius'];
    if (newFilters) {
      if (!_.isEmpty(newFilters.mapBounds)) {
        newFilters.search = '';
        newFilters.city = [];
        newFilters.state = [];
        newFilters.county = [];
        newFilters.geoLocation = [];
        newFilters.zip = [];
        newFilters.locations = '';
        newFilters.globalSearch = false;
      }
      if (newFilters.openedAssetId) {
        urlSearchParams.set('selectedListingId', newFilters.openedAssetId);
        const newUrlSearchParams = new URLSearchParams(urlSearchParams.toString());
        setUrlSearchParams(newUrlSearchParams);
      }
      // @ts-ignore promises must be awaited
      processFilters({
        ...currentFilters,
        ...newFilters
      });
      const change = trackFilterChanged(currentFilters, newFilters, filterDefinitionsRef.current);
      if (change) onFilterChangedRef.current?.(change);
    } else {
      // if newFilters is undefined this means reset filters.
      // Keep location filters the same
      pendingFilters.current = {};
      const newCurrentFilters = {
        ..._.omit(filtersDefaultState, locationFilterKeys),
        ..._.pick(currentFilters, locationFilterKeys)
      };
      // @ts-ignore promises must be awaited
      processFilters(newCurrentFilters);
      const change = trackFilterChanged(currentFilters, newCurrentFilters, filterDefinitionsRef.current);
      if (change) onFilterChangedRef.current?.(change);
    }
  }, [currentFilters, featureFlags, filtersDefaultState, isLoading, processFilters, urlSearchParams]);
  useEffect(() => {
    if (!_.isEmpty(pendingFilters.current) && !isLoading) {
      onFilterChange(pendingFilters.current);
      pendingFilters.current = {};
    }
  }, [isLoading, onFilterChange]);
  // I know this is not the best of solutions, but since not ALL of our so-called filters (if you know, you know) in SRP are present/compatibile with the newDefinitionGenerator yet, I
  // think that exposing this as a different function is a good tradeoff to get mobile to stop accessing the useFilter actions directly and manage here the updates.
  // This will ensure we actually make the api call when a filter changes.
  const onFancyFilterChange = (model, key, newValue) => {
    logger$c.info('onFancyFilterChange ', {
      model,
      key,
      newValue
    });
    const nextAppValues = filtersActions.update(model.name, key, newValue);
    onFilterChange({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      [key]: nextAppValues[key]
    });
  };
  const onFancyFilterReset = (model, key, newValue) => {
    logger$c.info('onFancyFilterReset ', {
      model,
      key,
      newValue
    });
    onFilterChange(filtersActions.reset(model.name, key, newValue));
  };
  const onFancyFilterResetAll = () => {
    logger$c.info('onFancyFilterResetAll');
    filtersActions.resetAll();
    onFilterChange();
  };
  const onLocationSuggestionSelect = suggestion => {
    const newCurrentFilters = match(suggestion).with({
      type: 'currentLocation'
    }, () => {
      const city = suggestion.filters.property_city || currentLocation?.city;
      const state = suggestion.filters.property_state || currentLocation?.state;
      const geoLocation = suggestion.filters.geo_location || currentLocation?.coordinates;
      const newCurrentFilters = {
        city: city ? [city] : [],
        state: state ? [state] : [],
        county: [],
        zip: [],
        geoLocation: geoLocation ? [geoLocation.latitude, geoLocation.longitude] : [],
        search: '',
        mapBounds: [],
        locations: '',
        globalSearch: false
      };
      return {
        ...currentFilters,
        ...newCurrentFilters
      };
    }).with({
      type: 'globalSearch'
    }, () => {
      const newCurrentFilters = {
        city: [],
        state: [],
        county: [],
        zip: [],
        geoLocation: [],
        search: '',
        mapBounds: [],
        locations: '',
        globalSearch: 'y'
      };
      return {
        ...currentFilters,
        ...newCurrentFilters
      };
    }).otherwise(() => {
      const geoLocation = suggestion.filters.geo_location?.split(',') || [];
      const emptyLocationFilters = {
        city: [],
        state: [],
        county: [],
        zip: [],
        search: '',
        locations: '',
        mapBounds: [],
        globalSearch: false,
        geoLocation
      };
      const currentFiltersWithResetLocationFilters = {
        ...currentFilters,
        ...emptyLocationFilters
      };
      const newCurrentFilters = parseFiltersFromUrl(suggestion.link, currentFiltersWithResetLocationFilters);
      return newCurrentFilters;
    });
    // any selection in location suggestion dropdown enables active_lt,
    // y_nbs and sort to be visible in the url from this point on
    __RATIONALIZE_YOUR_AWFUL_HACKJOB__.current.filtersToOmitFromUrl = new Set([]);
    // @ts-ignore promises must be awaited
    processFilters(newCurrentFilters);
  };
  const data = {
    listings,
    currentUiFilterGroups,
    defaultUiFilterGroups,
    currentFilters,
    listingCompatabilityFilters,
    currentUrl,
    currentSearchId,
    isLoading,
    filtersData
  };
  const actions = {
    onFilterChange,
    onLocationSuggestionSelect,
    filtersActions,
    onFancyFilterChange,
    onFancyFilterReset,
    onFancyFilterResetAll
  };
  return {
    data,
    actions
  };
};

const MAX_DISPLAYABLE_SUGGESTIONS = 35;
const MAX_DISPLAYABLE_SUGGESTIONS_PER_TYPE = 7;
const locationTypePrecedenceList = ['state', 'city', 'zip', 'county', 'address'];
const isValidLocation = location => {
  if (location.city) {
    const isAirForceBase = location.city.toLowerCase().includes('afb') || location.city.toLowerCase().includes('air force base');
    return !isAirForceBase;
  }
  return true;
};
const enforceSuggestionCorrectness = suggestion => {
  if (suggestion.type === 'zip') {
    /**
     * In our locations auto-suggestion index we correlate zip searches with specific cities. The issue is that a) some
     * zips cross city boundaries so are not accurate 1:1 (mentioned lots of other places also) and b) consumers of this
     * component use best-effort top decide where this should redirect for a user to continue searching. If a city is
     * present, it has higher precedence for redirect logic than a zip, which then forces us into the issue in the point
     * a. We remove the county and state for the same reason here (even though we know we have this information should we
     * want to be more specific in the future)
     */
    delete suggestion.city;
    delete suggestion.county;
    delete suggestion.state;
  } else if (suggestion.type === 'city') {
    /**
     * Special case when searching on city, we do not want to include county as some cities (e.g. Atlanta) can span
     * multiple counties.
     */
    delete suggestion.county;
  }
  return suggestion;
};
const constructLinkFromFilters = filters => {
  const filtersModel = new FiltersModel(filters);
  return `/residential/${filtersModel.apiToUrlValue}`;
};
const normalizeGraphType = location => {
  const filters = {
    version: 1,
    property_city: location.city || '',
    property_state: location.state || '',
    property_zip: location.zip || '',
    property_county: location.county || '',
    // @ts-ignore this will be valid after new graph types with coords are published
    geo_location: location.coordinates ? [location.coordinates.lat, location.coordinates.lon].join(',') : null
  };
  const link = location.type === 'address' ? location.url : constructLinkFromFilters(filters);
  return {
    filters,
    link: link || '/residential',
    icon: {
      name: location.type === 'address' ? 'house-chimney' : 'location-dot'
    },
    label: location.type === 'state' && (location.value || '').length === 2 ? (location.value || '').toUpperCase() : location.value || '',
    subLabel: location.type || '',
    category: location.type === 'address' ? 'Listings' : 'Places',
    hideSubLabelWhenCategorized: location.type === 'address',
    type: location.type || 'text',
    relates_to_urn: location.relates_to_urn
  };
};
const normalizeGraphSaveSearchType = savedSearch => {
  const filters = savedSearch.parameters;
  return {
    filters,
    link: constructLinkFromFilters(filters),
    label: savedSearch.saved_search_name || savedSearch.saved_search_id || '',
    icon: {
      name: 'heart'
    },
    subLabel: 'saved search',
    category: 'Saved Searches',
    hideSubLabelWhenCategorized: true,
    type: 'savedSearch'
  };
};
const normalizeGlobalSearch = () => {
  const filters = {
    version: 1,
    global_search: 'y'
  };
  return {
    filters,
    link: constructLinkFromFilters(filters),
    label: GenericLocations.ALL_LOCATIONS,
    category: 'Other',
    hideSubLabelWhenCategorized: false,
    subLabel: 'US',
    icon: {
      name: 'earth-americas'
    },
    type: 'globalSearch'
  };
};
const normalizeCurrentLocation = currentLocation => {
  const filters = {
    version: 1,
    property_city: currentLocation.city || '',
    property_state: currentLocation.state || ''
  };
  return {
    filters,
    link: constructLinkFromFilters(filters),
    label: GenericLocations.CURRENT_LOCATION,
    category: 'Other',
    hideSubLabelWhenCategorized: false,
    subLabel: currentLocation.city && currentLocation.state ? `${currentLocation.city}, ${currentLocation.state}` : '',
    icon: {
      name: 'location-arrow'
    },
    type: 'currentLocation'
  };
};
const normalizeLocationSuggestions = locations => {
  const suggestions = [];
  const validLocations = locations.filter(isValidLocation);
  const partitionedSuggestionTypes = _.groupBy(validLocations, 'type');
  let suggestionsRemaining = MAX_DISPLAYABLE_SUGGESTIONS;
  for (const typePriority of locationTypePrecedenceList) {
    if (!suggestionsRemaining) break;
    if (!partitionedSuggestionTypes[typePriority]) continue;
    const typeSample = partitionedSuggestionTypes[typePriority].slice(0, Math.min(MAX_DISPLAYABLE_SUGGESTIONS_PER_TYPE, suggestionsRemaining));
    // We may not have the max allowed per group which is why we decrement
    suggestions.push(...typeSample);
    suggestionsRemaining = suggestionsRemaining - typeSample.length;
  }
  return suggestions.map(enforceSuggestionCorrectness).map(normalizeGraphType);
};
const normalizeSearchTextEntryType = searchText => {
  const filters = {
    version: 1,
    search: searchText
  };
  return {
    filters,
    link: constructLinkFromFilters(filters),
    label: searchText,
    subLabel: 'text',
    category: 'Text Search',
    hideSubLabelWhenCategorized: true,
    type: 'text',
    icon: {
      name: 'input-text'
    }
  };
};
const normalizeRecentSearch = suggestion => {
  // TODO: unclear about what the structure of recent search should be
  // for now leaving type unchanged since will need type to
  // show city, address, county etc labels when showing suggestions
  // grouped by category. That's why I'm not changing type to
  // recentSearch for now.
  return {
    ...suggestion,
    subLabel: 'recent',
    category: 'Recent Searches',
    hideSubLabelWhenCategorized: false
  };
};

const lookupSuggestions = async search => {
  if (!search.length) return {
    locationSuggestions: []
  };
  const results = await fetchLocationSuggestions(search);
  return {
    locationSuggestions: normalizeLocationSuggestions(results.rawLocationSuggestions)
  };
};
const lookupSavedSearches = async controller => {
  const results = await fetchSavedSearches(controller);
  return results.rawSavedSearches.map(normalizeGraphSaveSearchType);
};

const SUGGESTIONS_CACHE_TTL = ms('20m');
ms('60s');

const UNIQUE_RECENT_SEARCHES_KEY = 'blueprint::recentSearches';
const TYPING_DEBOUNCE_MS = 300;
const cache = new LRUCache({
  // The size of the cache, set intentionally small until we get more insights into search behavior here. What we do
  // know is that people tend to perform a few searches to narrow results and then move on to other filters or switch to
  // other parts of the site, so this likely won't ever be very large.
  max: 20,
  // Always provide an expiry mechanism in case we have bugs. This doesn't actively prune but will throw away stale
  // items once requested
  ttl: SUGGESTIONS_CACHE_TTL
});
/**
 * Given an input of filters, render it as a meaningful string.
 * For example, if we enter 'irvine' and get the city of irvine, render in nicely as "Irvine, CA"
 *
 * @param filters
 * @returns search text to show input
 */
function generateSearchInputFromFilters(filters, recognizedLocations) {
  // note, we could .when this first condition but compiler isn't smart enough to know that filters is not null in rest.
  if (_.isNil(filters)) return '';
  return match(filters).when(filters => filters.search,
  // @ts-ignore TS doesn't see that I'm checking for undefined above
  filters => filters.search).when(
  // @ts-ignore for now... until we have a plan for handling global search e.g. no filters at all (implicit)
  filters => filters.global_search === 'y', () => 'All Locations').when(
  // Geoid `locations` resolve to display labels server-side
  // (recognized_locations on the search response) — the client cannot resolve
  // geoid -> label itself, so we render the labels echoed back by the graph.
  filters => Boolean(filters.locations) && Boolean(recognizedLocations?.length), () => (recognizedLocations || []).map(location => location.label).filter(Boolean).join(', ')).when(filters => filters.property_city && filters.property_state, filters => `${filters.property_city}, ${filters.property_state}` // eslint-disable-line @typescript-eslint/restrict-template-expressions
  ).when(filters => filters.property_county && filters.property_state, filters => `${filters.property_county} County, ${filters.property_state}` // eslint-disable-line @typescript-eslint/restrict-template-expressions
  ).when(filters => filters.property_state,
  // @ts-ignore TS doesn't see that I'm checking for undefined above
  filters => filters.property_state) // TODO: consider converting two letter state to full name (perhaps?)
  .when(filters => filters.property_zip,
  // @ts-ignore TS doesn't see that I'm checking for undefined above
  filters => filters.property_zip).otherwise(() => '');
}
function useLocationSuggestions({
  filters,
  currentLocation,
  user,
  includedSuggestionTypes,
  // see default below
  maxRecentSearches,
  deviceStorage,
  elmId = '',
  graphWebsocket,
  recognizedLocations
}) {
  /**
   * Have to memoize this due to the sliced default, which if assigned directly to the
   * input param, it will cause an infinite loop.  (...and the sliced default is needed because
   * typescript doesn't let you assign a const array to a mutable array)
   */
  const includedSuggestionTypesMemo = useMemo(() => includedSuggestionTypes || ALL_SUGGESTION_TYPES.slice(), [includedSuggestionTypes]);
  const [state, setState] = useState({
    suggestions: null,
    locationSuggestions: null,
    searchText: generateSearchInputFromFilters(filters, recognizedLocations),
    selectedSuggestion: null,
    filters,
    loading: false
  });
  const isMounted = useIsMounted();
  const isIncludedSuggestionType = useCallback(suggestionType => includedSuggestionTypesMemo.includes(suggestionType), [includedSuggestionTypesMemo]);
  const [savedSearches] = useSavedSearches({
    enabled: Boolean(user?.logged_in) && isIncludedSuggestionType('savedSearch'),
    graphWebsocket
  });
  const [recentSearches, updateRecentSearches] = useRecentSearches(maxRecentSearches, deviceStorage);
  const hasInput = input => (input || '').length > 0;
  const setLoading = useCallback(loading => {
    if (!isMounted) return;
    setState(prev => ({
      ...prev,
      loading
    }));
  }, [isMounted]);
  const pruneSuggestions = useCallback(suggestions => {
    if (_.isEqual(includedSuggestionTypesMemo, ALL_SUGGESTION_TYPES)) return suggestions;
    return suggestions.filter(suggestion => isIncludedSuggestionType(suggestion.type));
  }, [includedSuggestionTypesMemo, isIncludedSuggestionType]);
  /**
   * This is effectively is the full suggestion list builder,
   * creating the list based on the current state.
   */
  useEffect(() => {
    const suggestions = [];
    if (state.searchText && hasInput(state.searchText)) {
      if (state.locationSuggestions) {
        suggestions.push(...pruneSuggestions(state.locationSuggestions));
      }
    }
    if (isIncludedSuggestionType('text') && state.searchText && hasInput(state.searchText)) {
      suggestions.push(normalizeSearchTextEntryType(state.searchText));
    }
    if (isIncludedSuggestionType('currentLocation') && currentLocation) {
      suggestions.push(normalizeCurrentLocation(currentLocation));
    }
    // this condition is here because recent search needs to be between current
    // location and global search results
    if (!(state.searchText && hasInput(state.searchText)) && isIncludedSuggestionType('recentSearch') && recentSearches) {
      suggestions.push(...recentSearches.map(suggestion => normalizeRecentSearch(suggestion)));
    }
    if (isIncludedSuggestionType('globalSearch')) suggestions.push(normalizeGlobalSearch());
    if (isIncludedSuggestionType('savedSearch') && savedSearches) {
      suggestions.push(...savedSearches);
    }
    setState(prev => ({
      ...prev,
      suggestions
    }));
  }, [currentLocation, isIncludedSuggestionType, pruneSuggestions, recentSearches, savedSearches, state.locationSuggestions, state.searchText]);
  const getSuggestions = useCallback(async searchText => {
    const cacheKey = `${searchText}_${elmId}`;
    if (!_.isNil(searchText)) {
      const cacheData = cache.get(cacheKey);
      if (!_.isNil(cacheData)) {
        setState(prev => ({
          ...prev,
          locationSuggestions: cacheData?.locationSuggestions || []
        }));
        return;
      }
      setLoading(true);
      const {
        locationSuggestions
      } = await lookupSuggestions(searchText);
      cache.set(cacheKey, {
        locationSuggestions
      });
      if (!isMounted) return;
      setState(prev => ({
        ...prev,
        locationSuggestions
      }));
      setLoading(false);
    }
  }, [isMounted, setLoading, elmId]);
  const debouncedGetSuggestions = useMemo(() => _.debounce(getSuggestions, TYPING_DEBOUNCE_MS), [getSuggestions]);
  /**
   * This is the main update function, which is called when the user types in the search box.
   * It will update the state with the new search text, and then call getSuggestions to fetch
   * the new suggestions.
   *
   * Note that we don't want to debounce this function, because we want to immediately update
   * the search text in the UI, and then fetch suggestions on a debounced basis.
   */
  const update = useCallback(async ({
    searchText
  }) => {
    // @ts-ignore - immediately update raw text from user
    setState(prev => ({
      ...prev,
      searchText,
      selectedSuggestion: null
    }));
    return await debouncedGetSuggestions(searchText);
  }, [debouncedGetSuggestions]);
  const trackSelectionChange = useCallback(suggestion => {
    setState(prev => ({
      ...prev,
      selectedSuggestion: suggestion
    }));
  }, []);
  const autoSelectSuggestion = useCallback(() => {
    const nullSuggestion = {
      filters: {},
      link: '/residential',
      label: '',
      subLabel: '',
      category: 'Other',
      type: 'currentLocation',
      hideSubLabelWhenCategorized: false
    };
    if (!state.suggestions || state.suggestions?.length === 0) {
      return nullSuggestion;
    }
    if (state.selectedSuggestion) {
      return state.selectedSuggestion;
    }
    if (!state.suggestions.find(suggestion => suggestion.category === SEARCH_SUGGESTION_GROUPS.textSearch)) {
      return nullSuggestion;
    }
    return state.suggestions[0];
  }, [state.selectedSuggestion, state.suggestions]);
  // Force update on initial layout
  useEffect(() => {
    const searchText = generateSearchInputFromFilters(filters, recognizedLocations);
    setState(prev => ({
      ...prev,
      filters,
      searchText
    }));
    update({
      searchText
    }); // eslint-disable-line
  }, [filters, recognizedLocations, update]);
  const actions = useMemo(() => ({
    update,
    autoSelectSuggestion,
    trackSelectionChange,
    updateRecentSearches
  }), [autoSelectSuggestion, update, trackSelectionChange, updateRecentSearches]);
  const data = useMemo(() => ({
    suggestions: state.suggestions,
    searchText: state.searchText,
    loading: state.loading
  }), [state.suggestions, state.searchText, state.loading]);
  return {
    data,
    actions
  };
}
const useRecentSearches = (maxRecentSearches = 1, deviceStorage = new DeviceStorageAdapter({
  storage: new NullDeviceStorage({
    namespace: 'recentSearch'
  })
})) => {
  // this helps to avoid creating new array each render
  // which causes useEffect in useLocalStorage, that has a dep on this variable,
  // to run too many times
  const initialValue = useMemo(() => [], []);
  const [recentSearches, _setRecentSearches] = useLocalStorage({
    key: UNIQUE_RECENT_SEARCHES_KEY,
    initialValue,
    deviceStorage
  });
  const updateRecentSearches = useCallback(async suggestion => {
    const tags = [`type:${suggestion.type}`];
    savedSearchLogger.info(`instrument increment suggestion_selection tags: ${tags}`);
    blueprintInstrument.increment('suggestion_selection', 1, tags);
    // We should avoid duplicate entries into the recent searches list
    if (recentSearches?.length && recentSearches.find(recentSearchesItem => recentSearchesItem.label === suggestion.label)) {
      return;
    }
    if (_.without(ALL_SUGGESTION_TYPES, 'savedSearch', 'currentLocation', 'globalSearch').includes(suggestion.type)) {
      const newSuggestions = [suggestion];
      // first in first out, starts from most recent
      const newRecentSearches = recentSearches?.length >= maxRecentSearches ? newSuggestions.concat(recentSearches).slice(0, maxRecentSearches) : newSuggestions.concat(recentSearches);
      await _setRecentSearches(newRecentSearches);
    }
  }, [_setRecentSearches, maxRecentSearches, recentSearches]);
  return [recentSearches, updateRecentSearches];
};
// to help with debugging on mobile
const savedSearchLogger = coreLogger.forModule('blueprint:saved_search');
const useSavedSearchesSubscriptionHook = ({
  graphWebsocket,
  enabled = true
}) => {
  const closeCurrentOperation = useRef(null);
  const [savedSearchMessage, setSavedSearchMessage] = useState(null);
  const isEnabled = graphWebsocket && enabled;
  useEffect(() => {
    if (isEnabled) {
      // @ts-ignore next line
      savedSearchLogger.info('subscribing');
      graphWebsocket.subscribe({
        operationOptions: {
          query: gql$6`
              subscription blueprint_userSavedSearchUpdated {
                savedSearchUpdated {
                  action
                  saved_search {
                    saved_search_id
                    saved_search_name
                    parameters
                    namespace
                  }
                }
              }
            `
        },
        onData: data => {
          // setNotification(data?.notificationSent)
          savedSearchLogger.info('New data loaded', data);
          const savedSearchMessage = data.savedSearchUpdated;
          setSavedSearchMessage(savedSearchMessage);
        },
        onError: error => {
          savedSearchLogger.warn(error);
        }
      }).then(graphSubscriber => {
        closeCurrentOperation.current = graphSubscriber.unsubscribe;
        return true;
      }).catch(err => savedSearchLogger.warn(err));
    }
    if (!isEnabled && typeof closeCurrentOperation.current === 'function') {
      savedSearchLogger.info('unsubscribing');
      // unsubscribe
      closeCurrentOperation.current();
      setSavedSearchMessage(null);
      closeCurrentOperation.current = null;
    }
  }, [graphWebsocket, isEnabled]);
  useEffect(() => {
    return () => {
      savedSearchLogger.info('unsubscribing on unmount');
      closeCurrentOperation.current?.();
    };
  }, []);
  return savedSearchMessage;
};
const useSavedSearches = ({
  enabled,
  graphWebsocket
}) => {
  const [savedSearches, setSavedSearches] = useState([]);
  useEffect(() => {
    if (!enabled) {
      // handle case when user who has saved searches logs out
      savedSearches.length > 0 && setSavedSearches([]);
      return;
    }
    const fetchSavedSearches = async () => {
      // savedSearches only need to be fetched once
      // this is to prevent unneccessary api calls due to potential useEffect reruns
      if (savedSearches.length > 0) {
        return;
      }
      const savedSearchSuggestions = await lookupSavedSearches();
      setSavedSearches(savedSearchSuggestions);
    };
    fetchSavedSearches().catch(err => coreLogger.error(err));
  }, [enabled, setSavedSearches, savedSearches.length]);
  // Connor if you find this, know that I tried using realtime watcher here...
  // I really did... I swear...
  // TODO: use useRealtimeWatcher hook in the foreseeable future, after
  // making GraphWebsocket restart work on mobile
  const incomingSavedSearchMessage = useSavedSearchesSubscriptionHook({
    graphWebsocket,
    enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false)
  });
  useEffect(() => {
    // upsert saved search
    if (incomingSavedSearchMessage) {
      const {
        action,
        saved_search: savedSearch
      } = incomingSavedSearchMessage;
      // TODO: these actions should be enums, but they're strings on be side
      // which already created a problem since some actions names are in all caps
      // and this one is lowecase with first letter capitalized
      // TODO for back end: when user changes the name of search after saving,
      // no UPDATE message is produced.  Also NO DELETE message
      action.toLowerCase() === 'create' && setSavedSearches(savedSearches => {
        const newSavedSearches = [normalizeGraphSaveSearchType(savedSearch), ...savedSearches];
        return newSavedSearches.slice(0, MAX_SAVED_SEARCHES);
      });
    }
  }, [incomingSavedSearchMessage]);
  return [savedSearches];
};

var hooks$b = /*#__PURE__*/Object.freeze({
    __proto__: null,
    generateSearchInputFromFilters: generateSearchInputFromFilters,
    useListingsSearch: useListingsSearch,
    useLocationSuggestions: useLocationSuggestions
});

const mapSortValue = (sortType, sortOrder) => {
  if (!sortType) {
    return SORT_TYPES_VALUES.AUCTION_DATE;
  }
  if ((sortType === SORT_TYPES.sqft || sortType === SORT_TYPES.valuationEstimate) && sortOrder === SORT_ORDERS.desc) {
    return `${sortType} ${SORT_ORDERS.desc}`;
  }
  if (sortType === SORT_TYPES.newlyAdded) {
    return SORT_TYPES_VALUES.NEWLY_ADDED;
  }
  return sortType;
};
const getSortData = key => {
  const sortItem = SORT_OPTIONS.find(item => item.key === key);
  if (!sortItem) {
    return SORT_OPTIONS.find(item => item.key === SORT_TYPES_VALUES.AUCTION_DATE);
  }
  return sortItem;
};
const mapSortKeyFromState = (sortType, sortOrder) => {
  if (sortType === SORT_TYPES.newlyAdded && sortOrder === SORT_ORDERS.desc) {
    return SORT_TYPES_VALUES.NEWLY_ADDED;
  }
  const sortItem = SORT_OPTIONS.find(item => {
    if (sortType === item.apiType) {
      if (item.apiType === SORT_TYPES.sqft || item.apiType === SORT_TYPES.valuationEstimate) {
        return item.apiOrder === (sortOrder || SORT_ORDERS.asc);
      }
      return item.apiOrder === sortOrder || !item.apiOrder && !sortOrder;
    }
    return false;
  });
  return sortItem?.key || SORT_TYPES_VALUES.AUCTION_DATE;
};
const parseSortWithDirection = sortValue => {
  let type = sortValue;
  let order = '';
  if (!sortValue) {
    return {
      type: SORT_TYPES.auctionStartDate,
      order: 'asc'
    };
  }
  if (sortValue === SORT_TYPES_VALUES.NEWLY_ADDED) {
    return {
      type: SORT_TYPES.newlyAdded,
      order: SORT_ORDERS.desc
    };
  }
  if (sortValue.includes(`_${SORT_ORDERS.desc}`)) {
    type = sortValue.replace(`_${SORT_ORDERS.desc}`, '');
    order = SORT_ORDERS.desc;
  } else if (sortValue.includes(` ${SORT_ORDERS.desc}`)) {
    type = sortValue.replace(` ${SORT_ORDERS.desc}`, '');
    order = SORT_ORDERS.desc;
  } else if (sortValue.includes(` ${SORT_ORDERS.asc}`)) {
    type = sortValue.replace(` ${SORT_ORDERS.asc}`, '');
    order = SORT_ORDERS.asc;
  }
  // Default to 'asc' if no order is set
  if (!order) {
    order = 'asc';
  }
  return {
    type,
    order
  };
};
const getSortObjectFromOption = sortOption => {
  return {
    type: sortOption.apiType,
    order: sortOption.apiOrder || 'asc'
  };
};
const normalizeSortValue = value => {
  if (!value) {
    return SORT_TYPES_VALUES.AUCTION_DATE;
  }
  return value;
};
const getUpdatedSortObject = (newSortValue, currentSort) => {
  if (_.isEqual(newSortValue, currentSort)) {
    return currentSort.sort;
  }
  return parseSortWithDirection(newSortValue);
};
const getSortOrderIcon = order => {
  return order === SORT_ORDERS.asc ? 'sort ascending' : 'sort descending';
};

var sorting$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getSortData: getSortData,
    getSortObjectFromOption: getSortObjectFromOption,
    getSortOrderIcon: getSortOrderIcon,
    getUpdatedSortObject: getUpdatedSortObject,
    mapSortKeyFromState: mapSortKeyFromState,
    mapSortValue: mapSortValue,
    normalizeSortValue: normalizeSortValue,
    parseSortWithDirection: parseSortWithDirection
});

/**
 * Build a Search (SRP) path from API-shaped filters (the ListingCompatibilityFilters vocabulary the
 * graph speaks: `listing_type`, `asset_event_type`, `usecode_product_type`, `property_state`,
 * `locations`, `price`, ...). This is THE way to compose an SRP link outside the SRP itself — e.g.
 * emails, dashboards, saved-preference deep links — so consumers never hand-write
 * `<value>_<urlKey>` segments and drift when the SRP route vocabulary changes.
 *
 * @example
 *   buildListingSearchPath({
 *     listing_type: 'active',
 *     asset_event_type: 'remote_bid',
 *     usecode_product_type: 'resi_ft',
 *   })
 *   // => '/residential/active_lt/remote_bid_af/foreclosures_at'
 */
const buildListingSearchPath = apiFilters => `/residential/${new FiltersModel(apiFilters).apiToUrlValue}`;
const nonHighlightableRows = ['globalSearch', 'currentLocation', 'text'];
// This function, given a specific search suggestion and a query string
// will return all indices where said string is located.
// we exclude certain suggestion types we do not wish to highlight here
const findMatchingText = (suggestion, searchText) => {
  const {
    label,
    type
  } = suggestion;
  const response = {
    type: 'highlight',
    key: searchText,
    offsets: []
  };
  if (nonHighlightableRows.includes(type)) {
    return response;
  }
  if (typeof searchText !== 'string' || searchText.length < 2) {
    return response;
  }
  const escapedSearchText = escapeRegExp(searchText);
  const regex = new RegExp(`(${escapedSearchText})`, 'gi');
  let match;
  while ((match = regex.exec(label)) !== null) {
    response.offsets.push([match.index, match.index + match[0].length]);
  }
  response.key = escapedSearchText;
  return response;
};
const getCategoryIcon = category => {
  return match(category).when(value => value === SEARCH_SUGGESTION_GROUPS.savedSearches, () => ({
    name: 'heart'
  })).when(value => value === SEARCH_SUGGESTION_GROUPS.textSearch, () => ({
    name: 'input-text'
  })).when(value => value === SEARCH_SUGGESTION_GROUPS.listings || value === SEARCH_SUGGESTION_GROUPS.recentSearch, () => ({
    name: 'house-chimney'
  })).otherwise(() => ({
    name: 'location-dot'
  }));
};
/* istanbul ignore next */
const sortSuggestionsByCategory = suggestions => {
  const suggestionGroupOrder = Object.values(SEARCH_SUGGESTION_GROUPS);
  return suggestions.sort((a, b) => {
    const indexA = suggestionGroupOrder.indexOf(a.category);
    const indexB = suggestionGroupOrder.indexOf(b.category);
    // If both categories are in the order array, compare their indices
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only one category is in the order array, prioritize it
    if (indexA !== -1) {
      return -1;
    }
    if (indexB !== -1) {
      return 1;
    }
    // If neither category is in the order array, maintain original order
    return 0;
  });
};
/* istanbul ignore next */
const getFormattedResultsWithCategories = (suggestions, formatResult) => {
  if (suggestions === null) {
    return {};
  }
  const groupedSuggestions = {};
  const sortedSuggestions = sortSuggestionsByCategory(suggestions);
  sortedSuggestions.forEach((suggestion, index) => {
    const {
      category
    } = suggestion;
    if (!groupedSuggestions[category]) {
      groupedSuggestions[category] = {
        name: category,
        results: [],
        icon: getCategoryIcon(category)
      };
    }
    groupedSuggestions[category].results.push(formatResult(suggestion, index));
  });
  return groupedSuggestions;
};
/* istanbul ignore next */
const getIsFilterSetChanged = (uiFilterGroup, defaultFilterGroup) => {
  return !isEqual(defaultFilterGroup, uiFilterGroup);
};

var utils$8 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    assetTypeRouteConverter: assetTypeRouteConverter,
    buildListingSearchPath: buildListingSearchPath,
    converFiltersToListingCompatabilityFilters: converFiltersToListingCompatabilityFilters,
    findMatchingText: findMatchingText,
    getCategoryIcon: getCategoryIcon,
    getFormattedResultsWithCategories: getFormattedResultsWithCategories,
    getIsFilterSetChanged: getIsFilterSetChanged,
    sortingUtils: sorting$1
});

var types$5 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

var types$4 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

Object.values(SEARCH_SUGGESTION_GROUPS);

var types$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BuyerListingSearchTypes: types$5,
    UseListingsSearchTypes: types$4
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
// Suggestion urns for geographic results are `adc:geoid:<id>`.
const GEOID_URN_PREFIX$2 = 'adc:geoid:';
const geoidFromUrn = urn => urn?.startsWith(GEOID_URN_PREFIX$2) ? urn.slice(GEOID_URN_PREFIX$2.length) : undefined;
/**
 * Format a location suggestion into a display label (e.g. "Irvine, CA")
 */
const formatLocationLabel = s => {
  const type = s.type || '';
  const city = s.city || '';
  const county = s.county || '';
  const state = s.state || '';
  const zip = s.zip || '';
  switch (type) {
    case 'city':
      return `${city}, ${state}`;
    case 'county':
      return `${county} County, ${state}`;
    case 'zip':
      // `location_suggestions` intentionally does not populate `city` for
      // zip-type results: a ZIP isn't tied to a single city (large metro ZIPs
      // span several), so the backend resolver leaves it out on purpose. Build
      // the label from whatever parts are present so we never render an empty
      // " - , " segment (e.g. "92004, CA" instead of "92004 - , CA").
      if (city && state) return `${zip} - ${city}, ${state}`;
      if (city) return `${zip} - ${city}`;
      if (state) return `${zip}, ${state}`;
      return zip;
    case 'state':
      return state;
    default:
      return s.value || '';
  }
};
// ─── Hook ────────────────────────────────────────────────────────────────────
/**
 * useLocationSearch — Hook for async location search with debouncing.
 *
 * Provides search functionality for the buying criteria location input.
 * Uses the existing `fetchLocationSuggestions` from `@adc/blueprint/search/providers`.
 *
 * @example
 * const { data, actions } = useLocationSearch()
 *
 * // In SearchField onChange:
 * actions.search(query)
 *
 * // In SearchField onResultSelect:
 * const location = actions.selectResult(result)
 * if (location) updateLocations([...locations, location])
 */
const useLocationSearch = ({
  debounceMs = 300,
  minSearchLength = 2,
  includeAddresses = true
} = {}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);
  const search = useCallback(query => {
    if (!query || query.length < minSearchLength) {
      setSearchResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setIsSearching(true);
      fetchLocationSuggestions(query).then(result => {
        setSearchResults((result.rawLocationSuggestions || []).map(s => ({
          title: formatLocationLabel(s),
          value: s.value || '',
          type: s.type || '',
          geoid: geoidFromUrn(s.relates_to_urn)
        })).filter(r => includeAddresses || r.type !== 'address'));
        return undefined;
      }).catch(() => {
        setSearchResults([]);
      }).finally(() => {
        setIsSearching(false);
      });
    }, debounceMs);
  }, [debounceMs, minSearchLength, includeAddresses]);
  const selectResult = useCallback(result => {
    if (!result.value && !result.geoid) return null;
    setSearchResults([]);
    // Use the geoid as the value when present so the location round-trips
    // identically through save -> structured_locations -> read (no clobber).
    return {
      type: result.type,
      value: result.geoid ?? result.value,
      label: result.title,
      ...(result.geoid ? {
        geoid: result.geoid
      } : {})
    };
  }, []);
  const clearResults = useCallback(() => {
    setSearchResults([]);
  }, []);
  return {
    data: {
      searchResults,
      isSearching
    },
    actions: {
      search,
      selectResult,
      clearResults
    }
  };
};

var locationSearch = /*#__PURE__*/Object.freeze({
    __proto__: null,
    formatLocationLabel: formatLocationLabel,
    useLocationSearch: useLocationSearch
});

/**
 * Filter Group Definitions
 */
const transformMarketingConfigToOptions = marketingConfig => marketingConfig.sort((a, b) => a.configuration.srp.order - b.configuration.srp.order).map(item => ({
  value: item.tag.toLowerCase(),
  label: item.configuration.srp.label,
  tooltip: item.configuration.srp.is_tooltip_active && item.configuration.srp.tooltip?.length ? item.configuration.srp.tooltip : null
}));
// Property Type
const getPropertyFilter = () => new FilterBuilder('propertyType', 'multiSelect', 'string[]').label('Property').mode('checkboxButton').options([{
  value: ['single-family'],
  label: 'Single Family',
  icon: 'house'
}, {
  value: ['multi-family'],
  label: 'Multi Family',
  icon: 'apartment'
}, {
  value: ['condos-townhouses'],
  label: 'Condo, single unit',
  icon: 'arrow-right-to-city'
}, {
  value: ['land'],
  label: 'Land',
  icon: 'trees'
}]);
const propertyTypeFilterDefinition = new FilterGroupBuilder('propertyType', 'Property Type').items([getPropertyFilter().styleIntent({
  displayTitle: false
}).build()]).build();
// Sale Format
const getSaleFormatFilter = () => new FilterBuilder('auctionFormat', 'multiSelect', 'string[]').label('Sale Format').mode('checkboxButton').options([{
  value: ['online'],
  label: 'Online Auction',
  icon: 'desktop'
}, {
  value: ['in_person'],
  label: 'In Person Auction',
  icon: 'gavel'
}, {
  value: ['remote_bid'],
  label: 'Remote Bid',
  icon: 'mobile-signal-out'
}, {
  value: ['direct_offer'],
  label: 'Offer',
  icon: 'messages-dollar'
}]);
const saleFormatFilterDefinition = new FilterGroupBuilder('saleFormat', 'Sale Format').items([getSaleFormatFilter().styleIntent({
  displayTitle: false
}).build()]).build();
// ListingType
const assetOptions = [{
  value: ['bank-owned', 'newly-foreclosed'],
  label: 'REO Bank Owned'
}, {
  value: ['foreclosures'],
  label: 'Foreclosure Homes'
}, {
  value: ['private-seller'],
  label: 'Private Seller'
}];
const getListingTypeFilter = optionsDecorator => {
  const decoratedOptions = optionsDecorator ?
  // calling this filter name assetType to be consistent with our schema
  optionsDecorator(assetOptions, 'assetType') : assetOptions;
  return new FilterBuilder('assetType', 'multiSelect', 'string[]').label('Listing Type').options(decoratedOptions);
};
/**
 * Gets the asset type filter definition...
 * This is a function as it allows for optional optionsDecorator to be passed in to modify the options.
 * @param optionsDecorator
 * @returns
 */
const getListingTypeFilterDefinition = optionsDecorator => new FilterGroupBuilder('listingType', 'Listing Type').items([getListingTypeFilter(optionsDecorator).styleIntent({
  displayTitle: false
}).build()]).build();
// // Occupancy
// const getOccupancyFilter = () =>
//   new FilterBuilder('occupancyStatus', 'multiSelect', 'string[]')
//     .label('Occupancy')
//     .labelIconProps({
//       name: 'house-person-return',
//     })
//     .options<'multiSelect'>([
//       // TODO: See what are the right values for the new options
//       { value: ['tenant'], label: 'Tenant Occupied' },
//       { value: ['adv_occupied'], label: 'Adversely Occupied' },
//       { value: ['rentback'], label: 'Seller Rentback' },
//       { value: ['vacant'], label: 'Vacant' },
//       { value: ['vacant_at_closing'], label: 'Vacant at Closing' },
//     ])
// const occupancyFilterDefinition = new FilterGroupBuilder(
//   'occupancy',
//   'Occupancy'
// )
//   .items([getOccupancyFilter().styleIntent({ displayTitle: false }).build()])
//   .build()
const getMarketingTagFilterDefinition = marketingTagConfig => new FilterSectionBuilder('marketInsights').label('').filters([new FilterBuilder('marketingTag', 'multiSelect', 'string').label('Market Insights & Deal Signals').labelIconProps({
  name: 'chart-mixed'
}).mode('chip').options(transformMarketingConfigToOptions(marketingTagConfig)).converterOverrides({
  toViewModelValue: v => {
    if (typeof v === 'string') {
      return v ? v.split(',') : [];
    }
    return v ? [v.toString()] : [];
  },
  toAppModelValue: v => {
    if (Array.isArray(v)) {
      return v.join(',');
    } else {
      return v;
    }
  }
}).build()]).build();
const getBuyerAdvantagesSectionDefinition = config => new FilterSectionBuilder('buyerAdvantages').label('Buyer Advantages').labelIconProps({
  name: 'magnifying-glass-dollar'
}).styleIntent({
  showDivider: false,
  twoColumns: 'desktop-only'
}).filters([new FilterBuilder('occupancyStatus', 'multiSelect', 'string[]').styleIntent({
  displayTitle: false
}).options([{
  value: ['vacant'],
  label: 'Vacant'
}]).build(), new FilterBuilder('showReserve', 'boolean', 'string').label(config?.featureFlags?.['fe.resi.enable-credit-bid-reveal'] ? 'Credit Bid or Reserve Disclosed' : 'Reserve Disclosed').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : false
}).build(), new FilterBuilder('noBuyerPremiumFilter', 'boolean', 'string').label("No Buyer's Premium").converterOverrides({
  toViewModelValue: v => v === 'n',
  toAppModelValue: v => v ? 'n' : false
}).build(), new FilterBuilder('financingAvailable', 'boolean', 'string').label('Eligible for Financing').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : false
}).build(), new FilterBuilder('brokerCoopAvailable', 'boolean', 'string').label('Broker Coop').description('Commission paid to buyer’s agent').converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : false
}).build(), new FilterBuilder('presale', 'boolean', 'string').label('First Look').description([{
  label: 'Learn More:',
  type: 'text'
}, {
  href: '/firstlook',
  label: "View First Look FAQ's",
  type: 'link'
}]).converterOverrides({
  toViewModelValue: v => v === 'y',
  toAppModelValue: v => v ? 'y' : false
}).build(), new FilterBuilder('listingType', 'boolean', 'string[]').label('Sold, Pending, or Removed').converterOverrides({
  toViewModelValue: v => arraysAreEqual(v, ['pending', 'closed', 'canceled']),
  toAppModelValue: v => v ? ['pending', 'closed', 'canceled'] : ['active']
}).build()]).build();
const getMoreFiltersDefinition = config => new FilterGroupBuilder('moreFilters', 'Filters').mode('completeFilter').labelIconProps({
  name: 'sliders'
}).items([new FilterSectionBuilder('propertySection').label('').filters([getPropertyFilter().build(), new FilterBuilder('beds', 'inlineSelect', 'number').label('Beds').mode('range').options([{
  value: '1',
  label: '1'
}, {
  value: '2',
  label: '2'
}, {
  value: '3',
  label: '3'
}, {
  value: '4',
  label: '4'
}, {
  value: '5',
  label: '5+'
}]).build(), new FilterBuilder('baths', 'inlineSelect', 'number').label('Baths').mode('single').options([{
  value: '1',
  label: '1+'
}, {
  value: '1.5',
  label: '1.5+'
}, {
  value: '2',
  label: '2+'
}, {
  value: '2.5',
  label: '2.5+'
}, {
  value: '3',
  label: '3+'
}, {
  value: '4',
  label: '4+'
}]).converterOverrides({
  toAppModelValue: v => {
    if (v === null || v === undefined) return null;
    // Ensure we always return an array for internal state
    return Array.isArray(v) ? v : [String(v)];
  },
  toViewModelValue: v => {
    if (!v) return v;
    // Return string for UI display
    return Array.isArray(v) ? v[0] : v;
  }
}).build(), new FilterBuilder('sqft', 'range', 'number').label('Square Footage').styleIntent({
  width: 'half'
}).shortLabel('Sqft').mode('select').range({
  optionList: [500, 750, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000, 2500, 3000, 4000, 5000].map(value => ({
    value,
    label: formatNumber$1(value)
  }))
}).build(), new FilterBuilder('price', 'range', 'number').label('Price Range').styleIntent({
  width: 'half'
}).mode('select').shortLabel('Price').range({
  optionList: [50000, 100000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000].map(value => ({
    value,
    label: formatMoney$1(value)
  }))
}).build()]).build(), new FilterSectionBuilder('listingSection').label('').filters([getSaleFormatFilter().build(), getListingTypeFilter(config?.optionsDecorator).styleIntent({
  twoColumns: 'desktop-only'
}).build()]).build(),
// getOccupancyFilter().styleIntent({ twoColumns: 'desktop-only' }).build(),
getBuyerAdvantagesSectionDefinition(config), ...(config?.marketingTagConfig ? [getMarketingTagFilterDefinition(config.marketingTagConfig)] : []), new FilterSectionBuilder('listingStatus').label('').styleIntent({
  twoColumns: 'all',
  showDivider: false
}).filters([
// new FilterBuilder('status', 'multiSelect', 'string[]')
//   .label('Status')
//   .labelIconProps({ name: 'house-circle-check' })
//   .styleIntent({ width: 'half' })
//   // TODO: How this is going to be integrated with backend?
//   .options<'multiSelect'>([
//     {
//       value: ['for-sale'],
//       label: 'For Sale',
//       description: '(Offer only)',
//     },
//     { value: ['in-progress'], label: 'Auction in Progress' },
//     { value: ['clear'], label: 'Cleared Sale' },
//     { value: ['pending'], label: 'Pending Sale' },
//     { value: ['closed'], label: 'Completed Sale' },
//   ])
//   .build(),
new FilterBuilder('daysSinceFirstPublished', 'singleSelect', 'string').label('Time on Auction').labelIconProps({
  name: 'clock'
}).styleIntent({
  width: 'half'
}).placeholder('No Max').options([{
  value: null,
  label: 'No Max'
}, {
  value: '1',
  label: 'Less than 7 days'
}, {
  value: '2',
  label: '7-30 days'
}, {
  value: '3',
  label: '30-90 days'
}, {
  value: '4',
  label: 'More than 90 days'
}]).build()]).build()]).build();
const definitionGenerator$1 = config => {
  return config?.isMobile ? [getMoreFiltersDefinition(config)] : [propertyTypeFilterDefinition, saleFormatFilterDefinition, getListingTypeFilterDefinition(config?.optionsDecorator),
  // occupancyFilterDefinition,
  getMoreFiltersDefinition(config)];
};

/* istanbul ignore file */

var index$i = /*#__PURE__*/Object.freeze({
    __proto__: null,
    definitionGenerator: definitionGenerator$2,
    getAssetType: getAssetType,
    newDefinitionGenerator: definitionGenerator$1
});

/* eslint-disable blueprint/namespace-exports -- Legacy: non-standard exports (buyerListingSearchFilters, sorting) need migration */
const providers$a = {
  lookupLocation,
  fetchMarketingTagConfig,
  fetchLocationSuggestions
};
const sorting = {
  constants: sorting$2,
  utils: sorting$1
};

var index$h = /*#__PURE__*/Object.freeze({
    __proto__: null,
    buyerListingSearchFilters: index$i,
    constants: constants$3,
    hooks: hooks$b,
    locationSearch: locationSearch,
    providers: providers$a,
    sorting: sorting,
    types: types$3,
    utils: utils$8
});

const deviceStorageKey = 'blueprint::nudges';
const defautConfig = {
  purchase_profiles: '2d',
  investor_preferences: '1w',
  sms_opt_in: '1w',
  authentication_prompt: '2w',
  feedback_button_prompt: '90d',
  buying_preference_nudge: '2d',
  log_in_to_see_cb: '1d',
  dual_track_path: '1w'
};
// Anonymous nudges (logged-out visitors) have no backend source of truth, so
// localStorage is their only store. User-scoped nudges are excluded here: they
// are seeded per-user via hydrate(), so backfilling from local defaults could
// leak one browser user's state into another's session.
const ANONYMOUS_NUDGE_KEYS = ['log_in_to_see_cb', 'dual_track_path'];
const useApplicationNudges = ({
  config = defautConfig,
  deviceStorage = new DeviceStorageAdapter({
    storage: new NullDeviceStorage({
      namespace: 'nudges'
    })
  })
}) => {
  const [nudgeData, setNudgeData] = useState({
    active: new Set(),
    inactive: new Set(),
    pristine: new Set()
  });
  const initialStorageNudgeData = useMemo(() => ({
    sms_opt_in: null,
    purchase_profiles: null,
    investor_preferences: null,
    authentication_prompt: null,
    feedback_button_prompt: null,
    buying_preference_nudge: null,
    log_in_to_see_cb: null,
    dual_track_path: null
  }), []);
  const getConfigValue = useCallback(key => config?.[key] || defautConfig[key], [config]);
  const [storageNudgeData, _setStorageNudgeData] = useLocalStorage({
    key: deviceStorageKey,
    initialValue: initialStorageNudgeData,
    deviceStorage
  });
  const isExpired = useCallback((lastNudge, key) => {
    if (typeof lastNudge === 'undefined') {
      return true;
    }
    const currentDate = new Date();
    const calculatedNextNudge = new Date(lastNudge);
    calculatedNextNudge.setMilliseconds(calculatedNextNudge.getMilliseconds() + ms(getConfigValue(key)));
    return currentDate > calculatedNextNudge;
  }, [getConfigValue]);
  const getNudgeNextPeriodInMS = useCallback(entryKey => {
    return getConfigValue(entryKey);
  }, [getConfigValue]);
  const calculateNudgeData = useCallback(storageData => {
    const out = {
      active: new Set([]),
      inactive: new Set([]),
      pristine: new Set([])
    };
    if (!_.isNil(storageData)) {
      const storageEntries = Object.entries(storageData);
      storageEntries.forEach(([key, item]) => {
        // Ignore unknown/stale keys (e.g. persisted before a nudge rename) —
        // they have no configured duration, so processing them would pass
        // undefined to ms(). Skip them rather than crash.
        if (!getConfigValue(key)) {
          return;
        }
        if (item === null || item === undefined) {
          out.pristine.add(key);
          out.active.add(key);
        } else if (isExpired(item?.lastNudgedAt, key)) {
          out.active.add(key);
        } else {
          out.inactive.add(key);
        }
      });
    }
    return out;
  }, [isExpired, getConfigValue]);
  // useLocalStorage returns stored data as-is (no merge with initialValue), so
  // a newly added anonymous key is absent for existing users and its nudge
  // would never surface. Backfill missing anonymous keys to null (pristine);
  // existing keys are untouched (stored value wins).
  const mergedStorageNudgeData = useMemo(() => {
    const merged = {
      ...storageNudgeData
    };
    ANONYMOUS_NUDGE_KEYS.forEach(key => {
      if (!(key in merged)) {
        merged[key] = initialStorageNudgeData[key];
      }
    });
    return merged;
  }, [initialStorageNudgeData, storageNudgeData]);
  useEffect(() => {
    setNudgeData(calculateNudgeData(mergedStorageNudgeData));
  }, [calculateNudgeData, mergedStorageNudgeData]);
  // storage data is null
  const nudge = useCallback(async entryKey => {
    if (!isExpired(mergedStorageNudgeData?.[entryKey]?.lastNudgedAt, entryKey)) {
      return;
    }
    // mark the next time the nudge should be displayed
    const nextReminder = new Date();
    const additionalTime = ms(getNudgeNextPeriodInMS(entryKey));
    nextReminder.setMilliseconds(nextReminder.getMilliseconds() + additionalTime);
    const newStorageNudgeData = {
      ...mergedStorageNudgeData,
      [entryKey]: {
        lastNudgedAt: new Date().toISOString(),
        nextNudgeScheduledAt: nextReminder.toISOString()
      }
    };
    setNudgeData(calculateNudgeData(mergedStorageNudgeData));
    await _setStorageNudgeData(newStorageNudgeData);
  }, [mergedStorageNudgeData, _setStorageNudgeData, getNudgeNextPeriodInMS, calculateNudgeData, isExpired, setNudgeData]);
  const getRemainingTime = useCallback(entryKey => {
    const currentDate = new Date();
    const nextNudgeScheduled = storageNudgeData[entryKey]?.nextNudgeScheduledAt;
    const nextNudgeDate = nextNudgeScheduled ? new Date(nextNudgeScheduled) : new Date();
    return nextNudgeDate.getTime() - currentDate.getTime();
  }, [storageNudgeData]);
  // Seed nudge state from an authoritative external source (e.g. the backend
  // marketing-milestones record) into device storage. Device storage is the
  // only thing the nudge derivation reads, so without this a fresh device or
  // cleared storage would re-show a nudge the user already saw on another
  // device. Merge is last-write-wins on lastNudgedAt: an incoming value only
  // wins if it is newer than what is already persisted locally, so we never
  // clobber a fresher local nudge with a stale backend value. The
  // nextNudgeScheduledAt is always recomputed from config so callers only need
  // to supply lastNudgedAt. Writing storage triggers the recompute effect
  // above, so late hydration still re-derives active/inactive correctly.
  const hydrate = useCallback(async partial => {
    if (_.isNil(partial)) {
      return;
    }
    const merged = {
      ...storageNudgeData
    };
    let changed = false;
    const entries = Object.entries(partial);
    entries.forEach(([key, incoming]) => {
      if (_.isNil(incoming) || !getConfigValue(key)) {
        return;
      }
      const existing = storageNudgeData?.[key];
      const incomingMs = new Date(incoming.lastNudgedAt).getTime();
      if (Number.isNaN(incomingMs)) {
        return;
      }
      const existingMs = existing?.lastNudgedAt ? new Date(existing.lastNudgedAt).getTime() : Number.NEGATIVE_INFINITY;
      if (incomingMs <= existingMs) {
        return;
      }
      const nextReminder = new Date(incomingMs + ms(getNudgeNextPeriodInMS(key)));
      merged[key] = {
        lastNudgedAt: incoming.lastNudgedAt,
        nextNudgeScheduledAt: nextReminder.toISOString()
      };
      changed = true;
    });
    if (changed) {
      await _setStorageNudgeData(merged);
    }
  }, [storageNudgeData, _setStorageNudgeData, getConfigValue, getNudgeNextPeriodInMS]);
  return {
    data: nudgeData,
    actions: {
      nudge,
      getRemainingTime,
      hydrate
    }
  };
};

var hooks$a = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useApplicationNudges: useApplicationNudges
});

var index$g = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hooks: hooks$a
});

const fragments$6 = {
  propertyCard: {
    reference: '...PropertyCardFields',
    definition: gql$5`
      fragment PropertyCardFields on Property {
        __typename
        property_id
        urn
        full_address
        is_newly_listed
        is_currently_saved @include(if: $hasAuthenticatedUser)

        summary {
          total_bedrooms
          total_bathrooms
          square_footage
          lot_size
          year_built
          valuation
          structure_type_code
          structure_type_group
        }

        # The representative listing is NOT selected here — nesting
        # ListingCardFields under
        # current_disposition_sales_cycle.active_listings would exceed the
        # graph's max operation depth of 10. Feed consumers select it from
        # the shallower item-level listing field (FeedPropertyItem.listing)
        # and pass it to PropertyCard explicitly.
      }
    `
  }
};

/**
 * Default Property History field set + display config.
 *
 * Data-driven: ranked field-change frequency across 150 active prod listings
 * (MERCH-93 analysis). `listing_status` is by far the dominant signal (49/150,
 * 146 changes). The real sale-date mover is `visible_auction_start_date_time`
 * (also reflects postponements); the actual opening-bid key is `starting_bid`.
 * `start_date` and `occupancy` rarely
 * move but are kept as opportunistic. `status_group`/`auction_status` are
 * redundant with `listing_status`; reserve is internal-only; everything else was
 * plumbing/marketing noise.
 *
 * Product-owned single source of truth (web + mobile). Values are statically
 * formatted for display (enum prettify / date / currency).
 */
const DEFAULT_PROPERTY_HISTORY_CONFIG = {
  period: '365d',
  fields: {
    listing_status: {
      title: 'Status Updated',
      badge: 'Status',
      format: 'enum'
    },
    'auction/visible_auction_start_date_time': {
      title: 'Sale Date Updated',
      badge: 'Date Updated',
      format: 'date'
    },
    'auction/starting_bid': {
      title: 'Opening Bid Updated',
      badge: 'Opening Bid',
      format: 'currency'
    },
    'auction/starting_price': {
      title: 'Opening Bid Updated',
      badge: 'Opening Bid',
      format: 'currency'
    },
    'listing_configuration/postponement_new_auction_date': {
      title: 'Postponed',
      badge: 'Postponement',
      format: 'date'
    },
    'listing_configuration/occupancy_status': {
      title: 'Occupancy Updated',
      badge: 'Occupancy',
      format: 'enum'
    },
    // Kept as opportunistic — rarely moves in prod (superseded by
    // visible_auction_start_date_time for actual sale-date changes).
    'auction/start_date': {
      title: 'Sale Date Updated',
      badge: 'Date Updated',
      format: 'date'
    }
  }
};

var constants$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DEFAULT_PROPERTY_HISTORY_CONFIG: DEFAULT_PROPERTY_HISTORY_CONFIG
});

/* istanbul ignore file */
/**
 * Listing IDs for the property's disposition cycle. Uses the cycle's
 * active_listing_ids — the guaranteed set of active listings in the cycle (e.g.
 * the FCL and private-seller listings behind the unified PDP toggle).
 *
 * Selecting the scalar active_listing_ids (not active_listings) avoids the
 * resolver's inline-priming limitation. Reached through the entry listing's
 * current cycle -> property -> cycles.
 * See resi-auction-graph-api SELL-270 (#1476 Property.cycles, #1505 listing ids).
 */
const fetchPropertyListingIds = urn => fetch.forAuctionGraph({
  query: gql$5`
        query resiPropertyHistory_listingIds($urn: URN!) {
          current_disposition_sales_cycle(urn: $urn) {
            property {
              cycles {
                active_listing_ids
              }
            }
          }
        }
      `,
  variables: {
    urn
  }
}).then(res => res.body).then(({
  data
}) => data?.current_disposition_sales_cycle?.property?.cycles?.active_listing_ids ?? []).catch(() => []);
const fetchListingTimeline = (listingId, fields, period) => fetch.forAuctionGraph({
  query: gql$5`
        query resiPropertyHistory_timeline(
          $listingId: ID!
          $fields: [String!]!
          $period: String!
        ) {
          listing(listingId: $listingId) {
            listing_id
            field_change_timeline(fields: $fields, period: $period) {
              field
              changes {
                changed_at
                previous_value
                current_value
                delta_label
              }
            }
          }
        }
      `,
  variables: {
    listingId,
    fields,
    period
  }
}).then(res => res.body).then(({
  data
}) => ({
  listingId,
  timelines: data?.listing?.field_change_timeline ?? []
})).catch(() => ({
  listingId,
  timelines: []
}));
/**
 * Fetch field-change timelines for every listing of a property.
 *
 * NOTE: fans out one request per listing today. Batching (a single audit read
 * across all listing entity_ids) is a graph-side follow-up (MERCH-93 PR-A).
 */
const fetchPropertyHistoryTimelines = async (urn, config) => {
  const listingIds = await fetchPropertyListingIds(urn);
  if (!listingIds.length) return [];
  const fields = Object.keys(config.fields);
  const period = config.period || '365d';
  return Promise.all(listingIds.map(id => fetchListingTimeline(id, fields, period)));
};

var providers$9 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    fetchPropertyHistoryTimelines: fetchPropertyHistoryTimelines
});

// The graph returns previous/current values JSON.stringify'd
// (e.g. '"VACANT"', '"2028-12-12"', '500000'). Normalize back to raw values.
const parseValue = v => {
  if (v === null || v === undefined) return null;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
};
const isEmpty = v => v === null || v === undefined || v === '';
const isMeaningful = (previous, current) => {
  if (isEmpty(previous) && isEmpty(current)) return false;
  return JSON.stringify(previous) !== JSON.stringify(current);
};
// Property-level dedupe: the same underlying change can surface on more than one
// listing of the property, so collapse identical events.
const dedupeKey = (field, changedAt, prev, curr) => `${field}|${changedAt}|${JSON.stringify(prev)}|${JSON.stringify(curr)}`;
// Safe primitive coercion (audit values are string/number/boolean once parsed;
// objects fall back to JSON so we never emit '[object Object]').
const toStringSafe = value => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};
// Static enum prettifier: 'AUCTION_IN_PROGRESS' -> 'Auction In Progress'.
const prettifyEnum = value => toStringSafe(value).toLowerCase().split(/[_-]/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const formatDate = value => {
  if (value == null || value === '') return '';
  const d = new Date(toStringSafe(value));
  return Number.isNaN(d.getTime()) ? toStringSafe(value) : d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
const formatCurrency = value => {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number(toStringSafe(value));
  return Number.isNaN(n) ? toStringSafe(value) : n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
};
/** Statically format a raw value for display, per the field's configured format. */
const formatDisplayValue = (value, format) => {
  if (value == null || value === '') return '';
  switch (format) {
    case 'enum':
      return prettifyEnum(value);
    case 'date':
      return formatDate(value);
    case 'currency':
      return formatCurrency(value);
    default:
      return toStringSafe(value);
  }
};
/**
 * Merge field-change timelines from every listing of a property into a single,
 * de-duplicated, most-recent-first list of display-ready events.
 *
 * Only fields present in `config.fields` are surfaced; no-ops are filtered.
 */
const derivePropertyHistory = (perListing, config) => {
  const seen = new Set();
  const events = [];
  for (const {
    listingId,
    timelines
  } of perListing || []) {
    for (const timeline of timelines || []) {
      const fieldConfig = config.fields[timeline.field];
      if (!fieldConfig) continue;
      for (const change of timeline.changes || []) {
        const previousValue = parseValue(change.previous_value);
        const currentValue = parseValue(change.current_value);
        if (!isMeaningful(previousValue, currentValue)) continue;
        // SDK DateTime widens to string | number | Date; our events use string.
        const changedAt = String(change.changed_at);
        const key = dedupeKey(timeline.field, changedAt, previousValue, currentValue);
        if (seen.has(key)) continue;
        seen.add(key);
        events.push({
          field: timeline.field,
          listingId,
          changedAt,
          previousValue,
          currentValue,
          deltaLabel: change.delta_label ?? null,
          title: fieldConfig.title,
          badge: fieldConfig.badge,
          previousDisplay: formatDisplayValue(previousValue, fieldConfig.format),
          currentDisplay: formatDisplayValue(currentValue, fieldConfig.format)
        });
      }
    }
  }
  events.sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
  return events;
};
/**
 * Whether a property has more than one active listing (multiple disposition
 * paths). Active listings are already scoped by the graph.
 */
const hasMultipleActiveListings = activeListings => (activeListings?.length ?? 0) > 1;

var utils$7 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    derivePropertyHistory: derivePropertyHistory,
    formatDisplayValue: formatDisplayValue,
    hasMultipleActiveListings: hasMultipleActiveListings,
    parseValue: parseValue
});

/**
 * Property-level history: merges field_change_timeline across every listing the
 * property has had into a single, de-duplicated, most-recent-first event list.
 *
 * Owns the product decisions (which fields, display config, merge/dedupe) so the
 * graph stays generic and web + mobile share one implementation.
 *
 * field_change_timeline is auth-gated, so callers should gate `enabled` on an
 * authenticated session (and any feature flag).
 */
const usePropertyHistory = ({
  urn,
  config = DEFAULT_PROPERTY_HISTORY_CONFIG,
  enabled = true
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Property History is property-scoped (spans all of a property's listings), so
  // it should fetch once when the section mounts — not refetch when the user
  // toggles between the property's listings. Mount the section only when it's
  // enabled (authenticated + flag on) so this fires with the right guards.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- @adc/hooks is loosely typed
  adcHooks.useMountEffect(() => {
    let active = true;
    if (!enabled || !urn) {
      return undefined;
    }
    setLoading(true);
    setError(null);
    const run = async () => {
      try {
        const perListing = await fetchPropertyHistoryTimelines(urn, config);
        if (active) setEvents(derivePropertyHistory(perListing, config));
      } catch (e) {
        if (active) {
          setEvents([]);
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    run().catch(() => {
      /* run() handles its own errors; guard against floating promise */
    });
    return () => {
      active = false;
    };
  });
  return useMemo(() => ({
    events,
    loading,
    error
  }), [events, loading, error]);
};

var hooks$9 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    usePropertyHistory: usePropertyHistory
});

var types$2 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

var index$f = /*#__PURE__*/Object.freeze({
    __proto__: null,
    constants: constants$2,
    fragments: fragments$6,
    hooks: hooks$9,
    providers: providers$9,
    types: types$2,
    utils: utils$7
});

const fragments$5 = {
  adaptiveNavigation: {
    reference: '...AdaptiveNavigationFields',
    definition: gql$5`
      fragment AdaptiveNavigationFields on User {
        bidder_signature {
          __typename
          ... on SellerSignature {
            historical {
              listing_count
            }
          }
        }
      }
    `
  },
  // Field list lives on the PartyBuyingPreference type so it can be reused both
  // at the root buying_preferences query and nested under User (see
  // buyingPreferences below) — single source of truth, no drift across repos.
  //
  // `parameters` and `intents` are JSONObjects: `parameters` holds the
  // ListingCompatabilityFilters-shaped values used for marketplace matching;
  // `intents` holds the full user selection (incl. non-filterable values like
  // ALL / NOT_SURE) so the form can be rehydrated exactly. `marketplace_match`
  // is intentionally NOT requested here — it triggers an expensive seek
  // aggregate, so it lives in its own opt-in query (see
  // previewDemandForCurrentPreference in providers.ts).
  buyingPreferenceFields: {
    reference: '...BuyingPreferenceFields',
    definition: gql$5`
      fragment BuyingPreferenceFields on PartyBuyingPreference {
        parameters
        intents
        structured_locations {
          inclusions {
            urn
            label
            location_type
          }
          exclusions {
            urn
            label
            location_type
          }
        }
        notes {
          note_id
          content
        }
      }
    `
  },
  buyingPreferences: {
    reference: '...BuyingPreferencesFields',
    definition: gql$5`
      fragment BuyingPreferencesFields on User {
        buying_preferences {
          ...BuyingPreferenceFields
        }
      }
    `
  }
};

/**
 * Intent values: explicit no-constraint selections a user can make on the
 * buying criteria dropdowns. Stored in `intents.<field>` (never in
 * `parameters.<field>`, which holds filterable values only — the backend
 * rejects intent values there). Mirrors the backend vocabulary in
 * resi-ms-profile's PartyBuyingPreferenceConverter.
 *
 * Leaf module: imported by both definition.ts and constants.ts, so it must
 * not import from either (rollup fails the build on circular dependencies).
 */
const INTENT_VALUES = ['ANY', 'NO_MIN', 'NO_MAX', 'ALL', 'NOT_SURE'];
/** Intent value emitted by the beds/baths/yearBuilt "Any" dropdown option. */
const INTENT_ANY = 'ANY';
/** Intent values emitted by the price range "No Min" / "No Max" options. */
const INTENT_NO_MIN = 'NO_MIN';
const INTENT_NO_MAX = 'NO_MAX';
/** Whether a dropdown value is an intent value (explicit no-constraint). */
const isIntentValue = value => typeof value === 'string' && INTENT_VALUES.includes(value);
/**
 * Narrow a dropdown value to its concrete (filterable) number. Intent values
 * and anything non-numeric return null, so callers can format/filter without
 * special-casing the intent vocabulary. Accepts numeric strings because
 * stored LCF parameters carry values as strings (e.g. price_min: '200000').
 */
const toConcreteValue = value => {
  if (value == null || isIntentValue(value)) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/* istanbul ignore file */
const updateOptInInformation = variables => fetch__default.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_updateVerifiedPhone')}($phone: String!, $isOptIn: Boolean!){
          updateVerifiedPhoneOptIn(phone: $phone, isOptIn: $isOptIn)
        }
     `,
  variables
}).then(({
  body
}) => {
  if (body.errors && body.errors[0]) {
    throw body.errors[0];
  }
  return body.data.updateVerifiedPhoneOptIn;
});
const getUserMarketingMilestones = () => fetch__default.forAuctionGraph({
  query: gql$5`
        query ${embeddableOperation('getUserMarketingMilestones')}{
          current_user {
            marketing_milestones {
              survey_feedback_submitted_date
              buying_preference_last_nudged_date
            }
          }
        }
      `
}).then(({
  body
}) => body.data.current_user);
const setUserMarketingMilestones = milestone => fetch__default.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_upsertUserMarketingMilestones')}($milestone: String) {
          upsertUserMarketingMilestones(milestone: $milestone) {
            survey_feedback_submitted_date
            buying_preference_last_nudged_date
          }
        }
      `,
  variables: {
    milestone
  }
}).then(({
  body
}) => body.data.upsertUserMarketingMilestones);
class SavePropertyError extends Error {}
// Feed fetches get a longer budget than @adc/fetch's 8s default: the graph
// assembles the feed from several time-boxed branches and then hydrates
// property + listing per item, which can exceed 8s on cold caches or
// high-latency environments (local dev against remote backends). The value
// is also sent to the graph as its duration-budget hint. Callers with a
// tighter budget (e.g. an SSR seed that must never sink the page render)
// pass their own `requestTimeout`.
const FEED_REQUEST_TIMEOUT_MS = 15000;
// How far back the feed's lifecycle-event selection reaches. Mirrors the
// graph's feed `lifecycleWindow` knob (3d): the graph's lifecycle_events
// filter DEFAULTS to a 1-day window, so without an explicit `from` the badges
// for 2-3 day old events would silently drop while the card still shows in
// Recent Updates.
const FEED_EVENTS_WINDOW_DAYS = 3;
// The current user's discovery feed (Your Feed). One flat, cursor-paginated
// The current user's discovery feed (Your Feed). One flat, cursor-paginated
// list; lifecycle items first then recommendations, each tagged with `group`.
// Property is selected via the shared PropertyCardFields fragment. The
// representative listing is selected at the ITEM level (`item.listing`, the
// schema's convenience field): nesting ListingCardFields under
// property.current_disposition_sales_cycle.active_listings would exceed the
// graph's max operation depth of 10. Consumers read `item.listing` directly.
//
// Soft login (MERCH-184): when a `shareToken` is given (the encrypted share
// token carried by Daily Digest email links), the feed is resolved through
// `Query.current_session(fromToken:)` instead of `current_user`, so the token
// owner's feed renders without authentication. Same Feed selection either
// way; `hasAuthenticatedUser` stays false in token mode so the property
// fragment keeps its logged-out shape (no saved-heart seed).
const getFeed = ({
  variant = enums$5.FeedVariant.Live,
  after = null,
  first,
  user = null,
  shareToken = null,
  req = null,
  requestTimeout = FEED_REQUEST_TIMEOUT_MS
} = {}) => {
  const fromToken = Boolean(shareToken);
  return fetch__default.forAuctionGraph({
    query: gql$5`
          ${fragments$7.listingCard.definition}
          ${fragments$6.propertyCard.definition}
          query ${embeddableOperation(fromToken ? 'blueprint_getSharedFeed' : 'blueprint_getFeed')}($variant: FeedVariant!, $after: ID, $first: NonNegativeInt, $hasAuthenticatedUser: Boolean!, $eventsFrom: DateTime${fromToken ? ', $shareToken: String' : ''}) {
            ${fromToken ? 'current_session(fromToken: $shareToken)' : 'current_user'} {
              feed(variant: $variant, filters: { after: $after, first: $first }) {
                total
                after
                aggregation
                content {
                  __typename
                  ... on FeedPropertyItem {
                    item_id
                    property_id
                    urn
                    group
                    event_occurred_at
                    attribution {
                      source
                      attribution_label
                    }
                    property {
                      ${fragments$6.propertyCard.reference}
                    }
                    listing {
                      ${fragments$7.listingCard.reference}
                      # Lifecycle events power the event badges (New
                      # Listing / Price Drop / Credit Bid Disclosed) and the
                      # previous-price strikethrough. Selected ONLY here —
                      # not in ListingCardFields — so non-feed surfaces
                      # (search, saved) don't fan out to the events backend.
                      lifecycle_events(filters: { from: $eventsFrom }) {
                        event_type
                        label
                        occurred_at
                        ... on PriceDroppedListingLifeCycleEvent {
                          previous_seller_valuation_amount
                          current_seller_valuation_amount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
    variables: {
      variant,
      after,
      first,
      hasAuthenticatedUser: Boolean(user),
      eventsFrom: subDays(new Date(), FEED_EVENTS_WINDOW_DAYS),
      ...(fromToken ? {
        shareToken
      } : {})
    }
  },
  // @ts-ignore we are ok with req null
  {
    req,
    requestTimeout
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    // An invalid/expired token resolves to an anonymous session whose feed
    // is null — surfaced as the same "could not fetch" error the hook
    // already degrades on (empty feed, no error page).
    const feed = fromToken ? data?.current_session?.feed : data?.current_user?.feed;
    if (!feed) {
      throw partialGraphErrorFactory$2('Could not fetch feed', errors);
    }
    return feed;
  });
};
// Toggle a property's hidden state on the feed (persisted in ms-profile).
const toggleHiddenProperty = (propertyId, hidden) => fetch__default.forAuctionGraph({
  query: gql$5`
    mutation ${embeddableOperation('blueprint_toggleHiddenProperty')}($propertyId: ID!, $hidden: Boolean!) {
      toggleHiddenProperty(propertyId: $propertyId, hidden: $hidden)
    }
  `,
  variables: {
    propertyId,
    hidden
  }
}).then(res => {
  if (res.body.errors) {
    throw partialGraphErrorFactory$2('Could not toggle hidden property', res.body.errors);
  }
  return {
    status: 'success'
  };
});
const saveProperty = propertyId => fetch__default.forAuctionGraph({
  query: gql$5`
    mutation ${embeddableOperation('blueprint_saveProperty')}($input: SavePropertyInput!) {
      saveProperty(input: $input)
    }
  `,
  variables: {
    input: {
      property_id: propertyId
    }
  }
}).then(res => {
  if (res.body.errors) {
    throw new SavePropertyError();
  }
  return {
    status: 'success'
  };
});
const deleteSavedProperty = propertyId => fetch__default.forAuctionGraph({
  query: gql$5`
    mutation ${embeddableOperation('blueprint_deleteSavedProperty')}($input: DeleteSavedPropertyInput!) {
      deleteSavedProperty(input: $input)
    }
  `,
  variables: {
    input: {
      property_id: propertyId
    }
  }
}).then(res => {
  if (res.body.errors) {
    throw new SavePropertyError();
  }
  return {
    status: 'success'
  };
});
const defaultVariables = {
  // TODO: Pagination
  // page: 0,
  hasAuthenticatedUser: true,
  req: null,
  take: 100,
  onlySmsMatches: false
};
const getListingsMatched = (variables = defaultVariables) => {
  const finalVariables = {
    ...defaultVariables,
    ...variables
  };
  return fetch__default.forAuctionGraph({
    query: gql$5`
          ${fragments$7.listingCard.definition}
          query ${embeddableOperation('getListingsMatched')}($filters: ListingMatchesFilters, $hasAuthenticatedUser: Boolean!){
            listing_matches (filters: $filters){
              current_page
              size
              total_count
              content {
                listing_match_id
                listing  {
                   ${fragments$7.listingCard.reference}
                }
              }
            }
          }

        `,
    variables: {
      filters: {
        // TODO: Pagination
        // page: finalVariables.page,
        take: finalVariables.take,
        only_sms_matches: Boolean(finalVariables.onlySmsMatches)
      },
      hasAuthenticatedUser: finalVariables.hasAuthenticatedUser
    }
  },
  // @ts-ignore we are ok with req null
  {
    req: finalVariables.req
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.listing_matches === null) {
      throw partialGraphErrorFactory$2('Could not fetch latest matches', errors);
    }
    return data.listing_matches;
  });
};
const MAX_RECOMMENDED_LISTINGS = 24;
// @ts-ignore postal code should be ignored sometimes
const defaultVariablesRecommendedListings = {
  take: MAX_RECOMMENDED_LISTINGS,
  hasAuthenticatedUser: true,
  req: null
};
const getRecommendedListings = (variables = defaultVariablesRecommendedListings) => {
  const finalVariables = {
    ...defaultVariables,
    ...variables
  };
  return fetch__default.forAuctionGraph({
    query: gql$5`
          ${fragments$7.listingCard.definition}
          query ${embeddableOperation('getRecommendedListings')}($filters: GroupedRecommendedListingsFilters!, $hasAuthenticatedUser: Boolean!){
            grouped_recommended_listings(filters: $filters) {
              category
              label
              short_title
              subtitle_label
              order
              aliases
              attributed_model
              listings {
                ${fragments$7.listingCard.reference}
              }
            }
          }
        `,
    variables: {
      filters: {
        take: finalVariables.take,
        postal_code: finalVariables.postal_code,
        categories: finalVariables.categories
      },
      hasAuthenticatedUser: finalVariables.hasAuthenticatedUser
    }
  },
  // @ts-ignore we are ok with req null
  {
    req: finalVariables.req
  }).then(res => res.body).then(({
    data
  }) => data?.grouped_recommended_listings || null);
};
/**
 * Reads the current user's Deals-for-You recommendation facts from
 * product_state.deals. A flat bag of facts — callers derive their own UI
 * decisions (link visibility from `fresh`, indicator dot from `unviewed`) and
 * analytics (population from `totalCount`).
 */
const getDealsProductState = () => {
  return fetch__default.forAuctionGraph({
    query: gql$5`
        query ${embeddableOperation('getDealsProductState')} {
          current_user {
            product_state {
              deals {
                total_count
                fresh
                unviewed
              }
            }
          }
        }
      `
  }).then(res => res.body).then(({
    data
  }) => {
    const deals = data?.current_user?.product_state?.deals;
    return {
      totalCount: deals?.total_count ?? 0,
      fresh: deals?.fresh ?? false,
      unviewed: deals?.unviewed ?? false
    };
  });
};
// ─── Buying preference ↔ ListingCompatabilityFilters mapping ─────────────────
//
// The form (useBuyingCriteria) keeps state in BuyingCriteriaState, but the
// backend now stores a ListingCompatabilityFilters-shaped `parameters` object
// (consumed by seek for marketplace matching) plus an `intents` object holding
// the full user selection. To stay byte-for-byte consistent with the SRP
// (useListingsSearch) we reuse the search FiltersModel converters
// (fe→api on save, api→url→fe on read) rather than maintaining a parallel map.
//
// Input and read shapes are symmetric (`{ parameters, intents }`), so the
// optimistic object the apps store back into user context round-trips through
// mapGraphQLToState unchanged.
// The definition stores values in the Search/LCF vocabulary directly
// ('vacant', 'bank-owned', 'single-family', ...), so no enum translation is
// needed — filterable values flow straight through FiltersModel. The only
// non-Search values are the intent markers (ALL / NOT_SURE): a disposition
// ("I'm open to anything" / "not sure") rather than a filter value.
//
// NOTE: `occupied` has no SRP precedent (Search only models `vacant`); the
// value comes from the definition and follows the Search lowercase convention.
// If seek expects different casing that's a backend concern shared with Search.
const INTENT_MARKERS = new Set(['ALL', 'NOT_SURE']);
// occupancy / sale type / property type: the BuyingCriteriaState key, the
// FiltersModel `fe` key it maps to, and the LCF/seek key it serializes to.
const MULTI_SELECT_FIELDS = [{
  key: 'occupancy',
  feKey: 'occupancyStatus',
  lcfKey: 'occ_status'
}, {
  key: 'saleType',
  feKey: 'assetType',
  lcfKey: 'usecode_product_type'
}, {
  key: 'propertyType',
  feKey: 'propertyType',
  lcfKey: 'usecode_property_type'
}];
const MULTI_SELECT_LCF_KEYS = MULTI_SELECT_FIELDS.map(field => field.lcfKey);
// Dropdown fields: the BuyingCriteriaState slice and the LCF/intent key it
// maps to. Each holds a concrete value, an intent value (ANY / NO_MIN /
// NO_MAX), or null (untouched). The intent value goes to `intents.<key>`
// only; concrete values are mirrored into `intents.<key>` so the backend can
// distinguish an explicit selection from an untouched field.
const DROPDOWN_INTENT_FIELDS = [{
  lcfKey: 'price_min',
  intentValue: INTENT_NO_MIN,
  read: criteria => criteria.price?.min
}, {
  lcfKey: 'price_max',
  intentValue: INTENT_NO_MAX,
  read: criteria => criteria.price?.max
}, {
  lcfKey: 'beds_min',
  intentValue: INTENT_ANY,
  read: criteria => criteria.beds
}, {
  lcfKey: 'baths_min',
  intentValue: INTENT_ANY,
  read: criteria => criteria.baths
}, {
  lcfKey: 'yr_bt_min',
  intentValue: INTENT_ANY,
  read: criteria => criteria.yearBuilt
}];
const DROPDOWN_INTENT_LCF_KEYS = DROPDOWN_INTENT_FIELDS.map(field => field.lcfKey);
const SNAPSHOT_PARAM_KEYS = ['price_min', 'price_max', 'beds_min', 'baths_min', 'yr_bt_min', ...MULTI_SELECT_LCF_KEYS, 'locations'];
const SNAPSHOT_INTENT_KEYS = [...MULTI_SELECT_LCF_KEYS, ...DROPDOWN_INTENT_LCF_KEYS];
const PARAM_NULL_DEFAULTS = _.zipObject(SNAPSHOT_PARAM_KEYS, SNAPSHOT_PARAM_KEYS.map(() => null));
const INTENT_NULL_DEFAULTS = _.zipObject(SNAPSHOT_INTENT_KEYS, SNAPSHOT_INTENT_KEYS.map(() => null));
// Serialized multi-location grammar shared with graph-api
// (parseSerializedGeolocationValue): comma-separated `division:value` entries.
// Preferred form is `geoid:<id>` (a stable id the graph resolves to the current
// value + label via forward.json). Named divisions (city/county/state/zip) are
// also accepted but the graph can't label them, so we always serialize geoid
// when a location carries one. Note: geoid ids never contain a comma, which
// also sidesteps the value-with-comma hazard of named entries.
const LOCATION_DIVISIONS = new Set(['city', 'county', 'state', 'zip']);
// Emit every clearable field as explicit null (not omitted) so a consumer
// deep-merging the snapshot clears removed fields instead of keeping stale
// values. Applied to the persisted mutation and the optimistic context;
// `mapStateToInput` stays omitted because search reuses it.
const toCompleteSaveSnapshot = ({
  parameters,
  intents
}) => ({
  parameters: _.defaults({
    ...parameters
  }, PARAM_NULL_DEFAULTS),
  intents: _.defaults({
    ...intents
  }, INTENT_NULL_DEFAULTS)
});
// Multi-select filter values are usually arrays, but the filter framework can
// hand back a single value for exclusive chips — coerce so array ops are safe.
const toArray = value => Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];
const withoutIntentMarkers = values => toArray(values).filter(value => value && !INTENT_MARKERS.has(value));
/** BuyingCriteriaState → the FiltersModel `fe` shape (Search-equivalent). */
const buildSearchFeFilters = criteria => {
  const fe = {
    // price / beds / baths / yearBuilt already share the FiltersModel fe
    // vocabulary. beds/baths are a single minimum: rangeToObject's non-array
    // branch emits only `${key}_min` (no exact-match `_max`), which is exactly
    // what a buying preference wants — so they pass through as scalars.
    // Intent values (ANY / NO_MIN / NO_MAX) are dispositions, not filters —
    // they never enter `parameters` (the backend rejects them there).
    price: {
      min: criteria.price?.min === INTENT_NO_MIN ? null : criteria.price?.min,
      max: criteria.price?.max === INTENT_NO_MAX ? null : criteria.price?.max
    }
  };
  if (criteria.beds != null && criteria.beds !== INTENT_ANY) fe.beds = criteria.beds;
  if (criteria.baths != null && criteria.baths !== INTENT_ANY) fe.baths = criteria.baths;
  if (criteria.yearBuilt != null && criteria.yearBuilt !== INTENT_ANY) fe.yearBuilt = criteria.yearBuilt;
  // occupancy/saleType differ from fe only by key name; propertyType matches.
  // Intent markers (ALL / NOT_SURE) are dispositions, not filters — dropped.
  MULTI_SELECT_FIELDS.forEach(({
    key,
    feKey
  }) => {
    const values = withoutIntentMarkers(criteria[key]);
    if (values.length) fe[feKey] = values;
  });
  return fe;
};
const serializeLocations = locations => {
  const serialized = (locations ?? []).map(loc => {
    if (!loc) return null;
    // Prefer the stable geoid the graph can resolve to value + label.
    if (loc.geoid) return `geoid:${loc.geoid}`;
    // Fallback: named division (graph accepts these too, but won't label them).
    const type = (loc.type ?? '').toLowerCase();
    if (LOCATION_DIVISIONS.has(type) && loc.value) return `${type}:${loc.value}`;
    return null;
  }).filter(Boolean).join(',');
  return serialized || null;
};
const parseLocations = serialized => {
  if (!serialized || typeof serialized !== 'string') return [];
  return serialized.split(',').flatMap(entry => {
    const idx = entry.indexOf(':');
    if (idx === -1) return [];
    const division = entry.slice(0, idx).trim().toLowerCase();
    const value = entry.slice(idx + 1).trim();
    if (!value) return [];
    if (division === 'geoid') {
      // value + label aren't resolvable client-side (forward.json is
      // graph-side). The geoid is the stable id; the display label comes from
      // the server-resolved `locations` field (see mapRecognizedLocations). This
      // path is only the fallback for the optimistic object that lacks it.
      return [{
        type: '',
        value,
        label: value,
        geoid: value
      }];
    }
    if (!LOCATION_DIVISIONS.has(division)) return [];
    return [{
      type: division,
      value,
      label: value
    }];
  });
};
// Server-resolved location rows (`urn`/`label`/`location_type`) -> form state.
// `urn` is `adc:geoid:<id>` for geoid refs.
const GEOID_URN_PREFIX$1 = 'adc:geoid:';
const mapRecognizedLocations = resolved => (resolved ?? []).flatMap(loc => {
  if (!loc) return [];
  const geoid = loc.urn?.startsWith(GEOID_URN_PREFIX$1) ? loc.urn.slice(GEOID_URN_PREFIX$1.length) : undefined;
  const label = loc.label ?? '';
  return [{
    type: loc.location_type ?? '',
    value: geoid ?? label,
    label,
    ...(geoid ? {
      geoid
    } : {})
  }];
});
const mapStateToInput = criteria => {
  const fe = buildSearchFeFilters(criteria);
  const parameters = {
    version: 1,
    ...new FiltersModel(fe).feToApiValue
  };
  const intents = {};
  // A non-filterable selection (ALL / NOT_SURE) can't be a seek filter: record
  // the LCF key as null in `parameters` (the field was addressed) and keep the
  // disposition in `intents`. ALL / NOT_SURE are exclusive (single value).
  MULTI_SELECT_FIELDS.forEach(({
    key,
    lcfKey
  }) => {
    const marker = toArray(criteria[key]).find(value => INTENT_MARKERS.has(value));
    if (!marker) return;
    parameters[lcfKey] = null;
    intents[lcfKey] = marker;
  });
  // Dropdowns: an intent value selection lives in `intents` only (the LCF key
  // is left absent from `parameters` — toCompleteSaveSnapshot nulls it);
  // a concrete value is mirrored into `intents` so the field reads as
  // explicitly selected. Untouched (null) stays out of both.
  DROPDOWN_INTENT_FIELDS.forEach(({
    lcfKey,
    intentValue,
    read
  }) => {
    const value = read(criteria);
    if (value == null) return;
    intents[lcfKey] = value === intentValue ? intentValue : String(value);
  });
  const locations = serializeLocations(criteria.locations);
  if (locations) parameters.locations = locations;
  // Note intentionally NOT written to ms-profile parameters anymore — the
  // buyer note lives in ms-workflow (see saveBuyingPreferenceNote).
  return {
    parameters,
    intents
  };
};
const readMultiSelect = (feValues, intentValue) => {
  // Filterable values come back from `parameters` already in the definition's
  // Search vocabulary, so they're used as-is. If the key has no value, fall
  // back to the intent disposition (ALL / NOT_SURE).
  const values = (feValues ?? []).filter(Boolean);
  if (values.length) return values;
  return intentValue ? [intentValue] : [];
};
const mapGraphQLToState = prefs => {
  // Both the server object and the apps' optimistic object are
  // `{ parameters, intents }`. `?? prefs` additionally tolerates a raw
  // flat-parameters object.
  const params = prefs?.parameters ?? prefs ?? {};
  const intents = prefs?.intents ?? {};
  // FiltersModel.apiToUrlValue reads the synthetic range key (`price`), not
  // `price_min`/`price_max`, so inject it the same way the model does for sqft.
  const paramsForUrl = {
    ...params
  };
  if (params.price_min != null || params.price_max != null) {
    paramsForUrl.price = {
      min: params.price_min != null ? Number(params.price_min) : null,
      max: params.price_max != null ? Number(params.price_max) : null
    };
  }
  // api → url → fe, reusing the SRP converters. urlToFe expects a leading '/'
  // (it drops the first path segment).
  const fe = FiltersModel.urlToFe(`/${new FiltersModel(paramsForUrl).apiToUrlValue}`);
  return {
    // Prefer the server-resolved location rows (labels included); fall back to
    // parsing the raw serialized geoids (optimistic object / named entries).
    locations: Array.isArray(prefs?.structured_locations?.inclusions) && prefs.structured_locations.inclusions.length ? mapRecognizedLocations(prefs.structured_locations.inclusions) : parseLocations(params.locations),
    notes: prefs?.notes?.[0]?.content ?? '',
    noteId: prefs?.notes?.[0]?.note_id ?? null,
    price: {
      min: intents.price_min === INTENT_NO_MIN ? INTENT_NO_MIN : fe.price?.min ?? null,
      max: intents.price_max === INTENT_NO_MAX ? INTENT_NO_MAX : fe.price?.max ?? null
    },
    occupancy: readMultiSelect(fe.occupancyStatus, intents.occ_status),
    saleType: readMultiSelect(fe.assetType, intents.usecode_product_type),
    propertyType: readMultiSelect(fe.propertyType, intents.usecode_property_type),
    beds: intents.beds_min === INTENT_ANY ? INTENT_ANY : fe.beds?.[0] != null ? Number(fe.beds[0]) : null,
    baths: intents.baths_min === INTENT_ANY ? INTENT_ANY : fe.baths?.[0] != null ? Number(fe.baths[0]) : null,
    yearBuilt: intents.yr_bt_min === INTENT_ANY ? INTENT_ANY : fe.yearBuilt != null ? Number(fe.yearBuilt) : null
  };
};
const loadBuyingPreferences = async () => {
  const res = await fetch__default.forAuctionGraph({
    query: gql$5`
        ${fragments$5.buyingPreferenceFields.definition}
        query ${embeddableOperation('blueprint_getBuyingPreferences')} {
          current_user {
            buying_preferences {
              ${fragments$5.buyingPreferenceFields.reference}
            }
          }
        }
      `
  });
  const prefs = res?.body?.data?.current_user?.buying_preferences;
  if (!prefs) return null;
  return mapGraphQLToState(prefs);
};
const saveBuyingPreferences = async criteria => {
  const input = toCompleteSaveSnapshot(mapStateToInput(criteria));
  await fetch__default.forAuctionGraph({
    query: gql$5`
      mutation ${embeddableOperation('blueprint_savePartyBuyingPreference')}($input: PartyBuyingPreferenceInput!) {
        savePartyBuyingPreference(input: $input) {
          parameters
          intents
        }
      }
    `,
    variables: {
      input
    }
  });
};
/**
 * Upsert the buyer's free-text note for their buying criteria. Stored in
 * ms-workflow (not the ms-profile parameters); the graph resolver updates the
 * existing note or creates one. Called from the buying-criteria autosave only
 * when the note changed.
 */
const saveBuyingPreferenceNote = async (content, noteId) => {
  const res = await fetch__default.forAuctionGraph({
    query: gql$5`
      mutation ${embeddableOperation('blueprint_savePartyBuyingPreferenceNote')}($content: String!, $noteId: ID) {
        savePartyBuyingPreferenceNote(content: $content, noteId: $noteId) {
          note_id
          content
        }
      }
    `,
    variables: {
      content,
      noteId: noteId ?? null
    }
  });
  return res?.body?.data?.savePartyBuyingPreferenceNote;
};
/**
 * Preview how much active inventory currently matches the user's buying
 * preference, broken down per location (Graph API builds the search filters
 * server-side). Expensive — only the matched-count UI needs this, so it's a
 * separate query. Returns [] when no preferences/locations are set.
 */
const previewDemandForCurrentPreference = async () => {
  const res = await fetch__default.forAuctionGraph({
    query: gql$5`
      query ${embeddableOperation('blueprint_getBuyingPreferenceLocationCounts')} {
        current_user {
          buying_preferences {
            marketplace_match {
              location_matches {
                urn
                label
                location_type
                active_listing_count
              }
            }
          }
        }
      }
    `
  });
  const matches = res?.body?.data?.current_user?.buying_preferences?.marketplace_match?.location_matches ?? [];
  return matches.filter(m => m != null);
};

var providers$8 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SNAPSHOT_INTENT_KEYS: SNAPSHOT_INTENT_KEYS,
    SNAPSHOT_PARAM_KEYS: SNAPSHOT_PARAM_KEYS,
    deleteSavedProperty: deleteSavedProperty,
    getDealsProductState: getDealsProductState,
    getFeed: getFeed,
    getListingsMatched: getListingsMatched,
    getRecommendedListings: getRecommendedListings,
    getUserMarketingMilestones: getUserMarketingMilestones,
    loadBuyingPreferences: loadBuyingPreferences,
    mapGraphQLToState: mapGraphQLToState,
    mapStateToInput: mapStateToInput,
    previewDemandForCurrentPreference: previewDemandForCurrentPreference,
    saveBuyingPreferenceNote: saveBuyingPreferenceNote,
    saveBuyingPreferences: saveBuyingPreferences,
    saveProperty: saveProperty,
    setUserMarketingMilestones: setUserMarketingMilestones,
    toCompleteSaveSnapshot: toCompleteSaveSnapshot,
    toggleHiddenProperty: toggleHiddenProperty,
    updateOptInInformation: updateOptInInformation
});

/**
 * Buying Criteria Filter Definitions
 *
 * These define the filter groups used in the Buying Criteria form.
 * They map to the same filter framework used in the search page (SRP),
 * allowing us to reuse FilterGroupLayout for rendering.
 *
 * Sections:
 * 1. Location — handled outside the filter framework (async search + multi-location)
 * 2. Price Range — standard range filter (min/max dropdowns)
 * 3. What You're Looking For — occupancy, sale type, property type (chip with icons)
 * 4. Property Details — beds, baths, year built (singleSelect dropdowns)
 * 5. Notes — handled outside the filter framework (free-text textarea)
 */
// ─── Section 2: Price Range ──────────────────────────────────────────────────
/**
 * Year Built options: "Any" followed by every year from the current year down
 * to 1950 (descending). Regenerated at module load so it always includes the
 * current year without manual updates.
 */
const buildYearBuiltOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [{
    value: INTENT_ANY,
    label: 'Any'
  }];
  for (let year = currentYear; year >= 1950; year -= 1) {
    years.push({
      value: year,
      label: String(year)
    });
  }
  return years;
};
// Shared by the full form and the nudge definitions. "Any" emits the ANY
// intent value (an explicit no-constraint selection) instead of null so it is
// distinguishable from an untouched dropdown.
const bedsOptions = [{
  value: INTENT_ANY,
  label: 'Any'
}, {
  value: 1,
  label: '1+'
}, {
  value: 2,
  label: '2+'
}, {
  value: 3,
  label: '3+'
}, {
  value: 4,
  label: '4+'
}, {
  value: 5,
  label: '5+'
}];
const bathsOptions = [{
  value: INTENT_ANY,
  label: 'Any'
}, {
  value: 1,
  label: '1+'
}, {
  value: 1.5,
  label: '1.5+'
}, {
  value: 2,
  label: '2+'
}, {
  value: 2.5,
  label: '2.5+'
}, {
  value: 3,
  label: '3+'
}, {
  value: 4,
  label: '4+'
}];
const priceRangeFilterDefinition = new FilterGroupBuilder('priceRange', 'Your price range').items([new FilterBuilder('price', 'range', 'number').label('Price Range').description("Set property est. market value range you're targeting.").styleIntent({
  width: 'full',
  displayTitle: false,
  showRangeLabels: true
}).mode('select').placeholder('Select', 'Select').range({
  intentValueOptions: {
    min: INTENT_NO_MIN,
    max: INTENT_NO_MAX
  },
  optionList: [25000, 50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000, 1000000, 1100000, 1200000, 1300000, 1400000, 1500000, 1600000, 1700000, 1800000, 1900000, 2000000, 2250000, 2500000, 2750000, 3000000, 3250000, 3500000, 3750000, 4000000, 4250000, 4500000, 4750000, 5000000].map(value => ({
    value,
    label: formatMoney$1(value)
  }))
}).build()]).build();
// ─── Section 3: What You're Looking For ──────────────────────────────────────
const lookingForFilterDefinition = new FilterGroupBuilder('lookingFor', "What you're looking for").items([new FilterSectionBuilder('occupancySection').label('OCCUPANCY').description('Vacant homes typically close faster. Occupied properties may involve tenants or extra steps.').styleIntent({
  showDivider: true
}).filters([new FilterBuilder('occupancy', 'multiSelect', 'string').styleIntent({
  displayTitle: false,
  chipSize: 'large'
}).mode('chip').options([{
  value: 'vacant',
  label: 'Prefer Vacant',
  icon: 'house-chimney',
  exclusive: true
}, {
  value: 'occupied',
  label: 'Prefer Occupied',
  icon: 'house-user',
  exclusive: true
}, {
  value: 'ALL',
  label: 'Open to both',
  icon: 'house',
  exclusive: true
}, {
  value: 'NOT_SURE',
  label: 'Not sure',
  icon: 'circle-question',
  exclusive: true
}]).build()]).build(), new FilterSectionBuilder('saleTypeSection').label('SALE TYPE').description('Each type has different timelines, competition and risk levels. Pick what matches how you buy.').styleIntent({
  showDivider: true
}).filters([new FilterBuilder('saleType', 'multiSelect', 'string').styleIntent({
  displayTitle: false,
  chipSize: 'large'
}).mode('chip').options([{
  value: 'foreclosures',
  label: 'Foreclosure',
  icon: 'gavel'
}, {
  value: 'bank-owned',
  label: 'Bank-Owned',
  icon: 'building-columns'
}, {
  value: 'private-seller',
  label: 'Private Seller',
  icon: 'sign'
}, {
  value: 'ALL',
  label: 'All',
  icon: 'grid-2',
  exclusive: true
}, {
  value: 'NOT_SURE',
  label: 'Not sure',
  icon: 'circle-question',
  exclusive: true
}]).build()]).build(), new FilterSectionBuilder('propertyTypeSection').label('PROPERTY TYPE').description("Choose everything you'd seriously consider buying.").filters([new FilterBuilder('propertyType', 'multiSelect', 'string').styleIntent({
  displayTitle: false,
  chipSize: 'large'
}).mode('chip').options([{
  value: 'single-family',
  label: 'Single-family',
  icon: 'house'
}, {
  value: 'condos-townhouses',
  label: 'Condo / single-unit',
  icon: 'building'
}, {
  value: 'multi-family',
  label: 'Multifamily',
  icon: 'buildings'
}, {
  value: 'land',
  label: 'Land',
  icon: 'mountain-sun'
}, {
  value: 'NOT_SURE',
  label: 'Not sure',
  icon: 'circle-question',
  exclusive: true
}]).build()]).build()]).build();
// ─── Section 4: Property Details ─────────────────────────────────────────────
const propertyDetailsFilterDefinition = new FilterGroupBuilder('propertyDetails', 'Property Details').items([new FilterSectionBuilder('bedsAndBathsAndYear').label('').description("Fill in what matters to you. We'll learn the rest from how you browse.").styleIntent({
  threeColumns: 'desktop-only'
}).filters([new FilterBuilder('beds', 'singleSelect', 'number').label('Bedrooms').placeholder('Select Minimum').options(bedsOptions).build(), new FilterBuilder('baths', 'singleSelect', 'number').label('Bathrooms').placeholder('Select Minimum').options(bathsOptions).build(), new FilterBuilder('yearBuilt', 'singleSelect', 'number').label('Year Built After').placeholder('Select').options(buildYearBuiltOptions()).typeahead({
  numericOnly: true,
  maxLength: 4
}).build()]).build()]).build();
/**
 * Generates the filter definitions for the Buying Criteria form.
 * This follows the same pattern as the SRP's definitionGenerator.
 */
const definitionGenerator = config => {
  const groups = [];
  if (config?.showPriceRange !== false) {
    groups.push(priceRangeFilterDefinition);
  }
  if (config?.showLookingFor !== false) {
    groups.push(lookingForFilterDefinition);
  }
  if (config?.showPropertyDetails !== false) {
    groups.push(propertyDetailsFilterDefinition);
  }
  return groups;
};
// Progressive capture: each nudge question gets its own standalone
// FilterGroupDefinition so FilterGroupLayout can render one question at a time.
/** Occupancy only (extracted from lookingFor) */
const occupancyNudgeDefinition = new FilterGroupBuilder('lookingFor', 'What types of occupancy are you open to?').items([new FilterSectionBuilder('occupancySection').label('').description('Vacant homes typically close faster. Occupied properties may involve tenants or extra steps.').filters([new FilterBuilder('occupancy', 'multiSelect', 'string').styleIntent({
  displayTitle: false
}).mode('chip').options([{
  value: 'vacant',
  label: 'Prefer Vacant',
  icon: 'house-chimney',
  exclusive: true
}, {
  value: 'occupied',
  label: 'Prefer Occupied',
  icon: 'house-user',
  exclusive: true
}, {
  value: 'ALL',
  label: 'Open to both',
  icon: 'house',
  exclusive: true
}, {
  value: 'NOT_SURE',
  label: 'Not sure',
  icon: 'circle-question',
  exclusive: true
}]).build()]).build()]).build();
/** Sale type only (extracted from lookingFor) */
const saleTypeNudgeDefinition = new FilterGroupBuilder('lookingFor', 'What types of sales are you open to?').items([new FilterSectionBuilder('saleTypeSection').label('').description('Different sale types come with different timelines, access, and competition levels.').filters([new FilterBuilder('saleType', 'multiSelect', 'string').styleIntent({
  displayTitle: false
}).mode('chip').options([{
  value: 'foreclosures',
  label: 'Foreclosure',
  icon: 'gavel'
}, {
  value: 'bank-owned',
  label: 'Bank-Owned',
  icon: 'building-columns'
}, {
  value: 'private-seller',
  label: 'Private Seller',
  icon: 'sign'
}, {
  value: 'ALL',
  label: 'All',
  icon: 'grid-2',
  exclusive: true
}, {
  value: 'NOT_SURE',
  label: 'Not sure',
  icon: 'circle-question',
  exclusive: true
}]).build()]).build()]).build();
/** Property type only (extracted from lookingFor) */
const propertyTypeNudgeDefinition = new FilterGroupBuilder('lookingFor', 'What types of properties interest you?').items([new FilterSectionBuilder('propertyTypeSection').label('').description("Choose everything you'd seriously consider buying.").filters([new FilterBuilder('propertyType', 'multiSelect', 'string').styleIntent({
  displayTitle: false
}).mode('chip').options([{
  value: 'single-family',
  label: 'Single-family',
  icon: 'house'
}, {
  value: 'condos-townhouses',
  label: 'Condo / single-unit',
  icon: 'building'
}, {
  value: 'multi-family',
  label: 'Multifamily',
  icon: 'buildings'
}, {
  value: 'land',
  label: 'Land',
  icon: 'mountain-sun'
}, {
  value: 'NOT_SURE',
  label: 'Not sure',
  icon: 'circle-question',
  exclusive: true
}]).build()]).build()]).build();
/** Beds + Baths only (extracted from propertyDetails) */
const bedsBathsNudgeDefinition = new FilterGroupBuilder('propertyDetails', 'How many bedrooms and bathrooms?').items([new FilterSectionBuilder('bedsBathsSection').label('').description('Set the minimum you need.').styleIntent({
  twoColumns: 'desktop-only'
}).filters([new FilterBuilder('beds', 'singleSelect', 'number').label('Bedrooms').placeholder('Select Minimum').options(bedsOptions).build(), new FilterBuilder('baths', 'singleSelect', 'number').label('Bathrooms').placeholder('Select Minimum').options(bathsOptions).build()]).build()]).build();
/** Year built only (extracted from propertyDetails) */
const yearBuiltNudgeDefinition = new FilterGroupBuilder('propertyDetails', 'How old of a property would you consider?').items([new FilterSectionBuilder('yearBuiltSection').label('').description('Set the minimum year built you prefer.').filters([new FilterBuilder('yearBuilt', 'singleSelect', 'number').label('Year Built From').placeholder('Select').options(buildYearBuiltOptions()).typeahead({
  numericOnly: true,
  maxLength: 4
}).build()]).build()]).build();
/** Price range nudge (same as full form but standalone export) */
const priceRangeNudgeDefinition = priceRangeFilterDefinition;
/**
 * `location` is handled outside the filter framework (custom UI).
 */
const NUDGE_GROUP_DEFINITIONS = {
  location: null,
  // Custom UI — not a filter group
  priceRange: priceRangeNudgeDefinition,
  occupancy: occupancyNudgeDefinition,
  saleType: saleTypeNudgeDefinition,
  propertyType: propertyTypeNudgeDefinition,
  bedsBaths: bedsBathsNudgeDefinition,
  yearBuilt: yearBuiltNudgeDefinition
};

// Suggestion/resolved-location urns for geo are `adc:geoid:<id>`.
const GEOID_URN_PREFIX = 'adc:geoid:';
/**
 * Build the read-shaped object to store back into user context after a save
 * (optimistic update). Unlike the mutation input (mapStateToInput, which is
 * just `{ parameters, intents }`), this also carries `structured_locations`
 * built from the rich criteria, so re-deriving initialData via
 * mapGraphQLToState reproduces the same state (labels + geoids) and does NOT
 * clobber the active form.
 */
const buildPreferenceContext = criteria => {
  const {
    parameters,
    intents
  } = toCompleteSaveSnapshot(mapStateToInput(criteria));
  return {
    parameters,
    intents,
    structured_locations: {
      inclusions: criteria.locations.map(loc => ({
        urn: loc.geoid ? `${GEOID_URN_PREFIX}${loc.geoid}` : null,
        label: loc.label,
        location_type: loc.type
      }))
    },
    // Note lives in ms-workflow (read as buying_preferences.notes), not in the
    // ms-profile parameters. Include note_id so a follow-up edit updates it.
    notes: criteria.notes ? [{
      note_id: criteria.noteId ?? undefined,
      content: criteria.notes
    }] : []
  };
};
/**
 * Build a Search (SRP) URL from a user's buying preferences, scoped to a single
 * location. SRP supports one location per URL, so callers produce one link per
 * saved location (e.g. MatchedPropertiesCard's per-row hrefBuilder). Reuses
 * `buildListingSearchPath` (the FiltersModel api->url converter) so the link
 * carries the same filters.
 */
const buildListingSearchUrlFromPreferences = (criteria, location) => {
  const {
    parameters
  } = mapStateToInput(criteria);
  const filters = {
    ...parameters
  };
  // SRP is single-location: scope to just this geoid, dropping the saved list.
  const geoid = location?.urn?.startsWith(GEOID_URN_PREFIX) ? location.urn.slice(GEOID_URN_PREFIX.length) : undefined;
  filters.locations = geoid ? `geoid:${geoid}` : undefined;
  // apiToUrlValue reads the synthetic `price` range key, not price_min/max.
  if (filters.price_min != null || filters.price_max != null) {
    filters.price = {
      min: filters.price_min != null ? Number(filters.price_min) : null,
      max: filters.price_max != null ? Number(filters.price_max) : null
    };
  }
  return buildListingSearchPath(filters);
};
// Neutral selections ("Open to both", "All", "Not sure", "Any") already arrive
// as null in `parameters` (the disposition is kept in `intents`), so they count
// as "no filter". `version`/`preference_notes` aren't inventory filters either.
const NON_FILTER_PARAM_KEYS = new Set(['version', 'preference_notes']);
// Reuses mapStateToInput so the "what is a filter" rule lives in one place.
// Empty criteria returns false: the signal to show total inventory.
const hasActiveBuyingCriteria = criteria => {
  if (!criteria) return false;
  const {
    parameters
  } = mapStateToInput(criteria);
  return Object.entries(parameters).some(([key, value]) => {
    if (NON_FILTER_PARAM_KEYS.has(key)) return false;
    if (value == null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
};

var utils$6 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    buildListingSearchUrlFromPreferences: buildListingSearchUrlFromPreferences,
    buildPreferenceContext: buildPreferenceContext,
    hasActiveBuyingCriteria: hasActiveBuyingCriteria
});

/**
 * The preferred order in which to nudge buying preference questions.
 */
const NUDGE_ORDER = ['location', 'priceRange', 'occupancy', 'saleType', 'propertyType', 'bedsBaths', 'yearBuilt'];

var constants$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    INTENT_ANY: INTENT_ANY,
    INTENT_NO_MAX: INTENT_NO_MAX,
    INTENT_NO_MIN: INTENT_NO_MIN,
    INTENT_VALUES: INTENT_VALUES,
    NUDGE_GROUP_DEFINITIONS: NUDGE_GROUP_DEFINITIONS,
    NUDGE_ORDER: NUDGE_ORDER,
    isIntentValue: isIntentValue,
    toConcreteValue: toConcreteValue
});

/** Manages the user's buying preferences (fetch, save, sync). */
const useBuyingPreferences = options => {
  const {
    initialData,
    onSave,
    isLoggedIn = false
  } = options ?? {};
  const shouldFetch = isLoggedIn && initialData === undefined;
  const [preferences, setPreferences] = useState(initialData ?? null);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [loadFailed, setLoadFailed] = useState(false);
  const hasFetched = useRef(false);
  // Last-persisted note, so autosave only upserts the ms-workflow note when
  // the buyer actually changed it (not on every filter-only save).
  const lastSavedNotesRef = useRef(initialData?.notes ?? '');
  // Id of the persisted note, sent on upsert so the BE updates it in place
  // instead of creating a duplicate.
  const noteIdRef = useRef(initialData?.noteId ?? null);
  // Re-sync local state when initialData changes (deep-equality to avoid loops).
  const lastInitialData = useRef(initialData);
  useEffect(() => {
    if (initialData !== undefined && !_.isEqual(initialData, lastInitialData.current)) {
      lastInitialData.current = initialData;
      setPreferences(initialData);
      lastSavedNotesRef.current = initialData?.notes ?? '';
      noteIdRef.current = initialData?.noteId ?? null;
    }
  }, [initialData]);
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const prefs = await loadBuyingPreferences();
      setPreferences(prefs);
      lastSavedNotesRef.current = prefs?.notes ?? '';
      noteIdRef.current = prefs?.noteId ?? null;
    } catch {
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const save = useCallback(async state => {
    await saveBuyingPreferences(state);
    // Note lives in ms-workflow, not the criteria parameters — upsert it only
    // when it changed since the last persisted value. Carry the resulting
    // note id forward so the next edit updates the same note.
    let nextState = state;
    const nextNotes = state.notes ?? '';
    if (nextNotes !== lastSavedNotesRef.current) {
      const saved = await saveBuyingPreferenceNote(nextNotes, noteIdRef.current);
      noteIdRef.current = saved?.note_id ?? noteIdRef.current;
      lastSavedNotesRef.current = nextNotes;
      nextState = {
        ...state,
        noteId: noteIdRef.current
      };
    }
    setPreferences(nextState);
    onSave?.(nextState);
  }, [onSave]);
  useEffect(() => {
    if (shouldFetch && !hasFetched.current) {
      hasFetched.current = true;
      refresh();
    }
  }, [shouldFetch, refresh]);
  return {
    data: {
      preferences,
      isLoading,
      loadFailed
    },
    actions: {
      refresh,
      save
    }
  };
};
/**
 * useBuyingPreferenceLocationCounts — per-location active listing counts based
 * on the user's buying preferences. Expensive (seek aggregate per location), so
 * it's a separate opt-in hook, NOT part of useBuyingPreferences.
 *
 * Reactive pattern: call `actions.refresh()` when the shared preferences change
 * (e.g. MatchedPropertiesCard effects on the shared `preferences`) so the
 * matched count stays current after a save.
 */
const useBuyingPreferenceLocationCounts = preferences => {
  const [locationCounts, setLocationCounts] = useState([]);
  const [totalInventory, setTotalInventory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isUnfiltered = !hasActiveBuyingCriteria(preferences);
  // Ref so refresh() reads the current value without needing it as a dep
  // (keeps refresh stable across isUnfiltered flips).
  const isUnfilteredRef = useRef(isUnfiltered);
  isUnfilteredRef.current = isUnfiltered;
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Re-arm on every (re)mount. React 18 StrictMode double-invokes effects in
    // dev (setup -> cleanup -> setup); a cleanup-only reset would leave this
    // stuck `false` after the first cycle, so refresh()'s `finally` guard would
    // never clear `isLoading` -> infinite loader (MatchedPropertiesCard).
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      let total = null;
      let counts = [];
      if (isUnfilteredRef.current) {
        total = await fetchTotalActiveInventory();
      } else {
        counts = await previewDemandForCurrentPreference();
      }
      if (!isMountedRef.current) return;
      setTotalInventory(total);
      setLocationCounts(counts);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []); // stable — reads isUnfiltered from ref at call-time
  // Fetch when the SAVED preferences change (the query reads server-persisted
  // state, so the consumer passes saved — not optimistic — criteria). One
  // fetch per save. null = criteria still loading -> keep the loader on. The
  // small debounce only coalesces the initial seeded -> loaded transition.
  useEffect(() => {
    if (!preferences) {
      setIsLoading(true);
      return;
    }
    // Set loading immediately so the component never renders stale/zero
    // counts during the debounce window before the fetch actually starts.
    setIsLoading(true);
    const id = setTimeout(refresh, 50);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);
  const total = useMemo(() => isUnfiltered ? totalInventory ?? 0 : locationCounts.reduce((sum, c) => sum + (c?.active_listing_count ?? 0), 0), [isUnfiltered, totalInventory, locationCounts]);
  return {
    data: {
      locationCounts,
      total,
      isUnfiltered,
      isLoading
    },
    actions: {
      refresh
    }
  };
};
const INITIAL_STATE = {
  locations: [],
  notes: '',
  noteId: null,
  price: {
    min: null,
    max: null
  },
  occupancy: [],
  saleType: [],
  propertyType: [],
  beds: null,
  baths: null,
  yearBuilt: null
};
const criteriaToFilterState = criteria => ({
  price: criteria.price,
  occupancy: criteria.occupancy,
  saleType: criteria.saleType,
  propertyType: criteria.propertyType,
  beds: criteria.beds,
  baths: criteria.baths,
  yearBuilt: criteria.yearBuilt
});
const FILTER_KEY_TO_SECTION = {
  price: 'priceRange',
  occupancy: 'lookingFor',
  saleType: 'lookingFor',
  propertyType: 'lookingFor',
  beds: 'propertyDetails',
  baths: 'propertyDetails',
  yearBuilt: 'propertyDetails'
};
/**
 * useBuyingCriteria — Form state machine for buying criteria.
 *
 * Manages filter framework state, auto-save/manual-save, discard,
 * and section status. Used by the full modal and progressive capture.
 */
const useBuyingCriteria = ({
  initialData,
  onSave,
  onPreferencesLoaded,
  debounceMs = 500,
  definitionConfig,
  track: trackFn,
  autoSave = true,
  isLoggedIn = false
} = {}) => {
  const {
    data: prefsData,
    actions: prefsActions
  } = useBuyingPreferences({
    initialData,
    onSave,
    isLoggedIn
  });
  const isLoading = prefsData.isLoading;
  const loadFailed = prefsData.loadFailed;
  const seededCriteria = initialData ? {
    ...INITIAL_STATE,
    ...initialData
  } : INITIAL_STATE;
  const [criteria, setCriteria] = useState(seededCriteria);
  const [lastSavedCriteria, setLastSavedCriteria] = useState(seededCriteria);
  const [sectionStatus, setSectionStatus] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const debounceRef = useRef(null);
  const hasTrackedVisit = useRef(false);
  const hasInitialized = useRef(initialData !== undefined);
  const isSavingRef = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (prefsData.preferences) {
        setCriteria(prev => ({
          ...prev,
          ...prefsData.preferences
        }));
        setLastSavedCriteria(prev => ({
          ...prev,
          ...prefsData.preferences
        }));
      }
      onPreferencesLoaded?.(prefsData.preferences ?? null);
      return;
    }
    if (isSavingRef.current) {
      isSavingRef.current = false;
      return;
    }
    if (prefsData.preferences) {
      setCriteria(prev => ({
        ...prev,
        ...prefsData.preferences
      }));
      setLastSavedCriteria(prev => ({
        ...prev,
        ...prefsData.preferences
      }));
    }
  }, [isLoading, prefsData.preferences, onPreferencesLoaded]);
  // Memoize filter state to prevent infinite re-render loop in useFilters
  const filterState = useMemo(() => criteriaToFilterState(criteria), [criteria]);
  // Filter framework — manages filter-based fields
  const {
    data: filterFrameworkData,
    actions: filterActions
  } = useFilters({
    filterState,
    definitionGenerator,
    definitionConfig
  });
  const readUserPreferences = useCallback(() => {
    hasInitialized.current = false;
    prefsActions.refresh();
  }, [prefsActions]);
  useEffect(() => {
    if (!hasTrackedVisit.current) {
      trackFn?.('preferences_center_visited', {});
      hasTrackedVisit.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const triggerAutoSave = useCallback((updatedCriteria, section) => {
    if (loadFailed || !autoSave) {
      if (!autoSave) setHasUnsavedChanges(true);
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSectionStatus(prev => ({
      ...prev,
      [section]: 'saving'
    }));
    debounceRef.current = setTimeout(() => {
      trackFn?.('preferences_center_updated', {
        fields_populated: Object.entries(updatedCriteria).filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0) && !(typeof v === 'object' && !Array.isArray(v) && v !== null && !('min' in v && v.min) && !('max' in v && v.max))).length
      });
      isSavingRef.current = true;
      prefsActions.save(updatedCriteria).then(() => {
        setSectionStatus(prev => ({
          ...prev,
          [section]: 'saved'
        }));
        setLastSavedCriteria(updatedCriteria);
        return undefined;
      }).catch(() => {
        setSectionStatus(prev => ({
          ...prev,
          [section]: 'error'
        }));
      });
    }, debounceMs);
  }, [loadFailed, autoSave, prefsActions, debounceMs, trackFn]);
  const updateFilter = useCallback((groupName, key, value) => {
    // Single source of truth: update `criteria` only and let the derived
    // `filterState` drive useFilters' view models. Calling `filterActions.update`
    // here too would double-drive the VM (optimistic update + full recompute from
    // the new filterState), which races on rapid edits and makes chips lag/jump.
    const section = FILTER_KEY_TO_SECTION[key] || groupName;
    setCriteria(prev => {
      const updated = {
        ...prev,
        [key]: value
      };
      trackFn?.('preferences_center_edited', {
        field: key
      });
      triggerAutoSave(updated, section);
      return updated;
    });
  }, [triggerAutoSave, trackFn]);
  const updateLocations = useCallback(locations => {
    setCriteria(prev => {
      const updated = {
        ...prev,
        locations
      };
      trackFn?.('preferences_center_edited', {
        field: 'locations'
      });
      triggerAutoSave(updated, 'location');
      return updated;
    });
  }, [triggerAutoSave, trackFn]);
  const updateNotes = useCallback(notes => {
    setCriteria(prev => {
      const updated = {
        ...prev,
        notes
      };
      trackFn?.('preferences_center_edited', {
        field: 'notes'
      });
      triggerAutoSave(updated, 'notes');
      return updated;
    });
  }, [triggerAutoSave, trackFn]);
  const resetAllFilters = useCallback(() => {
    filterActions.resetAll();
    setCriteria(INITIAL_STATE);
    setHasUnsavedChanges(false);
  }, [filterActions]);
  const saveImmediate = useCallback(async () => {
    if (loadFailed) return;
    setSectionStatus(prev => {
      const updated = {
        ...prev
      };
      Object.keys(FILTER_KEY_TO_SECTION).forEach(key => {
        const section = FILTER_KEY_TO_SECTION[key];
        updated[section] = 'saving';
      });
      return updated;
    });
    try {
      isSavingRef.current = true;
      await prefsActions.save(criteria);
      setLastSavedCriteria(criteria);
      setHasUnsavedChanges(false);
      setSectionStatus(prev => {
        const updated = {
          ...prev
        };
        Object.keys(FILTER_KEY_TO_SECTION).forEach(key => {
          const section = FILTER_KEY_TO_SECTION[key];
          updated[section] = 'saved';
        });
        return updated;
      });
      trackFn?.('preferences_center_confirmed', {});
    } catch {
      setSectionStatus(prev => {
        const updated = {
          ...prev
        };
        Object.keys(FILTER_KEY_TO_SECTION).forEach(key => {
          const section = FILTER_KEY_TO_SECTION[key];
          updated[section] = 'error';
        });
        return updated;
      });
    }
  }, [criteria, loadFailed, prefsActions, trackFn]);
  const discardGroup = useCallback(groupName => {
    const keysInGroup = Object.entries(FILTER_KEY_TO_SECTION).filter(([, section]) => section === groupName).map(([key]) => key);
    setCriteria(prev => {
      const reverted = {
        ...prev
      };
      keysInGroup.forEach(key => {
        reverted[key] = lastSavedCriteria[key];
      });
      return reverted;
    });
    trackFn?.('preferences_center_skipped', {
      group: groupName
    });
  }, [lastSavedCriteria, trackFn]);
  const data = useMemo(() => ({
    criteria,
    savedCriteria: lastSavedCriteria,
    filterData: {
      filterState: filterFrameworkData.filterState,
      filterViewModels: filterFrameworkData.filterViewModels,
      filterDefinitions: filterFrameworkData.filterDefinitions
    },
    isLoading,
    loadFailed,
    sectionStatus,
    hasUnsavedChanges
  }), [criteria, lastSavedCriteria, filterFrameworkData, isLoading, loadFailed, sectionStatus, hasUnsavedChanges]);
  const actions = useMemo(() => ({
    updateFilter,
    updateLocations,
    updateNotes,
    resetAllFilters,
    readUserPreferences,
    saveImmediate,
    discardGroup
  }), [updateFilter, updateLocations, updateNotes, resetAllFilters, readUserPreferences, saveImmediate, discardGroup]);
  return {
    data,
    actions
  };
};
/**
 * Builds `UseBuyingPreferencesOptions` from the app user context. Seeds from
 * `user.buying_preferences` so the auth-only fetch never runs for context
 * consumers, and gates fetch on `user.logged_in`.
 */
const useBuyingCriteriaOptionsFromContext = ({
  user,
  updateUser
}) => {
  const isLoggedIn = !!user?.logged_in;
  const rawPreferences = user?.logged_in ? user.buying_preferences : undefined;
  const initialData = useMemo(() => {
    if (rawPreferences === undefined) return undefined;
    if (rawPreferences === null) return null;
    return mapGraphQLToState(rawPreferences);
  }, [rawPreferences]);
  const onSave = useCallback(preferences => {
    if (!updateUser) return;
    updateUser({
      buying_preferences: buildPreferenceContext(preferences)
    });
  }, [updateUser]);
  return {
    initialData,
    onSave,
    isLoggedIn
  };
};

const logger$b = coreLogger.forModule('blueprint:user');
const useSavedProperties = ({
  initialData
}) => {
  const savedInitialData = useMemo(() => initialData.map(property => ({
    ...property,
    saved_at: new Date()
  })), [initialData]);
  const {
    collection: savedProperties,
    attemptWrite: attemptSavedPropertiesWrite
  } = useIdempotentBlueprintCollection(savedInitialData, {
    primaryKey: 'property_id',
    updateBehavior: 'merge',
    idempotencyKey: 'saved_at'
  });
  const updateSaveProperty = async (propertyId, value) => {
    const action = value ? saveProperty : deleteSavedProperty;
    return action(propertyId).then(r => {
      attemptSavedPropertiesWrite({
        data: [{
          property_id: propertyId,
          is_currently_saved: value,
          saved_at: new Date()
        }],
        source: 'local_optimistic_action'
      });
      return r;
    });
  };
  // Single source of truth for "is this property saved?". Prefers the hook's
  // collection (seeded from initialData + optimistic writes); when the property
  // isn't in the collection yet — e.g. a freshly paginated SRP card whose seed
  // hasn't been ingested by the reseed effect — it falls back to the passed
  // property's own is_currently_saved. The fallback only applies when the entry
  // is ABSENT, so an optimistic save/unsave (which puts an entry in the
  // collection) is never overridden.
  const isSaved = useCallback(property => Boolean(savedProperties.mapped.get(property.property_id)?.is_currently_saved ?? property.is_currently_saved), [savedProperties.mapped]);
  // Direction is decided from the hook's own collection (via isSaved), not from
  // the passed object's is_currently_saved — the consumer should not have to
  // tell the hook a state it already owns. Accepting the Property (not just the
  // id) is kept for consumer convenience.
  //
  // INVARIANT: this makes the toggle correct ONLY if consumers also render the
  // saved heart from this hook — either actions.isSaved(id) or the merged
  // collection entry (...savedProperties.mapped.get(id)). If a surface toggles
  // through here but displays saved-state from a different source (e.g. a raw
  // listing.is_currently_saved off redux), the card and the collection can
  // diverge and the toggle will act on the collection, not on what the user
  // saw. Historically that divergence existed (cards read redux while the hook
  // held its own list); it's resolved by sourcing both display and direction
  // from this collection — keep it that way.
  const toggleSaveProperty = async property => {
    return updateSaveProperty(property.property_id, !isSaved(property));
  };
  return {
    data: {
      savedProperties: savedProperties
    },
    actions: {
      updateSaveProperty,
      toggleSaveProperty,
      isSaved
    }
  };
};
// TODO we should be able to receive the page we are listening to!
const useMatchedListings = ({
  initialData,
  filters
}) => {
  const subjectIds = useMemo(() => initialData?.map(l => typeof l === 'string' ? l : l.listing_match_id), [initialData]);
  const {
    collection: listingMatches,
    attemptWrite: attemptMatchedListingsWrite
  } = useIdempotentBlueprintCollection(initialData, {
    primaryKey: 'listing_match_id',
    updateBehavior: 'merge'
  });
  useRealtimeWatcher({
    id: 'useMatchedListings_ListingMatch',
    // @ts-ignore subjectIds is as expected
    subjectIds,
    polling: {
      enabled: true,
      frequency: SUPPORTED_OFFSETS.NORMAL,
      executor: () => getListingsMatched(filters)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$b.info(`New matched listing data loaded. source=${source}`, data);
      attemptMatchedListingsWrite(payload);
    },
    onError: error => {
      logger$b.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$b.info('Have event', event, ctx);
    }
  });
  return {
    data: {
      // @ts-ignore
      listingMatches
    }
  };
};
const usePropertyRecommendations = ({
  initialData,
  filters,
  isLoggedIn = false
}) => {
  const subjectIds = useMemo(() => initialData?.map(l => l?.category || ''), [initialData]);
  const {
    collection: recommendations,
    attemptWrite: attemptListingsWrite,
    reset
  } = useIdempotentBlueprintCollection(initialData || [], {
    primaryKey: 'category',
    updateBehavior: 'overwrite'
  });
  // This may seem that should be already handled inside the idempotent collection, but it's not, current behavior is that on initialData change we append it, and it may be suitable for most cases, but we need a full reload for these.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    reset(initialData);
  }, [initialData]);
  useRealtimeWatcher({
    id: 'usePropertyRecommendations_Listing',
    // @ts-ignore subjectIds is as expected
    subjectIds,
    polling: {
      enabled: true,
      frequency: SUPPORTED_OFFSETS.NORMAL,
      executor: () => getRecommendedListings({
        ...filters,
        hasAuthenticatedUser: isLoggedIn
      })
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$b.info(`New recommendations data loaded. source=${source}`, data);
      attemptListingsWrite(payload);
    },
    onError: error => {
      logger$b.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$b.info('Have event', event, ctx);
    }
  });
  return {
    data: {
      recommendations: recommendations
    }
  };
};
/**
 * Reads the current user's Deals-for-You recommendation facts from
 * product_state.deals. Fires a single graph query for every logged-in user
 * (independent of the LD flag) so the facts are available for analytics
 * (population = `totalCount > 0`) as well as UI. Callers gate UI on the flag
 * and derive their own decisions: link visibility from `fresh`, the unseen-
 * update dot from `unviewed`.
 */
const useDealsProductState = ({
  isLoggedIn = false
}) => {
  // `loaded` flips true only once the graph read resolves, so consumers can
  // distinguish "no recommendations" from "not fetched yet". Analytics must not
  // identify the placeholder zeros (that would assert the user has 0 deals
  // before we know) nor identify on a failed read.
  const [state, setState] = useState({
    totalCount: 0,
    fresh: false,
    unviewed: false,
    loaded: false
  });
  useEffect(() => {
    if (!isLoggedIn) {
      setState({
        totalCount: 0,
        fresh: false,
        unviewed: false,
        loaded: false
      });
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const deals = await getDealsProductState();
        if (!cancelled) setState({
          ...deals,
          loaded: true
        });
      } catch (error) {
        // Leave loaded=false so we neither render the UI nor identify a
        // misleading 0 for a user whose read failed.
        logger$b.error('useDealsProductState failed', error);
      }
    };
    run().catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  return state;
};
const EMPTY_INITIAL = {
  items: [],
  total: 0,
  lifecycleCount: 0,
  after: null
};
const useFeed = ({
  initialData = EMPTY_INITIAL,
  variant = enums$5.FeedVariant.Live,
  user = null,
  shareToken = null,
  filters = {}
}) => {
  const {
    first = 50
  } = filters;
  // Snapshot the seed once: the idempotent collection re-ingests whenever the
  // initial array identity changes, so a consumer passing a fresh initialData
  // each render must not drive a loop. Later data comes via loadMore/refresh.
  const seedItems = useRef(initialData.items).current;
  const {
    collection: items,
    attemptWrite,
    reset
  } = useIdempotentBlueprintCollection(seedItems, {
    primaryKey: 'item_id',
    updateBehavior: 'merge'
  });
  // Fetch-driven feed meta changes together on every load, so it's one state
  // object; `hasMore` is derived from it, not stored.
  const [meta, setMeta] = useState({
    total: initialData.total,
    lifecycleCount: initialData.lifecycleCount,
    cursor: initialData.after
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Mirror the cursor into a ref so the async loader reads the latest value
  // without re-creating `load` on every cursor change.
  const cursorRef = useRef(meta.cursor);
  cursorRef.current = meta.cursor;
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  // Fetch a page and fold it into the collection: replace on refresh, append
  // (idempotent write) on loadMore. The collection dedupes by property_id.
  const load = useCallback(async ({
    append
  }) => {
    setIsLoading(true);
    setHasError(false);
    try {
      const feed = await getFeed({
        variant,
        after: append ? cursorRef.current : null,
        first,
        user,
        shareToken
      });
      const items = (feed?.content || []).filter(item => item?.__typename === 'FeedPropertyItem');
      if (append) attemptWrite({
        data: items,
        source: 'polling'
      });else reset(items);
      setMeta({
        total: feed?.total ?? 0,
        // Group counts ride the untyped `aggregation` aggregate
        // ({ counts: { <FeedGroup>: n }, total }) — the old dedicated
        // `lifecycle_count` field is gone (MOR-1241).
        lifecycleCount: feed?.aggregation?.counts?.[enums$5.FeedGroup.Lifecycle] ?? 0,
        cursor: feed?.after ?? null
      });
    } catch (error) {
      setHasError(true);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [variant, first, user, shareToken, attemptWrite, reset]);
  const loadMore = useCallback(async () => {
    if (meta.cursor === null || isLoading) return;
    try {
      await load({
        append: true
      });
    } catch (error) {
      logger$b.error('useFeed loadMore failed', error);
      throw error;
    }
  }, [meta.cursor, isLoading, load]);
  const refresh = useCallback(async () => {
    try {
      await load({
        append: false
      });
    } catch (error) {
      logger$b.error('useFeed refresh failed', error);
    }
  }, [load]);
  // Fetch on mount only when we weren't seeded (SSR/initialData path skips it).
  adcHooks.useMountEffect(() => {
    if (initialData.items.length === 0) refresh().catch(() => undefined);
  });
  // Hide/unhide is a single toggle keyed on current hidden state. Optimistically
  // flips the grey-out, persists, and rolls back on failure.
  const toggle = useCallback(async propertyId => {
    const willHide = !hiddenIds.has(propertyId);
    setHiddenIds(prev => {
      const next = new Set(prev);
      if (willHide) next.add(propertyId);else next.delete(propertyId);
      return next;
    });
    try {
      await toggleHiddenProperty(propertyId, willHide);
    } catch (error) {
      setHiddenIds(prev => {
        const next = new Set(prev);
        if (willHide) next.delete(propertyId);else next.add(propertyId);
        return next;
      });
      logger$b.error('useFeed toggle failed', error);
      throw error;
    }
  }, [hiddenIds]);
  const isHidden = useCallback(propertyId => hiddenIds.has(propertyId), [hiddenIds]);
  // Product-level section assembly lives here (not in the UI) so every consumer
  // groups the flat feed the same way: lifecycle first, then the recommendations
  // tail. Keyed on the SDK FeedGroup; the UI maps the group to display copy.
  // Pagination is a feed-level concern (data.hasMore), not a per-section flag.
  // Hidden items stay in place (greyed by the card via isHidden) until the next
  // refresh drops them.
  const sections = useMemo(() => {
    const lifecycle = [];
    const recommendations = [];
    for (const item of items.raw) {
      if (item.group === enums$5.FeedGroup.Lifecycle) lifecycle.push(item);else recommendations.push(item);
    }
    const result = [];
    if (lifecycle.length) result.push({
      kind: enums$5.FeedGroup.Lifecycle,
      items: lifecycle
    });
    result.push({
      kind: enums$5.FeedGroup.Recommendation,
      items: recommendations
    });
    return result;
  }, [items.raw]);
  const data = useMemo(() => ({
    items: items,
    sections,
    total: meta.total,
    lifecycleCount: meta.lifecycleCount,
    isLoading,
    hasError,
    hasMore: meta.cursor !== null
  }), [items, sections, meta, isLoading, hasError]);
  const actions = useMemo(() => ({
    loadMore,
    refresh,
    toggle,
    isHidden
  }), [loadMore, refresh, toggle, isHidden]);
  return {
    data,
    actions
  };
};

var hooks$8 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useBuyingCriteria: useBuyingCriteria,
    useBuyingCriteriaOptionsFromContext: useBuyingCriteriaOptionsFromContext,
    useBuyingPreferenceLocationCounts: useBuyingPreferenceLocationCounts,
    useDealsProductState: useDealsProductState,
    useFeed: useFeed,
    useMatchedListings: useMatchedListings,
    usePropertyRecommendations: usePropertyRecommendations,
    useSavedProperties: useSavedProperties
});

// Slug for the Deals for You PRP category. Matches the presence channel suffix
// (auctioncom.prp.deals-for-you) the PRP registers presence on and the category
// graph filters recommendations by.
const DEALS_FOR_YOU_CATEGORY = 'deals-for-you';
// LD flag gating the drive-demand-to-prp nav indicator + direct link. Boolean or
// JSON variation ({ enabled }). Freshness/unviewed are computed server-side and
// surfaced as facts on product_state.deals — this flag is purely the on/off gate.
const DRIVE_DEMAND_TO_PRP_FLAG = 'fe.resi.drive-demand-to-prp';
/**
 * Parses the drive-demand-to-prp LD flag. Supports a boolean variation or a
 * JSON variation ({ enabled }). The flag is a pure on/off gate; recommendation
 * freshness (the link-visibility gate) and unviewed state are server-derived
 * facts on product_state.deals.
 */
const parsePrpIndicatorFlag = (featureFlags = {}, flagKey = DRIVE_DEMAND_TO_PRP_FLAG) => {
  const flag = featureFlags?.[flagKey];
  if (flag === true) {
    return {
      enabled: true
    };
  }
  if (typeof flag === 'object' && flag !== null) {
    return {
      enabled: flag.enabled === true
    };
  }
  return {
    enabled: false
  };
};

var prpIndicator = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DEALS_FOR_YOU_CATEGORY: DEALS_FOR_YOU_CATEGORY,
    DRIVE_DEMAND_TO_PRP_FLAG: DRIVE_DEMAND_TO_PRP_FLAG,
    parsePrpIndicatorFlag: parsePrpIndicatorFlag
});

var index$e = /*#__PURE__*/Object.freeze({
    __proto__: null,
    constants: constants$1,
    fragments: fragments$5,
    hooks: hooks$8,
    providers: providers$8,
    prpIndicator: prpIndicator,
    utils: utils$6
});

/* istanbul file */
// TODO: How do we surface this to all clients without breaking anyone
const partialGraphErrorFactory$1 = (message, errors) => {
  const e = new Error(message);
  e.errors = errors;
  return e;
};
/* istanbul ignore next */
const getUserTrusteePurchaseProfiles = controller => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_trusteePurchaseProfiles')} {
          current_user {
            purchase_profiles(filters:{ productTypes: [TRUSTEE] }) {
              ... on TrusteePurchaseProfile {
                purchase_profile_id
                profile_name
                is_default
                updated_date
              }
            }
          }
        }`
}, {
  controller
}).then(res => res.body).then(({
  data,
  errors
}) => {
  // We allow partial errors so long as some information about a user was returned
  if (data === null || data.current_user.purchase_profiles === null) {
    throw partialGraphErrorFactory$1('Could not fetch current user', errors);
  }
  return data.current_user.purchase_profiles;
});
const getUserReoPurchaseProfiles = controller => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_reoPurchaseProfiles')} {
          current_user {
            purchase_profiles(filters:{ productTypes: [REO, DAY_1_REO] }) {
              ... on ReoPurchaseProfile {
                offer_template_id
                percent_complete
                template_name
              }
            }
          }
        }`
}, {
  controller
}).then(res => res.body).then(({
  data,
  errors
}) => {
  // We allow partial errors so long as some information about a user was returned
  if (data === null || data.current_user.purchase_profiles === null) {
    throw partialGraphErrorFactory$1('Could not fetch current user', errors);
  }
  return data.current_user.purchase_profiles;
});
const TRUSTEE_PURCHASE_PROFILE_FRAGMENT = `
  fragment TrusteePurchaseProfileFragment on TrusteePurchaseProfile {
    purchase_profile_id
    profile_name
    is_default
    updated_date
  }
`;
const getTrusteePurchaseProfilesFromPartyId = (partyId, controller) => fetch.forAuctionGraph({
  variables: {
    partyId
  },
  query: `query ${embeddableOperation('blueprint_trusteePurchaseProfilesFromPartyId')}($partyId: ID!) {
          user_from_party(partyId: $partyId) {
            purchase_profiles(filters:{ productTypes: [TRUSTEE] }) {
              ...TrusteePurchaseProfileFragment
            }
          }
        }
        ${TRUSTEE_PURCHASE_PROFILE_FRAGMENT}`
}, {
  controller
}).then(res => res.body).then(({
  data,
  errors
}) => {
  // We allow partial errors so long as some information about a user was returned
  if (data === null || data.user_from_party.purchase_profiles === null) {
    throw partialGraphErrorFactory$1('Could not fetch user', errors);
  }
  return data.user_from_party.purchase_profiles;
});
const trusteeProfileSubscriptionQuery = () => gql$6`subscription ${embeddableOperation('blueprint_trusteePurchaseProfileUpdated')} {
      trusteePurchaseProfileUpdated {
        action
        trustee_purchase_profile {
          ...TrusteePurchaseProfileFragment
        }
      }
    },
    ${TRUSTEE_PURCHASE_PROFILE_FRAGMENT}`;
const trusteeProfileWithFilterSubscriptionQuery = () => gql$6`subscription ${embeddableOperation('blueprint_trusteePurchaseProfileUpdated')}($partyIds: [ID!]!) {
      trusteePurchaseProfileUpdated(filters: { partyIds: $partyIds }) {
        action
        trustee_purchase_profile {
          ...TrusteePurchaseProfileFragment
        }
      }
    },
    ${TRUSTEE_PURCHASE_PROFILE_FRAGMENT}`;
const TRUSTEE_PURCHASE_PROFILE_FORM_FRAGMENT = `
  fragment TrusteePurchaseProfileFormFragment on TrusteePurchaseProfile {
    purchase_profile_id
    profile_name
    customer_type
    is_default
    is_max_bid_submit
    contact_info {
      company_name
      first_name
      last_name
      email
      phone
      contact_address {
        street_address
        street_name
        street_number
        municipality
        country_primary_subdivision
        postal_code
      }
    }
    deed_mailing_address {
      company_name
      first_name
      last_name
      phone
      street_address
      street_name
      street_number
      municipality
      country_primary_subdivision
      postal_code
    }
    refund_payable_to_address {
      company_name
      first_name
      last_name
      phone
      street_address
      street_name
      street_number
      municipality
      country_primary_subdivision
      postal_code
    }
    is_refund_address_different
    vesting_instructions
  }
`;
const upsertTrusteePurchaseProfile = upsertTrusteePurchaseProfileArgs => fetch.forAuctionGraph({
  query: `
       mutation ${embeddableOperation('blueprint_upsertTrusteePurchaseProfiles')}($partyId: ID!, $input: TrusteePurchaseProfileCreateInput!) {
         upsertTrusteePurchaseProfile(partyId: $partyId, input: $input) {
           ... on TrusteePurchaseProfile {
             ...TrusteePurchaseProfileFormFragment
           }
         }
       }

       ${TRUSTEE_PURCHASE_PROFILE_FORM_FRAGMENT}
     `,
  variables: upsertTrusteePurchaseProfileArgs
}).then(res => res.body).then(({
  data,
  errors
}) => {
  if (errors && errors.length > 0) {
    throw partialGraphErrorFactory$1('Could not upsert trustee purchase profile', errors);
  }
  return data;
});

var providers$7 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    TRUSTEE_PURCHASE_PROFILE_FORM_FRAGMENT: TRUSTEE_PURCHASE_PROFILE_FORM_FRAGMENT,
    getTrusteePurchaseProfilesFromPartyId: getTrusteePurchaseProfilesFromPartyId,
    getUserReoPurchaseProfiles: getUserReoPurchaseProfiles,
    getUserTrusteePurchaseProfiles: getUserTrusteePurchaseProfiles,
    trusteeProfileSubscriptionQuery: trusteeProfileSubscriptionQuery,
    trusteeProfileWithFilterSubscriptionQuery: trusteeProfileWithFilterSubscriptionQuery,
    upsertTrusteePurchaseProfile: upsertTrusteePurchaseProfile
});

const USER_INFO_POLLING_FREQUENCY = ms('30s');

/* istanbul ignore file */
const logger$a = coreLogger.forModule('blueprint:purchaseProfiles');
/**
 * @deprecated since version 0.5.209 as we have introduced useTrusteePurchaseProfiles which uses idempotent collection for the same behavior
 *  and also handles both buyer & contractor side based on TrusteeInitialData
 */
function useForeclosurePurchaseProfiles({
  enabled,
  initialData,
  graphWebsocket
}) {
  const [purchaseProfiles, setPurchaseProfiles] = useState((initialData.user.purchase_profiles || []).filter(profile => profile !== null && profile.__typename === 'TrusteePurchaseProfile'));
  useRealtimeWatcher({
    id: 'useForeclosurePurchaseProfiles_TrusteePurchaseProfile',
    subjectIds: [typeof initialData.user.user_id === 'string' ? initialData.user.user_id : 'user_id'],
    polling: {
      enabled,
      frequency: USER_INFO_POLLING_FREQUENCY,
      executor: ({
        controller
      }) => getUserTrusteePurchaseProfiles(controller)
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: gql$6`
          subscription ${embeddableOperation('blueprint_trusteePurchaseProfileUpdated')} {
            trusteePurchaseProfileUpdated {
              action
              trustee_purchase_profile {
                purchase_profile_id
                profile_name
                is_default
              }
            }
          }
        `
      }),
      dataExtractor: data => {
        // @ts-ignore TODO: retype useRealtimeWatcher to handle different polling ans subscription types
        return [data.trusteePurchaseProfileUpdated];
      }
    },
    onData: ({
      data,
      source
    }) => {
      logger$a.info(`New data loaded. source=${source}`, data);
      if (source === 'polling') {
        setPurchaseProfiles(data);
      }
      if (source === 'websocket') {
        const purchaseProfileData = data[0];
        const action = purchaseProfileData.action;
        const purchaseProfile = purchaseProfileData.trustee_purchase_profile;
        switch (action) {
          case 'DELETE':
            return setPurchaseProfiles(purchaseProfiles => purchaseProfiles.filter(p => p.purchase_profile_id !== purchaseProfile.purchase_profile_id));
          case 'CREATE':
            return setPurchaseProfiles(purchaseProfiles => [...purchaseProfiles, purchaseProfile]);
          case 'UPDATE':
            return setPurchaseProfiles(purchaseProfiles => purchaseProfiles.map(p => p.purchase_profile_id === purchaseProfile.purchase_profile_id ? _.merge({}, p, purchaseProfile) : p));
        }
      }
    },
    onError: error => {
      logger$a.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$a.info('Have event', event, ctx);
    }
  });
  return purchaseProfiles;
}
function useReoPurchaseProfiles({
  enabled,
  initialData,
  graphWebsocket
}) {
  const [purchaseProfiles, setPurchaseProfiles] = useState((initialData.user.purchase_profiles || []).filter(profile => profile !== null && profile.__typename === 'ReoPurchaseProfile'));
  useRealtimeWatcher({
    id: 'useReoPurchaseProfiles_reoPurchaseProfile',
    subjectIds: [typeof initialData.user.user_id === 'string' ? initialData.user.user_id : 'user_id'],
    polling: {
      enabled: enabled,
      frequency: USER_INFO_POLLING_FREQUENCY,
      executor: ({
        controller
      }) => getUserReoPurchaseProfiles(controller)
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: gql$6`
          subscription ${embeddableOperation('blueprint_reoPurchaseProfileUpdated')} {
            reoPurchaseProfileUpdated {
              action
              reo_purchase_profile {
                offer_template_id
                percent_complete
                template_name
              }
            }
          }
        `
      }),
      dataExtractor: data => {
        // @ts-ignore TODO: retype useRealtimeWatcher to handle different polling ans subscription types
        return [data.reoPurchaseProfileUpdated];
      }
    },
    onData: ({
      data,
      source
    }) => {
      logger$a.info(`New data loaded. source=${source}`, data);
      if (source === 'polling') {
        setPurchaseProfiles(data);
      }
      if (source === 'websocket') {
        const purchaseProfileData = data[0];
        const action = purchaseProfileData.action;
        const purchaseProfile = purchaseProfileData.reo_purchase_profile;
        switch (action) {
          case 'DELETE':
            return setPurchaseProfiles(purchaseProfiles => purchaseProfiles.filter(p => p.offer_template_id !== purchaseProfile.offer_template_id));
          case 'CREATE':
            return setPurchaseProfiles(purchaseProfiles => [...purchaseProfiles, purchaseProfile]);
          case 'UPDATE':
            return setPurchaseProfiles(purchaseProfiles => purchaseProfiles.map(p => p.offer_template_id === purchaseProfile.offer_template_id ? _.merge({}, p, purchaseProfile) : p));
        }
      }
    },
    onError: error => {
      logger$a.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$a.info('Have event', event, ctx);
    }
  });
  return purchaseProfiles;
}
function useTrusteePurchaseProfiles({
  enabled,
  initialData,
  graphWebsocket
}) {
  const {
    attemptWrite,
    collection: idempotentProfiles
  } = useIdempotentBlueprintCollection([], {
    primaryKey: 'purchase_profile_id',
    idempotencyKey: 'updated_date',
    updateBehavior: 'merge'
  });
  const data = useMemo(() => ({
    profiles: idempotentProfiles
  }), [idempotentProfiles]);
  const output = useMemo(() => ({
    data
  }), [data]);
  const result = enabled ? match(initialData).with({
    partyIds: [P.string]
  }, ({
    partyIds
  }) => {
    return {
      isSuperUser: true,
      partyIds: partyIds
    };
  }).with({
    user: {
      party_id: P.string
    }
  }, ({
    user
  }) => {
    return {
      isSuperUser: false,
      partyIds: [user.party_id]
    };
  }).otherwise(() => null) : null;
  const isActive = enabled && result !== null;
  useRealtimeWatcher({
    id: 'useTrusteePurchaseProfiles_TrusteePurchaseProfile',
    subjectIds: result?.partyIds ?? [],
    polling: {
      enabled: isActive,
      frequency: USER_INFO_POLLING_FREQUENCY,
      executor: ({
        controller
      }) => result?.isSuperUser ? getTrusteePurchaseProfilesFromPartyId(result.partyIds[0], controller) : getUserTrusteePurchaseProfiles(controller)
    },
    websocket: {
      graphWebsocket,
      enabled: isActive && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: result?.isSuperUser ? trusteeProfileWithFilterSubscriptionQuery() : trusteeProfileSubscriptionQuery(),
        variables: {
          partyIds: result?.partyIds ?? []
        }
      }),
      dataExtractor: data => {
        return [data.trusteePurchaseProfileUpdated];
      }
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      let profiles = [];
      logger$a.info(`New data loaded. source=${source}`, data);
      if (source === 'polling') {
        profiles = data;
        attemptWrite({
          data: profiles,
          source
        });
      }
      if (source === 'websocket') {
        const purchaseProfileData = data[0];
        const action = purchaseProfileData.action;
        const purchaseProfile = purchaseProfileData.trustee_purchase_profile;
        const profiles = match(action).with('DELETE', () => {
          return idempotentProfiles.raw.filter(p => p.purchase_profile_id !== purchaseProfile.purchase_profile_id);
        }).with('CREATE', () => {
          return [...idempotentProfiles.raw, purchaseProfile];
        }).with('UPDATE', () => {
          return idempotentProfiles.raw.map(p => p.purchase_profile_id === purchaseProfile.purchase_profile_id ? _.merge({}, p, purchaseProfile) : p);
        }).exhaustive();
        attemptWrite({
          data: profiles,
          source
        });
      }
    },
    onError: error => {
      logger$a.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$a.info('Have event', event, ctx);
    }
  });
  return output;
}

var hooks$7 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useForeclosurePurchaseProfiles: useForeclosurePurchaseProfiles,
    useReoPurchaseProfiles: useReoPurchaseProfiles,
    useTrusteePurchaseProfiles: useTrusteePurchaseProfiles
});

const checkHasACompleteProfile = purchaseProfileResponse => {
  return _.some(purchaseProfileResponse, profile => profile.percent_complete === 100);
};

var utils$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    checkHasACompleteProfile: checkHasACompleteProfile
});

var index$d = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hooks: hooks$7,
    providers: providers$7,
    utils: utils$5
});

/**
 * Assembles the parts into one PDF (selection order preserved) and returns the
 * serialized bytes. PDFs contribute their pages; images become one full-page each.
 */
const stitchToPdf = async parts => {
  const {
    PDFDocument
  } = await import('pdf-lib');
  const output = await PDFDocument.create();
  for (const part of parts) {
    if (part.kind === 'pdf') {
      const src = await PDFDocument.load(part.bytes);
      const pages = await output.copyPages(src, src.getPageIndices());
      pages.forEach(page => output.addPage(page));
    } else {
      const image = part.kind === 'png' ? await output.embedPng(part.bytes) : await output.embedJpg(part.bytes);
      const page = output.addPage([image.width, image.height]);
      page.drawImage(image, {
        x: 0,
        y: 0,
        width: image.width,
        height: image.height
      });
    }
  }
  return output.save();
};

function isNonEmptyAgentLicenseNumber(input) {
  if (typeof input !== 'string') return false;
  // Trim whitespace
  const trimmed = input.trim();
  // Reject empty or whitespace-only strings
  if (trimmed.length === 0) return false;
  return true;
}
function isValidAgentLicenseNumber(input) {
  if (!isNonEmptyAgentLicenseNumber(input)) return false;
  // Trim whitespace
  const trimmed = input?.trim();
  // List of invalid placeholder values (case-insensitive)
  const invalidValues = ['n/a', 'na', 'none', '-'];
  // Check if the value matches any invalid placeholder
  if (invalidValues.includes(trimmed.toLowerCase())) {
    return false;
  }
  return true;
}

var utils$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    isNonEmptyAgentLicenseNumber: isNonEmptyAgentLicenseNumber,
    isValidAgentLicenseNumber: isValidAgentLicenseNumber,
    stitchToPdf: stitchToPdf
});

var index$c = /*#__PURE__*/Object.freeze({
    __proto__: null,
    utils: utils$4
});

const creditTypes = [graph.enums.TransactionType.Credit, graph.enums.TransactionType.PendingCredit];
const debitTypes = [graph.enums.TransactionType.Debit, graph.enums.TransactionType.PendingDebit, graph.enums.TransactionType.Void];
const getTargetContext = log => {
  const contextIdentifiers = log.context_identifiers || [];
  return contextIdentifiers.find(c => log.id === c?.transaction_log_id) || contextIdentifiers[0];
};
const getListing = log => {
  const targetContext = getTargetContext(log);
  const listing = ['LiveAuctionSegment', 'OnlineAuctionSegment'].includes(targetContext?.resolved_context?.__typename || '') ? _.get(targetContext?.resolved_context, 'listing') : targetContext?.resolved_context;
  return listing;
};
const getLogDescription = (description, formatted_address = '') => {
  return {
    description: `${description}${formatted_address ? ` ${formatted_address}` : ''}`,
    unresolved_description: `${description}${formatted_address ? ` {{listing.formatted_address}}` : ''}`
  };
};
const logDescriptionMatch = log => {
  const contextIdentifiers = log.context_identifiers || [];
  const targetContext = getTargetContext(log);
  const maybeWireTransfer = contextIdentifiers.find(i => i?.context_source === graph.enums.TransactionContextSource.ManualWireTransfer);
  const listing = getListing(log);
  const formattedAddress = listing?.formatted_address ? listing?.formatted_address?.[0] : '';
  const buildDescription = (description, connector = 'for') => formattedAddress ? `${description} ${connector}` : description;
  const value = match({
    ...log,
    targetContext
  }).with({
    payment_type: graph.enums.BuyerPaymentType.LiveRemoteBidding,
    transaction_type: P.when(type => !!type && creditTypes.includes(type)),
    transaction_log_type: P.when(t => [graph.enums.TransactionLogType.Reversal, graph.enums.TransactionLogType.Refund].includes(t)),
    targetContext: P.when(context => Boolean(context?.action === graph.enums.TransactionContextAction.CreditForCancellation))
  }, () => {
    return getLogDescription(buildDescription('Funds Added for cancelation', 'of'), formattedAddress);
  }).with({
    payment_type: P.union(graph.enums.BuyerPaymentType.LiveRemoteBidding, graph.enums.BuyerPaymentType.ForeclosureOnline),
    transaction_type: P.when(type => !!type && creditTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Payment
  }, () => getLogDescription(maybeWireTransfer?.id ? `Funds Added - Wire ID ${maybeWireTransfer.id}` : 'General Funding')).with({
    payment_type: graph.enums.BuyerPaymentType.LiveRemoteBidding,
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Charge,
    targetContext: P.when(context => Boolean(context?.action === graph.enums.TransactionContextAction.RemainingPaymentForClosing))
  }, () => getLogDescription(buildDescription('Remaining Payment'), formattedAddress)).with({
    payment_type: graph.enums.BuyerPaymentType.LiveRemoteBidding,
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Charge,
    targetContext: P.when(context => Boolean(context?.action === graph.enums.TransactionContextAction.AssignTheBidHold))
  }, () => getLogDescription(buildDescription('Assign the Bid (Hold)'), formattedAddress)).with({
    payment_type: graph.enums.BuyerPaymentType.LiveRemoteBidding,
    transaction_type: P.when(type => !!type && creditTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Reversal,
    targetContext: P.when(context => Boolean(context?.action === graph.enums.TransactionContextAction.AssignTheBidReturn))
  }, () => getLogDescription(buildDescription('Assign the Bid (Return)'), formattedAddress)).with({
    payment_type: graph.enums.BuyerPaymentType.LiveRemoteBidding,
    transaction_type: P.when(type => !!type && creditTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Reversal
  }, () => getLogDescription(buildDescription('You did not execute the contract'), formattedAddress)).with({
    payment_type: P.union(graph.enums.BuyerPaymentType.LiveRemoteBidding, graph.enums.BuyerPaymentType.ForeclosureOnline),
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    charge_reason: graph.enums.ChargeReason.Sale
  }, () => {
    const stateDepositRule = _.get(targetContext?.resolved_context, 'configuration.state_deposit_rule');
    const description = Boolean(stateDepositRule) && stateDepositRule !== '100%' ? 'Winning Bid Deposit' : 'Payment';
    return getLogDescription(buildDescription(description), formattedAddress);
  }).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && creditTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Reversal
  }, () => getLogDescription('Funds Added to Wallet by Auction.com')).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && creditTypes.includes(type)),
    targetContext: P.when(context => Boolean(context?.action === graph.enums.TransactionContextAction.BiddingRegistration))
  }, () => getLogDescription(`Registration Deposit${formattedAddress ? `: ${formattedAddress}` : ''}`)).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && creditTypes.includes(type))
  }, () => getLogDescription('Wallet Deposit')).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    payment_method_type: graph.enums.PaymentMethodType.Card,
    transaction_log_type: graph.enums.TransactionLogType.Dispute
  }, () => getLogDescription('Return of Funds: Credit Card Dispute')).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Dispute
  }, () => getLogDescription('Return of Funds: Dispute')).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    charge_reason: graph.enums.ChargeReason.ManualFallout
  }, () => getLogDescription(`Failure to perform${formattedAddress ? ` on ${formattedAddress}` : ''}`)).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    charge_reason: graph.enums.ChargeReason.AutoFallout
  }, () => getLogDescription(`Failure to perform${formattedAddress ? ` on ${formattedAddress}` : ''}`)).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_log_type: graph.enums.TransactionLogType.Payment,
    transaction_status: graph.enums.TransactionStatus.Failed,
    failure_reason: graph.enums.PaymentFailureReason.InsufficientFunds
  }, () => getLogDescription('Insufficient Funds')).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_log_type: graph.enums.TransactionLogType.Payment,
    transaction_status: graph.enums.TransactionStatus.Failed
  }, () => getLogDescription('Failed Deposit')).with({
    payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
    transaction_log_type: graph.enums.TransactionLogType.Refund,
    transaction_status: graph.enums.TransactionStatus.Failed
  }, () => getLogDescription('Failed Refund')).with({
    transaction_type: P.when(type => !!type && debitTypes.includes(type)),
    transaction_log_type: graph.enums.TransactionLogType.Refund
  }, () => getLogDescription('Return of Funds')).with({
    transaction_type: P.when(type => !!type && creditTypes.includes(type))
  }, () => getLogDescription('General Funding')).with({
    transaction_type: P.when(type => !!type && debitTypes.includes(type))
  }, () => getLogDescription('General Debit')).otherwise(() => getLogDescription(''));
  return value;
};
const numberSignMatch = log => match(log).with({
  transaction_type: P.when(type => !!type && creditTypes.includes(type))
}, {
  payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
  transaction_status: graph.enums.TransactionStatus.Processing
}, {
  payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
  transaction_type: graph.enums.TransactionType.Void,
  transaction_log_type: graph.enums.TransactionLogType.Refund
}, () => log.transaction_amount ? '+' : '').with({
  transaction_type: P.when(type => !!type && debitTypes.includes(type))
}, {
  payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
  transaction_status: graph.enums.TransactionStatus.Failed
}, () => log.transaction_amount ? '-' : '').otherwise(() => '');
const formatAmount = (log, numberSign) => {
  const nSign = numberSign ?? numberSignMatch(log);
  return `${nSign ? `${nSign} ` : ''}${formatMoney$2(log.transaction_amount ?? 0, {
    minimumFractionDigits: 2
  })}`;
};
const transactionTypeMatch = log => match(log).with({
  payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
  transaction_status: graph.enums.TransactionStatus.Processing
}, {
  transaction_type: graph.enums.TransactionType.PendingCredit
}, {
  payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
  transaction_type: graph.enums.TransactionType.Void,
  transaction_log_type: graph.enums.TransactionLogType.Refund
}, () => graph.enums.TransactionType.Credit).with({
  payment_type: graph.enums.BuyerPaymentType.OnlineBidDeposit,
  transaction_status: graph.enums.TransactionStatus.Failed
}, {
  transaction_type: graph.enums.TransactionType.PendingDebit
}, {
  transaction_type: graph.enums.TransactionType.Void
}, () => graph.enums.TransactionType.Debit).otherwise(() => log.transaction_type);
const downloadForeclosureOnlinePaymentInstruction = partyId => {
  window.open(`${configment.get('RESI_GRAPH_API_URL_EXTERNAL')}/download/adc:fcl_online_wire_instructions:${partyId}`, '_blank');
};

var utils$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    downloadForeclosureOnlinePaymentInstruction: downloadForeclosureOnlinePaymentInstruction,
    formatAmount: formatAmount,
    getListing: getListing,
    logDescriptionMatch: logDescriptionMatch,
    numberSignMatch: numberSignMatch,
    transactionTypeMatch: transactionTypeMatch
});

const requestRefund = variables => {
  return fetch.forAuctionGraph({
    query: gql$5`
        mutation ${embeddableOperation('blueprint_wallet_requestRefund')}($type: BuyerPaymentType!) {
          requestRefund(type: $type) {
            total_refunded_amount
            refund_transactions {
              transaction_status
              refund_intent_status
            }
          }
        }
        `,
    variables
  }).then(res => {
    const errors = _.get(res, 'body.errors[0]');
    if (errors) throw errors;
    return res;
  });
};

var providers$6 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    requestRefund: requestRefund
});

var index$b = /*#__PURE__*/Object.freeze({
    __proto__: null,
    providers: providers$6,
    utils: utils$3
});

const create = invite => fetch.forAuctionGraph({
  variables: {
    input: invite
  },
  query: gql$5`
        mutation ${embeddableOperation('blueprint_invitations_accept')}(input: CreateInvitation!) {
          createInvitation(input: $input) {
            invitation_id
          }
        }
      `
}).then(response => {
  if (response.body.errors && response.body.errors[0]) {
    throw response.body.errors[0];
  }
  return response.body.data.createInvitation;
});
const accept = invitationToken => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_invitations_accept')}($invitationToken: String!) {
          acceptInvitation(invitationToken: $invitationToken) {
            invitation_token
          }
        }
      `,
  variables: {
    invitationToken
  }
}).then(res => {
  const result = res.body.data.acceptInvitation;
  // eslint-disable-next-line promise/always-return
  if (!result?.invitation_token) {
    if (res.body.errors) {
      const {
        errors
      } = res.body;
      throw errors[0];
    }
    throw new Error('Failed to accept invitation');
  }
});
const revoke = invitationToken => fetch.forAuctionGraph({
  variables: {
    invitationToken
  },
  query: gql$5`
        mutation ${embeddableOperation('blueprint_invitations_revoke')}($invitationToken: String!) {
          revokeInvitation(invitationToken: $invitationToken) {
            invitation_id
            updated_date
            status
          }
        }
      `
}).then(response => {
  if (response.body.errors && response.body.errors[0]) {
    throw response.body.errors[0];
  }
  return response.body.data.revokeInvitation;
});

var providers$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    accept: accept,
    create: create,
    revoke: revoke
});

const fragments$3 = {
  pendingInvitation: {
    reference: '...BlueprintInvitationPendingInvitation',
    definition: gql$5`
      fragment BlueprintInvitationPendingInvitation on Invitation {
        invitation_id
        invitation_token
        updated_date
        created_date
        status
        relatives {
          object_description
          object_type
          object_id
          recipient_role_code
        }
      }
    `
  }
};

var fragments$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    default: fragments$3
});

var index$a = /*#__PURE__*/Object.freeze({
    __proto__: null,
    fragments: fragments$4,
    providers: providers$5
});

/* eslint-disable blueprint/namespace-exports -- Legacy: direct type exports need migration to types namespace */
const PROGRESS_INCREMENT_FACTOR = 0.2;
/**
 * A generalized flow hook for building multi-step processes with automatic step filtering,
 * progress tracking, and event emission.
 *
 * @template S - Union of step IDs (e.g., 'step1' | 'step2' | 'step3')
 * @template T - Type of data being constructed through the flow
 * @template E - Context type passed to step functions (defaults to unknown)
 * @template R - Computed data type stored in __computed__ property (defaults to unknown)
 *
 * @param definition - Array of step definitions that make up the flow
 * @param context - Context object passed to all step functions
 * @param emitter - Event emitter for flow state changes. Can close over fresh data; tracking only fires on step changes
 * @param initialData - Initial data to populate the flow
 * @param transform - Function to transform data before submission (default: removes __computed__)
 * @param submit - Function called when flow is submitted
 *
 * @returns Object containing flow data and actions
 *
 * @example
 * ```tsx
 * // Emitter callbacks always receive fresh data from closures
 * const { data, actions } = useFlow({
 *   definition: myFlowSteps,
 *   context: { user, listing },
 *   emitter: {
 *     onStep: (step) => trackPageView(step, user) // 'user' is always current
 *   },
 * })
 * ```
 */
function useFlow({
  definition,
  // everything in context can get passed through to next...
  context,
  emitter,
  initialData: initialDataProp,
  transform = data => omit(data, '__computed__'),
  submit
}) {
  const filterStepDefinition = useCallback((context, initialData) => {
    return definition.filter(s => !s?.canBeSkipped?.({
      context,
      initialData
    }));
  }, [definition]);
  const [initialData, setInitialData] = useState(initialDataProp);
  const [stepsDefinition, setStepsDefinition] = useState(filterStepDefinition(context, initialData));
  // NOTE: This isn't sufficient. Since steps can have conditions to show (think of id verification being present, but
  // automatically skipping, since there's no reason to ask for it -- resi-reg represents this now already)
  // Additional thoughts: I'm not sure if it's worth adding more customization for the first step as the apps can just determine what
  // they want their starting step to be when they initialize the flow. There may not be a need to fit this into the useFlow architecture
  // rather than just constructing your FlowDefinition based off your initial needs
  const firstStepId = stepsDefinition[0].id;
  // track touched per-step in a map instead of mutating step definitions the way the PDP version does
  const [stepTouchedMap, setStepTouchedMap] = useState(() => ({
    [firstStepId]: false
  }));
  const derivedStartingStepId = useOnce(() => {
    const stepId = stepsDefinition.find(step => !step?.canBeSkipped?.({
      context,
      initialData
    }))?.id;
    return stepId ?? firstStepId;
  });
  // history is an array of tuples, where each tuple is [stepId, progressIncrement]
  const [history, setHistory] = useState([[derivedStartingStepId, 0]]);
  const stepsVisited = history.map(([step]) => step);
  const progressIncrements = history.map(([, inc]) => inc);
  const currentProgress = progressIncrements.reduce((sum, a) => sum + a, 0);
  const remainingProgress = 100 - currentProgress;
  const stepMap = React.useMemo(() => {
    return stepsDefinition.reduce((acc, step) => {
      acc[step.id] = step;
      return acc;
    }, {});
  }, [stepsDefinition]);
  // NOTE: single useMemo (useOnce would be good here as well btw, as a generic hook) to do something like
  // `deriveStartingStep` which might not be the the current step (for the auto skip reason I told you above)
  const [currentStep, setCurrentStep] = React.useState(derivedStartingStepId);
  // Sticky edit-excursion marker: the step `returnToStep` jumps back to. Set by
  // `goToStep({ returnTo })`, survives intermediate advance/retreat so an edit
  // can span multiple steps. `undefined` when not editing.
  const [editReturnStep, setEditReturnStep] = React.useState(undefined);
  // Data snapshot captured when the excursion opened, surfaced for discard.
  const [editSnapshot, setEditSnapshot] = React.useState(undefined);
  // History length at the moment the excursion opened. Ending the excursion
  // (by Save/returnToStep OR by advancing forward back to the return target)
  // truncates history back to this length, so the excursion leaves no trace and
  // Previous from the return step walks the original backbone.
  const editOriginLenRef = React.useRef(null);
  // derive "currentStepTouched" for the current step from the map
  const currentStepTouched = stepTouchedMap[currentStep] ?? false;
  // updater that marks the current step as touched
  const setCurrentStepTouched = React.useCallback(flag => {
    setStepTouchedMap(prev => ({
      ...prev,
      [currentStep]: flag
    }));
  }, [currentStep, setStepTouchedMap]);
  const currentStepObject = stepMap[currentStep];
  // Track previous step for payload
  const previousStepRef = React.useRef(undefined);
  // Store emitter in a ref so we always call the latest version without re-running effects
  const emitterRef = React.useRef(emitter);
  React.useEffect(() => {
    emitterRef.current = emitter;
  });
  // Call onStep callback for optional tracking whenever currentStep changes
  // Note: emitter is NOT in the dependency array - we call through emitterRef instead
  // This ensures we always use the latest emitter with fresh data, but only trigger
  // tracking when the step actually changes, but not when the dependencies of the emitter change
  React.useEffect(() => {
    emitterRef.current?.onStep?.(currentStep, {
      from: previousStepRef.current,
      to: currentStep
    });
    previousStepRef.current = currentStep;
  }, [currentStep]);
  // Auto-invalidate a stale edit excursion: if the step definition is
  // recalculated (e.g. reset, or a canBeSkipped condition flips) and the sticky
  // return marker is filtered out, clear the excursion so `isEditing` goes false
  // and the consumer UI recovers instead of pointing at a dead step.
  React.useEffect(() => {
    if (editReturnStep && !stepMap[editReturnStep]) {
      setEditReturnStep(undefined);
      setEditSnapshot(undefined);
    }
  }, [editReturnStep, stepMap]);
  // Close the active edit excursion and return to the sticky marker, restoring
  // history to its pre-excursion length so the excursion leaves no trace (no
  // duplicate entries for Previous to walk back through). Shared by
  // returnToStep() and the forward-arrival effect below.
  const endEditExcursion = React.useCallback(emitReturn => {
    const target = editReturnStep;
    if (!target) return;
    // Guard against a stale marker: the step definition may have been
    // recalculated so the return target is no longer in the filtered flow.
    // Don't navigate to a dead step — clear the excursion and bail.
    if (!stepMap[target]) {
      // eslint-disable-next-line no-console -- surface misuse in dev
      console.warn(`[useFlow] edit excursion target "${target}" is no longer in the flow (steps recalculated) — clearing excursion.`);
      setEditReturnStep(undefined);
      setEditSnapshot(undefined);
      editOriginLenRef.current = null;
      return;
    }
    const from = currentStep;
    setCurrentStep(target);
    setStepTouchedMap(prev => ({
      ...prev,
      [target]: prev[target] ?? false
    }));
    // truncate history back to the length it had when the excursion opened;
    // if the target isn't the resulting tail (returnTo differed from the
    // origin step), fall back to the last occurrence of the target.
    // Capture originLen into a local BEFORE scheduling setHistory — the ref
    // is cleared synchronously below, but the updater runs later.
    const originLen = editOriginLenRef.current;
    setHistory(h => {
      let base = originLen != null && originLen >= 1 && originLen <= h.length ? h.slice(0, originLen) : h.slice();
      if (base.length === 0 || base[base.length - 1][0] !== target) {
        let idx = -1;
        for (let i = base.length - 1; i >= 0; i--) {
          if (base[i][0] === target) {
            idx = i;
            break;
          }
        }
        base = idx >= 0 ? base.slice(0, idx + 1) : [...base, [target, 0]];
      }
      return base;
    });
    if (emitReturn) {
      emitter?.on?.('flow:return', {
        from,
        to: target
      });
    }
    setEditReturnStep(undefined);
    setEditSnapshot(undefined);
    editOriginLenRef.current = null;
  }, [editReturnStep, stepMap, currentStep, emitter]);
  // Close an edit excursion when the current step reaches the return target by
  // ANY path — not just returnToStep(), but also advancing forward through the
  // remaining steps back to Review. This covers "edit step 4, then continue
  // Next through step 5 into Review" where progress accumulates normally. Guard
  // on stepsVisited length so opening an excursion whose returnTo equals the
  // (single-entry) starting step doesn't clear immediately.
  React.useEffect(() => {
    if (editReturnStep && currentStep === editReturnStep && stepsVisited.length > 1) {
      endEditExcursion(false);
    }
    // stepsVisited derives from history; depend on its length as a stable signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, editReturnStep, stepsVisited.length, endEditExcursion]);
  const actions = React.useMemo(() => {
    return {
      // NOTE: Match you're emitted events/callbacks here...
      advance: props => {
        const propsAndContext = {
          ...props,
          context,
          initialData
        };
        if (currentStepObject?.canSubmit?.(propsAndContext) && submit) {
          const transformedData = transform(propsAndContext.data);
          submit?.(transformedData);
          emitter?.on?.('flow:submit', {
            from: currentStep
          });
        }
        const isLast = currentStepObject.isLastStep(propsAndContext);
        // if we're on the last step already, signal complete
        if (isLast) {
          emitter?.on?.('flow:complete', {
            from: currentStep
          });
          return;
        }
        // otherwise advance
        const nextStepId = currentStepObject.next(propsAndContext);
        if (!nextStepId) return;
        setCurrentStep(nextStepId);
        // ensure the new step has an entry in touchedMap
        setStepTouchedMap(prev => ({
          ...prev,
          [nextStepId]: prev[nextStepId] ?? false
        }));
        // progress bar
        const nextIncrement = currentStepObject.isLastStep(propsAndContext) ? remainingProgress : remainingProgress * PROGRESS_INCREMENT_FACTOR;
        setHistory(h => [...h, [nextStepId, nextIncrement]]);
        emitter?.on?.('flow:advance', {
          from: currentStep,
          to: nextStepId
        });
      },
      // Jump directly to any step in the filtered definition. Appends to
      // history (progress increment 0) so retreat returns to the origin step,
      // enabling "Edit from Review" navigation. Optionally opens a sticky edit
      // excursion (see returnToStep).
      goToStep: (stepId, options) => {
        // guard: only steps present in the filtered stepMap are valid targets
        // (steps removed by canBeSkipped are not navigable)
        if (!stepMap[stepId]) {
          // eslint-disable-next-line no-console -- surface misuse in dev
          console.warn(`[useFlow] goToStep called with unknown or skipped stepId "${stepId}" — no-op.`);
          return;
        }
        // open a sticky edit excursion if requested. returnTo defaults to the
        // step we are jumping from so "save" returns to the origin by default.
        const openingExcursion = !!options && 'returnTo' in options && !!stepMap[options.returnTo ?? currentStep];
        if (openingExcursion) {
          const marker = options.returnTo ?? currentStep;
          setEditReturnStep(marker);
          setEditSnapshot(options.snapshot);
        }
        // already on the target — nothing to navigate, but the excursion marker
        // above may still have been set
        if (stepId === currentStep) return;
        setCurrentStep(stepId);
        // ensure the target step has an entry in touchedMap (match advance)
        setStepTouchedMap(prev => ({
          ...prev,
          [stepId]: prev[stepId] ?? false
        }));
        // Append the target with a progress increment that snaps the bar to
        // that step's original level (its cumulative progress at first visit),
        // so jumping back to an earlier step reflects where you are. If the
        // step hasn't been visited yet, don't move the bar (increment 0).
        // Ending the excursion truncates this out of history, restoring the
        // origin's progress and leaving no trace for Previous to walk.
        setHistory(h => {
          // capture the pre-append length as the excursion origin (fresh h,
          // never stale) so ending the excursion restores history exactly
          if (openingExcursion) editOriginLenRef.current = h.length;
          let cumulative = 0;
          let targetCumulative;
          for (const [sid, inc] of h) {
            cumulative += inc;
            if (sid === stepId) {
              targetCumulative = cumulative;
              break;
            }
          }
          const currentTotal = h.reduce((sum, [, inc]) => sum + inc, 0);
          const increment = targetCumulative === undefined ? 0 : targetCumulative - currentTotal;
          return [...h, [stepId, increment]];
        });
        emitter?.on?.('flow:goto', {
          from: currentStep,
          to: stepId
        });
      },
      // Close the active edit excursion and jump back to the sticky returnTo
      // marker, no matter how many steps deep the excursion went. Restores
      // history to its pre-excursion state so Back from the return step is
      // unaffected. No-op if no excursion is active.
      returnToStep: () => endEditExcursion(true),
      retreat: () => {
        if (stepsVisited.length <= 1) return;
        setHistory(h => {
          if (h.length <= 1) return h;
          const newHistory = h.slice(0, -1);
          const [prevStepId] = newHistory[newHistory.length - 1];
          setCurrentStep(prevStepId);
          emitter?.on?.('flow:retreat', {
            from: currentStep,
            to: prevStepId
          });
          return newHistory;
        });
        // do not automatically clear touched on retreat, maybe it could be needed in the future?
      },
      // This function mostly exists as a util to be used in storybook, at the time this hook is created, we don't have a use-case for this in the application but
      // it could be useful in the future for some kind of flow reset functionality if we want to provide customers a way to restart the conversational flow or guided offer flow without re-rendering the whole component.
      reset: initialDataOverride => {
        // Deep merge initialDataProp and initialDataOverride
        const initialData = merge({}, initialDataProp, initialDataOverride);
        const definition = filterStepDefinition(context, initialData);
        const firstStepId = definition[0].id;
        setInitialData(initialData);
        setStepsDefinition(definition);
        setCurrentStep(firstStepId);
        setHistory([[firstStepId, 0]]);
        setStepTouchedMap({
          [firstStepId]: false
        });
        setEditReturnStep(undefined);
        setEditSnapshot(undefined);
      },
      setCurrentStepTouched,
      submit: data => {
        if (!submit) return;
        const transformedData = transform(data);
        submit?.(transformedData);
        emitter?.on?.('flow:submit', {
          from: currentStep
        });
      }
    };
  }, [context, currentStep, currentStepObject, editReturnStep, endEditExcursion, emitter, filterStepDefinition, initialData, initialDataProp, remainingProgress, stepMap, setCurrentStepTouched, stepsVisited.length, transform, submit]);
  const data = React.useMemo(() => {
    return {
      currentStepTouched,
      currentProgress,
      currentStepObject,
      stepsVisited,
      stepsDefinition,
      editReturnStep,
      isEditing: !!editReturnStep,
      editSnapshot
    };
  }, [currentStepTouched, currentProgress, currentStepObject, stepsVisited, stepsDefinition, editReturnStep, editSnapshot]);
  return {
    data,
    actions
  };
}
const hooks$6 = {
  useFlow
};

var index$9 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hooks: hooks$6
});

const CHAT_TASK_FRAGMENTS = Object.fromEntries(graph.utils.cognition.chatTasks.map(taskName => {
  const pascalCaseTaskName = _.upperFirst(_.camelCase(taskName));
  return [taskName, {
    input: {
      reference: `...CognitionEvaluation${pascalCaseTaskName}InputMessageFragment`,
      definition: graph.gql`
            fragment CognitionEvaluation${pascalCaseTaskName}InputMessageFragment on CognitionEvaluationChatInputMessage {
              __typename
              object {
                message
              }
              sequence
              type
              visibility
            }
          `
    },
    output: {
      reference: `...CognitionEvaluation${pascalCaseTaskName}OutputMessageFragment`,
      definition: graph.gql`
            fragment CognitionEvaluation${pascalCaseTaskName}OutputMessageFragment on CognitionEvaluationChatOutputMessage {
              __typename
              object {
                message
                error
              }
              sequence
              type
            }
          `
    }
  }];
}));
const fragments$2 = {
  actorFieldsFragment: {
    reference: '...ActorFields',
    definition: graph.gql`
      fragment ActorFields on Actor {
        __typename
        ... on Party {
          party_id
          urn
          user {
            email
            display_name
          }
        }
        ... on CognitionEvaluationJudge {
          description
          urn
        }
      }
    `
  },
  progressFragment: {
    reference: '...CognitionEvaluationProgressFragment',
    definition: graph.gql`
      fragment CognitionEvaluationProgressFragment on CognitionEvaluationProgress {
        description
        sequence
        sub_sequence
        updated_date
      }
    `
  },
  evaluationGroupFragment: null,
  ...CHAT_TASK_FRAGMENTS,
  [graph.enums.CognitionEvaluationTaskName.ProofOfFundsReview]: {
    input: {
      reference: '...CognitionEvaluationProofOfFundsReviewInputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationProofOfFundsReviewInputMessageFragment on CognitionEvaluationProofOfFundsReviewInputMessage {
          __typename
          object {
            listing_id
            file_descriptor_id
          }
          sequence
          type
          visibility
        }
      `
    },
    output: {
      reference: '...CognitionEvaluationProofOfFundsReviewOutputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationProofOfFundsReviewOutputMessageFragment on CognitionEvaluationProofOfFundsReviewOutputMessage {
          __typename
          object {
            overall_result
            bundle_type_verdict
            documents {
              document_index
              detected_type
              rules {
                rule_type
                result
                extracted_value
                explanation
              }
            }
            batch_balance_rule {
              result
              total_qualifying_balance
              total_purchase_price
              explanation
            }
            dropped_documents {
              document_index
              detected_type
              type_result
              type_reason
            }
          }
          sequence
          type
        }
      `
    }
  },
  [graph.enums.CognitionEvaluationTaskName.DeedReviewExtraction]: {
    input: {
      reference: '...CognitionEvaluationDeedReviewExtractionInputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationDeedReviewExtractionInputMessageFragment on CognitionEvaluationDeedReviewExtractionInputMessage {
          __typename
          object {
            offer_id
            listing_id
            media_id
            file_descriptor_id
            overwrite_owner_party_id
          }
          sequence
          type
          visibility
        }
      `
    },
    output: {
      reference: '...CognitionEvaluationDeedReviewExtractionOutputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationDeedReviewExtractionOutputMessageFragment on CognitionEvaluationDeedReviewExtractionOutputMessage {
          __typename
          object {
            audit_points {
              name
              pass
              rule
              value_from_result_system
              value_from_result_extracted
              explanation
              rule_logic
              suggestion
            }
          }
          sequence
          type
        }
      `
    }
  },
  [graph.enums.CognitionEvaluationTaskName.OpenSearchDslGeneration]: {
    input: {
      reference: '...CognitionEvaluationDSLGenerationInputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationDSLGenerationInputMessageFragment on CognitionEvaluationDSLGenerationInputMessage {
          __typename
          message
          sequence
          type
          visibility
        }
      `
    },
    output: {
      reference: '...CognitionEvaluationDSLGenerationOutputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationDSLGenerationOutputMessageFragment on CognitionEvaluationDSLGenerationOutputMessage {
          __typename
          object
          sequence
          type
        }
      `
    }
  },
  [graph.enums.CognitionEvaluationTaskName.TextConversation]: {
    input: {
      reference: '...CognitionEvaluationTextConversationInputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationTextConversationInputMessageFragment on CognitionEvaluationInputMessage {
          __typename
          sequence
          type
        }
      `
    },
    output: {
      reference: '...CognitionEvaluationTextConversationOutputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationTextConversationOutputMessageFragment on CognitionEvaluationGroupMessage {
          __typename
          sequence
          type
        }
      `
    }
  },
  [graph.enums.CognitionEvaluationTaskName.TextGeneration]: {
    input: {
      reference: '...CognitionEvaluationTextGenerationInputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationTextGenerationInputMessageFragment on CognitionEvaluationInputMessage {
          __typename
          sequence
          type
        }
      `
    },
    output: {
      reference: '...CognitionEvaluationTextGenerationOutputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationTextGenerationOutputMessageFragment on CognitionEvaluationGroupMessage {
          __typename
          sequence
          type
        }
      `
    }
  },
  [graph.enums.CognitionEvaluationTaskName.TransactionSummary]: {
    input: {
      reference: '...CognitionEvaluationTransactionSummaryInputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationTransactionSummaryInputMessageFragment on CognitionEvaluationTransactionSummaryInputMessage {
          __typename
          object {
            transaction_urn
          }
          sequence
          type
          visibility
        }
      `
    },
    output: {
      reference: '...CognitionEvaluationTransactionSummaryOutputMessageFragment',
      definition: graph.gql`
        fragment CognitionEvaluationTransactionSummaryOutputMessageFragment on CognitionEvaluationTransactionSummaryOutputMessage {
          __typename
          object {
            transaction_urn
            listing_id
            current_status
            customer_engagement
            recent_updates
            blockers_risks
            outlook_next_steps
            needs_attention
            possibly_blocked
            generated_at
            prompt_type
          }
          sequence
          type
        }
      `
    }
  }
};
/**
 * Shared fields for CognitionEvaluationGroup mutation responses.
 *
 * Not a standard GraphQL fragment (uses operation-level variables), but follows
 * the { reference, definition } pattern for consistency.
 * - reference: the inline selection set to interpolate inside the operation
 * - definition: the dependent fragment definitions to append after the operation
 */
fragments$2.evaluationGroupFragment = task => ({
  reference: `
    urn
    created_date
    updated_date
    status
    evaluations(filters: $evaluationFilters) {
      method
      is_winner
      outputs(retrievalPolicy: $retrievalPolicy) {
        ${fragments$2[task].output.reference}
      }
      progress(retrievalPolicy: $retrievalPolicy) {
        ${fragments$2.progressFragment.reference}
      }
    }
    inputs(retrievalPolicy: $retrievalPolicy) {
      ${fragments$2[task].input.reference}
    }
  `,
  definition: `
    ${fragments$2[task].input.definition}
    ${fragments$2[task].output.definition}
    ${fragments$2.progressFragment.definition}
  `
});

const {
  gql: gql$4
} = graph;
const createEvaluationGroup = ({
  params,
  evaluationFilters,
  retrievalPolicy
}) => {
  return fetch.forAuctionGraph({
    query: gql$4`
          mutation ${embeddableOperation('blueprint_createEvaluationGroup')}($params: CognitionEvaluationGroupParams!, $evaluationFilters: CognitionEvaluationFilters!, $retrievalPolicy: CognitionEvaluationMessageRetrievalPolicy!) {
            createEvaluationGroup(params: $params) {
              ${fragments$2.evaluationGroupFragment(params.task).reference}
            }
          }
          ${fragments$2.evaluationGroupFragment(params.task).definition}
        `,
    variables: {
      params,
      evaluationFilters,
      retrievalPolicy
    }
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.createEvaluationGroup === null) {
      throw partialGraphErrorFactory$3('Could not create new evaluation group', errors);
    }
    return data.createEvaluationGroup;
  });
};
const getEvaluationGroup = ({
  controller,
  urn,
  task,
  evaluationFilters,
  retrievalPolicy,
  viewingFrom
}) => {
  return fetch.forAuctionGraph({
    query: gql$4`
            query ${embeddableOperation('blueprint_getEvaluationGroup')}($urn: URN!, $evaluationFilters: CognitionEvaluationFilters!, $retrievalPolicy: CognitionEvaluationMessageRetrievalPolicy!, $viewingFromStaff: Boolean!) {
              evaluation_group(urn: $urn) {
                ${fragments$2.evaluationGroupFragment(task).reference}
                created_by
                created_by_user @include(if: $viewingFromStaff) {
                  email
                  display_name
                }
                owner {
                  ${fragments$2.actorFieldsFragment.reference}
                }
                context {
									sources
								}
                last_scored_at
                assignee_urn
                assignee {
                  ... on Party {
                    web_communications {
                      identifier
                    }
                    person {
                      first_name
                      last_name
                    }
                  }
                }
                data_set {
				 	        description
				        }
                evaluations(filters: $evaluationFilters) {
                  method_version
                  is_winner
                  rubrics {
                    evaluator {
                      ${fragments$2.actorFieldsFragment.reference}
                    }
                    output_urn
                    score
                    notes
                  }
                }
                tags @include(if: $viewingFromStaff)
              }
            }
            ${fragments$2.evaluationGroupFragment(task).definition}
            ${fragments$2.actorFieldsFragment.definition}
          `,
    variables: {
      urn,
      evaluationFilters,
      retrievalPolicy,
      viewingFromStaff: viewingFrom === graph.enums.CurrentTargetAudience.Staff
    }
  }, {
    controller
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.evaluation_group === null) {
      throw partialGraphErrorFactory$3('Could not fetch latest evaluation group', errors);
    }
    return data.evaluation_group;
  });
};
const getEvaluationGroupsBatched = ({
  controller,
  urns,
  task,
  evaluationFilters,
  retrievalPolicy,
  viewingFrom
}) => {
  return fetch.forAuctionGraph({
    query: gql$4`
            query ${embeddableOperation('blueprint_getEvaluationGroupsBatched')}($urns: [URN!]!, $evaluationFilters: CognitionEvaluationFilters!, $retrievalPolicy: CognitionEvaluationMessageRetrievalPolicy!, $viewingFromStaff: Boolean!) {
              evaluation_groups_batched(urns: $urns) {
                ${fragments$2.evaluationGroupFragment(task).reference}
                created_by
                created_by_user @include(if: $viewingFromStaff) {
                  email
                  display_name
                }
                context {
									sources
								}
                evaluations(filters: $evaluationFilters) {
                  method_version
                  is_winner
                  rubrics {
                    evaluator {
                      ${fragments$2.actorFieldsFragment.reference}
                    }
                    output_urn
                    score
                    notes
                  }
                }
                tags @include(if: $viewingFromStaff)
              }
            }
            ${fragments$2.evaluationGroupFragment(task).definition}
            ${fragments$2.actorFieldsFragment.definition}
          `,
    variables: {
      urns,
      evaluationFilters,
      retrievalPolicy,
      viewingFromStaff: viewingFrom === graph.enums.CurrentTargetAudience.Staff
    }
  }, {
    controller
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.evaluation_groups_batched === null) {
      throw partialGraphErrorFactory$3('Could not fetch evaluation groups batched', errors);
    }
    return data.evaluation_groups_batched;
  });
};
const sendEvaluationGroupMessage = ({
  urn,
  task,
  message,
  evaluationFilters,
  retrievalPolicy
}) => {
  return fetch.forAuctionGraph({
    query: gql$4`
        mutation ${embeddableOperation('blueprint_sendEvaluationGroupMessage')}($urn: URN!, $message: CognitionEvaluationDataSetMessageInput!, $evaluationFilters: CognitionEvaluationFilters!, $retrievalPolicy: CognitionEvaluationMessageRetrievalPolicy!) {
          sendEvaluationGroupMessage(urn: $urn, message: $message) {
            ${fragments$2.evaluationGroupFragment(task).reference}
          }
        }
        ${fragments$2.evaluationGroupFragment(task).definition}
      `,
    variables: {
      urn,
      message: {
        [task.toLocaleLowerCase()]: message
      },
      evaluationFilters,
      retrievalPolicy
    }
  })
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  .then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.sendEvaluationGroupMessage === null) {
      throw partialGraphErrorFactory$3('Could not send evaluation group message', errors);
    }
    return data.sendEvaluationGroupMessage;
  });
};
const scoreEvaluation = args => {
  return fetch.forAuctionGraph({
    query: gql$4`
        mutation ${embeddableOperation('blueprint_scoreEvaluation')}($urn: URN!, $method: CognitionEvaluationMethod!, $score: CognitionEvaluationScore!) {
          scoreEvaluation(urn: $urn, method: $method, score: $score)
        }
      `,
    variables: {
      urn: args.urn,
      method: args.method,
      score: args.score
    }
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || data.scoreEvaluation === null) {
      throw partialGraphErrorFactory$3('Could not score evaluation', errors);
    }
    return data.scoreEvaluation;
  });
};

const mergeProgress = (existingProgress, newProgress) => [...(existingProgress || []), ...newProgress.filter(p => !(existingProgress || []).some(ep => ep.sequence === p.sequence && ep.sub_sequence === p.sub_sequence))];
const getBatchedEvaluationGroupDataForMethod = (oldEvaluationGroup, newEvaluationGroup) => {
  // Update inputs
  let updatedInputs = [];
  if (oldEvaluationGroup && oldEvaluationGroup.inputs) updatedInputs = oldEvaluationGroup.inputs;
  const newInput = newEvaluationGroup.inputs && newEvaluationGroup.inputs.length > 0 ? newEvaluationGroup.inputs[0] : null;
  if (newInput) {
    updatedInputs.findIndex(i => i.sequence === newInput.sequence) === -1 && updatedInputs.push(newInput);
  }
  // Update outputs
  let updatedEvaluations = [];
  if (oldEvaluationGroup && oldEvaluationGroup.evaluations) updatedEvaluations = oldEvaluationGroup.evaluations;
  // Only update with the new evaluation if there is a new evaluation
  if (newEvaluationGroup.evaluations.length > 0) {
    newEvaluationGroup.evaluations.forEach(evaluation => {
      const method = evaluation.method;
      // If no method, this is a progress-only update — merge progress into each existing evaluation
      if (!method) {
        const newProgress = evaluation.progress;
        if (newProgress && newProgress.length > 0) {
          updatedEvaluations = updatedEvaluations.map(e => ({
            ...e,
            progress: mergeProgress(e.progress, newProgress)
          }));
        }
        return;
      }
      const updatedEvaluationIndex = updatedEvaluations.findIndex(e => e.method === method);
      let updatedEvaluation = updatedEvaluationIndex > -1 ? updatedEvaluations[updatedEvaluationIndex] : undefined;
      // No evaluations yet, initial prompt subscription response and create the evaluation for the outputs
      if (updatedEvaluation === undefined) {
        updatedEvaluation = newEvaluationGroup.evaluations.find(e => e.method === method);
        if (updatedEvaluation) {
          updatedEvaluation = {
            ...updatedEvaluation,
            outputs: [...(updatedEvaluation.outputs || [])],
            progress: [...(updatedEvaluation.progress || [])]
          };
          updatedEvaluations = [...updatedEvaluations, updatedEvaluation];
        }
      } else {
        const newEvaluation = newEvaluationGroup.evaluations.find(e => e.method === method);
        const newOutput = newEvaluation?.outputs?.[0];
        const newProgress = newEvaluation?.progress;
        // Add evaluation method output if it doesn't already exist
        if (newOutput && !updatedEvaluation.outputs.some(o => o.sequence === newOutput.sequence)) {
          updatedEvaluation = {
            ...updatedEvaluation,
            outputs: [...updatedEvaluation.outputs, newOutput]
          };
        }
        // Merge new progress items if they don't already exist
        if (newProgress && newProgress.length > 0) {
          updatedEvaluation = {
            ...updatedEvaluation,
            progress: mergeProgress(updatedEvaluation.progress, newProgress)
          };
        }
        updatedEvaluations[updatedEvaluationIndex] = updatedEvaluation;
      }
    });
  }
  // Prefer the most recent updated_date and its associated status to prevent
  // stale websocket messages from reverting a newer mutation response.
  const useOldTimestamp = oldEvaluationGroup?.updated_date && newEvaluationGroup.updated_date && new Date(oldEvaluationGroup.updated_date) > new Date(newEvaluationGroup.updated_date);
  return {
    ...newEvaluationGroup,
    ...(useOldTimestamp && {
      status: oldEvaluationGroup.status,
      updated_date: oldEvaluationGroup.updated_date
    }),
    inputs: updatedInputs,
    evaluations: updatedEvaluations
  };
};
const getBatchedEvaluationGroupData = (source, newEvaluationGroups, idempotentEvaluationGroups) => {
  const evaluationGroupsForFullOverwrite = newEvaluationGroups.reduce((acc, current) => {
    const evaluationGroup = idempotentEvaluationGroups.mapped.get(current.urn);
    const updatedEvaluationGroup = getBatchedEvaluationGroupDataForMethod(evaluationGroup, current);
    updatedEvaluationGroup && acc.push(updatedEvaluationGroup);
    return acc;
  }, []);
  const batchedData = [];
  evaluationGroupsForFullOverwrite?.length && batchedData.push({
    source,
    data: evaluationGroupsForFullOverwrite,
    updateBehavior: 'overwrite'
  });
  return batchedData;
};
const mapProgressToMessages = (progressItems, startSequence) => {
  const messages = [];
  let seq = startSequence;
  progressItems.forEach(p => {
    messages.push({
      ...p,
      sequence: seq++,
      role: enums$5.CognitionEvaluationRole.Model
    });
  });
  return {
    messages,
    nextSequence: seq
  };
};
const getMessages = (evaluationGroup, {
  includeProgress = false,
  includePrivateMessages = false,
  temporalInterimMessages = true
} = {}) => {
  /*
   * Result sequence increments by 1 for each item, starting at 0.
   * Order per input sequence: input → progress (by sub_sequence) → output.
   */
  const sortedInputs = [...(evaluationGroup.inputs || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  // Initial prompt with no responses
  const messages = {};
  if (evaluationGroup.evaluations?.length === 0) {
    messages[enums$5.CognitionEvaluationMethod.InitialMethod] = sortedInputs.map((input, idx) => ({
      ...input,
      sequence: idx,
      role: enums$5.CognitionEvaluationRole.User
    }));
  }
  // Evaluation(s) with a response
  else {
    evaluationGroup.evaluations?.forEach(evaluation => {
      // Map outputs by original sequence (first occurrence wins for dedup)
      const outputBySequence = new Map();
      (evaluation.outputs || []).forEach(output => {
        const seq = output?.sequence || 0;
        if (!outputBySequence.has(seq)) {
          outputBySequence.set(seq, output);
        }
      });
      // Group progress by sequence, sorted by sub_sequence within each group (only when opted in)
      const progressBySequence = includeProgress ? (() => {
        const map = new Map();
        (evaluation.progress || []).forEach(p => {
          if (!map.has(p.sequence)) {
            map.set(p.sequence, []);
          }
          map.get(p.sequence).push(p);
        });
        map.forEach(items => items.sort((a, b) => a.sub_sequence - b.sub_sequence));
        return map;
      })() : null;
      const result = [];
      let seq = 0;
      // Collect all sequence numbers from inputs and outputs to build in order
      // Private inputs: include if includePrivateMessages, otherwise only if no output at same sequence
      const inputBySeq = new Map(sortedInputs.filter(i => {
        if (i.visibility !== 'PRIVATE') return true;
        return includePrivateMessages || !outputBySequence.has(i.sequence || 0);
      }).map(i => [i?.sequence || 0, i]));
      // Gather all unique sequences from inputs and outputs, sorted
      const allSequences = [...new Set([...inputBySeq.keys(), ...outputBySequence.keys()])].sort((a, b) => a - b);
      // Build result in sequential order
      allSequences.forEach(currentSeq => {
        // Input for this sequence
        if (inputBySeq.has(currentSeq)) {
          const input = inputBySeq.get(currentSeq);
          result.push({
            ...input,
            sequence: seq++,
            role: enums$5.CognitionEvaluationRole.User
          });
        }
        // Progress for this sequence
        // When temporal (default), only show for sequences without output
        // When non-temporal (trace view), always show
        if (progressBySequence?.has(currentSeq) && (!temporalInterimMessages || !outputBySequence.has(currentSeq))) {
          const {
            messages: progressMsgs,
            nextSequence
          } = mapProgressToMessages(progressBySequence.get(currentSeq), seq);
          result.push(...progressMsgs);
          seq = nextSequence;
        }
        // Output for this sequence
        if (outputBySequence.has(currentSeq)) {
          const output = outputBySequence.get(currentSeq);
          result.push({
            ...output,
            sequence: seq++,
            role: enums$5.CognitionEvaluationRole.Model
          });
        }
      });
      // Add progress sequences that don't match any input or output (ahead-of-input progress)
      if (progressBySequence) {
        [...progressBySequence.keys()].sort((a, b) => a - b).forEach(pSeq => {
          if (!inputBySeq.has(pSeq) && !outputBySequence.has(pSeq)) {
            const {
              messages: progressMsgs,
              nextSequence
            } = mapProgressToMessages(progressBySequence.get(pSeq), seq);
            result.push(...progressMsgs);
            seq = nextSequence;
          }
        });
      }
      messages[evaluation.method] = result;
    });
  }
  return messages;
};
const getWinnerEvaluationMethod = evaluationGroup => {
  const winnerEvaluation = evaluationGroup.evaluations?.find(e => e.is_winner === true);
  return winnerEvaluation ? winnerEvaluation.method : undefined;
};
const evaluationGroupUtils = {
  getMessages,
  getWinnerEvaluationMethod
};

var utils$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    evaluationGroupUtils: evaluationGroupUtils,
    getBatchedEvaluationGroupData: getBatchedEvaluationGroupData,
    getEvaluationGroup: getEvaluationGroup,
    getEvaluationGroupsBatched: getEvaluationGroupsBatched,
    scoreEvaluation: scoreEvaluation
});

const logger$9 = coreLogger.forModule('blueprint:cognition');
const COGNITION_GROUP_EVALUATED__TERMINAL_STATUSES = [enums$5.CognitionEvaluationGroupStatus.Completed, enums$5.CognitionEvaluationGroupStatus.Failed];
const COGNITION_CONTEXT = 'cognition.task_evaluated';
function useCognition({
  enabled,
  graphWebsocket,
  initialData,
  evaluationFilters,
  task,
  user,
  onError
}) {
  const graphSubscriberRef = useRef(null);
  const initiationTimeMapRef = useRef(new Map());
  const pollingTerminateRef = useRef(null);
  const handleError = useCallback(({
    error,
    logMetrics = true
  }) => {
    const errorObj = isObjectType(error) || error instanceof Error ? error : new Error(String(error));
    // Terminate polling on authentication errors (graph body.errors with
    // BACKEND_FORBIDDEN_ERROR/UNAUTHENTICATED, HTTP 403, or websocket auth errors).
    if (isAnyAuthenticationError(error)) {
      logger$9.info(`Polling terminated for cognition task ${task} due to authentication error`);
      pollingTerminateRef.current?.();
    }
    logger$9.info(`${logMetrics ? 'UPLOAD' : 'NOT upload'} error metrics for cognition task ${task}`);
    if (logMetrics) blueprintInstrument.increment(COGNITION_CONTEXT, 1, [`type:${task}`, 'status:error']);
    onError?.(errorObj);
  }, [onError, task]);
  const {
    collection: idempotentEvaluationGroups,
    attemptWrite: attemptEvaluationGroupWrite,
    attemptBatchWrite: attemptBatchEvaluationGroupWrite,
    reset: resetEvaluationGroups
  } = useIdempotentBlueprintCollection(initialData.evaluationGroups, {
    primaryKey: 'urn',
    // @ts-ignore next
    idempotencyKey: 'updated_date',
    utils: evaluationGroupUtils,
    updateBehavior: 'merge'
  });
  useEffect(() => {
    resetEvaluationGroups(initialData.evaluationGroups);
  }, [initialData.evaluationGroups, resetEvaluationGroups]);
  // Detect transition from unauthenticated to logged in.
  // Send a mutation immediately to notify the evaluation group of the
  // authentication change instead of waiting on the websocket to reconnect.
  const wasLoggedInRef = useRef(user?.logged_in);
  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current;
    wasLoggedInRef.current = user?.logged_in;
    if (!wasLoggedIn && user?.logged_in && subjectIds.length > 0) {
      const loginInput = {
        object: {
          message: 'User has logged in'
        },
        type: 'object',
        visibility: enums$5.CognitionInputVisibility.Private
      };
      const mutationPromise = sendEvaluationGroupMessage({
        urn: subjectIds[0],
        task,
        message: loginInput,
        evaluationFilters,
        retrievalPolicy: enums$5.CognitionEvaluationMessageRetrievalPolicy.Complete
      });
      // Write the mutation response to the collection on success.
      // By this point, any reconnection data has already been written,
      // so force-writing the server's confirmed response ensures the
      // login input is reflected in the collection.
      mutationPromise.then(evaluationGroup => {
        logger$9.info('Login mutation succeeded, writing response to collection');
        attemptBatchEvaluationGroupWrite([{
          data: [evaluationGroup],
          source: 'local_optimistic_action'
        }]);
      }).catch(error => {
        handleError({
          error,
          logMetrics: false
        });
      });
    }
  }, [user?.logged_in]);
  const data = useMemo(() => {
    return {
      currentTask: task,
      evaluationGroups: idempotentEvaluationGroups
    };
  }, [task, idempotentEvaluationGroups]);
  const chatActions = useMemo(() => ({
    endChat: async () => {
      // TODO: Implement endChat logic
      return Promise.resolve();
    }
  }), []);
  const dslGenerationActions = useMemo(() => ({
    generateDsl: async () => {
      // TODO: Implement generateDsl logic
      return Promise.resolve();
    }
  }), []);
  const actions = useMemo(function deriveActionsForTask() {
    let taskActions = {};
    switch (task) {
      case enums$5.CognitionEvaluationTaskName.BuyerPdpChat:
      case enums$5.CognitionEvaluationTaskName.PropertyPricingAssistant:
      case enums$5.CognitionEvaluationTaskName.WorkflowAdvancement:
        taskActions = chatActions;
        break;
      case enums$5.CognitionEvaluationTaskName.OpenSearchDslGeneration:
        taskActions = dslGenerationActions;
        break;
      default:
        taskActions = {};
    }
    return {
      ...taskActions,
      createEvaluationGroup: async _params => {
        const initiationTime = +Date.now();
        try {
          const response = await createEvaluationGroup({
            params: _params,
            evaluationFilters,
            retrievalPolicy: enums$5.CognitionEvaluationMessageRetrievalPolicy.Complete
          });
          instrument$1.domainEvent({
            // @ts-ignore
            type: enums$5.DomainEventTypeName.CognitionEvaluationInitiated,
            source: response.urn,
            data: {
              task
            }
          });
          initiationTimeMapRef.current.set(response.urn, initiationTime);
          return response;
        } catch (error) {
          logger$9.error(`Catch createEvaluationGroup error for cognition task ${task} `, error);
          handleError({
            error
          });
        }
        return undefined;
      },
      upsertEvaluationGroup: async (_params, urn) => {
        initiationTimeMapRef.current.set(urn, +Date.now());
        const _userInput = {
          object: {
            message: _params.content
          },
          type: 'object'
        };
        graphSubscriberRef.current?.subscriptionFeedback(_userInput);
        instrument$1.domainEvent({
          // @ts-ignore
          type: enums$5.DomainEventTypeName.CognitionEvaluationInitiated,
          source: urn,
          data: {
            task
          }
        });
      },
      scoreEvaluation: async args => {
        return scoreEvaluation(args);
      }
    };
  }, [task]);
  const output = useMemo(() => ({
    data,
    actions
  }), [data, actions]);
  const subjectIds = useMemo(() => {
    return initialData.evaluationGroups ? initialData.evaluationGroups.map(eg => typeof eg === 'string' ? eg : eg.urn) : [];
  }, [initialData]);
  // Websocket subscription for bidirectional communication.
  // Always called unconditionally to satisfy Rules of Hooks.
  // The `enabled` flag is passed to websocket.enabled to control behavior.
  useRealtimeWatcher({
    id: `useCognition_evaluationGroupUpdated`,
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && !_.isEmpty(subjectIds) && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: gql$5`
          subscription blueprint_evaluationGroupUpdated(
            $urn: URN!
            $filters: CognitionEvaluationGroupUpdatedFilters!
            $evaluationFilters: CognitionEvaluationFilters!
            $retrievalPolicy: CognitionEvaluationMessageRetrievalPolicy!
          ) {
            evaluationGroupUpdated(urn: $urn, filters: $filters) {
              action
              evaluation_group {
                ${fragments$2.evaluationGroupFragment(task).reference}
                evaluations(filters: $evaluationFilters) {
                  rubrics {
                    evaluator {
                      ${fragments$2.actorFieldsFragment.reference}
                    }
                    output_urn
                    score
                    notes
                  }
                }
              }
            }
          }
          ${fragments$2.evaluationGroupFragment(task).definition}
          ${fragments$2.actorFieldsFragment.definition}
        `,
        variables: {
          urn: subjectIds[0],
          filters: {
            actions: [enums$5.CognitionEvaluationAction.Completed, enums$5.CognitionEvaluationAction.InProgressUpdate]
          },
          evaluationFilters,
          retrievalPolicy: enums$5.CognitionEvaluationMessageRetrievalPolicy.Latest
        }
      }),
      dataExtractor: data => {
        // @ts-ignore Need to infer types from schema documents for this.
        if (!data?.evaluationGroupUpdated) {
          logger$9.info('Received null evaluationGroupUpdated from websocket');
          return [];
        }
        logger$9.info(
        // @ts-ignore Need to infer types from schema documents for this.
        `New CognitionEvaluationGroup data loaded. action=${data.evaluationGroupUpdated.action}`);
        // @ts-ignore Need to infer types from schema documents for this.
        return [data.evaluationGroupUpdated.evaluation_group].filter(Boolean);
      }
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$9.info(`New CognitionEvaluationGroup data loaded. source=${source}`);
      const batchedData = getBatchedEvaluationGroupData(source, data, idempotentEvaluationGroups);
      // When batched merge data is available, use it exclusively to avoid
      // the raw data (with Latest retrievalPolicy) from overwriting full
      // outputs/inputs arrays with incomplete data.
      if (batchedData.length > 0) {
        attemptBatchEvaluationGroupWrite(batchedData);
      } else {
        attemptBatchEvaluationGroupWrite([{
          source,
          data
        }]);
      }
    },
    onError: ({
      error
    }) => {
      handleError({
        error
      });
    },
    onEvent: (event, ctx) => {
      logger$9.info('Have event', event, ctx);
      // @ts-ignore
      if (ctx.graphSubscriber) {
        // @ts-ignore
        graphSubscriberRef.current = ctx.graphSubscriber;
      }
    }
  });
  useEffect(() => {
    if (!enabled || _.isEmpty(idempotentEvaluationGroups)) return;
    // Log elapsed time for cognition evaluation response for updates to each idempotent evaluation group
    idempotentEvaluationGroups.raw.forEach(evaluationGroup => {
      const initiationTime = initiationTimeMapRef.current.get(evaluationGroup.urn);
      if (initiationTime && !_.isEmpty(evaluationGroup.evaluations)) {
        if (COGNITION_GROUP_EVALUATED__TERMINAL_STATUSES.includes(evaluationGroup.status)) {
          blueprintInstrument.increment(COGNITION_CONTEXT, 1, [`type:${task}`, `status:${evaluationGroup.status}`]);
          const elapsedTime = +Date.now() - initiationTime;
          // Check the most recent output sequence to determine if this is the initial response or a subsequent one
          // 1 === initial response, 2+ === subsequent response
          // If the status is FAILED, the most recent output sequence will be
          // 0 === initial response, 1+ = subsequent response
          const initialiResponseSequence = evaluationGroup.status === enums$5.CognitionEvaluationGroupStatus.Completed ? 1 : 0;
          let isInitialResponse = true; // Default to true for response to initial prompt, whether it's successfully COMPLETED or FAILED status
          const evaluation = evaluationGroup.evaluations.find(e => e.is_winner === true);
          if (evaluation && !_.isEmpty(evaluation.outputs)) {
            const sortedOutputs = evaluation.outputs.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
            isInitialResponse = sortedOutputs[sortedOutputs.length - 1].sequence === initialiResponseSequence;
          }
          blueprintInstrument.gauge(COGNITION_CONTEXT, elapsedTime, [`type:${task}`, `elapsed_time:${isInitialResponse ? 'initial' : 'subsequent'}`]);
          instrument$1.domainEvent({
            // @ts-ignore
            type: enums$5.DomainEventTypeName.CognitionEvaluationResponseReceived,
            source: evaluationGroup.urn,
            data: {
              elapsed_time: elapsedTime,
              isInitialResponse,
              task
            }
          });
          initiationTimeMapRef.current.set(evaluationGroup.urn, null);
        }
      }
    });
  }, [idempotentEvaluationGroups]);
  // @ts-ignore next
  const evaluationGroupsExecutor = useCallback(async ({
    controller
  }) => {
    try {
      if (idempotentEvaluationGroups.raw.length > 0) {
        const evaluationGroups = await getEvaluationGroupsBatched({
          controller,
          urns: idempotentEvaluationGroups.raw.map(eg => eg.urn),
          task,
          evaluationFilters,
          retrievalPolicy: enums$5.CognitionEvaluationMessageRetrievalPolicy.Complete,
          viewingFrom: enums$5.CurrentTargetAudience.Buyer
        });
        return evaluationGroups;
      }
      return Promise.resolve([]);
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  }, [idempotentEvaluationGroups]);
  // Polling for the cognition evaluation group arrays
  useRealtimeWatcher({
    id: `useCognition_evaluationGroupPolling`,
    subjectIds,
    polling: {
      enabled: enabled && !_.isEmpty(idempotentEvaluationGroups),
      frequency: SUPPORTED_OFFSETS.URGENT,
      executor: evaluationGroupsExecutor
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$9.info(`new CognitionEvaluationGroup data loaded. source=${source}`);
      attemptEvaluationGroupWrite({
        data,
        source
      });
    },
    onError: ({
      error
    }) => {
      // Check if it's a @adc/fetch "Call timed out at 8000ms" and not log the error metrics into datadog RUM
      // Chrome browser have resource allocation for executing network requests.
      // If there are other network requests of higher priority, it will postpone the execution of lower priority requests.
      // @adc/fetch will throw a TimeoutError if the execution is postponed for over 8 seconds.
      handleError({
        error,
        logMetrics: !(error instanceof Error && error.message.includes('Call timed out'))
      });
    },
    onEvent: (event, ctx) => {
      logger$9.info('Have event', event, ctx);
      // @ts-ignore
      if (ctx?.terminate) {
        // @ts-ignore
        pollingTerminateRef.current = ctx.terminate;
      }
    }
  });
  return output;
}

var hooks$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useCognition: useCognition
});

var index$8 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hooks: hooks$5,
    utils: utils$2
});

/** Find the BUYER-audience rule for a channel. */
const getBuyerRule = (communicationPreference, channel) => (communicationPreference?.rules || []).find(rule => rule?.channel === channel && rule?.audience === enums$5.Audience.Buyer) ?? undefined;
const isBuyerChannelOptedIn = (communicationPreference, channel) => getBuyerRule(communicationPreference, channel)?.opt_status === enums$5.OptStatus.OptedIn;
const isEmailMarketingOptedIn = communicationPreference => isBuyerChannelOptedIn(communicationPreference, enums$5.CommunicationPreferenceChannel.Email);
const isSmsMarketingOptedIn = communicationPreference => isBuyerChannelOptedIn(communicationPreference, enums$5.CommunicationPreferenceChannel.Sms);
/** Twilio opt-out (external OPTED_OUT + source TWILIO); can't reverse via API. */
const isSmsMarketingTwilioBlocked = communicationPreference => {
  const rule = getBuyerRule(communicationPreference, enums$5.CommunicationPreferenceChannel.Sms);
  return rule?.external_opt_status === enums$5.OptStatus.OptedOut && rule?.opt_source === enums$5.OptSource.Twilio;
};
/** URN to write against; falls back to adc:email:<email>. */
const getCommPreferenceUrn = (communicationPreference, email) => {
  if (communicationPreference?.urn) return communicationPreference.urn;
  return email ? `adc:email:${email}` : undefined;
};
/** A single BUYER opt-in rule payload for setCommunicationPreference. */
const buildBuyerOptInRule = channel => ({
  audience: enums$5.Audience.Buyer,
  channel,
  opt_status: enums$5.OptStatus.OptedIn
});
/** Copy of the preference with the BUYER channel rule set OPTED_IN (upsert). */
const withBuyerChannelOptedIn = (communicationPreference, channel) => {
  const base = communicationPreference ?? {
    rules: []
  };
  const rules = base.rules ?? [];
  const exists = rules.some(rule => rule?.channel === channel && rule?.audience === enums$5.Audience.Buyer);
  const nextRules = exists ? rules.map(rule => rule?.channel === channel && rule?.audience === enums$5.Audience.Buyer ? {
    ...rule,
    opt_status: enums$5.OptStatus.OptedIn
  } : rule) : [...rules, buildBuyerOptInRule(channel)];
  return {
    ...base,
    rules: nextRules
  };
};

var utils$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    buildBuyerOptInRule: buildBuyerOptInRule,
    getBuyerRule: getBuyerRule,
    getCommPreferenceUrn: getCommPreferenceUrn,
    isBuyerChannelOptedIn: isBuyerChannelOptedIn,
    isEmailMarketingOptedIn: isEmailMarketingOptedIn,
    isSmsMarketingOptedIn: isSmsMarketingOptedIn,
    isSmsMarketingTwilioBlocked: isSmsMarketingTwilioBlocked,
    withBuyerChannelOptedIn: withBuyerChannelOptedIn
});

const getCommunicationPreferences = searchUrn => fetch__default.forAuctionGraph({
  // eslint-disable-next-line
  query: gql$5`
        query ${embeddableOperation('blueprint_communication_preferences_get')}($searchUrn: URN!) {
          communication_preference(searchUrn: $searchUrn) {
            urn
            owner {
              ... on MinimalPreferenceUser {
                user_id
                party_id
                signup_source
              }
              ... on MinimalLead {
                lead_id
                lead_person_id
              }
            }
            rules {
              audience
              channel
              opt_status
              external_opt_status
              opt_source
            }
          }
        }
      `,
  variables: {
    searchUrn
  } // eslint-disable-line
}).then(res => {
  const errors = res.body.errors?.[0];
  if (errors) throw errors;
  // @ts-ignore next
  return res.body.data.communication_preference; // eslint-disable-line
}).catch(error => {
  console.error('Error fetching communication preferences:', error);
  throw error;
});
const updateCommunicationPreferences = (urn, rules) => {
  // external_opt_status is externally/ADC-managed (read-only) and is not part
  // of CommunicationPreferenceRuleInput — the graph rejects it. Strip to the
  // accepted fields before sending so a user save can't fail on it.
  const inputRules = (rules ?? []).filter(rule => Boolean(rule)).map(({
    audience,
    channel,
    opt_status,
    opt_source
  }) => ({
    audience,
    channel,
    opt_status,
    opt_source
  }));
  return fetch__default.forAuctionGraph({
    // eslint-disable-next-line
    query: gql$5`
          mutation ${embeddableOperation('blueprint_communication_preferences_update')}(
            $urn: URN!
            $rules: [CommunicationPreferenceRuleInput!]!
          ) {
            setCommunicationPreference(urn: $urn, rules: $rules)
          }
        `,
    variables: {
      urn,
      rules: inputRules
    } // eslint-disable-line
  }).then(res => {
    const errors = res.body.errors?.[0];
    if (errors) throw errors;
    // @ts-ignore next
    return res.body.data.setCommunicationPreference; // eslint-disable-line
  }).catch(error => {
    console.error('Error updating communication preferences:', error);
    throw error;
  });
};
/**
 * Opt the user into a BUYER marketing channel: resolves the URN (falls back to
 * adc:email:<email>) and upserts a single OPTED_IN rule. No-op without a URN.
 */
const optInBuyerChannel = (communicationPreference, email, channel) => {
  const urn = getCommPreferenceUrn(communicationPreference, email);
  if (!urn) return Promise.resolve(undefined);
  return updateCommunicationPreferences(urn, [buildBuyerOptInRule(channel)]);
};

var providers$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getCommunicationPreferences: getCommunicationPreferences,
    optInBuyerChannel: optInBuyerChannel,
    updateCommunicationPreferences: updateCommunicationPreferences
});

/**
 * Owns a user's communication preferences: fetches on mount, exposes derived
 * marketing state, and opts a BUYER channel in (optimistically updating the
 * cached preference so callers stay consistent without a refetch).
 */
const useCommunicationPreferences = ({
  searchUrn,
  email
}) => {
  const [communicationPreference, setPreference] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [optingIn, setOptingIn] = useState(false);
  const refetch = useCallback(async () => {
    if (!searchUrn) return;
    setLoading(true);
    setError(null);
    try {
      const preference = await getCommunicationPreferences(searchUrn);
      setPreference(preference);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [searchUrn]);
  useEffect(() => {
    refetch();
  }, [refetch]);
  const optIn = useCallback(async channel => {
    setOptingIn(true);
    try {
      await optInBuyerChannel(communicationPreference, email, channel);
      setPreference(prev => withBuyerChannelOptedIn(prev, channel));
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setOptingIn(false);
    }
  }, [communicationPreference, email]);
  return useMemo(() => ({
    communicationPreference,
    loading,
    error,
    refetch,
    isEmailMarketingOptedIn: isEmailMarketingOptedIn(communicationPreference),
    isSmsMarketingOptedIn: isSmsMarketingOptedIn(communicationPreference),
    isSmsMarketingTwilioBlocked: isSmsMarketingTwilioBlocked(communicationPreference),
    optingIn,
    optIn
  }), [communicationPreference, loading, error, optingIn, refetch, optIn]);
};

/* istanbul-ignore-file */

var index$7 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    providers: providers$4,
    useCommunicationPreferences: useCommunicationPreferences,
    utils: utils$1
});

/* istanbul ignore file */
const fragments$1 = {
  closingAssociation: {
    reference: '...BlueprintClosingAssociationFragment',
    definition: gql$5`
      fragment BlueprintClosingAssociationFragment on ClosingAssociation {
        party_id
        closing_relationship
        party {
          party_id
          person {
            first_name
            last_name
            company_name
          }
          addresses {
            street_number
            street_name
            street_unit
            municipality
            country_primary_subdivision
            postal_code
          }
        }
      }
    `
  },
  closingOverviewStatus: {
    reference: '...BlueprintClosingOverviewStatusFragment',
    definition: gql$5`
      fragment BlueprintClosingOverviewStatusFragment on ClosingOverviewStatus {
        closing_status_count {
          key
          name
          statuses {
            status
            count
          }
        }
      }
    `
  },
  listingClosingConfiguration: {
    reference: '...BlueprintListingClosingConfigurationFragment',
    definition: gql$5`
      fragment BlueprintListingClosingConfigurationFragment on ListingClosingConfiguration {
        responsibility_type
        is_closing_document_required
        earnest_money_deposit_timeline_label
      }
    `
  },
  tcClosing: {
    reference: '...BlueprintTCClosingFragment',
    definition: '' // set below after closingAssociation is defined
  },
  reoOffer: {
    reference: '...BlueprintReoOfferFragment',
    definition: '' // set below after closingAssociation is defined
  },
  // CIF resume read field set. Mirrors `buildSaveCifInput` (mutations.ts) so the
  // wizard can rehydrate saved progress. Dedicated (not the `reoOffer` fragment)
  // to avoid bloating the closing detail/list views with CIF-only fields.
  reoOfferCif: {
    reference: '...BlueprintReoOfferCifFragment',
    definition: gql$5`
      fragment BlueprintReoOfferCifFragment on ReoOffer {
        offer_id
        vesting_type
        funding_type
        lending_status
        purchase_type
        buyer_occupancy_code
        real_estate_agent_type
        licensed_agent_flag
        first_time_buyer_flag
        seller_escrow_company_flag
        agent {
          agent_party {
            first_name
            last_name
            email_address
            phone_number
          }
          agent_firm {
            company_name
            street_name
          }
        }
        lender {
          lender_party {
            first_name
            last_name
            email_address
            phone_number
          }
        }
        escrow_company {
          escrow_company_party {
            street_name
          }
        }
        buyers {
          primary_flag
          marital_status
          is_spouse_on_contract
          spouse_name
          spouse_email_address
          spouse_marital_status
          is_international_buyer
          buyer_party {
            first_name
            middle_name
            last_name
            email_address
          }
        }
        entity_profile {
          entity_name
          entity_type
          entity_sub_type
          entity_state
          entity_title
          entity_date
          percent_stake
          more_entity_involved
          more_signer_involved
          individuals {
            signer_name
            signer_title
            signer_email
            signer_phone
            owner_type
            percent_stake
            signer
          }
          interested_parties {
            entity_name
            entity_type
            entity_sub_type
            entity_state
            entity_title
            entity_date
            percent_stake
            more_entity_involved
            more_signer_involved
            individuals {
              signer_name
              signer_title
              signer_email
              signer_phone
              owner_type
              percent_stake
              signer
            }
          }
        }
      }
    `
  },
  trusteeContract: {
    reference: '...BlueprintTrusteeContractFragment',
    definition: '' // set below after closingAssociation is defined
  },
  workflowResourceBase: {
    reference: '...BlueprintWorkflowResourceBaseFragment',
    definition: gql$5`
      fragment BlueprintWorkflowResourceBaseFragment on WorkflowResource {
        workflow {
          current_status_label
          task_groups {
            code
            label
            description
            tooltip_label
            description_tooltip
            label_tooltip
            status
            tasks {
              code
              label
              status
              completed_date
              last_updated_date
              expected_completion_date
            }
          }
        }
      }
    `
  },
  workflow: ({
    withViewingAs = false
  } = {}) => ({
    reference: '...BlueprintWorkflowFragment',
    definition: gql$5`
      fragment BlueprintWorkflowFragment on ReoOffer {
        workflow${withViewingAs ? '(viewingAs: $workflowViewingAs)' : ''} {
          current_status_label
          task_groups {
            code
            label
            description
            tooltip_label
            description_tooltip
            label_tooltip
            status
            tasks {
              code
              label
              status
              completed_date
              last_updated_date
              metadata {
                ... on PurchaseDocumentWorkflowMetadata {
                  task_status
                  upload_status
                  rejection_reason
                  rejection_comments
                  document_url
                }
              }
              instructions {
                header
                body
                supporting_data {
                  key
                  value
                  value_type
                }
                action
              }
            }
          }
        }
      }
    `
  }),
  associatedClosings: {
    reference: '...BlueprintAssociatedClosingsFragment',
    definition: '' // set below after reoOffer, trusteeContract, workflow are defined
  },
  // Shared listing selection spread by the list fragments (reoOfferList,
  // trusteeContractList) and consumed directly by ClosingCard. Single source
  // of truth for the card-row listing fields. See CLOSE-266.
  closingListing: {
    reference: '...BlueprintClosingListingFragment',
    definition: gql$5`
      fragment BlueprintClosingListingFragment on Listing {
        listing_id
        listing_status
        listing_status_label(intent: SEARCH)
        listing_status_group
        auction {
          is_online
        }
        primary_property {
          property_id
        }
        listing_page_path
        primary_photo
        listing_configuration {
          asset_type
          product_type
          is_third_party_online
        }
        formatted_address(format: DOUBLE_LINE)
        venue {
          venue_type
        }
        event {
          trustee_sale
        }
      }
    `
  },
  // Lightweight variants for the paginated list view. Only includes fields
  // rendered in the list row (ClosingCard + MilestonesProgress). Keeps the
  // total backend call count well under RESILIENCE_HTTP_REQUEST_LIMIT. See CLOSE-161.
  reoOfferList: {
    reference: '...BlueprintReoOfferListFragment',
    definition: '' // set below
  },
  trusteeContractList: {
    reference: '...BlueprintTrusteeContractListFragment',
    definition: '' // set below
  },
  workflowList: {
    reference: '...BlueprintWorkflowListFragment',
    definition: gql$5`
      fragment BlueprintWorkflowListFragment on ReoOffer {
        workflow {
          task_groups {
            code
            label
            status
          }
        }
      }
    `
  },
  associatedClosingsList: {
    reference: '...BlueprintAssociatedClosingsListFragment',
    definition: '' // set below after list sub-fragments are defined
  }
};
// --- Composed fragments (depend on sub-fragments) ---
fragments$1.tcClosing.definition = gql$5`
  fragment BlueprintTCClosingFragment on TransactionCoordinatorClosings {
    __typename
    sold_amount
    sold_date
    listing_id
    closed_date
    closing_method_type
    closing_resource_id
    closing_status
    closing_associations {
      ${fragments$1.closingAssociation.reference}
    }
    listing {
      formatted_address(format: DOUBLE_LINE)
      parties {
        party_name
        party_role
        party_id
      }
    }
  }
`;
fragments$1.reoOffer.definition = gql$5`
  fragment BlueprintReoOfferFragment on ReoOffer {
    __typename
    offer_id
    listing_id
    origin
    offer_status
    offer_identifier
    urn
    page_path
    percent_complete
    total_purchase_price_amount
    winning_bid_amount
    sold_time
    completed_time
    subject_to_seller_confirmation
    current_user_associated_relationships
    buyers_premium_amount
    buyers_premium_paid_by
    auction_fee_amount
    funding_type
    earnest_money_deposit_percentage
    earnest_money_deposit_amount
    down_payment_amount
    seller_concession_percentage
    seller_concession_amount
    buyer_agent_commission_percentage
    buyer_agent_commission_flat_amount
    buyer_agent_commission_amount
    buyer_agent_commission_paid_by
    buyer_agent_commission_message
    listing_agent_commission_percentage
    listing_agent_commission_flat_amount
    listing_agent_commission_amount
    listing_agent_commission_paid_by
    listing_agent_commission_message
    is_agent_included
    is_appraisal_contingency
    is_inspection_contingency
    is_financing_contingency
    is_leaseback_contingency
    is_inspection_contingency_waived
    is_option_contingency
    inspection_contingency_date
    inspection_contingency_scheduled_date
    inspection_contingency_completed_date
    inspection_contingency_comment
    inspection_contingency_period_days
    option_period_duration
    option_period_cost_amount
    financing_contingency_days
    financing_contingency_date
    financing_contingency_approval_date
    appraisal_contingency_days
    appraisal_contingency_date
    appraisal_contingency_completed_date
    close_of_escrow_days
    close_of_escrow_date
    leaseback_days
    leaseback_amount
    additional_notes
    lending_status
    scheduled_closing_date
    sent_to_buyer_time
    sent_to_listing_agent_date
    contract_received_from_buyer_date
    contract_received_from_seller_date
    contract_sent_to_seller_date
    executed_contract_date
    confirmed_executed_contract_date
    deed_received_date
    deed_review_received_date
    seller_escrow_company_flag
    entity_profile {
      entity_name
    }
    owning_entity {
      entity_type
      entity_name
    }
    buyers {
      buyer_id
      buyer_party {
        party_id
        display_name
        first_name
        last_name
        email_address
        phone_number
      }
    }
    closing_associations {
      ${fragments$1.closingAssociation.reference}
    }
    offer_milestones {
      extended_through_date
      fallout_reason
      fallout_caused_by
      cancellation_date
      closing_cancellation_reason
      extension_fully_executed_date
      cancellation_fully_executed_date
      fallout_date
      reviewed_by_auction_date
      deed_sent_to_seller_date
      deed_fully_executed_date
      hud1_sent_to_servicer_date
      hud1_approved_to_servicer_date
      subject_to_seller_status
      cancellation_received_from_seller_date
    }
    offer_configuration {
      responsibility_type
      online_segment_type
    }
    closing_cost_detail {
      marketplace_discount_amt
      actual_closed_date
      marketplace_credit_offset_amt
      marketplace_buyer_credit_amt
      marketplace_discount_description
      marketplace_credit_offset_description
      marketplace_buyer_credit_description
      hud_settlement_date
      preliminary_hud_to_adc_received_date
      final_hud_received_date
      referral_fee_amount
      estimated_disbursement_date
      adc_wire_amount
      adc_wire_amount_confirmed_date
      proceeds_amount
      proposed_auction_contribution_amount
      invoice_balance_amount
    }
    closing_deed_review_evaluation {
      pass
    }
    offer_parties {
      role
      first_name
      middle_name
      last_name
      full_name
      company_name
      address_type
      street_number
      street_name
      street_unit
      city
      state
      postal_code
      email_address
      phone_type
      phone_number
      license_number
      license_state
      license_type
    }
    media {
      media_id
      media_type
      urn
      version
      created_date
      updated_date
      versions {
        version_number
      }
    }
    escrow_company {
      escrow_company_id
      escrow_company_party {
        party_id
        party_type
        full_name
        company_name
        email_address
        phone_number
      }
      escrow_company_contact {
        company_name
        email_address
        phone_number
      }
    }
    agent {
      agent_party {
        full_name
        company_name
        email_address
        phone_number
      }
    }
    latest_contract {
      media {
        media_id
        media_uri
        download_uri
        internal_title
        file_name
        media_type
        urn
        updated_date
        created_date
      }
      tracking {
        docusign_tracking {
          docusign_recipient_tracking {
            docusign_recipient_tracking_id
            signed
            recipient_status
            recipient_type
            recipient_party {
              first_name
              last_name
              middle_name
              full_name
              email_address
            }
          }
        }
      }
    }
    listing {
      listing_id
      listing_status
      listing_status_label(intent: SEARCH)
      listing_status_group
      auction {
        is_online
      }
      primary_property {
        property_id
      }
      listing_page_path
      primary_photo
      listing_configuration {
        asset_type
        product_type
        is_third_party_online
      }
      formatted_address(format: DOUBLE_LINE)
      venue {
        venue_type
      }
      event {
        trustee_sale
      }
      seller_configuration {
        contract {
          vesting_name
        }
        parent_seller_account_alias
      }
      parties {
        party_id
        party_role
        party_name
        party_organization_title
        party_email
        party_phone
        party_license_number
        party_address {
          country_primary_subdivision
          municipality
          postal_code
          street_description
        }
      }
      listing_wire_instruction {
        bank_name
        routing_number
        account_name
        account_number
        notes
      }
    }
  }
`;
fragments$1.trusteeContract.definition = gql$5`
  fragment BlueprintTrusteeContractFragment on TrusteeContract {
    __typename
    trustee_contract_id
    listing_id
    urn
    page_path
    created_date
    closed_date
    winning_bid_amount
    record_status
    contract_status
    current_user_associated_relationships
    trustee_buyers: buyers {
      id
      first_name
      last_name
    }
    closing_associations {
      ${fragments$1.closingAssociation.reference}
    }
    certificates {
      certificate_type
      file_descriptor {
        urls {
          download
        }
      }
      media {
        media_kind
        urn
      }
    }
    listing {
      listing_id
      listing_status
      listing_status_label(intent: SEARCH)
      listing_status_group
      auction {
        is_online
      }
      primary_property {
        property_id
      }
      listing_page_path
      primary_photo
      listing_configuration {
        asset_type
        product_type
        is_third_party_online
      }
      formatted_address(format: DOUBLE_LINE)
      venue {
        venue_type
      }
      event {
        trustee_sale
      }
      seller_configuration {
        contract {
          vesting_name
        }
      }
      parties {
        party_id
        party_role
        party_name
        party_organization_title
        party_email
        party_license_number
        party_phone
        party_address {
          country_primary_subdivision
          municipality
          postal_code
          street_description
        }
      }
    }
  }
`;
fragments$1.associatedClosings.definition = gql$5`
  fragment BlueprintAssociatedClosingsFragment on User {
    associated_closings(
      filters: $associatedClosingFilters
      viewingAs: $viewingAs
    ) {
      current_page
      size
      total_count
      total_elements
      total_pages
      content {
        ... on ReoOffer {
          ${fragments$1.reoOffer.reference}
          ${fragments$1.workflow().reference}
        }
        ... on TrusteeContract {
          ${fragments$1.trusteeContract.reference}
        }
      }
    }
  }
`;
// --- Lightweight list variants (CLOSE-161) ---
// The paginated Won & Closed list only renders ClosingCard (photo, address,
// status, sold amount/date), a Details/Download action, and a group-level
// MilestonesProgress bar. It does NOT render latest_contract, media, escrow,
// agent, milestones, cost detail, offer parties, or per-row parties/wire info.
// Those per-item resolvers each make their own backend HTTP call, and with a
// full page of closings the operation exceeds RESILIENCE_HTTP_REQUEST_LIMIT
// (default 50) and gets aborted. These trimmed fragments keep the list well
// under that cap. The full fragments above remain for the detail view
// (fetchClosingByListing / fetchIndividualClosing).
fragments$1.reoOfferList.definition = gql$5`
  fragment BlueprintReoOfferListFragment on ReoOffer {
    __typename
    offer_id
    listing_id
    urn
    page_path
    offer_status
    sold_time
    winning_bid_amount
    current_user_associated_relationships
    listing {
      ${fragments$1.closingListing.reference}
    }
  }
`;
fragments$1.trusteeContractList.definition = gql$5`
  fragment BlueprintTrusteeContractListFragment on TrusteeContract {
    __typename
    trustee_contract_id
    listing_id
    urn
    page_path
    created_date
    closed_date
    winning_bid_amount
    current_user_associated_relationships
    certificates {
      certificate_type
      file_descriptor {
        urls {
          download
        }
      }
      media {
        media_kind
        urn
      }
    }
    listing {
      ${fragments$1.closingListing.reference}
    }
  }
`;
fragments$1.associatedClosingsList.definition = gql$5`
  fragment BlueprintAssociatedClosingsListFragment on User {
    associated_closings(
      filters: $associatedClosingFilters
      viewingAs: $viewingAs
    ) {
      current_page
      size
      total_count
      total_elements
      total_pages
      content {
        ... on ReoOffer {
          ${fragments$1.reoOfferList.reference}
          ${fragments$1.workflowList.reference}
        }
        ... on TrusteeContract {
          ${fragments$1.trusteeContractList.reference}
        }
      }
    }
  }
`;

/**
 * CIF constants. Values reconciled against canonical resi-fe-contract
 * `contracting.js` (funding/vesting/entity subsets, title maps), sourced from
 * `@adc/sdk` enums via `deriveOptionsList`. Certified-buyer omitted (not
 * collected buyer-side).
 */
const {
  enums: enums$2
} = graph;
// -------------------------------------------------------------------
// Owning entity — the CIF path fork (canonical OWNING_ENTITY_OPTIONS)
// -------------------------------------------------------------------
const OWNING_ENTITY_OPTIONS = [{
  label: 'Myself as an individual',
  value: enums$2.ReoOfferOwningEntityType.Individual
}, {
  label: 'Myself with Co-Buyer',
  value: enums$2.ReoOfferOwningEntityType.Cobuyer
}, {
  label: 'Entity',
  value: enums$2.ReoOfferOwningEntityType.Entity
}, {
  label: 'Trust',
  value: enums$2.ReoOfferOwningEntityType.Trust
}, {
  label: 'Retirement Plan',
  value: enums$2.ReoOfferOwningEntityType.RetirementPlan
}];
/** Maps an owning-entity selection to its CIF path. */
const OWNING_ENTITY_TO_PATH = {
  [enums$2.ReoOfferOwningEntityType.Individual]: 'individual',
  [enums$2.ReoOfferOwningEntityType.Cobuyer]: 'cobuyer',
  [enums$2.ReoOfferOwningEntityType.Entity]: 'business',
  [enums$2.ReoOfferOwningEntityType.Trust]: 'trust',
  [enums$2.ReoOfferOwningEntityType.RetirementPlan]: 'ira'
};
// -------------------------------------------------------------------
// Vesting (title vesting) — canonical VESTING_TYPE_OPTIONS
// Individual is represented as SOLE_OWNERSHIP; ENTITY is NOT a vesting option
// (entity structures are handled via OWNING_ENTITY_OPTIONS above).
// -------------------------------------------------------------------
const VESTING_TYPE_INDIVIDUAL = enums$2.ReoOfferVestingType.SoleOwnership;
const VESTING_TYPE_OPTIONS = [{
  label: 'Individual',
  value: enums$2.ReoOfferVestingType.SoleOwnership
}, {
  label: 'Joint Tenants',
  value: enums$2.ReoOfferVestingType.JointTenants
}, {
  label: 'Tenants by Entireties',
  value: enums$2.ReoOfferVestingType.TenantsByEntireties
}, {
  label: 'Tenants in Common',
  value: enums$2.ReoOfferVestingType.TenantsInCommon
}, {
  label: 'Community Property',
  value: enums$2.ReoOfferVestingType.CommunityProperty
}, {
  label: 'Sole and Separate Properties',
  value: enums$2.ReoOfferVestingType.SoleAndSeparate
}, {
  label: 'Community Property with ROS',
  value: enums$2.ReoOfferVestingType.CommunityPropertyRos
}, {
  label: 'Other',
  value: enums$2.ReoOfferVestingType.Other
}];
// -------------------------------------------------------------------
// Entity type / retirement plan type (canonical ENTITY_TYPE_OPTIONS + PLAN_TYPE_OPTIONS)
// -------------------------------------------------------------------
const ENTITY_TYPE_OPTIONS = [{
  label: 'Corporation',
  value: enums$2.ReoOfferOwningEntitySubType.Corporation
}, {
  label: 'General Partnership',
  value: enums$2.ReoOfferOwningEntitySubType.GeneralPartnership
}, {
  label: 'Limited Liability Company',
  value: enums$2.ReoOfferOwningEntitySubType.Llc
}, {
  label: 'Limited Liability Limited Partnership',
  value: enums$2.ReoOfferOwningEntitySubType.LtdLiabilityPartnership
}, {
  label: 'Limited Partnership',
  value: enums$2.ReoOfferOwningEntitySubType.LimitedPartnership
}];
const PLAN_TYPE_OPTIONS = [{
  label: '401K',
  value: enums$2.ReoOfferOwningEntitySubType.Retirement_401K
}, {
  label: 'IRA',
  value: enums$2.ReoOfferOwningEntitySubType.Ira
}, {
  label: 'Pension Plan',
  value: enums$2.ReoOfferOwningEntitySubType.PensionPlan
}];
// -------------------------------------------------------------------
// Entity sub-type resolution per path
// Trust has NO sub-type picker (canonical contracting.js) — its entity_type
// and entity_sub_type are both fixed to TRUST. business/ira present a picker;
// individual/cobuyer are not entity-bearing.
// -------------------------------------------------------------------
/** Paths backed by an EntityProfile. Drives entity_type_selection + entity steps. */
const ENTITY_BEARING_PATHS = ['business', 'trust', 'ira'];
/** Trust's fixed entity sub-type (assigned, not chosen — no picker). */
const TRUST_ENTITY_SUBTYPE = enums$2.ReoOfferOwningEntitySubType.Trust;
/**
 * Sub-type picker options by path. Trust is intentionally absent — it has no
 * picker; the flow auto-assigns TRUST_ENTITY_SUBTYPE and skips the step.
 */
const ENTITY_SUBTYPE_OPTIONS_BY_PATH = {
  business: ENTITY_TYPE_OPTIONS,
  ira: PLAN_TYPE_OPTIONS
};
// -------------------------------------------------------------------
// Funding (canonical: deriveOptionsList(FinanceType) ∩ ALLOWED + label overrides)
// -------------------------------------------------------------------
const ALLOWED_FUNDING_TYPES = [enums$2.FinanceType.Cash, enums$2.FinanceType.Conventional, enums$2.FinanceType.Fha, enums$2.FinanceType.HardMoneyLoan, enums$2.FinanceType.LineOfCredit, enums$2.FinanceType.Other, enums$2.FinanceType.RehabLoan, enums$2.FinanceType.Usda, enums$2.FinanceType.Va];
const FUNDING_TYPE_OPTIONS_TO_UPPER_CASE = [enums$2.FinanceType.Fha, enums$2.FinanceType.Usda, enums$2.FinanceType.Va];
const FUNDING_TYPE_LABEL_OVERRIDES = Object.fromEntries(FUNDING_TYPE_OPTIONS_TO_UPPER_CASE.map(value => [value, value.toUpperCase()]));
const FUNDING_TYPE_OPTIONS = deriveOptionsList(enums$2.FinanceType, FUNDING_TYPE_LABEL_OVERRIDES).filter(option => ALLOWED_FUNDING_TYPES.includes(option.value));
/** Funding types needing lender/finance details (Cash + Other skip those steps). */
const SHOW_FINANCE_DETAILS = ALLOWED_FUNDING_TYPES.filter(type => type !== enums$2.FinanceType.Cash && type !== enums$2.FinanceType.Other);
// -------------------------------------------------------------------
// Marital status (canonical MARITAL_STATUS_OPTIONS + conflicts + reduced set)
// -------------------------------------------------------------------
/** Two-person (married-couple) statuses that trigger the co-buyer/spouse path. */
const MARITAL_STATUS_CONFLICTS = [{
  label: 'Husband and Wife',
  value: enums$2.MaritalStatus.HusbandAndWife
}, {
  label: 'Wife and Husband',
  value: enums$2.MaritalStatus.WifeAndHusband
}, {
  label: 'Wife and Wife',
  value: enums$2.MaritalStatus.WifeAndWife
}, {
  label: 'Husband and Husband',
  value: enums$2.MaritalStatus.HusbandAndHusband
}];
const REDUCED_MARITAL_STATUS_OPTIONS = [{
  label: 'A Single Man',
  value: enums$2.MaritalStatus.SingleMan
}, {
  label: 'A Single Woman',
  value: enums$2.MaritalStatus.SingleWoman
}, {
  label: 'A Married Man',
  value: enums$2.MaritalStatus.MarriedMan
}, {
  label: 'A Married Woman',
  value: enums$2.MaritalStatus.MarriedWoman
}];
const MARITAL_STATUS_OPTIONS = [{
  label: 'A Single Man',
  value: enums$2.MaritalStatus.SingleMan
}, {
  label: 'A Single Woman',
  value: enums$2.MaritalStatus.SingleWoman
}, {
  label: 'An Unmarried Man',
  value: enums$2.MaritalStatus.UnmarriedMan
}, {
  label: 'An Unmarried Woman',
  value: enums$2.MaritalStatus.UnmarriedWoman
}, {
  label: 'A Widower',
  value: enums$2.MaritalStatus.Widower
}, {
  label: 'A Widow',
  value: enums$2.MaritalStatus.Widow
}, {
  label: 'A Married Man',
  value: enums$2.MaritalStatus.MarriedMan
}, {
  label: 'A Married Woman',
  value: enums$2.MaritalStatus.MarriedWoman
}, ...MARITAL_STATUS_CONFLICTS, {
  label: 'Other',
  value: enums$2.MaritalStatus.Other
}];
/** Married/two-person statuses that trigger the spouse/co-buyer step. */
const MARRIED_MARITAL_STATUSES = [enums$2.MaritalStatus.MarriedMan, enums$2.MaritalStatus.MarriedWoman, ...MARITAL_STATUS_CONFLICTS.map(o => o.value)];
// -------------------------------------------------------------------
// Signer/position title options — keyed by entity sub-type (canonical)
// -------------------------------------------------------------------
const ENTITY_CORP_SIGNOR_TITLE_OPTIONS = [{
  label: 'President',
  value: 'PRESIDENT'
}, {
  label: 'Vice President',
  value: 'VICE_PRESIDENT'
}, {
  label: 'Treasurer',
  value: 'TREASURER'
}, {
  label: 'Secretary',
  value: 'SECRETARY'
}, {
  label: 'CEO',
  value: 'CEO'
}, {
  label: 'CFO',
  value: 'CFO'
}, {
  label: 'Chief Executive Officer',
  value: 'CHIEF_EXECUTIVE_OFFICER'
}, {
  label: 'Authorized Signatory',
  value: 'AUTHORIZED_SIGNATORY'
}, {
  label: 'Power of Attorney',
  value: 'POWER_OF_ATTORNEY'
}];
const PARTNERSHIP_SIGNOR_TITLE_OPTIONS = [{
  label: 'General Partner',
  value: 'GENERAL_PARTNER'
}, {
  label: 'Manager',
  value: 'MANAGER'
}, {
  label: 'Member',
  value: 'MEMBER'
}, {
  label: 'Managing Member',
  value: 'MANAGING_MEMBER'
}, ...ENTITY_CORP_SIGNOR_TITLE_OPTIONS];
const LLC_SIGNOR_TITLE_OPTIONS = [{
  label: 'Manager',
  value: 'MANAGER'
}, {
  label: 'Member',
  value: 'MEMBER'
}, {
  label: 'Managing Member',
  value: 'MANAGING_MEMBER'
}, {
  label: 'Authorized Signatory',
  value: 'AUTHORIZED_SIGNATORY'
}, {
  label: 'Power of Attorney',
  value: 'POWER_OF_ATTORNEY'
}];
/** Title options for entity signers, keyed by entity sub-type. */
const ENTITY_SIGNOR_TITLE_OPTIONS = {
  [enums$2.ReoOfferOwningEntitySubType.Corporation]: ENTITY_CORP_SIGNOR_TITLE_OPTIONS,
  [enums$2.ReoOfferOwningEntitySubType.GeneralPartnership]: PARTNERSHIP_SIGNOR_TITLE_OPTIONS,
  [enums$2.ReoOfferOwningEntitySubType.LtdLiabilityPartnership]: PARTNERSHIP_SIGNOR_TITLE_OPTIONS,
  [enums$2.ReoOfferOwningEntitySubType.LimitedPartnership]: PARTNERSHIP_SIGNOR_TITLE_OPTIONS,
  [enums$2.ReoOfferOwningEntitySubType.Llc]: LLC_SIGNOR_TITLE_OPTIONS
};
/** Title options for retirement-plan signers, keyed by plan sub-type. */
const RETIREMENT_SIGNOR_TITLE_OPTIONS = {
  [enums$2.ReoOfferOwningEntitySubType.Ira]: [{
    label: 'Authorized Signatory',
    value: 'AUTHORIZED_SIGNATORY'
  }],
  [enums$2.ReoOfferOwningEntitySubType.Retirement_401K]: [{
    label: 'Trustee',
    value: 'TRUSTEE'
  }],
  [enums$2.ReoOfferOwningEntitySubType.PensionPlan]: [{
    label: 'Trustee',
    value: 'TRUSTEE'
  }]
};
const TRUST_SIGNER_TITLE_OPTIONS = [{
  label: 'Trustee',
  value: 'TRUSTEE'
}, {
  label: 'Authorized Signatory',
  value: 'AUTHORIZED_SIGNATORY'
}, {
  label: 'Power of Attorney',
  value: 'POWER_OF_ATTORNEY'
}, {
  label: 'Other',
  value: 'OTHER'
}];
// -------------------------------------------------------------------
// Agent representation (canonical AGENT_RADIO_OPTIONS)
// -------------------------------------------------------------------
const AGENT_RADIO_OPTIONS = [{
  label: 'Yes',
  value: enums$2.ReoOfferRealEstateAgentType.Agent
}, {
  label: 'No',
  value: enums$2.ReoOfferRealEstateAgentType.None
}, {
  label: 'I am a real estate agent representing myself',
  value: enums$2.ReoOfferRealEstateAgentType.Self
}];
// -------------------------------------------------------------------
// Closing / escrow company (canonical ESCROW_COMPANY_OPTIONS)
// -------------------------------------------------------------------
const ESCROW_COMPANY_SELLER = 'sellers';
const ESCROW_COMPANY_OWN = 'own';
const ESCROW_COMPANY_OPTIONS = [{
  label: 'I will be using the Seller selected escrow company',
  value: ESCROW_COMPANY_SELLER
}, {
  label: 'I will be using my own escrow company',
  value: ESCROW_COMPANY_OWN
}];
// -------------------------------------------------------------------
// Purchase type / occupancy (canonical PURCHASE_TYPE_RADIO_OPTIONS)
// -------------------------------------------------------------------
const PURCHASE_TYPE_OPTIONS = [{
  label: 'An investment',
  value: enums$2.ReoOfferPurchaseType.Investment
}, {
  label: 'My primary residence',
  value: enums$2.ReoOfferPurchaseType.Primary
}, {
  label: 'A second home',
  value: enums$2.ReoOfferPurchaseType.Secondary
}];
// -------------------------------------------------------------------
// Additional questions — seller-agnostic (canonical). Seller-code-gated
// options (FNMA/Freddie/homesteps) stay in FE-2.
// -------------------------------------------------------------------
const YES_NO_OPTIONS = [{
  label: 'Yes',
  value: 'yes'
}, {
  label: 'No',
  value: 'no'
}];
const TITLE_INSURANCE_OPTIONS = YES_NO_OPTIONS;
const FIRST_TIME_BUYER_OPTIONS = YES_NO_OPTIONS;
// -------------------------------------------------------------------
// Field-specification labels (canonical FIELD_SPECIFICATION_LABEL_MAP)
// -------------------------------------------------------------------
const FIELD_SPECIFICATION_LABEL_MAP = {
  funding_type: 'How will you be funding this purchase?',
  title_insurance_flag: 'Will you be purchasing Title Insurance?',
  real_estate_agent_type: 'Did you work with a Real Estate Agent?',
  is_agent_included: 'Is agent included?'
};
// -------------------------------------------------------------------
// Misc (canonical + ticket)
// -------------------------------------------------------------------
/** Maximum number of co-buyers supported (canonical MAX_COBUYERS). */
const MAX_COBUYERS = 3;
// -------------------------------------------------------------------
// Business owner type — per-owner INDIVIDUAL vs ENTITY toggle on
// the business-ownership screen. Local union until `@adc/sdk` exposes the
// generated `CifOwnerType` enum from the graph.
// -------------------------------------------------------------------
const OWNER_TYPE_OPTIONS = [{
  label: 'Individual',
  value: 'INDIVIDUAL'
}, {
  label: 'Entity',
  value: 'ENTITY'
}];

var constants = /*#__PURE__*/Object.freeze({
    __proto__: null,
    AGENT_RADIO_OPTIONS: AGENT_RADIO_OPTIONS,
    ALLOWED_FUNDING_TYPES: ALLOWED_FUNDING_TYPES,
    ENTITY_BEARING_PATHS: ENTITY_BEARING_PATHS,
    ENTITY_SIGNOR_TITLE_OPTIONS: ENTITY_SIGNOR_TITLE_OPTIONS,
    ENTITY_SUBTYPE_OPTIONS_BY_PATH: ENTITY_SUBTYPE_OPTIONS_BY_PATH,
    ENTITY_TYPE_OPTIONS: ENTITY_TYPE_OPTIONS,
    ESCROW_COMPANY_OPTIONS: ESCROW_COMPANY_OPTIONS,
    ESCROW_COMPANY_OWN: ESCROW_COMPANY_OWN,
    ESCROW_COMPANY_SELLER: ESCROW_COMPANY_SELLER,
    FIELD_SPECIFICATION_LABEL_MAP: FIELD_SPECIFICATION_LABEL_MAP,
    FIRST_TIME_BUYER_OPTIONS: FIRST_TIME_BUYER_OPTIONS,
    FUNDING_TYPE_LABEL_OVERRIDES: FUNDING_TYPE_LABEL_OVERRIDES,
    FUNDING_TYPE_OPTIONS: FUNDING_TYPE_OPTIONS,
    MARITAL_STATUS_CONFLICTS: MARITAL_STATUS_CONFLICTS,
    MARITAL_STATUS_OPTIONS: MARITAL_STATUS_OPTIONS,
    MARRIED_MARITAL_STATUSES: MARRIED_MARITAL_STATUSES,
    MAX_COBUYERS: MAX_COBUYERS,
    OWNER_TYPE_OPTIONS: OWNER_TYPE_OPTIONS,
    OWNING_ENTITY_OPTIONS: OWNING_ENTITY_OPTIONS,
    OWNING_ENTITY_TO_PATH: OWNING_ENTITY_TO_PATH,
    PLAN_TYPE_OPTIONS: PLAN_TYPE_OPTIONS,
    PURCHASE_TYPE_OPTIONS: PURCHASE_TYPE_OPTIONS,
    REDUCED_MARITAL_STATUS_OPTIONS: REDUCED_MARITAL_STATUS_OPTIONS,
    RETIREMENT_SIGNOR_TITLE_OPTIONS: RETIREMENT_SIGNOR_TITLE_OPTIONS,
    SHOW_FINANCE_DETAILS: SHOW_FINANCE_DETAILS,
    TITLE_INSURANCE_OPTIONS: TITLE_INSURANCE_OPTIONS,
    TRUST_ENTITY_SUBTYPE: TRUST_ENTITY_SUBTYPE,
    TRUST_SIGNER_TITLE_OPTIONS: TRUST_SIGNER_TITLE_OPTIONS,
    VESTING_TYPE_INDIVIDUAL: VESTING_TYPE_INDIVIDUAL,
    VESTING_TYPE_OPTIONS: VESTING_TYPE_OPTIONS,
    YES_NO_OPTIONS: YES_NO_OPTIONS
});

/**
 * Headless spouse-pairing relationships for the CIF co-buyer path (CLOSE-350).
 *
 * Ported from the design-system `multiCoBuyerForm` (`shared/utils/relationships.js`)
 * and adapted to the Blueprint `BuyerInfo` shape. No React, no JSX — pure data
 * helpers the FE wizard consumes to build the "who is married to whom" model.
 *
 * A person is identified by `getBuyerId` (name + email). Pairing is stored
 * per-buyer on the gender-neutral fields `isSpouseOnContract` / `spouseName` /
 * `spouseEmail`; only `maritalStatus` carries gender.
 */
/** Sentinel: a married person whose spouse is NOT on the contract. */
const SOMEONE_ELSE = 'SOMEONE_ELSE';
const isMarriedStatus = status => Boolean(status && MARRIED_MARITAL_STATUSES.includes(status));
/** True when this buyer holds a married marital status. */
const isMarriedBuyer = buyer => isMarriedStatus(buyer.maritalStatus);
/** Two or more married people means a pairing question is warranted. */
const checkTwoMarriedPeople = (buyers = []) => buyers.filter(isMarriedBuyer).length >= 2;
/** Full legal name (first middle last), collapsed. */
const getBuyerFullName$1 = buyer => [buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ').trim();
/** Stable identity for a buyer: name + email (matches multiCoBuyerForm). */
const getBuyerId = buyer => `${getBuyerFullName$1(buyer)} ${buyer.email ?? ''}`.trim().toLowerCase();
/** Find a buyer by their derived id. */
const findBuyerById = (buyers, id) => id ? buyers.find(b => getBuyerId(b) === id) : undefined;
/**
 * Derive the current relationship value for a buyer from persisted spouse
 * fields: the spouse's id (if the spouse is another buyer on the contract),
 * SOMEONE_ELSE (spouse explicitly off-contract), or '' (unanswered).
 */
const deriveRelationship = (buyer, buyers) => {
  if (buyer.isSpouseOnContract && buyer.spouseName) {
    const spouse = buyers.find(b => getBuyerFullName$1(b) === buyer.spouseName && b !== buyer);
    if (spouse) return getBuyerId(spouse);
  }
  if (buyer.isSpouseOnContract === false) return SOMEONE_ELSE;
  return '';
};
/**
 * Build the initial relationship map over the married buyers. Keyed by buyerId,
 * value is the spouse's buyerId / SOMEONE_ELSE / '' (unanswered).
 */
const buildRelationshipsMap = (buyers = []) => {
  const married = buyers.filter(isMarriedBuyer);
  const map = {};
  married.forEach(buyer => {
    map[getBuyerId(buyer)] = deriveRelationship(buyer, buyers);
  });
  return map;
};
/**
 * Married buyers still selectable as a partner for `buyerId`: married, not the
 * buyer themselves, and not already answered/claimed in the map. A buyer with
 * any truthy map value (a partner OR SOMEONE_ELSE) is excluded, enforcing
 * monogamy — a claimed person can't be offered to a third.
 */
const getAvailablePartners = (buyers, relationships, buyerId) => buyers.filter(b => {
  if (!isMarriedBuyer(b)) return false;
  const id = getBuyerId(b);
  if (id === buyerId) return false;
  return !relationships[id]; // paired or SOMEONE_ELSE => unavailable
});
/**
 * Set `buyerId`'s partner to `spouseId`, keeping the map consistent:
 *  - clears any stale reciprocal left by `buyerId`'s previous partner (re-pair),
 *  - sets the mutual reciprocal when pairing to an on-contract buyer,
 *  - SOMEONE_ELSE / '' write only `buyerId`'s own entry (no reciprocal).
 * Returns a new map (does not mutate the input).
 */
const setPartner = (relationships, buyerId, spouseId) => {
  const next = {
    ...relationships
  };
  // Free the previous partner (if it still points back at buyerId).
  const prev = next[buyerId];
  if (prev && prev !== SOMEONE_ELSE && next[prev] === buyerId) {
    next[prev] = '';
  }
  next[buyerId] = spouseId;
  if (spouseId && spouseId !== SOMEONE_ELSE) {
    // Free whoever the new partner was previously bound to, then bind mutually.
    const partnerPrev = next[spouseId];
    if (partnerPrev && partnerPrev !== SOMEONE_ELSE && next[partnerPrev] === spouseId) {
      next[partnerPrev] = '';
    }
    next[spouseId] = buyerId;
  }
  return next;
};
/**
 * Any married buyer still unanswered ('') with no available on-contract partner
 * left must have their spouse off-contract -> default to SOMEONE_ELSE. A single
 * pass is sufficient: forcing SOMEONE_ELSE only ever removes availability
 * (monotonic), and it only fires for a buyer with zero options — a determined
 * outcome, never a genuine choice. Works for n >= 2 married. Returns a new map.
 */
const resolveStrandedMarried = (buyers, relationships) => {
  const next = {
    ...relationships
  };
  buyers.filter(isMarriedBuyer).forEach(b => {
    const id = getBuyerId(b);
    if (next[id]) return; // already answered/paired
    if (getAvailablePartners(buyers, next, id).length === 0) {
      next[id] = SOMEONE_ELSE;
    }
  });
  return next;
};
/**
 * Apply a relationship map back onto the buyers, writing the gender-neutral
 * pairing fields. Mirrors multiCoBuyerForm `matchRelationship`:
 *  - paired to another buyer -> isSpouseOnContract=true + spouse name/email
 *  - SOMEONE_ELSE            -> isSpouseOnContract=false, spouse fields cleared
 *  - unanswered ('')         -> spouse fields cleared (undefined)
 * Returns a new array (does not mutate the input).
 */
const matchRelationship = (buyers, relationships = {}) => buyers.map(buyer => {
  const spouseId = relationships[getBuyerId(buyer)];
  const spouse = findBuyerById(buyers, spouseId);
  if (spouseId && spouseId !== SOMEONE_ELSE && spouse) {
    return {
      ...buyer,
      isSpouseOnContract: true,
      spouseName: getBuyerFullName$1(spouse),
      spouseEmail: spouse.email,
      spouseMaritalStatus: spouse.maritalStatus
    };
  }
  if (spouseId === SOMEONE_ELSE) {
    return {
      ...buyer,
      isSpouseOnContract: false,
      spouseName: undefined,
      spouseEmail: undefined,
      spouseMaritalStatus: undefined
    };
  }
  return {
    ...buyer,
    isSpouseOnContract: undefined,
    spouseName: undefined,
    spouseEmail: undefined,
    spouseMaritalStatus: undefined
  };
});
/**
 * When a buyer becomes single, clear their spouse linkage AND their former
 * partner's linkage. Mirrors multiCoBuyerForm `updateMaritalStatus`. Returns a
 * new array.
 */
const clearSingleBuyerSpouses = buyers => {
  const result = buyers.map(b => ({
    ...b
  }));
  result.forEach(buyer => {
    if (isMarriedBuyer(buyer)) return;
    // This buyer is single — clear any partner pointing at them, then self.
    if (buyer.spouseName) {
      const partner = result.find(b => getBuyerFullName$1(b) === buyer.spouseName && b !== buyer);
      if (partner) {
        partner.isSpouseOnContract = undefined;
        partner.spouseName = undefined;
        partner.spouseEmail = undefined;
        partner.spouseMaritalStatus = undefined;
      }
    }
    buyer.isSpouseOnContract = undefined;
    buyer.spouseName = undefined;
    buyer.spouseEmail = undefined;
    buyer.spouseMaritalStatus = undefined;
  });
  return result;
};

var relationships = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SOMEONE_ELSE: SOMEONE_ELSE,
    buildRelationshipsMap: buildRelationshipsMap,
    checkTwoMarriedPeople: checkTwoMarriedPeople,
    clearSingleBuyerSpouses: clearSingleBuyerSpouses,
    findBuyerById: findBuyerById,
    getAvailablePartners: getAvailablePartners,
    getBuyerFullName: getBuyerFullName$1,
    getBuyerId: getBuyerId,
    isMarriedBuyer: isMarriedBuyer,
    matchRelationship: matchRelationship,
    resolveStrandedMarried: resolveStrandedMarried,
    setPartner: setPartner
});

// ---------------------------------------------------------------------------
// Buyer name primitives (CLOSE-25). Single source of truth for a buyer's
// full legal name. The name is resolved server-side via
// ReoOfferParty.display_name (CLOSE-133, first + middle + last), so consumers
// read it rather than assembling from CIF parts. Shared by doc-gen, the buyer
// dashboard (POF), and other consumers.
// ---------------------------------------------------------------------------
const getBuyerArray = reoOffer => reoOffer.buyers ?? [];
/** Full legal name of a party, resolved server-side (ReoOfferParty.display_name). */
const getFullName = party => party?.display_name?.trim() ?? '';
/** Buyer's full legal name, resolved server-side (ReoOfferParty.display_name). */
const getBuyerFullName = buyer => getFullName(buyer?.buyer_party);
const findPrimaryBuyer = reoOffer => getBuyerArray(reoOffer).filter(buyer => buyer.primary_flag).at(0);
const getPrimaryBuyerFullName = reoOffer => {
  const buyer = findPrimaryBuyer(reoOffer);
  return buyer?.buyer_party ? getBuyerFullName(buyer) : '';
};
const getCoBuyers = reoOffer => getBuyerArray(reoOffer).filter(buyer => !buyer.primary_flag);
const isReoOffer = closingMethod => closingMethod?.__typename === 'ReoOffer';
const isReoWorkflowCancelled = closingMethod => Boolean(isReoOffer(closingMethod) && (closingMethod.offer_status === graph.enums.OfferStatus.Cancelled || closingMethod?.offer_milestones?.cancellation_received_from_seller_date));
const isReoWorkflowRejected = closingMethod => Boolean(isReoOffer(closingMethod) && closingMethod.subject_to_seller_confirmation && closingMethod?.offer_milestones?.subject_to_seller_status?.toUpperCase?.() === graph.enums.ReoOfferExtensionServicerDecision.Rejected);
const getBuyerCompanyName = reoOffer => {
  const owningEntityType = reoOffer?.owning_entity?.entity_type;
  switch (owningEntityType) {
    case graph.enums.ReoOfferOwningEntityType.Individual:
    case graph.enums.ReoOfferOwningEntityType.Cobuyer:
      return '';
    case graph.enums.ReoOfferOwningEntityType.Entity:
      return reoOffer?.entity_profile?.entity_name ?? '';
    case graph.enums.ReoOfferOwningEntityType.Trust:
    case graph.enums.ReoOfferOwningEntityType.RetirementPlan:
      return reoOffer?.owning_entity?.entity_name ?? '';
    default:
      return '';
  }
};
/**
 * Joins names into an "or" list per POF instruction copy requirements:
 *   1 name   -> "Jane Doe"
 *   2 names  -> "Jane Doe or John Doe"
 *   3+ names -> "Jane Doe, John Doe, or John Smith"
 */
const joinNamesWithOr = names => {
  const cleaned = names.map(name => (name || '').trim()).filter(Boolean);
  if (cleaned.length === 0) return '';
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} or ${cleaned[1]}`;
  return `${cleaned.slice(0, -1).join(', ')}, or ${cleaned[cleaned.length - 1]}`;
};
/**
 * Builds the contract name(s) a Proof of Funds document must show.
 * - Entity / Trust / Retirement Plan buyer -> full entity name.
 * - Individual buyer(s) -> full buyer name(s) as an "or" list, primary first.
 */
const getPofContractNames = reoOffer => {
  const entityName = getBuyerCompanyName(reoOffer);
  if (entityName) return entityName;
  // Primary buyer first, then co-buyers. Names are built from CIF
  // first/middle/last via the shared buyer-name primitives (CLOSE-25).
  const primaryBuyer = findPrimaryBuyer(reoOffer);
  const orderedBuyers = [...(primaryBuyer ? [primaryBuyer] : []), ...getCoBuyers(reoOffer)];
  const names = orderedBuyers.map(getBuyerFullName).filter(Boolean);
  return joinNamesWithOr(names);
};
// ---------------------------------------------------------------------------
// ClosingMethod discriminators (migrated from resi-fe-dashboard,
// associatedClosingsList/helpers.ts). Complements isReoOffer above so all
// closing-method type narrowing lives in one place.
// ---------------------------------------------------------------------------
const isTrusteeContract = closingMethod => closingMethod?.__typename === 'TrusteeContract';
/** The Certificate of Sale document for a trustee contract, if present. */
const getCertificateOfSale = contract => contract?.certificates?.find(certificate => certificate?.media?.media_kind === graph.enums.AuctionResultMediaFileKind.CertificateOfSales);
// ---------------------------------------------------------------------------
// Closing company / party / wire-instruction extractors (migrated from
// resi-fe-dashboard, individualClosing/importantLinks/helpers.ts). Pure
// derivations over Listing / ReoOffer graph shapes shared by the buyer
// dashboard "Important Links" modals.
// ---------------------------------------------------------------------------
const getSellerClosingCompany = listing => {
  if (!listing?.parties) {
    return null;
  }
  const parties = listing.parties;
  const companyName = parties.find(party => party?.party_role === graph.enums.ListingPartyRole.ContractClosingCompany)?.party_name;
  if (!companyName) {
    return null;
  }
  // prioritize ESCROW_OFFICER over AUCTION_CLOSING_COMPANY_CONTACT
  const priorityRoles = [graph.enums.ListingPartyRole.EscrowOfficer, graph.enums.ListingPartyRole.AuctionClosingCompanyContact];
  const prioritizedParty = parties.find(party => party?.party_role && priorityRoles.includes(party.party_role));
  return {
    companyName,
    contactName: companyName,
    email: prioritizedParty?.party_email,
    phone: prioritizedParty?.party_phone,
    name: prioritizedParty?.party_name
  };
};
const getBuyerClosingCompany = escrowCompany => {
  if (!escrowCompany) return null;
  const {
    escrow_company_party,
    escrow_company_contact
  } = escrowCompany;
  return {
    companyName: escrow_company_party?.company_name ?? escrow_company_contact?.company_name,
    contactName: escrow_company_party?.company_name ?? escrow_company_contact?.company_name,
    email: escrow_company_party?.email_address ?? escrow_company_contact?.email_address,
    phone: escrow_company_party?.phone_number ?? escrow_company_contact?.phone_number
  };
};
const getBuyerAgent = agent => {
  if (!agent) return null;
  if (!agent?.agent_party) return null;
  return {
    companyName: agent?.agent_party?.company_name,
    contactName: agent?.agent_party?.full_name,
    email: agent?.agent_party?.email_address,
    phone: agent?.agent_party?.phone_number,
    name: agent?.agent_party?.full_name
  };
};
const getListingAgent = listing => {
  if (!listing || !listing.parties) {
    return null;
  }
  const parties = listing.parties;
  // Get listing agent
  const listingAgent = parties.find(party => party?.party_role === graph.enums.ListingPartyRole.ListingAgent);
  if (!listingAgent?.party_name) {
    return null;
  }
  // Get broker (fallback for email, phone, and company name)
  const broker = parties.find(party => party?.party_role === graph.enums.ListingPartyRole.Broker);
  return {
    companyName: broker?.party_name,
    contactName: broker?.party_name,
    email: listingAgent.party_email ?? broker?.party_email,
    phone: listingAgent.party_phone ?? broker?.party_phone,
    name: listingAgent.party_name
  };
};
const getSellerWireInstructions = listing => {
  if (!listing?.parties) return null;
  const wireInstructionCompany = listing.parties.find(party => party?.party_role === graph.enums.ListingPartyRole.WireInstructionCompany);
  const listingWireInstructions = listing.listing_wire_instruction ?? {};
  const bankAddress = wireInstructionCompany?.party_address ? [wireInstructionCompany.party_address.street_description, wireInstructionCompany.party_address.municipality, wireInstructionCompany.party_address.country_primary_subdivision, wireInstructionCompany.party_address.postal_code].filter(Boolean).join(', ') : '';
  return {
    bankName: listingWireInstructions.bank_name ?? '',
    routingNumber: listingWireInstructions.routing_number ?? '',
    accountName: listingWireInstructions.account_name ?? '',
    accountNumber: listingWireInstructions.account_number ?? '',
    bankAddress,
    bankPhone: wireInstructionCompany?.party_phone ?? '',
    additionalNotes: listingWireInstructions.notes ?? ''
  };
};
// ---------------------------------------------------------------------------
// Seller-alias helpers (migrated from resi-fe-dashboard). Kept in closing
// utils for now; candidates to promote to listing utils if reused outside the
// closing surface.
// ---------------------------------------------------------------------------
const getParentSeller = listing => listing?.seller_configuration?.parent_seller_account_alias;
const getIsFreddieMacSeller = listing => getParentSeller(listing) === 'FMC';
const getIsFannieMaeSeller = listing => getParentSeller(listing) === 'FNM';
// ---------------------------------------------------------------------------
// CIF path + predicate helpers. Single source of truth for the branching logic
// used by the FE CIF flow definition and its resume routing (deriveCurrentStep)
// — kept here so the two stay in sync and any other consumer can reuse them.
// All operate on a partial CIFData so they work mid-flow.
// ---------------------------------------------------------------------------
/** Resolve the active CIF path from the entity type (preferred) or buyer/marital signals. */
const getCifPath = data => {
  const entityType = data.entityProfile?.entityType;
  if (entityType && OWNING_ENTITY_TO_PATH[entityType]) {
    return OWNING_ENTITY_TO_PATH[entityType];
  }
  // Co-buyer inferred from present co-buyers or a married two-person status.
  if (data.coBuyers?.length || data.buyer?.maritalStatus && MARRIED_MARITAL_STATUSES.includes(data.buyer.maritalStatus)) {
    return 'cobuyer';
  }
  return 'individual';
};
/** Path backed by an EntityProfile (business/trust/ira). */
const isEntityBearingPath = path => ENTITY_BEARING_PATHS.includes(path);
/** Path that presents a sub-type picker (business/ira). Trust is absent → auto-assign. */
const pathHasSubtypePicker = path => Boolean(ENTITY_SUBTYPE_OPTIONS_BY_PATH[path]);
/** True when the active path is the trust path. */
const isTrustPath = data => getCifPath(data ?? {}) === 'trust';
/**
 * Trust is the only entity path with no sub-type picker: entity_type_selection
 * is skipped and TRUST_ENTITY_SUBTYPE is auto-assigned so the trust path can
 * satisfy the entity gating without a picker screen. Idempotent — returns the
 * same reference when nothing needs patching.
 */
const withTrustSubtype = data => {
  if (!isTrustPath(data) || data.entityProfile?.entitySubType) return data;
  return {
    ...data,
    entityProfile: {
      ...data.entityProfile,
      entitySubType: TRUST_ENTITY_SUBTYPE
    }
  };
};
/** Buyer's marital status is a married two-person status (drives spouse/co-buyer). */
const isMarried = data => Boolean(data?.buyer?.maritalStatus && MARRIED_MARITAL_STATUSES.includes(data.buyer.maritalStatus));
/** Funding type requires lender/lending details (i.e. not cash). */
const isFinanced = data => Boolean(data?.funding?.fundingType && SHOW_FINANCE_DETAILS.includes(data.funding.fundingType));
/** Buyer is unrepresented (no agent). */
const agentIsNone = data => data?.agent?.agentType === graph.enums.ReoOfferRealEstateAgentType.None;
/** Buyer is bringing their own closing (escrow/title) company. */
const usesOwnCloser = data => data?.closingCompany?.useSellerClosingCompany === false;

var utils = /*#__PURE__*/Object.freeze({
    __proto__: null,
    agentIsNone: agentIsNone,
    findPrimaryBuyer: findPrimaryBuyer,
    getBuyerAgent: getBuyerAgent,
    getBuyerArray: getBuyerArray,
    getBuyerClosingCompany: getBuyerClosingCompany,
    getBuyerCompanyName: getBuyerCompanyName,
    getBuyerFullName: getBuyerFullName,
    getCertificateOfSale: getCertificateOfSale,
    getCifPath: getCifPath,
    getCoBuyers: getCoBuyers,
    getFullName: getFullName,
    getIsFannieMaeSeller: getIsFannieMaeSeller,
    getIsFreddieMacSeller: getIsFreddieMacSeller,
    getListingAgent: getListingAgent,
    getParentSeller: getParentSeller,
    getPofContractNames: getPofContractNames,
    getPrimaryBuyerFullName: getPrimaryBuyerFullName,
    getSellerClosingCompany: getSellerClosingCompany,
    getSellerWireInstructions: getSellerWireInstructions,
    isEntityBearingPath: isEntityBearingPath,
    isFinanced: isFinanced,
    isMarried: isMarried,
    isReoOffer: isReoOffer,
    isReoWorkflowCancelled: isReoWorkflowCancelled,
    isReoWorkflowRejected: isReoWorkflowRejected,
    isTrustPath: isTrustPath,
    isTrusteeContract: isTrusteeContract,
    joinNamesWithOr: joinNamesWithOr,
    pathHasSubtypePicker: pathHasSubtypePicker,
    relationships: relationships,
    usesOwnCloser: usesOwnCloser,
    withTrustSubtype: withTrustSubtype
});

/* istanbul ignore file */
// -------------------------------------------------------------------
// Mutations
// -------------------------------------------------------------------
/**
 * Perform a closing workflow action (extend, cancel, fallout, close).
 */
const performClosingWorkflowAction = input => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_performClosingWorkflowAction')}(
          $input: ClosingWorkflowActionInput!
        ) {
          performClosingWorkflowAction(input: $input) {
            offer_milestones {
              extended_through_date
              extension_fully_executed_date
              cancellation_received_from_seller_date
              cancellation_fully_executed_date
              cancellation_date
              closing_cancellation_reason
              fallout_date
              fallout_reason
              fallout_caused_by
            }
            offer {
              offer_id
              offer_status
              scheduled_closing_date
              closing_cost_detail {
                actual_closed_date
              }
            }
          }
        }
      `,
  variables: {
    input
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.performClosingWorkflowAction ?? {};
});
/**
 * Save closing form fields with a flat field map. The graph routes each field to
 * the correct downstream contract endpoint (offer details / milestones / closing
 * cost details). Consumers own field sanitization before calling.
 */
const saveClosingFormFields = input => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_saveClosingFormFields')}(
          $input: SaveClosingFormFieldsInput!
        ) {
          saveClosingFormFields(input: $input) {
            success
            sections {
              offer_details {
                saved
                error
              }
              milestones {
                saved
                error
              }
              closing_cost_details {
                saved
                error
              }
            }
          }
        }
      `,
  variables: {
    input
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  const data = response.body.data.saveClosingFormFields;
  if (!data) {
    throw new Error('Failed to save closing form fields: no response data');
  }
  return data;
});
/**
 * Get the document generation status for an offer via the dedicated graph
 * `contract_generation_status` query (CLOSE-320). Returns the generation
 * lifecycle status plus any validation/generation errors.
 */
const getDocGenStatus = (offerId, controller) => fetch.forAuctionGraph({
  query: gql$5`
          query ${embeddableOperation('blueprint_getDocGenStatus')}(
            $offerId: ID!
          ) {
            contract_generation_status(offerId: $offerId) {
              offer_id
              listing_id
              contract_id
              status
              errors {
                code
                message
              }
            }
          }
        `,
  variables: {
    offerId
  }
}, controller ? {
  controller
} : undefined).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.contract_generation_status;
});
/**
 * Trigger contract document generation for an offer via the graph
 * `generateContract` mutation (CLOSE-320). Returns the generate result; poll
 * `getDocGenStatus` for the resulting generation lifecycle status.
 */
const generateContract = offerId => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_generateContract')}(
          $offerId: ID!
        ) {
          generateContract(offerId: $offerId) {
            contract_id
            contract_generation_allowed
            status
          }
        }
      `,
  variables: {
    offerId
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.generateContract;
});
// -------------------------------------------------------------------
// CIF save
// Input/response types come from `@adc/sdk` (graph `saveCIF`, CLOSE-99).
// -------------------------------------------------------------------
/** Strip null/undefined keys (arrow predicate avoids the unbound-method lint). */
const omitNil = obj => _.omitBy(obj, v => v === null || v === undefined);
const mapBuyer = buyer => {
  if (!buyer) return undefined;
  return omitNil({
    first_name: buyer.firstName,
    middle_name: buyer.middleName,
    last_name: buyer.lastName,
    email: buyer.email,
    phone: buyer.phone,
    date_of_birth: buyer.dateOfBirth,
    marital_status: buyer.maritalStatus,
    is_spouse_on_contract: buyer.isSpouseOnContract,
    spouse_name: buyer.spouseName,
    spouse_email: buyer.spouseEmail,
    spouse_marital_status: buyer.spouseMaritalStatus,
    is_international_buyer: buyer.isInternationalBuyer,
    address: buyer.address
  });
};
/**
 * Map a CIF `EntityProfile` (parent or nested) to a graph entity-profile
 * sub-input. Recursive via `interested_parties` so nested entity owners (e.g.
 * an entity owner that itself has signers) round-trip. Adds signer
 * `phone`/`ownerType`, `date_of_trust`, and nested `interested_parties`.
 */
const mapEntityProfileInput = entity => {
  if (!entity) return undefined;
  const interestedParties = (entity.interestedParties ?? []).map(mapEntityProfileInput).filter(e => Boolean(e));
  return omitNil({
    entity_name: entity.entityName,
    entity_type: entity.entityType,
    entity_sub_type: entity.entitySubType,
    entity_state: entity.entityState,
    entity_title: entity.entityTitle,
    percent_stake: entity.percentStake,
    more_entity_involved: entity.moreEntityInvolved,
    more_signer_involved: entity.moreSignerInvolved,
    signers: entity.signers?.map(s => ({
      name: s.name,
      title: s.title,
      email: s.email,
      // phone + ownerType. Graph wire is ready; downstream
      // contract-service Signer columns are a BE follow-up.
      phone: s.phone,
      owner_type: s.ownerType,
      percent_stake: s.percentStake,
      is_signer: s.isSigner
    })),
    // trust establishment date -> Stakeholder.entityDate.
    date_of_trust: entity.dateOfTrust,
    // nested entity owners with their own signers (1 level per Figma,
    // but recursion supports arbitrary depth).
    interested_parties: interestedParties.length ? interestedParties : undefined
  });
};
/** Build a partial `SaveCifInput` from partial CIF data (only present fields). */
const buildSaveCifInput = (offerId, data) => {
  const entity = data.entityProfile;
  // Co-buyers: up to MAX_COBUYERS. Emit a single `cobuyers` array to match the
  // graph SaveCifInput (CLOSE-349) — the singular `cobuyer` field was removed.
  const cobuyers = (data.coBuyers ?? []).slice(0, MAX_COBUYERS).map(mapBuyer).filter(b => Boolean(b));
  const input = omitNil({
    offer_id: offerId,
    buyer: mapBuyer(data.buyer),
    cobuyers: cobuyers.length ? cobuyers : undefined,
    vesting_type: data.vestingType,
    entity_profile: mapEntityProfileInput(entity),
    funding: data.funding ? omitNil({
      funding_type: data.funding.fundingType,
      lending_status: data.funding.lendingStatus,
      lender_name: data.funding.lenderName,
      // financing agent contact -> Lender.lenderParty.
      financing_agent_name: data.funding.financingAgentName,
      financing_agent_phone: data.funding.financingAgentPhone,
      financing_agent_email: data.funding.financingAgentEmail
    }) : undefined,
    purchase_type: data.purchaseType,
    buyer_occupancy_code: data.occupancyCode,
    agent: data.agent ? omitNil({
      real_estate_agent_type: data.agent.agentType,
      license_type: data.agent.licenseType,
      license_number: data.agent.licenseNumber,
      // agent contact -> Agent.agentParty; firm -> Agent.agentFirm.
      name: data.agent.name,
      email: data.agent.email,
      phone: data.agent.phone,
      firm_name: data.agent.firmName,
      firm_address: data.agent.firmAddress
    }) : undefined,
    closing_company: data.closingCompany ? omitNil({
      use_seller_closing_company: data.closingCompany.useSellerClosingCompany,
      company_name: data.closingCompany.companyName,
      contact_name: data.closingCompany.contactName,
      contact_email: data.closingCompany.contactEmail,
      contact_phone: data.closingCompany.contactPhone,
      // free-text closing-company address.
      address: data.closingCompany.address
    }) : undefined,
    title_insurance_flag: data.titleInsurance,
    first_time_buyer_flag: data.firstTimeBuyer
  });
  return input;
};
/** Save CIF via a single mutation (partial input). Blocked by CLOSE-99. */
const saveCIF = input => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_saveCIF')}(
          $input: SaveCifInput!
        ) {
          saveCIF(input: $input) {
            success
            results {
              offer {
                saved
                error
              }
              entity_profile {
                saved
                error
              }
            }
          }
        }
      `,
  variables: {
    input
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  const data = response.body.data?.saveCIF;
  if (!data) {
    throw new Error('Failed to save CIF: no response data');
  }
  return data;
});

const {
  gql: gql$3,
  enums: enums$1
} = graph;
const logger$8 = coreLogger.forModule('blueprint:closing:offerMediaUpload');
/**
 * Delivers a single (client-stitched) file to offer media through the unified
 * `ingest` mutation. The graph routes the OFFER_MEDIA UPLOAD operation to
 * `POST /v2/offers/{offer_id}/media/upload?mediaKind={media_kind}`.
 *
 * The file travels as a multipart `Upload`, so the actual mutation must run on the
 * consumer's Apollo upload client (see `useOfferMediaUpload`'s injected executor);
 * blueprint's `@adc/fetch` cannot upload files through graph.
 */
const offerMediaIngestMutation = () => gql$3`
  mutation ${embeddableOperation('blueprint_ingestOfferMedia')}(
    $input: IngestInput!
  ) {
    ingest(input: $input) {
      reo_contract_media_file {
        media_id
        file_name
        media_type
      }
      success
    }
  }
`;
/**
 * Builds the `ingest` input for a single-file OFFER_MEDIA upload.
 * `offer_id` lives in the metadata, so no `resource` URN is required.
 */
const buildOfferMediaIngestInput = (offerId, file, mediaKind) => ({
  // `type` is the ingest resolver's dispatch discriminant; without it the call
  // falls back to the ORGANIZATION path. Must be 'OFFER_MEDIA'.
  type: 'OFFER_MEDIA',
  operation: enums$1.IngestOperation.Upload,
  metadata: {
    offer_media: {
      offer_id: offerId,
      media_kind: mediaKind,
      file
    }
  }
});
/**
 * Blueprint hook that owns the offer-media delivery state machine
 * (idle → uploading → success | failed). The file upload itself is an injected
 * `executor` (Apollo upload client), keeping blueprint platform-agnostic and free
 * of any upload/apollo dependency.
 */
function useOfferMediaUpload(options) {
  const {
    executor
  } = options;
  const [status, setStatus] = useState(UploadStatus.Idle);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const upload = useCallback(async (offerId, file, mediaKind) => {
    setStatus(UploadStatus.Uploading);
    setError(null);
    setResult(null);
    try {
      const media = await executor(buildOfferMediaIngestInput(offerId, file, mediaKind));
      if (!media?.media_id) {
        throw new Error('No media returned from offer-media ingest.');
      }
      setStatus(UploadStatus.Success);
      setResult(media);
      return media;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Offer media upload failed.';
      logger$8.error('Offer media upload failed:', e);
      setStatus(UploadStatus.Failed);
      setError(msg);
      throw e;
    }
  }, [executor]);
  const reset = useCallback(() => {
    setStatus(UploadStatus.Idle);
    setError(null);
    setResult(null);
  }, []);
  return {
    upload,
    status,
    error,
    result,
    reset
  };
}

const {
  enums
} = graph;
// Proof of Funds is a specialization of the generic offer-media upload
// (see offerMediaUpload.ts) pinned to the PROOF_OF_FUNDS media kind.
/** @see offerMediaIngestMutation — identical mutation, kept for back-compat. */
const proofOfFundsIngestMutation = offerMediaIngestMutation;
/**
 * Builds the `ingest` input for a single-file Proof of Funds offer-media upload.
 * Thin wrapper over `buildOfferMediaIngestInput` pinned to PROOF_OF_FUNDS.
 */
const buildProofOfFundsIngestInput = (offerId, file) => buildOfferMediaIngestInput(offerId, file, enums.ReoContractMediaFileType.ProofOfFunds);
/**
 * Blueprint hook for Proof of Funds delivery. Thin wrapper over
 * `useOfferMediaUpload` that pins the media kind to PROOF_OF_FUNDS so callers
 * keep the original `upload(offerId, file)` signature.
 */
function useProofOfFundsUpload(options) {
  const {
    upload,
    status,
    error,
    result,
    reset
  } = useOfferMediaUpload(options);
  // Keep `upload` referentially stable (only depends on the underlying stable
  // upload) so consumers can safely use it in effect dependency arrays.
  const uploadProofOfFunds = useCallback((offerId, file) => upload(offerId, file, enums.ReoContractMediaFileType.ProofOfFunds), [upload]);
  return {
    upload: uploadProofOfFunds,
    status,
    error,
    result,
    reset
  };
}

/* istanbul ignore file */
const partialGraphErrorFactory = (message, errors) => {
  const e = new Error(message);
  e.errors = errors;
  return e;
};
const CLOSING_OVERVIEW_STATUSES = [graph.enums.ClosingStatus.PendingContract, graph.enums.ClosingStatus.PendingBuyerContract, graph.enums.ClosingStatus.PendingSellerContract, graph.enums.ClosingStatus.InClosing, graph.enums.ClosingStatus.PendingFunding];
// `take: 20` is safe against the RESILIENCE_HTTP_REQUEST_LIMIT (default 50)
// because associated_closings resolves through DataLoaders: each unique
// resolver path (listing, listing.auction, primary_property, etc.) batches
// into ONE backend HTTP call regardless of item count. Total calls scale with
// the number of distinct fields in the *List fragments (~15-16), NOT with
// page size. If you raise `take`, the budget still holds — but only as long
// as the list query keeps using the lightweight *List fragments (CLOSE-161).
// Swapping in a per-item (non-batched) field would break this assumption.
const defaultAssociatedClosingsFilters = {
  take: 20,
  page: 0
};
const defaultAssociatedClosingsViewingAs = [graph.enums.ClosingRelationship.WinningBidder, graph.enums.ClosingRelationship.Buyer, graph.enums.ClosingRelationship.Cobuyer, graph.enums.ClosingRelationship.BuyerAgent];
/**
 * Fetch paginated associated closings for the current user.
 */
const fetchAssociatedClosings = (filters = defaultAssociatedClosingsFilters, viewingAs = defaultAssociatedClosingsViewingAs, controller) => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_fetchAssociatedClosings')}(
          $associatedClosingFilters: FilterableCollectionFilters
          $viewingAs: [ClosingRelationship]
        ) {
          current_user {
            ${fragments$1.associatedClosingsList.reference}
          }
        }
        ${fragments$1.associatedClosingsList.definition}
        ${fragments$1.reoOfferList.definition}
        ${fragments$1.trusteeContractList.definition}
        ${fragments$1.closingListing.definition}
        ${fragments$1.workflowList.definition}`,
  variables: {
    associatedClosingFilters: filters,
    viewingAs
  }
}, controller ? {
  controller
} : undefined).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null || data.current_user === null) {
    throw partialGraphErrorFactory('Could not fetch associated closings', errors);
  }
  return data.current_user;
});
/**
 * Fetch a single closing by listing ID (resolves ReoOffer or TrusteeContract).
 */
const fetchClosingByListing = (listingId, controller,
// Optional SSR request forwarded to the fetch layer for server-side auth.
// (Mirrors the req-forwarding pattern used by user.providers.)
req, options) => {
  const withViewingAs = Boolean(options?.workflowViewingAs);
  const workflowFragment = fragments$1.workflow({
    withViewingAs
  });
  return fetch.forAuctionGraph({
    query: `query ${embeddableOperation('blueprint_fetchClosingByListing')}(
          $listingId: ID!${withViewingAs ? '\n          $workflowViewingAs: CompanyWorkflowRelationship' : ''}
        ) {
          listing(listingId: $listingId) {
            # Listing-level parties merged onto closing_method.listing below.
            # Role set is the union needed by consumers: buyer/seller dashboards
            # (agent/broker/escrow/wire lookups) + the resi-fe-btbproperty ops
            # closing-page party grid (seller-side roles). Fields cover
            # normalizeListingParty (party_id/org_title/license). See CLOSE-240.
            parties(
              partyRoles: [
                ASSET_MANAGER
                LISTING_AGENT
                BROKER
                AUCTION_CLOSING_COMPANY_CONTACT
                CONTRACT_CLOSING_COMPANY
                ESCROW_OFFICER
                WIRE_INSTRUCTION_COMPANY
                LISTING_AGENT_CONTACT
                LISTING_TRANSACTION_COORDINATOR_AGENT
                PRIVATE_SELLER
                SELLER_VESTING
                CO_SELLER
              ]
            ) {
              party_id
              party_role
              party_name
              party_organization_title
              party_email
              party_phone
              party_license_number
              party_address {
                street_description
                municipality
                country_primary_subdivision
                postal_code
              }
            }
            listing_wire_instruction {
              bank_name
              routing_number
              account_name
              account_number
              notes
            }
            seller_configuration {
              parent_seller_account_alias
            }
            closing_method {
              ... on ReoOffer {
                ${fragments$1.reoOffer.reference}
                ${workflowFragment.reference}
              }
              ... on TrusteeContract {
                ${fragments$1.trusteeContract.reference}
              }
            }
          }
        }
        ${fragments$1.reoOffer.definition}
        ${fragments$1.trusteeContract.definition}
        ${workflowFragment.definition}
        ${fragments$1.closingAssociation.definition}`,
    variables: {
      listingId,
      ...(withViewingAs ? {
        workflowViewingAs: options?.workflowViewingAs
      } : {})
    }
  }, {
    ...(controller ? {
      controller
    } : {}),
    ...(req ? {
      req
    } : {})
  }).then(res => res.body).then(({
    data,
    errors
  }) => {
    if (data === null || !data.listing) {
      throw partialGraphErrorFactory('Could not fetch closing for listing', errors);
    }
    const {
      closing_method,
      ...listingFields
    } = data.listing;
    const closingMethod = closing_method ?? null;
    const vestingName = closingMethod?.listing?.seller_configuration?.contract?.vesting_name ?? closingMethod?.listing?.parties?.find(party => party.party_role === graph.enums.ListingPartyRole.SellerVesting)?.party_name ?? null;
    const buyerCompanyName = getBuyerCompanyName(closingMethod);
    return {
      closing_method: closingMethod ? {
        ...closingMethod,
        listing: {
          ...closingMethod.listing,
          ...listingFields
        }
      } : null,
      __computed__: {
        vesting_name: vestingName,
        buyer_company_name: buyerCompanyName
      }
    };
  });
};
/**
 * Fetch a single closing (alias for fetchClosingByListing for named clarity).
 */
const fetchIndividualClosing = fetchClosingByListing;
/**
 * Fetch transaction coordinator closings with status counts.
 */
const fetchTCClosings = (partyId, status, controller) => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_fetchTCClosings')}(
          $ids: [ID!]
          $statuses: [ClosingStatus]
          $count_statuses: [ClosingStatus]
        ) {
          closing_overview_status(statuses: $count_statuses, partyIds: $ids) {
            ${fragments$1.closingOverviewStatus.reference}
          }
          transaction_coordinator_closings(partyIds: $ids, statuses: $statuses) {
            ${fragments$1.tcClosing.reference}
          }
        }
        ${fragments$1.closingOverviewStatus.definition}
        ${fragments$1.tcClosing.definition}
        ${fragments$1.closingAssociation.definition}`,
  variables: {
    ids: [parseInt(partyId, 10)],
    statuses: status ? [status] : [],
    count_statuses: CLOSING_OVERVIEW_STATUSES
  }
}, controller ? {
  controller,
  requestTimeout: 30000
} : {
  requestTimeout: 30000
}).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null) {
    throw partialGraphErrorFactory('Could not fetch TC closings', errors);
  }
  return {
    transaction_coordinator_closings: data.transaction_coordinator_closings ?? [],
    closing_overview_status: data.closing_overview_status ?? null
  };
});
/**
 * Fetch closing overview projection (grouped closings by status).
 */
const fetchClosingOverview = controller => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_fetchClosingOverview')} {
          closing_overview_projection {
            grouping_key
            grouped_closings {
              key
              group_label
              closings {
                __typename
                ... on ReoOffer {
                  ${fragments$1.reoOffer.reference}
                }
                ... on TrusteeContract {
                  ${fragments$1.trusteeContract.reference}
                }
              }
            }
          }
        }
        ${fragments$1.reoOffer.definition}
        ${fragments$1.trusteeContract.definition}
        ${fragments$1.closingAssociation.definition}`
}, controller ? {
  controller,
  requestTimeout: 30000
} : {
  requestTimeout: 30000
}).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null) {
    throw partialGraphErrorFactory('Could not fetch closing overview', errors);
  }
  return data.closing_overview_projection ?? null;
});
/**
 * Fetch global closing overview status counts (no party filter).
 *
 * Unlike `fetchTCClosings`, this queries `closing_overview_status` without
 * `partyIds`, returning the workspace-wide status counts the ops closings
 * dashboard renders. See CLOSE-240.
 */
const fetchClosingOverviewStatus = (statuses = CLOSING_OVERVIEW_STATUSES, controller) => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_fetchClosingOverviewStatus')}($statuses: [ClosingStatus]) {
          closing_overview_status(statuses: $statuses) {
            ${fragments$1.closingOverviewStatus.reference}
          }
        }
        ${fragments$1.closingOverviewStatus.definition}`,
  variables: {
    statuses
  }
}, controller ? {
  controller
} : undefined).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null) {
    throw partialGraphErrorFactory('Could not fetch closing overview status', errors);
  }
  return data.closing_overview_status ?? null;
});
/**
 * Fetch an offer's existing CIF data for resume + the fieldSpecificationsMap.
 * DOB is not read (net-new, CLOSE-99).
 */
const fetchOfferForCIF = (listingId, controller) => fetch.forAuctionGraph({
  query: `query ${embeddableOperation('blueprint_fetchOfferForCIF')}(
          $listingId: ID!
        ) {
          reo_offer(listingId: $listingId) {
            ${fragments$1.reoOfferCif.reference}
          }
        }
        ${fragments$1.reoOfferCif.definition}`,
  variables: {
    listingId
  }
}, controller ? {
  controller
} : undefined).then(res => res.body).then(({
  data,
  errors
}) => {
  if (data === null) {
    throw partialGraphErrorFactory('Could not fetch offer for CIF', errors);
  }
  const offer = data.reo_offer;
  if (!offer) {
    return {
      data: {},
      fieldSpecifications: {}
    };
  }
  const buyers = offer.buyers ?? [];
  const primaryBuyer = buyers.find(b => b?.primary_flag) ?? buyers[0];
  const party = primaryBuyer?.buyer_party;
  // Non-primary buyers hydrate into coBuyers[] (multi co-buyer, CLOSE-350).
  const coBuyerRaws = buyers.filter(b => Boolean(b) && b !== primaryBuyer);
  const mapBuyerRaw = b => {
    const p = b.buyer_party;
    // Optional fields intentionally resolve to `undefined` (not '' or null)
    // when absent: the flow's default transform strips `undefined` keys from
    // the saveCIF payload, so an unset optional is omitted rather than sent
    // as an empty value. Keep this pattern when adding/touching optional
    // fields here.
    return {
      firstName: p?.first_name ?? '',
      middleName: p?.middle_name ?? undefined,
      lastName: p?.last_name ?? '',
      email: p?.email_address ?? '',
      phone: '',
      // DOB is net-new (CLOSE-99) — not available for resume yet.
      dateOfBirth: '',
      address: {},
      maritalStatus: b.marital_status ?? undefined,
      isSpouseOnContract: b.is_spouse_on_contract ?? undefined,
      spouseName: b.spouse_name ?? undefined,
      spouseEmail: b.spouse_email_address ?? undefined,
      spouseMaritalStatus: b.spouse_marital_status ?? undefined,
      isInternationalBuyer: b.is_international_buyer ?? undefined
    };
  };
  // recursive entity mapper so nested `interested_parties`
  // (entity owners with their own signers) hydrate on resume.
  const mapEntityRaw = e => ({
    entityName: e.entity_name,
    entityType: e.entity_type,
    entitySubType: e.entity_sub_type,
    entityState: e.entity_state,
    entityTitle: e.entity_title ?? undefined,
    // trust establishment date (Stakeholder.entityDate).
    dateOfTrust: e.entity_date ?? undefined,
    percentStake: e.percent_stake,
    moreEntityInvolved: e.more_entity_involved,
    moreSignerInvolved: e.more_signer_involved,
    signers: (e.individuals ?? []).map(i => ({
      name: i.signer_name,
      title: i.signer_title,
      email: i.signer_email ?? undefined,
      // signer phone + ownerType round-trip.
      phone: i.signer_phone ?? undefined,
      ownerType: i.owner_type ?? undefined,
      percentStake: i.percent_stake,
      isSigner: i.signer
    })),
    ...(e.interested_parties?.length ? {
      interestedParties: e.interested_parties.map(mapEntityRaw)
    } : {})
  });
  const mapped = {
    vestingType: offer.vesting_type ?? undefined,
    purchaseType: offer.purchase_type ?? undefined,
    occupancyCode: offer.buyer_occupancy_code ?? undefined,
    firstTimeBuyer: offer.first_time_buyer_flag ?? undefined,
    ...(offer.funding_type || offer.lending_status ? {
      funding: {
        fundingType: offer.funding_type,
        lendingStatus: offer.lending_status ?? undefined,
        // financing agent contact from Lender.lenderParty.
        financingAgentName: [offer.lender?.lender_party?.first_name, offer.lender?.lender_party?.last_name].filter(Boolean).join(' ') || undefined,
        financingAgentPhone: offer.lender?.lender_party?.phone_number ?? undefined,
        financingAgentEmail: offer.lender?.lender_party?.email_address ?? undefined
      }
    } : {}),
    ...(offer.real_estate_agent_type ? {
      agent: {
        agentType: offer.real_estate_agent_type,
        // agent contact (agent_party) + firm (agent_firm).
        name: [offer.agent?.agent_party?.first_name, offer.agent?.agent_party?.last_name].filter(Boolean).join(' ') || undefined,
        email: offer.agent?.agent_party?.email_address ?? undefined,
        phone: offer.agent?.agent_party?.phone_number ?? undefined,
        firmName: offer.agent?.agent_firm?.company_name ?? undefined,
        firmAddress: offer.agent?.agent_firm?.street_name ?? undefined
      }
    } : {}),
    ...(offer.seller_escrow_company_flag != null || offer.escrow_company?.escrow_company_party?.street_name ? {
      closingCompany: {
        useSellerClosingCompany: offer.seller_escrow_company_flag ?? undefined,
        // free-text closing-company address (own company).
        address: offer.escrow_company?.escrow_company_party?.street_name ?? undefined
      }
    } : {}),
    ...(party ? {
      buyer: {
        firstName: party.first_name ?? '',
        middleName: party.middle_name ?? undefined,
        lastName: party.last_name ?? '',
        email: party.email_address ?? '',
        phone: '',
        // DOB is net-new (CLOSE-99) — not available for resume yet.
        dateOfBirth: '',
        address: {},
        maritalStatus: primaryBuyer?.marital_status ?? undefined,
        isSpouseOnContract: primaryBuyer?.is_spouse_on_contract ?? undefined,
        spouseName: primaryBuyer?.spouse_name ?? undefined,
        spouseEmail: primaryBuyer?.spouse_email_address ?? undefined,
        spouseMaritalStatus: primaryBuyer?.spouse_marital_status ?? undefined,
        isInternationalBuyer: primaryBuyer?.is_international_buyer ?? undefined
      }
    } : {}),
    ...(coBuyerRaws.length ? {
      coBuyers: coBuyerRaws.map(mapBuyerRaw)
    } : {}),
    ...(offer.entity_profile ? {
      entityProfile: mapEntityRaw(offer.entity_profile)
    } : {})
  };
  return {
    data: mapped,
    fieldSpecifications: offer.field_specifications ?? {}
  };
});

var providers$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    CLOSING_OVERVIEW_STATUSES: CLOSING_OVERVIEW_STATUSES,
    buildOfferMediaIngestInput: buildOfferMediaIngestInput,
    buildProofOfFundsIngestInput: buildProofOfFundsIngestInput,
    buildSaveCifInput: buildSaveCifInput,
    defaultAssociatedClosingsFilters: defaultAssociatedClosingsFilters,
    defaultAssociatedClosingsViewingAs: defaultAssociatedClosingsViewingAs,
    fetchAssociatedClosings: fetchAssociatedClosings,
    fetchClosingByListing: fetchClosingByListing,
    fetchClosingOverview: fetchClosingOverview,
    fetchClosingOverviewStatus: fetchClosingOverviewStatus,
    fetchIndividualClosing: fetchIndividualClosing,
    fetchOfferForCIF: fetchOfferForCIF,
    fetchTCClosings: fetchTCClosings,
    generateContract: generateContract,
    getDocGenStatus: getDocGenStatus,
    offerMediaIngestMutation: offerMediaIngestMutation,
    performClosingWorkflowAction: performClosingWorkflowAction,
    proofOfFundsIngestMutation: proofOfFundsIngestMutation,
    saveCIF: saveCIF,
    saveClosingFormFields: saveClosingFormFields
});

/* istanbul ignore file */
/**
 * Purchase-docs upload processing status (CLOSE-470).
 *
 * After a purchase document is uploaded — via Tenbox links or the graph
 * OFFER_MEDIA `ingest` (POF) path — ms-contract runs an async job to
 * convert + combine the files and create the downstream Salesforce task. This
 * module polls that job until it reaches a terminal state.
 *
 * Design notes:
 *  - This is TRANSIENT job state, not durable closing data, so it is exposed as
 *    a standalone hook rather than a field on the `useIndividualClosing` /
 *    workflow projection (which would pollute the steady-state model and force
 *    churn on a durable read).
 *  - It is TRANSPORT-AGNOSTIC: both the Tenbox and graph-ingest upload paths
 *    converge on this same poll, so it is a sibling hook — deliberately NOT
 *    folded into `useOfferMediaUpload`.
 *  - Shape mirrors `useDocGenStatus`, but adds the internal poll loop the legacy
 *    dashboard `providers/contract.js#fetchUploadStatus` drove by hand.
 */
const logger$7 = coreLogger.forModule('blueprint:closing:uploadStatus');
const DEFAULT_POLL_INTERVAL_MS = 5000;
// Terminal ms-contract statuses come from the generated `DocumentUploadStatusType`
// enum (CLOSE-471). Anything non-terminal (PENDING/PROCESSED, or a null/empty
// body the graph maps to null) => keep polling.
/**
 * Client-facing lifecycle state for the upload-processing poll. Named to match
 * the `usePurchaseDocsUploadStatus` hook. NOTE: shares its identifier with the
 * generated `PurchaseDocsUploadStatus` graph response type in `@adc/sdk/types`
 * (a different thing — the `{ offer_id, document_type, status }` object). This
 * module doesn't import that type, so there's no local clash; a consumer that
 * needs BOTH should alias the SDK import
 * (e.g. `import type { PurchaseDocsUploadStatus as PurchaseDocsUploadStatusResponse }`).
 */
var PurchaseDocsUploadStatus;
(function (PurchaseDocsUploadStatus) {
  PurchaseDocsUploadStatus["Idle"] = "IDLE";
  PurchaseDocsUploadStatus["Polling"] = "POLLING";
  PurchaseDocsUploadStatus["Complete"] = "COMPLETE";
  PurchaseDocsUploadStatus["Failed"] = "FAILED";
})(PurchaseDocsUploadStatus || (PurchaseDocsUploadStatus = {}));
// -------------------------------------------------------------------
// Provider
// -------------------------------------------------------------------
/**
 * Fetch the processing status for a purchase document via the
 * `purchase_docs_upload_status` graph query (CLOSE-471). Mirrors
 * `getDocGenStatus`; returns the generated `DocumentUploadStatusType` enum value
 * (or null when ms-contract reports an INVALID/empty status) so the hook's
 * terminal-status comparison is type-checked against the SDK enum.
 */
const getPurchaseDocsUploadStatus = (offerId, documentType, controller) => fetch.forAuctionGraph({
  query: gql$5`
          query ${embeddableOperation('blueprint_purchaseDocsUploadStatus')}(
            $offerId: ID!
            $documentType: PurchaseDocumentType!
          ) {
            offer_status(offerId: $offerId) {
              purchase_docs_upload_status(documentType: $documentType) {
                status
              }
            }
          }
        `,
  variables: {
    offerId: String(offerId),
    documentType
  }
}, controller ? {
  controller
} : undefined).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return _.get(response, 'body.data.offer_status.purchase_docs_upload_status.status', null) ?? null;
});
/**
 * Polls ms-contract for purchase-doc processing status until terminal
 * (COMPLETE / FAILED). Transient errors are logged and retried, mirroring the
 * legacy behavior. Aborts in-flight requests and clears timers on stop/unmount.
 *
 * Usage:
 *   const upload = usePurchaseDocsUploadStatus(offerId, docType, {
 *     onComplete: refreshTasks,
 *     onFailed: showError,
 *   })
 *   await pofUpload.upload(String(offerId), file)
 *   upload.start()
 */
function usePurchaseDocsUploadStatus(offerId, documentType, options = {}) {
  const {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    onComplete,
    onFailed
  } = options;
  const [status, setStatus] = useState(PurchaseDocsUploadStatus.Idle);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const timerRef = useRef(null);
  // Guards against setState after stop/unmount and re-entrant polls.
  const activeRef = useRef(false);
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const stop = useCallback(() => {
    activeRef.current = false;
    clearTimer();
    controllerRef.current?.abort();
    controllerRef.current = null;
    setStatus(PurchaseDocsUploadStatus.Idle);
  }, [clearTimer]);
  const poll = useCallback(async () => {
    if (!activeRef.current || !offerId || !documentType) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const raw = await getPurchaseDocsUploadStatus(offerId, documentType, controller);
      if (controller.signal.aborted || !activeRef.current) return;
      setError(null);
      switch (raw) {
        case enums$5.DocumentUploadStatusType.Complete:
          activeRef.current = false;
          setStatus(PurchaseDocsUploadStatus.Complete);
          onComplete?.();
          break;
        case enums$5.DocumentUploadStatusType.Failed:
          activeRef.current = false;
          setStatus(PurchaseDocsUploadStatus.Failed);
          onFailed?.();
          break;
        default:
          // PENDING / PROCESSED / null (empty body) => keep polling.
          timerRef.current = setTimeout(() => {
            poll().catch(err => logger$7.error('upload-status poll', err));
          }, intervalMs);
          break;
      }
    } catch (err) {
      if (controller.signal.aborted || !activeRef.current) return;
      logger$7.error(`upload-status poll failed offerId=${offerId} documentType=${documentType}`, err);
      setError(err);
      // Mirror legacy behavior: retry on transient error.
      timerRef.current = setTimeout(() => {
        poll().catch(e => logger$7.error('upload-status poll', e));
      }, intervalMs);
    }
  }, [offerId, documentType, intervalMs, onComplete, onFailed]);
  const start = useCallback(() => {
    if (!offerId || !documentType) return;
    activeRef.current = true;
    setError(null);
    setStatus(PurchaseDocsUploadStatus.Polling);
    clearTimer();
    poll().catch(err => logger$7.error('upload-status poll', err));
  }, [offerId, documentType, poll, clearTimer]);
  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      activeRef.current = false;
      clearTimer();
      controllerRef.current?.abort();
    };
  }, [clearTimer]);
  return {
    status,
    error,
    start,
    stop
  };
}

/* istanbul ignore file */
const logger$6 = coreLogger.forModule('blueprint:closing');
/**
 * Fetch paginated associated closings for the current user.
 * Errors bubble to the nearest error boundary.
 */
function useAssociatedClosings({
  enabled = true,
  filters = defaultAssociatedClosingsFilters,
  viewingAs = defaultAssociatedClosingsViewingAs,
  initialData = null
} = {}) {
  const [data, setData] = useState(initialData);
  const controllerRef = useRef(null);
  // The fetch itself — always runs when invoked via `refetch`, independent of
  // `enabled` (which only gates the auto-fetch effect below).
  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const result = await fetchAssociatedClosings(filters, viewingAs, controller);
    if (controller.signal.aborted) return;
    setData({
      closings: result?.associated_closings ?? null
    });
  }, [filters, viewingAs]);
  useEffect(() => {
    if (!enabled) return;
    load().catch(err => logger$6.error('useAssociatedClosings', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load, enabled]);
  return {
    data,
    refetch: load
  };
}
// -------------------------------------------------------------------
// useIndividualClosing
// -------------------------------------------------------------------
/**
 * Fetch a single closing by listing ID.
 * Errors bubble to the nearest error boundary.
 *
 * `enabled` (default true) gates only the auto-fetch-on-mount effect —
 * `refetch` always fetches. `initialData` seeds the first render from an SSR
 * payload so no client fetch is required on mount (parity with the bidding
 * hooks).
 */
function useIndividualClosing(listingId, options) {
  const {
    workflowViewingAs,
    enabled = true,
    initialData = null
  } = options ?? {};
  const [data, setData] = useState(initialData);
  const controllerRef = useRef(null);
  // The fetch itself — always runs when invoked via `refetch`, independent of
  // `enabled` (which only gates the auto-fetch effect below).
  const load = useCallback(async () => {
    if (!listingId) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const result = await fetchClosingByListing(listingId, controller, undefined, workflowViewingAs ? {
      workflowViewingAs
    } : undefined);
    if (controller.signal.aborted) return;
    setData(result);
  }, [listingId, workflowViewingAs]);
  useEffect(() => {
    if (!enabled) return;
    load().catch(err => logger$6.error('useIndividualClosing', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load, enabled]);
  return {
    data,
    refetch: load
  };
}
/**
 * Alias for useIndividualClosing (semantic clarity for direct listing-level access).
 */
const useClosingByListing = useIndividualClosing;
// -------------------------------------------------------------------
// useDocGenStatus
// -------------------------------------------------------------------
/**
 * Fetch the contract document generation status for an offer. Auto-fetches on
 * mount; call `refetch` after triggering generation or for a manual refresh.
 * Errors bubble to the nearest error boundary.
 */
function useDocGenStatus(offerId) {
  const [data, setData] = useState(null);
  const controllerRef = useRef(null);
  const load = useCallback(async () => {
    if (!offerId) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const result = await getDocGenStatus(offerId, controller);
    if (controller.signal.aborted) return;
    setData(result);
  }, [offerId]);
  useEffect(() => {
    load().catch(err => logger$6.error('useDocGenStatus', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load]);
  return {
    data,
    refetch: load
  };
}
/**
 * Fetch transaction coordinator closings for a party.
 * Errors bubble to the nearest error boundary.
 */
function useTransactionCoordinatorClosings(partyId, status) {
  const [data, setData] = useState(null);
  const controllerRef = useRef(null);
  const load = useCallback(async () => {
    if (!partyId) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const result = await fetchTCClosings(partyId, status, controller);
    if (controller.signal.aborted) return;
    setData({
      closings: result.transaction_coordinator_closings,
      overviewStatus: result.closing_overview_status
    });
  }, [partyId, status]);
  useEffect(() => {
    load().catch(err => logger$6.error('useTransactionCoordinatorClosings', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load]);
  return {
    data,
    refetch: load
  };
}
// -------------------------------------------------------------------
// useClosingOverview
// -------------------------------------------------------------------
/**
 * Fetch closing overview projection (grouped view).
 * Errors bubble to the nearest error boundary.
 */
function useClosingOverview() {
  const [data, setData] = useState(null);
  const controllerRef = useRef(null);
  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const result = await fetchClosingOverview(controller);
    if (controller.signal.aborted) return;
    setData(result);
  }, []);
  useEffect(() => {
    load().catch(err => logger$6.error('useClosingOverview', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load]);
  return {
    data,
    refetch: load
  };
}
// -------------------------------------------------------------------
// useClosingWorkflow
// -------------------------------------------------------------------
/**
 * Fetch the closing workflow for a specific listing's closing method.
 * Extracts the workflow sub-field from the full closing fetch.
 */
function useClosingWorkflow(listingId) {
  const closing = useIndividualClosing(listingId);
  const data = useMemo(() => {
    if (!closing.data?.closing_method) return null;
    const method = closing.data.closing_method;
    if (method.__typename === 'ReoOffer') {
      return {
        workflow: method.workflow ?? null
      };
    }
    return {
      workflow: null
    };
  }, [closing.data]);
  return {
    data,
    refetch: closing.refetch
  };
}
/**
 * Provides closing mutation actions.
 * Errors bubble — consumer handles via error boundary or catch.
 */
function useClosingActions() {
  return useMemo(() => ({
    extendOffer: (offerId, milestones) => performClosingWorkflowAction({
      offer_id: offerId,
      action: graph.enums.ClosingWorkflowActionType.Extend,
      payload: milestones
    }),
    cancelOffer: (offerId, milestones) => performClosingWorkflowAction({
      offer_id: offerId,
      action: graph.enums.ClosingWorkflowActionType.Cancel,
      payload: milestones
    }),
    falloutOffer: (offerId, milestones) => performClosingWorkflowAction({
      offer_id: offerId,
      action: graph.enums.ClosingWorkflowActionType.Fallout,
      payload: milestones
    }),
    closeOffer: (offerId, closingCosts) => performClosingWorkflowAction({
      offer_id: offerId,
      action: graph.enums.ClosingWorkflowActionType.Close,
      payload: closingCosts
    }),
    generateContract: offerId => generateContract(offerId)
  }), []);
}
/** Load an offer's CIF data (resume) + fieldSpecificationsMap. Errors on `error`, not thrown. */
function useClosingState(listingId) {
  const [data, setData] = useState(null);
  const [fieldSpecifications, setFieldSpecifications] = useState({});
  const [loading, setLoading] = useState(Boolean(listingId));
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const load = useCallback(async () => {
    if (!listingId) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchOfferForCIF(listingId, controller);
      if (controller.signal.aborted) return;
      setData(result.data);
      setFieldSpecifications(result.fieldSpecifications);
    } catch (err) {
      if (controller.signal.aborted) return;
      logger$6.error('useClosingState', err);
      setError(err);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [listingId]);
  useEffect(() => {
    load().catch(err => logger$6.error('useClosingState', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load]);
  return {
    data,
    fieldSpecifications,
    loading,
    error,
    refetch: load
  };
}
/** CIF save: builds a partial `SaveCifInput` and calls `saveCIF`. Errors bubble. */
function useCIFActions() {
  return useMemo(() => ({
    save: (offerId, fields) => saveCIF(buildSaveCifInput(offerId, fields))
  }), []);
}
/** fieldSpecificationsMap + memoized lock/required/hidden lookups. Pass the map from `useClosingState`. */
function useFieldSpecifications(fieldSpecifications = {}) {
  return useMemo(() => ({
    map: fieldSpecifications,
    isLocked: fieldPath => Boolean(fieldSpecifications[fieldPath]?.locked),
    isRequired: fieldPath => Boolean(fieldSpecifications[fieldPath]?.required),
    isHidden: fieldPath => Boolean(fieldSpecifications[fieldPath]?.hidden)
  }), [fieldSpecifications]);
}

var hooks$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get PurchaseDocsUploadStatus () { return PurchaseDocsUploadStatus; },
    UploadStatus: UploadStatus,
    useAssociatedClosings: useAssociatedClosings,
    useCIFActions: useCIFActions,
    useClosingActions: useClosingActions,
    useClosingByListing: useClosingByListing,
    useClosingOverview: useClosingOverview,
    useClosingState: useClosingState,
    useClosingWorkflow: useClosingWorkflow,
    useDocGenStatus: useDocGenStatus,
    useFieldSpecifications: useFieldSpecifications,
    useIndividualClosing: useIndividualClosing,
    useOfferMediaUpload: useOfferMediaUpload,
    useProofOfFundsUpload: useProofOfFundsUpload,
    usePurchaseDocsUploadStatus: usePurchaseDocsUploadStatus,
    useTransactionCoordinatorClosings: useTransactionCoordinatorClosings
});

var types$1 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

var index$6 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    constants: constants,
    fragments: fragments$1,
    hooks: hooks$4,
    providers: providers$3,
    types: types$1,
    utils: utils
});

const getWorkflowNotes = async params => {
  const {
    urns,
    match
  } = params;
  return fetch.forAuctionGraph({
    query: gql$5`
        query ${embeddableOperation('blueprint_getWorkflowNotes')}($urns: [URN!], $match: MatchType) {
          workflow_notes(urns: $urns, match: $match) {
            note_id
            subject
            content
            type
            visibility
            created_date
            updated_date
            author_party_id
            author_name
            author {
              __typename
              ... on Party {
                party_id
              }
              ... on User {
                first_name
                last_name
              }
              ... on SalesforceUser {
                first_name
                last_name
              }
            }
            note_task {
              note_id
              assignee_name
              assignee {
                __typename
                ... on Party {
                  party_id
                }
                ... on User {
                  first_name
                  last_name
                }
                ... on SalesforceUser {
                  first_name
                  last_name
                }
              }
              due_date
              status
              assignee_urn
              type
              note_task_id
            }
          }
        }
      `,
    variables: {
      urns,
      ...(match && {
        match
      })
    }
  }).then(response => {
    const errors = _.get(response, 'body.errors[0]');
    if (errors) throw errors;
    return response.body.data.workflow_notes;
  });
};
const createWorkflowNote = async noteInput => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_createWorkflowNote')}($input: CreateWorkflowNoteInput!) {
          createWorkflowNote(input: $input) {
            note_id
            author_party_id
            author_name
            author {
              __typename
              ... on Party {
                party_id
              }
              ... on User {
                first_name
                last_name
              }
              ... on SalesforceUser {
                first_name
                last_name
              }
            }
            subject
            content
            type
            visibility
            created_date
            updated_date
          }
        }
      `,
  variables: {
    input: noteInput
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.createWorkflowNote;
});
const createWorkflowNoteWithTask = async noteInput => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_createWorkflowNoteTask')}($input: CreateWorkflowNoteTaskInput!) {
          createWorkflowNoteTask(input: $input) {
            note_id
            author_party_id
            author_name
            author {
              __typename
              ... on Party {
                party_id
              }
              ... on User {
                first_name
                last_name
              }
              ... on SalesforceUser {
                first_name
                last_name
              }
            }
            subject
            content
            type
            created_date
            updated_date
            note_task {
              note_id
              assignee_name
              assignee {
                __typename
                ... on Party {
                  party_id
                }
                ... on User {
                  first_name
                  last_name
                }
                ... on SalesforceUser {
                  first_name
                  last_name
                }
              }
              due_date
              status
              assignee_urn
              type
              note_task_id
            }
          }
        }
      `,
  variables: {
    input: noteInput
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.createWorkflowNoteTask;
});
const getWorkflowNoteCounts = async params => {
  const {
    urn_groups,
    match
  } = params;
  return fetch.forAuctionGraph({
    query: gql$5`
        query ${embeddableOperation('blueprint_getWorkflowNoteCounts')}($input: WorkflowNoteCountsInput!) {
          workflow_note_counts(input: $input) {
            urns
            match
            count
          }
        }
      `,
    variables: {
      input: {
        urn_groups,
        ...(match && {
          match
        })
      }
    }
  }).then(response => {
    const errors = _.get(response, 'body.errors[0]');
    if (errors) throw errors;
    return response.body.data.workflow_note_counts;
  });
};
const getRecentNotesByUrns = async params => {
  const {
    lookback_days,
    match,
    urn_groups
  } = params;
  return fetch.forAuctionGraph({
    query: gql$5`
        query ${embeddableOperation('blueprint_getRecentNotesByUrns')}($input: RecentNotesByUrnsInput!) {
          recent_notes_by_urns(input: $input) {
            urns
            lookback_days
            match
            has_recent_notes
          }
        }
      `,
    variables: {
      input: {
        lookback_days,
        ...(match && {
          match
        }),
        urn_groups
      }
    }
  }).then(response => {
    const errors = _.get(response, 'body.errors[0]');
    if (errors) throw errors;
    return response.body.data.recent_notes_by_urns;
  });
};

var providers$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createWorkflowNote: createWorkflowNote,
    createWorkflowNoteWithTask: createWorkflowNoteWithTask,
    getRecentNotesByUrns: getRecentNotesByUrns,
    getWorkflowNoteCounts: getWorkflowNoteCounts,
    getWorkflowNotes: getWorkflowNotes
});

const useWorkflowNotes = ({
  urns,
  match
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  // Serialize array to stabilize useCallback deps
  const urnsKey = JSON.stringify(urns);
  const fetchNotes = useCallback(async () => {
    const parsedUrns = JSON.parse(urnsKey);
    if (!parsedUrns?.length) return;
    setLoading(true);
    setError(null);
    try {
      const notes = await getWorkflowNotes({
        urns: parsedUrns,
        match
      });
      setData(notes);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [urnsKey, match]);
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);
  const executeMutation = useCallback(async (mutationFn, input) => {
    setCreating(true);
    try {
      const note = await mutationFn(input);
      setData(prev => [note, ...prev]);
      return note;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setCreating(false);
    }
  }, []);
  const createNote = useCallback(input => executeMutation(createWorkflowNote, input), [executeMutation]);
  const createNoteWithTask = useCallback(input => executeMutation(createWorkflowNoteWithTask, input), [executeMutation]);
  return {
    data,
    loading,
    error,
    refetch: fetchNotes,
    creating,
    createNote,
    createNoteWithTask
  };
};
const useWorkflowNoteCount = ({
  urn_groups,
  match
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const urnGroupsKey = JSON.stringify(urn_groups);
  const fetchCounts = useCallback(async () => {
    const parsedUrnGroups = JSON.parse(urnGroupsKey);
    if (!parsedUrnGroups?.length) return;
    setLoading(true);
    setError(null);
    try {
      const results = await getWorkflowNoteCounts({
        urn_groups: parsedUrnGroups,
        match
      });
      setData(results);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [urnGroupsKey, match]);
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);
  return {
    data,
    loading,
    error,
    refetch: fetchCounts
  };
};
const useRecentNotesByUrns = ({
  lookback_days,
  match,
  urn_groups
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Serialize array to stabilize useCallback deps
  const urnGroupsKey = JSON.stringify(urn_groups);
  const fetchRecentNotes = useCallback(async () => {
    const parsedUrnGroups = JSON.parse(urnGroupsKey);
    if (!parsedUrnGroups?.length) return;
    setLoading(true);
    setError(null);
    try {
      const results = await getRecentNotesByUrns({
        lookback_days,
        match,
        urn_groups: parsedUrnGroups
      });
      setData(results);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [lookback_days, match, urnGroupsKey]);
  useEffect(() => {
    fetchRecentNotes();
  }, [fetchRecentNotes]);
  return {
    data,
    loading,
    error,
    refetch: fetchRecentNotes
  };
};

var hooks$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useRecentNotesByUrns: useRecentNotesByUrns,
    useWorkflowNoteCount: useWorkflowNoteCount,
    useWorkflowNotes: useWorkflowNotes
});

var index$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hooks: hooks$3,
    providers: providers$2
});

// ── Queries ──────────────────────────────────────────────────────────
/**
 * Shared work_queue selection set. `current_user` and `user(userId:)` both
 * return `User`, so the same selection is embedded into both static queries
 * below via a fragment — no per-call string concatenation.
 */
const WORK_QUEUE_FRAGMENT = `
  fragment WorkQueueFields on User {
    work_queue(
      includeAllWorkflowTasks: $includeAll
      fromDate: $fromDate
      toDate: $toDate
      dateField: $dateField
      overdueOnly: $overdueOnly
      workflowType: $workflowType
    ) {
      groups {
          resource_urn
          workflow_type
          total_tasks
          completed_tasks
          is_overdue
          transaction {
            transaction_urn
            listing {
              listing_id
              formatted_address(format: DOUBLE_LINE)
            }
            associated_urns {
              associated_urn
            }
            parties {
              party_role
              party_name
              party_email
            }
          }
          assignments {
            task_assignment_id
            target_task_urn
            task_label
            task_code
            task_status
            task {
              expected_completion_date
              activated_date
              completed_date
            }
            assignee_urn
            assignee {
              __typename
              ... on Party {
                display_name
              }
              ... on User {
                display_name
              }
              ... on SalesforceUser {
                display_name
              }
            }
            assigned_by_urn
            assigned_by {
              __typename
              ... on User {
                display_name
              }
              ... on SalesforceUser {
                display_name
              }
            }
            group_urn
            assigned_date
          }
        }
    }
  }
`;
/** Shared work_queue argument variable declarations. */
const WORK_QUEUE_ARGS = '$includeAll: Boolean, $fromDate: DateTime, $toDate: DateTime, $dateField: WorkQueueDateField, $overdueOnly: Boolean, $workflowType: ShallowTaskWorkflowType';
// Queries are built lazily (memoized) rather than at module load: the
// operation name comes from configment, which isn't initialized until after
// the app boots. Building at import time would call configment.get() too early.
let currentUserQuery = null;
let userQuery = null;
/** Static query for the logged-in user's queue (current_user root). */
const getCurrentUserWorkQueueQuery = () => {
  if (!currentUserQuery) {
    currentUserQuery = gql$5`
      query ${embeddableOperation('blueprint_getWorkQueue')}(${WORK_QUEUE_ARGS}) {
        current_user {
          ...WorkQueueFields
        }
      }
      ${WORK_QUEUE_FRAGMENT}
    `;
  }
  return currentUserQuery;
};
/** Static query for another user's queue (user(userId:) root). */
const getUserWorkQueueQuery = () => {
  if (!userQuery) {
    userQuery = gql$5`
      query ${embeddableOperation('blueprint_getUserWorkQueue')}($userId: ID!, ${WORK_QUEUE_ARGS}) {
        user(userId: $userId) {
          ...WorkQueueFields
        }
      }
      ${WORK_QUEUE_FRAGMENT}
    `;
  }
  return userQuery;
};
/**
 * Fetches a work queue — workflows with progress, assignments, assignee names,
 * and listing address when available.
 *
 * When `userId` is provided, fetches that user's queue via `user(userId:)`.
 * Otherwise fetches the logged-in user's queue via `current_user`. Both root
 * fields expose an identical `work_queue` field with the same arguments, so a
 * shared fragment is embedded in both static queries.
 */
const getWorkQueue = async params => {
  const includeAll = params?.includeAllWorkflowTasks ?? false;
  const userId = params?.userId;
  return fetch.forAuctionGraph({
    query: userId ? getUserWorkQueueQuery() : getCurrentUserWorkQueueQuery(),
    variables: {
      includeAll,
      fromDate: params?.fromDate || null,
      toDate: params?.toDate || null,
      dateField: params?.dateField || null,
      overdueOnly: params?.overdueOnly ?? null,
      workflowType: params?.workflowType || null,
      ...(userId ? {
        userId
      } : {})
    }
  }).then(response => {
    const errors = _.get(response, 'body.errors[0]');
    if (errors) throw errors;
    const path = userId ? 'body.data.user.work_queue.groups' : 'body.data.current_user.work_queue.groups';
    return _.get(response, path) || [];
  });
};
/**
 * Fetches identity fields for a user by id. Used when viewing another user's
 * work queue: the display name builds the page title, and party_id scopes the
 * "{User}'s Only" toggle (assignments filter by assignee party urn).
 */
const getWorkQueueUser = async userId => {
  return fetch.forAuctionGraph({
    query: gql$5`
        query ${embeddableOperation('blueprint_getWorkQueueUser')}($userId: ID!) {
          user(userId: $userId) {
            user_id
            party_id
            first_name
            last_name
            email
          }
        }
      `,
    variables: {
      userId
    }
  }).then(response => {
    const errors = _.get(response, 'body.errors[0]');
    if (errors) throw errors;
    const user = _.get(response, 'body.data.user');
    return user || null;
  });
};
/**
 * Lightweight query for homepage card — counts + top workflows for summary display.
 * Fetches just enough to show progress, workflow type, address, and sort by recency.
 */
const getWorkQueueSummary = async () => {
  return fetch.forAuctionGraph({
    query: gql$5`
        query ${embeddableOperation('blueprint_getWorkQueueSummary')} {
          current_user {
            work_queue {
              groups {
                  resource_urn
                  workflow_type
                  total_tasks
                  completed_tasks
                  is_overdue
                  transaction {
                    transaction_urn
                    listing {
                      listing_id
                      formatted_address(format: DOUBLE_LINE)
                    }
                    associated_urns {
                      associated_urn
                    }
                    parties {
                      party_role
                      party_name
                      party_email
                    }
                  }
                  assignments {
                    task_assignment_id
                    task_code
                    task_status
                    task {
                      expected_completion_date
                      activated_date
                      completed_date
                    }
                    assigned_date
                  }
              }
            }
          }
        }
      `
  }).then(response => {
    const errors = _.get(response, 'body.errors[0]');
    if (errors) throw errors;
    return _.get(response, 'body.data.current_user.work_queue.groups') || [];
  });
};
/**
 * Batch assign tasks.
 */
const assignTasks = async input => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_assignTasks')}($input: [AssignTaskInput!]!) {
          assignTasks(input: $input) {
            task_assignment_id
            target_task_urn
            assignee_urn
            assigned_by_urn
            group_urn
            assigned_date
          }
        }
      `,
  variables: {
    input
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.assignTasks;
});
/**
 * Reassign existing assignments to a new assignee.
 */
const reassignTasks = async input => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_reassignTasks')}($input: ReassignTasksInput!) {
          reassignTasks(input: $input) {
            task_assignment_id
            target_task_urn
            assignee_urn
            assigned_by_urn
            group_urn
            assigned_date
          }
        }
      `,
  variables: {
    input
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.reassignTasks;
});
/**
 * Create an impromptu task on a workflow.
 *
 * Takes a stable `resource_urn` (available on each work_queue group). The
 * backend resolves the current workflow instance version at write time, so
 * there is no stale-version risk from a client holding a version id.
 */
const createImpromptuTask = async input => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_createImpromptuTask')}($input: CreateImpromptuTaskInput!) {
          createImpromptuTask(input: $input) {
            task_assignment_id
            target_task_urn
            assignee_urn
            assigned_by_urn
            assigned_date
          }
        }
      `,
  variables: {
    input
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.createImpromptuTask;
});
/**
 * Complete an impromptu task by its URN.
 */
const completeImpromptuTask = async taskUrn => fetch.forAuctionGraph({
  query: gql$5`
        mutation ${embeddableOperation('blueprint_completeImpromptuTask')}($taskUrn: URN!) {
          completeImpromptuTask(taskUrn: $taskUrn) {
            task_assignment_id
            target_task_urn
            assignee_urn
            assigned_by_urn
            assigned_date
          }
        }
      `,
  variables: {
    taskUrn
  }
}).then(response => {
  const errors = _.get(response, 'body.errors[0]');
  if (errors) throw errors;
  return response.body.data.completeImpromptuTask;
});

var providers$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    assignTasks: assignTasks,
    completeImpromptuTask: completeImpromptuTask,
    createImpromptuTask: createImpromptuTask,
    getWorkQueue: getWorkQueue,
    getWorkQueueSummary: getWorkQueueSummary,
    getWorkQueueUser: getWorkQueueUser,
    reassignTasks: reassignTasks
});

/** Funnel order — earliest stage first. */
const DOMAIN_ORDER = ['INTAKE', 'MARKETPLACE_ACTIVATION', 'SELLING', 'CLOSING'];
/** Human-readable labels per domain. */
const DOMAIN_LABELS = {
  INTAKE: 'Intake',
  MARKETPLACE_ACTIVATION: 'Marketplace Activation',
  SELLING: 'Selling',
  CLOSING: 'Closing'
};
/**
 * Narrows an arbitrary workflow_type string to a known WorkflowDomain,
 * or null if it isn't one we render (e.g. CLIENT_ONBOARDING / UNKNOWN).
 */
function toWorkflowDomain(workflowType) {
  if (!workflowType) return null;
  return DOMAIN_ORDER.includes(workflowType) ? workflowType : null;
}
// ── Assignment status ────────────────────────────────────────────────
/**
 * True when a task assignment is considered "done".
 *
 * NOTE: the backend reports task_status as COMPLETE for finished tasks, but
 * work_queue.completed_tasks currently miscounts (see MOR-90 / MOR-138). We
 * derive counts from assignment statuses so the UI is accurate until that lands.
 */
function isAssignmentComplete(assignment) {
  return assignment.task_status === 'COMPLETE' || assignment.task_status === 'DONE';
}
/** True when a task assignment is actively in progress. */
function isAssignmentOngoing(assignment) {
  return assignment.task_status === 'ONGOING';
}
/**
 * Derives completed/total task counts for a workflow from its assignments.
 * Counts are assignment-based (not backend completed_tasks/total_tasks), so the
 * progress fraction matches the mine/others accounting elsewhere.
 */
function deriveProgress(group) {
  const assignments = group.assignments ?? [];
  return {
    completed: assignments.filter(isAssignmentComplete).length,
    total: assignments.length
  };
}
/** How many calendar days out still counts as "due soon". */
const DUE_SOON_DAYS = 3;
/** Returns local midnight for a date (strips time-of-day). */
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
/**
 * Whole-day delta between the task's due date and today.
 * Negative = overdue, 0 = due today, positive = days remaining. Null = no date.
 */
function daysUntilDue(assignment) {
  const raw = assignment.task?.expected_completion_date;
  if (!raw) return null;
  const due = new Date(raw);
  if (Number.isNaN(due.getTime())) return null;
  const today = startOfDay(new Date());
  const dueDay = startOfDay(due);
  return Math.round((dueDay.getTime() - today.getTime()) / 86_400_000);
}
/**
 * Maps a task assignment to a fine-grained due category. Completed tasks
 * return COMPLETED. Uses expected_completion_date.
 */
function toDueCategory(assignment) {
  if (isAssignmentComplete(assignment)) return 'COMPLETED';
  const days = daysUntilDue(assignment);
  if (days === null) return 'NO_DUE_DATE';
  if (days < 0) return 'OVERDUE';
  if (days === 0) return 'DUE_TODAY';
  if (days <= DUE_SOON_DAYS) return 'DUE_SOON';
  return 'FUTURE';
}
/**
 * Maps a task assignment to a coarse urgency bucket. Completed tasks return
 * null. Everything open that isn't overdue or due-today is REMAINING.
 */
function toUrgencyFilter(assignment) {
  const cat = toDueCategory(assignment);
  if (cat === 'COMPLETED') return null;
  if (cat === 'OVERDUE') return 'OVERDUE';
  if (cat === 'DUE_TODAY') return 'DUE_TODAY';
  return 'REMAINING';
}

/**
 * Fetches workflows grouped by resource_urn with progress.
 * Primary hook for the "My Work" view.
 */
const useWorkQueue = params => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWorkQueue(params);
      setWorkflows(result);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [params?.includeAllWorkflowTasks, params?.fromDate, params?.toDate, params?.dateField, params?.overdueOnly, params?.workflowType, params?.userId]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return {
    workflows,
    loading,
    error,
    refetch: fetch
  };
};
/**
 * Fetches identity fields (name, party_id, email) for a user by id.
 * Used when viewing another user's work queue to build the page title and
 * scope the "{User}'s Only" toggle. Skips the fetch when userId is falsy.
 */
const useWorkQueueUser = userId => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!userId) {
      setUser(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getWorkQueueUser(userId).then(result => {
      if (!cancelled) setUser(result);
    }).catch(e => {
      if (!cancelled) setError(e);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  return {
    user,
    loading,
    error
  };
};
/**
 * Lightweight summary of all open work.
 * Use on homepage cards. Returns totals + top 3 workflows sorted by recency.
 */
const useWorkQueueSummary = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWorkQueueSummary();
      setWorkflows(result);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetch();
  }, [fetch]);
  const summary = useMemo(() => {
    let totalAssignments = 0;
    let totalCompleted = 0;
    let totalTasks = 0;
    // Assignment-derived completed/total (matches work-queue page % bar).
    let completed = 0;
    let total = 0;
    // Urgency totals across all open assignments.
    let overdue = 0;
    let dueToday = 0;
    let remaining = 0;
    // Per-domain rollups, keyed for O(1) accumulation.
    const domainAcc = {
      INTAKE: {
        overdue: 0,
        inProgress: 0,
        completed: 0,
        total: 0
      },
      MARKETPLACE_ACTIVATION: {
        overdue: 0,
        inProgress: 0,
        completed: 0,
        total: 0
      },
      SELLING: {
        overdue: 0,
        inProgress: 0,
        completed: 0,
        total: 0
      },
      CLOSING: {
        overdue: 0,
        inProgress: 0,
        completed: 0,
        total: 0
      }
    };
    for (const group of workflows) {
      const assignments = group.assignments ?? [];
      totalAssignments += assignments.length;
      totalCompleted += group.completed_tasks;
      totalTasks += group.total_tasks;
      const domain = toWorkflowDomain(group.workflow_type);
      for (const a of assignments) {
        total += 1;
        const done = isAssignmentComplete(a);
        if (done) completed += 1;
        const urgency = toUrgencyFilter(a);
        if (urgency === 'OVERDUE') overdue += 1;else if (urgency === 'DUE_TODAY') dueToday += 1;else if (urgency === 'REMAINING') remaining += 1;
        if (domain) {
          const acc = domainAcc[domain];
          acc.total += 1;
          if (done) acc.completed += 1;
          if (isAssignmentOngoing(a)) acc.inProgress += 1;
          if (urgency === 'OVERDUE') acc.overdue += 1;
        }
      }
    }
    const domains = DOMAIN_ORDER.map(domain => ({
      domain,
      overdue: domainAcc[domain].overdue,
      inProgress: domainAcc[domain].inProgress,
      completed: domainAcc[domain].completed,
      total: domainAcc[domain].total
    }));
    // Sort by most recent assigned_date across assignments in each group
    const sorted = [...workflows].sort((a, b) => {
      const aLatest = a.assignments.reduce((max, ass) => ass.assigned_date > max ? ass.assigned_date : max, '');
      const bLatest = b.assignments.reduce((max, ass) => ass.assigned_date > max ? ass.assigned_date : max, '');
      return bLatest.localeCompare(aLatest);
    });
    const topWorkflows = sorted.slice(0, 3).map(group => ({
      resource_urn: group.resource_urn,
      workflow_type: group.workflow_type,
      completed_tasks: group.completed_tasks,
      total_tasks: group.total_tasks,
      address: group.transaction?.listing?.formatted_address?.join(', ') || null
    }));
    return {
      totalWorkflows: workflows.length,
      totalAssignments,
      totalCompleted,
      totalTasks,
      completed,
      total,
      overdue,
      dueToday,
      remaining,
      domains,
      topWorkflows
    };
  }, [workflows]);
  return {
    summary,
    loading,
    error,
    refetch: fetch
  };
};
const useAssignTasks = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const assign = useCallback(async input => {
    setLoading(true);
    setError(null);
    try {
      return await assignTasks(input);
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    assign,
    loading,
    error
  };
};
const useReassignTasks = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reassign = useCallback(async input => {
    setLoading(true);
    setError(null);
    try {
      return await reassignTasks(input);
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    reassign,
    loading,
    error
  };
};
/**
 * Creates an ad-hoc (impromptu) task on a workflow.
 * Takes a stable resource_urn; the backend resolves the current workflow
 * version at write time.
 */
const useCreateImpromptuTask = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const createTask = useCallback(async input => {
    setLoading(true);
    setError(null);
    try {
      return await createImpromptuTask(input);
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    createTask,
    loading,
    error
  };
};
/**
 * Completes an impromptu task by its URN.
 */
const useCompleteImpromptuTask = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const completeTask = useCallback(async taskUrn => {
    setLoading(true);
    setError(null);
    try {
      return await completeImpromptuTask(taskUrn);
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    completeTask,
    loading,
    error
  };
};

var hooks$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useAssignTasks: useAssignTasks,
    useCompleteImpromptuTask: useCompleteImpromptuTask,
    useCreateImpromptuTask: useCreateImpromptuTask,
    useReassignTasks: useReassignTasks,
    useWorkQueue: useWorkQueue,
    useWorkQueueSummary: useWorkQueueSummary,
    useWorkQueueUser: useWorkQueueUser
});

var index$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DOMAIN_LABELS: DOMAIN_LABELS,
    DOMAIN_ORDER: DOMAIN_ORDER,
    daysUntilDue: daysUntilDue,
    deriveProgress: deriveProgress,
    hooks: hooks$2,
    isAssignmentComplete: isAssignmentComplete,
    isAssignmentOngoing: isAssignmentOngoing,
    providers: providers$1,
    toDueCategory: toDueCategory,
    toUrgencyFilter: toUrgencyFilter,
    toWorkflowDomain: toWorkflowDomain
});

/** Actions emitted by the workflow advance subscription */
var WorkflowAdvanceAction;
(function (WorkflowAdvanceAction) {
  WorkflowAdvanceAction["TASK_COMPLETE"] = "TASK_COMPLETE";
  WorkflowAdvanceAction["TASK_ADVANCE"] = "TASK_ADVANCE";
  WorkflowAdvanceAction["GROUP_COMPLETE"] = "GROUP_COMPLETE";
  WorkflowAdvanceAction["WORKFLOW_COMPLETE"] = "WORKFLOW_COMPLETE";
})(WorkflowAdvanceAction || (WorkflowAdvanceAction = {}));
/**
 * Utilities for WorkflowEntry idempotent collection operations.
 * Empty for now — placeholder for future bound methods.
 */
const workflowEntryUtils = {};

var types = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get WorkflowAdvanceAction () { return WorkflowAdvanceAction; },
    workflowEntryUtils: workflowEntryUtils
});

const {
  gql: gql$2
} = graph;
const fetchWorkflowByUrn = (urn, workflowTypes) => fetch.forAuctionGraph({
  query: gql$2`
        query ${embeddableOperation('blueprint_getWorkflowByUrn')}(
          $urn: URN!
          $workflowTypes: [ShallowTaskWorkflowType]
        ) {
          workflow(urn: $urn, workflowTypes: $workflowTypes) {
            current_status_label
            task_groups {
              workflow_type
              code
              label
              label_tooltip
              description
              description_tooltip
              expectation
              status
              tasks {
                code
                label
                expectation
                status
                last_updated_date
                activated_date
                completed_date
                expected_completion_date
                metadata {
                  __typename
                  task_status
                  upload_status
                  ... on PurchaseDocumentWorkflowMetadata {
                    task_status
                    upload_status
                    rejection_reason
                    rejection_comments
                    document_url
                  }
                }
              }
            }
          }
        }
      `,
  variables: {
    urn,
    ...(workflowTypes?.length && {
      workflowTypes
    })
  }
}).then(response => {
  if (response.body.errors && response.body.errors[0]) {
    throw partialGraphErrorFactory$3('workflow fetch error', response.body.errors);
  }
  return response.body.data?.workflow ?? null;
});

var providers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    fetchWorkflowByUrn: fetchWorkflowByUrn
});

const {
  gql: gql$1
} = graph;
const logger$5 = coreLogger.forModule('blueprint:workflow');
/**
 * Applies a workflow advance event to the local workflow state.
 * Mutates nothing — returns a new ShallowTaskWorkflow.
 */
function reconcileWorkflow(workflow, event) {
  const {
    action,
    code
  } = event;
  switch (action) {
    case WorkflowAdvanceAction.TASK_COMPLETE:
      {
        return {
          ...workflow,
          task_groups: workflow.task_groups.map(group => ({
            ...group,
            tasks: group.tasks.map(task => task.code === code ? {
              ...task,
              status: graph.enums.ShallowTaskWorkflowItemStatus.Complete,
              completed_date: event.completed_date
            } : task)
          }))
        };
      }
    case WorkflowAdvanceAction.TASK_ADVANCE:
      {
        return {
          ...workflow,
          task_groups: workflow.task_groups.map(group => ({
            ...group,
            tasks: group.tasks.map(task => task.code === code ? {
              ...task,
              status: graph.enums.ShallowTaskWorkflowItemStatus.Ongoing
            } : task)
          }))
        };
      }
    case WorkflowAdvanceAction.GROUP_COMPLETE:
      {
        const completedIndex = workflow.task_groups.findIndex(group => group.code === code);
        const updatedGroups = workflow.task_groups.map((group, idx) => {
          if (idx === completedIndex) {
            return {
              ...group,
              status: graph.enums.ShallowTaskWorkflowItemStatus.Complete
            };
          }
          if (idx === completedIndex + 1) {
            return {
              ...group,
              status: graph.enums.ShallowTaskWorkflowItemStatus.Ongoing,
              tasks: group.tasks.map((task, taskIdx) => taskIdx === 0 ? {
                ...task,
                status: graph.enums.ShallowTaskWorkflowItemStatus.Ongoing
              } : task)
            };
          }
          return group;
        });
        return {
          ...workflow,
          task_groups: updatedGroups
        };
      }
    case WorkflowAdvanceAction.WORKFLOW_COMPLETE:
      {
        return {
          ...workflow,
          task_groups: workflow.task_groups.map(group => ({
            ...group,
            status: graph.enums.ShallowTaskWorkflowItemStatus.Complete,
            tasks: group.tasks.map(task => ({
              ...task,
              status: graph.enums.ShallowTaskWorkflowItemStatus.Complete
            }))
          }))
        };
      }
    default:
      return workflow;
  }
}
/**
 * Real-time workflow hook.
 *
 * Polls for full workflow state (hydration + consistency backstop).
 * Subscribes to lightweight diff events via WebSocket and reconciles locally.
 * Uses an idempotent collection keyed by URN with `updated_date` as the
 * idempotency timestamp — stale writes are silently dropped.
 */
function useWorkflows({
  urns,
  graphWebsocket,
  enabled,
  workflowTypes,
  taskCodes
}) {
  const {
    collection,
    attemptWrite,
    reset: resetCollection
  } = useIdempotentBlueprintCollection([], {
    primaryKey: 'urn',
    idempotencyKey: 'updated_date',
    utils: workflowEntryUtils,
    updateBehavior: 'overwrite',
    collectionName: 'blueprint:workflow'
  });
  // Ref for accessing current collection state inside dataExtractor without stale closure
  const collectionRef = useRef(collection.raw);
  collectionRef.current = collection.raw;
  const subjectIds = useMemo(() => urns.length > 0 ? urns : [], [urns]);
  useEffect(() => {
    if (urns.length === 0) {
      resetCollection([]);
    }
  }, [urns, resetCollection]);
  useRealtimeWatcher({
    id: 'useWorkflows_workflowTaskCompleted',
    subjectIds,
    polling: {
      enabled: enabled && urns.length > 0,
      executor: async () => {
        if (urns.length === 0) return [];
        try {
          const entries = [];
          for (const urn of urns) {
            const workflow = await fetchWorkflowByUrn(urn, workflowTypes);
            if (workflow) {
              entries.push({
                urn,
                workflow,
                updated_date: new Date().toISOString(),
                last_action: null,
                last_task_code: null
              });
            }
          }
          return entries;
        } catch (e) {
          return [];
        }
      },
      frequency: SUPPORTED_OFFSETS.NORMAL
    },
    websocket: {
      graphWebsocket,
      enabled: enabled && urns.length > 0 && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: () => ({
        query: gql$1`
          subscription blueprint_workflowTaskCompleted(
            $urn: URN!
            $filters: WorkflowTaskCompletedFilters
          ) {
            workflowTaskCompleted(urn: $urn, filters: $filters) {
              resource_urn
              action
              code
              completed_date
            }
          }
        `,
        variables: {
          urn: urns[0],
          filters: {
            ...(taskCodes?.length && {
              taskCodes
            })
          }
        }
      }),
      dataExtractor: data => {
        // @ts-expect-error GraphQL response typing
        const update = data?.workflowTaskCompleted;
        if (!update?.action) {
          logger$5.info('Received null workflowTaskCompleted from websocket');
          return [];
        }
        const event = {
          resource_urn: update.resource_urn,
          action: update.action,
          code: update.code,
          completed_date: update.completed_date
        };
        const existing = collectionRef.current.find(e => e.urn === event.resource_urn);
        if (!existing) {
          logger$5.info('No existing workflow entry to reconcile against');
          return [];
        }
        const reconciledWorkflow = reconcileWorkflow(existing.workflow, event);
        return [{
          urn: event.resource_urn,
          workflow: reconciledWorkflow,
          updated_date: event.completed_date ?? new Date().toISOString(),
          last_action: event.action,
          last_task_code: event.code
        }];
      }
    },
    onData: ({
      data,
      source
    }) => {
      if (data.length > 0) {
        attemptWrite({
          data,
          source
        });
      }
    },
    onError: () => {}
  });
  return {
    data: collection,
    actions: null
  };
}

var hooks$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    reconcileWorkflow: reconcileWorkflow,
    useWorkflows: useWorkflows
});

var index$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get WorkflowAdvanceAction () { return WorkflowAdvanceAction; },
    hooks: hooks$1,
    providers: providers,
    types: types
});

const logger$4 = coreLogger.forModule('blueprint:venues:live');
function useLiveAuctionVenues({
  enabled,
  initialData,
  graphWebsocket
}) {
  const {
    collection: idempotentVenues,
    attemptWrite: attemptVenuesWrite
  } = useIdempotentBlueprintCollection(initialData.venues, {
    primaryKey: 'venue_id',
    // @ts-ignore idempotencyKey<Venue> where updated_date is optional as per types
    idempotencyKey: 'updated_date',
    updateBehavior: 'merge'
  });
  const data = useMemo(() => ({
    venues: idempotentVenues
  }), [idempotentVenues]);
  const subjectIds = useMemo(() => initialData.venues.map(v => typeof v === 'string' ? v : v.venue_id), [initialData.venues]);
  const output = useMemo(() => ({
    data,
    actions: null
  }), [data]);
  useRealtimeWatcher({
    id: 'useVenueLiveAuction',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription venueUpdated($venueIds: [ID!]) {
            venueUpdated(filters: { venueIds: $venueIds }) {
              action
              venue {
                venue_id
                status
                venue_title
                venue_name
                venue_address {
                  country_primary_subdivision
                  country_secondary_subdivision
                }
                formatted_address(format: DOUBLE_LINE)
                venue_schedule {
                  bid_start_local_date
                  bid_start_local_time
                  bid_start_date_time
                  registration_time
                }
                updated_date
              }
            }
          }
        `,
        variables: {
          venueIds: subjectIds
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data?.venueUpdated?.venue].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$4.info(`New venue data loaded. source=${source}`, JSON.stringify(data));
      attemptVenuesWrite({
        data,
        source
      });
    },
    onError: error => {
      logger$4.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$4.info('Have event', event, ctx);
    }
  });
  useRealtimeWatcher({
    id: 'useLiveAuction',
    subjectIds,
    websocket: {
      graphWebsocket,
      enabled: enabled && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false),
      executor: ({
        subjectIds
      }) => ({
        query: gql$6`
          subscription liveAuctionUpdated($venueIds: [ID!]) {
            liveAuctionUpdated(filters: { venueIds: $venueIds }) {
              action
              live_auction {
                status
                pending_count
                cleared_count
                in_progress_count
                sold_count
                not_sold_count
                scheduled_count
                postponed_count
                canceled_count
                location {
                  lat
                  lon
                }
                staff {
                  id
                  check_in_date
                  check_out_date
                  party_id
                  staff_role
                  user {
                    first_name
                    last_name
                  }
                }
                updated_date
                venue_id
              }
            }
          }
        `,
        variables: {
          venueIds: subjectIds
        }
      }),
      dataExtractor: data =>
      // @ts-ignore Need to infer types from schema documents for this.
      [data?.liveAuctionUpdated?.live_auction].filter(Boolean)
    },
    onData: payload => {
      const {
        data,
        source
      } = payload;
      logger$4.info(`New live auction data loaded. source=${source}`, JSON.stringify(data));
      const venueData = data.map(item => ({
        venue_id: item.venue_id,
        live_auction: {
          ...item
        },
        updated_date: item.updated_date
      }));
      attemptVenuesWrite({
        // @ts-ignore - live auction is the subset of venue data that we get from this subscription, so it should be safe to write it to the venues collection as a partial update.
        data: venueData,
        source
      });
    },
    onError: error => {
      logger$4.error('Realtime watcher error', error);
    },
    onEvent: (event, ctx) => {
      logger$4.info('Have event', event, ctx);
    }
  });
  return output;
}

var hooks = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useLiveAuctionVenues: useLiveAuctionVenues
});

var index$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hooks: hooks
});

const logger$3 = coreLogger.forModule('blueprint:intake:reducer');
const INITIAL_PIPELINE_STATE = {
  ingestionId: null,
  fileName: null,
  classification: null,
  status: 'IDLE',
  phases: [],
  overallProgress: {
    current: 0,
    total: 0
  },
  notifications: []
};
let notificationCounter = 0;
function createNotification(type, message) {
  return {
    id: `notif_${++notificationCounter}`,
    type,
    message,
    timestamp: new Date().toISOString()
  };
}
function computeElapsedMs(startedAt, completedAt) {
  if (!startedAt || !completedAt) return null;
  const elapsed = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  return elapsed >= 0 ? elapsed : null;
}
function mapLifecycleStatus(status) {
  switch (status) {
    case 'COMPLETED':
      return 'COMPLETED';
    case 'FAILED':
      return 'FAILED';
    default:
      return 'IN_PROGRESS';
  }
}
const STATUS_ORDER = {
  UPCOMING: 0,
  ONGOING: 1,
  COMPLETE: 2,
  FAILED: 2
};
function isStatusRegression(current, incoming) {
  return (STATUS_ORDER[current] ?? 0) >= (STATUS_ORDER[incoming] ?? 0);
}
function mapStepStatus(stepStatus) {
  switch (stepStatus) {
    case 'START':
      return 'ONGOING';
    case 'IN_PROGRESS':
      return 'ONGOING';
    case 'COMPLETED':
      return 'COMPLETE';
    case 'FAILED':
      return 'FAILED';
    case 'WARNING':
      return 'COMPLETE';
    default:
      return 'UPCOMING';
  }
}
function derivePhaseStatus(steps) {
  if (steps.length === 0) return 'UPCOMING';
  if (steps.some(s => s.status === 'FAILED')) return 'FAILED';
  if (steps.every(s => s.status === 'COMPLETE' || s.status === 'FAILED')) return 'COMPLETE';
  if (steps.some(s => s.status === 'ONGOING' || s.status === 'COMPLETE')) return 'ONGOING';
  return 'UPCOMING';
}
function processMessage(state, msg) {
  // Deep clone phases for immutability
  let phases = state.phases.map(p => ({
    ...p,
    steps: p.steps.map(s => ({
      ...s
    }))
  }));
  let notifications = [...state.notifications];
  let overallProgress = {
    ...state.overallProgress
  };
  // ─── Update lifecycle fields (from polling) ──────────────────────────────
  let status = state.status;
  if (msg.status && msg.status !== 'NEW') {
    status = mapLifecycleStatus(msg.status);
  }
  const fileName = msg.file_name ?? state.fileName;
  const classification = msg.classification ?? state.classification;
  const ingestionId = msg.ingestion_id != null ? String(msg.ingestion_id) : state.ingestionId;
  // Detect terminal status from step field even without a phase
  if (msg.step === 'FAILED' || msg.step_status === 'FAILED') {
    status = 'FAILED';
  } else if (msg.step === 'COMPLETED' && msg.step_status === 'COMPLETED') {
    status = 'COMPLETED';
  }
  // If no phase-level data, return with lifecycle/terminal updates only
  if (!msg.phase) {
    return {
      ...state,
      ingestionId,
      fileName,
      classification,
      status
    };
  }
  // If no step to process within a phase, return
  if (!msg.step) {
    return {
      ...state,
      ingestionId,
      fileName,
      classification,
      status
    };
  }
  // ─── Step-level processing (from websocket) ──────────────────────────────
  // Edge case #5: pipelineStepIndex regression
  if (msg.pipeline_step_index != null && msg.pipeline_step_index < overallProgress.current) {
    logger$3.warn('[IngestionPipeline] WARN: step index regression', msg.pipeline_step_index, '<', overallProgress.current);
    notifications = [...notifications, createNotification('warning', `Unexpected progress regression: step index ${msg.pipeline_step_index}, previously at ${overallProgress.current}`)];
    // Still allow message enrichment for steps that were implied complete
    const regressionPhase = phases.find(p => p.code === msg.phase);
    if (regressionPhase && msg.step && msg.message) {
      const regressionStep = regressionPhase.steps.find(s => s.code === msg.step);
      if (regressionStep && !regressionStep.message) {
        regressionStep.message = msg.message;
        if (msg.created_date && !regressionStep.completedAt) {
          regressionStep.completedAt = msg.created_date;
          regressionStep.elapsedMs = computeElapsedMs(regressionStep.startedAt, msg.created_date);
        }
      }
    }
    return {
      ...state,
      ingestionId,
      fileName,
      classification,
      status,
      phases,
      notifications
    };
  }
  // ─── Ensure phase exists (edge case #3, #8) ──────────────────────────────
  let phase = phases.find(p => p.code === msg.phase);
  if (!phase) {
    // New phase arriving — mark all previous ONGOING phases as COMPLETE (implied)
    for (const prevPhase of phases) {
      if (prevPhase.status === 'ONGOING') {
        prevPhase.status = 'COMPLETE';
        if (!prevPhase.completedAt) prevPhase.completedAt = msg.created_date;
        for (const prevStep of prevPhase.steps) {
          if (prevStep.status === 'UPCOMING' || prevStep.status === 'ONGOING') {
            logger$3.debug(`[IngestionPipeline] ${prevPhase.code} > ${prevStep.code} | implied COMPLETE (new phase started)`);
            prevStep.status = 'COMPLETE';
            if (!prevStep.completedAt) prevStep.completedAt = msg.created_date;
          }
        }
      }
    }
    // Phase never announced — create on-the-fly
    const newPhase = {
      code: msg.phase,
      status: 'ONGOING',
      steps: [],
      totalSteps: msg.total_steps_in_phase ?? 0,
      startedAt: msg.created_date
    };
    if (msg.phase_steps && msg.phase_steps.length > 0) {
      newPhase.steps = msg.phase_steps.map(stepCode => ({
        code: stepCode,
        status: 'UPCOMING'
      }));
      newPhase.totalSteps = msg.phase_steps.length;
      logger$3.debug(`[IngestionPipeline] ${msg.phase} | PHASE START | steps: [${msg.phase_steps.join(', ')}]`);
    } else {
      logger$3.debug(`[IngestionPipeline] ${msg.phase} | PHASE START (no steps announced)`);
    }
    phases = [...phases, newPhase];
    phase = phases[phases.length - 1];
  } else if (msg.phase_status === 'START' && msg.phase_steps && msg.phase_steps.length > 0) {
    if (phase.steps.length === 0) {
      phase.steps = msg.phase_steps.map(stepCode => ({
        code: stepCode,
        status: 'UPCOMING'
      }));
    }
    phase.totalSteps = msg.total_steps_in_phase ?? msg.phase_steps.length;
    if (!phase.startedAt) phase.startedAt = msg.created_date;
    phase.status = 'ONGOING';
  }
  // Edge case #10: Update totalSteps if message provides a newer/larger value
  if (msg.total_steps_in_phase != null && msg.total_steps_in_phase > phase.totalSteps) {
    phase.totalSteps = msg.total_steps_in_phase;
  }
  // ─── Ensure step exists (edge case #12) ──────────────────────────────────
  let step = phase.steps.find(s => s.code === msg.step);
  if (!step) {
    logger$3.debug(`[IngestionPipeline] ${msg.phase} > ${msg.step} | dynamic insert`);
    const newStep = {
      code: msg.step,
      status: 'UPCOMING'
    };
    const insertIdx = msg.phase_step_index != null ? Math.min(msg.phase_step_index - 1, phase.steps.length) : phase.steps.length;
    phase.steps.splice(insertIdx, 0, newStep);
    step = phase.steps[insertIdx];
    if (phase.steps.length > phase.totalSteps) {
      phase.totalSteps = phase.steps.length;
    }
  }
  // ─── Edge case #11: Implied completion for skipped steps ─────────────────
  if (msg.phase_step_index != null && msg.phase_step_index > 1) {
    for (let i = 0; i < msg.phase_step_index - 1; i++) {
      const prevStep = phase.steps[i];
      if (prevStep && (prevStep.status === 'UPCOMING' || prevStep.status === 'ONGOING')) {
        logger$3.debug(`[IngestionPipeline] ${msg.phase} > ${prevStep.code} | implied COMPLETE (skipped)`);
        prevStep.status = 'COMPLETE';
        if (!prevStep.completedAt) prevStep.completedAt = msg.created_date;
        prevStep.elapsedMs = computeElapsedMs(prevStep.startedAt, prevStep.completedAt);
      }
    }
  }
  // ─── Apply step status ───────────────────────────────────────────────────
  const incomingStatus = mapStepStatus(msg.step_status);
  if (!isStatusRegression(step.status, incomingStatus)) {
    step.status = incomingStatus;
    if (msg.step_status === 'START') {
      step.startedAt = msg.created_date;
      logger$3.debug(`[IngestionPipeline] ${msg.phase} > ${msg.step} | START`);
    }
    if (msg.step_status === 'COMPLETED' || msg.step_status === 'FAILED' || msg.step_status === 'WARNING') {
      step.completedAt = msg.created_date;
      step.message = msg.message;
      step.elapsedMs = computeElapsedMs(step.startedAt, step.completedAt);
      logger$3.debug(`[IngestionPipeline] ${msg.phase} > ${msg.step} | ${msg.step_status}${msg.message ? ` | "${msg.message}"` : ''}`);
    }
  } else {
    // Status didn't change but message may carry new info (e.g. implied completion then real completion)
    if (msg.message && !step.message) {
      step.message = msg.message;
      logger$3.debug(`[IngestionPipeline] ${msg.phase} > ${msg.step} | message updated: "${msg.message}"`);
    }
    if (msg.created_date && !step.completedAt) {
      step.completedAt = msg.created_date;
      step.elapsedMs = computeElapsedMs(step.startedAt, step.completedAt);
    }
  }
  // ─── Derive phase status ─────────────────────────────────────────────────
  phase.status = derivePhaseStatus(phase.steps);
  if (phase.status === 'COMPLETE' && !phase.completedAt) {
    phase.completedAt = msg.created_date;
    logger$3.debug(`[IngestionPipeline] ${msg.phase} | PHASE COMPLETE`);
  }
  // ─── Determine overall pipeline status ───────────────────────────────────
  if (msg.step_status === 'FAILED' || msg.step === 'FAILED') {
    status = 'FAILED';
  } else if (msg.step === 'COMPLETED' && msg.step_status === 'COMPLETED') {
    status = 'COMPLETED';
  } else if (status === 'IDLE') {
    status = 'IN_PROGRESS';
  }
  // ─── Update overall progress ─────────────────────────────────────────────
  const newOverallProgress = {
    current: msg.pipeline_step_index ?? overallProgress.current,
    total: msg.total_pipeline_steps ?? overallProgress.total
  };
  return {
    ingestionId,
    fileName,
    classification,
    status,
    phases,
    overallProgress: newOverallProgress,
    notifications
  };
}
function pipelineReducer(state, action) {
  switch (action.type) {
    case 'MESSAGE_RECEIVED':
      return processMessage(state, action.payload);
    case 'PIPELINE_STARTED':
      logger$3.debug(`[IngestionPipeline] === STARTED id=${action.payload.ingestionId} ===`);
      return {
        ...INITIAL_PIPELINE_STATE,
        ingestionId: action.payload.ingestionId,
        fileName: action.payload.fileName ?? null,
        status: 'IN_PROGRESS'
      };
    case 'PIPELINE_RESET':
      logger$3.debug('[IngestionPipeline] === RESET ===');
      return INITIAL_PIPELINE_STATE;
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: []
      };
    default:
      return state;
  }
}

/**
 * Secure fields — a small, generic capability for encrypting and revealing
 * sensitive values (loan numbers today; extensible to other secrets). The
 * capability is deliberately NOT owned by any one domain (intake, listing…) —
 * encrypting and decrypting are cross-cutting.
 *
 * Design: unify the CLIENT ergonomics (one hook + a field registry); preserve
 * per-field authorization on the SERVER. There is intentionally no generic
 * "decrypt any uuid" endpoint — authority is per field (e.g. LNM authorizes
 * loan numbers only) and a single stored secret can decrypt to several values,
 * so each field is revealed through its own authorized resolver.
 */
/** Secret kinds this capability supports. One entry per registered field. */
var SecureField;
(function (SecureField) {
  SecureField["LoanNumber"] = "LOAN_NUMBER";
})(SecureField || (SecureField = {}));

const {
  gql
} = graph;
const unwrapGqlError = errors => {
  if (errors && errors[0]) {
    // Surface the real GraphQL message (validation, permission, vault) while
    // carrying the raw errors on `.errors` — the shared PartialGraphError shape.
    const gqlError = errors[0];
    throw partialGraphErrorFactory$3(gqlError.message ?? JSON.stringify(gqlError), errors);
  }
};
/**
 * Encrypts a secure field through the unified `ingest` mutation and returns the
 * stored reference. Mirrors the closing offer-media ingest pattern (builder +
 * mutation + hook); secureFields is its own capability that produces an
 * `IngestInput`, not an extension of the ingest hook.
 */
const secureFieldEncryptMutation = () => gql`
  mutation ${embeddableOperation('blueprint_encryptSecureField')}(
    $input: IngestInput!
  ) {
    ingest(input: $input) {
      uuid
    }
  }
`;
/**
 * Builds the `ingest` input that encrypts a secure field. Dispatches on the
 * input's `field` discriminant; each field owns its own payload shape and maps
 * to one typed `metadata` member (the member key implies the ingestion type, so
 * no `type` discriminant is needed). Adding a field is a case here.
 */
const buildSecureFieldIngestInput = input => {
  switch (input.field) {
    case SecureField.LoanNumber:
      return {
        metadata: {
          batch_loan_encryption: {
            org_id: String(input.orgId),
            loan_numbers: [input.value]
          }
        }
      };
    default:
      throw partialGraphErrorFactory$3(`Unsupported secure field: ${String(input.field)}`, undefined);
  }
};
/**
 * Builds the reveal query for a secure field at a given entity. Each secret is
 * read through its own authorized resolver (there is no generic decrypt), so
 * per-field server authorization is preserved.
 */
const buildReveal = (field, ref) => {
  if (field === SecureField.LoanNumber) {
    switch (ref.kind) {
      case 'leadIntake':
        return {
          query: gql`
            query ${embeddableOperation('blueprint_revealLoanNumber_lead')}(
              $id: ID!
            ) {
              lead_intake(leadIntakeId: $id) {
                properties {
                  loan_number
                }
              }
            }
          `,
          variables: {
            id: ref.id
          },
          extract: data => {
            const lead = data?.lead_intake;
            return lead?.properties?.[0]?.loan_number ?? null;
          }
        };
      case 'listing':
        return {
          query: gql`
            query ${embeddableOperation('blueprint_revealLoanNumber_listing')}(
              $id: ID!
            ) {
              listing(listingId: $id) {
                decrypted_loan_number
              }
            }
          `,
          variables: {
            id: ref.id
          },
          extract: data => {
            const listing = data?.listing;
            return listing?.decrypted_loan_number ?? null;
          }
        };
    }
  }
  throw partialGraphErrorFactory$3(`Secure field ${String(field)} cannot be revealed from a ${ref.kind}.`, undefined);
};
/**
 * Encrypts a clear value for a secure field and returns the stored reference
 * (opaque id). Never persists on any entity — the caller stores the reference.
 * Errors bubble.
 */
async function encryptSecret(input, controller) {
  return fetch.forAuctionGraph({
    query: secureFieldEncryptMutation(),
    variables: {
      input: buildSecureFieldIngestInput(input)
    }
  }, controller ? {
    controller
  } : undefined).then(response => {
    unwrapGqlError(response.body.errors);
    const uuid = response.body.data.ingest?.uuid;
    if (!uuid) {
      throw partialGraphErrorFactory$3('Encryption succeeded but no reference was returned.', undefined);
    }
    return uuid;
  });
}
/**
 * Reveals the raw value of a stored secret on demand, through the field's own
 * authorized resolver. Resolves null when unauthorized or no secret exists.
 * Errors bubble.
 */
async function revealSecret(field, ref, controller) {
  const {
    query,
    variables,
    extract
  } = buildReveal(field, ref);
  return fetch.forAuctionGraph({
    variables,
    query
  }, controller ? {
    controller
  } : undefined).then(response => {
    unwrapGqlError(response.body.errors);
    return extract(response.body.data);
  });
}

/* istanbul ignore file */
const fragments = {
  leadIntake: {
    reference: '...LeadIntakeFields',
    definition: gql$5`
      fragment LeadIntakeFields on LeadIntake {
        lead_intake_id
        first_name
        last_name
        middle_name
        email
        phone
        lead_company_name
        preferred_contact_method
        is_land_lead
        number_of_properties
        seller_intent
        sync_status
        lead_source
        created_date
        lead_intake_person {
          lead_intake_person_id
          life_cycle_status
          lead_source_code
          first_name
          last_name
          email
          phone
        }
        properties {
          lead_intake_property_id
          listing_id
          property_id
          address
          address_source
          city
          state
          postal_code
          unit_number
          street_number
          county
          country_code
          apn
          property_type
          occupancy_description
          occupancy_status
          condition
          bedrooms
          bathrooms
          home_square_footage
          lot_size
          year_built
          desired_amount
          sale_timeline
          move_out_preference
          move_out_date
          lease_back_period_in_days
          lead_owner_company_name
          loan_type
          loan_number_uuid
          servicer_organization_id
          program_type
          agent_first_name
          agent_last_name
          agent_email
          agent_phone
          relationship_to_property
          seller_intent
        }
      }
    `
  }
};

const logger$2 = coreLogger.forModule('blueprint:intake');
// ─── Shared Fragment ────────────────────────────────────────────────────────────
const INGESTION_ATTEMPT_LIFECYCLE_FIELDS = gql$5`
  fragment IngestionAttemptLifecycleFields on IngestionAttempt {
    ingestion_id
    classification
    intent
    context_urn
    file_name
    status
    created_date
    updated_date
  }
`;
// ─── Subscription ───────────────────────────────────────────────────────────────
const INGESTION_ATTEMPT_SUBSCRIPTION = gql$5`
  ${INGESTION_ATTEMPT_LIFECYCLE_FIELDS}

  subscription blueprint_ingestionAttemptUpdated(
    $filters: IngestionAttemptUpdatedFilters
  ) {
    ingestionAttemptUpdated(filters: $filters) {
      ...IngestionAttemptLifecycleFields
      phase
      step
      step_status
      phase_status
      phase_step_index
      total_steps_in_phase
      pipeline_step_index
      total_pipeline_steps
      phase_steps
      message
    }
  }
`;
// ─── Polling Query ──────────────────────────────────────────────────────────────
/**
 * Polls the ingestion_attempt GraphQL query.
 * Returns lifecycle fields in the same IngestionAttempt type as ws.
 * Blueprint merges these fields into the consolidated state object.
 */
async function getIngestionAttempt(ingestionId, _controller) {
  return fetch.forAuctionGraph({
    query: gql$5`
        ${INGESTION_ATTEMPT_LIFECYCLE_FIELDS}

        query ${embeddableOperation('blueprint_ingestionAttempt')}($ingestionId: ID!) {
          ingestion_attempt(ingestionId: $ingestionId) {
            ...IngestionAttemptLifecycleFields
          }
        }
      `,
    variables: {
      ingestionId
    }
  }).then(response => {
    const data = response.body?.data?.ingestion_attempt ?? null;
    logger$2.info(`Poll response status=${data?.status || 'null'}`);
    return data;
  }).catch(e => {
    logger$2.error('Poll error:', e);
    return null;
  });
}
// ─── Intake Batches (File History) ──────────────────────────────────────────────
const INTAKE_BATCHES_FRAGMENT = `
  current_page
  size
  total_count
  total_elements
  total_pages
  content {
    intake_batch_id
    ingestion_id
    created_by
    created_by_user {
      first_name
      last_name
      email
    }
    file_name
    file_type
    upload_date_time
    processed_date_time
    completed_date_time
    status
    classification_type
    coverage_cost_score
    structural_cost_score
    error_description
    record_count
    success_count
    error_count
    is_reprocessable
    urn
    organization_mapping_id
    mapping {
      id
      name
      version
      status
    }
    source {
      intake_source_id
      organization_id
      channel
      type
      name
      intake_channel
    }
    results {
      id
      result_type
      row_count
      details {
        id
        row_index
        cause_code
        columns_detail {
          column
          description
        }
      }
    }
    output_files {
      id
      file_name
      file_type
      urn
    }
  }
`;
/**
 * Fetches paginated intake batch history through the Organization type.
 * Shared between Morpheus (btbhome) and AFI (attorney-dashboard).
 *
 * @param organizationId - The org to query through (always required for the organization wrapper)
 * @param filters - Optional filters for the intake_batches field. organizationIds within
 *   filters is optional — admin/collaborator users include it so the backend filters by org;
 *   non-admin users omit it and the backend resolves org from token.
 */
async function fetchIntakeBatches(organizationId, filters = {}) {
  return fetch.forAuctionGraph({
    variables: {
      organizationId,
      filters
    },
    query: gql$5`
        query ${embeddableOperation('blueprint_fetchIntakeBatches')}($organizationId: ID!, $filters: IntakeBatchSearchInput) {
          organization(organizationId: $organizationId) {
            intake_batches(filters: $filters) {
              ${INTAKE_BATCHES_FRAGMENT}
            }
          }
        }
      `
  }).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return response.body.data.organization.intake_batches;
  });
}
// ─── Organization Mappings ──────────────────────────────────────────────────────
/**
 * Fetches all mapping configurations for an organization (lightweight — no columns).
 * Shared between Morpheus and AFI for mapping-engine-based template selection.
 */
async function fetchOrganizationMappings(organizationId) {
  return fetch.forAuctionGraph({
    variables: {
      organizationId
    },
    query: gql$5`
        query ${embeddableOperation('blueprint_organizationMappings')}($organizationId: ID!) {
          organization_mappings(organizationId: $organizationId) {
            id
            organization_id
            is_default_mapping_for_organization
            name
            version
            status
            version_comment
            created_by
            created_date
            updated_by
            updated_date
          }
        }
      `
  }).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return response.body.data.organization_mappings;
  });
}
// ─── Reprocess Intake Batch (Mutation) ──────────────────────────────────────────
/**
 * Reprocesses an existing intake batch by re-running the mapping engine on its
 * failed rows (rows with ERROR or FLAGGED status). Returns true on success.
 *
 * Shared between Morpheus (btbhome) and AFI (attorney-dashboard). The caller is
 * responsible for refreshing the file-history list afterwards. Errors bubble to
 * the caller (thrown), consistent with the other intake providers.
 */
async function reprocessIntakeBatch(intakeBatchId, controller) {
  return fetch.forAuctionGraph({
    variables: {
      intakeBatchId
    },
    query: gql$5`
          mutation ${embeddableOperation('blueprint_reprocessIntakeBatch')}($intakeBatchId: ID!) {
            reprocessIntakeBatch(intakeBatchId: $intakeBatchId)
          }
        `
  }, controller ? {
    controller
  } : undefined).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return Boolean(response.body.data.reprocessIntakeBatch);
  });
}
// ─── Ingest Mutation ────────────────────────────────────────────────────────────
// ─── Lead Intake (single-record retrieval) ──────────────────────────────────────
// LeadIntakeFields fragment lives in ./fragments (shared with any future
// consumers that need the same field set without importing providers).
/**
 * Fetches a single consumer-listing-intake lead by id.
 * Source-of-truth record read (not the Elasticsearch FCL `leads` search).
 * Errors bubble to the caller (thrown), consistent with the closing providers.
 */
async function fetchLeadIntake(leadIntakeId, controller) {
  return fetch.forAuctionGraph({
    variables: {
      leadIntakeId
    },
    query: gql$5`
          ${fragments.leadIntake.definition}
          query ${embeddableOperation('blueprint_leadIntake')}($leadIntakeId: ID!) {
            lead_intake(leadIntakeId: $leadIntakeId) {
              ${fragments.leadIntake.reference}
            }
          }
        `
  }, controller ? {
    controller
  } : undefined).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return response.body.data.lead_intake ?? null;
  });
}
/**
 * Fetches the decrypted (raw) loan number for a lead on demand.
 *
 * Reads the lead-scoped `loan_number` field on LeadIntakeProperty,
 * which decrypts loan_number_uuid in the vault on read and is gated by the LNM
 * authority server-side — the field resolves to null when the caller is not
 * authorized or the lead has no loan number. Nothing is persisted. Errors
 * bubble (thrown), consistent with fetchLeadIntake.
 */
async function decryptLeadLoanNumber(leadIntakeId, controller) {
  return fetch.forAuctionGraph({
    variables: {
      leadIntakeId
    },
    query: gql$5`
          query ${embeddableOperation('blueprint_decryptLeadLoanNumber')}($leadIntakeId: ID!) {
            lead_intake(leadIntakeId: $leadIntakeId) {
              properties {
                loan_number
              }
            }
          }
        `
  }, controller ? {
    controller
  } : undefined).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      // Preserve the real GraphQL message (schema/validation, permission,
      // vault) instead of letting the caller mask it with a generic string.
      const gqlError = response.body.errors[0];
      throw new Error(gqlError.message ?? JSON.stringify(gqlError));
    }
    return response.body.data.lead_intake?.properties?.[0]?.loan_number ?? null;
  });
}
/**
 * Creates or updates a consumer-listing-intake lead through the authenticated
 * `saveLeadIntake` mutation. An `input` carrying `lead_intake_id` updates the
 * existing lead; without it a new lead is created. Returns the persisted lead,
 * hydrated with the same fields as `fetchLeadIntake`. Errors bubble to the
 * caller (thrown), consistent with the other intake providers.
 */
/**
 * Calls the existing (public) `upsertLeadIntake` graph mutation. An `input` with
 * `lead_intake_id` updates; without it creates. The backend is adding edit-time
 * authorization (logged-in + required roles) to this same endpoint, so authenticated
 * Morpheus edits and the anonymous public-capture flow share one mutation.
 */
async function upsertLeadIntake(input, controller) {
  return fetch.forAuctionGraph({
    variables: {
      input
    },
    query: gql$5`
          ${fragments.leadIntake.definition}
          mutation ${embeddableOperation('blueprint_upsertLeadIntake')}($input: LeadIntakeInput!) {
            upsertLeadIntake(input: $input) {
              ${fragments.leadIntake.reference}
            }
          }
        `
  }, controller ? {
    controller
  } : undefined).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return response.body.data.upsertLeadIntake;
  });
}
/**
 * Projects a fetched `LeadIntake` back into a complete `LeadIntakeInput`, then
 * overlays the caller's edits (edits win). `listing_id` / `property_id` are now
 * carried on the property so an update does not spawn a new listing (CLOSE-555).
 */
function mergeLeadIntakeInput(current, edits) {
  const p = current.properties?.[0] ?? {};
  const editProperty = edits.properties?.[0] ?? {};
  const property = {
    lead_intake_property_id: p.lead_intake_property_id,
    listing_id: p.listing_id,
    property_id: p.property_id,
    servicer_organization_id: p.servicer_organization_id,
    loan_number_uuid: p.loan_number_uuid,
    address: p.address,
    address_source: p.address_source,
    apn: p.apn,
    city: p.city,
    state: p.state,
    postal_code: p.postal_code,
    unit_number: p.unit_number,
    street_number: p.street_number,
    county: p.county,
    country_code: p.country_code,
    property_type: p.property_type,
    occupancy_description: p.occupancy_description,
    occupancy_status: p.occupancy_status,
    condition: p.condition,
    bedrooms: p.bedrooms,
    bathrooms: p.bathrooms,
    home_square_footage: p.home_square_footage,
    lot_size: p.lot_size,
    year_built: p.year_built,
    desired_amount: p.desired_amount,
    sale_timeline: p.sale_timeline,
    move_out_preference: p.move_out_preference,
    move_out_date: p.move_out_date,
    lease_back_period_in_days: p.lease_back_period_in_days,
    lead_owner_company_name: p.lead_owner_company_name,
    loan_type: p.loan_type,
    program_type: p.program_type,
    agent_first_name: p.agent_first_name,
    agent_last_name: p.agent_last_name,
    agent_email: p.agent_email,
    agent_phone: p.agent_phone,
    relationship_to_property: p.relationship_to_property,
    seller_intent: p.seller_intent,
    ...editProperty
  };
  return {
    lead_intake_id: current.lead_intake_id,
    first_name: current.first_name,
    last_name: current.last_name,
    middle_name: current.middle_name,
    email: current.email,
    phone: current.phone,
    lead_company_name: current.lead_company_name,
    preferred_contact_method: current.preferred_contact_method,
    is_land_lead: current.is_land_lead,
    number_of_properties: current.number_of_properties,
    seller_intent: current.seller_intent,
    sync_status: current.sync_status,
    ...edits,
    properties: [property]
  };
}
/**
 * Creates or updates a consumer-listing-intake lead.
 *
 * INTERIM IMPLEMENTATION — the backend `upsertLeadIntake` is a full replace: on
 * update it clears and rebuilds the lead's properties from the request alone, so a
 * partial payload would null every unspecified field. Until a real partial-update
 * endpoint exists (work-queue, next cycle) we re-fetch the full lead and resend the
 * complete object with the caller's edits overlaid.
 *
 * REVISIT: replace the get-merge-upsert dance below with a partial-update mutation
 * once the backend exposes one. `listing_id` / `property_id` are carried through on
 * the property (see mergeLeadIntakeInput) so updates preserve the existing listing.
 */
async function saveLeadIntake(edits, controller) {
  const id = edits.lead_intake_id;
  const current = id !== null && id !== undefined ? await fetchLeadIntake(String(id), controller) : null;
  const input = current ? mergeLeadIntakeInput(current, edits) : edits;
  return upsertLeadIntake(input, controller);
}
/**
 * Partially updates a single lead intake property through the `patchLeadIntakePropertyLoan`
 * graph mutation. Unlike `saveLeadIntake` (a full get-merge-upsert replace), this touches
 * ONLY the fields present on `input`: an omitted field is left untouched server-side, an
 * explicit `null` clears it. This is the authoritative path for clearing a lead's
 * loan_number_uuid when its servicer changes. Returns the hydrated lead. Errors bubble.
 */
async function patchLeadIntakePropertyLoan(input, controller) {
  return fetch.forAuctionGraph({
    variables: {
      input
    },
    query: gql$5`
          ${fragments.leadIntake.definition}
          mutation ${embeddableOperation('blueprint_patchLeadIntakePropertyLoan')}($input: LeadIntakePropertyLoanPatchInput!) {
            patchLeadIntakePropertyLoan(input: $input) {
              ${fragments.leadIntake.reference}
            }
          }
        `
  }, controller ? {
    controller
  } : undefined).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return response.body.data.patchLeadIntakePropertyLoan;
  });
}
/**
 * Encrypt-then-patch composition owned by the intake domain (NOT secureFields, which
 * stays a pure crypto primitive). Encrypts the raw loan number through secureFields —
 * which returns an opaque ciphertext reference and persists nothing — then PATCHes that
 * reference plus the servicer onto the lead property in one call. The raw loan number
 * never leaves this function; only the uuid is sent to the PATCH. Errors bubble.
 */
async function persistLeadLoanNumber(input, controller) {
  const uuid = await encryptSecret({
    field: SecureField.LoanNumber,
    value: input.loanNumber,
    orgId: input.servicerOrganizationId
  }, controller);
  return patchLeadIntakePropertyLoan({
    lead_intake_id: input.leadIntakeId,
    lead_intake_property_id: input.leadIntakePropertyId,
    loan_number_uuid: uuid,
    servicer_organization_id: input.servicerOrganizationId
  }, controller);
}
// ─── Leads (Lead List Page) ───────────────────────────────────────────────────
// Fragment on SmartsaleDashboardProjection returned by
// seek(type: SMARTSALE_DASHBOARD_PROJECTIONS).
const SMARTSALE_LEAD_FIELDS = gql$5`
  fragment SmartsaleLeadFields on SmartsaleDashboardProjection {
    lead_intake_id
    first_name
    last_name
    email
    phone
    lead_source
    status
    created_date
    updated_date
    property_addresses
    assignee_urn
    assignee_actor {
      __typename
      ... on Party {
        display_name
      }
      ... on User {
        display_name
      }
    }
  }
`;
// Fragment on the LeadIntake type returned by the assign mutation.
const LEAD_INTAKE_FIELDS = gql$5`
  fragment AssignedLeadFields on LeadIntake {
    lead_intake_id
    first_name
    last_name
    email
    phone
    lead_source
    status
    assignee_urn
    assignee_actor {
      __typename
      ... on Party {
        display_name
      }
      ... on User {
        display_name
      }
    }
  }
`;
// FE sort tokens -> seek smartsale-lead index fields. Text fields need the
// .keyword sub-field to be sortable. This is a product concern, so it lives here.
const LEAD_SORT_FIELD_MAP = {
  lastName: 'last_name.keyword',
  leadSourceCode: 'lead_source',
  statusCode: 'status',
  createdDate: 'created_date',
  lastActivityDate: 'updated_date'
};
const DEFAULT_LEAD_SORT = [{
  field: 'created_date',
  order: 'DESC'
}];
const computeLeadSort = sort => {
  if (!sort) return DEFAULT_LEAD_SORT;
  const [rawField, rawDir] = sort.split(',');
  const field = LEAD_SORT_FIELD_MAP[rawField];
  if (!field) return DEFAULT_LEAD_SORT;
  return [{
    field,
    order: rawDir?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC'
  }];
};
// Translates product filters into seek DSL expressions for the smartsale-lead index.
const computeLeadExpressions = filters => {
  const expressions = [];
  const addEquals = (field, value) => {
    if (value != null && value !== '') {
      expressions.push({
        equals: {
          fields: field,
          values: value
        }
      });
    }
  };
  const addRange = (field, gte, lte) => {
    if (gte != null) expressions.push({
      range: {
        fields: field,
        gte
      }
    });
    if (lte != null) expressions.push({
      range: {
        fields: field,
        lte
      }
    });
  };
  addEquals('assignee_urn', filters.assignee_party_id ? `adc:party:${filters.assignee_party_id}` : undefined);
  // Both status filters map to the single index `status` field.
  addEquals('status', filters.status);
  addEquals('status', filters.life_cycle_status);
  addEquals('lead_source', filters.lead_source_code);
  addRange('created_date', filters.created_date_from, filters.created_date_to);
  addRange('updated_date', filters.last_activity_from, filters.last_activity_to);
  // Server-side free-text search across the index's text fields (replaces the
  // old client-side page filter).
  if (filters.search) {
    expressions.push({
      match: {
        fields: ['first_name', 'last_name', 'email', 'assignee_first_name', 'assignee_last_name', 'property_addresses'],
        value: filters.search,
        exact_match: false
      }
    });
  }
  if (expressions.length === 0) {
    expressions.push({
      all: {}
    });
  }
  return expressions;
};
const buildLeadsDsl = (filters, paging) => JSON.stringify({
  query: {
    and: {
      expressions: computeLeadExpressions(filters)
    }
  },
  sort: computeLeadSort(paging.sort),
  size: paging.size,
  offset: (paging.page ?? 0) * (paging.size ?? 25)
});
/**
 * Fetches a page of smartsale leads AND the per-status counts in a single
 * seek call. Search + aggregation share one consistent query shape; the
 * aggregation runs over the whole filtered set (not just the page).
 */
async function fetchLeads(filters = {}, paging = {}) {
  return fetch.forAuctionGraph({
    variables: {
      dsl: buildLeadsDsl(filters, paging)
    },
    query: gql$5`
        ${SMARTSALE_LEAD_FIELDS}
        query ${embeddableOperation('blueprint_leads')}($dsl: String) {
          seek(type: SMARTSALE_DASHBOARD_PROJECTIONS, dsl: $dsl) {
            content {
              ...SmartsaleLeadFields
            }
            total_count
            total_pages
            current_page
            size
            aggregation(fields: ["status"])
          }
        }
      `
  }).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    const result = response.body.data.seek || {};
    const statusBuckets = result.aggregation?.status_count || {};
    return {
      rows: (result.content || []).filter(r => r != null),
      totalCount: result.total_count ?? 0,
      totalPages: result.total_pages ?? 0,
      currentPage: result.current_page ?? 0,
      statusCounts: statusBuckets
    };
  });
}
async function assignSmartsaleLead(leadIntakeId, assigneePartyId) {
  return fetch.forAuctionGraph({
    variables: {
      input: {
        lead_intake_id: leadIntakeId,
        assignee_party_id: assigneePartyId
      }
    },
    query: gql$5`
        ${LEAD_INTAKE_FIELDS}
        mutation ${embeddableOperation('blueprint_assignSmartsaleLead')}(
          $input: AssignLeadIntakeInput!
        ) {
          assignSmartsaleLead(input: $input) {
            ...AssignedLeadFields
          }
        }
      `
  }).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    return response.body.data.assignSmartsaleLead;
  });
}
// ─── Concierges (assignee filter) ─────────────────────────────────────────
const DEFAULT_CONCIERGE_ORG_ID = 1;
const PRIVATE_SELLER_CONCIERGE_GROUP = 'PRIVATE_SELLER_CONCIERGE';
const DEFAULT_CONCIERGE_PAGE_SIZE = 100;
/**
 * Fetch the concierge members used to populate the assignee filter. Mirrors the
 * uaa `POST /v1/organizations/members/search` endpoint via the existing
 * `seek(type: ORGANIZATION_MEMBERS)` query with an Elasticsearch DSL filtering
 * on organization_id, groups, and is_active. No dedicated graph query is needed.
 */
async function fetchConcierges({
  organizationId = DEFAULT_CONCIERGE_ORG_ID,
  group = PRIVATE_SELLER_CONCIERGE_GROUP,
  size = DEFAULT_CONCIERGE_PAGE_SIZE
} = {}) {
  const dsl = JSON.stringify({
    query: {
      and: {
        expressions: [{
          equals: {
            fields: 'organization_id',
            values: [organizationId]
          }
        }, {
          equals: {
            fields: 'groups',
            values: [group]
          }
        }, {
          equals: {
            fields: 'is_active',
            values: [true]
          }
        }]
      }
    },
    size
  });
  return fetch.forAuctionGraph({
    variables: {
      dsl
    },
    query: gql$5`
        query ${embeddableOperation('blueprint_concierges')}($dsl: String) {
          seek(type: ORGANIZATION_MEMBERS, dsl: $dsl) {
            content {
              ... on OrganizationMember {
                id
                party_id
                first_name
                last_name
                display_name
                email
              }
            }
          }
        }
      `
  }).then(response => {
    if (response.body.errors && response.body.errors[0]) {
      throw response.body.errors[0];
    }
    const content = response.body.data.seek?.content ?? [];
    return content.filter(m => m != null);
  });
}

const logger$1 = coreLogger.forModule('blueprint:intake');
// ─── Lifecycle Constants ──────────────────────────────────────────────────────
/**
 * Grace period to keep the WebSocket open beyond normal lifecycle boundaries.
 *
 * Applied in two scenarios:
 *   1. Initial grace: ws starts immediately when ingestionId is set, regardless of
 *      whether polling has confirmed non-terminal status yet. Covers fast pipelines
 *      that complete before the first poll returns.
 *   2. Terminal grace: ws stays open after terminal status is detected, catching
 *      trailing step events that arrive with Kafka lag after the lifecycle status
 *      transitions to COMPLETED/FAILED.
 *
 * Grace expiry is a no-op from the UI perspective — it only closes the socket.
 * PipelineState is never modified by timer expiry.
 */
const WS_GRACE_PERIOD_MS = ms('20s');
/**
 * Blueprint hook for real-time intake pipeline progress.
 *
 * Architecture (follows useOnlineAuctions pattern):
 * - Both polling and websocket resolve to the same IngestionAttempt GraphQL type
 * - Reducer merges fields from either source into a consolidated PipelineState
 * - Blueprint owns all edge case logic (gap detection, implied completions, etc.)
 * - Consumer (btbhome) just renders the clean state object
 *
 * Lifecycle management:
 * - WebSocket: enabled when ingestionId is set. Stays open for a grace period
 *   (WS_GRACE_PERIOD_MS) after terminal status is detected, allowing trailing
 *   step events to arrive. Also starts immediately on new ingestionId without
 *   waiting for polling to confirm non-terminal status.
 * - Polling: always enabled when ingestionId is set. Uses SUPPORTED_OFFSETS.CRITICAL
 *   while in progress, SUPPORTED_OFFSETS.NORMAL once terminal (background
 *   consistency without burdening backend).
 */
function useIntakePipeline({
  enabled,
  graphWebsocket,
  ingestionId
}) {
  const [state, dispatch] = useReducer(pipelineReducer, INITIAL_PIPELINE_STATE);
  // ─── WebSocket Grace Period ───────────────────────────────────────────────
  const [wsGraceActive, setWsGraceActive] = useState(false);
  const graceTimerRef = useRef(null);
  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);
  const startGraceTimer = useCallback(() => {
    clearGraceTimer();
    setWsGraceActive(true);
    graceTimerRef.current = setTimeout(() => {
      setWsGraceActive(false);
      graceTimerRef.current = null;
      logger$1.info('WebSocket grace period expired, closing connection');
    }, WS_GRACE_PERIOD_MS);
  }, [clearGraceTimer]);
  // Cleanup on unmount
  useEffect(() => clearGraceTimer, [clearGraceTimer]);
  // ─── Auto-reset when ingestionId changes (new pipeline started) ───────────
  const prevIngestionIdRef = useRef(ingestionId);
  useEffect(() => {
    if (ingestionId !== prevIngestionIdRef.current) {
      prevIngestionIdRef.current = ingestionId;
      if (ingestionId) {
        logger$1.info(`New ingestionId: ${ingestionId}`);
        dispatch({
          type: 'PIPELINE_STARTED',
          payload: {
            ingestionId
          }
        });
        // Initial grace: start ws immediately, give it time to connect
        // before polling might report terminal status
        startGraceTimer();
      } else {
        dispatch({
          type: 'PIPELINE_RESET'
        });
        clearGraceTimer();
        setWsGraceActive(false);
      }
    }
  }, [ingestionId, startGraceTimer, clearGraceTimer]);
  const subjectIds = useMemo(() => ingestionId ? [String(ingestionId)] : [], [ingestionId]);
  const isTerminal = state.status === 'COMPLETED' || state.status === 'FAILED';
  // ─── Terminal grace: keep ws open to catch trailing events ─────────────────
  const prevIsTerminalRef = useRef(isTerminal);
  useEffect(() => {
    if (isTerminal && !prevIsTerminalRef.current) {
      // Just transitioned to terminal — start tail grace
      logger$1.info('Terminal status detected, starting tail grace period');
      startGraceTimer();
    }
    prevIsTerminalRef.current = isTerminal;
  }, [isTerminal, startGraceTimer]);
  // ─── Real-time watcher: WebSocket (primary) + Polling (catch-up) ────────────
  const wsEnabled = enabled && !!ingestionId && (!isTerminal || wsGraceActive) && !configment.get('ENABLE_WEBSOCKET_KILL_SWITCH', false);
  useRealtimeWatcher({
    id: 'useIntakePipeline_progress',
    subjectIds,
    polling: {
      enabled: enabled && !!ingestionId,
      frequency: isTerminal ? SUPPORTED_OFFSETS.NORMAL : SUPPORTED_OFFSETS.CRITICAL,
      executor: async ({
        subjectIds: ids,
        controller
      }) => {
        if (!ids?.length) return [];
        const result = await getIngestionAttempt(ids[0]);
        return result ? [result] : [];
      }
    },
    websocket: {
      graphWebsocket,
      enabled: wsEnabled,
      executor: ({
        subjectIds: ids
      }) => ({
        query: INGESTION_ATTEMPT_SUBSCRIPTION,
        variables: {
          filters: {
            ingestionId: ids?.[0]
          }
        }
      }),
      dataExtractor: data => {
        const update = data?.ingestionAttemptUpdated;
        return update ? [update] : [];
      }
    },
    onData: ({
      data,
      source
    }) => {
      logger$1.info(`Pipeline update. source=${source} count=${data.length}`);
      for (const msg of data) {
        dispatch({
          type: 'MESSAGE_RECEIVED',
          payload: msg
        });
      }
    },
    onError: ({
      error
    }) => {
      logger$1.error('Pipeline watcher error', error);
    }
  });
  // ─── Actions ────────────────────────────────────────────────────────────────
  const start = useCallback(params => {
    dispatch({
      type: 'PIPELINE_STARTED',
      payload: params
    });
    startGraceTimer();
  }, [startGraceTimer]);
  const reset = useCallback(() => {
    dispatch({
      type: 'PIPELINE_RESET'
    });
    clearGraceTimer();
    setWsGraceActive(false);
  }, [clearGraceTimer]);
  const clearNotifications = useCallback(() => {
    dispatch({
      type: 'CLEAR_NOTIFICATIONS'
    });
  }, []);
  const actions = useMemo(() => ({
    start,
    reset,
    clearNotifications
  }), [start, reset, clearNotifications]);
  return {
    data: state,
    actions
  };
}
// ─── useIngestFile ──────────────────────────────────────────────────────────────
const ingestLogger = coreLogger.forModule('blueprint:intake:ingest');
/**
 * Hook to manage file ingestion state through the unified intake endpoint.
 * Apps provide an `executor` that calls the ingest mutation via their Apollo client
 * (required for multipart file upload). Blueprint manages the state machine.
 */
function useIngestFile(options) {
  const {
    executor
  } = options;
  const [status, setStatus] = useState(UploadStatus.Idle);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const ingest = useCallback(async input => {
    setStatus(UploadStatus.Uploading);
    setError(null);
    setResult(null);
    try {
      const res = await executor(input);
      if (!res?.ingestion_id) {
        throw new Error('No ingestion ID returned from server.');
      }
      setStatus(UploadStatus.Success);
      setResult(res);
      return res;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'File ingestion failed.';
      ingestLogger.error('Ingest failed:', e);
      setStatus(UploadStatus.Failed);
      setError(msg);
      throw e;
    }
  }, [executor]);
  const reset = useCallback(() => {
    setStatus(UploadStatus.Idle);
    setError(null);
    setResult(null);
  }, []);
  return {
    ingest,
    status,
    error,
    result,
    reset
  };
}
// ─── useIntakeFileHistory ───────────────────────────────────────────────────────
const fileHistoryLogger = coreLogger.forModule('blueprint:intake:fileHistory');
const DEFAULT_PAGE_SIZE = 10;
/**
 * Hook to fetch and paginate intake batch history.
 * Shared between Morpheus and AFI file history tables.
 */
function useIntakeFileHistory(options) {
  const {
    organizationId,
    sourceTypes,
    pageSize = DEFAULT_PAGE_SIZE,
    enabled = true
  } = options;
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  // Refs for values read inside fetchData without being in its dep array.
  // This prevents cascading re-renders from triggering redundant fetches.
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  // Track last-fetched org to detect org switches inside fetchData.
  const lastFetchedOrgRef = useRef(organizationId);
  // Monotonic fetch ID — incremented on every fetch. Stale responses are ignored.
  const fetchIdRef = useRef(0);
  // Single trigger counter: incremented by org change, page change, search, or refresh.
  const [fetchTrigger, setFetchTrigger] = useState(0);
  // Stabilize array references for useCallback deps
  const sourceTypesKey = JSON.stringify(sourceTypes || []);
  const fetchData = useCallback(async () => {
    if (!enabled || !organizationId) return;
    const thisFetchId = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      const orgChanged = organizationId !== lastFetchedOrgRef.current;
      if (orgChanged) {
        lastFetchedOrgRef.current = organizationId;
        currentPageRef.current = 1;
        searchTextRef.current = '';
        setCurrentPage(1);
        setSearchText('');
      }
      const filters = {
        take: pageSize,
        page: orgChanged ? 0 : Math.max(0, currentPageRef.current - 1)
      };
      if (!orgChanged && searchTextRef.current && searchTextRef.current.length >= 3) {
        filters.fileName = searchTextRef.current;
      }
      if (sourceTypes?.length) {
        ;
        filters.sourceTypes = sourceTypes;
      }
      const result = await fetchIntakeBatches(organizationId, filters);
      // Ignore stale responses — a newer fetch has been initiated
      if (thisFetchId !== fetchIdRef.current) return;
      setRows((result.content || []).filter(b => b != null));
      setTotalCount(result.total_count || 0);
    } catch (e) {
      if (thisFetchId !== fetchIdRef.current) return;
      fileHistoryLogger.error('Fetch error:', e);
      setRows([]);
      setTotalCount(0);
    } finally {
      if (thisFetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, sourceTypesKey, enabled, pageSize, fetchTrigger]);
  useEffect(() => {
    void fetchData();
  }, [fetchData]);
  const refresh = useCallback(() => {
    setFetchTrigger(c => c + 1);
  }, []);
  const setPage = useCallback(page => {
    setCurrentPage(page);
    currentPageRef.current = page;
    setFetchTrigger(c => c + 1);
  }, []);
  const updateSearchText = useCallback(text => {
    setSearchText(text);
    searchTextRef.current = text;
    setCurrentPage(1);
    currentPageRef.current = 1;
    setFetchTrigger(c => c + 1);
  }, []);
  return {
    rows,
    totalCount,
    isLoading,
    currentPage,
    setPage,
    refresh,
    setSearchText: updateSearchText,
    searchText
  };
}
// ─── useOrganizationMappings ────────────────────────────────────────────────────
const mappingsLogger = coreLogger.forModule('blueprint:intake:mappings');
/**
 * Hook to fetch and manage organization mapping selection.
 * Returns available mappings and selection state. The consumer is responsible
 * for setting the selected mapping via `setSelectedMapping`.
 */
function useOrganizationMappings(organizationId) {
  const [mappings, setMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!organizationId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setSelectedMapping(null);
    fetchOrganizationMappings(organizationId).then(configs => {
      setMappings(configs);
    }).catch(err => {
      mappingsLogger.error('Failed to fetch mappings:', err);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [organizationId]);
  return {
    mappings,
    selectedMapping,
    setSelectedMapping,
    isLoading
  };
}
// ─── useLeadIntake ───────────────────────────────────────────────────────
const leadIntakeLogger = coreLogger.forModule('blueprint:intake:leadIntake');
/**
 * Fetch-by-id hook for a single consumer-listing-intake lead.
 * Errors bubble to the nearest error boundary (auto-fetch logs; `refetch`
 * rejects so callers can catch). Mirrors `closing.useIndividualClosing`.
 *
 * `enabled` (default true) gates only the auto-fetch-on-mount effect — `refetch`
 * always fetches. `initialData` seeds the first render (SSR parity).
 */
function useLeadIntake(leadIntakeId, options = {}) {
  const {
    enabled = true,
    initialData = null
  } = options;
  const [data, setData] = useState(initialData);
  const controllerRef = useRef(null);
  const load = useCallback(async () => {
    if (!leadIntakeId) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const result = await fetchLeadIntake(leadIntakeId, controller);
    if (controller.signal.aborted) return;
    setData(result);
  }, [leadIntakeId]);
  useEffect(() => {
    if (!enabled) return;
    load().catch(err => leadIntakeLogger.error('useLeadIntake', err));
    return () => {
      controllerRef.current?.abort();
    };
  }, [load, enabled]);
  return {
    data,
    refetch: load
  };
}
// ─── useSaveLeadIntake ───────────────────────────────────────────────────
const saveLeadIntakeLogger = coreLogger.forModule('blueprint:intake:saveLeadIntake');
/**
 * Mutation hook to create or update a consumer-listing-intake lead via the
 * authenticated `saveLeadIntake` mutation. `save` resolves with the persisted
 * lead and rejects on failure so callers can handle it inline; `isSaving` and
 * `error` expose state for UI (button spinners, inline errors).
 *
 * Pass an `input` with `lead_intake_id` to update the lead shown on the detail
 * page; omit it to create a new lead.
 */
function useSaveLeadIntake() {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const save = useCallback(async input => {
    setIsSaving(true);
    setError(null);
    try {
      return await saveLeadIntake(input);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to save lead.');
      saveLeadIntakeLogger.error('useSaveLeadIntake', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);
  return {
    save,
    isSaving,
    error
  };
}
// ─── usePatchLeadIntakePropertyLoan ────────────────────────────────
const patchLeadIntakePropertyLoanLogger = coreLogger.forModule('blueprint:intake:patchLeadIntakePropertyLoan');
/**
 * Imperative hook for the PATCH model on a lead property. `patch` touches only the
 * fields provided (omit = untouched, explicit null = cleared) — the way to clear a
 * loan_number_uuid on a servicer change. `persistLoanNumber` is the thin
 * encrypt-then-patch convenience (secureFields encrypt → PATCH ciphertext ref +
 * servicer), so the app hook does not orchestrate the crypto round-trip itself.
 * Both reject on failure; `isPatching` / `error` drive button + inline error UI.
 */
function usePatchLeadIntakePropertyLoan() {
  const [isPatching, setIsPatching] = useState(false);
  const [error, setError] = useState(null);
  const patch = useCallback(async input => {
    setIsPatching(true);
    setError(null);
    try {
      return await patchLeadIntakePropertyLoan(input);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to update the property.');
      patchLeadIntakePropertyLoanLogger.error('usePatchLeadIntakePropertyLoan', err);
      setError(err);
      throw err;
    } finally {
      setIsPatching(false);
    }
  }, []);
  const persistLoanNumber = useCallback(async input => {
    setIsPatching(true);
    setError(null);
    try {
      return await persistLeadLoanNumber(input);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to save the loan number.');
      patchLeadIntakePropertyLoanLogger.error('usePatchLeadIntakePropertyLoan:persistLoanNumber', err);
      setError(err);
      throw err;
    } finally {
      setIsPatching(false);
    }
  }, []);
  return {
    patch,
    persistLoanNumber,
    isPatching,
    error
  };
}
// ─── useDecryptLeadLoanNumber ────────────────────────────────
const decryptLeadLoanNumberLogger = coreLogger.forModule('blueprint:intake:decryptLeadLoanNumber');
/**
 * Imperative hook to reveal a lead's raw loan number on demand. `decrypt`
 * resolves with the raw value — or null when the caller lacks the LNM authority
 * or the lead has no loan number — and rejects on failure so callers can handle
 * it inline. `isDecrypting` / `error` drive button + inline error UI. Never
 * persists; the decrypted value is returned to the caller only.
 */
function useDecryptLeadLoanNumber() {
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState(null);
  const decrypt = useCallback(async leadIntakeId => {
    setIsDecrypting(true);
    setError(null);
    try {
      return await decryptLeadLoanNumber(leadIntakeId);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to retrieve the loan number.');
      decryptLeadLoanNumberLogger.error('useDecryptLeadLoanNumber', err);
      setError(err);
      throw err;
    } finally {
      setIsDecrypting(false);
    }
  }, []);
  return {
    decrypt,
    isDecrypting,
    error
  };
}
// ─── useReprocessIntakeBatch ─────────────────────────────────
const reprocessIntakeBatchLogger = coreLogger.forModule('blueprint:intake:reprocessIntakeBatch');
/**
 * Mutation hook to reprocess an intake batch (re-run the mapping engine on its
 * failed rows). Shared by the Morpheus and AFI file-history tables.
 *
 * `reprocess` resolves true on success and rejects on failure so callers can
 * handle it inline (toast + list refresh). `reprocessingBatchId` holds the id of
 * the in-flight batch (null when idle) so the table can disable just that row's
 * button; `error` drives inline error UI.
 */
function useReprocessIntakeBatch() {
  const [reprocessingBatchId, setReprocessingBatchId] = useState(null);
  const [error, setError] = useState(null);
  const reprocess = useCallback(async intakeBatchId => {
    setReprocessingBatchId(intakeBatchId);
    setError(null);
    try {
      return await reprocessIntakeBatch(intakeBatchId);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to reprocess the intake batch.');
      reprocessIntakeBatchLogger.error('useReprocessIntakeBatch', err);
      setError(err);
      throw err;
    } finally {
      setReprocessingBatchId(null);
    }
  }, []);
  return {
    reprocess,
    reprocessingBatchId,
    error
  };
}
// ─── Leads (Lead List Page) ─────────────────────────────────────────────────
const leadsLogger = coreLogger.forModule('blueprint:intake:leads');
const conciergesLogger = coreLogger.forModule('blueprint:intake:concierges');
const DEFAULT_LEADS_PAGE_SIZE = 25;
const EMPTY_LEADS_RESULT = {
  rows: [],
  totalCount: 0,
  totalPages: 0,
  currentPage: 0,
  statusCounts: {}
};
/**
 * Fetches a page of smartsale leads plus the per-status counts via a single
 * seek call. The hook owns only fetch state and the current filters/sort/page;
 * it returns the graph response as-is (no loading flag, no derived fields).
 * Consumers drive pending UI with `useTransition`.
 */
function useSmartsaleLeads(options = {}) {
  const {
    pageSize = DEFAULT_LEADS_PAGE_SIZE,
    enabled = true,
    initialFilters = {},
    initialSort
  } = options;
  const [data, setData] = useState(EMPTY_LEADS_RESULT);
  const [filters, setFiltersState] = useState(initialFilters);
  const [sort, setSortState] = useState(initialSort);
  const [currentPage, setCurrentPage] = useState(1);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const pageRef = useRef(currentPage);
  pageRef.current = currentPage;
  const fetchIdRef = useRef(0);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const thisFetchId = ++fetchIdRef.current;
    fetchLeads(filtersRef.current, {
      page: Math.max(0, pageRef.current - 1),
      size: pageSize,
      sort: sortRef.current
    }).then(result => {
      if (!cancelled && thisFetchId === fetchIdRef.current) setData(result);
    }).catch(e => {
      if (!cancelled && thisFetchId === fetchIdRef.current) {
        leadsLogger.error('Fetch error:', e);
        setData(EMPTY_LEADS_RESULT);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, pageSize, filters, sort, currentPage, fetchTrigger]);
  const setFilters = useCallback(next => {
    setFiltersState(next);
    setCurrentPage(1);
  }, []);
  const setSort = useCallback(next => setSortState(next), []);
  const setPage = useCallback(page => setCurrentPage(page), []);
  const refresh = useCallback(() => setFetchTrigger(c => c + 1), []);
  const assign = useCallback(async (leadIntakeId, assigneePartyId) => {
    await assignSmartsaleLead(leadIntakeId, assigneePartyId);
    setFetchTrigger(c => c + 1);
  }, []);
  return {
    ...data,
    filters,
    sort,
    setFilters,
    setSort,
    setPage,
    refresh,
    assign
  };
}
function useConcierges(options = {}) {
  const {
    enabled = true,
    organizationId,
    group,
    size
  } = options;
  const [concierges, setConcierges] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchConcierges({
      organizationId,
      group,
      size
    }).then(list => {
      if (!cancelled) setConcierges(list);
    }).catch(e => {
      if (!cancelled) {
        conciergesLogger.error('Fetch error:', e);
        setConcierges([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, organizationId, group, size]);
  return {
    concierges
  };
}

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    INGESTION_ATTEMPT_SUBSCRIPTION: INGESTION_ATTEMPT_SUBSCRIPTION,
    INITIAL_PIPELINE_STATE: INITIAL_PIPELINE_STATE,
    assignSmartsaleLead: assignSmartsaleLead,
    decryptLeadLoanNumber: decryptLeadLoanNumber,
    fetchConcierges: fetchConcierges,
    fetchIntakeBatches: fetchIntakeBatches,
    fetchLeadIntake: fetchLeadIntake,
    fetchLeads: fetchLeads,
    fetchOrganizationMappings: fetchOrganizationMappings,
    getIngestionAttempt: getIngestionAttempt,
    mergeLeadIntakeInput: mergeLeadIntakeInput,
    patchLeadIntakePropertyLoan: patchLeadIntakePropertyLoan,
    persistLeadLoanNumber: persistLeadLoanNumber,
    reprocessIntakeBatch: reprocessIntakeBatch,
    saveLeadIntake: saveLeadIntake,
    useConcierges: useConcierges,
    useDecryptLeadLoanNumber: useDecryptLeadLoanNumber,
    useIngestFile: useIngestFile,
    useIntakeFileHistory: useIntakeFileHistory,
    useIntakePipeline: useIntakePipeline,
    useLeadIntake: useLeadIntake,
    useOrganizationMappings: useOrganizationMappings,
    usePatchLeadIntakePropertyLoan: usePatchLeadIntakePropertyLoan,
    useReprocessIntakeBatch: useReprocessIntakeBatch,
    useSaveLeadIntake: useSaveLeadIntake,
    useSmartsaleLeads: useSmartsaleLeads
});

const logger = coreLogger.forModule('blueprint:secureFields');
/**
 * Generic hook for encrypting and revealing secure fields. Loan number is the
 * first registered field; adding others is a registry entry, not new hook code.
 *
 * `encrypt` returns the stored reference (the caller persists it on its own
 * entity). `reveal` decrypts on demand through the field's authorized resolver.
 * Neither persists anything here. `error` holds the most recent failure.
 *
 * This is a primitive: it owns the crypto round-trip only. Entity persistence,
 * field state machines, and gating belong to the consuming feature layer.
 */
function useSecureFields() {
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [error, setError] = useState(null);
  const encrypt = useCallback(async input => {
    setIsEncrypting(true);
    setError(null);
    try {
      return await encryptSecret(input);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to encrypt the value.');
      logger.error('useSecureFields:encrypt', err);
      setError(err);
      throw err;
    } finally {
      setIsEncrypting(false);
    }
  }, []);
  const reveal = useCallback(async (field, ref) => {
    setIsRevealing(true);
    setError(null);
    try {
      return await revealSecret(field, ref);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Failed to reveal the value.');
      logger.error('useSecureFields:reveal', err);
      setError(err);
      throw err;
    } finally {
      setIsRevealing(false);
    }
  }, []);
  return {
    encrypt,
    reveal,
    isEncrypting,
    isRevealing,
    error
  };
}

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get SecureField () { return SecureField; },
    encryptSecret: encryptSecret,
    revealSecret: revealSecret,
    useSecureFields: useSecureFields
});

export { types$5 as BuyerListingSearchTypes, INTENT_ANY, INTENT_NO_MAX, INTENT_NO_MIN, INTENT_VALUES, MISSING_VALUE_LABEL, RESERVE_LABELS, types$4 as UseListingsSearchTypes, index$k as bidding, index$6 as closing, index$8 as cognition, index$7 as communicationPreferences, index$c as documents, index$j as filters, index$9 as flows, formatters, getReserveLabel, instrument, index$1 as intake, index$a as invitations, isIntentValue, index$l as listing, index$g as nudges, index$m as primitives, index$f as property, index$d as purchaseProfiles, index$h as search, index as secureFields, index$4 as tasksAssignments, toConcreteValue, index$e as user, utils$d as utils, index$2 as venues, index$b as wallet, index$3 as workflow, index$5 as workflowNotes };
//# sourceMappingURL=blueprint.js.map
